// Canvas.jsx
import Konva from "konva";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import {
  normalizeShapeType,
  isShapeType,
} from "../../../../util/shapeTypeMapper";
import {
  Circle,
  Group,
  Layer,
  Line,
  Rect,
  Stage,
  Text,
  Transformer,
} from "react-konva";
import { Html } from "react-konva-utils";
import ArrowElement from "../arrow/ArrowElement";
import Background from "./Background";
import CircleElement from "../circle/CircleElement";
import ImageElement from "../image/ImageElement";
import LineElement from "../line/LineElement";
import RectangleElement from "../rectangle/RectangleElement";
import "../styles.css";
import DoubleSidedArrowElement from "../arrow/DoubleSidedArrowElement";
import TextElement from "../text/TextElement";
import {
  calculateGroupHeight,
  estimateKonvaTextHeight,
  measureHtmlHeight,
} from "../../../SectionElements/plugins/utils.js";
import ReactDOM from "react-dom";
import { getCursorStyle } from "../../utils/cursorUtils";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import {
  stripHtml,
  stripHtmlPreserveFormatting,
  hasUserContent,
  hasTaskContent,
  hasNoteContent,
  calculateTextBlockWidth,
  formatListContent,
} from "../../utils/textCanvasUtils";
import { createTextRenderer } from "./renderers/textRenderer";
import { createArrowRenderer } from "./renderers/arrowRenderer";
import { createRectangleRenderer } from "./renderers/rectangleRenderer";
import { createCircleRenderer } from "./renderers/circleRenderer";
import { createLineRenderer } from "./renderers/lineRenderer";
import { createDoubleSidedArrowRenderer } from "./renderers/doubleArrowRenderer";
import { calculateTotalContentHeight } from "../../../../util/canvas/canvasHtmlSnapshot";
import {
  FIXED_MAIN_CANVAS_STAGE_WIDTH_PX,
  FIXED_OFF_CANVAS_SIDE_WIDTH_PX,
  getCanvasStageHeightPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "../../utils/mainCanvasLayout";
import { createImageRenderer } from "./renderers/imageRenderer";
import {
  getRelativePointerPosition,
  constrainToBoundaries,
  ensureElementInBounds,
  ensureAllElementsInBounds,
  getAbsoluteHorizontalExtent,
  getOffCanvasMainColumnBounds,
  getOffCanvasHorizontalStripSide,
  shiftXToStayInOffCanvasStrip,
  clampOffCanvasTransformerBox,
} from "../../utils/boundaryUtils";
import {
  handleTransformUpdate as handleTransformUpdateUtil,
  handleImageClick as handleImageClickUtil,
  handleImageResize as handleImageResizeUtil,
  handlePartClick as handlePartClickUtil,
} from "../../utils/canvasElementUtils";
import {
  createPanHandlers,
  createResizeHandlers,
} from "../../utils/canvasEventHandlers";
import { clearTransformers, findElementCanvas } from "../../utils/transformerUtils";
import { useTransformerManager } from "../../utils/useTransformerManager";
import {
  beginCrossStageGesture,
  endCrossStageGesture,
} from "../../utils/crossStageGesture";
import {
  handleElementDragEnd as handleElementDragEndUtil,
  calculateClientCoordinates,
  determineTargetCanvas,
  constrainOffCanvasPosition,
} from "../../utils/dragEndUtils";
import { v4 as uuidv4 } from "uuid";
import { clearCrossCanvasCloneServerIdentity } from "../../utils/canvasCrossCanvasIdentityUtils";
import MultiSelectIcon from "./MultiSelectIcon";
import SelectAllIcon from "./SelectAllIcon";
import CrosshairTooltip from "./CrosshairTooltip";
import { X } from "lucide-react";
import "../../../../common/Popup/Popup.css";

const cjsonChangePlainText = (value) =>
  stripHtmlPreserveFormatting(value == null ? "" : String(value));

// Set to true to log line drag preview / hide logic (main→off canvas source copy)
const LINE_DRAG_PREVIEW_DEBUG = true;
// Set to true to trace text drag + top-layer interception issues.
const DEBUG_TEXT_DRAG_ISSUE = true;
// Set to true for off-canvas selected-image/transformer layering diagnostics.
const DEBUG_SELECTION_FLOW = false;
// Set to true to inspect off-canvas image transformer source + anchor styling.
const DEBUG_IMAGE_TRANSFORMER_FLOW = true;
const TRANSFORMER_ANCHOR_BUFFER_PX = 10;

// Cap pixel ratio for performance on high-DPI displays (3x+ retina). 2 is a good balance.
const CANVAS_PIXEL_RATIO =
  typeof window !== "undefined" ? Math.min(2, window.devicePixelRatio || 1) : 1;

// Performance optimization utilities for multi-selection drag
const performanceCache = {
  stageBounds: new Map(),
  elementNodes: new Map(),
  lastCacheTime: 0,
  CACHE_DURATION: 500, // PERFORMANCE FIX: Increase cache duration from 100ms to 500ms for better performance
};

// Clear cache periodically to prevent memory leaks
const clearPerformanceCache = () => {
  const now = Date.now();
  if (now - performanceCache.lastCacheTime > 10000) {
    // PERFORMANCE FIX: Clear every 10 seconds instead of 5
    performanceCache.stageBounds.clear();
    performanceCache.elementNodes.clear();
    performanceCache.lastCacheTime = now;
  }
};

// Get cached stage bounds to avoid repeated DOM queries
const getCachedStageBounds = (stage) => {
  if (!stage) return null;

  const stageId = stage._id || "unknown";
  const cached = performanceCache.stageBounds.get(stageId);
  const now = Date.now();

  if (cached && now - cached.timestamp < performanceCache.CACHE_DURATION) {
    return cached.bounds;
  }

  try {
    const bounds = stage.container().getBoundingClientRect();
    performanceCache.stageBounds.set(stageId, {
      bounds,
      timestamp: now,
    });
    return bounds;
  } catch (e) {
    return null;
  }
};

// Get cached element node to avoid repeated findOne calls
const getCachedElementNode = (stage, elementId) => {
  if (!stage) return null;

  const cacheKey = `${stage._id || "unknown"}-${elementId}`;
  const cached = performanceCache.elementNodes.get(cacheKey);
  const now = Date.now();

  if (cached && now - cached.timestamp < performanceCache.CACHE_DURATION) {
    // Verify node is still valid and in the stage
    try {
      if (cached.node && cached.node.getStage() === stage) {
        return cached.node;
      }
    } catch (e) {
      // Node is no longer valid, remove from cache
    }
  }

  try {
    const node = stage.findOne(`#${elementId}`);
    if (node) {
      performanceCache.elementNodes.set(cacheKey, {
        node,
        timestamp: now,
      });
    }
    return node;
  } catch (e) {
    return null;
  }
};

// Optimized delta calculation with minimal operations
const calculateOptimizedDelta = (
  draggedElementCurrentX,
  draggedElementCurrentY,
  initialPos,
) => {
  return {
    deltaX: draggedElementCurrentX - (initialPos.x || 0),
    deltaY: draggedElementCurrentY - (initialPos.y || 0),
  };
};

// Optimized position calculation with early returns
const calculateOptimizedPosition = (initialPos, deltaX, deltaY) => {
  // All element types use the same calculation - no need for branching
  return {
    x: (initialPos.x || 0) + deltaX,
    y: (initialPos.y || 0) + deltaY,
  };
};

// Optimized boundary checking with minimal calculations
const constrainToStageBounds = (
  x,
  y,
  element,
  stageWidth,
  stageHeight,
  elementType,
) => {
  // Fast path for most common case - element is already in bounds
  if (x >= 0 && y >= 0 && x <= stageWidth && y <= stageHeight) {
    return { x, y };
  }

  // Only calculate bounds if necessary
  let finalX = Math.max(0, Math.min(stageWidth, x));
  let finalY = Math.max(0, Math.min(stageHeight, y));

  // Special handling for elements with stroke width
  if (elementType === "Rect" || elementType === "Circle") {
    const strokeWidth = element?.strokeWidth || 2;
    const padding = strokeWidth / 2;
    finalX = Math.max(padding, Math.min(stageWidth - padding, x));
    finalY = Math.max(padding, Math.min(stageHeight - padding, y));
  }

  return { x: finalX, y: finalY };
};

// Optimized node update with batching
const batchUpdateNodePositions = (updates) => {
  const layersToUpdate = new Set();

  updates.forEach(({ node, x, y }) => {
    if (node && typeof node.x === "function" && typeof node.y === "function") {
      node.x(x);
      node.y(y);
      const layer = node.getLayer();
      if (layer) {
        layersToUpdate.add(layer);
      }
    }
  });

  // Batch draw all updated layers
  layersToUpdate.forEach((layer) => {
    try {
      layer.batchDraw();
    } catch (e) {
      // Ignore errors during batch draw
    }
  });
};

// Optimized selectedObjects update with minimal array operations
const createOptimizedSelectedObjectsUpdate = (
  selectedObjects,
  elementPositions,
  deltaX,
  deltaY,
) => {
  if (!selectedObjects || selectedObjects.length === 0) {
    return [];
  }

  // Use Map for faster lookups
  const positionMap = new Map();
  elementPositions.forEach((pos) => {
    positionMap.set(`${pos.type}-${pos.id}`, pos);
  });

  return selectedObjects.map((obj) => {
    const posKey = `${obj.type}-${obj.id}`;
    const initialPos = positionMap.get(posKey);

    if (!initialPos) {
      return obj; // Return unchanged if no initial position found
    }

    const newPos = calculateOptimizedPosition(initialPos, deltaX, deltaY);
    return {
      ...obj,
      x: newPos.x,
      y: newPos.y,
    };
  });
};

// Throttled function to prevent excessive updates during drag
const createThrottledUpdate = (updateFn, delay = 16) => {
  // 60fps
  let lastCall = 0;
  let timeoutId = null;

  return (...args) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;

    if (timeSinceLastCall >= delay) {
      updateFn(...args);
      lastCall = now;
    } else {
      // Schedule update for later
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        updateFn(...args);
        lastCall = Date.now();
      }, delay - timeSinceLastCall);
    }
  };
};

// Toolbar boundary constant - prevents elements from going behind toolbar
// Toolbar is positioned at left: 8px with padding: 5px and buttons: 28px wide
// Total width: 8px (left) + 5px (padding) + 28px (button) + 5px (padding) = 46px
// Using 55px to account for toolbar area, border-radius, box-shadow, and safety margin
const TOOLBAR_WIDTH = 55;

const Canvas = ({
  viewportWidth = window.innerWidth,
  texts,
  images,
  rectangles, // Main canvas rectangles
  circles,
  arrows,
  doubleSidedArrows,
  setDoubleSidedArrows,
  strokeColor,
  setSelectedTextId,
  setImages,
  lines,
  transformerRef,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  stageRef, // Main canvas stage ref
  /** Optional: parent ref to `#main-canvas-stage-container` for zoom scroll centering */
  mainStageContainerRefFromParent = null,
  scrollAreaRef = null,
  topLayerStageRefFromParent, // Optional ref from parent for leave snapshot compositing
  setSelectedArrow,
  selectedArrow,
  onDoubleClick,
  setArrows,
  setWaypointCount,
  setSelectedText,
  isLeavingCanvas,
  selectedText,
  selectedTextId,
  saveToHistory,
  handleRectangleDragEnd,
  handleCircleDragEnd,
  handleArrowDragEnd,
  handleTextDragEnd,
  handleImageDragEnd,
  updateImageProperties,
  setSelectedObject,
  canvasHeight,
  setCanvasHeight,
  applyCanvasHeight,
  effectiveZoomLevel = 1, // Used for stroke width compensation at low zoom levels
  canvasResizeTrigger = 0,
  setCanvasResizeTrigger,
  setTexts,
  setShowTextBox,
  setShowEditTask,
  setShowEditNote,
  setShowQtyUnitOptions = () => {},
  showEditTask,
  showEditNote,
  handleLineDragEnd,
  setRectangles, // Main canvas rectangles setter
  setCircles,
  setScribbles = () => {},
  action,
  selectedImage,
  setSelectedImage,
  selectedObject,

  handleDrop,
  // Paste is handled on `window` in MyCanvas (`handleNativePaste`). Kept as optional no-op for callers / stale refs.
  // eslint-disable-next-line no-unused-vars -- default intentionally unused here
  handlePaste = () => {},
  setLines,
  selectedLine,
  handleDotDragMove,
  setSelectedLine,
  selectedDoubleSidedArrow,
  setSelectedDoubleSidedArrow,
  handleDoubleSidedArrowDragEnd,
  hoverPoint,
  isDrawingLine,
  lineStartPoint,
  fillColor,
  isDrawingArrow,
  arrowStartPoint,
  doubleArrowStartPoint,
  isDrawingDoubleArrow,
  isDrawingDoubleSidedArrow,
  isDrawingRect,
  isDrawingRectRef = null, // Synced before re-render; avoids missing draw finalization on fast click
  isDrawingCircle,
  isDrawingText = false,
  isDrawingImage,
  ACTIONS,
  setAction,
  setEditTextBoxContent,
  editTextBoxContent,
  selectedObjects,
  selectedObjectsRef,
  handleCut,
  handleCopy,
  setSelectedObjects,
  setGroupDragDisplay = null,
  isMultiSelectMode = false,
  setIsMultiSelectMode = null,
  individuallySelectedIds = new Set(),
  setIndividuallySelectedIds = null,
  justFinishedSelectionRef = null,
  justFinishedShapeDrawRef = null,
  pendingSelection = null,
  isWaitingForPaste = false,
  preMouseDownSelectionCountRef = null, // CRITICAL: Ref to capture selection count before Stage.onMouseDown clears it
  onCopyGroupBetweenCanvasesReady = null, // Callback to expose handleCopyGroupBetweenCanvases
  onOffCanvasStageRefReady = null, // Callback to expose offCanvasStageRef
  setIsWaitingForPaste = null,
  setLockedAction = null,
  setIsCloneMode = null,
  setCloneTemplate = null,
  setCloneTemplates = null,
  pasteModeType = null,
  pendingClipboard = [],
  pasteElementsAtPosition = null,
  cloneElementsAtPosition = null,
  cloneTemplates = [],
  cloneTemplate = null, // Single-element clone template (for backward compatibility)
  getTextStyle,
  getMinCanvasHeightPercent,

  setEditing,
  updateArrowProperties,
  updateDoubleSidedArrowProperties,
  updateLineProperties,
  offCanvasArrows = [],
  setOffCanvasArrows = () => {},
  offCanvasLines = [],
  setOffCanvasLines = () => {},
  offCanvasDoubleSidedArrows = [],
  setOffCanvasDoubleSidedArrows = () => {},
  offCanvasTexts = [],
  setOffCanvasTexts = () => {},
  offCanvasImages = [],
  setOffCanvasImages = () => {},
  offCanvasRectangles = [],
  setOffCanvasRectangles = () => {},
  offCanvasCircles = [],
  setOffCanvasCircles = () => {},

  offCanvasScribbles = [],
  setOffCanvasScribbles = () => {},
  lockedAction,
  isCloneMode, // Clone mode state to keep editor open
  endElementEditing = () => {},
  isElementBeingEdited = false,
  setPendingChanges = () => {},
  pendingChangesRef = null, // Ref to update synchronously
  latestUpdatedImagesRef = null, // Ref to store latest updated images from transform operations
  latestUpdatedTextsRef = null, // Ref to store latest updated texts from transform operations
  isTransformingRef = null, // Ref to track if we're currently transforming (shared with MyCanvas)
  isCropModalOpen = false, // Whether the crop modal is open
  isCropModalOpenRef = null, // Ref set synchronously on Crop click (fixes first-time close)
  showErrorSnackbar = () => {}, // Function to show error snackbar at bottom of canvas
  currentImageId = null, // Ref to track current drawing image ID
  currentRectId = null, // Ref to track current drawing rectangle ID
  largeImageWarning = false,
  importedCjsonPartChanges = [],
  onCloseCjsonPartChangesPopup = () => {},
  onApplyImportedPartUpdates = () => {},
  isApplyingImportedPartUpdates = false,
}) => {
  const mainCanvasWidth = FIXED_MAIN_CANVAS_STAGE_WIDTH_PX;
  const offCanvasSideWidth = FIXED_OFF_CANVAS_SIDE_WIDTH_PX;
  const offCanvasStageWidth = getOffCanvasStageWidthPx();
  /** Pixels tall for Konva stages; driven by `canvasHeight` % (orb / height editor). */
  const getMainStageHeightPxFromPercent = () =>
    getStageHeightPxFromCanvasHeight(canvasHeight);
  const [stageHeightPx, setStageHeightPx] = useState(() =>
    getMainStageHeightPxFromPercent(),
  );
  useEffect(() => {
    setStageHeightPx(getMainStageHeightPxFromPercent());
  }, [canvasHeight, canvasResizeTrigger]);
  const fixedStageHeightPx = stageHeightPx;
  // Keep off-canvas stage height independent from main-canvas height % changes.
  const offCanvasStageHeightPx = getCanvasStageHeightPx();
  useEffect(() => {
  }, [
    canvasHeight,
    fixedStageHeightPx,
    offCanvasStageHeightPx,
    canvasResizeTrigger,
  ]);
  /**
   * Align off-canvas stage so its main overlay column is exactly under the fixed main stage.
   * Layout is [left off-canvas strip][main overlay] (no right strip).
   */
  const offCanvasStageLeft = `calc(50% - ${mainCanvasWidth / 2 + offCanvasSideWidth}px)`;
  const mainStageAbsoluteLeft = `calc(50% - ${mainCanvasWidth / 2}px)`;
  const isDuplicatePlacementActive =
    isCloneMode || !!lockedAction || !!cloneTemplate || cloneTemplates.length > 0;

  useEffect(() => {
    if (!isDuplicatePlacementActive) return;
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = "crosshair";
    return () => {
      document.body.style.cursor = prevCursor || "";
    };
  }, [isDuplicatePlacementActive]);

  // CRITICAL FIX: Clear recentDraggableElementMousedownRef when an image is selected
  // This prevents the two-click deselection issue after drag-to-create
  useEffect(() => {
    if (selectedImage) {
      // Clear the ref after a short delay to ensure selection is complete
      const timeoutId = setTimeout(() => {
        if (recentDraggableElementMousedownRef.current.nodeId) {
          recentDraggableElementMousedownRef.current = {
            nodeId: null,
            timestamp: 0,
          };
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedImage]);

  const contentTextareaRef = useRef(null);
  const taskTextRef = useRef(null);
  const offCanvasTransformerRef = useRef(null);
  const topLayerStageRef = useRef(null);
  const topLayerTransformerRef = useRef(null);
  const [isPointerOverTopLayerImage, setIsPointerOverTopLayerImage] =
    useState(false);
  const isPointerOverTopLayerImageRef = useRef(false);
  // Ref to track wrapper rectangle drag state
  const wrapperDragStartRef = useRef({ x: 0, y: 0, elementPositions: [] });
  // Ref to track if wrapper was just dragged (to distinguish click from drag)
  const wrapperJustDraggedRef = useRef(false);
  // NEW: Ref for the auxiliary canvas stage
  const offCanvasStageRef = useRef(null);

  // Separate stage for off-canvas top-layer images — sits above the off-canvas Stage's
  // <Html> overlays (z-index 2 vs z-index 1), mirroring the main-canvas topLayerStage approach.
  const offCanvasTopLayerStageRef = useRef(null);
  // NEW: Ref for the main stage container wrapper (for clean screenshots)
  const mainStageContainerRef = useRef(null);
  React.useLayoutEffect(() => {
    if (!isDuplicatePlacementActive) return;

    const clearTransformer = (trRef) => {
      const tr = trRef?.current;
      if (!tr) return;
      try {
        tr.nodes([]);
        tr.getLayer?.()?.batchDraw?.();
      } catch (_) {
        // ignore
      }
    };

    clearTransformer(transformerRef);
    clearTransformer(offCanvasTransformerRef);
    clearTransformer(topLayerTransformerRef);
  }, [
    isDuplicatePlacementActive,
    transformerRef,
    offCanvasTransformerRef,
    topLayerTransformerRef,
  ]);
  // isTransformingRef is now passed as a prop from MyCanvas (shared ref for transform state tracking)
  // Ref to track pan state for scroll synchronization
  const panStateRef = useRef({
    initialScrollLeft: 0,
    initialScrollTop: 0,
    scrollableParent: null,
    accumulatedPanX: 0,
    accumulatedPanY: 0,
  });
  // Debounce timers for transform operations
  const transformDebounceRefs = useRef({});

  // Debounced state update function to reduce update frequency during transform
  const debouncedStateUpdate = (updateFn, key, delay = 16) => {
    if (transformDebounceRefs.current[key]) {
      clearTimeout(transformDebounceRefs.current[key]);
    }
    transformDebounceRefs.current[key] = setTimeout(() => {
      updateFn();
      delete transformDebounceRefs.current[key];
    }, delay);
  };

  /** Cancel a pending debounced transform so onTransformEnd / final attrs are not overwritten. */
  const clearDebouncedTransformForKey = React.useCallback((key) => {
    if (key && transformDebounceRefs.current[key]) {
      clearTimeout(transformDebounceRefs.current[key]);
      delete transformDebounceRefs.current[key];
    }
  }, []);

  // Cleanup function to clear pending debounced updates
  const clearAllDebouncedUpdates = () => {
    Object.values(transformDebounceRefs.current).forEach(clearTimeout);
    transformDebounceRefs.current = {};
  };

  // Cleanup effect to clear pending debounced updates on unmount
  useEffect(() => {
    return () => {
      clearAllDebouncedUpdates();
      clearPerformanceCache();
    };
  }, []);

  // noteTextRef.current = useRef(null); // Fix: use .current for useRef assignment

  // Offcanvas state is now managed by parent component (MyCanvas)
  // This ensures proper state management and deletion functionality

  // Provide default values for offcanvas props (these are already destructured above)
  // No need to redeclare them as constants

  // getRelativePointerPosition is now imported from boundaryUtils.js

  // Handle viewport resize (but not zoom changes)
  React.useEffect(() => {
    const handleResize = () => {
      // Force re-render when viewport changes
      if (stageRef.current) {
        stageRef.current.batchDraw();
      }
    };

    // Listen for resize events only
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [texts, images, rectangles, circles, arrows, lines, doubleSidedArrows]);

  // Clear selectedContent for all texts when selectedText is null
  useEffect(() => {
    if (selectedText === null) {
      setTexts((prevTexts) =>
        prevTexts.map((t) => ({
          ...t,
          selectedContent: null,
        })),
      );
    }
  }, [selectedText]);

  /* TEMPORARILY DISABLED: when canvas height increases, this moved off-canvas stage
   * elements into the main canvas (rects, circles, arrows, lines, texts, images).
   * Re-enable by uncommenting the block below.
   *
  // Convert off-canvas elements to main canvas when canvas height increases
  const prevCanvasHeightRef = useRef(canvasHeight);
  // Debounce timer for expensive off-canvas conversion
  const offCanvasConversionTimeoutRef = useRef(null);
  // Refs to store current off-canvas arrays to avoid dependency issues
  const offCanvasArraysRef = useRef({
    rectangles: offCanvasRectangles,
    circles: offCanvasCircles,
    arrows: offCanvasArrows,
    doubleSidedArrows: offCanvasDoubleSidedArrows,
    lines: offCanvasLines,
    texts: offCanvasTexts,
    images: offCanvasImages,
  });

  // Keep refs in sync with current values
  useEffect(() => {
    offCanvasArraysRef.current = {
      rectangles: offCanvasRectangles,
      circles: offCanvasCircles,
      arrows: offCanvasArrows,
      doubleSidedArrows: offCanvasDoubleSidedArrows,
      lines: offCanvasLines,
      texts: offCanvasTexts,
      images: offCanvasImages,
    };
  }, [
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasArrows,
    offCanvasDoubleSidedArrows,
    offCanvasLines,
    offCanvasTexts,
    offCanvasImages,
  ]);

  useEffect(() => {
    const prevHeight = parseFloat(prevCanvasHeightRef.current) || 0;
    const currentHeight = parseFloat(canvasHeight) || 0;

    // Only process if canvas height increased
    if (currentHeight <= prevHeight) {
      prevCanvasHeightRef.current = canvasHeight;
      return;
    }

    // Clear any pending conversion
    if (offCanvasConversionTimeoutRef.current) {
      if (typeof offCanvasConversionTimeoutRef.current === "number") {
        clearTimeout(offCanvasConversionTimeoutRef.current);
      } else {
        cancelAnimationFrame(offCanvasConversionTimeoutRef.current);
      }
    }

    // Debounce the expensive conversion operation to prevent blocking
    // Use requestAnimationFrame to ensure it runs after the current render cycle
    const rafId = requestAnimationFrame(() => {
      // Use setTimeout to defer to next event loop tick, preventing UI blocking
      const timeoutId = setTimeout(() => {
        const mainStage = stageRef?.current;
        const offStage = offCanvasStageRef?.current;

        if (!mainStage || !offStage) {
          prevCanvasHeightRef.current = canvasHeight;
          return;
        }

        // Get current off-canvas arrays from refs
        const currentOffCanvasArrays = offCanvasArraysRef.current;

        const mainBBox = mainStage.container().getBoundingClientRect();
        const offBBox = offStage.container().getBoundingClientRect();

        // Calculate main canvas boundaries
        const mainCanvasRight = mainBBox.left + mainCanvasWidth;
        const mainCanvasBottom =
          mainBBox.top +
          (fixedStageHeightPx);

        // Helper function to check if an element is within main canvas bounds
        const isElementInMainCanvas = (element, elementType) => {
          // Get element's position in client coordinates
          const elementX = offBBox.left + (element.x || 0);
          const elementY = offBBox.top + (element.y || 0);

          // For lines/arrows, check center point
          if (
            elementType === "Line" ||
            elementType === "Arrow" ||
            elementType === "DoubleSidedArrow"
          ) {
            const points = element.points || [0, 0, 0, 0];
            if (points.length >= 4) {
              const minX = Math.min(points[0], points[2]);
              const maxX = Math.max(points[0], points[2]);
              const minY = Math.min(points[1], points[3]);
              const maxY = Math.max(points[1], points[3]);
              const centerX = elementX + (minX + maxX) / 2;
              const centerY = elementY + (minY + maxY) / 2;

              return (
                centerX >= mainBBox.left &&
                centerX <= mainCanvasRight &&
                centerY >= mainBBox.top &&
                centerY <= mainCanvasBottom
              );
            }
          }

          // For other elements, check their position
          // For centered elements (Image, Circle), check center
          if (elementType === "Image" || elementType === "Circle") {
            return (
              elementX >= mainBBox.left &&
              elementX <= mainCanvasRight &&
              elementY >= mainBBox.top &&
              elementY <= mainCanvasBottom
            );
          }

          // For top-left anchored elements (Rect, Text), check top-left corner
          // IMPORTANT: Only convert if element is clearly within main canvas bounds
          // Elements below the main canvas (even if horizontally within bounds) should stay in off-canvas
          const isHorizontallyInMainCanvas =
            elementX >= mainBBox.left && elementX <= mainCanvasRight;
          const isVerticallyInMainCanvas =
            elementY >= mainBBox.top && elementY <= mainCanvasBottom;

          // Only convert if both horizontally AND vertically within main canvas bounds
          // This prevents elements below the main canvas from being incorrectly converted
          return isHorizontallyInMainCanvas && isVerticallyInMainCanvas;
        };

        // Helper function to convert element coordinates from off-canvas to main canvas
        const convertElementPosition = (element, elementType) => {
          // Convert from off-canvas stage coordinates to main canvas stage coordinates
          // First, get the element's position in client/viewport coordinates
          const elementClientX = offBBox.left + (element.x || 0);
          const elementClientY = offBBox.top + (element.y || 0);

          // Then convert to main canvas stage coordinates
          const newX = elementClientX - mainBBox.left;
          const newY = elementClientY - mainBBox.top;

          return { ...element, x: newX, y: newY };
        };

        // Convert rectangles
        const rectanglesToConvert = currentOffCanvasArrays.rectangles.filter(
          (rect) => isElementInMainCanvas(rect, "Rect"),
        );
        if (rectanglesToConvert.length > 0) {
          const convertedRectangles = rectanglesToConvert.map((rect) =>
            convertElementPosition(rect, "Rect"),
          );
          const rectangleIdsToRemove = new Set(
            rectanglesToConvert.map((rect) => rect.id),
          );
          setRectangles((prev) => [...prev, ...convertedRectangles]);
          setOffCanvasRectangles((prev) =>
            prev.filter((el) => !rectangleIdsToRemove.has(el.id)),
          );
        }

        // Convert circles
        const circlesToConvert = currentOffCanvasArrays.circles.filter(
          (circle) => isElementInMainCanvas(circle, "Circle"),
        );
        if (circlesToConvert.length > 0) {
          const convertedCircles = circlesToConvert.map((circle) =>
            convertElementPosition(circle, "Circle"),
          );
          const circleIdsToRemove = new Set(
            circlesToConvert.map((circle) => circle.id),
          );
          setCircles((prev) => [...prev, ...convertedCircles]);
          setOffCanvasCircles((prev) =>
            prev.filter((el) => !circleIdsToRemove.has(el.id)),
          );
        }

        // Convert arrows
        const arrowsToConvert = currentOffCanvasArrays.arrows.filter((arrow) =>
          isElementInMainCanvas(arrow, "Arrow"),
        );
        if (arrowsToConvert.length > 0) {
          const convertedArrows = arrowsToConvert.map((arrow) =>
            convertElementPosition(arrow, "Arrow"),
          );
          const arrowIdsToRemove = new Set(
            arrowsToConvert.map((arrow) => arrow.id),
          );
          setArrows((prev) => [...prev, ...convertedArrows]);
          setOffCanvasArrows((prev) =>
            prev.filter((el) => !arrowIdsToRemove.has(el.id)),
          );
        }

        // Convert double-sided arrows
        const doubleArrowsToConvert =
          currentOffCanvasArrays.doubleSidedArrows.filter((arrow) =>
            isElementInMainCanvas(arrow, "DoubleSidedArrow"),
          );
        if (doubleArrowsToConvert.length > 0) {
          const convertedDoubleArrows = doubleArrowsToConvert.map((arrow) =>
            convertElementPosition(arrow, "DoubleSidedArrow"),
          );
          const doubleArrowIdsToRemove = new Set(
            doubleArrowsToConvert.map((arrow) => arrow.id),
          );
          setDoubleSidedArrows((prev) => [...prev, ...convertedDoubleArrows]);
          setOffCanvasDoubleSidedArrows((prev) =>
            prev.filter((el) => !doubleArrowIdsToRemove.has(el.id)),
          );
        }

        // Convert lines
        const linesToConvert = currentOffCanvasArrays.lines.filter((line) =>
          isElementInMainCanvas(line, "Line"),
        );
        if (linesToConvert.length > 0) {
          const convertedLines = linesToConvert.map((line) =>
            convertElementPosition(line, "Line"),
          );
          const lineIdsToRemove = new Set(
            linesToConvert.map((line) => line.id),
          );
          setLines((prev) => [...prev, ...convertedLines]);
          setOffCanvasLines((prev) =>
            prev.filter((el) => !lineIdsToRemove.has(el.id)),
          );
        }

        // Convert texts
        const textsToConvert = currentOffCanvasArrays.texts.filter((text) =>
          isElementInMainCanvas(text, "Text"),
        );
        if (textsToConvert.length > 0) {
          const convertedTexts = textsToConvert.map((text) =>
            convertElementPosition(text, "Text"),
          );
          const textIdsToRemove = new Set(
            textsToConvert.map((text) => text.id),
          );
          setTexts((prev) => [...prev, ...convertedTexts]);
          setOffCanvasTexts((prev) =>
            prev.filter((el) => !textIdsToRemove.has(el.id)),
          );
        }

        // Convert images
        const imagesToConvert = currentOffCanvasArrays.images.filter((image) =>
          isElementInMainCanvas(image, "Image"),
        );
        if (imagesToConvert.length > 0) {
          const convertedImages = imagesToConvert.map((image) =>
            convertElementPosition(image, "Image"),
          );
          const imageIdsToRemove = new Set(
            imagesToConvert.map((image) => image.id),
          );
          setImages((prev) => [...prev, ...convertedImages]);
          setOffCanvasImages((prev) =>
            prev.filter((el) => !imageIdsToRemove.has(el.id)),
          );
        }

        // Save to history if any elements were converted
        if (
          rectanglesToConvert.length > 0 ||
          circlesToConvert.length > 0 ||
          arrowsToConvert.length > 0 ||
          doubleArrowsToConvert.length > 0 ||
          linesToConvert.length > 0 ||
          textsToConvert.length > 0 ||
          imagesToConvert.length > 0
        ) {
          if (typeof saveToHistory === "function") {
            setTimeout(() => saveToHistory(), 0);
          }
        }

        // Update ref for next comparison
        prevCanvasHeightRef.current = canvasHeight;
      }, 0);
      offCanvasConversionTimeoutRef.current = timeoutId;
    });
    offCanvasConversionTimeoutRef.current = rafId;
  }, [
    canvasHeight,
    stageRef,
    offCanvasStageRef,
    // Removed off-canvas arrays from dependencies to prevent unnecessary re-runs
    // The effect only needs to run when canvasHeight changes
    // Current values are accessed via refs (offCanvasArraysRef)
    setRectangles,
    setCircles,
    setArrows,
    setDoubleSidedArrows,
    setLines,
    setTexts,
    setImages,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasLines,
    setOffCanvasTexts,
    setOffCanvasImages,
    saveToHistory,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (offCanvasConversionTimeoutRef.current) {
        if (typeof offCanvasConversionTimeoutRef.current === "number") {
          clearTimeout(offCanvasConversionTimeoutRef.current);
        } else {
          cancelAnimationFrame(offCanvasConversionTimeoutRef.current);
        }
      }
    };
  }, []);

  */

  // Boundary constraint functions are now imported from boundaryUtils.js

  // Wrapper to call ensureAllElementsInBounds with current state
  const checkAllElementsInBounds = () => {
    ensureAllElementsInBounds({
      stageRef,
      texts,
      rectangles,
      circles,
      arrows,
      images,
      lines,
      setTexts,
      setRectangles,
      setCircles,
      setArrows,
      setImages,
      setLines,
    });
  };

  /**
   * NEW: Consolidated handleDragEnd for rectangles, checking which stage it landed on.
   * @param {string} id The ID of the rectangle being dragged.
   * @param {number} newX The new X coordinate relative to the source stage.
   * @param {number} newY The new Y coordinate relative to the source stage.
   * @param {Konva.Stage} sourceStageRef The stage from which the drag originated.
   */

  // Paste: handled once in MyCanvas via window "paste" + createHandleNativePaste (avoid duplicate addImage).

  const handleTransformUpdate = (params) => {
    handleTransformUpdateUtil({
      ...params,
      setTexts,
      setSelectedText,
    });
  };
  // handleElementDragEnd is now imported from dragEndUtils.js
  const handleElementDragEnd = (params) => {
    setGroupDragDisplay?.(null);
    // Guard against the synthetic click that fires immediately after drag end.
    // Without this, the global/background click handlers may clear selection.
    if (justFinishedDragRef) {
      justFinishedDragRef.current = true;
      setTimeout(() => {
        if (justFinishedDragRef) {
          justFinishedDragRef.current = false;
        }
      }, 150);
    }
    // CRITICAL: If multiple elements are selected, this is a group drag scenario
    // We should handle it specially and NOT call the normal drag end handler
    // This prevents individual elements from being moved separately
    if (selectedObjects && selectedObjects.length > 1) {
      const hasTopLayerImageInSelection = (selectedObjects || []).some(
        (obj) =>
          obj?.type === "Image" &&
          ((images || []).some(
            (img) => img.id === obj.id && img.isOnTopLayer === true,
          ) ||
            (offCanvasImages || []).some(
              (img) => img.id === obj.id && img.isOnTopLayer === true,
            )),
      );
      
      const elementPositionsLen =
        individualElementDragStartRef.current?.elementPositions?.length ?? 0;
      const hasGroupDragContext =
        isGroupDraggingRef.current || elementPositionsLen > 0;
      // Multi-select toggle-off can call stopDrag after cancelActiveGroupDrag cleared refs;
      // avoid treating that as a real group drag end (prevents stray sync / wrong path).
      if (!hasGroupDragContext) {
        // Fallback: if multi-select is still active but refs were cleared, rebuild
        // drag context from selected objects so drag-end clamp still runs.
        const rebuiltElementPositions = (selectedObjects || [])
          .map((obj) => {
            if (!obj?.id || !obj?.type) return null;
            const element = getActualElementForDrag(obj);
            if (!element) return null;

            let isOnMainCanvas = false;
            switch (obj.type) {
              case "Rect":
                isOnMainCanvas = rectangles.some((r) => r.id === obj.id);
                break;
              case "Circle":
                isOnMainCanvas = circles.some((c) => c.id === obj.id);
                break;
              case "Image":
                isOnMainCanvas = images.some((img) => img.id === obj.id);
                break;
              case "Text":
                isOnMainCanvas = texts.some((t) => t.id === obj.id);
                break;
              case "Arrow":
                isOnMainCanvas =
                  lines.some((l) => l.id === obj.id) ||
                  arrows.some((a) => a.id === obj.id);
                break;
              case "DoubleSidedArrow":
                isOnMainCanvas =
                  lines.some((l) => l.id === obj.id) ||
                  doubleSidedArrows.some((a) => a.id === obj.id);
                break;
              case "Line":
                isOnMainCanvas = lines.some((l) => l.id === obj.id);
                break;
              default:
                isOnMainCanvas = false;
            }

            return {
              id: obj.id,
              type: obj.type,
              x: Number(element.x) || 0,
              y: Number(element.y) || 0,
              isOnMainCanvas,
            };
          })
          .filter(Boolean);

        if (rebuiltElementPositions.length > 0) {
          individualElementDragStartRef.current = {
            elementPositions: rebuiltElementPositions,
          };
          syncGroupDragPositionsToState();
        }

        setGroupDragPreviews([]);
        groupDragPreviewsRef.current = [];
        isGroupDraggingRef.current = false;
        return;
      }

      const {
        id,
        newX,
        newY,
        type,
        sourceStageRef,
        mouseClientX,
        mouseClientY,
      } = params;

      // Find the dragged element in selectedObjects
      let draggedElement = selectedObjects.find(
        (obj) => obj.id === id && obj.type === type,
      );
      if (!draggedElement) {
        // Unified line/arrow drags can report a different runtime type than selectedObjects.
        draggedElement = selectedObjects.find((obj) => obj.id === id);
      }

      if (draggedElement) {
        const mainStage = stageRef?.current;
        const offStage = offCanvasStageRef?.current;

        if (mainStage && offStage) {
          // CRITICAL FIX: Check if elements have already been moved during handleGroupDragMove
          // If they have, we should NOT call handleCopyGroupBetweenCanvases again
          // Elements should already be in the correct canvas arrays with correct positions

          // Determine which canvas each element is currently on by checking state arrays
          let allOnMainCanvas = true;
          let allOnOffCanvas = true;

          selectedObjects.forEach((obj) => {
            if (!obj || !obj.id || !obj.type) return;

            let isOnMainCanvas = false;
            switch (obj.type) {
              case "Rect":
                isOnMainCanvas = rectangles.some((r) => r.id === obj.id);
                break;
              case "Circle":
                isOnMainCanvas = circles.some((c) => c.id === obj.id);
                break;
              case "Image":
                isOnMainCanvas =
                  images?.some((img) => img.id === obj.id) ?? false;
                break;
              case "Text":
                isOnMainCanvas = texts?.some((t) => t.id === obj.id) ?? false;
                break;
              case "Arrow":
                // CRITICAL FIX: Check lines array FIRST for unified arrow elements
                isOnMainCanvas =
                  lines?.some((l) => {
                    if (l.id !== obj.id) return false;
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true || l.pointerAtEnd === true;
                    return hasStartArrow || hasEndArrow;
                  }) ?? false;
                // Fallback to legacy arrows array if not found in lines
                if (!isOnMainCanvas) {
                  isOnMainCanvas =
                    arrows?.some((a) => a.id === obj.id) ?? false;
                }
                break;
              case "DoubleSidedArrow":
                // CRITICAL FIX: Check lines array FIRST for unified double-sided arrow elements
                isOnMainCanvas =
                  lines?.some((l) => {
                    if (l.id !== obj.id) return false;
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true || l.pointerAtEnd === true;
                    return hasStartArrow && hasEndArrow;
                  }) ?? false;
                // Fallback to legacy doubleSidedArrows array if not found in lines
                if (!isOnMainCanvas) {
                  isOnMainCanvas =
                    doubleSidedArrows?.some((a) => a.id === obj.id) ?? false;
                }
                break;
              case "Line":
                // CRITICAL FIX: Check for both pure lines and unified arrow elements in lines array
                isOnMainCanvas = lines?.some((l) => l.id === obj.id) ?? false;
                break;
            }

            if (isOnMainCanvas) {
              allOnOffCanvas = false;
            } else {
              allOnMainCanvas = false;
            }
          });

          // Calculate group center from current positions
          // IMPORTANT: Use positions from state arrays (which were updated by handleGroupDragMove)
          // not from selectedObjects (which might be stale)
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

          selectedObjects.forEach((obj) => {
            if (!obj || !obj.id || !obj.type) return;

            // Get current element position from state arrays (updated by handleGroupDragMove)
            let element = null;
            let currentX, currentY;

            // Always get from state arrays to get the most current position after group drag move
            switch (obj.type) {
              case "Rect":
                element =
                  rectangles.find((r) => r.id === obj.id) ||
                  offCanvasRectangles.find((r) => r.id === obj.id);
                break;
              case "Circle":
                element =
                  circles.find((c) => c.id === obj.id) ||
                  offCanvasCircles.find((c) => c.id === obj.id);
                break;
              case "Image":
                element =
                  images.find((img) => img.id === obj.id) ||
                  offCanvasImages.find((img) => img.id === obj.id);
                break;
              case "Text":
                element =
                  texts.find((t) => t.id === obj.id) ||
                  offCanvasTexts.find((t) => t.id === obj.id);
                break;
              case "Arrow":
                element =
                  lines.find((l) => {
                    if (l.id !== obj.id) return false;
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true || l.pointerAtEnd === true;
                    return hasStartArrow || hasEndArrow;
                  }) ||
                  offCanvasLines.find((l) => {
                    if (l.id !== obj.id) return false;
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true || l.pointerAtEnd === true;
                    return hasStartArrow || hasEndArrow;
                  }) ||
                  arrows.find((a) => a.id === obj.id) ||
                  offCanvasArrows.find((a) => a.id === obj.id);
                break;
              case "DoubleSidedArrow":
                element =
                  doubleSidedArrows.find((a) => a.id === obj.id) ||
                  offCanvasDoubleSidedArrows.find((a) => a.id === obj.id) ||
                  lines.find((l) => {
                    if (l.id !== obj.id) return false;
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true || l.pointerAtEnd === true;
                    return hasStartArrow && hasEndArrow;
                  }) ||
                  offCanvasLines.find((l) => {
                    if (l.id !== obj.id) return false;
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true || l.pointerAtEnd === true;
                    return hasStartArrow && hasEndArrow;
                  });
                break;
              case "Line":
                element =
                  lines.find((l) => {
                    if (l.id !== obj.id) return false;
                    return (
                      !l.hasStartArrow &&
                      !l.hasEndArrow &&
                      !l.pointerAtBeginning &&
                      !l.pointerAtEnd
                    );
                  }) ||
                  offCanvasLines.find((l) => {
                    if (l.id !== obj.id) return false;
                    return (
                      !l.hasStartArrow &&
                      !l.hasEndArrow &&
                      !l.pointerAtBeginning &&
                      !l.pointerAtEnd
                    );
                  });
                break;
            }

            // CRITICAL FIX: For group center calculation, use Konva node positions
            // State positions might be stale (especially for text elements with negative positions)
            // Konva node positions reflect the actual current position during drag
            const elementNode =
              mainStage?.findOne(`#${obj.id}`) ||
              offStage?.findOne(`#${obj.id}`) ||
              topLayerStageRef?.current?.findOne(`#${obj.id}`);

            if (elementNode) {
              // Use Konva node position (most accurate during drag)
              currentX = elementNode.x();
              currentY = elementNode.y();
            } else if (element) {
              // Fallback to state position if node not found
              currentX = element.x || 0;
              currentY = element.y || 0;
            } else {
              return; // Element not found, skip
            }

            // Get bounding box to calculate group bounds
            const bbox = getElementBoundingBox(
              { ...obj, x: currentX, y: currentY },
              obj.type,
            );
            minX = Math.min(minX, bbox.x);
            minY = Math.min(minY, bbox.y);
            maxX = Math.max(maxX, bbox.x + bbox.width);
            maxY = Math.max(maxY, bbox.y + bbox.height);
          });

          const groupCenterX = (minX + maxX) / 2;
          const groupCenterY = (minY + maxY) / 2;

          // Convert to client coordinates
          const sourceBBox =
            sourceStageRef === offStage
              ? offStage.container().getBoundingClientRect()
              : mainStage.container().getBoundingClientRect();

          const groupCenterClientX = sourceBBox.left + groupCenterX;
          const groupCenterClientY = sourceBBox.top + groupCenterY;

          // Calculate dragged element's client coordinates to check if it crossed boundary
          const draggedElementClientX = sourceBBox.left + newX;
          const draggedElementClientY = sourceBBox.top + newY;

          // Check if group should be moved based on group center OR dragged element position
          // Use mouse position if available, otherwise use global mouse tracker, then dragged element position
          const finalMouseX =
            mouseClientX ??
            globalMousePositionRef.current.x ??
            draggedElementClientX;
          const finalMouseY =
            mouseClientY ??
            globalMousePositionRef.current.y ??
            draggedElementClientY;
          // CRITICAL FIX: Use Group Center for boundary check instead of mouse position
          // This ensures that dragging any part of a large group reliably triggers transfer
          // when the group's center crosses the boundary, matching "selection box" behavior
          const checkX = groupCenterClientX;
          const checkY = groupCenterClientY;

          const { shouldMove, targetCanvas } = shouldMoveGroupToOppositeCanvas(
            checkX,
            checkY,
            selectedObjects,
          );

          // CRITICAL FIX: If elements have already been moved during handleGroupDragMove,
          // they should already be in the correct canvas. Only call handleCopyGroupBetweenCanvases
          // if elements are NOT already on the target canvas.
          const elementsAlreadyOnTargetCanvas =
            (targetCanvas === "main" && allOnMainCanvas) ||
            (targetCanvas === "offCanvas" && allOnOffCanvas);

          if (shouldMove && elementsAlreadyOnTargetCanvas) {
            // Elements are already on the target canvas (moved during handleGroupDragMove)
            // CRITICAL FIX: Reset group drag flag BEFORE syncing positions to allow top-layer image state updates
            isGroupDraggingRef.current = false;
            syncGroupDragPositionsToState();
            setGroupDragPreviews([]);
            groupDragPreviewsRef.current = [];
            return; // Don't call handleCopyGroupBetweenCanvases
          }

          if (shouldMove && !elementsAlreadyOnTargetCanvas) {
            // Prevent multiple concurrent group moves
            if (isGroupMovingRef.current) {
              // CRITICAL FIX: Still sync positions even if group move is in progress
              // This ensures positions are persisted
              isGroupDraggingRef.current = false;
              syncGroupDragPositionsToState();
              return;
            }

            // CRITICAL: Only move group if we're actually at drag end, not during drag
            // Check if we're still in a group drag - if so, this might be a premature onDragEnd
            // Wait a bit to see if another drag move comes in (which would reset isGroupDraggingRef)
            if (isGroupDraggingRef.current) {
              // Cancel any existing pending move
              if (pendingGroupMoveRef.current) {
                clearTimeout(pendingGroupMoveRef.current);
              }

              // Store the move parameters for later
              const moveParams = {
                finalMouseX:
                  mouseClientX ??
                  globalMousePositionRef.current.x ??
                  draggedElementClientX,
                finalMouseY:
                  mouseClientY ??
                  globalMousePositionRef.current.y ??
                  draggedElementClientY,
                id,
                type,
                newX,
                newY,
                targetCanvas,
              };

              // Record when this drag end occurred
              const dragEndTime = Date.now();
              const lastMoveTimeBeforeEnd = lastDragMoveTimeRef.current;

              // Set a longer timeout to check if drag really ended
              // This gives more time for the user to continue dragging
              // If another drag move comes in after this, lastDragMoveTimeRef will be updated
              // If no drag move comes in, we proceed with the move
              const timeoutId = setTimeout(() => {
                // Check if a new drag move came in after this drag end
                // If lastDragMoveTimeRef is greater than lastMoveTimeBeforeEnd, a new move came in, so don't move
                // If lastDragMoveTimeRef is the same or less, no new move came in, so proceed with move
                const timeSinceDragEnd = Date.now() - dragEndTime;
                const newMoveCameIn =
                  lastDragMoveTimeRef.current > lastMoveTimeBeforeEnd;

                if (
                  !isGroupMovingRef.current &&
                  !newMoveCameIn &&
                  timeSinceDragEnd >= 200
                ) {
                  // Reset group drag flag before moving
                  isGroupDraggingRef.current = false;
                  setGroupDragPreviews([]);
                  groupDragPreviewsRef.current = [];
                  pendingGroupMoveRef.current = null;
                  handleCopyGroupBetweenCanvases(
                    null,
                    null,
                    moveParams.finalMouseX,
                    moveParams.finalMouseY,
                    {
                      [moveParams.id]: {
                        x: moveParams.newX,
                        y: moveParams.newY,
                      },
                    },
                  );
                } else {
                  pendingGroupMoveRef.current = null;
                }
              }, 300); // Increased timeout to 300ms to give more time for drag to continue

              pendingGroupMoveRef.current = timeoutId;
              // CRITICAL FIX: Sync positions immediately even if we're deferring the move
              // This ensures top-layer images and other elements have their positions persisted
              // The deferred move will handle canvas transfer, but positions should be synced now
              isGroupDraggingRef.current = false;
              syncGroupDragPositionsToState();
              return; // Don't call normal drag end handler
            }

            // Move the entire group
            // Pass mouse position to preserve drop location, or use global tracker, then dragged element position as fallback
            const finalMouseX =
              mouseClientX ??
              globalMousePositionRef.current.x ??
              draggedElementClientX;
            const finalMouseY =
              mouseClientY ??
              globalMousePositionRef.current.y ??
              draggedElementClientY;
            // Reset group drag flag before moving
            isGroupDraggingRef.current = false;
            setGroupDragPreviews([]);
            groupDragPreviewsRef.current = [];
            handleCopyGroupBetweenCanvases(
              null,
              null,
              finalMouseX,
              finalMouseY,
            );
            return; // Don't call normal drag end handler
          } else {
            // Even if group center hasn't crossed, check if the dragged element itself has crossed
            // If so, we should still move the group to keep them together
            const mainBBox = mainStage.container().getBoundingClientRect();
            const offBBox = offStage.container().getBoundingClientRect();
            const mainCanvasRight = mainBBox.left + mainBBox.width;
            const mainCanvasBottom =
              mainBBox.top +
              fixedStageHeightPx;

            // Check if dragged element has crossed boundary
            const draggedElementTarget = determineTargetCanvas({
              type,
              draggedElement: { x: 0, y: 0 },
              clientX: draggedElementClientX,
              clientY: draggedElementClientY,
              mainBBox,
              offBBox,
              mainCanvasRight,
              mainCanvasBottom,
              sourceStageRef:
                sourceStageRef === offStage ? offStage : mainStage,
              mainStage,
              offCanvasStage: offStage,
              isFromMainCanvas: sourceStageRef === mainStage,
            });

            const draggedElementSourceCanvas =
              sourceStageRef === offStage ? "offCanvas" : "main";

            // If dragged element has crossed boundary, move the entire group
            // But only if elements haven't already been moved during handleGroupDragMove
            if (
              draggedElementTarget &&
              draggedElementTarget !== draggedElementSourceCanvas
            ) {
              // Check if elements are already on the target canvas (moved during handleGroupDragMove)
              const elementsAlreadyOnTargetCanvas =
                (draggedElementTarget === "main" && allOnMainCanvas) ||
                (draggedElementTarget === "offCanvas" && allOnOffCanvas);

              if (elementsAlreadyOnTargetCanvas) {
                // CRITICAL FIX: Reset group drag flag BEFORE syncing positions to allow top-layer image state updates
                isGroupDraggingRef.current = false;
                syncGroupDragPositionsToState();
                setGroupDragPreviews([]);
                groupDragPreviewsRef.current = [];
                return; // Don't call normal drag end handler or move function
              }

              // Prevent multiple concurrent group moves
              if (isGroupMovingRef.current) {
                // CRITICAL FIX: Still sync positions even if group move is in progress
                // This ensures positions are persisted
                isGroupDraggingRef.current = false;
                syncGroupDragPositionsToState();
                return;
              }

              const finalMouseX =
                mouseClientX ??
                globalMousePositionRef.current.x ??
                draggedElementClientX;
              const finalMouseY =
                mouseClientY ??
                globalMousePositionRef.current.y ??
                draggedElementClientY;
              // Reset group drag flag before moving
              isGroupDraggingRef.current = false;
              setGroupDragPreviews([]);
              groupDragPreviewsRef.current = [];
              handleCopyGroupBetweenCanvases(
                null,
                null,
                finalMouseX,
                finalMouseY,
                {
                  [id]: { x: newX, y: newY },
                },
              );
              return; // Don't call normal drag end handler
            } else {
              // Don't sync here - will sync once at the end
              setGroupDragPreviews([]);
              groupDragPreviewsRef.current = [];
            }
          }
        } else {
          // Don't sync here - will sync once at the end
        }
      } else {
        // Don't sync here - will sync once at the end
      }

      // CRITICAL FIX: Sync Konva node positions back to React state arrays
      // This prevents elements from jumping back to original positions when unselected
      // handleGroupDragMove only updates Konva nodes during drag for performance
      // We only reach here if we've determined that the group should NOT be moved between canvases
      // (all the move logic above would have returned early if a move was needed)
      // So we can safely sync positions here

      // CRITICAL: Sync positions ONCE at the end, while group drag flag is still true
      // This ensures useLayoutEffect doesn't run between sync calls and reset positions
      syncGroupDragPositionsToState();

      // CRITICAL FIX: Reset group drag flag AFTER syncing positions
      isGroupDraggingRef.current = false;

      // CRITICAL FIX: After syncing positions, signal all UnifiedLineArrowElement components
      // to clear their lastDraggedPositionRef to prevent stale positions
      window.clearLastDraggedPositions = true;
      setTimeout(() => {
        delete window.clearLastDraggedPositions;
      }, 100);

      // CRITICAL FIX: Update selectedObjects after group drag ends to sync with final positions
      // IMPORTANT: Read positions directly from Konva nodes (source of truth) instead of state arrays
      // State arrays in this closure are stale due to React's async state updates
      if (selectedObjects && setSelectedObjects) {
        const mainStage = stageRef?.current;
        const offStage = offCanvasStageRef?.current;
        const topLayerStage = topLayerStageRef?.current;
        const offCanvasTopLayerStage = offCanvasTopLayerStageRef?.current;

        setSelectedObjects((prevSelectedObjects) => {
          return prevSelectedObjects.map((obj) => {
            // Find the Konva node to get the current position (source of truth)
            let elementNode = null;

            // Check top layer stage first for images
            if (obj.type === "Image" && topLayerStage) {
              elementNode = topLayerStage.findOne(`#${obj.id}`);
            }

            // If not found on top layer, check main and off-canvas stages
            if (!elementNode) {
              elementNode =
                mainStage?.findOne(`#${obj.id}`) ||
                offStage?.findOne(`#${obj.id}`);
            }

            if (elementNode) {
              // Get position directly from Konva node
              const currentX = elementNode.x();
              const currentY = elementNode.y();

              // Update position from Konva node
              return {
                ...obj,
                x: currentX,
                y: currentY,
              };
            }

            return obj;
          });
        });

        // Also update selectedObject if needed
        if (selectedObject && setSelectedObject) {
          setSelectedObject((prevSelectedObject) => {
            if (!prevSelectedObject) return prevSelectedObject;

            // Find the Konva node to get the current position
            let elementNode = null;

            // Check top layer stage first for images
            if (prevSelectedObject?.type === "Image" && topLayerStage) {
              elementNode = topLayerStage.findOne(`#${prevSelectedObject?.id}`);
            }

            // If not found on top layer, check main and off-canvas stages
            if (!elementNode) {
              elementNode =
                mainStage?.findOne(`#${prevSelectedObject?.id}`) ||
                offStage?.findOne(`#${prevSelectedObject?.id}`);
            }

            if (elementNode) {
              // Get position directly from Konva node
              const currentX = elementNode.x();
              const currentY = elementNode.y();

              // Update position from Konva node
              return {
                ...prevSelectedObject,
                x: currentX,
                y: currentY,
              };
            }

            return prevSelectedObject;
          });
        }
      }

      // Clear group drag previews
      setGroupDragPreviews([]);
      groupDragPreviewsRef.current = [];
      return;
    }

    // Normal drag end behavior (only for single element drags, not group drags)
    handleElementDragEndUtil({
      ...params,
      stageRef,
      offCanvasStageRef,
      canvasHeight,
      setSelectedObject,
      setSelectedArrow,
      setSelectedLine,
      setSelectedDoubleSidedArrow,
      setSelectedText, // Pass setSelectedText for text elements
      transformerRef,
      offCanvasTransformerRef,
      saveToHistory,
      handleImageDragEnd,
      handleRectangleDragEnd,
      handleCircleDragEnd,
      handleArrowDragEnd,
      handleDoubleSidedArrowDragEnd,
      handleLineDragEnd,
      handleTextDragEnd,
      setPendingChanges, // Pass setPendingChanges to mark pending changes after drag transfer
      pendingChangesRef, // Pass ref to update synchronously
    });
  };

  // handlePartClick is now imported from canvasElementUtils.js
  const handlePartClick = (clickedPart, typeKey, isNote, textId) => {
    handlePartClickUtil({
      clickedPart,
      typeKey,
      isNote,
      textId,
      setEditTextBoxContent,
      setSelectedObject,
      setShowEditTask,
      setShowEditNote,
      setShowTextBox,
      setTexts,
      setSelectedText,
      setShowQtyUnitOptions,
    });
  };

  // Panning state - declared early so it can be used in disableInteraction
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPos, setLastPanPos] = useState({ x: 0, y: 0 });
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [spacePressed, setSpacePressed] = useState(false);
  // Selection box state for drag-to-select
  const [selectionBox, setSelectionBox] = useState(null); // { startX, startY, endX, endY }
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBoxStage, setSelectionBoxStage] = useState(null); // Track which stage the selection box is on ('main' or 'off-canvas')
  // Track if Select All button was explicitly clicked (to show enabled state)
  const [selectAllButtonClicked, setSelectAllButtonClicked] = useState(false);
  // Temporary state to show blue background for 1 second when Select All button is clicked
  const [selectAllButtonClickedFeedback, setSelectAllButtonClickedFeedback] =
    useState(false);
  // Crosshair position for paste mode
  const [crosshairPos, setCrosshairPos] = useState({ x: 0, y: 0 });
  // Main canvas center position for resize handle alignment
  const [mainCanvasCenterX, setMainCanvasCenterX] = useState(
    window.innerWidth / 2,
  );
  const [isNearResizeHandle, setIsNearResizeHandle] = useState(false);
  const [circleTransformerKey, setCircleTransformerKey] = useState(0);
  const prevMaintainAspectRatioRef = useRef(null); // Track previous maintainAspectRatio value for circles
  const [rectangleTransformerKey, setRectangleTransformerKey] = useState(0);
  const prevRectMaintainAspectRatioRef = useRef(null); // Track previous maintainAspectRatio value for rectangles
  const resizeHandleStageRef = useRef(null);
  // Add state for resizing - declared early so it can be used in useEffect below
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(null);
  const [initialHeight, setInitialHeight] = useState(null);
  // Global mouse position tracker for accurate drag end coordinates
  const globalMousePositionRef = useRef({ x: 0, y: 0 });
  // Flag to track if we're currently in a group drag
  const isGroupDraggingRef = useRef(false);
  const groupDragPointerStartRef = useRef(null);
  // Source stage for the active group drag – used to feed setPointersPositions so Konva
  // keeps firing dragmove even when the pointer crosses into another stage's container
  const groupDragSourceStageRef = useRef(null);
  // Stage that owns the active individual drag or transformer — window pointer events
  // call setPointersPositions on it so Konva keeps dragmove/transform firing when the
  // cursor crosses into another Stage's DOM container (main ↔ off-canvas).
  const crossStagePointerFeedStageRef = useRef(null);
  // Synced from selectionPointerIsolationSide so Konva ref callbacks read latest value
  const selectionPointerIsolationSideRef = useRef(null);
  // Restores pointer-events on other stages (see crossStageGesture.js; no DOM pointer capture)
  const crossStageGestureCleanupRef = useRef(null);

  const crossStageGestureBegin = useCallback((sourceStage, nativeEvt) => {
    beginCrossStageGesture(sourceStage, nativeEvt, {
      crossStagePointerFeedStageRef,
      crossStageGestureCleanupRef,
      stageRef,
      offCanvasStageRef,
      topLayerStageRef,
      offCanvasTopLayerStageRef,
    });
  }, []);

  const crossStageGestureEnd = useCallback(() => {
    endCrossStageGesture({
      crossStageGestureCleanupRef,
      crossStagePointerFeedStageRef,
    });
  }, []);

  // Initial bbox of the blue wrapper Rect(s) when a group drag starts.
  // Used to imperatively move the wrapper during individual-element group drag so the
  // selection box tracks the pointer without waiting for React re-renders.
  const wrapperNodeInitialAttrsRef = useRef(null);

  // CRITICAL: Expose isGroupDraggingRef globally so child components can access it
  // This is needed for UnifiedLineArrowElement and other components that need to check group drag state
  useEffect(() => {
    window.isGroupDraggingRef = isGroupDraggingRef;
    return () => {
      delete window.isGroupDraggingRef;
    };
  }, []);

  // Flag to prevent multiple concurrent group moves
  const isGroupMovingRef = useRef(false);
  // CRITICAL: Track when we've fired mousedown on a draggable element to prevent Background onClick
  const recentDraggableElementMousedownRef = useRef({
    nodeId: null,
    timestamp: 0,
  });
  // CRITICAL: Skip clearing selection on the next stage/background click when we just finished a transform.
  // Konva can fire a click with target=stage after transformend, which would otherwise deselect the element.
  const justFinishedTransformRef = useRef(false);
  // Time-based fallback: skip clear if a transform started recently (handles event ordering)
  const lastTransformStartTimeRef = useRef(0);
  // Skip clearing selection on the click that can fire after drag end (transform then drag → drag end would otherwise deselect)
  const justFinishedDragRef = useRef(false);
  // Track where current draw gesture started ("main" | "off-canvas")
  const drawingOriginCanvasRef = useRef(null);
  const lastGlobalDrawUpKeyRef = useRef("");
  const lastOffCanvasForwardedUpRef = useRef({
    ts: 0,
    x: null,
    y: null,
  });
  // When an image drag ends, store { id, time } so we can skip clearing selection if the click clears the same image
  const lastDraggedImageRef = useRef({ id: null, time: 0 });
  // Track the last time a drag move occurred
  const lastDragMoveTimeRef = useRef(0);
  // Track pending deferred group moves to prevent duplicates
  const pendingGroupMoveRef = useRef(null);
  // Throttle selectedObjects/selectedObject sync during group drag to prevent
  // heavy React re-renders from making top-layer images appear to "float".
  const groupDragSelectionSyncRef = useRef({ lastTime: 0, ms: 33 });
  const groupDragLastDeltaRef = useRef({ x: 0, y: 0 });
  // State to force re-render of selection boxes during drag
  const [dragUpdateTrigger, setDragUpdateTrigger] = useState(0);
  // PERFORMANCE: Debounce ref for drag update trigger to reduce re-renders
  const dragUpdateDebounceRef = useRef(null);
  const recentTopLayerManualCloneRef = useRef({
    ts: 0,
    clientX: null,
    clientY: null,
  });

  // Calculate main canvas center X position for resize handle alignment
  useEffect(() => {
    const updateMainCanvasCenter = () => {
      if (mainStageContainerRef.current) {
        const rect = mainStageContainerRef.current.getBoundingClientRect();
        // Calculate center of the container (which contains the stage)
        const centerX = rect.left + rect.width / 2;
        setMainCanvasCenterX(centerX);
      }
    };

    updateMainCanvasCenter();
    window.addEventListener("resize", updateMainCanvasCenter);
    // Also update periodically to catch panning changes (stagePos changes)
    const interval = setInterval(updateMainCanvasCenter, 100);

    return () => {
      window.removeEventListener("resize", updateMainCanvasCenter);
      clearInterval(interval);
    };
  }, [stagePos]);

  // Track mouse position to detect when near resize handle
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      // Update global mouse position tracker for accurate drag end coordinates
      globalMousePositionRef.current = {
        x: e.clientX,
        y: e.clientY,
      };
      if (parseInt(canvasHeight) === 0) {
        setIsNearResizeHandle(false);
        return;
      }

      const resizeHandleY = Math.max(
        0,
        Math.max(
          parseInt(canvasHeight) <= 1
            ? 10
            : parseInt(canvasHeight) <= 5
              ? 10
              : parseInt(canvasHeight) < 30
                ? 50
                : 20,
          fixedStageHeightPx,
        ) -
          RESIZE_BOTTOM_SAFE +
          stagePos.y,
      );

      // Get zoom level from inline style (same method as in resize handle render)
      const getZoomLevel = () => {
        try {
          const elements = document.querySelectorAll('[style*="transform"]');
          for (const el of elements) {
            const inlineStyle = el.getAttribute("style") || "";
            const scaleMatch = inlineStyle.match(
              /transform:\s*scale\(([^)]+)\)/,
            );
            if (scaleMatch && scaleMatch[1]) {
              const zoom = parseFloat(scaleMatch[1]);
              if (!isNaN(zoom) && zoom > 0) {
                return zoom;
              }
            }
          }
        } catch (e) {
          // Fallback to 1 if we can't detect zoom
        }
        return 1;
      };
      const zoomLevel = getZoomLevel();

      // Check if mouse is near resize handle
      // Mouse coordinates (e.clientX, e.clientY) and handle position (mainCanvasCenterX, resizeHandleY)
      // are both in viewport pixels, so they should match directly
      // However, when zoomed, we need to account for any coordinate system differences
      const distanceX = Math.abs(e.clientX - mainCanvasCenterX);
      const distanceY = Math.abs(e.clientY - resizeHandleY);
      const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

      // Use a generous threshold - when zoomed, the effective threshold should be similar
      // The hit area is 50px * zoomLevel in Stage coordinates, but appears as 50px in viewport
      // due to inverse transform, so we use a fixed viewport threshold
      const threshold = 100; // Large threshold to ensure pointer events are enabled

      const isNear = distance < threshold;

      // Debug: log when close to handle
      if (distance < 100) {
      }

      setIsNearResizeHandle(isNear);

      // Synchronously update pointer-events style directly on DOM to avoid React render delay
      // This ensures resize handle is immediately interactive when mouse approaches
      // Only try to update if we're near the handle or resizing (to avoid unnecessary warnings)
      if (isNear || isResizing) {
        if (resizeHandleStageRef.current?.content) {
          const container = resizeHandleStageRef.current.content;
          if (container) {
            const shouldEnable = isNear || isResizing;
            container.style.pointerEvents = shouldEnable ? "auto" : "none";
            if (shouldEnable && distance < 100) {
            }
          } else {
          }
        }
        // Only warn if we actually need the ref (when near or resizing)
        // Don't warn if ref is null when we're not near the handle
      }
    };

    window.addEventListener("mousemove", handleGlobalMouseMove, {
      passive: true,
    });
    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [canvasHeight, stagePos.y, mainCanvasCenterX, isResizing]);

  // Monitor selectedObjects and clear selectAllButtonClicked flag when appropriate
  // The flag should only stay true when ALL elements are selected
  useEffect(() => {
    if (!selectAllButtonClicked) {
      return; // No need to check if flag is already false
    }

    // Get reference element to determine which canvas we're on
    const referenceElement =
      selectedObject ||
      (selectedObjects && selectedObjects.length > 0
        ? selectedObjects[0]
        : null);

    if (!referenceElement) {
      // No elements selected, clear the flag
      setSelectAllButtonClicked(false);
      return;
    }

    // Get all elements from the same canvas
    const allElementsFromSameCanvas =
      getAllElementsFromSameCanvas(referenceElement);

    if (allElementsFromSameCanvas.length === 0) {
      // No elements in canvas, clear the flag
      setSelectAllButtonClicked(false);
      return;
    }

    // Check if all elements are selected
    let allSelected = false;
    if (
      isMultiSelectMode &&
      individuallySelectedIds &&
      individuallySelectedIds.size > 0
    ) {
      // In multi-select mode, check if all elements are in individuallySelectedIds
      allSelected = allElementsFromSameCanvas.every((el) =>
        individuallySelectedIds.has(el.id),
      );
    } else if (selectedObjects && selectedObjects.length > 0) {
      // Check if all elements from same canvas are in selectedObjects
      const selectedIds = new Set(
        selectedObjects.map((obj) => `${obj.id}-${obj.type}`),
      );
      allSelected = allElementsFromSameCanvas.every((el) =>
        selectedIds.has(`${el.id}-${el.type}`),
      );
    }

    // If not all elements are selected, clear the flag
    if (!allSelected) {
      setSelectAllButtonClicked(false);
    }
  }, [
    selectedObjects,
    selectedObject,
    isMultiSelectMode,
    individuallySelectedIds,
    selectAllButtonClicked,
  ]);

  // Compute disableInteraction: true if any drawing action is in progress or panning is active
  const disableInteraction =
    isDrawingLine ||
    isDrawingArrow ||
    isDrawingRect ||
    isDrawingDoubleSidedArrow ||
    isDrawingCircle ||
    isDrawingImage ||
    spacePressed ||
    isPanning;

  const [dragPreview, setDragPreview] = useState(null);
  const [groupDragPreviews, setGroupDragPreviews] = useState([]);
  const dragPreviewStateRef = useRef(null);
  const groupDragPreviewsRef = useRef([]);
  // When a line/arrow is being dragged, store context so window mousemove can keep updating preview
  // (main stage often stops receiving Konva events when pointer is over off-canvas)
  const lineDragPreviewContextRef = useRef(null);
  // Last source-stage position per line id: when pointer moves to off-canvas we pass this to the
  // source-stage line so we never pass off-canvas coords and Konva doesn't fire dragEnd.
  const lineLastSourcePositionRef = useRef(Object.create(null));
  // When line is in cross-stage drag, keep source hidden until drag end (sticky; avoids "copy" showing when holding still)
  const lineSourceHiddenForDragRef = useRef(Object.create(null));
  // Hysteresis: last target for line cross-stage so we don't flip at boundary and cause vibration
  const lineDragPreviewTargetRef = useRef(null);
  // Last committed position for line cross-stage; only update preview when moved > 0.5px to reduce jitter
  const lineDragLastCommittedRef = useRef({ x: null, y: null });
  // Smoothed ghost position (exponential moving average) to reduce cursor noise / fluctuation
  const lineDragSmoothedRef = useRef({ x: null, y: null });
  // CRITICAL: Throttle dragPreview updates using requestAnimationFrame to improve performance
  const dragPreviewUpdateFrameRef = useRef(null);
  // Text cross-canvas: when pointer is over off-canvas, main stage may not get dragMove.
  // Store context so window mousemove can keep calling handleDragPreview (main→off→main follows cursor).
  const textDragPreviewContextRef = useRef(null);
  // Text: only show ghost on main when pointer has left main (main→off→main). Same-stage-only drag = no ghost (avoids white rect offset).
  const textDragHasLeftMainRef = useRef(false);

  // CRITICAL FIX: Line/arrow ghosts must track the cursor "instantly" during cross-stage drags.
  // Throttling too aggressively makes the ghost appear behind the pointer.
  // Keep it close to frame-rate so it feels like rectangles.
  const lineDragPreviewThrottleRef = useRef({ lastTime: 0, ms: 0 }); // rAF already limits to ~60fps

  const clearDragPreview = React.useCallback(() => {
    dragPreviewStateRef.current = null;
    lineDragPreviewContextRef.current = null;
    textDragPreviewContextRef.current = null;
    textDragHasLeftMainRef.current = false;
    lineLastSourcePositionRef.current = Object.create(null);
    lineSourceHiddenForDragRef.current = Object.create(null);
    lineDragPreviewTargetRef.current = null;
    lineDragLastCommittedRef.current = { x: null, y: null };
    lineDragSmoothedRef.current = { x: null, y: null };
    // CRITICAL FIX: Reset throttle timer when clearing preview
    lineDragPreviewThrottleRef.current.lastTime = 0;
    setDragPreview(null);
    setGroupDragPreviews([]);
    groupDragPreviewsRef.current = [];
  }, []);
  const handleDragPreview = React.useCallback(
    ({ event, newAttrs, sourceStageRef, element, type, isOffCanvas }) => {
      if (
        !event?.evt ||
        !sourceStageRef ||
        !stageRef?.current ||
        !offCanvasStageRef?.current
      ) {
        return;
      }

      const clientX = event.evt.clientX;
      const clientY = event.evt.clientY;

      // Store context for line/arrow so window mousemove can keep updating when pointer is over off-canvas
      const isLineArrow =
        type === "Line" || type === "Arrow" || type === "DoubleSidedArrow";
      if (isLineArrow) {
        const attrs = newAttrs || { ...element, x: element?.x, y: element?.y };
        const prevCtx = lineDragPreviewContextRef.current;
        lineDragPreviewContextRef.current = {
          newAttrs: attrs,
          sourceStageRef,
          element,
          type,
          isOffCanvas,
          // Preserve grab-offset across updates; needed when Konva dragMove stops.
          grabOffsetX: prevCtx?.grabOffsetX,
          grabOffsetY: prevCtx?.grabOffsetY,
          _grabOffsetId: prevCtx?._grabOffsetId,
        };
        // Do NOT update lineLastSourcePositionRef here - update only when pointer is on source stage (see below).
        // This prevents overwriting with wrong values when pointer is on off-canvas (window mousemove path).
        if (LINE_DRAG_PREVIEW_DEBUG) {
        }
      }
      // Text: store context so window mousemove can keep updating when pointer is over off-canvas
      // (main stage often stops receiving dragMove once pointer leaves; without this, main→off→main leaves ghost stuck)
      if (type === "Text") {
        const attrs = newAttrs || { ...element, x: element?.x, y: element?.y };
        textDragPreviewContextRef.current = {
          newAttrs: attrs,
          sourceStageRef,
          element,
          type: "Text",
          isOffCanvas,
        };
      }

      if (clientX === undefined || clientY === undefined) {
        return;
      }

      const mainStage = stageRef.current;
      const offStage = offCanvasStageRef.current;

      const mainBBox = mainStage.container().getBoundingClientRect();
      const offBBox = offStage.container().getBoundingClientRect();
      const sourceBBox = sourceStageRef?.container()?.getBoundingClientRect?.();

      if (!sourceBBox) {
        return;
      }

      // Line/arrow: compute the initial grab offset (cursor relative to source-stage group origin).
      // We reuse this during cross-stage preview updates so the ghost follows the pointer with
      // the same offset Konva uses for the real draggable node.
      if (isLineArrow && lineDragPreviewContextRef.current) {
        const ctx = lineDragPreviewContextRef.current;
        const id =
          (newAttrs && (newAttrs.id ?? newAttrs?.data?.id)) || element?.id;

        const srcX = newAttrs?.x ?? element?.x ?? 0;
        const srcY = newAttrs?.y ?? element?.y ?? 0;
        const grabOffsetX = clientX - (sourceBBox.left + srcX);
        const grabOffsetY = clientY - (sourceBBox.top + srcY);

        // Only compute once per drag target (id changes are treated as new drags).
        if (ctx._grabOffsetId !== id) {
          ctx._grabOffsetId = id;
          ctx.grabOffsetX = grabOffsetX;
          ctx.grabOffsetY = grabOffsetY;
        }
      }

      // CRITICAL FIX: Check if this is a top-layer image BEFORE determining sourceTarget
      // Top-layer images use topLayerStageRef, which is different from mainStage/offStage
      const isTopLayerImage =
        element?.isOnTopLayer === true || newAttrs?.isOnTopLayer === true;

      // CRITICAL: Determine sourceTarget based on which canvas the image is actually on
      // For top-layer images, check if they're in offCanvasImages or images array
      let sourceTarget = "main"; // Default to main

      if (isTopLayerImage) {
        // Check if the image is from off-canvas by looking in offCanvasImages array
        const isFromOffCanvas = offCanvasImages.some(
          (img) =>
            img.id === (element?.id || newAttrs?.id) &&
            img.isOnTopLayer === true,
        );
        sourceTarget = isFromOffCanvas ? "offCanvas" : "main";

      } else {
        // For non-top-layer images, determine by comparing sourceStageRef
        sourceTarget =
          sourceStageRef === mainStage
            ? "main"
            : sourceStageRef === offStage
              ? "offCanvas"
              : "main"; // Default to main
      }

      let target = sourceTarget;

      // Use actual stage bounding box for "within main" (match lineRenderer/circleRenderer).
      // Previously used 45% viewport width which could make withinMain false when pointer was
      // over the main stage, so off→main line drag never showed the ghost.
      const withinMain =
        clientX >= mainBBox.left &&
        clientX <= mainBBox.right &&
        clientY >= mainBBox.top &&
        clientY <= mainBBox.bottom;

      const withinOff =
        clientX >= offBBox.left &&
        clientX <= offBBox.right &&
        clientY >= offBBox.top &&
        clientY <= offBBox.bottom;

      // Ambiguous overlap zone (both stages report true): keep source target
      // so drag-end does not falsely treat this as a cross-stage drop.
      if (withinMain && withinOff) {
        target = sourceTarget;
      } else if (withinMain) {
        target = "main";
      } else if (withinOff) {
        target = "offCanvas";
      }

      // Line/arrow: hysteresis in a single strip around the seam (main right edge) so we don't flip every frame
      const LINE_DRAG_BOUNDARY_MARGIN = 12;
      const boundaryX = mainBBox.right;
      if (isLineArrow && (target === "main" || target === "offCanvas")) {
        const inBoundaryStrip =
          clientX >= boundaryX - LINE_DRAG_BOUNDARY_MARGIN &&
          clientX <= boundaryX + LINE_DRAG_BOUNDARY_MARGIN;
        const lastTarget =
          lineDragPreviewTargetRef.current ??
          dragPreviewStateRef.current?.target;
        // Only lock to last target when the cursor is ambiguous (not clearly inside either stage).
        // If the cursor is actually withinMain/withinOff, don't override it; this removes the
        // off->main preview "flip" that looks like fluctuation.
        const cursorIsInsideAnyStage = withinMain || withinOff;
        if (inBoundaryStrip && !cursorIsInsideAnyStage && lastTarget != null) {
          target = lastTarget;
        } else {
          lineDragPreviewTargetRef.current = target;
        }
      }

      // Line/arrow: only update frozen source position when pointer is on source stage (like circle).
      // Keeps line stable when dragging to off-canvas and prevents vibration from dual update sources.
      const withinSource =
        sourceTarget === "main"
          ? withinMain
          : sourceTarget === "offCanvas"
            ? withinOff
            : false;
      if (isLineArrow && withinSource) {
        const attrs = newAttrs || { ...element, x: element?.x, y: element?.y };
        const lid = element?.id ?? attrs?.id;
        if (lid) {
          lineLastSourcePositionRef.current[lid] = {
            x: attrs.x ?? 0,
            y: attrs.y ?? 0,
          };
        }
      }

      // CRITICAL FIX: For top-layer images, allow target to be "offCanvas" when dragging off canvas
      // Previously, we forced target to "main", which prevented preview from showing on off-canvas
      // Only force "main" if the cursor is actually over the main canvas
      if (isTopLayerImage && !withinOff) {
        // If not over off-canvas, default to main (for top-layer images on main canvas)
        target = "main";
      }

      // Restriction: disable cross-canvas drag preview entirely.
      if (target !== sourceTarget) {
        clearDragPreview();
        return;
      }

      // Text: mark that pointer has left MAIN so we know to show ghost when back on main (main→off→main).
      // IMPORTANT: Only apply this for drags that STARTED on main. Off-canvas text drags should NOT
      // enable the ghost just because the pointer is on the off-canvas stage.
      if (
        type === "Text" &&
        sourceTarget === "main" &&
        target === "offCanvas"
      ) {
        textDragHasLeftMainRef.current = true;
      }

      // CRITICAL: For line/arrow same-stage drag (main-only or off-only), clear preview and restore position.
      // We MUST call setDragPreview(null) here so the ghost disappears when pointer returns to source stage
      // (e.g. off-canvas line dragged to main, then back to off-canvas). Otherwise dragPreview state stays
      // set and the ghost stays visible / line appears stuck at boundary.
      if (isLineArrow && target === sourceTarget) {
        const lid = element?.id ?? newAttrs?.id;
        if (lid) {
          lineSourceHiddenForDragRef.current[lid] = false;
          // Store current position (from context when returning from other stage)
          const sx = newAttrs?.x ?? element?.x ?? 0;
          const sy = newAttrs?.y ?? element?.y ?? 0;
          lineLastSourcePositionRef.current[lid] = { x: sx, y: sy };
          // Restore selectedObject to source-stage coords so line is not stuck at boundary
          if (setSelectedObject && lineLastSourcePositionRef.current[lid]) {
            const last = lineLastSourcePositionRef.current[lid];
            setSelectedObject((prev) =>
              prev?.id === lid
                ? {
                    ...prev,
                    x: last.x,
                    y: last.y,
                    _isDragging: true,
                    _previewTargetStage: sourceTarget,
                  }
                : prev,
            );
          }
        }
        dragPreviewStateRef.current = null;
        lineDragLastCommittedRef.current = { x: null, y: null };
        lineDragSmoothedRef.current = { x: null, y: null };
        setDragPreview(null);
        return;
      }

      // CRITICAL FIX: For top-layer images, only show dragPreview when dragging to off-canvas
      // Showing preview on main canvas interferes with the drag operation.
      // For most element types, only show preview when dragging between canvases (target !== sourceTarget).
      // EXCEPTION: Text — show ghost on main only when pointer has left main (main→off→main), so ghost follows cursor.
      // Same-stage-only text drag: do NOT show ghost (only real element); avoids white rect offset from transformer.
      if (target === sourceTarget && type !== "Text") {
        // Same canvas drag - don't show preview. Match circle: for line/arrow only clear when
        // pointer is actually OVER the source stage. If pointer is in gap or over other stage,
        // keep preview so it doesn't vanish (circle never gets handleDragPreview when pointer
        // is on other stage, so we must not clear in that case for line either).
        const withinSource =
          sourceTarget === "main"
            ? withinMain
            : sourceTarget === "offCanvas"
              ? withinOff
              : false;
        if (isLineArrow && dragPreviewStateRef.current && !withinSource) {
          // Pointer not on source stage - keep existing preview (do not clear)
          if (LINE_DRAG_PREVIEW_DEBUG) {
          }
          return;
        }
        if (dragPreviewStateRef.current) {
          // CRITICAL: For line/arrow, do NOT clear lineDragPreviewContextRef here.
          // When the pointer moves to off-canvas, Konva stops firing dragMove; only window
          // mousemove can update. If we clear the context now, that handler has nothing to use.
          if (isLineArrow) {
            // Store last source-stage position so when pointer moves to off-canvas we pass it to the
            // source-stage line and never pass off-canvas coords (prevents Konva dragEnd).
            const lid = element?.id ?? newAttrs?.id;
            if (lid) {
              const sx = newAttrs?.x ?? element?.x ?? 0;
              const sy = newAttrs?.y ?? element?.y ?? 0;
              lineLastSourcePositionRef.current[lid] = { x: sx, y: sy };
            }
            // Pointer is back on source stage: show source line again (stop hiding it)
            if (lid) lineSourceHiddenForDragRef.current[lid] = false;
            // CRITICAL: Restore selectedObject position to source-stage coords so line is not stuck at boundary.
            // When dragging off-canvas → main → back to off-canvas, selectedObject had main-stage coords; restore last source position.
            if (
              lid &&
              setSelectedObject &&
              lineLastSourcePositionRef.current[lid]
            ) {
              const last = lineLastSourcePositionRef.current[lid];
              setSelectedObject((prev) =>
                prev?.id === lid
                  ? {
                      ...prev,
                      x: last.x,
                      y: last.y,
                      _isDragging: true,
                      _previewTargetStage: sourceTarget,
                    }
                  : prev,
              );
            }
            if (LINE_DRAG_PREVIEW_DEBUG) {
            }
            dragPreviewStateRef.current = null;
            lineDragLastCommittedRef.current = { x: null, y: null };
            lineDragSmoothedRef.current = { x: null, y: null };
            setDragPreview(null);
          } else {
            clearDragPreview();
          }
        }
        return;
      }

      // Text: same-stage drags should NOT show a ghost.
      // Exception: main→off→main needs a ghost on main after leaving main once, because main stage
      // can stop receiving dragMove while pointer is over off-canvas.
      if (target === sourceTarget && type === "Text") {
        if (DEBUG_TEXT_DRAG_ISSUE) {
        }
        // Off-canvas → off-canvas (and any non-main same-stage): never show ghost.
        if (sourceTarget !== "main") {
          if (dragPreviewStateRef.current) {
            dragPreviewStateRef.current = null;
            setDragPreview(null);
          }
          return;
        }
        // Main → main: only show ghost if we previously left main during this drag.
        if (!textDragHasLeftMainRef.current) {
          if (dragPreviewStateRef.current) {
            dragPreviewStateRef.current = null;
            setDragPreview(null);
          }
          return;
        }
      }

      const mergedElement = { ...(element || {}), ...(newAttrs || {}) };

      const newX = mergedElement.x ?? 0;
      const newY = mergedElement.y ?? 0;

      // CRITICAL FIX: For top-layer images, calculate coordinates based on target canvas
      // When dragging to off-canvas, convert coordinates from topLayerStage to offCanvasStage
      // When dragging from off-canvas to main canvas, convert coordinates from offStage to mainStage
      let targetX, targetY;
      if (isTopLayerImage) {
        // Check if the image is from off-canvas (in offCanvasImages) or main canvas (in images)
        const isFromOffCanvas = offCanvasImages.some(
          (img) => img.id === mergedElement.id && img.isOnTopLayer === true,
        );

        if (target === "offCanvas") {
          // Top-layer image being dragged to off-canvas - convert coordinates
          // If from main canvas, convert from topLayerStage/mainStage to offCanvasStage
          // If from off-canvas, coordinates are already relative to offStage
          if (!isFromOffCanvas) {
            // From main canvas to off-canvas - convert coordinates
            const absX = sourceBBox.left + newX;
            const absY = sourceBBox.top + newY;
            targetX = absX - offBBox.left;
            targetY = absY - offBBox.top;
          } else {
            // From off-canvas to off-canvas (staying in off-canvas) - use position directly
            targetX = newX;
            targetY = newY;
          }
        } else {
          // Top-layer image being dragged to main canvas
          if (isFromOffCanvas) {
            // From off-canvas to main canvas - convert coordinates from offStage to mainStage
            const absX = sourceBBox.left + newX;
            const absY = sourceBBox.top + newY;
            targetX = absX - mainBBox.left;
            targetY = absY - mainBBox.top;
          } else {
            // From main canvas to main canvas - use the new position directly
            // These coordinates are already relative to the topLayerStage/mainStage
            targetX = newX;
            targetY = newY;
          }
        }
      } else if (
        type === "Text" &&
        target === "main" &&
        sourceTarget === "main"
      ) {
        // Text on main: ghost must use stage coordinates so it aligns with the real text node
        // (container pixels would misalign when stage has zoom/pan and cause white rect to appear offset from transformer)
        const mainStage = stageRef?.current;
        if (mainStage) {
          const scaleX = mainStage.scaleX();
          const scaleY = mainStage.scaleY();
          const stageX = mainStage.x();
          const stageY = mainStage.y();
          targetX = (clientX - mainBBox.left) / scaleX - stageX;
          targetY = (clientY - mainBBox.top) / scaleY - stageY;
        } else {
          targetX = clientX - mainBBox.left;
          targetY = clientY - mainBBox.top;
        }
      } else if (target === sourceTarget) {
        // Same canvas drag for non-top-layer image - use the new position directly
        targetX = newX;
        targetY = newY;
      } else {
        // Cross-canvas drag
        const targetBBox = target === "main" ? mainBBox : offBBox;
        const isLineArrowCross = isLineArrow && target !== sourceTarget;
        if (isLineArrowCross) {
          // Line/arrow cross-stage:
          // Konva dragMove for lines/arrows may stop once the pointer is over the other stage.
          // When that happens we rely on window mousemove, which only gives live clientX/clientY.
          // So compute the preview position from the cursor position on the TARGET stage.
          //
          const cursorX = clientX - targetBBox.left;
          const cursorY = clientY - targetBBox.top;
          const ctx = lineDragPreviewContextRef.current;
          const grabOffsetX = ctx?.grabOffsetX ?? 0;
          const grabOffsetY = ctx?.grabOffsetY ?? 0;
          targetX = cursorX - grabOffsetX;
          targetY = cursorY - grabOffsetY;
        } else if (
          type === "Text" &&
          target === "offCanvas" &&
          sourceTarget === "main"
        ) {
          // Text main→off: keep the exact grab point under the cursor.
          // Compute where the text's top-left is in absolute (viewport) pixels on the SOURCE
          // canvas at this moment, then preserve the offset between that top-left and the cursor
          // when mapping into the TARGET (off-canvas) coordinates.
          const sourceAbsLeft = sourceBBox.left + newX;
          const sourceAbsTop = sourceBBox.top + newY;
          const grabOffsetX = clientX - sourceAbsLeft;
          const grabOffsetY = clientY - sourceAbsTop;

          const cursorX = clientX - targetBBox.left;
          const cursorY = clientY - targetBBox.top;
          targetX = cursorX - grabOffsetX;
          targetY = cursorY - grabOffsetY;
        } else if (
          type === "Text" &&
          target === "main" &&
          sourceTarget === "offCanvas"
        ) {
          // Text off→main: keep the exact grab point under the cursor, accounting for main stage pan/zoom.
          // 1) On the SOURCE (off-canvas), compute the text top-left in absolute pixels.
          const sourceAbsLeft = sourceBBox.left + newX;
          const sourceAbsTop = sourceBBox.top + newY;
          // 2) The grab offset is the distance from top-left of the text to the cursor in pixels.
          const grabOffsetX = clientX - sourceAbsLeft;
          const grabOffsetY = clientY - sourceAbsTop;

          const mainStage = stageRef?.current;
          if (mainStage) {
            const scaleX = mainStage.scaleX();
            const scaleY = mainStage.scaleY();
            const stageX = mainStage.x();
            const stageY = mainStage.y();
            // 3) Find the stage coordinates whose screen position is cursor - grabOffset.
            const desiredScreenX = clientX - grabOffsetX;
            const desiredScreenY = clientY - grabOffsetY;
            targetX = (desiredScreenX - mainBBox.left) / scaleX - stageX;
            targetY = (desiredScreenY - mainBBox.top) / scaleY - stageY;
          } else {
            // Fallback without stage transform: treat target as simple container pixels.
            const cursorX = clientX - targetBBox.left;
            const cursorY = clientY - targetBBox.top;
            targetX = cursorX - grabOffsetX;
            targetY = cursorY - grabOffsetY;
          }
        } else {
          // Circle, rect: convert source position to target stage
          const absX = sourceBBox.left + newX;
          const absY = sourceBBox.top + newY;
          targetX = absX - targetBBox.left;
          targetY = absY - targetBBox.top;
        }
      }

      // NOTE: We do NOT sync the real text node position here when pointer re-enters main (main→off→main).
      // Setting textNode.position() during drag causes Konva to fire dragEnd and the drag stops prematurely.
      // The ghost follows the cursor; the real node stays at the boundary until release; dragEndUtils syncs the node on drop.

      const mergedId = mergedElement.id ?? element?.id ?? "preview";
      const prev = dragPreviewStateRef.current;
      const isLineArrowCrossStage = isLineArrow && target !== sourceTarget;
      // Ghost: do not introduce lag during cross-stage drags.
      // Using alpha=1 makes the ghost stick exactly to the computed target position.
      const LINE_DRAG_SMOOTH_ALPHA = 1;
      let ghostX = targetX;
      let ghostY = targetY;
      if (isLineArrowCrossStage) {
        const last = lineDragSmoothedRef.current;
        if (last.x != null && last.y != null) {
          ghostX = last.x + LINE_DRAG_SMOOTH_ALPHA * (targetX - last.x);
          ghostY = last.y + LINE_DRAG_SMOOTH_ALPHA * (targetY - last.y);
        }
        lineDragSmoothedRef.current = { x: ghostX, y: ghostY };
      }
      // Editor: use rounded raw position when we update setSelectedObject so panel shows stable integers.
      const displayX = isLineArrowCrossStage ? Math.round(targetX) : targetX;
      const displayY = isLineArrowCrossStage ? Math.round(targetY) : targetY;

      // Line cross-stage: never skip ghost update (so preview follows cursor). Only skip *editor* update when move is small (see below).
      // Other types: threshold check to avoid jitter
      if (!isLineArrowCrossStage && !isTopLayerImage) {
        const positionThreshold = 0.5;
        if (
          prev &&
          prev.target === target &&
          prev.id === mergedId &&
          Math.abs(prev.x - ghostX) < positionThreshold &&
          Math.abs(prev.y - ghostY) < positionThreshold
        ) {
          return;
        }
      }

      // For lines and arrows, ensure points are preserved in preview data (use ghostX/ghostY for smooth movement)
      const previewData = {
        ...mergedElement,
        id: `${mergedId}-ghost`,
        x: isLineArrowCrossStage ? ghostX : displayX,
        y: isLineArrowCrossStage ? ghostY : displayY,
        // CRITICAL: Preserve isOnTopLayer for top-layer images so they render in the correct layer
        isOnTopLayer: mergedElement.isOnTopLayer || element?.isOnTopLayer,
        // Ensure points are included for lines and arrows (they're relative coordinates)
        points:
          mergedElement.points ||
          element?.points ||
          (type === "Line" || type === "Arrow" || type === "DoubleSidedArrow"
            ? [0, 0, 0, 0]
            : undefined),
        // CRITICAL: Ghost must always be visible on target stage. Source line is hidden with opacity 0,
        // and that can flow into mergedElement; force opacity 1 for line/arrow preview so it doesn't hide.
        ...(isLineArrow && { opacity: 1 }),
      };

      dragPreviewStateRef.current = {
        target,
        id: mergedId,
        type,
        x: isLineArrowCrossStage ? ghostX : displayX,
        y: isLineArrowCrossStage ? ghostY : displayY,
      };
      if (DEBUG_TEXT_DRAG_ISSUE && type === "Text") {
      }
      if (isLineArrow && mergedId && target !== sourceTarget) {
        lineSourceHiddenForDragRef.current[mergedId] = true;
      }
      // CRITICAL: For line/arrow cross-stage, always update ghost so it follows cursor.
      // Only update setSelectedObject (editor) when position moved >8px to avoid constant recalc and wrong x / fluctuation (match circles: fewer updates).
      const LINE_DRAG_EDITOR_UPDATE_THRESHOLD = 8;
      if (isLineArrowCrossStage) {
        setDragPreview({
          type,
          target,
          data: previewData,
        });
        const last = lineDragLastCommittedRef.current;
        const movedEnough =
          last.x == null ||
          last.y == null ||
          Math.abs(displayX - last.x) >= LINE_DRAG_EDITOR_UPDATE_THRESHOLD ||
          Math.abs(displayY - last.y) >= LINE_DRAG_EDITOR_UPDATE_THRESHOLD;
        if (movedEnough && setSelectedObject) {
          setSelectedObject((prev) => {
            if (
              prev?.id === mergedElement.id &&
              prev?.x === displayX &&
              prev?.y === displayY
            ) {
              return prev;
            }
            return {
              ...(prev || {}),
              ...mergedElement,
              x: displayX,
              y: displayY,
              type,
              _isDragging: true,
              _previewTargetStage:
                target === "offCanvas" ? "offCanvas" : "main",
            };
          });
          lineDragLastCommittedRef.current = { x: displayX, y: displayY };
        }
      } else {
        setDragPreview({
          type,
          target,
          data: previewData,
        });
      }

      // For non-line cross-stage, or when not cross-stage: update selectedObject as before.
      if (
        isLineArrow &&
        target !== sourceTarget &&
        setSelectedObject &&
        !isLineArrowCrossStage
      ) {
        setSelectedObject((prev) => {
          if (
            prev?.id === mergedElement.id &&
            prev?.x === displayX &&
            prev?.y === displayY
          ) {
            return prev;
          }
          return {
            ...(prev || {}),
            ...mergedElement,
            x: displayX,
            y: displayY,
            type,
            _isDragging: true,
            _previewTargetStage: target === "offCanvas" ? "offCanvas" : "main",
          };
        });
      }

      if (LINE_DRAG_PREVIEW_DEBUG && isLineArrow) {
      }
    },
    [
      stageRef,
      offCanvasStageRef,
      topLayerStageRef,
      clearDragPreview,
      offCanvasImages,
      images,
      setSelectedObject,
    ],
  );

  // CRITICAL: When dragging a line/arrow, the main stage often stops receiving Konva dragMove
  // once the pointer is over the off-canvas area. Keep preview in sync via window mousemove.
  const handleDragPreviewRef = useRef(handleDragPreview);
  handleDragPreviewRef.current = handleDragPreview;
  const lineDragPendingRef = useRef(null);
  const lineDragRafRef = useRef(null);
  useEffect(() => {
    // Konva Stage._pointerleave clears _pointerPositions after the window mousemove runs; feeding
    // again in a microtask restores coordinates after that leave handler (see konva Stage.js).
    const feedStagePointer = (stage, nativeEvt, isStillActive) => {
      if (
        !stage ||
        typeof stage.setPointersPositions !== "function" ||
        !nativeEvt
      ) {
        return;
      }
      stage.setPointersPositions(nativeEvt);
      queueMicrotask(() => {
        if (
          typeof isStillActive === "function" &&
          isStillActive() &&
          typeof stage.setPointersPositions === "function"
        ) {
          stage.setPointersPositions(nativeEvt);
        }
      });
    };

    const onWindowMouseMove = (e) => {
      const ctx = lineDragPreviewContextRef.current;
      const textCtx = textDragPreviewContextRef.current;
      // CRITICAL: Feed the source stage with pointer position so Konva keeps tracking during drag (line/arrow).
      if (ctx?.sourceStageRef && e) {
        const st = ctx.sourceStageRef;
        feedStagePointer(st, e, () => lineDragPreviewContextRef.current?.sourceStageRef === st);
      }
      // CRITICAL: Same fix for group drag – keep the wrapper's source stage tracking the pointer
      // so Konva doesn't end the drag when the cursor crosses into another stage's container.
      if (isGroupDraggingRef.current && groupDragSourceStageRef.current && e) {
        const st = groupDragSourceStageRef.current;
        feedStagePointer(st, e, () => groupDragSourceStageRef.current === st);
      }
      // Individual element drag / transformer: keep the gesture stage fed when pointer moves
      // over a different Stage's container (must run even when line/text preview ctx is empty).
      const crossFeed = crossStagePointerFeedStageRef.current;
      if (crossFeed && e) {
        feedStagePointer(crossFeed, e, () => crossStagePointerFeedStageRef.current === crossFeed);
      }

      if ((!ctx || !ctx.newAttrs) && !textCtx) return;
      // One update per frame; use latest event when rAF fires so preview follows cursor.
      lineDragPendingRef.current = e;
      if (lineDragRafRef.current == null) {
        lineDragRafRef.current = requestAnimationFrame(() => {
          lineDragRafRef.current = null;
          const ev = lineDragPendingRef.current;
          const c = lineDragPreviewContextRef.current;
          const textC = textDragPreviewContextRef.current;
          if (!ev) return;

          // CRITICAL FIX: Apply throttling to match imageRenderer behavior when images are present
          const now = Date.now();
          const throttle = lineDragPreviewThrottleRef.current;
          const shouldUpdate = now - throttle.lastTime >= throttle.ms;

          if (c?.newAttrs && shouldUpdate) {
            throttle.lastTime = now;
            handleDragPreviewRef.current({
              event: { evt: { clientX: ev.clientX, clientY: ev.clientY } },
              newAttrs: c.newAttrs,
              sourceStageRef: c.sourceStageRef,
              element: c.element,
              type: c.type,
              isOffCanvas: c.isOffCanvas,
            });
          } else if (textC) {
            // Text updates don't need throttling as they don't conflict with images
            handleDragPreviewRef.current({
              event: { evt: { clientX: ev.clientX, clientY: ev.clientY } },
              newAttrs: textC.newAttrs,
              sourceStageRef: textC.sourceStageRef,
              element: textC.element,
              type: "Text",
              isOffCanvas: textC.isOffCanvas,
            });
          }
        });
      }
    };
    window.addEventListener("mousemove", onWindowMouseMove, { passive: true });
    window.addEventListener("pointermove", onWindowMouseMove, { passive: true });
    window.addEventListener("touchmove", onWindowMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onWindowMouseMove);
      window.removeEventListener("pointermove", onWindowMouseMove);
      window.removeEventListener("touchmove", onWindowMouseMove);
      if (lineDragRafRef.current != null) {
        cancelAnimationFrame(lineDragRafRef.current);
        lineDragRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const endIfActive = (ev) => {
      if (ev?.type === "lostpointercapture") {
        const feed = crossStagePointerFeedStageRef.current;
        const container = feed?.container?.();
        if (!container || ev.target !== container) {
          return;
        }
      }
      if (crossStageGestureCleanupRef.current) {
        endCrossStageGesture({
          crossStageGestureCleanupRef,
        });
      }
    };
    window.addEventListener("pointerup", endIfActive, true);
    window.addEventListener("pointercancel", endIfActive, true);
    window.addEventListener("lostpointercapture", endIfActive, true);
    return () => {
      window.removeEventListener("pointerup", endIfActive, true);
      window.removeEventListener("pointercancel", endIfActive, true);
      window.removeEventListener("lostpointercapture", endIfActive, true);
    };
  }, []);

  // Helper function to check if two rectangles overlap with meaningful intersection
  // Returns true only if there's a significant overlap area (not just touching edges)
  const doRectanglesOverlap = (rect1, rect2, minOverlapArea = 25) => {
    // Calculate the intersection rectangle
    const intersectionLeft = Math.max(rect1.x, rect2.x);
    const intersectionTop = Math.max(rect1.y, rect2.y);
    const intersectionRight = Math.min(
      rect1.x + rect1.width,
      rect2.x + rect2.width,
    );
    const intersectionBottom = Math.min(
      rect1.y + rect1.height,
      rect2.y + rect2.height,
    );

    // Calculate intersection width and height
    const intersectionWidth = intersectionRight - intersectionLeft;
    const intersectionHeight = intersectionBottom - intersectionTop;

    // If there's no intersection, return false
    if (intersectionWidth <= 0 || intersectionHeight <= 0) {
      return false;
    }

    // Calculate the intersection area
    const intersectionArea = intersectionWidth * intersectionHeight;

    // If minOverlapArea is 0, any intersection counts (for very small selection boxes)
    // Otherwise, require minimum intersection area to avoid selecting barely-touching elements
    if (minOverlapArea === 0) {
      return true; // Any intersection is enough for small selection boxes
    }

    // Require minimum intersection area for larger selection boxes
    return intersectionArea >= minOverlapArea;
  };

  // Helper function to check if a rectangle overlaps with a circle
  // Returns true if ANY part of circle (including stroke) overlaps with the rectangle
  const doesRectOverlapCircle = (rect, circle, minOverlapArea = 25) => {
    // Get circle properties
    const radius =
      circle.radius || Math.max(circle.radiusX || 0, circle.radiusY || 0);
    const circleCenterX = circle.x || 0;
    const circleCenterY = circle.y || 0;

    // First, check if rectangle is completely inside circle
    const rectCorners = [
      { x: rect.x, y: rect.y },
      { x: rect.x + rect.width, y: rect.y },
      { x: rect.x, y: rect.y + rect.height },
      { x: rect.x + rect.width, y: rect.y + rect.height },
    ];

    // Check if any rectangle corner is inside circle (stroke intersection)
    let hasCornerInside = false;
    for (const corner of rectCorners) {
      const dx = corner.x - circleCenterX;
      const dy = corner.y - circleCenterY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= radius) {
        hasCornerInside = true;
        break;
      }

    }

    if (hasCornerInside) {
      return true;
    }

    // Check if circle center is inside rectangle
    const centerInsideRect =
      circleCenterX >= rect.x &&
      circleCenterX <= rect.x + rect.width &&
      circleCenterY >= rect.y &&
      circleCenterY <= rect.y + rect.height;

    if (centerInsideRect) {
      return true;
    }

    // Check for actual intersection by finding closest points
    let closestX = Math.max(
      rect.x,
      Math.min(circleCenterX, rect.x + rect.width),
    );
    let closestY = Math.max(
      rect.y,
      Math.min(circleCenterY, rect.y + rect.height),
    );

    // Calculate distance from circle center to closest point on rectangle
    const dx = circleCenterX - closestX;
    const dy = circleCenterY - closestY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If distance is less than or equal to radius, there's an intersection
    // This includes stroke intersection - even if just the edge touches
    if (distance <= radius) {
      return true;
    }

    // Additional check: if any edge of rectangle intersects circle
    // Check horizontal edges
    if (circleCenterY >= rect.y && circleCenterY <= rect.y + rect.height) {
      const leftDist = Math.abs(circleCenterX - rect.x);
      const rightDist = Math.abs(circleCenterX - (rect.x + rect.width));
      if (leftDist <= radius || rightDist <= radius) {
        return true;
      }
    }

    // Check vertical edges
    if (circleCenterX >= rect.x && circleCenterX <= rect.x + rect.width) {
      const topDist = Math.abs(circleCenterY - rect.y);
      const bottomDist = Math.abs(circleCenterY - (rect.y + rect.height));
      if (topDist <= radius || bottomDist <= radius) {
        return true;
      }
    }

    return false;
  };

  // Helper function to check if a rectangle overlaps with an arrow/line
  const doesRectOverlapLine = (rect, line) => {
    // If line already has bounding box properties (from getElementBoundingBox), use them directly
    if (
      line.width !== undefined &&
      line.height !== undefined &&
      line.x !== undefined &&
      line.y !== undefined
    ) {
      return doRectanglesOverlap(rect, {
        x: line.x,
        y: line.y,
        width: line.width,
        height: line.height,
      });
    }

    // Otherwise, calculate from points
    if (!line.points || line.points.length < 4) return false;

    const minX = Math.min(line.points[0], line.points[2]);
    const maxX = Math.max(line.points[0], line.points[2]);
    const minY = Math.min(line.points[1], line.points[3]);
    const maxY = Math.max(line.points[1], line.points[3]);

    const lineBBox = {
      x: (line.x || 0) + minX,
      y: (line.y || 0) + minY,
      width: maxX - minX,
      height: maxY - minY,
    };

    return doRectanglesOverlap(rect, lineBBox);
  };

  // Precise line-rectangle intersection function
  // Checks if any line segment actually intersects with the selection rectangle
  // Helper function to calculate arrow head triangle points
  const calculateArrowHeadPoints = (
    fromX,
    fromY,
    toX,
    toY,
    pointerLength = 10,
    pointerWidth = 10,
  ) => {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const angle = Math.atan2(dy, dx);

    // In Konva, the arrow tip is at the endpoint (toX, toY)
    // The base of the arrow head is pointerLength pixels back from the endpoint
    const tipX = toX;
    const tipY = toY;

    // Calculate the base center point (pointerLength back from the tip)
    const baseCenterX = toX - Math.cos(angle) * pointerLength;
    const baseCenterY = toY - Math.sin(angle) * pointerLength;

    // Calculate the base points of the triangle (perpendicular to the line)
    const perpAngle = angle + Math.PI / 2;
    const baseHalfWidth = pointerWidth / 2;

    const baseX1 = baseCenterX + Math.cos(perpAngle) * baseHalfWidth;
    const baseY1 = baseCenterY + Math.sin(perpAngle) * baseHalfWidth;

    const baseX2 = baseCenterX - Math.cos(perpAngle) * baseHalfWidth;
    const baseY2 = baseCenterY - Math.sin(perpAngle) * baseHalfWidth;

    return {
      tip: { x: tipX, y: tipY },
      base1: { x: baseX1, y: baseY1 },
      base2: { x: baseX2, y: baseY2 },
    };
  };

  // Keep unified arrowhead geometry in sync with UnifiedLineArrowElement.jsx
  const getUnifiedArrowHeadDimensions = (strokeWidth = 2) => {
    const safeStroke = Number(strokeWidth) || 2;
    return {
      pointerLength: Math.min(56, Math.max(12, safeStroke * 3.2)),
      pointerWidth: Math.min(42, Math.max(10, safeStroke * 2.2)),
    };
  };

  // Helper function to check if a point is inside a triangle using cross product method
  // Returns true if point (px, py) is inside triangle formed by (v1x, v1y), (v2x, v2y), (v3x, v3y)
  const isPointInTriangle = (px, py, v1x, v1y, v2x, v2y, v3x, v3y) => {
    // Calculate cross products to determine if point is on the same side of each edge
    const sign = (p1x, p1y, p2x, p2y, p3x, p3y) => {
      return (p1x - p3x) * (p2y - p3y) - (p2x - p3x) * (p1y - p3y);
    };

    const d1 = sign(px, py, v1x, v1y, v2x, v2y);
    const d2 = sign(px, py, v2x, v2y, v3x, v3y);
    const d3 = sign(px, py, v3x, v3y, v1x, v1y);

    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;

    // Point is inside triangle if all cross products have the same sign (or one is zero)
    return !(hasNeg && hasPos);
  };

  // Helper function to check if a triangle intersects with a rectangle
  const triangleIntersectsRect = (
    triangle,
    rectLeft,
    rectTop,
    rectRight,
    rectBottom,
  ) => {
    const { tip, base1, base2 } = triangle;

    // Check if any vertex is inside the rectangle
    if (
      (tip.x >= rectLeft &&
        tip.x <= rectRight &&
        tip.y >= rectTop &&
        tip.y <= rectBottom) ||
      (base1.x >= rectLeft &&
        base1.x <= rectRight &&
        base1.y >= rectTop &&
        base1.y <= rectBottom) ||
      (base2.x >= rectLeft &&
        base2.x <= rectRight &&
        base2.y >= rectTop &&
        base2.y <= rectBottom)
    ) {
      return true;
    }

    // Check if any triangle edge intersects with any rectangle edge
    const triangleEdges = [
      [tip.x, tip.y, base1.x, base1.y],
      [base1.x, base1.y, base2.x, base2.y],
      [base2.x, base2.y, tip.x, tip.y],
    ];

    const rectEdges = [
      [rectLeft, rectTop, rectRight, rectTop], // top
      [rectRight, rectTop, rectRight, rectBottom], // right
      [rectRight, rectBottom, rectLeft, rectBottom], // bottom
      [rectLeft, rectBottom, rectLeft, rectTop], // left
    ];

    for (const triEdge of triangleEdges) {
      for (const rectEdge of rectEdges) {
        if (
          lineSegmentsIntersect(
            triEdge[0],
            triEdge[1],
            triEdge[2],
            triEdge[3],
            rectEdge[0],
            rectEdge[1],
            rectEdge[2],
            rectEdge[3],
          )
        ) {
          return true;
        }
      }
    }

    // CRITICAL FIX: Check if any point inside the selection rectangle is inside the triangle
    // This ensures that even if the selection box is entirely within the triangle (like around just the tip),
    // it will be detected. Check all four corners and the center of the rectangle.
    const rectCorners = [
      { x: rectLeft, y: rectTop }, // top-left
      { x: rectRight, y: rectTop }, // top-right
      { x: rectRight, y: rectBottom }, // bottom-right
      { x: rectLeft, y: rectBottom }, // bottom-left
      { x: (rectLeft + rectRight) / 2, y: (rectTop + rectBottom) / 2 }, // center
    ];

    for (const corner of rectCorners) {
      if (
        isPointInTriangle(
          corner.x,
          corner.y,
          tip.x,
          tip.y,
          base1.x,
          base1.y,
          base2.x,
          base2.y,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const doesSelectionBoxIntersectLine = (selectionRect, line) => {
    if (!line.points || line.points.length < 4) return false;

    const elementX = line.x || 0;
    const elementY = line.y || 0;
    const strokeWidth = line.strokeWidth || 2;

    // Very small padding - just enough to account for the stroke width
    // This ensures we only select when actually touching the visible line
    const padding = strokeWidth / 2;

    // Convert selection rectangle to line segment format for intersection testing
    const selLeft = selectionRect.x;
    const selRight = selectionRect.x + selectionRect.width;
    const selTop = selectionRect.y;
    const selBottom = selectionRect.y + selectionRect.height;

    // Check if this is an arrow (has arrow heads)
    const hasStartArrow =
      line.hasStartArrow === true || line.pointerAtBeginning === true;
    const hasEndArrow = line.hasEndArrow === true || line.pointerAtEnd === true;

    // Arrow head dimensions (matching UnifiedLineArrowElement)
    const { pointerLength, pointerWidth } =
      getUnifiedArrowHeadDimensions(strokeWidth);

    // Check each line segment of the line
    for (let i = 0; i < line.points.length - 2; i += 2) {
      const x1 = elementX + line.points[i];
      const y1 = elementY + line.points[i + 1];
      const x2 = elementX + line.points[i + 2];
      const y2 = elementY + line.points[i + 3];

      const segmentIntersects = lineIntersectsRect(
        x1,
        y1,
        x2,
        y2,
        selLeft,
        selTop,
        selRight,
        selBottom,
        padding,
      );

      // Check if line segment intersects with any edge of the selection rectangle
      if (segmentIntersects) {
        return true;
      }

      // Check arrow head at the end of this segment (if it's the last segment and hasEndArrow)
      if (i === line.points.length - 4 && hasEndArrow) {
        const arrowHead = calculateArrowHeadPoints(
          x1,
          y1,
          x2,
          y2,
          pointerLength,
          pointerWidth,
        );
        if (
          triangleIntersectsRect(
            arrowHead,
            selLeft,
            selTop,
            selRight,
            selBottom,
          )
        ) {
          return true;
        }
      }

      // Check arrow head at the start of this segment (if it's the first segment and hasStartArrow)
      if (i === 0 && hasStartArrow) {
        // For start arrow, we need to reverse the direction
        const arrowHead = calculateArrowHeadPoints(
          x2,
          y2,
          x1,
          y1,
          pointerLength,
          pointerWidth,
        );
        if (
          triangleIntersectsRect(
            arrowHead,
            selLeft,
            selTop,
            selRight,
            selBottom,
          )
        ) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper function to check if a line segment intersects with a rectangle
  const lineIntersectsRect = (
    x1,
    y1,
    x2,
    y2,
    rectLeft,
    rectTop,
    rectRight,
    rectBottom,
    padding = 0,
  ) => {
    // Expand rectangle by minimal padding (only for stroke width)
    const expandedLeft = rectLeft - padding;
    const expandedRight = rectRight + padding;
    const expandedTop = rectTop - padding;
    const expandedBottom = rectBottom + padding;

    // Check if either endpoint is inside the expanded rectangle
    if (
      (x1 >= expandedLeft &&
        x1 <= expandedRight &&
        y1 >= expandedTop &&
        y1 <= expandedBottom) ||
      (x2 >= expandedLeft &&
        x2 <= expandedRight &&
        y2 >= expandedTop &&
        y2 <= expandedBottom)
    ) {
      return true;
    }

    // Check if line segment intersects with any of the four rectangle edges
    // Top edge
    if (
      lineSegmentsIntersect(
        x1,
        y1,
        x2,
        y2,
        expandedLeft,
        expandedTop,
        expandedRight,
        expandedTop,
      )
    )
      return true;
    // Right edge
    if (
      lineSegmentsIntersect(
        x1,
        y1,
        x2,
        y2,
        expandedRight,
        expandedTop,
        expandedRight,
        expandedBottom,
      )
    )
      return true;
    // Bottom edge
    if (
      lineSegmentsIntersect(
        x1,
        y1,
        x2,
        y2,
        expandedRight,
        expandedBottom,
        expandedLeft,
        expandedBottom,
      )
    )
      return true;
    // Left edge
    if (
      lineSegmentsIntersect(
        x1,
        y1,
        x2,
        y2,
        expandedLeft,
        expandedBottom,
        expandedLeft,
        expandedTop,
      )
    )
      return true;

    return false;
  };

  // Helper function to check if two line segments intersect
  const lineSegmentsIntersect = (x1, y1, x2, y2, x3, y3, x4, y4) => {
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 0.0001) return false; // Lines are parallel

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  };

  // Helper function to check if a rectangle overlaps with any existing element
  // Now accounts for rotation of existing elements
  const hasOverlapWithAnyElement = (testRect, allElements) => {
    // Check rectangles (with rotation)
    if (
      allElements.rectangles?.some((rect) => {
        const rectBBox = getElementBoundingBox(rect, "Rect");
        return doRectanglesOverlap(testRect, rectBBox);
      })
    ) {
      return true;
    }

    // Check circles
    if (
      allElements.circles?.some((circle) => {
        const circleBBox = getElementBoundingBox(circle, "Circle");
        return doesRectOverlapCircle(testRect, circleBBox);
      })
    ) {
      return true;
    }

    // Check images (with rotation)
    if (
      allElements.images?.some((img) => {
        const imgBBox = getElementBoundingBox(img, "Image");
        return doRectanglesOverlap(testRect, imgBBox);
      })
    ) {
      return true;
    }

    // Check texts (with rotation)
    if (
      allElements.texts?.some((text) => {
        const textBBox = getElementBoundingBox(text, "Text");
        return doRectanglesOverlap(testRect, textBBox);
      })
    ) {
      return true;
    }

    // Check arrows (with rotation)
    if (
      allElements.arrows?.some((arrow) => {
        const arrowBBox = getElementBoundingBox(arrow, "Arrow");
        return doRectanglesOverlap(testRect, arrowBBox);
      })
    ) {
      return true;
    }

    // Check double-sided arrows (with rotation)
    if (
      allElements.doubleSidedArrows?.some((arrow) => {
        const arrowBBox = getElementBoundingBox(arrow, "DoubleSidedArrow");
        return doRectanglesOverlap(testRect, arrowBBox);
      })
    ) {
      return true;
    }

    // Check lines (with rotation)
    if (
      allElements.lines?.some((line) => {
        const lineBBox = getElementBoundingBox(line, "Line");
        return doRectanglesOverlap(testRect, lineBBox);
      })
    ) {
      return true;
    }

    return false;
  };

  // Helper function to find a non-overlapping position
  // Searches systematically from top-left to bottom-right, row by row
  const findNonOverlappingPosition = (
    newRect,
    allElements,
    canvasWidth,
    canvasHeight,
  ) => {
    const margin = 20; // Minimum spacing between rectangles

    const rectWidth = newRect.width;
    const rectHeight = newRect.height;

    // Use a step size based on rectangle dimensions for efficiency
    // Smaller step for smaller rectangles, larger step for larger ones
    const stepSize = Math.max(10, Math.min(rectWidth / 4, rectHeight / 4, 30));

    // Calculate the searchable area (accounting for margins)
    const searchStartX = margin;
    const searchStartY = margin;
    const searchEndX = canvasWidth - margin - rectWidth;
    const searchEndY = canvasHeight - margin - rectHeight;

    // Start searching from top-left corner, row by row (left to right, top to bottom)
    for (let y = searchStartY; y <= searchEndY; y += stepSize) {
      for (let x = searchStartX; x <= searchEndX; x += stepSize) {
        // Create test rectangle at this position
        const testRect = {
          x: x,
          y: y,
          width: rectWidth,
          height: rectHeight,
        };

        // Check if this position is within bounds
        if (
          x >= margin &&
          y >= margin &&
          x + rectWidth <= canvasWidth - margin &&
          y + rectHeight <= canvasHeight - margin
        ) {
          // Check if this position overlaps with any existing element
          // (even if a small part of an element exists, we skip this position)
          if (!hasOverlapWithAnyElement(testRect, allElements)) {
            // Found a valid non-overlapping position
            return { x: x, y: y };
          }
        }
      }
    }

    // If no position found in the grid search, try corners as fallback
    const fallbackPositions = [
      { x: margin, y: margin }, // Top-left
      { x: canvasWidth - rectWidth - margin, y: margin }, // Top-right
      { x: margin, y: canvasHeight - rectHeight - margin }, // Bottom-left
      {
        x: canvasWidth - rectWidth - margin,
        y: canvasHeight - rectHeight - margin,
      }, // Bottom-right
    ];

    for (const pos of fallbackPositions) {
      const testRect = {
        x: pos.x,
        y: pos.y,
        width: rectWidth,
        height: rectHeight,
      };
      if (!hasOverlapWithAnyElement(testRect, allElements)) {
        return pos;
      }
    }

    // Last resort: place at bottom-right corner (might overlap, but at least it's in bounds)
    return {
      x: Math.max(margin, canvasWidth - rectWidth - margin),
      y: Math.max(margin, canvasHeight - rectHeight - margin),
    };
  };

  // Helper function to calculate rotated bounding box
  // Takes a rectangle (top-left x, y, width, height) and rotation angle, returns axis-aligned bounding box
  // isCentered: if true, x and y are center coordinates; if false, they are top-left coordinates
  const getRotatedBoundingBox = (
    x,
    y,
    width,
    height,
    rotation,
    isCentered = false,
  ) => {
    if (!rotation || rotation === 0) {
      if (isCentered) {
        // Convert center to top-left
        return { x: x - width / 2, y: y - height / 2, width, height };
      }
      return { x, y, width, height };
    }

    // Convert rotation to radians
    const angle = (rotation * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Calculate center point
    let centerX, centerY;
    if (isCentered) {
      centerX = x;
      centerY = y;
    } else {
      centerX = x + width / 2;
      centerY = y + height / 2;
    }

    // Get all 4 corners relative to center
    const corners = [
      { x: -width / 2, y: -height / 2 }, // Top-left
      { x: width / 2, y: -height / 2 }, // Top-right
      { x: width / 2, y: height / 2 }, // Bottom-right
      { x: -width / 2, y: height / 2 }, // Bottom-left
    ];

    // Rotate each corner around center
    const rotatedCorners = corners.map((corner) => {
      const rotatedX = corner.x * cos - corner.y * sin;
      const rotatedY = corner.x * sin + corner.y * cos;
      return {
        x: centerX + rotatedX,
        y: centerY + rotatedY,
      };
    });

    // Find min/max x and y of rotated corners
    const xs = rotatedCorners.map((c) => c.x);
    const ys = rotatedCorners.map((c) => c.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  };

  // Helper function to find all elements that intersect with a selection box
  // Helper function to check if an element is in main canvas or off-canvas
  const isElementInMainCanvasArray = (element) => {
    if (!element || !element.type) return false;

    switch (element.type) {
      case "Rect":
        return rectangles.some((r) => r.id === element.id);
      case "Circle":
        return circles.some((c) => c.id === element.id);
      case "Image":
        return images.some((i) => i.id === element.id);
      case "Text":
        return texts.some((t) => t.id === element.id);
      case "Arrow":
        // Check both arrows array and lines array (for unified arrows)
        return (
          arrows.some((a) => a.id === element.id) ||
          lines.some((l) => {
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            return (
              l.id === element.id &&
              (hasStartArrow || hasEndArrow) &&
              !(hasStartArrow && hasEndArrow)
            );
          })
        );
      case "Line":
        return lines.some((l) => l.id === element.id);
      case "DoubleSidedArrow":
        // Check both doubleSidedArrows array and lines array (for unified double-sided arrows)
        return (
          doubleSidedArrows.some((d) => d.id === element.id) ||
          lines.some((l) => {
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            return l.id === element.id && hasStartArrow && hasEndArrow;
          })
        );
      default:
        return false;
    }
  };

  // Keep off-canvas element detection explicit (mirrors main-canvas detection)
  // so top-layer image demotion works the same way on both canvases.
  const isElementInOffCanvasArray = (element) => {
    if (!element || !element.type) return false;

    switch (element.type) {
      case "Rect":
        return offCanvasRectangles.some((r) => r.id === element.id);
      case "Circle":
        return offCanvasCircles.some((c) => c.id === element.id);
      case "Image":
        return offCanvasImages.some((i) => i.id === element.id);
      case "Text":
        return offCanvasTexts.some((t) => t.id === element.id);
      case "Arrow":
        // Check both arrows array and lines array (for unified arrows)
        return (
          offCanvasArrows.some((a) => a.id === element.id) ||
          offCanvasLines.some((l) => {
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            return (
              l.id === element.id &&
              (hasStartArrow || hasEndArrow) &&
              !(hasStartArrow && hasEndArrow)
            );
          })
        );
      case "Line":
        return offCanvasLines.some((l) => l.id === element.id);
      case "DoubleSidedArrow":
        // Check both doubleSidedArrows array and lines array (for unified double-sided arrows)
        return (
          offCanvasDoubleSidedArrows.some((d) => d.id === element.id) ||
          offCanvasLines.some((l) => {
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            return l.id === element.id && hasStartArrow && hasEndArrow;
          })
        );
      default:
        return false;
    }
  };

  // When multi-select is enabled, block clicks on the other stage so user cannot add elements from a different stage
  const { blockClicksOnOffCanvasElements, blockClicksOnMainStageElements } =
    useMemo(() => {
      const allSelected = [];
      const seen = new Set();
      if (selectedObjects?.length) {
        selectedObjects.forEach((obj) => {
          if (obj?.id && !seen.has(obj.id)) {
            seen.add(obj.id);
            allSelected.push(obj);
          }
        });
      }
      if (selectedObject?.id && !seen.has(selectedObject.id)) {
        allSelected.push(selectedObject);
      }
      if (allSelected.length === 0 || !isMultiSelectMode) {
        return {
          blockClicksOnOffCanvasElements: false,
          blockClicksOnMainStageElements: false,
        };
      }
      const allFromMain = allSelected.every((el) =>
        isElementInMainCanvasArray(el),
      );
      // Must use explicit off-canvas arrays — !main is wrong for "neither" and mis-classifies some ids.
      const allFromOffCanvas = allSelected.every((el) =>
        isElementInOffCanvasArray(el),
      );
      return {
        blockClicksOnOffCanvasElements: isMultiSelectMode && allFromMain,
        blockClicksOnMainStageElements: isMultiSelectMode && allFromOffCanvas,
      };
    }, [
      isMultiSelectMode,
      selectedObject,
      selectedObjects,
      rectangles,
      circles,
      images,
      texts,
      lines,
      arrows,
      doubleSidedArrows,
      offCanvasRectangles,
      offCanvasCircles,
      offCanvasImages,
      offCanvasTexts,
      offCanvasLines,
      offCanvasArrows,
      offCanvasDoubleSidedArrows,
    ]);

  // Unified strokes live in `lines` / `offCanvasLines` but selection typing uses Line vs Arrow vs DoubleSidedArrow.
  const unifiedLineLikeToSelectionType = (lineLike) => {
    const hasStartArrow =
      lineLike.hasStartArrow === true || lineLike.pointerAtBeginning === true;
    const hasEndArrow =
      lineLike.hasEndArrow === true || lineLike.pointerAtEnd === true;
    if (hasStartArrow && hasEndArrow) return "DoubleSidedArrow";
    if (hasStartArrow || hasEndArrow) return "Arrow";
    return "Line";
  };

  // Helper function to get all elements from main canvas or off-canvas based on a reference element
  const getAllElementsFromSameCanvas = (referenceElement) => {
    if (!referenceElement) return [];

    const isMainCanvas = isElementInMainCanvasArray(referenceElement);
    const allElements = [];

    if (isMainCanvas) {
      // Gather all elements from main canvas arrays
      rectangles.forEach((rect) => {
        allElements.push({
          id: rect.id,
          type: "Rect",
          name: rect.name || `Rectangle ${rect.id}`,
          ...rect,
        });
      });
      circles.forEach((circle) => {
        allElements.push({
          id: circle.id,
          type: "Circle",
          name: circle.name || `Circle ${circle.id}`,
          ...circle,
        });
      });
      images.forEach((img) => {
        allElements.push({
          id: img.id,
          type: "Image",
          name: img.name || `Image ${img.id}`,
          ...img,
        });
      });
      texts.forEach((text) => {
        allElements.push({
          id: text.id,
          type: "Text",
          name: text.name || `Text ${text.id}`,
          ...text,
        });
      });
      // First, collect all line IDs to avoid duplicates
      const lineIds = new Set(lines.map((l) => l.id));

      arrows.forEach((arrow) => {
        // Only include legacy arrows (not in lines array)
        // Unified elements are stored in lines array, so skip if already in lines
        if (!lineIds.has(arrow.id)) {
          allElements.push({
            id: arrow.id,
            type: "Arrow",
            name: arrow.name || `Arrow ${arrow.id}`,
            ...arrow,
          });
        }
      });
      doubleSidedArrows.forEach((arrow) => {
        // Only include legacy double-sided arrows (not in lines array)
        // Unified elements are stored in lines array, so skip if already in lines
        if (!lineIds.has(arrow.id)) {
          allElements.push({
            id: arrow.id,
            type: "DoubleSidedArrow",
            name: arrow.name || `Double Arrow ${arrow.id}`,
            ...arrow,
          });
        }
      });
      lines.forEach((line) => {
        // Check if this is a unified element (has hasStartArrow or hasEndArrow properties)
        const hasStartArrow =
          line.hasStartArrow === true || line.pointerAtBeginning === true;
        const hasEndArrow =
          line.hasEndArrow === true || line.pointerAtEnd === true;

        let elementType = "Line";
        if (hasStartArrow && hasEndArrow) {
          elementType = "DoubleSidedArrow";
        } else if (hasStartArrow || hasEndArrow) {
          elementType = "Arrow";
        }

        allElements.push({
          id: line.id,
          type: elementType,
          name:
            line.name ||
            (elementType === "Line"
              ? `Line ${line.id}`
              : elementType === "DoubleSidedArrow"
                ? `Double Arrow ${line.id}`
                : `Arrow ${line.id}`),
          ...line,
        });
      });
    } else {
      // Gather all elements from off-canvas arrays
      offCanvasRectangles.forEach((rect) => {
        allElements.push({
          id: rect.id,
          type: "Rect",
          name: rect.name || `Rectangle ${rect.id}`,
          ...rect,
        });
      });
      offCanvasCircles.forEach((circle) => {
        allElements.push({
          id: circle.id,
          type: "Circle",
          name: circle.name || `Circle ${circle.id}`,
          ...circle,
        });
      });
      offCanvasImages.forEach((img) => {
        allElements.push({
          id: img.id,
          type: "Image",
          name: img.name || `Image ${img.id}`,
          ...img,
        });
      });
      offCanvasTexts.forEach((text) => {
        allElements.push({
          id: text.id,
          type: "Text",
          name: text.name || `Text ${text.id}`,
          ...text,
        });
      });
      // First, collect all off-canvas line IDs to avoid duplicates
      const offCanvasLineIds = new Set(offCanvasLines.map((l) => l.id));

      offCanvasArrows.forEach((arrow) => {
        // Only include legacy arrows (not in offCanvasLines array)
        // Unified elements are stored in offCanvasLines array, so skip if already in lines
        if (!offCanvasLineIds.has(arrow.id)) {
          allElements.push({
            id: arrow.id,
            type: "Arrow",
            name: arrow.name || `Arrow ${arrow.id}`,
            ...arrow,
          });
        }
      });
      offCanvasDoubleSidedArrows.forEach((arrow) => {
        // Only include legacy double-sided arrows (not in offCanvasLines array)
        // Unified elements are stored in offCanvasLines array, so skip if already in lines
        if (!offCanvasLineIds.has(arrow.id)) {
          allElements.push({
            id: arrow.id,
            type: "DoubleSidedArrow",
            name: arrow.name || `Double Arrow ${arrow.id}`,
            ...arrow,
          });
        }
      });
      offCanvasLines.forEach((line) => {
        // Check if this is a unified element
        const hasStartArrow =
          line.hasStartArrow === true || line.pointerAtBeginning === true;
        const hasEndArrow =
          line.hasEndArrow === true || line.pointerAtEnd === true;

        let elementType = "Line";
        if (hasStartArrow && hasEndArrow) {
          elementType = "DoubleSidedArrow";
        } else if (hasStartArrow || hasEndArrow) {
          elementType = "Arrow";
        }

        allElements.push({
          id: line.id,
          type: elementType,
          name:
            line.name ||
            (elementType === "Line"
              ? `Line ${line.id}`
              : elementType === "DoubleSidedArrow"
                ? `Double Arrow ${line.id}`
                : `Arrow ${line.id}`),
          ...line,
        });
      });
    }

    return allElements;
  };

  const findElementsInSelectionBox = (selectionRect, targetStageRef = null) => {
    const selectedElements = [];

    // Normalize selection box coordinates
    const selX = Math.min(selectionRect.startX, selectionRect.endX);
    const selY = Math.min(selectionRect.startY, selectionRect.endY);
    const selWidth = Math.abs(selectionRect.endX - selectionRect.startX);
    const selHeight = Math.abs(selectionRect.endY - selectionRect.startY);

    const selectionBBox = {
      x: selX,
      y: selY,
      width: selWidth,
      height: selHeight,
    };

    // Calculate dynamic minimum overlap area based on selection box size
    // For very small selection boxes, require NO minimum overlap - ANY intersection should select
    // This ensures that even the tiniest selection box touching any part of an element will select it
    // For larger boxes, use a proportional threshold to avoid accidental selections
    const selectionArea = selWidth * selHeight;
    let minOverlapArea;
    if (selectionArea <= 25) {
      // For small boxes (≤25px²), require NO minimum overlap - any intersection counts
      // This allows even 1px selection boxes to select elements they touch
      minOverlapArea = 0;
    } else {
      // For larger boxes, use standard threshold to avoid accidental selections
      minOverlapArea = 25;
    }

    // Debug logging for selection box

    // Determine which canvas to search based on targetStageRef
    // If targetStageRef is offCanvasStageRef, search off-canvas elements
    // Otherwise, search main canvas elements
    const isOffCanvas = targetStageRef === offCanvasStageRef;
    const stage = targetStageRef?.current;

    // Get the appropriate arrays to search
    const rectsToSearch = isOffCanvas ? offCanvasRectangles : rectangles;
    const circlesToSearch = isOffCanvas ? offCanvasCircles : circles;
    const imagesToSearch = isOffCanvas ? offCanvasImages : images;
    const textsToSearch = isOffCanvas ? offCanvasTexts : texts;
    const arrowsToSearch = isOffCanvas ? offCanvasArrows : arrows;

    const doubleSidedArrowsToSearch = isOffCanvas
      ? offCanvasDoubleSidedArrows
      : doubleSidedArrows;
    const linesToSearch = isOffCanvas ? offCanvasLines : lines;

    // Helper function to get element with current position from Konva node
    const getElementWithCurrentPosition = (element, elementType) => {
      if (!stage || !element?.id) return element;

      // Try to find the Konva node for this element
      const node = stage.findOne(`#${element.id}`);
      if (node) {
        // Get current position from Konva node (this is the actual rendered position)
        const currentX = node.x();
        const currentY = node.y();

        // Always use Konva node position if available (even if 0)
        // This ensures we get the actual rendered position
        if (currentX !== undefined && currentY !== undefined) {
          const result = {
            ...element,
            x: currentX,
            y: currentY,
          };

          // Debug logging for lines/arrows (disabled)
          // if (elementType === "Line" && element.pointerAtBeginning) {
          // }

          return result;
        }
      }
      return element;
    };

    // Check rectangles - select if ANY part overlaps with selection box
    rectsToSearch.forEach((rect) => {
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const rectWithCurrentPos = getElementWithCurrentPosition(rect, "Rect");
      const rectBBox = getElementBoundingBox(rectWithCurrentPos, "Rect");
      const overlaps = doRectanglesOverlap(
        selectionBBox,
        rectBBox,
        minOverlapArea,
      );

      // Debug logging for rectangle selection
      if (overlaps || Math.abs(rectBBox.x - selectionBBox.x) < 100) {
      }

      if (overlaps) {
        selectedElements.push({
          ...rectWithCurrentPos,
          id: rect.id,
          type: "Rect",
          name: rect.name || `Rectangle ${rect.id}`,
        });
      }
    });

    // Check circles

    circlesToSearch.forEach((circle) => {
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const circleWithCurrentPos = getElementWithCurrentPosition(
        circle,
        "Circle",
      );
      const circleBBox = getElementBoundingBox(circleWithCurrentPos, "Circle");
      // Pass the actual circle object (with center and radius) to overlap function, not the bounding box
      const overlaps = doesRectOverlapCircle(
        selectionBBox,
        circleWithCurrentPos,
        0,
      );

      // Debug logging for circle selection (disabled)

      if (overlaps) {
        selectedElements.push({
          ...circleWithCurrentPos,
          id: circle.id,
          type: "Circle",
          name: circle.name || `Circle ${circle.id}`,
        });
      }
    });

    // Check images
    imagesToSearch.forEach((img) => {
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const imgWithCurrentPos = getElementWithCurrentPosition(img, "Image");
      const imgBBox = getElementBoundingBox(imgWithCurrentPos, "Image");
      // Use dynamic minOverlapArea based on selection box size
      if (doRectanglesOverlap(selectionBBox, imgBBox, minOverlapArea)) {
        selectedElements.push({
          ...imgWithCurrentPos,
          id: img.id,
          type: "Image",
          name: img.name || `Image ${img.id}`,
        });
      }
    });

    // Check texts
    textsToSearch.forEach((text) => {
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const textWithCurrentPos = getElementWithCurrentPosition(text, "Text");
      const textBBox = getElementBoundingBox(textWithCurrentPos, "Text");
      if (doRectanglesOverlap(selectionBBox, textBBox, minOverlapArea)) {
        selectedElements.push({
          ...textWithCurrentPos,
          id: text.id,
          type: "Text",
          name: text.name || `Text ${text.id}`,
        });
      }
    });

    // Check arrows (legacy arrows - not unified elements)
    // Unified elements are stored in lines array, so we skip arrows that have hasStartArrow/hasEndArrow
    arrowsToSearch.forEach((arrow) => {
      // Skip unified elements - they're stored in lines array
      if (
        arrow.hasStartArrow !== undefined ||
        arrow.hasEndArrow !== undefined ||
        arrow.pointerAtBeginning !== undefined ||
        arrow.pointerAtEnd !== undefined
      ) {
        return; // Skip - this is a unified element, will be checked in lines array
      }
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const arrowWithCurrentPos = getElementWithCurrentPosition(arrow, "Arrow");
      const arrowBBox = getElementBoundingBox(arrowWithCurrentPos, "Arrow");
      if (doRectanglesOverlap(selectionBBox, arrowBBox, minOverlapArea)) {
        selectedElements.push({
          ...arrowWithCurrentPos,
          id: arrow.id,
          type: "Arrow",
          name: arrow.name || `Arrow ${arrow.id}`,
        });
      }
    });

    // Check double-sided arrows (legacy - not unified elements)
    // Unified elements are stored in lines array, so we skip doubleSidedArrows that have hasStartArrow/hasEndArrow
    doubleSidedArrowsToSearch.forEach((arrow) => {
      // Skip unified elements - they're stored in lines array
      if (
        arrow.hasStartArrow !== undefined ||
        arrow.hasEndArrow !== undefined ||
        arrow.pointerAtBeginning !== undefined ||
        arrow.pointerAtEnd !== undefined
      ) {
        return; // Skip - this is a unified element, will be checked in lines array
      }
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const arrowWithCurrentPos = getElementWithCurrentPosition(
        arrow,
        "DoubleSidedArrow",
      );
      const arrowBBox = getElementBoundingBox(
        arrowWithCurrentPos,
        "DoubleSidedArrow",
      );
      if (doRectanglesOverlap(selectionBBox, arrowBBox, minOverlapArea)) {
        selectedElements.push({
          ...arrowWithCurrentPos,
          id: arrow.id,
          type: "DoubleSidedArrow",
          name: arrow.name || `Double Arrow ${arrow.id}`,
        });
      }
    });

    // Check lines (including unified line/arrow elements)
    linesToSearch.forEach((line) => {
      // CRITICAL: Get current position from Konva node to ensure we use the latest position
      const lineWithCurrentPos = getElementWithCurrentPosition(line, "Line");

      // Check if this is a unified element (has hasStartArrow or hasEndArrow properties)
      const hasStartArrow =
        lineWithCurrentPos.hasStartArrow === true ||
        lineWithCurrentPos.pointerAtBeginning === true;
      const hasEndArrow =
        lineWithCurrentPos.hasEndArrow === true ||
        lineWithCurrentPos.pointerAtEnd === true;

      // Determine the correct type for unified elements
      let elementType = "Line";
      if (hasStartArrow && hasEndArrow) {
        elementType = "DoubleSidedArrow";
      } else if (hasStartArrow || hasEndArrow) {
        elementType = "Arrow";
      }

      // CRITICAL FIX: Use precise line-rectangle intersection instead of bounding box overlap
      // This prevents selection of V-shaped lines when selection box is in the empty space
      const overlaps = doesSelectionBoxIntersectLine(
        selectionBBox,
        lineWithCurrentPos,
      );

      // Enhanced logging for debugging (disabled)
      // if (hasStartArrow || hasEndArrow) {
      // }

      if (overlaps) {
        selectedElements.push({
          ...lineWithCurrentPos,
          id: lineWithCurrentPos.id,
          type: elementType,
          name:
            lineWithCurrentPos.name ||
            (elementType === "Line"
              ? `Line ${lineWithCurrentPos.id}`
              : elementType === "DoubleSidedArrow"
                ? `Double Arrow ${lineWithCurrentPos.id}`
                : `Arrow ${lineWithCurrentPos.id}`),
        });
        if (hasStartArrow || hasEndArrow) {
        }
      } else {
        if (hasStartArrow || hasEndArrow) {
        }
      }
    });

    return selectedElements;
  };

  // Refs for element selection to avoid stale closures
  const selectedElementsRef = useRef([]);
  const setSelectedElementsRef = (elements) => {
    selectedElementsRef.current = elements;
    setSelectedObjects(elements);
  };

  // Refs for callback functions to prevent useEffect re-running
  const findElementsInSelectionBoxRef = useRef(findElementsInSelectionBox);
  const setSelectedObjectsRef = useRef(setSelectedObjects);
  const setSelectedObjectRef = useRef(setSelectedObject);
  const setIndividuallySelectedIdsRef = useRef(setIndividuallySelectedIds);

  // CRITICAL FIX: Add a ref to track if selection has been processed to prevent duplicate processing
  const selectionProcessedRef = useRef(false);
  const setIsMultiSelectModeRef = useRef(setIsMultiSelectMode);
  const isMultiSelectModeRef = useRef(isMultiSelectMode);
  const localJustFinishedSelectionRef = useRef(false);
  const activeJustFinishedSelectionRef =
    justFinishedSelectionRef || localJustFinishedSelectionRef;
  const justFinishedSelectionRefRef = useRef(activeJustFinishedSelectionRef);
  // CRITICAL FIX: Add ref to store latest selectionBox value to avoid stale closures in global handlers
  const selectionBoxRef = useRef(null);
  const selectionBoxStageRef = useRef(null);
  // CRITICAL FIX: Track previous button state to detect button release
  const previousButtonsRef = useRef(null);
  const selectedObjectsLengthRef = useRef(0);
  const prevIsMultiSelectModeRef = useRef(isMultiSelectMode);
  const justProcessedSelectionBoxRef = useRef(false);
  const selectionMouseupInProgressRef = useRef(false);
  
  // CRITICAL: Refs to store selection count before Stage.onMouseDown clears it
  // preMouseDownSelectionCountRef is now passed as a prop from MyCanvas
  // Ensure it exists for backwards compatibility
  const localPreMouseDownSelectionCountRef = useRef(0);
  const preMouseDownSelectionLengthRef = useRef(0);
  const preMouseDownSelectionArrayRef = useRef([]); // Store the actual selection array
  
  // Use the passed ref if available, otherwise use the local ref
  const effectivePreMouseDownSelectionCountRef = preMouseDownSelectionCountRef || localPreMouseDownSelectionCountRef;
  // React Strict Mode double-invokes state updaters; cache result so second run returns same value (no double-toggle)
  const selectionBoxResultRef = useRef(null);
  const markSelectionJustFinished = () => {
    const jf = justFinishedSelectionRefRef.current;
    if (!jf) return;
    jf.current = true;
    setTimeout(() => {
      jf.current = false;
    }, 700);
  };

  // Update refs when functions change
  useEffect(() => {
    findElementsInSelectionBoxRef.current = findElementsInSelectionBox;
    setSelectedObjectsRef.current = setSelectedObjects;
    setSelectedObjectRef.current = setSelectedObject;
    setIndividuallySelectedIdsRef.current = setIndividuallySelectedIds;
    setIsMultiSelectModeRef.current = setIsMultiSelectMode;
    isMultiSelectModeRef.current = isMultiSelectMode;
    justFinishedSelectionRefRef.current = activeJustFinishedSelectionRef;
  }, [
    findElementsInSelectionBox,
    setSelectedObjects,
    setSelectedObject,
    setIndividuallySelectedIds,
    setIsMultiSelectMode,
    isMultiSelectMode,
    activeJustFinishedSelectionRef,
  ]);

  // CRITICAL FIX: Update selectionBox refs whenever selectionBox state changes
  // This ensures global handlers always have access to the latest selectionBox value
  useEffect(() => {
    selectionBoxRef.current = selectionBox;
  }, [selectionBox]);

  useEffect(() => {
    selectionBoxStageRef.current = selectionBoxStage;
  }, [selectionBoxStage]);

  useEffect(() => {
    selectedObjectsLengthRef.current = selectedObjects?.length ?? 0;
    // Keep pre-mousedown snapshot refs in sync with explicit clears (Cancel / Esc),
    // so global mouseup logic does not restore stale previous selections.
    if ((selectedObjects?.length ?? 0) === 0) {
      effectivePreMouseDownSelectionCountRef.current = 0;
      preMouseDownSelectionLengthRef.current = 0;
      preMouseDownSelectionArrayRef.current = [];
    }
  }, [selectedObjects]);

  // Keep multi-select mode in sync with actual selection content.
  // If the final selected item is removed, turn multi-select off so toolbar/actions re-enable.
  useEffect(() => {
    if (!setIsMultiSelectMode) return;
    if (!isMultiSelectMode) return;
    if ((selectedObjects?.length ?? 0) === 0) {
      setIsMultiSelectMode(false);
    }
  }, [selectedObjects?.length, isMultiSelectMode, setIsMultiSelectMode]);

  // Keep selection stable when multi-select flag toggles off.
  // Clearing here can wipe an active group due to transient mode flips.
  useEffect(() => {
    const wasMultiSelect = prevIsMultiSelectModeRef.current;
    if (wasMultiSelect && !isMultiSelectMode) {
      // No-op: selection should remain as-is.
      // Callers that explicitly want to clear selection already do so.
    }
    prevIsMultiSelectModeRef.current = isMultiSelectMode;
  }, [
    isMultiSelectMode,
  ]);

  // CRITICAL: Capture-phase click to clear selection on first click after selection box
  // (Konva/bubbling can require 2 clicks; this runs first and clears when click is on stage/background)
  useEffect(() => {
    const handleCaptureClick = (e) => {
      if (selectedObjectsLengthRef.current <= 0) return;
      if (selectedObjectsLengthRef.current > 1) return;
      // CRITICAL: When multi-select is on, never clear here — user may be clicking another shape to add to selection.
      // Clearing here runs before the shape's onClick and wipes selection even when getIntersection later finds the shape.
      if (isMultiSelectMode) return;
      // Don't clear selection when duplicate-to-selected-location (clone mode) is active – keep SelectionEditor open while placing duplicates
      if (isCloneMode || lockedAction) return;
      // Ignore click inside shape editor or text panel (e.g. SelectionEditor overlap on narrow screens)
      if (
        e.target?.closest?.(".shape-editor-container") ||
        e.target?.closest?.(".textbox-with-buttons")
      )
        return;
      // Ignore click that immediately follows selection box end (would clear the selection we just applied)
      if (justProcessedSelectionBoxRef.current) return;
      // Ignore click that fires right after transform end (resize/rotate) - would wrongly clear selection
      const transformJustFinished =
        justFinishedTransformRef?.current ||
        (lastTransformStartTimeRef?.current &&
          Date.now() - lastTransformStartTimeRef.current < 300);
      // Ignore click that fires right after drag end - for top-layer images the main-stage
      // intersection test returns the Background rect (top-layer images live on a separate stage),
      // so without this guard the handler clears selection every time a top-layer image is dragged.
      const dragJustFinished = justFinishedDragRef?.current;
      const rawShapeDrawJustFinished = justFinishedShapeDrawRef?.current;
      const shapeDrawJustFinished =
        typeof rawShapeDrawJustFinished === "number"
          ? Date.now() - rawShapeDrawJustFinished < 60
          : Boolean(rawShapeDrawJustFinished);
      if (transformJustFinished || dragJustFinished || shapeDrawJustFinished) {
        if (justFinishedTransformRef) justFinishedTransformRef.current = false;
        if (justFinishedDragRef) justFinishedDragRef.current = false;
        if (justFinishedShapeDrawRef) justFinishedShapeDrawRef.current = 0;
        return;
      }
      const stage = stageRef?.current;
      const container = mainStageContainerRef?.current;
      if (!stage || !container) return;
      // Ignore clicks on UI outside the canvas (e.g. Select All / Multi-select buttons in portal)
      // so we don't clear selection when the user clicks those buttons
      if (!container.contains(e.target)) return;
      const rect = container.getBoundingClientRect();
      const clientX = e.clientX;
      const clientY = e.clientY;
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      )
        return;
      stage.setPointersPositions(e);
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const shape = stage.getIntersection(pos);
      if (!shape) return;
      const isStage = shape === stage;
      const isBg = shape.attrs?.id === "bg";
      if (!isStage && !isBg) return;
      if ((selectedObjects?.length || 0) > 1) {
        // Preserve group selection on background clicks.
        return;
      }
      setIsSelecting(false);
      setSelectionBox(null);
      setSelectionBoxStage(null);
      setSelectAllButtonClicked(false);

      // DEBUG: Log selection clear
      setSelectedObject(null);
      setSelectedObjects([]);
      if (setIsMultiSelectMode) setIsMultiSelectMode(false);
      transformerRef?.current?.nodes([]);
      setSelectedText(null);
      setShowTextBox(false);
      setSelectedTextId(null);
      setSelectedImage(null);
      setSelectedArrow(null);
      setSelectedLine(null);
      setSelectedDoubleSidedArrow(null);
    };
    window.addEventListener("click", handleCaptureClick, true);
    return () => window.removeEventListener("click", handleCaptureClick, true);
  }, [
    isMultiSelectMode,
    isCloneMode,
    lockedAction,
    setSelectedObject,
    setSelectedObjects,
    setIsMultiSelectMode,
    setSelectedText,
    setShowTextBox,
    setSelectedTextId,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    justFinishedShapeDrawRef,
  ]);

  // Helper function to get element with current position from the appropriate stage
  const getElementWithCurrentPositionForWrapper = (
    element,
    elementType,
    isOffCanvas = false,
  ) => {
    const targetStage = isOffCanvas
      ? offCanvasStageRef.current
      : stageRef.current;
    const otherStage = isOffCanvas
      ? stageRef.current
      : offCanvasStageRef.current;

    if (!element?.id) return element;

    // Try the target stage first
    if (targetStage) {
      const node = targetStage.findOne(`#${element.id}`);
      if (node) {
        // Get current position from Konva node (this is the actual rendered position)
        const currentX = node.x();
        const currentY = node.y();

        // Always use Konva node position if available (even if 0)
        // This ensures we get the actual rendered position
        if (currentX !== undefined && currentY !== undefined) {
          const result = {
            ...element,
            x: currentX,
            y: currentY,
          };

          return result;
        }
      }
    }

    // Try the other stage as fallback
    if (otherStage) {
      const node = otherStage.findOne(`#${element.id}`);
      if (node) {
        // Get current position from Konva node (this is the actual rendered position)
        const currentX = node.x();
        const currentY = node.y();

        // If position is available from node, use it (it's the current rendered position)
        if (currentX !== undefined && currentY !== undefined) {
          const result = {
            ...element,
            x: currentX,
            y: currentY,
          };

          return result;
        }
      }
    }

    // Also check topLayerStage – top-layer images live there, not on main or off stage.
    // Without this, their wrapper bbox contribution uses stale React-state coordinates.
    const topLayerStage = topLayerStageRef?.current;
    if (topLayerStage) {
      const node = topLayerStage.findOne(`#${element.id}`);
      if (node) {
        const currentX = node.x();
        const currentY = node.y();
        if (currentX !== undefined && currentY !== undefined) {
          return { ...element, x: currentX, y: currentY };
        }
      }
    }

    return element;
  };
  const getElementBoundingBox = (element, elementType, options = {}) => {
    const { includeStroke = false } = options;
    const rotation = element.rotation || 0;
    const strokePadding = includeStroke
      ? Math.max((element.strokeWidth || 0) / 2, 0)
      : 0;

    switch (elementType) {
      case "Rect": {
        const x = element.x || 0;
        const y = element.y || 0;
        const width = element.width || 0;
        const height = element.height || 0;

        // CRITICAL FIX: For rotated rectangles, get the actual bounding box from Konva node
        // This ensures we get the accurate bounding box accounting for rotation
        // Konva's getClientRect() automatically accounts for rotation and returns the axis-aligned bounding box
        // We use geometric bounds (without stroke) to prevent the wrapper box height from increasing more than expected
        if (rotation && rotation !== 0) {
          // Try to get bounding box from Konva node (most accurate - matches what's actually rendered)
          const mainStage = stageRef?.current;
          const offStage = offCanvasStageRef?.current;
          const topLayerStage = topLayerStageRef?.current;

          const rectNode =
            mainStage?.findOne(`#${element.id}`) ||
            offStage?.findOne(`#${element.id}`) ||
            topLayerStage?.findOne(`#${element.id}`);

          if (rectNode && typeof rectNode.getClientRect === "function") {
            try {
              // getClientRect() returns {x, y, width, height} in stage coordinates
              let clientRect;
              if (includeStroke) {
                clientRect = rectNode.getClientRect();
              } else {
                try {
                  // Prefer geometric bounds for non-wrapper calculations
                  clientRect = rectNode.getClientRect({ skipStroke: true });
                } catch (e) {
                  clientRect = rectNode.getClientRect();
                  const strokeWidth = element.strokeWidth || 0;
                  if (strokeWidth > 0 && clientRect) {
                    const fallbackStrokePadding = strokeWidth / 2;
                    clientRect = {
                      x: clientRect.x + fallbackStrokePadding,
                      y: clientRect.y + fallbackStrokePadding,
                      width: Math.max(0, clientRect.width - strokeWidth),
                      height: Math.max(0, clientRect.height - strokeWidth),
                    };
                  }
                }
              }

              if (clientRect && clientRect.width > 0 && clientRect.height > 0) {
                return {
                  x: clientRect.x,
                  y: clientRect.y,
                  width: clientRect.width,
                  height: clientRect.height,
                };
              }
            } catch (e) {
              // Fallback to calculated bounding box if getClientRect fails
            }
          }
        }

        // For non-rotated rectangles or if node not found, use geometric bounds
        // Rectangles are stored with top-left coordinates
        return getRotatedBoundingBox(
          x - strokePadding,
          y - strokePadding,
          width + strokePadding * 2,
          height + strokePadding * 2,
          rotation,
          false,
        );
      }
      case "Circle": {
        // CRITICAL FIX: For ellipses (when maintainAspectRatio is false), we need to use
        // radiusX and radiusY separately, not just the maximum
        // If maintainAspectRatio is true or radius exists, it's a circle - use radius for both dimensions
        // If maintainAspectRatio is false and radiusX/radiusY exist, it's an ellipse - use them separately
        const centerX = element.x || 0;
        const centerY = element.y || 0;

        let width, height;
        const maintainAspectRatio = element.maintainAspectRatio ?? true;

        if (
          maintainAspectRatio &&
          element.radius !== undefined &&
          element.radius !== null
        ) {
          // It's a circle - use radius for both dimensions
          const radius = element.radius;
          width = radius * 2;
          height = radius * 2;
        } else {
          // It's an ellipse - use radiusX and radiusY separately
          // If radius exists but maintainAspectRatio is false, still use radiusX/radiusY
          const radiusX = element.radiusX || element.radius || 0;
          const radiusY = element.radiusY || element.radius || 0;
          width = radiusX * 2;
          height = radiusY * 2;
        }

        // Circles/Ellipses use center coordinates
        return getRotatedBoundingBox(
          centerX,
          centerY,
          width + strokePadding * 2,
          height + strokePadding * 2,
          rotation,
          true,
        );
      }
      case "Image": {
        const imgWidth = element.width || 0;
        const imgHeight = element.height || 0;
        const imgX = element.x || 0;
        const imgY = element.y || 0;

        // CRITICAL FIX: For rotated images, get the actual bounding box from Konva node
        // This ensures we get the accurate bounding box accounting for rotation
        // Konva's getClientRect() automatically accounts for rotation and returns the axis-aligned bounding box
        // We use geometric bounds (without stroke) to prevent the wrapper box height from increasing more than expected
        if (rotation && rotation !== 0) {
          // Try to get bounding box from Konva node (most accurate - matches what's actually rendered)
          const mainStage = stageRef?.current;
          const offStage = offCanvasStageRef?.current;
          const topLayerStage = topLayerStageRef?.current;

          const imageNode =
            mainStage?.findOne(`#${element.id}`) ||
            offStage?.findOne(`#${element.id}`) ||
            topLayerStage?.findOne(`#${element.id}`);

          if (imageNode && typeof imageNode.getClientRect === "function") {
            try {
              // getClientRect() returns {x, y, width, height} in stage coordinates
              // Try to exclude stroke if the API supports it, otherwise use the geometric calculation
              // which doesn't include stroke width
              let clientRect;
              try {
                // Try with skipStroke option (if supported by Konva version)
                clientRect = imageNode.getClientRect({ skipStroke: true });
              } catch (e) {
                // If skipStroke is not supported, use regular getClientRect
                // Images don't typically have stroke, so this should be fine
                clientRect = imageNode.getClientRect();
              }

              if (clientRect && clientRect.width > 0 && clientRect.height > 0) {
                return {
                  x: clientRect.x,
                  y: clientRect.y,
                  width: clientRect.width,
                  height: clientRect.height,
                };
              }
            } catch (e) {
              // Fallback to calculated bounding box if getClientRect fails
            }
          }
        }

        // For non-rotated images or if node not found, use calculated bounding box
        // CRITICAL FIX: Images can use either center coordinates or top-left coordinates
        // depending on useTopLeftAnchor property
        // When useTopLeftAnchor === true, x and y represent top-left corner
        // When useTopLeftAnchor === false (default), x and y represent center
        const useTopLeftAnchor = element.useTopLeftAnchor === true;
        return getRotatedBoundingBox(
          imgX,
          imgY,
          imgWidth,
          imgHeight,
          rotation,
          !useTopLeftAnchor, // isCentered = true when NOT using top-left anchor
        );
      }
      case "Text": {
        const x = element.x || 0;
        const y = element.y || 0;
        const width = element.width || 0;

        // CRITICAL FIX: Get actual height from Konva node to match TextElement's rendered height exactly
        // This ensures the selection box matches the actual element bounds without extra padding
        let height = element.height || 0;

        // Try to get height from Konva node (most accurate - matches what's actually rendered)
        const mainStage = stageRef?.current;
        const offStage = offCanvasStageRef?.current;
        const topLayerStage = topLayerStageRef?.current;

        const textNode =
          mainStage?.findOne(`#${element.id}`) ||
          offStage?.findOne(`#${element.id}`) ||
          topLayerStage?.findOne(`#${element.id}`);

        if (textNode && textNode.height) {
          // Get actual rendered height from Konva node (this matches TextElement's totalContentHeight exactly)
          const nodeHeight = textNode.height();
          if (nodeHeight && nodeHeight > 0) {
            height = nodeHeight;
          }
        }

        // Fallback: If node not found or height is 0, calculate it (but this should rarely happen)
        if (!height || height === 0) {
          const canvasWidth = mainCanvasWidth;
          const calculatedHeight = calculateTotalContentHeight(
            element,
            canvasWidth,
          );
          // Apply same minimum height logic as TextElement
          const getMinHeight = () => {
            if (element.fontSize === 11) return 30;
            if (element.fontSize === 14 || element.fontSize === "14") return 35;
            return 43;
          };
          height = Math.max(calculatedHeight, getMinHeight());
        }

        // Add padding for stroke width to improve overlap detection for small selection boxes
        // This ensures that selection boxes drawn around the stroke of text elements will select them
        // Use padding to include stroke area - at least 3px to ensure small selection boxes work
        const strokeWidth = element.strokeWidth || 0;
        const padding = Math.max(strokeWidth / 2, 3); // At least 3px padding to include stroke area

        // CRITICAL FIX: Don't add padding to height/y to prevent extra spacing in selection box
        // The TextElement Group has height = totalContentHeight, and stroke is drawn on the Group
        // The actual bounds of the element are from y to y + height, so we shouldn't add padding to height/y
        // Only add padding to width/x for selection detection, but keep height/y accurate for visual bounds
        // Texts use top-left coordinates, so adjust x and expand width (but keep y and height accurate)
        return getRotatedBoundingBox(
          x - padding - strokePadding,
          y - strokePadding,
          width + padding * 2,
          height + strokePadding * 2,
          rotation,
          false,
        );
      }
      case "Arrow":
      case "DoubleSidedArrow":
      case "Line": {
        if (!element.points || element.points.length < 4) {
          return { x: element.x || 0, y: element.y || 0, width: 0, height: 0 };
        }
        // For lines/arrows, points are already relative to element position
        // We need to account for rotation if present
        const xCoords = element.points.filter((_, i) => i % 2 === 0);
        const yCoords = element.points.filter((_, i) => i % 2 === 1);
        let minX = Math.min(...xCoords);
        let maxX = Math.max(...xCoords);
        let minY = Math.min(...yCoords);
        let maxY = Math.max(...yCoords);

        // Check if this is an arrow (has arrow heads) and expand bounding box to include arrow heads
        const typeSaysArrow =
          element?.type === "Arrow" ||
          element?.type === "arrow" ||
          element?.type === "DoubleSidedArrow" ||
          element?.type === "double_sided_arrow";
        const typeSaysDouble =
          element?.type === "DoubleSidedArrow" ||
          element?.type === "double_sided_arrow";
        const hasStartArrow =
          element.hasStartArrow === true ||
          element.pointerAtBeginning === true ||
          typeSaysArrow;
        const hasEndArrow =
          element.hasEndArrow === true ||
          element.pointerAtEnd === true ||
          typeSaysArrow;
        // If the type explicitly says double-sided, ensure both ends are included even if flags are missing
        const effectiveHasStartArrow = typeSaysDouble ? true : hasStartArrow;
        const effectiveHasEndArrow = typeSaysDouble ? true : hasEndArrow;

        // Arrow head dimensions (matching UnifiedLineArrowElement)
        const { pointerLength, pointerWidth } = getUnifiedArrowHeadDimensions(
          element.strokeWidth || 2,
        );

        // Expand bounding box to include arrow heads
        if (effectiveHasStartArrow || effectiveHasEndArrow) {
          const elementX = element.x || 0;
          const elementY = element.y || 0;

          // Check start arrow head
          if (effectiveHasStartArrow && element.points.length >= 4) {
            const startX = elementX + element.points[0];
            const startY = elementY + element.points[1];
            const nextX = elementX + element.points[2];
            const nextY = elementY + element.points[3];

            // Calculate arrow head points
            const arrowHead = calculateArrowHeadPoints(
              nextX,
              nextY,
              startX,
              startY,
              pointerLength,
              pointerWidth,
            );

            // Expand bounding box to include arrow head triangle
            minX = Math.min(
              minX,
              arrowHead.tip.x - elementX,
              arrowHead.base1.x - elementX,
              arrowHead.base2.x - elementX,
            );
            maxX = Math.max(
              maxX,
              arrowHead.tip.x - elementX,
              arrowHead.base1.x - elementX,
              arrowHead.base2.x - elementX,
            );
            minY = Math.min(
              minY,
              arrowHead.tip.y - elementY,
              arrowHead.base1.y - elementY,
              arrowHead.base2.y - elementY,
            );
            maxY = Math.max(
              maxY,
              arrowHead.tip.y - elementY,
              arrowHead.base1.y - elementY,
              arrowHead.base2.y - elementY,
            );
          }

          // Check end arrow head
          if (effectiveHasEndArrow && element.points.length >= 4) {
            const endX = elementX + element.points[element.points.length - 2];
            const endY = elementY + element.points[element.points.length - 1];
            const prevX = elementX + element.points[element.points.length - 4];
            const prevY = elementY + element.points[element.points.length - 3];

            // Calculate arrow head points
            const arrowHead = calculateArrowHeadPoints(
              prevX,
              prevY,
              endX,
              endY,
              pointerLength,
              pointerWidth,
            );

            // Expand bounding box to include arrow head triangle
            minX = Math.min(
              minX,
              arrowHead.tip.x - elementX,
              arrowHead.base1.x - elementX,
              arrowHead.base2.x - elementX,
            );
            maxX = Math.max(
              maxX,
              arrowHead.tip.x - elementX,
              arrowHead.base1.x - elementX,
              arrowHead.base2.x - elementX,
            );
            minY = Math.min(
              minY,
              arrowHead.tip.y - elementY,
              arrowHead.base1.y - elementY,
              arrowHead.base2.y - elementY,
            );
            maxY = Math.max(
              maxY,
              arrowHead.tip.y - elementY,
              arrowHead.base1.y - elementY,
              arrowHead.base2.y - elementY,
            );
          }
        }

        // Add padding for stroke width to improve overlap detection
        const strokeWidth = element.strokeWidth || 2;
        const padding = Math.max(strokeWidth / 2, 5); // At least 5px padding

        const baseX = (element.x || 0) + minX - padding;
        const baseY = (element.y || 0) + minY - padding;
        const baseWidth = maxX - minX + padding * 2;
        const baseHeight = maxY - minY + padding * 2;

        // If rotated, calculate rotated bounding box
        if (rotation && rotation !== 0) {
          return getRotatedBoundingBox(
            baseX,
            baseY,
            baseWidth,
            baseHeight,
            rotation,
          );
        }

        return {
          x: baseX,
          y: baseY,
          width: baseWidth,
          height: baseHeight,
        };
      }
      default:
        return { x: 0, y: 0, width: 0, height: 0 };
    }
  };

  // Generic copy handler for all element types
  const handleCopyElementBetweenCanvases = useCallback(
    (elementId, elementType, sourceCanvas) => {
      // Only allow copying from off-canvas to main canvas
      if (sourceCanvas !== "offCanvas") {
        return;
      }

      const mainStage = stageRef?.current;
      const offStage = offCanvasStageRef?.current;

      if (!mainStage || !offStage) {
        return;
      }

      // Find the element to copy based on type
      let elementToCopy = null;
      let setMainState = null;
      let setOffCanvasState = null;

      switch (elementType) {
        case "Rect":
          elementToCopy = offCanvasRectangles.find((el) => el.id === elementId);
          setMainState = setRectangles;
          break;
        case "Circle":
          elementToCopy = offCanvasCircles.find((el) => el.id === elementId);
          setMainState = setCircles;
          break;
        case "Image":
          elementToCopy = offCanvasImages.find((el) => el.id === elementId);
          setMainState = setImages;
          break;
        case "Text":
          elementToCopy = offCanvasTexts.find((el) => el.id === elementId);
          setMainState = setTexts;
          break;
        case "Arrow":
          elementToCopy = offCanvasArrows.find((el) => el.id === elementId);
          setMainState = setArrows;
          break;
        case "DoubleSidedArrow":
          elementToCopy = offCanvasDoubleSidedArrows.find(
            (el) => el.id === elementId,
          );
          setMainState = setDoubleSidedArrows;
          break;
        case "Line":
          elementToCopy = offCanvasLines.find((el) => el.id === elementId);
          setMainState = setLines;
          break;
        default:
          return;
      }

      if (!elementToCopy) {
        return;
      }

      const sourceBBox = offStage.container().getBoundingClientRect();
      const targetBBox = mainStage.container().getBoundingClientRect();

      const { clientX, clientY } = calculateClientCoordinates({
        type: elementType,
        draggedElement: elementToCopy,
        newX: elementToCopy.x,
        newY: elementToCopy.y,
        sourceBBox,
      });

      let newX = clientX - targetBBox.left;
      let newY = clientY - targetBBox.top;

      // Get canvas dimensions
      const canvasWidth = mainCanvasWidth;
      const canvasPixelHeight =
        fixedStageHeightPx;

      // Get element bounding box for overlap checking (accounts for rotation)
      const elementBBox = getElementBoundingBox(elementToCopy, elementType);
      const elementWidth = elementBBox.width;
      const elementHeight = elementBBox.height;

      // Adjust position based on element type (centered vs top-left)
      // For rotated elements, elementBBox is already the axis-aligned bounding box
      // We need to position the element so its rotated bounding box fits within canvas
      let testRectX, testRectY;

      if (elementType === "Image" || elementType === "Circle") {
        // These are centered - elementBBox is the rotated bounding box with top-left at (x, y)
        // We want the center of the rotated bounding box to be at (newX, newY)
        testRectX = newX - elementWidth / 2;
        testRectY = newY - elementHeight / 2;
      } else {
        // These use top-left coordinates - elementBBox is the rotated bounding box
        // We want the top-left of the rotated bounding box to be at (newX, newY)
        testRectX = newX;
        testRectY = newY;
      }

      // Ensure initial position is within canvas bounds using rotated bounding box dimensions
      const margin = 20;
      testRectX = Math.max(
        margin,
        Math.min(testRectX, canvasWidth - elementWidth - margin),
      );
      testRectY = Math.max(
        margin,
        Math.min(testRectY, canvasPixelHeight - elementHeight - margin),
      );

      // Create a test rectangle at the calculated position using rotated bounding box
      const testRect = {
        x: testRectX,
        y: testRectY,
        width: elementWidth,
        height: elementHeight,
      };

      // Collect all elements on main canvas for overlap checking
      const allMainCanvasElements = {
        rectangles: rectangles || [],
        circles: circles || [],
        images: images || [],
        texts: texts || [],
        arrows: arrows || [],
        doubleSidedArrows: doubleSidedArrows || [],
        lines: lines || [],
      };

      // Always check for overlapping with ALL existing elements on main canvas
      const hasOverlapWithExisting = hasOverlapWithAnyElement(
        testRect,
        allMainCanvasElements,
      );

      if (hasOverlapWithExisting) {
        // Find a non-overlapping position
        const newPosition = findNonOverlappingPosition(
          testRect,
          allMainCanvasElements,
          canvasWidth,
          canvasPixelHeight,
        );
        testRectX = newPosition.x;
        testRectY = newPosition.y;

        // Double-check the new position is within bounds using rotated bounding box
        testRectX = Math.max(
          margin,
          Math.min(testRectX, canvasWidth - elementWidth - margin),
        );
        testRectY = Math.max(
          margin,
          Math.min(testRectY, canvasPixelHeight - elementHeight - margin),
        );
      }

      // Convert back to element's coordinate system (center or top-left)
      if (elementType === "Image" || elementType === "Circle") {
        // For centered elements, the rotated bounding box center should be at the element center
        // testRectX, testRectY is the top-left of the rotated bounding box
        newX = testRectX + elementWidth / 2;
        newY = testRectY + elementHeight / 2;
      } else if (
        elementType === "Line" ||
        elementType === "Arrow" ||
        elementType === "DoubleSidedArrow"
      ) {
        // For lines/arrows, points are relative to the Group position (x, y)
        // The bounding box is at testRectX, testRectY (which includes padding for overlap detection)
        // We need to calculate where the Group should be positioned
        // Bounding box calculation: baseX = element.x + minX - padding, so element.x = baseX - minX + padding
        if (!elementToCopy.points || elementToCopy.points.length < 4) {
          newX = testRectX;
          newY = testRectY;
        } else {
          const xCoords = elementToCopy.points.filter((_, i) => i % 2 === 0);
          const yCoords = elementToCopy.points.filter((_, i) => i % 2 === 1);
          const minX = Math.min(...xCoords);
          const minY = Math.min(...yCoords);

          // Account for padding that was added to bounding box for overlap detection
          const strokeWidth = elementToCopy.strokeWidth || 2;
          const padding = Math.max(strokeWidth / 2, 5);

          // The bounding box top-left is at testRectX, testRectY (with padding)
          // Group position should be: testRectX - minX + padding, testRectY - minY + padding
          newX = testRectX - minX + padding;
          newY = testRectY - minY + padding;
        }
      } else {
        // For top-left elements (Rect, Text), we need to calculate where the element should be positioned
        // so that its rotated bounding box is at testRectX, testRectY
        const rotation = elementToCopy.rotation || 0;
        if (rotation && rotation !== 0) {
          // We need to work backwards: given the rotated bounding box position,
          // calculate where the element's top-left should be
          // The element's center is at: element.x + element.width/2, element.y + element.height/2
          // After rotation, the bounding box center is at: testRectX + elementWidth/2, testRectY + elementHeight/2
          // The bounding box center equals the element center (rotation is around center)
          const bboxCenterX = testRectX + elementWidth / 2;
          const bboxCenterY = testRectY + elementHeight / 2;
          // Element center = bbox center, so element top-left = center - width/2, center - height/2
          const originalElementWidth = elementToCopy.width || 0;
          const originalElementHeight = elementToCopy.height || 0;
          newX = bboxCenterX - originalElementWidth / 2;
          newY = bboxCenterY - originalElementHeight / 2;
        } else {
          // No rotation, so element position = bounding box position
          newX = testRectX;
          newY = testRectY;
        }
      }

      // Generate new name based on array length
      let newName = elementToCopy.name;
      switch (elementType) {
        case "Rect":
          newName = `Rectangle ${(rectangles?.length || 0) + 1}`;
          break;
        case "Circle":
          newName = `Circle ${(circles?.length || 0) + 1}`;
          break;
        case "Image":
          newName = `Image ${(images?.length || 0) + 1}`;
          break;
        case "Text":
          newName = `Text ${(texts?.length || 0) + 1}`;
          break;
        case "Arrow":
          newName = `Arrow ${(arrows?.length || 0) + 1}`;
          break;
        case "DoubleSidedArrow":
          newName = `Double Sided Arrow ${
            (doubleSidedArrows?.length || 0) + 1
          }`;
          break;
        case "Line":
          // Check if it's a unified element (has arrow properties)
          const hasStartArrow =
            elementToCopy.hasStartArrow === true ||
            elementToCopy.pointerAtBeginning === true;
          const hasEndArrow =
            elementToCopy.hasEndArrow === true ||
            elementToCopy.pointerAtEnd === true;

          // Unified elements are stored in lines array, so use lines.length for numbering
          // But name should reflect the element type
          if (hasStartArrow && hasEndArrow) {
            newName = `Double Sided Arrow ${(lines?.length || 0) + 1}`;
          } else if (hasStartArrow || hasEndArrow) {
            newName = `Arrow ${(lines?.length || 0) + 1}`;
          } else {
            newName = `Stroke ${(lines?.length || 0) + 1}`;
          }
          break;
        default:
          // Keep original name if type is unknown
          break;
      }

      // Clone the element with new position, ID, and name
      // Create a clean copy without id to prevent any ID conflicts
      const { id, ...cleanElementToCopy } = elementToCopy;
      const clonedElement = {
        ...clearCrossCanvasCloneServerIdentity(cleanElementToCopy),
        id: uuidv4(), // Always set a fresh unique ID
        name: newName,
        x: newX,
        y: newY,
        isNew: false,
      };

      // Add to main canvas
      setMainState((prev) => [...prev, clonedElement]);

      // Update selection
      const selectionPayload = {
        ...clonedElement,
        type: elementType,
      };

      setSelectedObject(selectionPayload);
      setSelectedObjects([selectionPayload]);

      // DEBUG: Log selection change
      setSelectedArrow(null);
      setSelectedLine(null);
      setSelectedDoubleSidedArrow(null);
      setSelectedImage(null);

      // For text elements, also set selectedText and selectedTextId
      if (elementType === "Text") {
        setSelectedText(selectionPayload);
        setSelectedTextId(clonedElement.id);
        setShowTextBox(true); // Show text box for the new text element
      } else {
        setSelectedText(null);
        setSelectedTextId(null);
        setShowTextBox(false);
      }

      setShowEditTask(false);
      setShowEditNote(false);

      if (typeof saveToHistory === "function") {
        setTimeout(() => saveToHistory(), 0);
      }
    },
    [
      rectangles,
      circles,
      images,
      texts,
      arrows,
      doubleSidedArrows,
      lines,
      offCanvasRectangles,
      offCanvasCircles,
      offCanvasImages,
      offCanvasTexts,
      offCanvasArrows,
      offCanvasDoubleSidedArrows,
      offCanvasLines,
      stageRef,
      offCanvasStageRef,
      setRectangles,
      setCircles,
      setImages,
      setTexts,
      setArrows,
      setDoubleSidedArrows,
      setLines,
      setSelectedObject,
      setSelectedObjects,
      setSelectedArrow,
      setSelectedLine,
      setSelectedDoubleSidedArrow,
      setSelectedImage,
      setSelectedText,
      setSelectedTextId,
      setShowTextBox,
      setShowEditTask,
      setShowEditNote,
      saveToHistory,
      canvasHeight,
    ],
  );

  // Keep the rectangle-specific handler for backward compatibility
  const handleCopyRectangleBetweenCanvases = useCallback(
    (rectangleId, sourceCanvas) => {
      handleCopyElementBetweenCanvases(rectangleId, "Rect", sourceCanvas);
    },
    [handleCopyElementBetweenCanvases],
  );

  // Helper function to check if group should be moved to opposite canvas based on position
  // Can check either group center OR a specific point (like dragged element position)
  const shouldMoveGroupToOppositeCanvas = useCallback(
    (checkX, checkY, objectsToCheck) => {
      if (!objectsToCheck || objectsToCheck.length === 0) {
        return { shouldMove: false, targetCanvas: null };
      }

      const mainStage = stageRef?.current;
      const offStage = offCanvasStageRef?.current;

      if (!mainStage || !offStage) {
        return { shouldMove: false, targetCanvas: null };
      }

      // Determine which canvas the group is currently on
      let allFromMainCanvas = true;
      let allFromOffCanvas = true;

      objectsToCheck.forEach((obj) => {
        if (!obj || !obj.id || !obj.type) return;

        let isInMain = false;
        let isInOff = false;

        switch (obj.type) {
          case "Rect":
            isInMain = rectangles.some((r) => r.id === obj.id);
            isInOff = offCanvasRectangles.some((r) => r.id === obj.id);
            break;
          case "Circle":
            isInMain = circles.some((c) => c.id === obj.id);
            isInOff = offCanvasCircles.some((c) => c.id === obj.id);
            break;
          case "Image":
            isInMain = images.some((img) => img.id === obj.id);
            isInOff = offCanvasImages.some((img) => img.id === obj.id);
            break;
          case "Text":
            isInMain = texts.some((t) => t.id === obj.id);
            isInOff = offCanvasTexts.some((t) => t.id === obj.id);
            break;
          case "Arrow":
            // CRITICAL FIX: Check lines array FIRST for unified arrow elements (with arrow heads)
            // This prioritizes unified arrows over legacy arrows
            isInMain = lines.some((l) => {
              if (l.id !== obj.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
            isInOff = offCanvasLines.some((l) => {
              if (l.id !== obj.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
            // Fallback to legacy arrows array if not found in lines
            if (!isInMain) {
              isInMain = arrows.some((a) => a.id === obj.id);
            }
            if (!isInOff) {
              isInOff = offCanvasArrows.some((a) => a.id === obj.id);
            }
            break;
          case "DoubleSidedArrow":
            // Check doubleSidedArrows array first
            isInMain = doubleSidedArrows.some((a) => a.id === obj.id);
            isInOff = offCanvasDoubleSidedArrows.some((a) => a.id === obj.id);
            // Also check lines array for unified double-sided arrow elements
            if (!isInMain) {
              isInMain = lines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });
            }
            if (!isInOff) {
              isInOff = offCanvasLines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });
            }
            break;
          case "Line":
            // Lines without arrow heads are in the lines array
            isInMain = lines.some((l) => {
              if (l.id !== obj.id) return false;
              return (
                !l.hasStartArrow &&
                !l.hasEndArrow &&
                !l.pointerAtBeginning &&
                !l.pointerAtEnd
              );
            });
            isInOff = offCanvasLines.some((l) => {
              if (l.id !== obj.id) return false;
              return (
                !l.hasStartArrow &&
                !l.hasEndArrow &&
                !l.pointerAtBeginning &&
                !l.pointerAtEnd
              );
            });
            break;
        }

        if (isInMain) allFromOffCanvas = false;
        if (isInOff) allFromMainCanvas = false;
      });

      const sourceCanvas = allFromOffCanvas ? "offCanvas" : "main";
      const sourceStage = sourceCanvas === "offCanvas" ? offStage : mainStage;

      // Determine target canvas based on group center position
      const mainBBox = mainStage.container().getBoundingClientRect();
      const offBBox = offStage.container().getBoundingClientRect();
      const mainCanvasRight = mainBBox.left + mainBBox.width;
      const mainCanvasBottom =
        mainBBox.top + fixedStageHeightPx;

      // Use a representative element to determine target canvas
      const firstElement = objectsToCheck[0];
      if (!firstElement) {
        return { shouldMove: false, targetCanvas: null };
      }

      const target = determineTargetCanvas({
        type: firstElement.type,
        draggedElement: { x: 0, y: 0 },
        clientX: checkX, // Use the provided check position (could be group center or dragged element position)
        clientY: checkY, // Use the provided check position
        mainBBox,
        offBBox,
        mainCanvasRight,
        mainCanvasBottom,
        sourceStageRef: sourceStage,
        mainStage,
        offCanvasStage: offStage,
        isFromMainCanvas: sourceCanvas === "main",
      });

      if (target && target !== sourceCanvas) {
        return { shouldMove: true, targetCanvas: target };
      }

      return { shouldMove: false, targetCanvas: null };
    },
    [
      stageRef,
      offCanvasStageRef,
      rectangles,
      offCanvasRectangles,
      circles,
      offCanvasCircles,
      images,
      offCanvasImages,
      texts,
      offCanvasTexts,
      arrows,
      offCanvasArrows,
      doubleSidedArrows,
      offCanvasDoubleSidedArrows,
      lines,
      offCanvasLines,
      canvasHeight,
    ],
  );

  // Handler to copy/move entire group between canvases
  // Expose via ref or callback if needed
  // wrapperFinalX, wrapperFinalY: optional wrapper position in source canvas coordinates
  // mouseClientX, mouseClientY: optional mouse position to preserve drop location
  const handleCopyGroupBetweenCanvases = useCallback(
    (
      wrapperFinalX = null,
      wrapperFinalY = null,
      mouseClientX = null,
      mouseClientY = null,
      overridePositions = {}, // Map of id -> {x, y} to use instead of state (e.g. for dragged element)
    ) => {
      // Prevent multiple concurrent group moves
      if (isGroupMovingRef.current) {
        return;
      }

      isGroupMovingRef.current = true;

      if (!selectedObjects || selectedObjects.length === 0) {
        isGroupMovingRef.current = false;
        return;
      }

      const mainStage = stageRef?.current;
      const offStage = offCanvasStageRef?.current;

      if (!mainStage || !offStage) {
        isGroupMovingRef.current = false;
        return;
      }

      // Determine which canvas the group is currently on
      // Check if all elements are from the same canvas
      let allFromMainCanvas = true;
      let allFromOffCanvas = true;

      selectedObjects.forEach((obj) => {
        if (!obj || !obj.id || !obj.type) return;

        let isInMain = false;
        let isInOff = false;

        switch (obj.type) {
          case "Rect":
            isInMain = rectangles.some((r) => r.id === obj.id);
            isInOff = offCanvasRectangles.some((r) => r.id === obj.id);
            break;
          case "Circle":
            isInMain = circles.some((c) => c.id === obj.id);
            isInOff = offCanvasCircles.some((c) => c.id === obj.id);
            break;
          case "Image":
            isInMain = images.some((img) => img.id === obj.id);
            isInOff = offCanvasImages.some((img) => img.id === obj.id);
            break;
          case "Text":
            isInMain = texts.some((t) => t.id === obj.id);
            isInOff = offCanvasTexts.some((t) => t.id === obj.id);
            break;
          case "Arrow":
            // CRITICAL FIX: Check lines array FIRST for unified arrow elements (with arrow heads)
            // This prioritizes unified arrows over legacy arrows
            isInMain = lines.some((l) => {
              if (l.id !== obj.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
            isInOff = offCanvasLines.some((l) => {
              if (l.id !== obj.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
            // Fallback to legacy arrows array if not found in lines
            if (!isInMain) {
              isInMain = arrows.some((a) => a.id === obj.id);
            }
            if (!isInOff) {
              isInOff = offCanvasArrows.some((a) => a.id === obj.id);
            }
            break;
          case "DoubleSidedArrow":
            // Check doubleSidedArrows array first
            isInMain = doubleSidedArrows.some((a) => a.id === obj.id);
            isInOff = offCanvasDoubleSidedArrows.some((a) => a.id === obj.id);
            // Also check lines array for unified double-sided arrow elements
            if (!isInMain) {
              isInMain = lines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });
            }
            if (!isInOff) {
              isInOff = offCanvasLines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });
            }
            break;
          case "Line":
            // Lines without arrow heads are in the lines array
            isInMain = lines.some((l) => {
              if (l.id !== obj.id) return false;
              // Only return lines without arrow heads
              return (
                !l.hasStartArrow &&
                !l.hasEndArrow &&
                !l.pointerAtBeginning &&
                !l.pointerAtEnd
              );
            });
            isInOff = offCanvasLines.some((l) => {
              if (l.id !== obj.id) return false;
              // Only return lines without arrow heads
              return (
                !l.hasStartArrow &&
                !l.hasEndArrow &&
                !l.pointerAtBeginning &&
                !l.pointerAtEnd
              );
            });
            break;
        }

        if (isInMain) allFromOffCanvas = false;
        if (isInOff) allFromMainCanvas = false;
      });

      // Determine source canvas
      const sourceCanvas = allFromOffCanvas ? "offCanvas" : "main";
      const targetCanvas = sourceCanvas === "offCanvas" ? "main" : "offCanvas";

     // Calculate group center from current positions (after drag) to maintain relative positions
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      selectedObjects.forEach((obj) => {
        if (!obj || !obj.id || !obj.type) return;

        let element = null;
        if (sourceCanvas === "offCanvas") {
          switch (obj.type) {
            case "Rect":
              element = offCanvasRectangles.find((r) => r.id === obj.id);
              break;
            case "Circle":
              element = offCanvasCircles.find((c) => c.id === obj.id);
              break;
            case "Image":
              element = offCanvasImages.find((img) => img.id === obj.id);
              break;
            case "Text":
              element = offCanvasTexts.find((t) => t.id === obj.id);
              break;
            case "Arrow":
              // CRITICAL FIX: Check offCanvasLines array FIRST for unified arrow elements (with arrow heads)
              // This prioritizes unified arrows over legacy arrows
              element = offCanvasLines.find((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow || hasEndArrow;
              });
              // Fallback to legacy offCanvasArrows array if not found in lines
              if (!element) {
                element = offCanvasArrows.find((a) => a.id === obj.id);
              }
              break;
            case "DoubleSidedArrow":
              // Check offCanvasDoubleSidedArrows array first
              element = offCanvasDoubleSidedArrows.find((a) => a.id === obj.id);
              // Also check offCanvasLines array for unified double-sided arrow elements
              if (!element) {
                element = offCanvasLines.find((l) => {
                  if (l.id !== obj.id) return false;
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  const typeSaysDouble =
                    l.type === "DoubleSidedArrow" ||
                    l.type === "double_sided_arrow";
                  return (hasStartArrow && hasEndArrow) || typeSaysDouble;
                });
              }
              break;
            case "Line":
              // Lines without arrow heads are in the offCanvasLines array
              element = offCanvasLines.find((l) => {
                if (l.id !== obj.id) return false;
                // Only return lines without arrow heads
                return (
                  !l.hasStartArrow &&
                  !l.hasEndArrow &&
                  !l.pointerAtBeginning &&
                  !l.pointerAtEnd
                );
              });
              break;
          }
        } else {
          switch (obj.type) {
            case "Rect":
              // CRITICAL FIX: Check both rectangles and offCanvasRectangles arrays
              // Elements can be in either array regardless of sourceCanvas determination
              element = rectangles.find((r) => r.id === obj.id);
              if (!element) {
                element = offCanvasRectangles.find((r) => r.id === obj.id);
              }
              break;
            case "Circle":
              // CRITICAL FIX: Check both circles and offCanvasCircles arrays
              // Elements can be in either array regardless of sourceCanvas determination
              element = circles.find((c) => c.id === obj.id);
              if (!element) {
                element = offCanvasCircles.find((c) => c.id === obj.id);
              }
              break;
            case "Image":
              // CRITICAL FIX: Check both images and offCanvasImages arrays
              // Elements can be in either array regardless of sourceCanvas determination
              element = images.find((img) => img.id === obj.id);
              if (!element) {
                element = offCanvasImages.find((img) => img.id === obj.id);
              }
              break;
            case "Text":
              // CRITICAL FIX: Check both texts and offCanvasTexts arrays
              // Text elements can be in either array regardless of sourceCanvas determination
              // This prevents selection box stretching when elements are mixed between canvases
              element = texts.find((t) => t.id === obj.id);
              if (!element) {
                element = offCanvasTexts.find((t) => t.id === obj.id);
              }
              break;
            case "Arrow":
              // CRITICAL FIX: Check lines array FIRST for unified arrow elements (with arrow heads)
              // This prioritizes unified arrows over legacy arrows
              element = lines.find((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow || hasEndArrow;
              });
              // Fallback to legacy arrows array if not found in lines
              if (!element) {
                element = arrows.find((a) => a.id === obj.id);
              }
              break;
            case "DoubleSidedArrow":
              // Check doubleSidedArrows array first
              element = doubleSidedArrows.find((a) => a.id === obj.id);
              // Also check lines array for unified double-sided arrow elements
              if (!element) {
                element = lines.find((l) => {
                  if (l.id !== obj.id) return false;
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  return hasStartArrow && hasEndArrow;
                });
              }
              break;
            case "Line":
              // Lines without arrow heads are in the lines array
              element = lines.find((l) => {
                if (l.id !== obj.id) return false;
                // Only return lines without arrow heads
                return (
                  !l.hasStartArrow &&
                  !l.hasEndArrow &&
                  !l.pointerAtBeginning &&
                  !l.pointerAtEnd
                );
              });
              break;
          }
        }

        if (!element) return;

        let currentMinX, currentMinY, currentMaxX, currentMaxY;

        if (overridePositions && overridePositions[obj.id]) {
          const { x: overrideX, y: overrideY } = overridePositions[obj.id];
          const tempElement = { ...element, x: overrideX, y: overrideY };

          // CRITICAL FIX: Calculate bounding box using consistent coordinate system
          // Use element center coordinates for all element types to avoid mixed coordinate systems
          let elementCenterX, elementCenterY;
          const bbox = getElementBoundingBox(tempElement, obj.type);

          if (obj.type === "Text") {
            // For text elements, use the override position (top-left) and calculate center
            elementCenterX = overrideX + bbox.width / 2;
            elementCenterY = overrideY + bbox.height / 2;
          } else if (obj.type === "Image" || obj.type === "Circle") {
            // For images and circles, override position is already center
            elementCenterX = overrideX;
            elementCenterY = overrideY;
          } else if (
            obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"
          ) {
            // For lines/arrows, calculate center from points
            const points = tempElement.points || [0, 0, 0, 0];
            if (points.length >= 4) {
              const xCoords = points.filter((_, i) => i % 2 === 0);
              const yCoords = points.filter((_, i) => i % 2 === 1);
              const minX = Math.min(...xCoords);
              const maxX = Math.max(...xCoords);
              const minY = Math.min(...yCoords);
              const maxY = Math.max(...yCoords);
              elementCenterX = overrideX + (minX + maxX) / 2;
              elementCenterY = overrideY + (minY + maxY) / 2;
            } else {
              elementCenterX = overrideX;
              elementCenterY = overrideY;
            }
          } else {
            // For rectangles and other top-left coordinate elements
            elementCenterX = overrideX + bbox.width / 2;
            elementCenterY = overrideY + bbox.height / 2;
          }

          // Calculate bounding box from center coordinates
          currentMinX = elementCenterX - bbox.width / 2;
          currentMinY = elementCenterY - bbox.height / 2;
          currentMaxX = elementCenterX + bbox.width / 2;
          currentMaxY = elementCenterY + bbox.height / 2;
        } else {
          // CRITICAL FIX: Calculate bounding box using consistent coordinate system
          // Use element center coordinates for all element types to avoid mixed coordinate systems
          let elementCenterX, elementCenterY;
          const bbox = getElementBoundingBox(element, obj.type);

          if (obj.type === "Text") {
            // For text elements, use actual position (top-left) and calculate center
            elementCenterX = (element.x || 0) + bbox.width / 2;
            elementCenterY = (element.y || 0) + bbox.height / 2;
          } else if (obj.type === "Image" || obj.type === "Circle") {
            // For images and circles, position is already center
            elementCenterX = element.x || 0;
            elementCenterY = element.y || 0;
          } else if (
            obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"
          ) {
            // For lines/arrows, calculate center from points
            const points = element.points || [0, 0, 0, 0];
            if (points.length >= 4) {
              const xCoords = points.filter((_, i) => i % 2 === 0);
              const yCoords = points.filter((_, i) => i % 2 === 1);
              const minX = Math.min(...xCoords);
              const maxX = Math.max(...xCoords);
              const minY = Math.min(...yCoords);
              const maxY = Math.max(...yCoords);
              elementCenterX = (element.x || 0) + (minX + maxX) / 2;
              elementCenterY = (element.y || 0) + (minY + maxY) / 2;
            } else {
              elementCenterX = element.x || 0;
              elementCenterY = element.y || 0;
            }
          } else {
            // For rectangles and other top-left coordinate elements
            elementCenterX = (element.x || 0) + bbox.width / 2;
            elementCenterY = (element.y || 0) + bbox.height / 2;
          }

          // Calculate bounding box from center coordinates
          currentMinX = elementCenterX - bbox.width / 2;
          currentMinY = elementCenterY - bbox.height / 2;
          currentMaxX = elementCenterX + bbox.width / 2;
          currentMaxY = elementCenterY + bbox.height / 2;
        }

        minX = Math.min(minX, currentMinX);
        minY = Math.min(minY, currentMinY);
        maxX = Math.max(maxX, currentMaxX);
        maxY = Math.max(maxY, currentMaxY);
      });

      // CRITICAL FIX: Ensure groupCenterX/Y are pure Source Coordinates
      // The previous loop (lines 3601-3615) mixed Source and Target coordinates via overridePositions
      // We must use the clean values calculated below (sMinX etc) for relative positioning
      // Use temporary variables here, will be overwritten by the correct calculation below
      let groupCenterX = (minX + maxX) / 2;
      let groupCenterY = (minY + maxY) / 2;

      // Get canvas bounding boxes
      const sourceBBox =
        sourceCanvas === "offCanvas"
          ? offStage.container().getBoundingClientRect()
          : mainStage.container().getBoundingClientRect();
      const targetBBox =
        targetCanvas === "offCanvas"
          ? offStage.container().getBoundingClientRect()
          : mainStage.container().getBoundingClientRect();

      // Calculate offset to preserve drop position
      let offsetX, offsetY;

      if (
        wrapperFinalX !== null &&
        wrapperFinalY !== null &&
        mouseClientX !== null &&
        mouseClientY !== null
      ) {
        // Use wrapper's final position and mouse position to preserve where user dropped the group
        // The wrapper's top-left is at wrapperFinalX, wrapperFinalY in source canvas
        // The group center is at wrapperFinalX + wrapperWidth/2, wrapperFinalY + wrapperHeight/2

        // Calculate wrapper dimensions from group bounding box
        const wrapperWidth = maxX - minX;
        const wrapperHeight = maxY - minY;

        // Group center in source canvas coordinates
        const sourceGroupCenterX = wrapperFinalX + wrapperWidth / 2;
        const sourceGroupCenterY = wrapperFinalY + wrapperHeight / 2;

        // Group center in client coordinates (where it currently is)
        const sourceGroupCenterClientX = sourceBBox.left + sourceGroupCenterX;
        const sourceGroupCenterClientY = sourceBBox.top + sourceGroupCenterY;

        // Place the group center at the mouse position where it was dropped
        // This is what the user expects - where they drop it, that's where it should be
        const targetGroupCenterClientX = mouseClientX;
        const targetGroupCenterClientY = mouseClientY;

        // Convert to target canvas stage coordinates
        const targetGroupCenterX = targetGroupCenterClientX - targetBBox.left;
        const targetGroupCenterY = targetGroupCenterClientY - targetBBox.top;

        // Calculate offset to move group center from current position to target position
        // Current position is groupCenterX, groupCenterY in source canvas coordinates
        // We need to convert it to target canvas coordinates first
        const currentGroupCenterInTargetCoordsX =
          sourceGroupCenterClientX - targetBBox.left;
        const currentGroupCenterInTargetCoordsY =
          sourceGroupCenterClientY - targetBBox.top;

        offsetX = targetGroupCenterX - currentGroupCenterInTargetCoordsX;
        offsetY = targetGroupCenterY - currentGroupCenterInTargetCoordsY;
      } else {
        // Fallback: center the group on target canvas
        const targetCenterX = targetBBox.left + targetBBox.width / 2;
        const targetCenterY = targetBBox.top + targetBBox.height / 2;
        const sourceCenterX = sourceBBox.left + sourceBBox.width / 2;
        const sourceCenterY = sourceBBox.top + sourceBBox.height / 2;

        offsetX = targetCenterX - sourceCenterX;
        offsetY = targetCenterY - sourceCenterY;
      }

      // Calculate target group center in stage coordinates for element positioning
      // Calculate target group center in stage coordinates for element positioning
      let targetGroupCenterStageX, targetGroupCenterStageY;

      // CRITICAL FIX: Anchor group to the specific element being dragged
      // If we have overridePositions, it means a specific element triggered this move
      // We should position the group such that this element ends up exactly at its override position
      const draggedElementId = overridePositions
        ? Object.keys(overridePositions)[0]
        : null;

      if (draggedElementId) {
        const { x: newX, y: newY } = overridePositions[draggedElementId];
        // Find the original element to get its dimensions
        // We need to look in the source arrays
        let validElement = null;
        let elementType = null;

        selectedObjects.forEach((obj) => {
          if (obj.id === draggedElementId) {
            elementType = obj.type;
            const actualElement = getActualElementForDrag(obj);
            validElement = actualElement;
          }
        });

        if (validElement) {
          // Calculate the offset of this element from the Group Center (in Source coordinates)
          // We use the computed groupCenterX/Y from earlier (which respects overridePositions for bbox)
          // Wait, the groupCenterX calculated earlier USES the override position for this element
          // So groupCenterX IS the target group center!
          // precise_note: earlier loop calculated minX/minY using overridePositions.
          // So (minX + maxX)/2 IS ALREADY the target group center in stage coordinates?
          // Let's verify.
          // minX/maxX are calculated using element.x (source) OR override.x (target).
          // If we mix source and target coordinates in minX calculation, the result is garbage.
          // The loop at 3438 mixes them!
          // If overridePositions is present, that element is in Target coords. Others are in Source coords.
          // This is the root cause of the "stretching"/"separation".
          // We cannot mix coordinates.
          // CORRECTION:
          // 1. Calculate Group BBox in SOURCE coordinates (ignoring override for a moment, or untranslating it).
          //    Actually, we want RELATIVE positions. Relative positions are constant.
          // 2. We need the "Anchor Element" (the dragged one).
          // 3. We know where the Anchor Element IS going to be (newX, newY).
          // 4. We know where every other element is RELATIVE to the Anchor Element.
          // 5. TargetPos(Other) = TargetPos(Anchor) - RelativePos(Anchor) + RelativePos(Other)
          //    Or simply: TargetPos(Other) = TargetPos(Anchor) + (SourcePos(Other) - SourcePos(Anchor))
          // So we don't need Group Center at all for placement if we have an anchor!
          // But we need a Strategy.
          // Strategy:
          // If draggedElementId exists:
          //    TargetGroupCenterX = newX + (ElementWidth/2) - (ElementCenter - GroupCenter_Source)
          //    This seems complex to wiring into existing loop.
          // Simpler Strategy:
          // Calculate Source Group Center (purely source coords).
          // Calculate Delta = NewPos(Anchor) - OldPos(Anchor).
          // TargetGroupCenter = SourceGroupCenter + Delta.
          // Let's implement "TargetGroupCenter = SourceGroupCenter + Delta" logic.
          // First we need Source Group Center.
          // We need to re-run the bbox loop properly, purely on source coordinates.
        }
      }

      // Re-calculating proper Source Group values to avoid the "mixed coordinates" mess
      // CRITICAL: Calculate group bounding box using ONLY source coordinates (no overrides)
      let sMinX = Infinity,
        sMinY = Infinity,
        sMaxX = -Infinity,
        sMaxY = -Infinity;

      // Store element positions for relative offset calculation
      // CRITICAL FIX: Use persistent storage to avoid recalculating with modified coordinates
      let elementRelativePositions;

      // Check if we already have stored positions for this group
      const groupKey = selectedObjects
        .map((obj) => obj.id)
        .sort()
        .join(",");
      const existingPositions =
        persistentRelativePositionsRef.current.get(groupKey);

      if (existingPositions) {
        // Use existing positions to maintain consistency across moves
        elementRelativePositions = existingPositions;
      } else {
        // First time - calculate and store positions
        elementRelativePositions = new Map();
      }

      selectedObjects.forEach((obj) => {
        const element = getActualElementForDrag(obj);
        if (!element) return;

        // Only calculate positions if we don't have existing ones
        if (!existingPositions) {
          const bbox = getElementBoundingBox(element, obj.type);

          // CRITICAL FIX: Calculate element center consistently for all element types
          // This ensures group center calculation uses the same coordinate system for all elements
          let elementCenterX, elementCenterY;

          if (obj.type === "Text") {
            // For text elements, element.x/y are top-left, so calculate center from actual position
            elementCenterX = (element.x || 0) + bbox.width / 2;
            elementCenterY = (element.y || 0) + bbox.height / 2;
          } else if (obj.type === "Image" || obj.type === "Circle") {
            // For images and circles, element.x/y are already center coordinates
            elementCenterX = element.x || 0;
            elementCenterY = element.y || 0;
          } else if (
            obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"
          ) {
            // For lines/arrows, calculate center from points
            const points = element.points || [0, 0, 0, 0];
            if (points.length >= 4) {
              const xCoords = points.filter((_, i) => i % 2 === 0);
              const yCoords = points.filter((_, i) => i % 2 === 1);
              const minX = Math.min(...xCoords);
              const maxX = Math.max(...xCoords);
              const minY = Math.min(...yCoords);
              const maxY = Math.max(...yCoords);
              elementCenterX = (element.x || 0) + (minX + maxX) / 2;
              elementCenterY = (element.y || 0) + (minY + maxY) / 2;
            } else {
              elementCenterX = element.x || 0;
              elementCenterY = element.y || 0;
            }
          } else {
            // For rectangles and other top-left coordinate elements
            elementCenterX = (element.x || 0) + bbox.width / 2;
            elementCenterY = (element.y || 0) + bbox.height / 2;
          }

          // Store element's center position for relative offset calculation
          elementRelativePositions.set(obj.id, {
            centerX: elementCenterX,
            centerY: elementCenterY,
            x: element.x || 0,
            y: element.y || 0,
          });
        }

        // Use stored positions for group bounding box calculation
        const storedPos = elementRelativePositions.get(obj.id);
        if (storedPos) {
          const bbox = getElementBoundingBox(element, obj.type);
          sMinX = Math.min(sMinX, storedPos.centerX - bbox.width / 2);
          sMinY = Math.min(sMinY, storedPos.centerY - bbox.height / 2);
          sMaxX = Math.max(sMaxX, storedPos.centerX + bbox.width / 2);
          sMaxY = Math.max(sMaxY, storedPos.centerY + bbox.height / 2);
        }
      });

      // Store positions in persistent ref for future moves
      if (!existingPositions) {
        persistentRelativePositionsRef.current.set(
          groupKey,
          elementRelativePositions,
        );
      }

      const sourceGroupCenterX = (sMinX + sMaxX) / 2;
      const sourceGroupCenterY = (sMinY + sMaxY) / 2;

      // CRITICAL FIX: Update the group center variables used for relative positioning
      // This ensures we calculate offsets relative to the true Source Group Center
      groupCenterX = sourceGroupCenterX;
      groupCenterY = sourceGroupCenterY;

      if (draggedElementId) {
        const { x: targetElementX, y: targetElementY } =
          overridePositions[draggedElementId];

        // Find the anchor element in source
        let anchorElement = null;
        let anchorType = null;
        selectedObjects.forEach((obj) => {
          if (obj.id === draggedElementId) {
            anchorElement = getActualElementForDrag(obj);
            anchorType = obj.type;
          }
        });

        if (anchorElement) {
          // CRITICAL FIX: Use mouse position directly for target positioning
          // This avoids coordinate conversion issues that cause jumping

          if (
            mouseClientX !== null &&
            mouseClientY !== null &&
            sourceCanvas !== targetCanvas
          ) {
            // When moving between stages, use mouse position as the target
            // Convert mouse client position to target stage coordinates
            targetGroupCenterStageX = mouseClientX - targetBBox.left;
            targetGroupCenterStageY = mouseClientY - targetBBox.top;
          } else {
            // When staying on same stage or no mouse position, use override positions
            const anchorRelativePos =
              elementRelativePositions.get(draggedElementId);
            if (anchorRelativePos) {
              // Calculate offset from group center to anchor center (in source coordinates)
              const anchorOffsetX =
                anchorRelativePos.centerX - sourceGroupCenterX;
              const anchorOffsetY =
                anchorRelativePos.centerY - sourceGroupCenterY;

              // Place group center such that anchor ends up at target position
              // targetGroupCenter = targetAnchorPosition - anchorOffset
              targetGroupCenterStageX = targetElementX - anchorOffsetX;
              targetGroupCenterStageY = targetElementY - anchorOffsetY;
            } else {
              // Fallback: use simple delta
              const deltaX = targetElementX - anchorElement.x;
              const deltaY = targetElementY - anchorElement.y;
              targetGroupCenterStageX = sourceGroupCenterX + deltaX;
              targetGroupCenterStageY = sourceGroupCenterY + deltaY;
            }
          }
        }
      } else {
        // No anchor element found, use fallback positioning
        if (mouseClientX !== null && mouseClientY !== null) {
          // Use mouse position as the new group center
          targetGroupCenterStageX = mouseClientX - targetBBox.left;
          targetGroupCenterStageY = mouseClientY - targetBBox.top;
        } else if (wrapperFinalX !== null && wrapperFinalY !== null) {
          const wrapperWidth = maxX - minX;
          const wrapperHeight = maxY - minY;
          targetGroupCenterStageX = wrapperFinalX + wrapperWidth / 2;
          targetGroupCenterStageY = wrapperFinalY + wrapperHeight / 2;
        } else {
          targetGroupCenterStageX = targetBBox.width / 2;
          targetGroupCenterStageY = targetBBox.height / 2;
        }
      }

      // Move all elements
      // CRITICAL: Sort objects by render order (Z-index) before transferring
      // Render Order: Image -> Rect -> Circle -> Arrow -> DoubleSidedArrow -> Line -> Text
      // Within same type, preserve index from source array
      const typePriority = {
        Image: 0,
        Rect: 1,
        Circle: 2,
        Arrow: 3,
        DoubleSidedArrow: 4,
        Line: 5,
        Text: 6,
      };

      const getElementIndex = (id, type, targetSourceCanvas) => {
        let array = [];
        if (targetSourceCanvas === "main") {
          switch (type) {
            case "Image":
              array = images;
              break;
            case "Rect":
              array = rectangles;
              break;
            case "Circle":
              array = circles;
              break;
            case "Arrow":
              array = arrows;
              break;
            case "DoubleSidedArrow":
              array = doubleSidedArrows;
              break;
            case "Line":
              array = lines;
              break;
            case "Text":
              array = texts;
              break;
          }
        } else {
          switch (type) {
            case "Image":
              array = offCanvasImages;
              break;
            case "Rect":
              array = offCanvasRectangles;
              break;
            case "Circle":
              array = offCanvasCircles;
              break;
            case "Arrow":
              array = offCanvasArrows;
              break;
            case "DoubleSidedArrow":
              array = offCanvasDoubleSidedArrows;
              break;
            case "Line":
              array = offCanvasLines;
              break;
            case "Text":
              array = offCanvasTexts;
              break;
          }
        }
        return array.findIndex((item) => item.id === id);
      };

      // Create a sorted copy of objects to check
      const sortedObjects = [...selectedObjects].sort((a, b) => {
        const typeA = a.type || (a.isArrow ? "Arrow" : "Rect"); // Fallback
        const typeB = b.type || (b.isArrow ? "Arrow" : "Rect");

        const priorityA =
          typePriority[typeA] !== undefined ? typePriority[typeA] : 99;
        const priorityB =
          typePriority[typeB] !== undefined ? typePriority[typeB] : 99;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // If same type, sort by index in source array
        // We use sourceCanvas variable from closure which tells us where the group came from
        const indexA = getElementIndex(a.id, typeA, sourceCanvas);
        const indexB = getElementIndex(b.id, typeB, sourceCanvas);

        return indexA - indexB;
      });

      const movedElements = [];
      // Store full movedElement objects (with all properties) for text elements
      // This allows us to update setSelectedText with complete data
      const fullMovedElements = new Map(); // id -> full movedElement object
      sortedObjects.forEach((obj) => {
        if (!obj || !obj.id || !obj.type) return;

        let element = null;
        let setMainState = null;
        let setOffCanvasState = null;

        // UnifiedLineArrowElement instances (Line/Arrow/DoubleSidedArrow) are stored in `lines` / `offCanvasLines`.
        // When moving groups, determine the backing array by ID presence, not by arrow flags (which can be missing).
        const isUnifiedBackedByLines = (elementId, canvas) => {
          if (!elementId) return false;
          if (canvas === "main") {
            return (lines ?? []).some((l) => l?.id === elementId);
          }
          return (offCanvasLines ?? []).some((l) => l?.id === elementId);
        };

        // Try to find element in both canvases first (in case it's in transition)
        let foundInMain = false;
        let foundInOff = false;

        // Check main canvas
        switch (obj.type) {
          case "Rect":
            foundInMain = rectangles.some((r) => r.id === obj.id);
            foundInOff = offCanvasRectangles.some((r) => r.id === obj.id);
            break;
          case "Circle":
            foundInMain = circles.some((c) => c.id === obj.id);
            foundInOff = offCanvasCircles.some((c) => c.id === obj.id);
            break;
          case "Image":
            foundInMain = images.some((img) => img.id === obj.id);
            foundInOff = offCanvasImages.some((img) => img.id === obj.id);
            break;
          case "Text":
            foundInMain = texts.some((t) => t.id === obj.id);
            foundInOff = offCanvasTexts.some((t) => t.id === obj.id);
            break;
          case "Arrow":
            foundInMain =
              lines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow || hasEndArrow;
              }) || arrows.some((a) => a.id === obj.id);
            foundInOff =
              offCanvasLines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow || hasEndArrow;
              }) || offCanvasArrows.some((a) => a.id === obj.id);
            break;
          case "DoubleSidedArrow":
            foundInMain =
              doubleSidedArrows.some((a) => a.id === obj.id) ||
              lines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });
            foundInOff =
              offCanvasDoubleSidedArrows.some((a) => a.id === obj.id) ||
              offCanvasLines.some((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });
            break;
          case "Line":
            // CRITICAL FIX: UnifiedLineArrowElement instances (including Arrow/DoubleSidedArrow)
            // can appear in selectedObjects as type "Line". For group moves, treat ANY matching
            // entry in lines/offCanvasLines as present, regardless of arrow-head flags.
            foundInMain = lines.some((l) => l.id === obj.id);
            foundInOff = offCanvasLines.some((l) => l.id === obj.id);
            break;
        }

        // Determine actual source canvas based on where element actually is
        const actualSourceCanvas = foundInOff
          ? "offCanvas"
          : foundInMain
            ? "main"
            : sourceCanvas;

        if (sourceCanvas === "offCanvas") {
          switch (obj.type) {
            case "Rect":
              element =
                offCanvasRectangles.find((r) => r.id === obj.id) ||
                (actualSourceCanvas === "main"
                  ? rectangles.find((r) => r.id === obj.id)
                  : null);
              setMainState = setRectangles;
              setOffCanvasState = setOffCanvasRectangles;
              break;
            case "Circle":
              element =
                offCanvasCircles.find((c) => c.id === obj.id) ||
                (actualSourceCanvas === "main"
                  ? circles.find((c) => c.id === obj.id)
                  : null);
              setMainState = setCircles;
              setOffCanvasState = setOffCanvasCircles;
              break;
            case "Image":
              element =
                offCanvasImages.find((img) => img.id === obj.id) ||
                (actualSourceCanvas === "main"
                  ? images.find((img) => img.id === obj.id)
                  : null);
              setMainState = setImages;
              setOffCanvasState = setOffCanvasImages;
              break;
            case "Text":
              element =
                offCanvasTexts.find((t) => t.id === obj.id) ||
                (actualSourceCanvas === "main"
                  ? texts.find((t) => t.id === obj.id)
                  : null);
              setMainState = setTexts;
              setOffCanvasState = setOffCanvasTexts;
              break;
            case "Arrow":
              // CRITICAL FIX: Check offCanvasLines array FIRST for unified arrow elements (with arrow heads)
              element = offCanvasLines.find((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                const typeSaysArrow =
                  l.type === "Arrow" ||
                  l.type === "arrow" ||
                  l.type === "DoubleSidedArrow" ||
                  l.type === "double_sided_arrow";
                return hasStartArrow || hasEndArrow || typeSaysArrow;
              });
              // Fallback to legacy offCanvasArrows array
              if (!element) {
                element = offCanvasArrows.find((a) => a.id === obj.id);
              }
              // If still not found and element is actually in main canvas, check main canvas
              if (!element && actualSourceCanvas === "main") {
                element = lines.find((l) => {
                  if (l.id !== obj.id) return false;
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  const typeSaysArrow =
                    l.type === "Arrow" ||
                    l.type === "arrow" ||
                    l.type === "DoubleSidedArrow" ||
                    l.type === "double_sided_arrow";
                  return hasStartArrow || hasEndArrow || typeSaysArrow;
                });
                if (!element) {
                  element = arrows.find((a) => a.id === obj.id);
                }
              }
              // Unified arrow elements use setLines, legacy arrows use setArrows
              if (isUnifiedBackedByLines(obj.id, actualSourceCanvas)) {
                setMainState = setLines;
                setOffCanvasState = setOffCanvasLines;
              } else {
                setMainState = setArrows;
                setOffCanvasState = setOffCanvasArrows;
              }
              break;
            case "DoubleSidedArrow":
              // Check offCanvasDoubleSidedArrows array first
              element = offCanvasDoubleSidedArrows.find((a) => a.id === obj.id);
              // Also check offCanvasLines array for unified double-sided arrow elements
              if (!element) {
                element = offCanvasLines.find((l) => {
                  if (l.id !== obj.id) return false;
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  return hasStartArrow && hasEndArrow;
                });
              }
              // Unified double-sided arrow elements use setLines, legacy arrows use setDoubleSidedArrows
              // CRITICAL FIX: Check all possible property names for arrow heads
              if (isUnifiedBackedByLines(obj.id, actualSourceCanvas)) {
                setMainState = setLines;
                setOffCanvasState = setOffCanvasLines;
              } else {
                setMainState = setDoubleSidedArrows;
                setOffCanvasState = setOffCanvasDoubleSidedArrows;
              }
              break;
            case "Line":
              // CRITICAL FIX: For group moves, "Line" selection may represent a unified arrow.
              // Pull by ID from lines/offCanvasLines regardless of arrow flags.
              element = offCanvasLines.find((l) => l.id === obj.id);
              // If still not found and element is actually in main canvas, check main canvas
              if (!element && actualSourceCanvas === "main") {
                element = lines.find((l) => l.id === obj.id);
              }
              setMainState = setLines;
              setOffCanvasState = setOffCanvasLines;
              break;
          }
        } else {
          switch (obj.type) {
            case "Rect":
              element =
                rectangles.find((r) => r.id === obj.id) ||
                (actualSourceCanvas === "offCanvas"
                  ? offCanvasRectangles.find((r) => r.id === obj.id)
                  : null);
              setMainState = setRectangles;
              setOffCanvasState = setOffCanvasRectangles;
              break;
            case "Circle":
              element =
                circles.find((c) => c.id === obj.id) ||
                (actualSourceCanvas === "offCanvas"
                  ? offCanvasCircles.find((c) => c.id === obj.id)
                  : null);
              setMainState = setCircles;
              setOffCanvasState = setOffCanvasCircles;
              break;
            case "Image":
              element =
                images.find((img) => img.id === obj.id) ||
                (actualSourceCanvas === "offCanvas"
                  ? offCanvasImages.find((img) => img.id === obj.id)
                  : null);
              setMainState = setImages;
              setOffCanvasState = setOffCanvasImages;
              break;
            case "Text":
              element =
                texts.find((t) => t.id === obj.id) ||
                (actualSourceCanvas === "offCanvas"
                  ? offCanvasTexts.find((t) => t.id === obj.id)
                  : null);
              setMainState = setTexts;
              setOffCanvasState = setOffCanvasTexts;
              break;
            case "Arrow":
              // CRITICAL FIX: Check lines array FIRST for unified arrow elements (with arrow heads)
              element = lines.find((l) => {
                if (l.id !== obj.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                const typeSaysArrow =
                  l.type === "Arrow" ||
                  l.type === "arrow" ||
                  l.type === "DoubleSidedArrow" ||
                  l.type === "double_sided_arrow";
                return hasStartArrow || hasEndArrow || typeSaysArrow;
              });
              // Fallback to legacy arrows array
              if (!element) {
                element = arrows.find((a) => a.id === obj.id);
              }
              // If still not found and element is actually in off canvas, check off canvas
              if (!element && actualSourceCanvas === "offCanvas") {
                element = offCanvasLines.find((l) => {
                  if (l.id !== obj.id) return false;
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  const typeSaysArrow =
                    l.type === "Arrow" ||
                    l.type === "arrow" ||
                    l.type === "DoubleSidedArrow" ||
                    l.type === "double_sided_arrow";
                  return hasStartArrow || hasEndArrow || typeSaysArrow;
                });
                if (!element) {
                  element = offCanvasArrows.find((a) => a.id === obj.id);
                }
              }
              // CRITICAL FIX: Always use setLines/setOffCanvasLines for unified arrows
              // Check if element has arrow properties OR if obj.type is "Arrow"
              // This ensures arrows are added to the correct array even if element properties are missing
              if (isUnifiedBackedByLines(obj.id, actualSourceCanvas)) {
                setMainState = setLines;
                setOffCanvasState = setOffCanvasLines;
              } else {
                setMainState = setArrows;
                setOffCanvasState = setOffCanvasArrows;
              }
              break;
            case "DoubleSidedArrow":
              // Check doubleSidedArrows array first
              element = doubleSidedArrows.find((a) => a.id === obj.id);
              // Also check lines array for unified double-sided arrow elements
              if (!element) {
                element = lines.find((l) => {
                  if (l.id !== obj.id) return false;
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  const typeSaysDouble =
                    l.type === "DoubleSidedArrow" ||
                    l.type === "double_sided_arrow";
                  return (hasStartArrow && hasEndArrow) || typeSaysDouble;
                });
              }
              // CRITICAL FIX: Always use setLines/setOffCanvasLines for unified double-sided arrows
              // Check if element has both arrow properties OR if obj.type is "DoubleSidedArrow"
              // This ensures double-sided arrows are added to the correct array even if element properties are missing
              if (isUnifiedBackedByLines(obj.id, actualSourceCanvas)) {
                setMainState = setLines;
                setOffCanvasState = setOffCanvasLines;
              } else {
                setMainState = setDoubleSidedArrows;
                setOffCanvasState = setOffCanvasDoubleSidedArrows;
              }
              break;
            case "Line":
              // CRITICAL FIX: For group moves, "Line" selection may represent a unified arrow.
              // Pull by ID from lines/offCanvasLines regardless of arrow flags.
              element = lines.find((l) => l.id === obj.id);
              // If still not found and element is actually in off canvas, check off canvas
              if (!element && actualSourceCanvas === "offCanvas") {
                element = offCanvasLines.find((l) => l.id === obj.id);
              }
              setMainState = setLines;
              setOffCanvasState = setOffCanvasLines;
              break;
          }
        }

        if (!element) {
          return;
        }
        if (!setMainState || !setOffCanvasState) {
          return;
        }

        // Calculate new position
        // CRITICAL FIX: Use initial positions if available to guarantee rigid formation
        // This avoids issues with asynchronous state updates (double movement or lag)
        let newX, newY;

        // CRITICAL: Check if element position was updated by handleGroupDragMove
        // If so, we need to use the Konva node position, not the state position
        const mainStage = stageRef?.current;
        const offStage = offCanvasStageRef?.current;
        let konvaNodeX = element.x;
        let konvaNodeY = element.y;

        if (mainStage || offStage) {
          const node =
            mainStage?.findOne(`#${obj.id}`) || offStage?.findOne(`#${obj.id}`);
          if (node) {
            const nodeX = node.x();
            const nodeY = node.y();
            if (nodeX !== undefined && nodeY !== undefined) {
              konvaNodeX = nodeX;
              konvaNodeY = nodeY;
            }
          }
        }

        // CRITICAL FIX: For text elements, if state position is negative, use initial position instead
        // Negative positions indicate the element was dragged left and crossed the boundary
        // The state position might be stale/negative, so we should use the initial position for calculation
        let elementToUse = element;

        if (obj.type === "Text" && (element.x < 0 || element.y < 0)) {
          // State has negative position (from drag), use initial position instead
          const initialPositions =
            individualElementDragStartRef.current?.elementPositions;
          const initialElementPos = initialPositions?.find(
            (p) => p.id === obj.id,
          );

          if (initialElementPos) {
            elementToUse = {
              ...element,
              x: initialElementPos.x,
              y: initialElementPos.y,
            };
          }
        }

        const initialPositions =
          individualElementDragStartRef.current?.elementPositions;
        const initialAnchorPos = initialPositions?.find(
          (p) => p.id === draggedElementId,
        );
        const initialElementPos = initialPositions?.find(
          (p) => p.id === obj.id,
        );

        if (
          draggedElementId &&
          initialAnchorPos &&
          initialElementPos &&
          overridePositions &&
          overridePositions[draggedElementId]
        ) {
          // CRITICAL FIX: Calculate relative offset from anchor element
          // This ensures perfect spacing preservation during canvas transfer

          // Get the anchor element's current position in SOURCE canvas coordinates
          const anchorCurrentX = overridePositions[draggedElementId].x;
          const anchorCurrentY = overridePositions[draggedElementId].y;

          // Calculate the element's relative offset from anchor in SOURCE coordinates
          // This offset is INVARIANT - it doesn't change regardless of canvas
          const relativeOffsetX = initialElementPos.x - initialAnchorPos.x;
          const relativeOffsetY = initialElementPos.y - initialAnchorPos.y;

          // Apply the relative offset to the anchor's new position
          // This gives us the element's position in SOURCE canvas coordinates
          let targetXInSourceCoords = anchorCurrentX + relativeOffsetX;
          let targetYInSourceCoords = anchorCurrentY + relativeOffsetY;

          // Get stage scale for coordinate transformation
          const stageScaleX = mainStage.scaleX() || 1;
          const stageScaleY = mainStage.scaleY() || 1;

          // CRITICAL: Apply canvas shift ONLY if transferring between canvases
          // This converts from source canvas coordinates to target canvas coordinates
          let targetXInTargetCoords = targetXInSourceCoords;
          let targetYInTargetCoords = targetYInSourceCoords;

          if (sourceCanvas !== targetCanvas) {
            // Calculate pixel offset between canvas containers on screen
            const pixelOffsetX = sourceBBox.left - targetBBox.left;
            const pixelOffsetY = sourceBBox.top - targetBBox.top;

            // Normalize by stage scale to convert pixel offset to stage coordinates
            targetXInTargetCoords += pixelOffsetX / stageScaleX;
            targetYInTargetCoords += pixelOffsetY / stageScaleY;
          }

          newX = targetXInTargetCoords;
          newY = targetYInTargetCoords;
        } else {
          // Fallback to relative calculation (for paste/duplicate/non-drag moves)
          // CRITICAL FIX: Use stored relative positions if available
          const relativePos = elementRelativePositions.get(obj.id);

          if (relativePos) {
            // CRITICAL FIX: Use the stored relative position directly without recalculating group center
            // The stored centerX/centerY are the original center coordinates from when the group was first created
            // We should use these directly with the target group center, not calculate relative to a changing source group center

            // Calculate the offset from the original group center to this element's center
            // Use the first element's stored center as the reference group center
            const firstElementId = selectedObjects[0]?.id;
            const firstElementRelativePos =
              elementRelativePositions.get(firstElementId);

            let newCenterX, newCenterY;

            if (firstElementRelativePos) {
              // Use the first element as the anchor point
              const anchorCenterX = firstElementRelativePos.centerX;
              const anchorCenterY = firstElementRelativePos.centerY;

              // Calculate this element's offset from the anchor
              const offsetFromAnchorX = relativePos.centerX - anchorCenterX;
              const offsetFromAnchorY = relativePos.centerY - anchorCenterY;

              // Position the anchor at the target group center, then apply the offset
              newCenterX = targetGroupCenterStageX + offsetFromAnchorX;
              newCenterY = targetGroupCenterStageY + offsetFromAnchorY;
            } else {
              // Fallback: use stored position directly
              newCenterX = targetGroupCenterStageX;
              newCenterY = targetGroupCenterStageY;
            }

            // Convert center to element position based on type
            if (obj.type === "Image" || obj.type === "Circle") {
              newX = newCenterX;
              newY = newCenterY;
            } else if (
              obj.type === "Line" ||
              obj.type === "Arrow" ||
              obj.type === "DoubleSidedArrow"
            ) {
              const points = elementToUse.points || [0, 0, 0, 0];
              if (points.length >= 4) {
                const xCoords = points.filter((_, i) => i % 2 === 0);
                const yCoords = points.filter((_, i) => i % 2 === 1);
                const minX = Math.min(...xCoords);
                const minY = Math.min(...yCoords);
                const maxX = Math.max(...xCoords);
                const maxY = Math.max(...yCoords);
                const centerOffsetX = (minX + maxX) / 2;
                const centerOffsetY = (minY + maxY) / 2;
                newX = newCenterX - centerOffsetX;
                newY = newCenterY - centerOffsetY;
              } else {
                newX = newCenterX;
                newY = newCenterY;
              }
            } else if (obj.type === "Text") {
              // CRITICAL FIX: For text elements, treat them the same as rectangles
              // Both use top-left coordinates, so the positioning logic should be identical
              const bbox = getElementBoundingBox(elementToUse, obj.type);
              newX = newCenterX - bbox.width / 2;
              newY = newCenterY - bbox.height / 2;
            } else {
              // For rectangles and other top-left coordinate elements
              const bbox = getElementBoundingBox(elementToUse, obj.type);
              newX = newCenterX - bbox.width / 2;
              newY = newCenterY - bbox.height / 2;
            }
          } else {
            // Fallback if relative position not stored
            // CRITICAL FIX: Calculate relative position based on element's actual coordinates
            // For text elements, we need to be careful about coordinate systems
            let elementCenterX, elementCenterY;

            if (obj.type === "Text") {
              // For text elements, use the actual x,y coordinates (top-left) and calculate center
              const textX = elementToUse.x || 0;
              const textY = elementToUse.y || 0;
              const bbox = getElementBoundingBox(elementToUse, obj.type);
              elementCenterX = textX + bbox.width / 2;
              elementCenterY = textY + bbox.height / 2;
            } else if (obj.type === "Image" || obj.type === "Circle") {
              // For images and circles, x,y are already center coordinates
              elementCenterX = elementToUse.x || 0;
              elementCenterY = elementToUse.y || 0;
            } else if (
              obj.type === "Line" ||
              obj.type === "Arrow" ||
              obj.type === "DoubleSidedArrow"
            ) {
              // For lines/arrows, calculate center from points
              const points = elementToUse.points || [0, 0, 0, 0];
              if (points.length >= 4) {
                const xCoords = points.filter((_, i) => i % 2 === 0);
                const yCoords = points.filter((_, i) => i % 2 === 1);
                const minX = Math.min(...xCoords);
                const maxX = Math.max(...xCoords);
                const minY = Math.min(...yCoords);
                const maxY = Math.max(...yCoords);
                elementCenterX = (elementToUse.x || 0) + (minX + maxX) / 2;
                elementCenterY = (elementToUse.y || 0) + (minY + maxY) / 2;
              } else {
                elementCenterX = elementToUse.x || 0;
                elementCenterY = elementToUse.y || 0;
              }
            } else {
              // For rectangles and other top-left coordinate elements
              const rectX = elementToUse.x || 0;
              const rectY = elementToUse.y || 0;
              const bbox = getElementBoundingBox(elementToUse, obj.type);
              elementCenterX = rectX + bbox.width / 2;
              elementCenterY = rectY + bbox.height / 2;
            }

            const relativeX = elementCenterX - groupCenterX;
            const relativeY = elementCenterY - groupCenterY;

            const newCenterX = targetGroupCenterStageX + relativeX;
            const newCenterY = targetGroupCenterStageY + relativeY;

            // Convert center to element position based on type
            if (obj.type === "Image" || obj.type === "Circle") {
              newX = newCenterX;
              newY = newCenterY;
            } else if (
              obj.type === "Line" ||
              obj.type === "Arrow" ||
              obj.type === "DoubleSidedArrow"
            ) {
              const points = elementToUse.points || [0, 0, 0, 0];
              if (points.length >= 4) {
                const xCoords = points.filter((_, i) => i % 2 === 0);
                const yCoords = points.filter((_, i) => i % 2 === 1);
                const minX = Math.min(...xCoords);
                const minY = Math.min(...yCoords);
                const maxX = Math.max(...xCoords);
                const maxY = Math.max(...yCoords);
                const centerOffsetX = (minX + maxX) / 2;
                const centerOffsetY = (minY + maxY) / 2;
                newX = newCenterX - centerOffsetX;
                newY = newCenterY - centerOffsetY;
              } else {
                newX = newCenterX;
                newY = newCenterY;
              }
            } else if (obj.type === "Text") {
              // CRITICAL FIX: For text elements, treat them the same as rectangles
              // Both use top-left coordinates, so the positioning logic should be identical
              const bbox = getElementBoundingBox(elementToUse, obj.type);
              newX = newCenterX - bbox.width / 2;
              newY = newCenterY - bbox.height / 2;
            } else {
              // For rectangles and other top-left coordinate elements
              const bbox = getElementBoundingBox(elementToUse, obj.type);
              newX = newCenterX - bbox.width / 2;
              newY = newCenterY - bbox.height / 2;
            }
          }
        }

        // CRITICAL FIX: Constrain positions to be within off-canvas stage bounds when moving to off-canvas
        // Minimum x and y positions should be 0, maximum should respect stage width/height
        let constrainedX = newX;
        let constrainedY = newY;

        if (targetCanvas === "offCanvas" && offStage) {
          // Get stage dimensions
          const stageWidth = offStage.width() || window.innerWidth;
          const stageHeight = offStage.height() || fixedStageHeightPx;

          // Calculate bounding box to know element dimensions
          const bbox = getElementBoundingBox(
            { ...elementToUse, x: newX, y: newY },
            obj.type,
          );

          // Determine coordinate system based on element type
          const isCenterCoordinate =
            obj.type === "Image" || obj.type === "Circle";

          if (isCenterCoordinate) {
            // For center coordinate elements (Image, Circle)
            // Constrain center position so element stays within bounds
            const halfWidth = bbox.width / 2;
            const halfHeight = bbox.height / 2;

            // For circles, account for stroke width and toolbar
            let minX = halfWidth;
            if (obj.type === "Circle") {
              const strokeWidth = elementToUse.strokeWidth || 2;
              // Leftmost point of stroke = x - radius - strokeWidth/2
              // Constraint: x - radius - strokeWidth/2 >= TOOLBAR_WIDTH
              // Therefore: x >= TOOLBAR_WIDTH + radius + strokeWidth/2
              const radius =
                elementToUse.radius ||
                Math.max(elementToUse.radiusX || 0, elementToUse.radiusY || 0);
              minX = TOOLBAR_WIDTH + radius + strokeWidth / 2;
            }

            constrainedX = Math.max(
              minX,
              Math.min(stageWidth - halfWidth, newX),
            );
            constrainedY = Math.max(
              halfHeight,
              Math.min(stageHeight - halfHeight, newY),
            );
          } else {
            // For top-left coordinate elements (Rect, Text, Line, Arrow)
            // Constrain top-left position so element stays within bounds
            let minX = 0;
            if (obj.type === "Rect") {
              const strokeWidth = elementToUse.strokeWidth || 2;
              // Leftmost point of stroke = x - strokeWidth/2
              // Constraint: x - strokeWidth/2 >= TOOLBAR_WIDTH
              // Therefore: x >= TOOLBAR_WIDTH + strokeWidth/2
              minX = TOOLBAR_WIDTH + strokeWidth / 2;
            }

            constrainedX = Math.max(
              minX,
              Math.min(stageWidth - bbox.width, newX),
            );
            constrainedY = Math.max(
              0,
              Math.min(stageHeight - bbox.height, newY),
            );
          }

          if (newX !== constrainedX || newY !== constrainedY) {
          }
        }

        // Create moved element
        // CRITICAL FIX: Ensure we use the calculated positions (newX, newY) which are correct
        // Don't use elementToUse.x/y as they might be stale (especially for text elements with negative positions)
        const movedElement = {
          ...elementToUse,
          x: constrainedX, // Use constrained position to ensure >= 0 for off-canvas
          y: constrainedY, // Use constrained position to ensure >= 0 for off-canvas
        };

        if (obj.type === "Arrow" || obj.type === "DoubleSidedArrow") {
        }

        // Remove from source canvas

        if (sourceCanvas === "offCanvas") {
          setOffCanvasState((prev) => {
            const filtered = prev.filter((el) => el.id !== obj.id);
            return filtered;
          });
        } else {
          setMainState((prev) => {
            const filtered = prev.filter((el) => el.id !== obj.id);
            return filtered;
          });
        }

        // Add to target canvas
        if (targetCanvas === "offCanvas") {
          setOffCanvasState((prev) => {
            // CRITICAL FIX: Remove any existing element with the same ID first
            // This prevents duplicates and ensures we use the correct position
            const filtered = prev.filter((el) => el.id !== obj.id);
            const updated = [...filtered, movedElement];
            return updated;
          });
        } else {
          setMainState((prev) => {
            // CRITICAL FIX: Remove any existing element with the same ID first
            // This prevents duplicates and ensures we use the correct position
            const filtered = prev.filter((el) => el.id !== obj.id);
            const updated = [...filtered, movedElement];
            return updated;
          });
        }

        // Create selection payload with all necessary properties
        const selectionPayload = {
          id: movedElement.id,
          type: obj.type,
          x: movedElement.x,
          y: movedElement.y,
          // Include other properties that might be needed for selection
          ...(movedElement.width !== undefined && {
            width: movedElement.width,
          }),
          ...(movedElement.height !== undefined && {
            height: movedElement.height,
          }),
          ...(movedElement.radius !== undefined && {
            radius: movedElement.radius,
          }),
          ...(movedElement.points !== undefined && {
            points: movedElement.points,
          }),
          ...(movedElement.text !== undefined && { text: movedElement.text }),
          ...(movedElement.rotation !== undefined && {
            rotation: movedElement.rotation,
          }),
          ...(movedElement.scaleX !== undefined && {
            scaleX: movedElement.scaleX,
          }),
          ...(movedElement.scaleY !== undefined && {
            scaleY: movedElement.scaleY,
          }),
          // _canvas lets useTransformerManager detect the canvas change and re-attach the transformer
          _canvas: targetCanvas,
        };

        movedElements.push(selectionPayload);

        // Store full movedElement for text elements so we can update setSelectedText with all properties
        if (obj.type === "Text") {
          fullMovedElements.set(obj.id, movedElement);
        }

        // CRITICAL FIX: Update Konva node position after moving element
        // This ensures the node position matches the new state position
        // This is especially important for text elements that might have negative positions
        // Try to find the node on the target stage - it might not be rendered yet, so we'll try both stages
        const targetStage = targetCanvas === "offCanvas" ? offStage : mainStage;
        const sourceStage = targetCanvas === "offCanvas" ? mainStage : offStage;

        // Try target stage first (where it should be after the move)
        let elementNode = targetStage?.findOne(`#${obj.id}`);

        // If not found on target stage, try source stage (it might still be there during transition)
        if (!elementNode && sourceStage) {
          elementNode = sourceStage.findOne(`#${obj.id}`);
        }

        // Also try top layer stage
        if (!elementNode && topLayerStageRef?.current) {
          elementNode = topLayerStageRef.current.findOne(`#${obj.id}`);
        }

        if (elementNode) {
          // Update node position to match the moved element's position
          elementNode.x(newX);
          elementNode.y(newY);
          const stageToDraw = elementNode.getStage();
          stageToDraw?.getLayer()?.batchDraw();
        } else {
        }
      });

      // Update selection to moved elements

      if (movedElements.length > 0) {
        setSelectedObjects(movedElements);
        setSelectedObject(movedElements[0]);

        // DEBUG: Log multi-selection change
        // CRITICAL FIX: For text elements, also update setSelectedText so properties panel shows correct position
        // This ensures TextBoxWithButtons displays the correct X/Y values after moving to off-canvas
        const movedTextElement = movedElements.find((el) => el.type === "Text");
        if (movedTextElement && setSelectedText) {
          // Get the full movedElement from the Map (has all properties with correct position)
          const fullMovedTextElement = fullMovedElements.get(
            movedTextElement.id,
          );

          if (fullMovedTextElement) {
            // Use the full movedElement which has all properties and correct position
            setSelectedText({
              ...fullMovedTextElement,
              type: "Text",
            });
          } else {
            // Fallback: use the moved element from selection (has correct position)
            setSelectedText({
              ...movedTextElement,
              type: "Text",
            });
          }
        }
      } else {
        console.error("🚀 [GROUP-MOVE] ERROR: No elements were moved!", {
          selectedObjectsCount: selectedObjects.length,
          selectedObjectsInfo: selectedObjects.map((obj) => ({
            id: obj.id,
            type: obj.type,
          })),
        });
      }

      // Save to history
      if (saveToHistory) {
        saveToHistory();
      }

      // Reset group moving flag after a short delay to allow state updates
      setTimeout(() => {
        isGroupMovingRef.current = false;
      }, 100);
    },
    [
      selectedObjects,
      stageRef,
      offCanvasStageRef,
      rectangles,
      offCanvasRectangles,
      circles,
      offCanvasCircles,
      images,
      offCanvasImages,
      texts,
      offCanvasTexts,
      arrows,
      offCanvasArrows,
      doubleSidedArrows,
      offCanvasDoubleSidedArrows,
      lines,
      offCanvasLines,
      setRectangles,
      setOffCanvasRectangles,
      setCircles,
      setOffCanvasCircles,
      setImages,
      setOffCanvasImages,
      setTexts,
      setOffCanvasTexts,
      setArrows,
      setOffCanvasArrows,
      setDoubleSidedArrows,
      setOffCanvasDoubleSidedArrows,
      setLines,
      setOffCanvasLines,
      setSelectedObjects,
      setSelectedObject,
      saveToHistory,
    ],
  );

  // Expose handleCopyGroupBetweenCanvases via callback
  useEffect(() => {
    if (onCopyGroupBetweenCanvasesReady) {
      onCopyGroupBetweenCanvasesReady(handleCopyGroupBetweenCanvases);
    }
  }, [handleCopyGroupBetweenCanvases, onCopyGroupBetweenCanvasesReady]);

  // Expose offCanvasStageRef to parent component
  useEffect(() => {
    if (onOffCanvasStageRefReady) {
      onOffCanvasStageRefReady(offCanvasStageRef);
    }
  }, [onOffCanvasStageRefReady]);

  // Helper function to calculate opacity based on selection state
  // When an element is selected, all other elements should have reduced opacity
  // Uses the SAME logic as transformer nodes - if element is in selectedObjects or matches selectedObject, full opacity
  const getElementOpacity = (elementId, elementType) => {
    // CRITICAL: Ensure elementId is valid
    if (!elementId || typeof elementId !== "string") {
      return 1; // Default to full opacity if ID is invalid (better UX)
    }

    // CRITICAL: If nothing is selected, all elements should have full opacity
    const hasSelection =
      (selectedObjects && selectedObjects.length > 0) ||
      (selectedObject && selectedObject.id);

    if (!hasSelection) {
      return 1; // Full opacity when nothing is selected
    }

    // SAME LOGIC AS TRANSFORMER: Check if element is in selectedObjects array
    // This is the exact same check used to determine if an element gets a transformer
    if (selectedObjects && selectedObjects.length > 0) {
      const isInSelectedObjects = selectedObjects.some((obj) => {
        // Direct ID comparison - same as transformer logic
        return obj && obj.id === elementId;
      });

      if (isInSelectedObjects) {
        return 1; // Full opacity for elements in selectedObjects (same as transformer logic)
      }
    }

    // SAME LOGIC AS TRANSFORMER: Check if element matches selectedObject
    // This is the exact same check used for single selection transformers
    if (selectedObject && selectedObject.id === elementId) {
      return 1; // Full opacity for selected object (same as transformer logic)
    }

    // If element is not in selectedObjects and doesn't match selectedObject, reduce opacity
    // This matches the transformer logic - no transformer = reduced opacity
    // BUT: Only reduce opacity if something is actually selected (checked above)
    return 0.3;
  };

  // Helper function to get actual element from state arrays
  const getActualElementForDrag = useCallback(
    (obj) => {
      if (!obj || !obj.id || !obj.type) return obj;

      // Always get from state arrays to ensure we have the latest position
      // Check both main and off-canvas arrays to find where element actually is
      switch (obj.type) {
        case "Rect": {
          const rect =
            rectangles.find((r) => r.id === obj.id) ||
            offCanvasRectangles.find((r) => r.id === obj.id);
          return rect || obj;
        }
        case "Circle": {
          const circle =
            circles.find((c) => c.id === obj.id) ||
            offCanvasCircles.find((c) => c.id === obj.id);
          return circle || obj;
        }
        case "Image": {
          const img =
            images.find((img) => img.id === obj.id) ||
            offCanvasImages.find((img) => img.id === obj.id);
          return img || obj;
        }
        case "Text": {
          const text =
            texts.find((t) => t.id === obj.id) ||
            offCanvasTexts.find((t) => t.id === obj.id);
          return text || obj;
        }
        case "Arrow": {
          // CRITICAL FIX: Check lines array FIRST for unified arrow elements
          const unifiedArrow = lines.find((l) => {
            if (l.id !== obj.id) return false;
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            // Also treat explicit type as arrow for backward compatibility
            // (some unified elements rely on type without persisting flags).
            const typeSaysArrow =
              l.type === "Arrow" ||
              l.type === "arrow" ||
              l.type === "DoubleSidedArrow" ||
              l.type === "double_sided_arrow";
            return hasStartArrow || hasEndArrow || typeSaysArrow;
          });
          if (unifiedArrow) return unifiedArrow;
          // Fallback to legacy arrows array
          const arrow = arrows.find((a) => a.id === obj.id);
          if (arrow) return arrow;
          // Check off-canvas arrays - prioritize lines first
          const offCanvasUnifiedArrow = offCanvasLines.find((l) => {
            if (l.id !== obj.id) return false;
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            const typeSaysArrow =
              l.type === "Arrow" ||
              l.type === "arrow" ||
              l.type === "DoubleSidedArrow" ||
              l.type === "double_sided_arrow";
            return hasStartArrow || hasEndArrow || typeSaysArrow;
          });
          if (offCanvasUnifiedArrow) return offCanvasUnifiedArrow;
          const offCanvasArrow = offCanvasArrows.find((a) => a.id === obj.id);
          return offCanvasArrow || obj;
        }
        case "DoubleSidedArrow": {
          // Check main canvas arrays first
          const arrow = doubleSidedArrows.find((a) => a.id === obj.id);
          if (arrow) return arrow;
          const unifiedArrow = lines.find((l) => {
            if (l.id !== obj.id) return false;
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            const typeSaysDouble =
              l.type === "DoubleSidedArrow" || l.type === "double_sided_arrow";
            return (hasStartArrow && hasEndArrow) || typeSaysDouble;
          });
          if (unifiedArrow) return unifiedArrow;
          // Check off-canvas arrays
          const offCanvasArrow = offCanvasDoubleSidedArrows.find(
            (a) => a.id === obj.id,
          );
          if (offCanvasArrow) return offCanvasArrow;
          const offCanvasUnifiedArrow = offCanvasLines.find((l) => {
            if (l.id !== obj.id) return false;
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            const typeSaysDouble =
              l.type === "DoubleSidedArrow" || l.type === "double_sided_arrow";
            return (hasStartArrow && hasEndArrow) || typeSaysDouble;
          });
          return offCanvasUnifiedArrow || obj;
        }
        case "Line": {
          // CRITICAL FIX: UnifiedLineArrowElement instances (including Arrow/DoubleSidedArrow)
          // can appear in selection/drag contexts as type "Line". For drag/group-drag preview,
          // always resolve by ID from lines/offCanvasLines regardless of arrow-head flags.
          const line =
            lines.find((l) => l.id === obj.id) ||
            offCanvasLines.find((l) => l.id === obj.id);
          return line || obj;
        }
        default:
          return obj;
      }
    },
    [
      rectangles,
      offCanvasRectangles,
      circles,
      offCanvasCircles,
      images,
      offCanvasImages,
      texts,
      offCanvasTexts,
      arrows,
      offCanvasArrows,
      doubleSidedArrows,
      offCanvasDoubleSidedArrows,
      lines,
      offCanvasLines,
    ],
  );

  // Helper function to handle group drag when individual element is dragged in multi-select mode
  // This stores initial positions and applies delta to all selected elements
  const individualElementDragStartRef = useRef({ elementPositions: [] });

  // CRITICAL FIX: Persistent storage for original relative positions to avoid recalculation
  const persistentRelativePositionsRef = useRef(new Map());

  // Clean up persistent positions when selection changes or is cleared
  useEffect(() => {
    // If no objects are selected, clear all persistent positions
    if (!selectedObjects || selectedObjects.length === 0) {
      if (persistentRelativePositionsRef.current.size > 0) {
        persistentRelativePositionsRef.current.clear();
      }
    }
  }, [selectedObjects]);

  const handleGroupDragStart = useCallback(
    (draggedElementId, draggedElementType) => {
      // Duplicate-to-selected-location (clone mode): don't start group drag – only click-to-place duplicates
      if (isCloneMode || lockedAction) return;
      // If multiple elements are selected, enable group drag regardless of isMultiSelectMode
      if (!selectedObjects || selectedObjects.length <= 1) {
        return;
      }

      // CRITICAL FIX: Prevent re-capturing initial positions if drag is already active
      // This prevents the "exponential divergence" bug where initialPos resets while delta accumulates
      if (isGroupDraggingRef.current) {
        return;
      }
      groupDragPointerStartRef.current = null;
      groupDragLastDeltaRef.current = { x: 0, y: 0 };

      // CRITICAL FIX: Clear any stale lastDraggedPositionRef for all elements in the group
      // This ensures elements don't use old individual drag positions during group drag
      // We need to clear these refs in the UnifiedLineArrowElement components
      // Signal this by setting a flag that the components can check
      window.clearLastDraggedPositions = true;
      setTimeout(() => {
        delete window.clearLastDraggedPositions;
      }, 100);

      // CRITICAL FIX: Always get fresh positions from Konva nodes to ensure accuracy
      // This prevents stale state from affecting subsequent drags
      const elementPositions = selectedObjects.map((obj) => {
        const actualElement = getActualElementForDrag(obj);

        // CRITICAL: Get current position from Konva node (most accurate)
        let currentX = actualElement.x || 0;
        let currentY = actualElement.y || 0;

        const mainStage = stageRef?.current;
        const offStage = offCanvasStageRef?.current;
        const topLayerStage = topLayerStageRef?.current;
        const offCanvasTopLayerStage = offCanvasTopLayerStageRef?.current;

        // CRITICAL FIX: Check if this is a top-layer image
        // Top-layer images are on topLayerStage, not mainStage or offStage
        const isTopLayerImage =
          obj.type === "Image" && actualElement?.isOnTopLayer === true;

        if (isTopLayerImage && (topLayerStage || offCanvasTopLayerStage)) {
          // For top-layer images, check both top-layer stages first
          const node =
            topLayerStage?.findOne(`#${obj.id}`) ||
            offCanvasTopLayerStage?.findOne(`#${obj.id}`);
          if (node) {
            const nodeX = node.x();
            const nodeY = node.y();
            if (nodeX !== undefined && nodeY !== undefined) {
              currentX = nodeX;
              currentY = nodeY;
            }
          }
        } else if (mainStage || offStage) {
          // For other elements, check mainStage or offStage
          const node =
            mainStage?.findOne(`#${obj.id}`) || offStage?.findOne(`#${obj.id}`);
          if (node) {
            const nodeX = node.x();
            const nodeY = node.y();
            if (nodeX !== undefined && nodeY !== undefined) {
              currentX = nodeX;
              currentY = nodeY;
            }
          }
        }

        // Determine which canvas the element is on
        let isOnMainCanvas = false;
        switch (obj.type) {
          case "Rect":
            isOnMainCanvas = rectangles.some((r) => r.id === obj.id);
            break;
          case "Circle":
            isOnMainCanvas = circles.some((c) => c.id === obj.id);
            break;
          case "Image":
            // CRITICAL FIX: Check both arrays for images
            // Top-layer images can be in either main canvas or off-canvas images array
            // Regular images are in the array corresponding to their canvas
            const isInMainImages = images.some((img) => img.id === obj.id);
            const isInOffCanvasImages = offCanvasImages.some(
              (img) => img.id === obj.id,
            );
            // If in main images array, it's on main canvas
            // If only in off-canvas images array, it's on off-canvas
            // Top-layer images can be in either, but we determine canvas based on which array they're in
            isOnMainCanvas = isInMainImages;
            // CRITICAL: If image is in both arrays (shouldn't happen, but handle it), prefer main canvas
            break;
          case "Text":
            isOnMainCanvas = texts.some((t) => t.id === obj.id);
            break;
          case "Arrow":
            // Arrows with heads are stored in the lines array as unified elements
            // CRITICAL FIX: Check lines array FIRST for unified arrow elements
            isOnMainCanvas =
              lines.some((l) => l.id === obj.id) ||
              arrows.some((a) => a.id === obj.id);
            break;
          case "DoubleSidedArrow":
            // Double-sided arrows are stored in the lines array as unified elements
            // Check both doubleSidedArrows array and lines array
            isOnMainCanvas =
              lines.some((l) => l.id === obj.id) ||
              doubleSidedArrows.some((a) => a.id === obj.id);
            break;
          case "Line":
            // CRITICAL FIX: Unified strokes (including arrows) can be typed as "Line" in selection.
            // For group drag, determine canvas by ID presence in lines array.
            isOnMainCanvas = lines.some((l) => l.id === obj.id);
            break;
        }

        return {
          id: obj.id,
          type: obj.type,
          x: currentX,
          y: currentY,
          isOnMainCanvas, // CRITICAL: Track which canvas element is on
        };
      });

      // Store initial positions
      individualElementDragStartRef.current = {
        elementPositions,
      };

      // Capture current blue wrapper Rect(s) positions so handleGroupDragMove can
      // imperatively translate them by deltaX/deltaY on every drag frame.
      const mainWrapperNode = stageRef?.current?.findOne(
        "#group-drag-wrapper-main",
      );
      const offWrapperNode = offCanvasStageRef?.current?.findOne(
        "#group-drag-wrapper-offcanvas",
      );
      wrapperNodeInitialAttrsRef.current = {
        main: mainWrapperNode
          ? {
              x: mainWrapperNode.x(),
              y: mainWrapperNode.y(),
              width: mainWrapperNode.width(),
              height: mainWrapperNode.height(),
            }
          : null,
        off: offWrapperNode
          ? {
              x: offWrapperNode.x(),
              y: offWrapperNode.y(),
              width: offWrapperNode.width(),
              height: offWrapperNode.height(),
            }
          : null,
      };

      // CRITICAL: Mark group drag as active immediately so any redundant handleGroupDragStart
      // calls from onDragMove handlers return early and do NOT re-capture initial positions
      // at the (already-moved) first drag frame position.
      isGroupDraggingRef.current = true;
      groupDragSelectionSyncRef.current.lastTime = 0;
    },
    [
      isCloneMode,
      lockedAction,
      selectedObjects,
      getActualElementForDrag,
      rectangles,
      circles,
      images,
      texts,
      arrows,
      doubleSidedArrows,
      lines,
      stageRef,
      offCanvasStageRef,
    ],
  );

  const cancelActiveGroupDrag = useCallback(() => {
    if (pendingGroupMoveRef.current) {
      clearTimeout(pendingGroupMoveRef.current);
      pendingGroupMoveRef.current = null;
    }
    isGroupDraggingRef.current = false;
    groupDragPointerStartRef.current = null;
    groupDragSourceStageRef.current = null;
    individualElementDragStartRef.current = { elementPositions: [] };
    groupDragPreviewsRef.current = [];
    setGroupDragPreviews([]);
    setGroupDragDisplay?.(null);
    groupDragSelectionSyncRef.current.lastTime = 0;
  }, [setGroupDragDisplay]);

  const stopKonvaDragUnderPointer = useCallback((stage, pointerPos) => {
    if (!stage || !pointerPos) return;
    let n = stage.getIntersection(pointerPos);
    while (n && n !== stage) {
      if (typeof n.stopDrag === "function") {
        try {
          n.stopDrag();
        } catch (_) {
          /* ignore */
        }
        return;
      }
      n = n.getParent?.() ?? null;
    }
  }, []);

  const handleGroupDragMove = useCallback(
    (draggedElementId, draggedElementType, newX, newY, event = null) => {
      // Duplicate-to-selected-location (clone mode): don't move group – only click-to-place duplicates
      if (isCloneMode || lockedAction) {
        isGroupDraggingRef.current = false;
        return;
      }
      // DEBUG: Log group drag move
      // If multiple elements are selected, enable group drag regardless of isMultiSelectMode
      if (!selectedObjects || selectedObjects.length <= 1) {
        isGroupDraggingRef.current = false;
        return;
      }

      // Mark that we're in a group drag
      isGroupDraggingRef.current = true;
      // Update last drag move time
      lastDragMoveTimeRef.current = Date.now();

      // Cancel any pending group move since we're still dragging
      if (pendingGroupMoveRef.current) {
        clearTimeout(pendingGroupMoveRef.current);
        pendingGroupMoveRef.current = null;
      }

      // Find the dragged element's initial position
      let draggedElementInitialPos =
        individualElementDragStartRef.current?.elementPositions?.find(
          (pos) =>
            pos.id === draggedElementId && pos.type === draggedElementType,
        );

      // UnifiedLineArrowElement drags may provide a `draggedElementType` that
      // doesn't exactly match the `selectedObjects` entry type (e.g. Arrow vs Line).
      // Fallback to ID-only match so multi-selection group-drag still applies.
      if (!draggedElementInitialPos) {
        draggedElementInitialPos =
          individualElementDragStartRef.current?.elementPositions?.find(
            (pos) => pos.id === draggedElementId,
          );
      }

      if (!draggedElementInitialPos) {
        return;
      }

      // Get stage references for boundary detection
      const mainStage = stageRef?.current;
      const offStage = offCanvasStageRef?.current;
      const topLayerStage = topLayerStageRef?.current;
      const offCanvasTopLayerStage = offCanvasTopLayerStageRef?.current;
      // Top-layer images on off-canvas live on `offCanvasTopLayerStage`; base off-canvas is `offStage`.
      // Use whichever exists for bounds/delta clamp and strip math so group drag + strip lock still run
      // when only the top-layer stage ref is ready (or briefly during layout).
      const offStageForBounds = offStage || offCanvasTopLayerStage;

      // CRITICAL FIX: Use newX/newY from drag event parameters directly for delta calculation
      // DO NOT read from Konva node as it was updated in the previous drag move iteration
      // Reading from node causes exponential growth: node has updated position → delta grows → update node → repeat
      // The newX/newY parameters are the raw positions from Konva's drag event and are correct
      const draggedElementCurrentX = newX;
      const draggedElementCurrentY = newY;

      // Use the same logic as wrapper rectangle drag - calculate delta from dragged element
      // and apply it to all elements using their initial positions (initialPos.x + deltaX)
      // This maintains relative positions perfectly, just like dragging the blue box
      let deltaX = draggedElementCurrentX - draggedElementInitialPos.x;
      let deltaY = draggedElementCurrentY - draggedElementInitialPos.y;
      let deltaSource = "node";
      if (!draggedElementInitialPos.isOnMainCanvas && event?.evt && offStageForBounds) {
        try {
          offStageForBounds.setPointersPositions(event.evt);
          const pointer = offStageForBounds.getPointerPosition?.();
          if (pointer && Number.isFinite(pointer.x) && Number.isFinite(pointer.y)) {
            if (!groupDragPointerStartRef.current) {
              groupDragPointerStartRef.current = { x: pointer.x, y: pointer.y };
              deltaSource = "pointer-init";
            } else {
              deltaX = pointer.x - groupDragPointerStartRef.current.x;
              deltaY = pointer.y - groupDragPointerStartRef.current.y;
              deltaSource = "pointer";
            }
          }
        } catch (_e) {
          // fallback to node delta
        }
      }
      if (!draggedElementInitialPos.isOnMainCanvas) {
        console.log("[GROUP-DRAG-MOVE][OFFCANVAS][DELTA]", {
          draggedElementId,
          draggedElementType,
          nodeX: draggedElementCurrentX,
          nodeY: draggedElementCurrentY,
          startX: draggedElementInitialPos.x,
          startY: draggedElementInitialPos.y,
          deltaX: Math.round(deltaX),
          deltaY: Math.round(deltaY),
          deltaSource,
          pointerStart: groupDragPointerStartRef.current,
        });
      }
      groupDragLastDeltaRef.current = { x: deltaX, y: deltaY };
      const wrapperInitial = wrapperNodeInitialAttrsRef.current;

      // Allow temporary overshoot during drag-move.
      // Final boundary clamping (with transformer buffer) is applied on drag-end.
      const constrainedMainDelta = { x: deltaX, y: deltaY };
      const constrainedOffDelta = { x: deltaX, y: deltaY };

      // REMOVED: Boundary crossing during drag move
      // Elements stay in their current canvas during drag and follow cursor smoothly
      // Boundary crossing will be handled on drag end (in handleElementDragEnd)

      // CRITICAL FIX: Do NOT update selectedObjects during drag
      // This causes images to be rendered twice (once from images array, once from selectedObjects)
      // creating visual duplicates. Instead, only update the actual element state arrays.
      // The wrapper rectangle will calculate positions from initialPos + delta directly.
      // selectedObjects will be updated on drag end when we update the actual element arrays.

      // CRITICAL FIX: Update Konva nodes ONLY during drag, do NOT update state
      // State updates cause React re-renders that lag and create duplicate rendering
      // Elements being dragged will be hidden from state rendering (filtered out)
      // State will be updated on drag end to persist final positions

      individualElementDragStartRef.current.elementPositions.forEach(
        (elementInitialPos) => {
          // CRITICAL FIX: Account for different coordinate systems when applying delta
          // Text and rectangles use top-left coordinates, images/circles use center coordinates
          let calculatedNewX, calculatedNewY;

          if (
            elementInitialPos.type === "Text" ||
            elementInitialPos.type === "Rect"
          ) {
            const deltaForCanvas = elementInitialPos.isOnMainCanvas
              ? constrainedMainDelta
              : constrainedOffDelta;
            // For elements that use top-left coordinates, apply delta directly
            calculatedNewX = elementInitialPos.x + deltaForCanvas.x;
            calculatedNewY = elementInitialPos.y + deltaForCanvas.y;
          } else if (
            elementInitialPos.type === "Image" ||
            elementInitialPos.type === "Circle"
          ) {
            const deltaForCanvas = elementInitialPos.isOnMainCanvas
              ? constrainedMainDelta
              : constrainedOffDelta;
            // For elements that use center coordinates, apply delta directly
            calculatedNewX = elementInitialPos.x + deltaForCanvas.x;
            calculatedNewY = elementInitialPos.y + deltaForCanvas.y;
          } else if (
            elementInitialPos.type === "Line" ||
            elementInitialPos.type === "Arrow" ||
            elementInitialPos.type === "DoubleSidedArrow"
          ) {
            const deltaForCanvas = elementInitialPos.isOnMainCanvas
              ? constrainedMainDelta
              : constrainedOffDelta;
            // For lines/arrows, apply delta to the base position
            calculatedNewX = elementInitialPos.x + deltaForCanvas.x;
            calculatedNewY = elementInitialPos.y + deltaForCanvas.y;
          } else {
            const deltaForCanvas = elementInitialPos.isOnMainCanvas
              ? constrainedMainDelta
              : constrainedOffDelta;
            // Default case
            calculatedNewX = elementInitialPos.x + deltaForCanvas.x;
            calculatedNewY = elementInitialPos.y + deltaForCanvas.y;
          }

          // PERFORMANCE OPTIMIZATION: Cache node references to avoid repeated findOne calls
          // Update Konva node position directly for immediate visual feedback
          // CRITICAL FIX: For top-layer images, check BOTH main and off-canvas arrays
          // Top-layer images can be in either array
          const isTopLayerImage =
            elementInitialPos.type === "Image" &&
            (images.some(
              (img) =>
                img.id === elementInitialPos.id && img.isOnTopLayer === true,
            ) ||
              offCanvasImages.some(
                (img) =>
                  img.id === elementInitialPos.id && img.isOnTopLayer === true,
              ));

          let elementNode = null;
          if (isTopLayerImage && (topLayerStage || offCanvasTopLayerStage)) {
            elementNode =
              topLayerStage?.findOne(`#${elementInitialPos.id}`) ||
              offCanvasTopLayerStage?.findOne(`#${elementInitialPos.id}`);
          }
          if (!elementNode) {
            elementNode =
              mainStage?.findOne(`#${elementInitialPos.id}`) ||
              offStage?.findOne(`#${elementInitialPos.id}`) ||
              topLayerStage?.findOne(`#${elementInitialPos.id}`) ||
              offCanvasTopLayerStage?.findOne(`#${elementInitialPos.id}`);
          }

          if (elementNode) {
            // CRITICAL FIX: Constrain positions to be within off-canvas stage bounds during drag
            // Check which stage the element node is on
            const elementStage = elementNode.getStage();
            const isOnOffCanvasStage = elementStage === offStage;
            const isOnTopLayerStage =
              elementStage === topLayerStage ||
              elementStage === offCanvasTopLayerStage;
            const isElementOnMainCanvas = !!elementInitialPos.isOnMainCanvas;

            // Constrain positions if element is on off-canvas stage
            // CRITICAL: Top-layer images are on topLayerStage but conceptually on main canvas
            // Don't constrain them as off-canvas elements
            let finalX = calculatedNewX;
            let finalY = calculatedNewY;

            if (
              false &&
              !isElementOnMainCanvas &&
              (isOnOffCanvasStage || isOnTopLayerStage) &&
              offStageForBounds
            ) {
              // PERFORMANCE: Cache stage dimensions (only read once per drag move, not per element)
              const stageWidth = offStageForBounds.width() || window.innerWidth;
              const stageHeight =
                offStageForBounds.height() || fixedStageHeightPx;

              // Get element from state to calculate bounding box
              let element = null;
              switch (elementInitialPos.type) {
                case "Rect":
                  element = offCanvasRectangles.find(
                    (r) => r.id === elementInitialPos.id,
                  );
                  break;
                case "Circle":
                  element = offCanvasCircles.find(
                    (c) => c.id === elementInitialPos.id,
                  );
                  break;
                case "Image":
                  element = offCanvasImages.find(
                    (img) => img.id === elementInitialPos.id,
                  );
                  break;
                case "Text":
                  element = offCanvasTexts.find(
                    (t) => t.id === elementInitialPos.id,
                  );
                  break;
                case "Line":
                case "Arrow":
                case "DoubleSidedArrow":
                  element =
                    offCanvasLines.find((l) => l.id === elementInitialPos.id) ||
                    offCanvasArrows.find(
                      (a) => a.id === elementInitialPos.id,
                    ) ||
                    offCanvasDoubleSidedArrows.find(
                      (a) => a.id === elementInitialPos.id,
                    );
                  break;
              }

              if (element) {
                // CRITICAL FIX: For lines/arrows, check ALL points individually
                // This ensures no point goes off-screen, even for vertical/horizontal lines
                if (
                  elementInitialPos.type === "Line" ||
                  elementInitialPos.type === "Arrow" ||
                  elementInitialPos.type === "DoubleSidedArrow"
                ) {
                  const points = element.points || [0, 0, 0, 0];
                  if (points.length >= 4) {
                    // Calculate absolute positions of all points with new group position
                    const absolutePoints = [];
                    for (let i = 0; i < points.length; i += 2) {
                      absolutePoints.push({
                        x: points[i] + calculatedNewX,
                        y: points[i + 1] + calculatedNewY,
                      });
                    }

                    // Find min/max across ALL points
                    let absMinX = Infinity;
                    let absMaxX = -Infinity;
                    let absMinY = Infinity;
                    let absMaxY = -Infinity;

                    for (const point of absolutePoints) {
                      absMinX = Math.min(absMinX, point.x);
                      absMaxX = Math.max(absMaxX, point.x);
                      absMinY = Math.min(absMinY, point.y);
                      absMaxY = Math.max(absMaxY, point.y);
                    }

                    // Add padding for stroke width
                    const strokeWidth = element.strokeWidth || 2;
                    const padding = Math.max(strokeWidth / 2, 5);

                    // Calculate adjustment needed to keep ALL points within bounds
                    let deltaX = 0;
                    let deltaY = 0;

                    // Constrain X: ensure ALL points have x >= padding and x <= stageWidth - padding
                    if (absMinX < padding) {
                      deltaX = padding - absMinX; // Move right
                    }
                    if (absMaxX > stageWidth - padding) {
                      const rightOverflow = absMaxX - (stageWidth - padding);
                      deltaX = Math.max(deltaX, -rightOverflow); // Move left if needed
                    }

                    // Constrain Y: ensure ALL points have y >= padding and y <= stageHeight - padding
                    if (absMinY < padding) {
                      deltaY = padding - absMinY; // Move down
                    }
                    if (absMaxY > stageHeight - padding) {
                      const bottomOverflow = absMaxY - (stageHeight - padding);
                      deltaY = Math.max(deltaY, -bottomOverflow); // Move up if needed
                    }

                    // Apply adjustment
                    finalX = calculatedNewX + deltaX;
                    finalY = calculatedNewY + deltaY;
                  } else {
                    // Fallback for invalid points
                    finalX = Math.max(0, Math.min(stageWidth, calculatedNewX));
                    finalY = Math.max(0, Math.min(stageHeight, calculatedNewY));
                  }
                } else {
                  // For other element types, use bounding box approach
                  const tempElement = {
                    ...element,
                    x: calculatedNewX,
                    y: calculatedNewY,
                  };
                  const bbox = getElementBoundingBox(
                    tempElement,
                    elementInitialPos.type,
                  );

                  // Determine coordinate system based on element type
                  const isCenterCoordinate =
                    elementInitialPos.type === "Image" ||
                    elementInitialPos.type === "Circle";

                  if (isCenterCoordinate) {
                    // For center coordinate elements (Image, Circle)
                    const halfWidth = bbox.width / 2;
                    const halfHeight = bbox.height / 2;

                    // For circles, account for stroke width and toolbar
                    let minX = halfWidth;
                    if (elementInitialPos.type === "Circle") {
                      const strokeWidth = element.strokeWidth || 2;
                      // Leftmost point of stroke = x - radius - strokeWidth/2
                      // Constraint: x - radius - strokeWidth/2 >= TOOLBAR_WIDTH
                      // Therefore: x >= TOOLBAR_WIDTH + radius + strokeWidth/2
                      const radius =
                        element.radius ||
                        Math.max(element.radiusX || 0, element.radiusY || 0);
                      minX = TOOLBAR_WIDTH + radius + strokeWidth / 2;
                    }

                    finalX = Math.max(
                      minX,
                      Math.min(stageWidth - halfWidth, calculatedNewX),
                    );
                    finalY = Math.max(
                      halfHeight,
                      Math.min(stageHeight - halfHeight, calculatedNewY),
                    );
                  } else {
                    // For top-left coordinate elements (Rect, Text)
                    let minX = 0;
                    if (elementInitialPos.type === "Rect") {
                      const strokeWidth = element.strokeWidth || 2;
                      // Leftmost point of stroke = x - strokeWidth/2
                      // Constraint: x - strokeWidth/2 >= TOOLBAR_WIDTH
                      // Therefore: x >= TOOLBAR_WIDTH + strokeWidth/2
                      minX = TOOLBAR_WIDTH + strokeWidth / 2;
                    }

                    finalX = Math.max(
                      minX,
                      Math.min(stageWidth - bbox.width, calculatedNewX),
                    );
                    finalY = Math.max(
                      0,
                      Math.min(stageHeight - bbox.height, calculatedNewY),
                    );
                  }
                }

                const origExt = getAbsoluteHorizontalExtent(
                  element,
                  elementInitialPos.type,
                  elementInitialPos.x,
                );
                const lockedStripSide = getOffCanvasHorizontalStripSide(
                  origExt.minX,
                  origExt.maxX,
                  stageWidth,
                );
                finalX = shiftXToStayInOffCanvasStrip(
                  element,
                  elementInitialPos.type,
                  finalX,
                  stageWidth,
                  lockedStripSide,
                );
                if (!isElementOnMainCanvas) {
                  console.log("[GROUP-DRAG-MOVE][OFFCANVAS][ENTRY-CLAMP]", {
                    id: elementInitialPos.id,
                    type: elementInitialPos.type,
                    isOnOffCanvasStage,
                    isOnTopLayerStage,
                    calculatedNewX: Math.round(calculatedNewX),
                    calculatedNewY: Math.round(calculatedNewY),
                    finalX: Math.round(finalX),
                    finalY: Math.round(finalY),
                    lockedStripSide,
                    stageWidth: Math.round(stageWidth),
                    stageHeight: Math.round(stageHeight),
                  });
                }
              } else {
                // Fallback: just constrain to >= 0 and <= stage dimensions
                finalX = Math.max(0, Math.min(stageWidth, calculatedNewX));
                finalY = Math.max(0, Math.min(stageHeight, calculatedNewY));
                if (!isElementOnMainCanvas) {
                  console.log("[GROUP-DRAG-MOVE][OFFCANVAS][ENTRY-CLAMP-FALLBACK]", {
                    id: elementInitialPos.id,
                    type: elementInitialPos.type,
                    calculatedNewX: Math.round(calculatedNewX),
                    calculatedNewY: Math.round(calculatedNewY),
                    finalX: Math.round(finalX),
                    finalY: Math.round(finalY),
                    stageWidth: Math.round(stageWidth),
                    stageHeight: Math.round(stageHeight),
                  });
                }
              }
            } else if (
              false &&
              isElementOnMainCanvas &&
              mainStage &&
              !wrapperInitial?.main
            ) {
              // Restrict main-canvas drag move to main stage bounds.
              const stageWidth = mainStage.width() || mainCanvasWidth;
              const stageHeight = mainStage.height() || fixedStageHeightPx;
              let element = null;
              switch (elementInitialPos.type) {
                case "Rect":
                  element = rectangles.find((r) => r.id === elementInitialPos.id);
                  break;
                case "Circle":
                  element = circles.find((c) => c.id === elementInitialPos.id);
                  break;
                case "Image":
                  element = images.find((img) => img.id === elementInitialPos.id);
                  break;
                case "Text":
                  element = texts.find((t) => t.id === elementInitialPos.id);
                  break;
                case "Line":
                case "Arrow":
                case "DoubleSidedArrow":
                  element =
                    lines.find((l) => l.id === elementInitialPos.id) ||
                    arrows.find((a) => a.id === elementInitialPos.id) ||
                    doubleSidedArrows.find((a) => a.id === elementInitialPos.id);
                  break;
              }

              if (element) {
                if (
                  elementInitialPos.type === "Line" ||
                  elementInitialPos.type === "Arrow" ||
                  elementInitialPos.type === "DoubleSidedArrow"
                ) {
                  const points = element.points || [0, 0, 0, 0];
                  if (points.length >= 4) {
                    let minPX = Infinity;
                    let maxPX = -Infinity;
                    let minPY = Infinity;
                    let maxPY = -Infinity;
                    for (let i = 0; i < points.length; i += 2) {
                      const absX = points[i] + calculatedNewX;
                      const absY = points[i + 1] + calculatedNewY;
                      minPX = Math.min(minPX, absX);
                      maxPX = Math.max(maxPX, absX);
                      minPY = Math.min(minPY, absY);
                      maxPY = Math.max(maxPY, absY);
                    }
                    const strokeWidth = element.strokeWidth || 2;
                    const padding = Math.max(strokeWidth / 2, 1);
                    let dx = 0;
                    let dy = 0;
                    if (minPX < padding) dx = padding - minPX;
                    if (maxPX > stageWidth - padding)
                      dx = Math.min(dx, stageWidth - padding - maxPX);
                    if (minPY < padding) dy = padding - minPY;
                    if (maxPY > stageHeight - padding)
                      dy = Math.min(dy, stageHeight - padding - maxPY);
                    finalX = calculatedNewX + dx;
                    finalY = calculatedNewY + dy;
                  }
                } else {
                  const tempElement = {
                    ...element,
                    x: calculatedNewX,
                    y: calculatedNewY,
                  };
                  const bbox = getElementBoundingBox(
                    tempElement,
                    elementInitialPos.type,
                  );
                  const isCenterCoordinate =
                    elementInitialPos.type === "Image" ||
                    elementInitialPos.type === "Circle";

                  if (isCenterCoordinate) {
                    const halfWidth = bbox.width / 2;
                    const halfHeight = bbox.height / 2;
                    finalX = Math.max(
                      halfWidth,
                      Math.min(stageWidth - halfWidth, calculatedNewX),
                    );
                    finalY = Math.max(
                      halfHeight,
                      Math.min(stageHeight - halfHeight, calculatedNewY),
                    );
                  } else {
                    finalX = Math.max(
                      0,
                      Math.min(stageWidth - bbox.width, calculatedNewX),
                    );
                    finalY = Math.max(
                      0,
                      Math.min(stageHeight - bbox.height, calculatedNewY),
                    );
                  }
                }
              } else {
                finalX = Math.max(0, Math.min(stageWidth, calculatedNewX));
                finalY = Math.max(0, Math.min(stageHeight, calculatedNewY));
              }
            }

            // DEBUG: Log position updates for lines/arrows
            if (
              elementInitialPos.type === "Line" ||
              elementInitialPos.type === "Arrow" ||
              elementInitialPos.type === "DoubleSidedArrow"
            ) {
            }

            // Update Konva node position with constrained values
            elementNode.x(finalX);
            elementNode.y(finalY);

            // For top-layer images, also move the occlusion blocker Rect so it
            // tracks the image during group drag (blocker's own onDragMove never
            // fires for non-dragged group members, so we sync it imperatively here).
            if (isTopLayerImage) {
              const nodeStage = elementNode.getStage();
              if (nodeStage) {
                const blockerNode = nodeStage.findOne(
                  `#${elementInitialPos.id}_occlusion_blocker`,
                );
                if (blockerNode) {
                  blockerNode.x(finalX);
                  blockerNode.y(finalY);
                }
              }
            }
          }
        },
      );

      // Imperatively translate the blue wrapper Rect(s) by the same deltaX/deltaY so the
      // selection box follows the pointer in real-time during individual-element group drag,
      // without waiting for a React re-render cycle.
      if (wrapperInitial) {
        const mainWrapperNode = mainStage?.findOne("#group-drag-wrapper-main");
        const offWrapperNode = offStage?.findOne(
          "#group-drag-wrapper-offcanvas",
        );
        if (mainWrapperNode && wrapperInitial.main) {
          mainWrapperNode.x(wrapperInitial.main.x + constrainedMainDelta.x);
          mainWrapperNode.y(wrapperInitial.main.y + constrainedMainDelta.y);
        }
        if (offWrapperNode && wrapperInitial.off) {
          offWrapperNode.x(wrapperInitial.off.x + constrainedOffDelta.x);
          offWrapperNode.y(wrapperInitial.off.y + constrainedOffDelta.y);
        }
      }

      // CRITICAL FIX: Show drag previews for all elements in the group when dragging between stages
      // This allows users to see where elements will land when dragging between canvases

      // Get mouse coordinates - MUST use actual mouse position for boundary detection.
      // Prefer global mouse position so off→main drag shows main-stage coords as soon as cursor enters main
      // (event.evt can be stale or wrong when dragging on off-canvas stage)
      let clientX, clientY;
      if (globalMousePositionRef?.current != null) {
        const g = globalMousePositionRef.current;
        if (typeof g.x === "number" && typeof g.y === "number") {
          clientX = g.x;
          clientY = g.y;
        }
      }
      if (clientX === undefined || clientY === undefined) {
        if (
          event?.evt?.clientX !== undefined &&
          event?.evt?.clientY !== undefined
        ) {
          clientX = event.evt.clientX;
          clientY = event.evt.clientY;
        }
      }
      if (
        (clientX === undefined || clientY === undefined) &&
        mainStage &&
        offStage
      ) {
        const isDraggedElementTopLayer =
          draggedElementType === "Image" &&
          (images.some(
            (img) => img.id === draggedElementId && img.isOnTopLayer === true,
          ) ||
            offCanvasImages.some(
              (img) => img.id === draggedElementId && img.isOnTopLayer === true,
            ));

        let draggedNode = null;
        if (
          isDraggedElementTopLayer &&
          (topLayerStage || offCanvasTopLayerStage)
        ) {
          draggedNode =
            topLayerStage?.findOne(`#${draggedElementId}`) ||
            offCanvasTopLayerStage?.findOne(`#${draggedElementId}`);
        }
        if (!draggedNode) {
          draggedNode =
            mainStage?.findOne(`#${draggedElementId}`) ||
            offStage?.findOne(`#${draggedElementId}`) ||
            topLayerStage?.findOne(`#${draggedElementId}`) ||
            offCanvasTopLayerStage?.findOne(`#${draggedElementId}`);
        }

        if (draggedNode) {
          const stage = draggedNode.getStage();
          const stageBox = stage.container().getBoundingClientRect();
          clientX = stageBox.left + newX;
          clientY = stageBox.top + newY;
        }
      }

      // Check if we have required data
      if (!mainStage || !offStage) {
        return;
      }

      // If we don't have client coordinates, we can't determine target canvas
      if (clientX === undefined || clientY === undefined) {
        return;
      }

      const mainBBox = mainStage.container().getBoundingClientRect();
      const offBBox = offStage.container().getBoundingClientRect();

      // Determine which canvas the mouse is over
      const withinMain =
        clientX >= mainBBox.left &&
        clientX <= mainBBox.right &&
        clientY >= mainBBox.top &&
        clientY <= mainBBox.bottom;

      const withinOff =
        clientX >= offBBox.left &&
        clientX <= offBBox.right &&
        clientY >= offBBox.top &&
        clientY <= offBBox.bottom;

      // Ambiguous overlap zone between stages should not force a target canvas.
      const targetCanvas =
        withinMain && withinOff
          ? null
          : withinMain
            ? "main"
            : withinOff
              ? "offCanvas"
              : null;

      // CRITICAL FIX: Update selectedObjects and selectedObject with current Konva node positions during drag
      // Convert positions to be relative to the target canvas stage when dragging over off-canvas
      // This ensures RectangleEditor shows positions relative to off-canvas stage origin even though
      // the element is still physically on the main canvas stage during drag
      if (selectedObjects && setSelectedObjects && mainStage && offStage) {
        const updatedSelectedObjects = selectedObjects.map((obj) => {
          // DEBUG: Log every object being processed in the map
          if (
            obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"
          ) {
          }

          // Find the corresponding initial position
          const initialPos =
            individualElementDragStartRef.current?.elementPositions?.find(
              (pos) => pos.id === obj.id && pos.type === obj.type,
            );

          // Find the Konva node to get current position
          const elementId = obj.id;
          const elementType = obj.type;

          const isTopLayerImage =
            elementType === "Image" &&
            (images.some(
              (img) => img.id === elementId && img.isOnTopLayer === true,
            ) ||
              offCanvasImages.some(
                (img) => img.id === elementId && img.isOnTopLayer === true,
              ));

          let elementNode = null;
          if (isTopLayerImage && (topLayerStage || offCanvasTopLayerStage)) {
            elementNode =
              topLayerStage?.findOne(`#${elementId}`) ||
              offCanvasTopLayerStage?.findOne(`#${elementId}`);
          }
          if (!elementNode) {
            const startedOnMainCanvas = initialPos
              ? !!initialPos.isOnMainCanvas
              : false;
            if (startedOnMainCanvas) {
              elementNode =
                mainStage?.findOne(`#${elementId}`) ||
                offStage?.findOne(`#${elementId}`) ||
                topLayerStage?.findOne(`#${elementId}`) ||
                offCanvasTopLayerStage?.findOne(`#${elementId}`);
            } else {
              elementNode =
                offStage?.findOne(`#${elementId}`) ||
                mainStage?.findOne(`#${elementId}`) ||
                offCanvasTopLayerStage?.findOne(`#${elementId}`) ||
                topLayerStage?.findOne(`#${elementId}`);
            }
          }

          // CRITICAL FIX: If node not found, it might be filtered out due to the flag
          // In this case, we still need to update the flag based on targetCanvas
          // and compute position (initialPos + delta) so SelectionEditor shows correct coords
          if (!elementNode) {
            // DEBUG: Log when element node is not found
            if (
              obj.type === "Line" ||
              obj.type === "Arrow" ||
              obj.type === "DoubleSidedArrow"
            ) {
            }

            // Determine if we should show this as an off-canvas element
            const isInOffCanvas =
              targetCanvas === "offCanvas" && !isTopLayerImage;

            let noNodeX = obj.x;
            let noNodeY = obj.y;
            // CRITICAL: When node is not found but we have initialPos, compute position
            // so off-canvas → main drag shows main-stage coords in SelectionEditor (not stale off-canvas)
            if (initialPos && mainBBox && offBBox) {
              noNodeX = initialPos.x + deltaX;
              noNodeY = initialPos.y + deltaY;
              if (targetCanvas === "main") {
                // Convert from off-canvas stage coords to main stage coords
                const absX = offBBox.left + noNodeX;
                const absY = offBBox.top + noNodeY;
                noNodeX = absX - mainBBox.left;
                noNodeY = absY - mainBBox.top;
              } else if (targetCanvas === "offCanvas") {
                // Convert from main stage coords to off-canvas stage coords
                const absX = mainBBox.left + noNodeX;
                const absY = mainBBox.top + noNodeY;
                noNodeX = absX - offBBox.left;
                noNodeY = absY - offBBox.top;
              }
            }

            // Create updated object with flag, position, and target canvas for editor display
            const updatedObj = {
              ...obj,
              x: noNodeX,
              y: noNodeY,
              _isInOffCanvasDuringDrag: isInOffCanvas ? true : false,
              ...(targetCanvas
                ? { _targetCanvasDuringDrag: targetCanvas }
                : {}),
            };

            // DEBUG: Log flag update
            if (
              obj.type === "Line" ||
              obj.type === "Arrow" ||
              obj.type === "DoubleSidedArrow"
            ) {
            }

            return updatedObj;
          }

          // Get current position from Konva node (this is relative to the stage the node is on)
          let currentX = elementNode.x();
          let currentY = elementNode.y();

          // CRITICAL FIX: For lines/arrows AND top-layer images, do NOT read from the
          // Konva node during group drag.  For lines/arrows the node position can accumulate
          // (exponential growth on conversion); for top-layer images the useTopLeftAnchor
          // ref-callback can reset the node's offsetX/Y mid-render which shifts node.y() away
          // from the imperatively-set value, injecting a stale coordinate into the delta
          // chain and causing all elements to drift downward.
          // Instead, always use initialPos + delta (the authoritative computed position).
          const isTopLayerImageForState =
            elementType === "Image" && isTopLayerImage;
          if (
            (elementType === "Line" ||
              elementType === "Arrow" ||
              elementType === "DoubleSidedArrow" ||
              isTopLayerImageForState) &&
            initialPos
          ) {
            // Calculate current position by applying delta to initial position
            // This matches what we did when updating the Konva node earlier
            currentX = initialPos.x + deltaX;
            currentY = initialPos.y + deltaY;

          }

          // Determine which stage the element node is currently on
          const elementStage = elementNode?.getStage?.();
          const isOnOffCanvasStage = elementStage === offStage;
          const isOnTopLayerStage =
            elementStage === topLayerStage ||
            elementStage === offCanvasTopLayerStage;
          const isOnMainCanvasStage = elementStage === mainStage;

          // Source canvas during drag must be stable; prefer drag-start canvas.
          const startedOnMainCanvas = initialPos
            ? !!initialPos.isOnMainCanvas
            : isOnMainCanvasStage;

          // CRITICAL: If dragging over off-canvas area and element is currently on main canvas stage,
          // convert the position to be relative to off-canvas stage origin
          // This is the key fix - we need to show position relative to target canvas, not source canvas
          // Check if element is on main canvas stage (regardless of where it started) when dragging over off-canvas

          if (
            targetCanvas === "offCanvas" &&
            startedOnMainCanvas &&
            !isTopLayerImage
          ) {
            // Element is on main canvas stage but being dragged over off-canvas area
            // Convert position from main canvas stage coordinates to off-canvas stage coordinates

            // Step 1: Convert from current stage coordinates to absolute/client coordinates
            // Since element is still on main canvas stage during drag, use mainBBox
            const originalX = currentX;
            const originalY = currentY;
            const absX = mainBBox.left + currentX;
            const absY = mainBBox.top + currentY;

            // Step 2: Convert from absolute/client coordinates to off-canvas stage coordinates
            currentX = absX - offBBox.left;
            currentY = absY - offBBox.top;

            // DEBUG: Log conversion for lines/arrows
            if (
              elementType === "Line" ||
              elementType === "Arrow" ||
              elementType === "DoubleSidedArrow"
            ) {
            }
          }
          // CRITICAL: If dragging over main canvas area but element started/is on off-canvas stage,
          // convert the position to be relative to main canvas stage origin
          else if (
            targetCanvas === "main" &&
            !startedOnMainCanvas &&
            !isTopLayerImage
          ) {
            // Element is on off-canvas stage but being dragged over main canvas area
            // Convert position from off-canvas stage coordinates to main canvas stage coordinates

            // Step 1: Convert from off-canvas stage coordinates to absolute/client coordinates
            const absX = offBBox.left + currentX;
            const absY = offBBox.top + currentY;

            // Step 2: Convert from absolute/client coordinates to main canvas stage coordinates
            currentX = absX - mainBBox.left;
            currentY = absY - mainBBox.top;
          }
          // If element is already on the target canvas stage, position is already relative to that stage's origin
          // No conversion needed in this case

          // Determine if we should show this as an off-canvas element in the editor
          // This is based on targetCanvas (where mouse is), not where element currently is
          // If targetCanvas is null (mouse not over either canvas), keep current state
          const isInOffCanvas =
            targetCanvas === "offCanvas" && !isTopLayerImage;

          // DEBUG: Log before flag update for lines/arrows
          if (
            obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"
          ) {
          }

          // Create updated object with converted position, off-canvas flag, and target canvas for editor
          const updatedObj = {
            ...obj,
            x: currentX,
            y: currentY,
          };

          // Set or clear the flag based on targetCanvas (where mouse is dragging)
          if (isInOffCanvas) {
            updatedObj._isInOffCanvasDuringDrag = true;
          } else {
            // CRITICAL FIX: Set to false instead of deleting to ensure React detects the change
            // Deleting the property might not trigger a re-render if React doesn't see it as a change
            updatedObj._isInOffCanvasDuringDrag = false;
          }
          // Pass target canvas so SelectionEditor shows x,y relative to the correct stage during drag
          if (targetCanvas) {
            updatedObj._targetCanvasDuringDrag = targetCanvas;
          }

          // DEBUG: Log flag changes for lines/arrows
          if (
            obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"
          ) {
          }

          return updatedObj;
        });

        // CRITICAL: Do NOT pass _isInOffCanvasDuringDrag into setSelectedObjects/setSelectedObject
        // during group drag move. That flag causes findElementCanvas() to return isInOffCanvas for
        // those elements, which can move the transformer/editor to the off-canvas stage and
        // interrupt Konva's drag (triggering premature group drag end when crossing main → off-canvas).
        // Strip the flag so elements stay considered on their current stage until drag end.
        // Same approach as individual rectangle/circle drag in rectangleRenderer and circleRenderer.
        const selectedObjectsForState = updatedSelectedObjects.map((obj) => {
          const { _isInOffCanvasDuringDrag: _unusedFlag, ...rest } = obj;
          return rest;
        });

        if (setGroupDragDisplay && targetCanvas) {
          const avgX =
            selectedObjectsForState.reduce((s, o) => s + (o.x ?? 0), 0) /
            selectedObjectsForState.length;
          const avgY =
            selectedObjectsForState.reduce((s, o) => s + (o.y ?? 0), 0) /
            selectedObjectsForState.length;
          setGroupDragDisplay({ avgX, avgY, targetCanvas });
        }

        // Throttle selectedObjects/selectedObject sync while dragging a group.
        // Konva node updates above remain per-frame (smooth visuals), while editor
        // state updates are rate-limited to reduce render pressure.
        const now = Date.now();
        const shouldSyncSelectionState =
          now - groupDragSelectionSyncRef.current.lastTime >=
          groupDragSelectionSyncRef.current.ms;

        if (shouldSyncSelectionState) {
          groupDragSelectionSyncRef.current.lastTime = now;
          setSelectedObjects(selectedObjectsForState);
          if (setSelectedObject) {
            if (selectedObjectsForState.length === 1) {
              setSelectedObject(selectedObjectsForState[0]);
            } else if (selectedObject && selectedObjectsForState.length > 0) {
              const matchingUpdatedObj = selectedObjectsForState.find(
                (obj) =>
                  selectedObject?.id === obj?.id &&
                  selectedObject?.type === obj?.type,
              );
              if (matchingUpdatedObj) {
                setSelectedObject(matchingUpdatedObj);
              }
            }
          }
        }
      }

      // CRITICAL FIX: Update selectionBoxStage when dragging group to off-canvas
      // Only update if value actually changes to avoid unnecessary re-renders
      if (
        targetCanvas === "offCanvas" &&
        selectedObjects &&
        selectedObjects.length > 0
      ) {
        // Update selection box stage to off-canvas for ALL element types
        // This ensures the blue selection box shows on the target canvas during drag
        if (selectionBoxStage !== "off-canvas") {
          setSelectionBoxStage("off-canvas");
        }
      } else if (targetCanvas === "main" && selectionBoxStage !== "main") {
        setSelectionBoxStage("main");
      }

      // Collect all previews for the group
      const previews = [];

      // CRITICAL FIX: Only show previews when dragging BETWEEN canvases (crossing boundaries)
      // When dragging within the same canvas, don't show previews - only move Konva nodes
      // This prevents duplicate rendering (preview + actual element)
      const shouldShowPreviews = false;

      // Show previews for all elements in the group - only when crossing canvas boundaries
      if (shouldShowPreviews) {
        individualElementDragStartRef.current.elementPositions.forEach(
          (initialPos) => {
            // Determine which canvas this element is currently on
            const isOnMainCanvas = initialPos.isOnMainCanvas;
            const sourceCanvas = isOnMainCanvas ? "main" : "offCanvas";

            // CRITICAL: Only create preview if actually crossing boundary
            if (sourceCanvas === targetCanvas) {
              return; // Skip preview for elements staying on same canvas
            }

            // CRITICAL: Always show preview for all elements in the group when dragging
            // This allows users to see where all elements will land during drag
            if (targetCanvas) {
              // Get the actual element data from state arrays
              let element = null;
              let sourceStageRef = isOnMainCanvas ? mainStage : offStage;

              switch (initialPos.type) {
                case "Rect":
                  element = isOnMainCanvas
                    ? rectangles.find((r) => r.id === initialPos.id)
                    : offCanvasRectangles.find((r) => r.id === initialPos.id);
                  break;
                case "Circle":
                  element = isOnMainCanvas
                    ? circles.find((c) => c.id === initialPos.id)
                    : offCanvasCircles.find((c) => c.id === initialPos.id);
                  break;
                case "Image": {
                  // CRITICAL: Check if this is a top-layer image - check both main and off-canvas arrays
                  const isTopLayerInMain = images.some(
                    (img) =>
                      img.id === initialPos.id && img.isOnTopLayer === true,
                  );
                  const isTopLayerInOff = offCanvasImages.some(
                    (img) =>
                      img.id === initialPos.id && img.isOnTopLayer === true,
                  );
                  const isTopLayerImage = isTopLayerInMain || isTopLayerInOff;

                  if (isTopLayerImage) {
                    // Top-layer images can be in either main canvas or off-canvas images array but on topLayerStage
                    // CRITICAL: Check both arrays to find the element
                    const elementFromMain = images.find(
                      (img) => img.id === initialPos.id,
                    );
                    const elementFromOff = offCanvasImages.find(
                      (img) => img.id === initialPos.id,
                    );
                    element = elementFromMain || elementFromOff;

                    // CRITICAL FIX: Ensure isOnTopLayer is set on the element if it's a top-layer image
                    if (element && !element.isOnTopLayer) {
                      element = { ...element, isOnTopLayer: true };
                    }
                    // CRITICAL FIX: For top-layer images, always use topLayerStage if available
                    // If topLayerStage is not available, fall back to mainStage
                    // This ensures top-layer images from both main and off-canvas use the correct stage
                    sourceStageRef = topLayerStage || mainStage;
                  } else {
                    element = isOnMainCanvas
                      ? images.find((img) => img.id === initialPos.id)
                      : offCanvasImages.find((img) => img.id === initialPos.id);
                  }
                  break;
                }
                case "Text":
                  element = isOnMainCanvas
                    ? texts.find((t) => t.id === initialPos.id)
                    : offCanvasTexts.find((t) => t.id === initialPos.id);
                  break;
                case "Arrow":
                  // CRITICAL FIX: Unified arrows are stored in lines/offCanvasLines.
                  // During cross-canvas previews, flags can be missing; prefer ID lookup in lines arrays.
                  element = isOnMainCanvas
                    ? lines.find((l) => l.id === initialPos.id) ||
                      arrows.find((a) => a.id === initialPos.id)
                    : offCanvasLines.find((l) => l.id === initialPos.id) ||
                      offCanvasArrows.find((a) => a.id === initialPos.id);
                  break;
                case "DoubleSidedArrow":
                  // CRITICAL FIX: Unified double-sided arrows are stored in lines/offCanvasLines.
                  // Prefer ID lookup in lines arrays; fall back to legacy arrays.
                  element = isOnMainCanvas
                    ? lines.find((l) => l.id === initialPos.id) ||
                      doubleSidedArrows.find((a) => a.id === initialPos.id)
                    : offCanvasLines.find((l) => l.id === initialPos.id) ||
                      offCanvasDoubleSidedArrows.find(
                        (a) => a.id === initialPos.id,
                      );
                  break;
                case "Line":
                  // CRITICAL FIX: Unified strokes (including arrows) can be labeled as "Line" in selection.
                  // For previews, fetch by ID from lines arrays regardless of arrow flags.
                  element = isOnMainCanvas
                    ? lines.find((l) => l.id === initialPos.id)
                    : offCanvasLines.find((l) => l.id === initialPos.id);
                  break;
              }

              // DEBUG: Log if element was not found
              if (
                !element &&
                (initialPos.type === "Line" ||
                  initialPos.type === "Arrow" ||
                  initialPos.type === "DoubleSidedArrow")
              ) {
              }

              // DEBUG: Log if element was found
              if (
                element &&
                (initialPos.type === "Line" ||
                  initialPos.type === "Arrow" ||
                  initialPos.type === "DoubleSidedArrow")
              ) {
              }

              // CRITICAL FIX: For top-layer images, ensure we have a valid sourceStageRef
              // Even if topLayerStage is null, we should still create the preview using mainStage
              if (element) {
                // If sourceStageRef is not set but we have an element, set a default
                if (!sourceStageRef) {
                  const isTopLayerImage =
                    initialPos.type === "Image" &&
                    (images.some(
                      (img) =>
                        img.id === initialPos.id && img.isOnTopLayer === true,
                    ) ||
                      offCanvasImages.some(
                        (img) =>
                          img.id === initialPos.id && img.isOnTopLayer === true,
                      ));
                  if (isTopLayerImage) {
                    sourceStageRef = topLayerStage || mainStage;
                  } else {
                    sourceStageRef = isOnMainCanvas ? mainStage : offStage;
                  }
                }

                if (!sourceStageRef) {
                  return; // Skip this element if we can't determine sourceStageRef
                }

                // Calculate the new position for this element
                let calculatedNewX, calculatedNewY;

                if (initialPos.type === "Text" || initialPos.type === "Rect") {
                  calculatedNewX = initialPos.x + deltaX;
                  calculatedNewY = initialPos.y + deltaY;
                } else if (
                  initialPos.type === "Image" ||
                  initialPos.type === "Circle"
                ) {
                  calculatedNewX = initialPos.x + deltaX;
                  calculatedNewY = initialPos.y + deltaY;
                } else if (
                  initialPos.type === "Line" ||
                  initialPos.type === "Arrow" ||
                  initialPos.type === "DoubleSidedArrow"
                ) {
                  calculatedNewX = initialPos.x + deltaX;
                  calculatedNewY = initialPos.y + deltaY;
                } else {
                  calculatedNewX = initialPos.x + deltaX;
                  calculatedNewY = initialPos.y + deltaY;
                }

                // Calculate target position using the same logic as handleDragPreview
                const sourceBBox = sourceStageRef
                  ?.container()
                  ?.getBoundingClientRect?.();

                // CRITICAL FIX: For top-layer images, if sourceBBox is not available, try to get it from topLayerStage
                // This ensures we can still create previews even if the initial sourceStageRef doesn't have a container
                let effectiveSourceBBox = sourceBBox;
                if (!effectiveSourceBBox && initialPos.type === "Image") {
                  const isTopLayerImage =
                    images.some(
                      (img) =>
                        img.id === initialPos.id && img.isOnTopLayer === true,
                    ) ||
                    offCanvasImages.some(
                      (img) =>
                        img.id === initialPos.id && img.isOnTopLayer === true,
                    );
                  if (isTopLayerImage && topLayerStage) {
                    effectiveSourceBBox = topLayerStage
                      .container()
                      ?.getBoundingClientRect?.();
                  }
                }

                if (effectiveSourceBBox) {
                  // CRITICAL FIX: Check both main and off-canvas arrays for top-layer images
                  const isTopLayerImage =
                    initialPos.type === "Image" &&
                    (images.some(
                      (img) =>
                        img.id === initialPos.id && img.isOnTopLayer === true,
                    ) ||
                      offCanvasImages.some(
                        (img) =>
                          img.id === initialPos.id && img.isOnTopLayer === true,
                      ));
                  const isTopLayerStage =
                    isTopLayerImage && sourceStageRef === topLayerStage;
                  const sourceTarget = isTopLayerStage
                    ? "main"
                    : sourceStageRef === mainStage
                      ? "main"
                      : sourceStageRef === offStage
                        ? "offCanvas"
                        : "main";

                  let targetX, targetY;
                  if (isTopLayerImage) {
                    // CRITICAL FIX: For top-layer images, convert coordinates based on source and target canvas
                    // Check if the image is from off-canvas (in offCanvasImages) or main canvas (in images)
                    const isFromOffCanvas = offCanvasImages.some(
                      (img) =>
                        img.id === initialPos.id && img.isOnTopLayer === true,
                    );

                    if (targetCanvas === "offCanvas") {
                      // Top-layer image being dragged to off-canvas - convert coordinates
                      // If from main canvas, convert from topLayerStage/mainStage to offCanvasStage
                      // If from off-canvas, coordinates are already relative to offStage
                      if (!isFromOffCanvas) {
                        // From main canvas to off-canvas - convert coordinates
                        const absX = effectiveSourceBBox.left + calculatedNewX;
                        const absY = effectiveSourceBBox.top + calculatedNewY;
                        targetX = absX - offBBox.left;
                        targetY = absY - offBBox.top;
                      } else {
                        // From off-canvas to off-canvas (staying in off-canvas) - use position directly
                        targetX = calculatedNewX;
                        targetY = calculatedNewY;
                      }
                    } else {
                      // Top-layer image being dragged to main canvas
                      if (isFromOffCanvas) {
                        // From off-canvas to main canvas - convert coordinates from offStage to mainStage
                        // CRITICAL: calculatedNewX/Y are in OFF-STAGE coordinates (image lives on offStage).
                        // Use offBBox as source, NOT topLayerStage/mainStage. topLayerStage overlays main only.
                        const absX = offBBox.left + calculatedNewX;
                        const absY = offBBox.top + calculatedNewY;
                        targetX = absX - mainBBox.left;
                        targetY = absY - mainBBox.top;
                      } else {
                        // From main canvas to main canvas - use the new position directly
                        // These coordinates are already relative to the topLayerStage/mainStage
                        targetX = calculatedNewX;
                        targetY = calculatedNewY;
                      }
                    }
                  } else if (targetCanvas === sourceTarget) {
                    targetX = calculatedNewX;
                    targetY = calculatedNewY;
                  } else {
                    // Cross-canvas drag - calculate target position based on bounding boxes
                    const absX = effectiveSourceBBox.left + calculatedNewX;
                    const absY = effectiveSourceBBox.top + calculatedNewY;
                    const targetBBox =
                      targetCanvas === "main" ? mainBBox : offBBox;
                    targetX = absX - targetBBox.left;
                    targetY = absY - targetBBox.top;
                  }

                  // CRITICAL: Apply boundary constraints to ALL preview positions
                  // This prevents previews from going off-screen when dragging between canvases
                  const targetStage =
                    targetCanvas === "main" ? mainStage : offStage;
                  if (targetStage) {
                    const stageWidth =
                      targetCanvas === "main"
                        ? mainStage?.width() || mainCanvasWidth
                        : offStage?.width() || window.innerWidth;
                    const stageHeight =
                      targetStage.height() || fixedStageHeightPx;

                    // Get element from ANY array to calculate bounding box for constraints
                    const element =
                      rectangles.find((r) => r.id === initialPos.id) ||
                      circles.find((c) => c.id === initialPos.id) ||
                      images.find((img) => img.id === initialPos.id) ||
                      texts.find((t) => t.id === initialPos.id) ||
                      lines.find((l) => l.id === initialPos.id) ||
                      arrows.find((a) => a.id === initialPos.id) ||
                      doubleSidedArrows.find((a) => a.id === initialPos.id) ||
                      offCanvasRectangles.find((r) => r.id === initialPos.id) ||
                      offCanvasCircles.find((c) => c.id === initialPos.id) ||
                      offCanvasImages.find((img) => img.id === initialPos.id) ||
                      offCanvasTexts.find((t) => t.id === initialPos.id) ||
                      offCanvasLines.find((l) => l.id === initialPos.id) ||
                      offCanvasArrows.find((a) => a.id === initialPos.id) ||
                      offCanvasDoubleSidedArrows.find(
                        (a) => a.id === initialPos.id,
                      );

                    if (element) {
                      // Apply constraints based on element type
                      if (
                        initialPos.type === "Image" ||
                        initialPos.type === "Circle"
                      ) {
                        // Center coordinate elements
                        const width =
                          element.width ||
                          (element.radius ? element.radius * 2 : 0);
                        const height =
                          element.height ||
                          (element.radius ? element.radius * 2 : 0);
                        const halfWidth = width / 2;
                        const halfHeight = height / 2;

                        targetX = Math.max(
                          TOOLBAR_WIDTH + halfWidth,
                          Math.min(stageWidth - halfWidth, targetX),
                        );
                        targetY = Math.max(
                          halfHeight,
                          Math.min(stageHeight - halfHeight, targetY),
                        );
                      } else if (
                        initialPos.type === "Rect" ||
                        initialPos.type === "Text"
                      ) {
                        // Top-left coordinate elements
                        const width = element.width || 0;
                        const height = element.height || 0;

                        targetX = Math.max(
                          TOOLBAR_WIDTH,
                          Math.min(stageWidth - width, targetX),
                        );
                        targetY = Math.max(
                          0,
                          Math.min(stageHeight - height, targetY),
                        );
                      } else {
                        // For lines/arrows and other elements, apply basic constraints
                        targetX = Math.max(
                          TOOLBAR_WIDTH,
                          Math.min(stageWidth, targetX),
                        );
                        targetY = Math.max(0, Math.min(stageHeight, targetY));
                      }
                    }
                  }

                  // Create preview data
                  // CRITICAL FIX: Explicitly preserve isOnTopLayer for top-layer images
                  // Check both arrays to ensure we get the correct value, especially for off-canvas top-layer images
                  // CRITICAL: Always check the arrays first - this is the source of truth
                  // Don't rely on element.isOnTopLayer which might be stale or incorrect
                  const previewIsOnTopLayer =
                    initialPos.type === "Image" &&
                    (images.some(
                      (img) =>
                        img.id === initialPos.id && img.isOnTopLayer === true,
                    ) ||
                      offCanvasImages.some(
                        (img) =>
                          img.id === initialPos.id && img.isOnTopLayer === true,
                      ));

                  // CRITICAL FIX: Use previewIsOnTopLayer as the primary source of truth
                  // Only fall back to element.isOnTopLayer if previewIsOnTopLayer check failed (shouldn't happen)
                  // This ensures we always get the correct value from the state arrays
                  const finalIsOnTopLayer =
                    previewIsOnTopLayer === true
                      ? true
                      : element?.isOnTopLayer === true
                        ? true
                        : false;

                  // CRITICAL FIX: Ensure all required image properties are preserved in preview data
                  // ImageElement requires: image (HTMLImageElement), src, width, height, x, y, etc.
                  // For images, we MUST have the image property (HTMLImageElement) for rendering
                  if (initialPos.type === "Image" && !element?.image) {
                    // Skip creating preview if image property is missing - continue to next element
                    return; // This returns from the forEach callback, skipping this element
                  }

                  const shouldPrioritizeTextBackgroundPreview =
                    initialPos.type === "Text" &&
                    element?.showBackground === true;

                  const previewData = {
                    ...element,
                    id: `${element.id}-ghost`,
                    x: targetX,
                    y: targetY,
                    isOnTopLayer: finalIsOnTopLayer,
                    // Keep text background previews above normal stage elements during cross-stage group drag.
                    // Top-layer images are still rendered in a separate higher-z stage.
                    zIndex: shouldPrioritizeTextBackgroundPreview
                      ? 99998
                      : element?.zIndex,
                    points:
                      element.points ||
                      (initialPos.type === "Line" ||
                      initialPos.type === "Arrow" ||
                      initialPos.type === "DoubleSidedArrow"
                        ? [0, 0, 0, 0]
                        : undefined),
                    // CRITICAL: Ensure image property is preserved (required for ImageElement to render)
                    image: element?.image,
                    src: element?.src,
                    originalImage: element?.originalImage,
                    originalImageDataUrl: element?.originalImageDataUrl,
                  };

                  previews.push({
                    type: initialPos.type,
                    target: targetCanvas,
                    data: previewData,
                  });

                  // DEBUG: Log preview creation for lines/arrows
                  if (
                    initialPos.type === "Line" ||
                    initialPos.type === "Arrow" ||
                    initialPos.type === "DoubleSidedArrow"
                  ) {
                  }
                }
              }
            }
          },
        );

        // CRITICAL FIX: Only update React state if previews meaningfully changed
        // Check previous value BEFORE updating ref to avoid unnecessary re-renders
        const prevPreviews = groupDragPreviewsRef.current || [];

        // DEBUG: Log final preview state
        
        const previewsChanged =
          prevPreviews.length !== previews.length ||
          (previews.length > 0 &&
            prevPreviews.some((prev, idx) => {
              const curr = previews[idx];
              if (!curr) return true;
              // Only check critical properties that affect rendering (use threshold to avoid micro-updates)
              return (
                prev.target !== curr.target ||
                prev.data?.id !== curr.data?.id ||
                Math.abs((prev.data?.x || 0) - (curr.data?.x || 0)) > 5 ||
                Math.abs((prev.data?.y || 0) - (curr.data?.y || 0)) > 5
              );
            }));

        // Update ref first
        groupDragPreviewsRef.current = previews;

        // Only update React state if previews meaningfully changed (avoid micro-updates every frame)
        if (previewsChanged) {
          setGroupDragPreviews(previews);
        }

        // Clear single drag preview when group drag is active
        if (previews.length > 0 && dragPreviewStateRef.current) {
          clearDragPreview();
        }
      } else {
        // Mouse not over any canvas, clear all previews
        groupDragPreviewsRef.current = [];
        setGroupDragPreviews([]);
        if (dragPreviewStateRef.current) {
          clearDragPreview();
        }
      }

      // Batch draw all stages at once for smooth animation (like wrapper rectangle)
      if (mainStage) {
        mainStage.getLayer()?.batchDraw();
      }
      if (offStage) {
        offStage.getLayer()?.batchDraw();
      }
      if (topLayerStage) {
        topLayerStage.getLayer()?.batchDraw();
      }
    },
    [
      isCloneMode,
      lockedAction,
      isMultiSelectMode,
      selectedObjects,
      setRectangles,
      setOffCanvasRectangles,
      setCircles,
      setOffCanvasCircles,
      setImages,
      setOffCanvasImages,
      updateImageProperties,
      setTexts,
      updateLineProperties,
      updateArrowProperties,
      updateDoubleSidedArrowProperties,
      setSelectedObjects,
      setSelectedObject,
      selectedObject,
      rectangles,
      offCanvasRectangles,
      circles,
      offCanvasCircles,
      images,
      offCanvasImages,
      stageRef,
      offCanvasStageRef,
      topLayerStageRef,
      handleDragPreview,
      clearDragPreview,
      dragPreviewStateRef,
      texts,
      arrows,
      doubleSidedArrows,
      lines,
      offCanvasTexts,
      offCanvasArrows,
      offCanvasDoubleSidedArrows,
      offCanvasLines,
      setDragUpdateTrigger,
    ],
  );

  // Sync Konva node positions back to React state arrays after group drag
  // This prevents elements from jumping back to original positions when unselected
  const syncGroupDragPositionsToState = useCallback(() => {
    const mainStage = stageRef?.current;
    const offStage = offCanvasStageRef?.current;
    const topLayerStage = topLayerStageRef?.current;
    const offCanvasTopLayerStage = offCanvasTopLayerStageRef?.current;

    if (!mainStage && !offStage) {
      return;
    }
    const lastGroupDelta = groupDragLastDeltaRef.current || { x: 0, y: 0 };

    // PERFORMANCE: Cache stage dimensions once instead of reading multiple times
    const offStageWidth = offStage
      ? offStage.width() || offCanvasStageWidth
      : offCanvasStageWidth;
    const offStageHeight = offStage
      ? offStage.height() || fixedStageHeightPx
      : fixedStageHeightPx;
    const clampNodeByRenderedClientRect = ({
      stage,
      node,
      candidateX,
      candidateY,
      avoidOffCanvasMainColumn = false,
      forceLeftOfOffCanvasMainColumn = false,
      edgeInsetPx = 0,
    }) => {
      if (!stage || !node) {
        return { x: candidateX, y: candidateY };
      }

      const stageWidth = stage.width() || window.innerWidth * 0.45;
      const stageHeight = stage.height() || window.innerHeight;
      const inset = Math.max(0, Number(edgeInsetPx) || 0);
      const { mainZoneStart, mainZoneEnd } = avoidOffCanvasMainColumn
        ? getOffCanvasMainColumnBounds(stageWidth)
        : { mainZoneStart: 0, mainZoneEnd: 0 };

      const getEffectiveInset = (requestedInset, stageSize, renderedSize) => {
        const safeInset = Math.max(0, Number(requestedInset) || 0);
        const safeStage = Math.max(0, Number(stageSize) || 0);
        const safeRendered = Math.max(0, Number(renderedSize) || 0);
        return Math.max(0, Math.min(safeInset, (safeStage - safeRendered) / 2));
      };

      const prevX = node.x();
      const prevY = node.y();
      try {
        node.x(candidateX);
        node.y(candidateY);

        for (let pass = 0; pass < 8; pass += 1) {
          const rect = node.getClientRect();
          const insetX = getEffectiveInset(inset, stageWidth, rect.width);
          const insetY = getEffectiveInset(inset, stageHeight, rect.height);
          let dx = 0;
          let dy = 0;

          if (rect.x < insetX) dx = insetX - rect.x;
          else if (rect.x + rect.width > stageWidth - insetX) {
            dx = stageWidth - insetX - (rect.x + rect.width);
          }

          if (rect.y < insetY) dy = insetY - rect.y;
          else if (rect.y + rect.height > stageHeight - insetY) {
            dy = stageHeight - insetY - (rect.y + rect.height);
          }

          if (avoidOffCanvasMainColumn) {
            const overlapsMainColumn =
              rect.x < mainZoneEnd && rect.x + rect.width > mainZoneStart;
            if (overlapsMainColumn) {
              if (forceLeftOfOffCanvasMainColumn) {
                // Off-canvas lives on the left in this layout.
                // Always push overlap back to the left lane.
                dx += mainZoneStart - (rect.x + rect.width);
              } else {
                const centerX = rect.x + rect.width / 2;
                const mid = (mainZoneStart + mainZoneEnd) / 2;
                if (centerX < mid) {
                  dx += mainZoneStart - (rect.x + rect.width);
                } else {
                  dx += mainZoneEnd - rect.x;
                }
              }
            }
          }

          if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
            break;
          }

          node.x((Number(node.x()) || 0) + dx);
          node.y((Number(node.y()) || 0) + dy);
        }

        const nextX = Math.round(Number(node.x()) || 0);
        const nextY = Math.round(Number(node.y()) || 0);
        node.x(nextX);
        node.y(nextY);
        node.getLayer?.()?.batchDraw?.();
        return { x: nextX, y: nextY };
      } catch (_e) {
        node.x(prevX);
        node.y(prevY);
        return { x: candidateX, y: candidateY };
      }
    };
    const GROUP_DRAG_END_SAFETY_INSET_PX = Math.max(
      Number(TRANSFORMER_ANCHOR_BUFFER_PX) || 0,
      10,
    );
    const pickClosestToTarget = (a, b, target) => {
      const aFinite = Number.isFinite(a);
      const bFinite = Number.isFinite(b);
      const safeTarget = Number.isFinite(target) ? Number(target) : 0;
      if (aFinite && bFinite) {
        return Math.abs(a - safeTarget) <= Math.abs(b - safeTarget) ? a : b;
      }
      if (aFinite) return a;
      if (bFinite) return b;
      return safeTarget;
    };

    const getElementForSync = (id, type) => {
      switch (type) {
        case "Rect":
          return (
            rectangles.find((r) => r.id === id) ||
            offCanvasRectangles.find((r) => r.id === id)
          );
        case "Circle":
          return (
            circles.find((c) => c.id === id) ||
            offCanvasCircles.find((c) => c.id === id)
          );
        case "Image":
          return (
            images.find((img) => img.id === id) ||
            offCanvasImages.find((img) => img.id === id)
          );
        case "Text":
          return (
            texts.find((t) => t.id === id) || offCanvasTexts.find((t) => t.id === id)
          );
        case "Line":
          return (
            lines.find((l) => l.id === id) || offCanvasLines.find((l) => l.id === id)
          );
        case "Arrow":
          return (
            lines.find((l) => {
              if (l.id !== id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            }) ||
            offCanvasLines.find((l) => {
              if (l.id !== id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            }) ||
            arrows.find((a) => a.id === id) ||
            offCanvasArrows.find((a) => a.id === id)
          );
        case "DoubleSidedArrow":
          return (
            lines.find((l) => {
              if (l.id !== id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow && hasEndArrow;
            }) ||
            offCanvasLines.find((l) => {
              if (l.id !== id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow && hasEndArrow;
            }) ||
            doubleSidedArrows.find((a) => a.id === id) ||
            offCanvasDoubleSidedArrows.find((a) => a.id === id)
          );
        default:
          return null;
      }
    };

    const computeGroupStageShift = ({
      entries,
      stage,
      minStageX = 0,
      avoidOffCanvasMainColumn = false,
      forceLeftOfOffCanvasMainColumn = false,
      edgeInsetPx = 0,
    }) => {
      if (!stage || !entries?.length) {
        return { x: 0, y: 0 };
      }

      const stageWidth = stage.width() || window.innerWidth;
      const stageHeight = stage.height() || fixedStageHeightPx;
      const inset = Math.max(0, Number(edgeInsetPx) || 0);
      const minXBound = Math.max(0, Number(minStageX) || 0) + inset;
      const maxXBound = Math.max(minXBound, stageWidth - inset);
      const minYBound = inset;
      const maxYBound = Math.max(minYBound, stageHeight - inset);

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      entries.forEach(({ elementNode, fallbackBBox }) => {
        let rect = null;
        if (elementNode && typeof elementNode.getClientRect === "function") {
          rect = elementNode.getClientRect();
        } else if (fallbackBBox) {
          rect = fallbackBBox;
        }
        if (!rect) return;
        minX = Math.min(minX, rect.x);
        minY = Math.min(minY, rect.y);
        maxX = Math.max(maxX, rect.x + rect.width);
        maxY = Math.max(maxY, rect.y + rect.height);
      });

      if (
        !Number.isFinite(minX) ||
        !Number.isFinite(minY) ||
        !Number.isFinite(maxX) ||
        !Number.isFinite(maxY)
      ) {
        return { x: 0, y: 0 };
      }

      let dx = 0;
      let dy = 0;

      if (minX < minXBound) dx = minXBound - minX;
      else if (maxX > maxXBound) dx = maxXBound - maxX;

      if (minY < minYBound) dy = minYBound - minY;
      else if (maxY > maxYBound) dy = maxYBound - maxY;

      if (avoidOffCanvasMainColumn) {
        const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
        const paddedStart = mainZoneStart - inset;
        const paddedEnd = mainZoneEnd + inset;
        const shiftedMinX = minX + dx;
        const shiftedMaxX = maxX + dx;
        const overlapsMainColumn =
          shiftedMinX < paddedEnd && shiftedMaxX > paddedStart;
        if (overlapsMainColumn) {
          if (forceLeftOfOffCanvasMainColumn) {
            dx += paddedStart - shiftedMaxX;
          } else {
            const centerX = (shiftedMinX + shiftedMaxX) / 2;
            const mid = (paddedStart + paddedEnd) / 2;
            if (centerX < mid) dx += paddedStart - shiftedMaxX;
            else dx += paddedEnd - shiftedMinX;
          }
        }
      }

      const shiftedMinX = minX + dx;
      const shiftedMaxX = maxX + dx;
      const shiftedMinY = minY + dy;
      const shiftedMaxY = maxY + dy;
      if (shiftedMinX < minXBound) dx += minXBound - shiftedMinX;
      else if (shiftedMaxX > maxXBound) dx += maxXBound - shiftedMaxX;
      if (shiftedMinY < minYBound) dy += minYBound - shiftedMinY;
      else if (shiftedMaxY > maxYBound) dy += maxYBound - shiftedMaxY;

      return { x: dx, y: dy };
    };

    const dragEntries =
      individualElementDragStartRef.current?.elementPositions
        ?.map((initialPos) => {
          const startedOnMainCanvas =
            initialPos?.isOnMainCanvas === true
              ? true
              : initialPos?.isOnMainCanvas === false
                ? false
                : null;
          const isTopLayerImage =
            initialPos.type === "Image" &&
            (images.some(
              (img) => img.id === initialPos.id && img.isOnTopLayer === true,
            ) ||
              offCanvasImages.some(
                (img) => img.id === initialPos.id && img.isOnTopLayer === true,
              ));

          let elementNode = null;
          if (isTopLayerImage && topLayerStage) {
            elementNode = topLayerStage.findOne(`#${initialPos.id}`);
          }
          if (!elementNode && isTopLayerImage && offCanvasTopLayerStage) {
            elementNode = offCanvasTopLayerStage.findOne(`#${initialPos.id}`);
          }
          if (!elementNode) {
            elementNode =
              mainStage?.findOne(`#${initialPos.id}`) ||
              offStage?.findOne(`#${initialPos.id}`);
          }

          const fallbackElement = getElementForSync(initialPos.id, initialPos.type);
          let currentX = Number(elementNode?.x?.());
          let currentY = Number(elementNode?.y?.());

          if (!Number.isFinite(currentX)) {
            currentX = Number(fallbackElement?.x);
          }
          if (!Number.isFinite(currentY)) {
            currentY = Number(fallbackElement?.y);
          }
          if (!Number.isFinite(currentX)) currentX = Number(initialPos.x) || 0;
          if (!Number.isFinite(currentY)) currentY = Number(initialPos.y) || 0;
          // Group drag-end can read node coords from a transient stage/frame (wrapper/top-layer),
          // producing large synthetic deltas. Prefer the drag-move cached delta for both canvases.
          const initialX = Number(initialPos.x || 0);
          const initialY = Number(initialPos.y || 0);
          const nodeDeltaX = currentX - initialX;
          const nodeDeltaY = currentY - initialY;
          const cachedDeltaX = Number(lastGroupDelta.x || 0);
          const cachedDeltaY = Number(lastGroupDelta.y || 0);
          const canUseCached =
            Number.isFinite(cachedDeltaX) && Number.isFinite(cachedDeltaY);
          if (canUseCached) {
            currentX = initialX + cachedDeltaX;
            currentY = initialY + cachedDeltaY;
          }
          console.log("[GROUP-DRAG-END][OFFCANVAS][SYNC-DELTA]", {
            id: initialPos.id,
            type: initialPos.type,
            startedOnMainCanvas,
            nodeDeltaX: Math.round(nodeDeltaX),
            nodeDeltaY: Math.round(nodeDeltaY),
            cachedDeltaX: Math.round(cachedDeltaX),
            cachedDeltaY: Math.round(cachedDeltaY),
            usedCached: canUseCached,
            resultingX: Math.round(currentX),
            resultingY: Math.round(currentY),
          });

          if (!elementNode && isTopLayerImage) {
            console.log("[GROUP-DRAG-END][TOPLAYER][NODE-MISS]", {
              id: initialPos.id,
              isInOffCanvas: startedOnMainCanvas === false,
              hasMainTopLayerStage: !!topLayerStage,
              hasOffCanvasTopLayerStage: !!offCanvasTopLayerStage,
            });
          }
          if (isTopLayerImage) {
            console.log("[GROUP-DRAG-END][TOPLAYER][NODE-RESOLVE]", {
              id: initialPos.id,
              foundNode: !!elementNode,
              nodeStageId: elementNode?.getStage?.()?.attrs?.id || null,
              currentX,
              currentY,
            });
          }

          const elementStage = elementNode?.getStage?.();
          const isOnOffCanvasStage = elementStage === offStage;
          const isOnTopLayerStage =
            elementStage === topLayerStage ||
            elementStage === offCanvasTopLayerStage;
          const isInOffCanvasArray =
            offCanvasRectangles.some((r) => r.id === initialPos.id) ||
            offCanvasCircles.some((c) => c.id === initialPos.id) ||
            offCanvasImages.some((img) => img.id === initialPos.id) ||
            offCanvasTexts.some((t) => t.id === initialPos.id) ||
            offCanvasLines.some((l) => l.id === initialPos.id) ||
            offCanvasArrows.some((a) => a.id === initialPos.id) ||
            offCanvasDoubleSidedArrows.some((a) => a.id === initialPos.id);
          // CRITICAL FIX: For group drag-end sync/clamp, prefer the drag-start canvas.
          // When the pointer exits on the far right, node stage detection can be transiently main,
          // which skips off-canvas clamp and leaves elements stuck near the right edge.
          const isInOffCanvas =
            startedOnMainCanvas === false
              ? true
              : startedOnMainCanvas === true
                ? false
                : ((isOnOffCanvasStage && !isOnTopLayerStage) ||
                    (isInOffCanvasArray && !isOnTopLayerStage));

          const fallbackBBox = fallbackElement
            ? getElementBoundingBox(
                { ...fallbackElement, x: currentX, y: currentY },
                initialPos.type,
              )
            : null;

          return {
            initialPos,
            isTopLayerImage,
            elementNode,
            currentX,
            currentY,
            isInOffCanvas,
            fallbackBBox,
          };
        })
        .filter(Boolean) || [];

    const mainEntries = dragEntries.filter((entry) => !entry.isInOffCanvas);
    const offEntries = dragEntries.filter((entry) => entry.isInOffCanvas);
    const mainGroupShift = computeGroupStageShift({
      entries: mainEntries,
      stage: mainStage,
      minStageX: 0,
      edgeInsetPx: TRANSFORMER_ANCHOR_BUFFER_PX,
    });
    let offGroupShift = computeGroupStageShift({
      entries: offEntries,
      stage: offStage,
      minStageX: 0,
      avoidOffCanvasMainColumn: true,
      forceLeftOfOffCanvasMainColumn: true,
      // Keep only transformer-anchor safety inset (10px), without toolbar offset.
      edgeInsetPx: TRANSFORMER_ANCHOR_BUFFER_PX,
    });
    const hasOffCanvasGroupSelection = offEntries.length > 1;
    if (hasOffCanvasGroupSelection) {
      // Avoid double-clamping for off-canvas group drag-end.
      // Shared clamp below already computes one rigid correction for the whole group.
      // Keeping a pre-shift here can create inconsistent axis behavior and apparent snap-back.
      offGroupShift = { x: 0, y: 0 };
    }
    let offSharedClampDeltaX = 0;
    let offSharedClampDeltaY = 0;
    if (hasOffCanvasGroupSelection && offStage) {
      const stageWidth = Number(offStage.width?.()) || offStageWidth;
      const stageHeight = Number(offStage.height?.()) || offStageHeight;
      const inset = Math.max(0, Number(TRANSFORMER_ANCHOR_BUFFER_PX) || 0);
      const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
      // Left edge: stage-left plus transformer-anchor inset only.
      const laneMinX = inset;
      const laneRight = Math.max(laneMinX, Number(mainZoneStart || stageWidth) - inset);
      const laneWidth = Math.max(0, laneRight - laneMinX);
      const sharedClampDebugRows = [];
      let allowedMinDx = Number.NEGATIVE_INFINITY;
      let allowedMaxDx = Number.POSITIVE_INFINITY;
      let allowedMinDy = Number.NEGATIVE_INFINITY;
      let allowedMaxDy = Number.POSITIVE_INFINITY;
      let rectCount = 0;
      offEntries.forEach((entry) => {
        const node = entry?.elementNode;
        const shiftedX = Math.round((entry.currentX || 0) + offGroupShift.x);
        const shiftedY = Math.round((entry.currentY || 0) + offGroupShift.y);
        const prevX = Number(node?.x?.()) || 0;
        const prevY = Number(node?.y?.()) || 0;
        try {
          let rect = null;
          if (node) {
            node.x(shiftedX);
            node.y(shiftedY);
            rect = node.getClientRect?.();
          } else if (entry?.fallbackBBox) {
            // Include node-miss entries in shared clamp so group bounds stay coherent.
            // fallbackBBox is at current coords; shift it by the same delta used for candidate.
            const dx = shiftedX - (entry.currentX || 0);
            const dy = shiftedY - (entry.currentY || 0);
            rect = {
              x: (Number(entry.fallbackBBox.x) || 0) + dx,
              y: (Number(entry.fallbackBBox.y) || 0) + dy,
              width: Math.max(0, Number(entry.fallbackBBox.width) || 0),
              height: Math.max(0, Number(entry.fallbackBBox.height) || 0),
            };
          }
          if (rect && Number.isFinite(rect.x) && Number.isFinite(rect.y)) {
            const rectWidth = Math.max(0, Number(rect.width) || 0);
            const rectHeight = Math.max(0, Number(rect.height) || 0);
            const rectRight = rect.x + rectWidth;
            const rectBottom = rect.y + rectHeight;
            const isOversizedForLane = rectWidth > laneWidth;

            // Keep right edge inside off-canvas lane for all entries.
            const maxDxForRight = laneRight - rectRight;
            allowedMaxDx = Math.min(allowedMaxDx, maxDxForRight);

            // For top-left anchored nodes (Rect/Text), clamp by anchor x so shared
            // left-bound correction does not over-push to the right due to clientRect offsets.
            const leftClampRefX =
              entry?.initialPos?.type === "Rect" ||
              entry?.initialPos?.type === "Text"
                ? shiftedX
                : rect.x;
            // Always track left-edge requirement, including oversized entries.
            const minDxForLeft = laneMinX - leftClampRefX;
            allowedMinDx = Math.max(allowedMinDx, minDxForLeft);

            // Keep vertical bounds inside stage with same rendered geometry references
            // used for all shared-group members.
            const topClampRefY = rect.y;
            const bottomClampRefY = rectBottom;
            const minDyForTop = inset - topClampRefY;
            const maxDyForBottom = stageHeight - inset - bottomClampRefY;
            allowedMinDy = Math.max(allowedMinDy, minDyForTop);
            allowedMaxDy = Math.min(allowedMaxDy, maxDyForBottom);
            sharedClampDebugRows.push({
              id: entry?.initialPos?.id,
              type: entry?.initialPos?.type,
              usedNode: !!node,
              shiftedX,
              shiftedY,
              rectX: Math.round(rect.x),
              rectY: Math.round(rect.y),
              rectWidth: Math.round(rectWidth),
              rectHeight: Math.round(rectHeight),
              isOversizedForLane,
              leftClampRef:
                entry?.initialPos?.type === "Rect" ||
                entry?.initialPos?.type === "Text"
                  ? "node-x"
                  : "rect-x",
              leftClampRefX: Math.round(leftClampRefX),
              topClampRef: "rect-y",
              topClampRefY: Math.round(topClampRefY),
              bottomClampRefY: Math.round(bottomClampRefY),
              minDxForLeft: Math.round(minDxForLeft),
              maxDxForRight: Math.round(maxDxForRight),
              minDyForTop: Math.round(minDyForTop),
              maxDyForBottom: Math.round(maxDyForBottom),
            });
            rectCount += 1;
          }
        } catch (_e) {
          // ignore and fall back below
        } finally {
          if (node) {
            node.x(prevX);
            node.y(prevY);
          }
        }
      });
      if (rectCount > 0) {
        if (allowedMinDx > allowedMaxDx) {
          // No perfect intersection on X.
          // For mostly-vertical drags, avoid injecting horizontal correction.
          const intendedDx = Number(lastGroupDelta.x) || 0;
          const intendedDy = Number(lastGroupDelta.y) || 0;
          const isMostlyVerticalIntent =
            Math.abs(intendedDx) <= 1 && Math.abs(intendedDy) > 1;
          if (isMostlyVerticalIntent) {
            offSharedClampDeltaX = 0;
          } else if (intendedDx > 0) {
            // Dragging right: prioritize keeping right seam inside off-canvas lane.
            offSharedClampDeltaX = Math.round(allowedMaxDx);
          } else if (intendedDx < 0) {
            // Dragging left: prioritize keeping left seam inside off-canvas lane.
            offSharedClampDeltaX = Math.round(allowedMinDx);
          } else {
            offSharedClampDeltaX = Math.round(
              pickClosestToTarget(allowedMinDx, allowedMaxDx, intendedDx),
            );
          }
        } else {
          offSharedClampDeltaX = Math.round(
            Math.max(allowedMinDx, Math.min(0, allowedMaxDx)),
          );
        }
        if (allowedMinDy > allowedMaxDy) {
          offSharedClampDeltaY = Math.round(allowedMaxDy);
        } else {
          offSharedClampDeltaY = Math.round(
            Math.max(allowedMinDy, Math.min(0, allowedMaxDy)),
          );
        }
        // Final rigid-group Y safety pass:
        // normalize shared Y delta against aggregate top/bottom after applying it.
        // This prevents residual top/bottom overflow when text metrics differ from
        // rendered rect references during shared clamp calculations.
        if (sharedClampDebugRows.length > 0) {
          const topAfter = Math.min(
            ...sharedClampDebugRows.map(
              (row) =>
                (Number(row.topClampRefY) || Number(row.rectY) || 0) +
                offSharedClampDeltaY,
            ),
          );
          const bottomAfter = Math.max(
            ...sharedClampDebugRows.map(
              (row) =>
                (Number(row.bottomClampRefY) ||
                  (Number(row.rectY) || 0) + (Number(row.rectHeight) || 0)) +
                offSharedClampDeltaY,
            ),
          );
          const minYBound = inset;
          const maxYBound = stageHeight - inset;
          let normalizeDy = 0;
          if (topAfter < minYBound) {
            normalizeDy = minYBound - topAfter;
          } else if (bottomAfter > maxYBound) {
            normalizeDy = maxYBound - bottomAfter;
          }
          if (normalizeDy !== 0) {
            offSharedClampDeltaY = Math.round(offSharedClampDeltaY + normalizeDy);
          }
        }
        console.log("[GROUP-DRAG-END-CLAMP][OFFCANVAS][SHARED-DELTA]", {
          hasOffCanvasGroupSelection,
          rectCount,
          stageWidth,
          stageHeight,
          inset,
          laneMinX: Math.round(laneMinX),
          laneRight: Math.round(laneRight),
          laneWidth: Math.round(laneWidth),
          allowedMinDx: Number.isFinite(allowedMinDx)
            ? Math.round(allowedMinDx)
            : allowedMinDx,
          allowedMaxDx: Number.isFinite(allowedMaxDx)
            ? Math.round(allowedMaxDx)
            : allowedMaxDx,
          allowedMinDy: Number.isFinite(allowedMinDy)
            ? Math.round(allowedMinDy)
            : allowedMinDy,
          allowedMaxDy: Number.isFinite(allowedMaxDy)
            ? Math.round(allowedMaxDy)
            : allowedMaxDy,
          offSharedClampDeltaX,
          offSharedClampDeltaY,
          rows: sharedClampDebugRows,
        });
      } else {
        const offDeltaX = [];
        const offDeltaY = [];
        offEntries.forEach((entry) => {
          if (!entry?.elementNode) return;
          const shiftedX = Math.round((entry.currentX || 0) + offGroupShift.x);
          const shiftedY = Math.round((entry.currentY || 0) + offGroupShift.y);
          const clamped = clampNodeByRenderedClientRect({
            stage: offStage,
            node: entry.elementNode,
            candidateX: shiftedX,
            candidateY: shiftedY,
            avoidOffCanvasMainColumn: true,
            forceLeftOfOffCanvasMainColumn: true,
            edgeInsetPx: TRANSFORMER_ANCHOR_BUFFER_PX,
          });
          const clampedX = Math.round(Number(clamped.x) || shiftedX);
          const clampedY = Math.round(Number(clamped.y) || shiftedY);
          offDeltaX.push(clampedX - shiftedX);
          offDeltaY.push(clampedY - shiftedY);
        });
        if (offDeltaX.length > 0) {
          const hasNegativeX = offDeltaX.some((d) => d < 0);
          offSharedClampDeltaX = hasNegativeX
            ? Math.min(...offDeltaX)
            : Math.max(...offDeltaX);
        }
        if (offDeltaY.length > 0) {
          const hasNegativeY = offDeltaY.some((d) => d < 0);
          offSharedClampDeltaY = hasNegativeY
            ? Math.min(...offDeltaY)
            : Math.max(...offDeltaY);
        }
        console.log("[GROUP-DRAG-END-CLAMP][OFFCANVAS][FALLBACK-DELTA]", {
          hasOffCanvasGroupSelection,
          offDeltaX,
          offDeltaY,
          offSharedClampDeltaX,
          offSharedClampDeltaY,
        });
      }
    }

    // Update each element type's state array with current Konva node positions

    dragEntries.forEach((entry) => {
        const {
          initialPos,
          isTopLayerImage,
          elementNode,
          currentX,
          currentY,
          fallbackBBox,
        } =
          entry;

        // DEBUG: Log position sync for lines/arrows
        if (
          initialPos.type === "Line" ||
          initialPos.type === "Arrow" ||
          initialPos.type === "DoubleSidedArrow"
        ) {
        }

        const shift = entry.isInOffCanvas ? offGroupShift : mainGroupShift;
        const shiftedX = Math.round(currentX + shift.x);
        const shiftedY = Math.round(currentY + shift.y);
        let constrainedX = shiftedX;
        let constrainedY = shiftedY;

        if (entry.isInOffCanvas && offStage) {
          const beforeSharedClampX = shiftedX;
          const beforeSharedClampY = shiftedY;
          if (elementNode) {
            constrainedX = hasOffCanvasGroupSelection
              ? Math.round(shiftedX + offSharedClampDeltaX)
              : (() => {
                  const clamped = clampNodeByRenderedClientRect({
                    stage: offStage,
                    node: elementNode,
                    candidateX: shiftedX,
                    candidateY: shiftedY,
                    avoidOffCanvasMainColumn: true,
                    forceLeftOfOffCanvasMainColumn: true,
                    edgeInsetPx: TRANSFORMER_ANCHOR_BUFFER_PX,
                  });
                  return Math.max(0, Math.round(Number(clamped.x) || shiftedX));
                })();
            constrainedY = hasOffCanvasGroupSelection
              ? Math.round(shiftedY + offSharedClampDeltaY)
              : (() => {
                  const clamped = clampNodeByRenderedClientRect({
                    stage: offStage,
                    node: elementNode,
                    candidateX: shiftedX,
                    candidateY: shiftedY,
                    avoidOffCanvasMainColumn: true,
                    forceLeftOfOffCanvasMainColumn: true,
                    edgeInsetPx: TRANSFORMER_ANCHOR_BUFFER_PX,
                  });
                  return Math.round(Number(clamped.y) || shiftedY);
                })();
          } else {
            if (hasOffCanvasGroupSelection) {
              constrainedX = Math.round(shiftedX + offSharedClampDeltaX);
              constrainedY = Math.round(shiftedY + offSharedClampDeltaY);
            } else {
              // Fallback for missing nodes: clamp to LEFT off-canvas lane bounds.
              const { mainZoneStart } = getOffCanvasMainColumnBounds(offStageWidth);
              const safeInset = Math.max(
                0,
                Number(TRANSFORMER_ANCHOR_BUFFER_PX) || 0,
              );
              const elementWidth = Math.max(1, Number(fallbackBBox?.width) || 1);
              const minX = safeInset;
              const maxX = Math.max(
                minX,
                mainZoneStart - safeInset - elementWidth,
              );
              constrainedX = Math.max(minX, Math.min(maxX, shiftedX));
              constrainedY = Math.max(0, Math.min(offStageHeight - 1, shiftedY));
            }
          }
          console.log("[GROUP-DRAG-END-CLAMP][OFFCANVAS][ENTRY-AFTER-SHARED]", {
            id: initialPos.id,
            type: initialPos.type,
            hasOffCanvasGroupSelection,
            shiftedX: beforeSharedClampX,
            shiftedY: beforeSharedClampY,
            constrainedX,
            constrainedY,
            appliedDx: constrainedX - beforeSharedClampX,
            appliedDy: constrainedY - beforeSharedClampY,
            usedNode: !!elementNode,
          });
          // IMPORTANT: For off-canvas GROUP drag-end, shared clamp delta above is already
          // computed from rendered client rects of all members as one rigid selection.
          // Running an additional per-element clamp here can over-shift items (e.g. circles).
          if (!hasOffCanvasGroupSelection) {
            const nodeRect = elementNode?.getClientRect?.();
            const visualWidth = Math.max(
              1,
              Number(nodeRect?.width) || 0,
              Number(fallbackBBox?.width) || 0,
            );
            const visualHeight = Math.max(
              1,
              Number(nodeRect?.height) || 0,
              Number(fallbackBBox?.height) || 0,
            );
            const { mainZoneStart } = getOffCanvasMainColumnBounds(offStageWidth);
            const safeInset = Math.max(
              0,
              Number(TRANSFORMER_ANCHOR_BUFFER_PX) || 0,
            );
            const laneMinX = safeInset;
            const laneRight = mainZoneStart - safeInset;
            const laneWidth = Math.max(0, laneRight - laneMinX);
            const maxStageX = laneRight - visualWidth;
            const minStageX = visualWidth > laneWidth ? maxStageX : laneMinX;
            const maxStageY = Math.max(0, offStageHeight - visualHeight);
            const beforePerElementClampX = constrainedX;
            const beforePerElementClampY = constrainedY;
            constrainedX = Math.max(minStageX, Math.min(maxStageX, constrainedX));
            constrainedY = Math.max(0, Math.min(maxStageY, constrainedY));
            console.log("[GROUP-DRAG-END-CLAMP][OFFCANVAS][PER-ELEMENT]", {
              id: initialPos.id,
              type: initialPos.type,
              laneMinX: Math.round(laneMinX),
              laneRight: Math.round(laneRight),
              laneWidth: Math.round(laneWidth),
              visualWidth: Math.round(visualWidth),
              visualHeight: Math.round(visualHeight),
              minStageX: Math.round(minStageX),
              maxStageX: Math.round(maxStageX),
              maxStageY: Math.round(maxStageY),
              beforeX: Math.round(beforePerElementClampX),
              beforeY: Math.round(beforePerElementClampY),
              afterX: Math.round(constrainedX),
              afterY: Math.round(constrainedY),
            });
          }
          if (elementNode) {
            elementNode.position({ x: constrainedX, y: constrainedY });
            elementNode.getLayer()?.batchDraw();
            if (isTopLayerImage) {
              console.log("[GROUP-DRAG-END][TOPLAYER][NODE-POS]", {
                id: initialPos.id,
                stageName: elementNode.getStage?.()?.attrs?.id || "unknown",
                constrainedX,
                constrainedY,
              });
            }
          }
        } else if (
          elementNode &&
          (constrainedX !== currentX || constrainedY !== currentY)
        ) {
          elementNode.position({ x: constrainedX, y: constrainedY });
          elementNode.getLayer()?.batchDraw();
        }

        // Final safety clamp at group drag-end:
        // ensure rendered client rect is fully inside the active stage bounds.
        // This catches residual 1-3px overflow for large mixed selections (700px elements)
        // after shared group-shift math.
        if (elementNode) {
          const targetStage = entry.isInOffCanvas ? offStage : mainStage;
          if (targetStage) {
            const finalClamped = clampNodeByRenderedClientRect({
              stage: targetStage,
              node: elementNode,
              candidateX: constrainedX,
              candidateY: constrainedY,
              avoidOffCanvasMainColumn: entry.isInOffCanvas,
              forceLeftOfOffCanvasMainColumn: entry.isInOffCanvas,
              edgeInsetPx: GROUP_DRAG_END_SAFETY_INSET_PX,
            });
            let finalX = Math.round(Number(finalClamped.x) || constrainedX);
            const finalY = Math.round(Number(finalClamped.y) || constrainedY);
            // Text-in-group can still leave wrapper edge slightly clipped because text
            // client rect and selection wrapper math use different left offsets.
            // Keep a hard left floor on main canvas for text nodes at drag-end.
            if (
              initialPos.type === "Text" &&
              !entry.isInOffCanvas &&
              finalX < GROUP_DRAG_END_SAFETY_INSET_PX
            ) {
              finalX = GROUP_DRAG_END_SAFETY_INSET_PX;
            }
            // Keep top-left anchored main-canvas nodes (Text/Rect) inside right boundary.
            // Text metrics can occasionally diverge from wrapper math at drag-end, leaving a
            // residual right overflow even after client-rect clamp.
            if (
              !entry.isInOffCanvas &&
              (initialPos.type === "Text" || initialPos.type === "Rect")
            ) {
              const stageWidth = Number(targetStage.width?.()) || 0;
              const safeInset = Math.max(
                0,
                Number(GROUP_DRAG_END_SAFETY_INSET_PX) || 0,
              );
              if (stageWidth > 0) {
                const visualWidth = Math.max(
                  1,
                  Number(fallbackBBox?.width) || 0,
                  Number(elementNode?.width?.()) || 0,
                );
                const maxAnchorX = Math.max(
                  safeInset,
                  stageWidth - safeInset - visualWidth,
                );
                finalX = Math.min(finalX, maxAnchorX);
              }
            }
            
            // CRITICAL FIX: Ensure finalX never goes below 0 for off-canvas elements
            // This prevents the final clamping step from allowing negative positions
            if (entry.isInOffCanvas) {
              // For top-left anchored shapes, keep the node anchor inside the off-canvas lane inset.
              // Text/Rect can otherwise end up with negative x while their rendered rect still appears inside,
              // causing visible "slightly left" drift after group drag-end.
              if (initialPos.type === "Text" || initialPos.type === "Rect") {
                finalX = Math.max(GROUP_DRAG_END_SAFETY_INSET_PX, finalX);
              } else {
                finalX = Math.max(0, finalX);
              }
            }
            
            constrainedX = finalX;
            constrainedY = finalY;
            elementNode.position({ x: constrainedX, y: constrainedY });
            elementNode.getLayer()?.batchDraw();
          }
        }

        // Update the appropriate state array based on element type
        switch (initialPos.type) {
          case "Rect":
            setRectangles((prev) =>
              prev.map((rect) =>
                rect.id === initialPos.id
                  ? { ...rect, x: constrainedX, y: constrainedY }
                  : rect,
              ),
            );
            setOffCanvasRectangles((prev) =>
              prev.map((rect) =>
                rect.id === initialPos.id
                  ? { ...rect, x: constrainedX, y: constrainedY }
                  : rect,
              ),
            );
            break;

          case "Circle":
            setCircles((prev) =>
              prev.map((circle) =>
                circle.id === initialPos.id
                  ? { ...circle, x: constrainedX, y: constrainedY }
                  : circle,
              ),
            );
            setOffCanvasCircles((prev) =>
              prev.map((circle) =>
                circle.id === initialPos.id
                  ? { ...circle, x: constrainedX, y: constrainedY }
                  : circle,
              ),
            );
            break;

          case "Image":
            // CRITICAL FIX: For top-layer images, ensure we update the main canvas images array
            // Top-layer images are in the images array (main canvas), not offCanvasImages
            // isTopLayerImage is computed in dragEntries so we stay in sync with node lookup.

            if (isTopLayerImage) {
              // CRITICAL FIX: Always update state for top-layer images to prevent fluctuation
              // The issue was that during group drag, Konva nodes were being updated but state wasn't,
              // causing React to re-render the image at the old state position, creating fluctuation.
              // Now we update both Konva nodes (in handleGroupDragMove) AND state (here) during drag.

              // Top-layer image: update main canvas images array with current position
              // Don't update offCanvasImages array for top-layer images
              setImages((prev) => {
                const updated = prev.map((img) =>
                  img.id === initialPos.id
                    ? { ...img, x: constrainedX, y: constrainedY }
                    : img,
                );
               

                return updated;
              });
              setOffCanvasImages((prev) => {
                if (!prev.some((img) => img.id === initialPos.id)) return prev;
                const updated = prev.map((img) =>
                  img.id === initialPos.id
                    ? { ...img, x: constrainedX, y: constrainedY }
                    : img,
                );
               
                return updated;
              });
            } else {
              // Regular image: update both arrays (one will have the image, the other won't)
              setImages((prev) =>
                prev.map((img) =>
                  img.id === initialPos.id
                    ? { ...img, x: constrainedX, y: constrainedY }
                    : img,
                ),
              );
              setOffCanvasImages((prev) =>
                prev.map((img) =>
                  img.id === initialPos.id
                    ? { ...img, x: constrainedX, y: constrainedY }
                    : img,
                ),
              );
            }
            break;

          case "Text":
            // CRITICAL FIX: Always sync text positions during group drag
            // Even if positions are negative (crossing boundary), we need to sync them
            // so that the text element node can be found when updating selectedObjects
            // The boundary crossing logic in handleElementDragEnd will handle canvas transfer
            setTexts((prev) =>
              prev.map((text) =>
                text.id === initialPos.id
                  ? { ...text, x: constrainedX, y: constrainedY }
                  : text,
              ),
            );
            setOffCanvasTexts((prev) =>
              prev.map((text) =>
                text.id === initialPos.id
                  ? { ...text, x: constrainedX, y: constrainedY }
                  : text,
              ),
            );
            break;

          case "Line":
            setLines((prev) =>
              prev.map((line) =>
                line.id === initialPos.id
                  ? { ...line, x: constrainedX, y: constrainedY }
                  : line,
              ),
            );
            setOffCanvasLines((prev) =>
              prev.map((line) =>
                line.id === initialPos.id
                  ? { ...line, x: constrainedX, y: constrainedY }
                  : line,
              ),
            );
            break;

          case "Arrow":
            // CRITICAL FIX: Unified elements (UnifiedLineArrowElement) are stored in lines array
            // Check if this element is in the lines array first (unified element)
            const isUnifiedArrow =
              lines.some((l) => {
                if (l.id !== initialPos.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow || hasEndArrow;
              }) ||
              offCanvasLines.some((l) => {
                if (l.id !== initialPos.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow || hasEndArrow;
              });

            if (isUnifiedArrow) {
              // Unified element - update lines array
              setLines((prev) =>
                prev.map((line) =>
                  line.id === initialPos.id
                    ? { ...line, x: constrainedX, y: constrainedY }
                    : line,
                ),
              );
              setOffCanvasLines((prev) =>
                prev.map((line) =>
                  line.id === initialPos.id
                    ? { ...line, x: constrainedX, y: constrainedY }
                    : line,
                ),
              );
            } else {
              // Legacy arrow - update arrows array
              setArrows((prev) =>
                prev.map((arrow) =>
                  arrow.id === initialPos.id
                    ? { ...arrow, x: constrainedX, y: constrainedY }
                    : arrow,
                ),
              );
              setOffCanvasArrows((prev) =>
                prev.map((arrow) =>
                  arrow.id === initialPos.id
                    ? { ...arrow, x: constrainedX, y: constrainedY }
                    : arrow,
                ),
              );
            }
            break;

          case "DoubleSidedArrow":
            // CRITICAL FIX: Unified elements (UnifiedLineArrowElement) are stored in lines array
            // Check if this element is in the lines array first (unified element)
            const isUnifiedDoubleSidedArrow =
              lines.some((l) => {
                if (l.id !== initialPos.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              }) ||
              offCanvasLines.some((l) => {
                if (l.id !== initialPos.id) return false;
                const hasStartArrow =
                  l.hasStartArrow === true || l.pointerAtBeginning === true;
                const hasEndArrow =
                  l.hasEndArrow === true || l.pointerAtEnd === true;
                return hasStartArrow && hasEndArrow;
              });

            if (isUnifiedDoubleSidedArrow) {
              // Unified element - update lines array
              setLines((prev) =>
                prev.map((line) =>
                  line.id === initialPos.id
                    ? { ...line, x: constrainedX, y: constrainedY }
                    : line,
                ),
              );
              setOffCanvasLines((prev) =>
                prev.map((line) =>
                  line.id === initialPos.id
                    ? { ...line, x: constrainedX, y: constrainedY }
                    : line,
                ),
              );
            } else {
              // Legacy double-sided arrow - update doubleSidedArrows array
              setDoubleSidedArrows((prev) =>
                prev.map((arrow) =>
                  arrow.id === initialPos.id
                    ? { ...arrow, x: constrainedX, y: constrainedY }
                    : arrow,
                ),
              );
              setOffCanvasDoubleSidedArrows((prev) =>
                prev.map((arrow) =>
                  arrow.id === initialPos.id
                    ? { ...arrow, x: constrainedX, y: constrainedY }
                    : arrow,
                ),
              );
            }
            break;

          default:
          // PERFORMANCE: Removed console.warn for better performance
        }
    });

    // Also update selectedObjects array with new positions
    // CRITICAL FIX: This ensures selectedObjects has the latest positions after group drag
    if (selectedObjects && selectedObjects.length > 0) {
      setSelectedObjects((prev) =>
        prev.map((obj) => {
          // CRITICAL FIX: For top-layer images, check topLayerStage first
          const isTopLayerImage =
            obj.type === "Image" &&
            (obj?.isOnTopLayer === true ||
              images.some(
                (img) => img.id === obj.id && img.isOnTopLayer === true,
              ) ||
              offCanvasImages.some(
                (img) => img.id === obj.id && img.isOnTopLayer === true,
              ));

          let elementNode = null;
          if (isTopLayerImage && topLayerStage) {
            // For top-layer images, check topLayerStage first
            elementNode = topLayerStage.findOne(`#${obj.id}`);
          }
          if (!elementNode && isTopLayerImage && offCanvasTopLayerStage) {
            elementNode = offCanvasTopLayerStage.findOne(`#${obj.id}`);
          }
          if (!elementNode) {
            // For other elements, check mainStage or offStage
            elementNode =
              mainStage?.findOne(`#${obj.id}`) ||
              offStage?.findOne(`#${obj.id}`);
          }

          if (elementNode) {
            const newX = elementNode.x();
            const newY = elementNode.y();

            // Log update for top-layer images
            if (isTopLayerImage) {
            }

            return {
              ...obj,
              x: newX,
              y: newY,
            };
          } else if (isTopLayerImage) {
          }
          return obj;
        }),
      );
    }

    // Ensure the off-canvas group wrapper is clamped after final sync.
    if (offStage) {
      const offWrapperNode = offStage.findOne("#group-drag-wrapper-offcanvas");
      if (offWrapperNode) {
        const stageWidth = Number(offStage.width?.()) || window.innerWidth;
        const stageHeight =
          Number(offStage.height?.()) || fixedStageHeightPx;
        const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
        const edgeInset = Math.max(
          0,
          Number(TRANSFORMER_ANCHOR_BUFFER_PX) || 0,
        );
        const scaleX = Number(offWrapperNode.scaleX?.()) || 1;
        const scaleY = Number(offWrapperNode.scaleY?.()) || 1;
        const rawWidth = Number(offWrapperNode.width?.()) || 0;
        const rawHeight = Number(offWrapperNode.height?.()) || 0;
        const wrapperWidth = rawWidth * scaleX;
        const wrapperHeight = rawHeight * scaleY;
        const wrapperStrokeWidth = Number(offWrapperNode.strokeWidth?.()) || 0;
        const wrapperHalfStroke = wrapperStrokeWidth / 2;
        const WRAPPER_RIGHT_SLACK_PX_POST = 6;
        const WRAPPER_TOP_SLACK_PX_POST = 6;
        const WRAPPER_BOTTOM_SLACK_PX_POST = 6;
        const laneMinX = edgeInset + wrapperHalfStroke;
        const laneRight =
          Number(mainZoneStart || stageWidth) -
          edgeInset -
          wrapperHalfStroke +
          WRAPPER_RIGHT_SLACK_PX_POST;
        const maxX =
          laneRight -
          Math.max(
            0,
            (Number(offWrapperNode.width?.()) || 0) *
              (Number(offWrapperNode.scaleX?.()) || 1),
          );
        const minX = Math.min(laneMinX, maxX);
        const minY = Math.max(
          wrapperHalfStroke,
          edgeInset + wrapperHalfStroke - WRAPPER_TOP_SLACK_PX_POST,
        );
        const maxY = Math.max(
          minY,
          stageHeight -
            edgeInset -
            Math.max(0, wrapperHeight) -
            wrapperHalfStroke +
            WRAPPER_BOTTOM_SLACK_PX_POST,
        );
        const clampedX = Math.max(
          minX,
          Math.min(maxX, Number(offWrapperNode.x?.()) || 0),
        );
        const clampedY = Math.max(minY, Math.min(maxY, Number(offWrapperNode.y?.()) || 0));
        console.log("[OFFCANVAS-WRAPPER][CLAMP-POST-SYNC]", {
          stageWidth: Math.round(stageWidth),
          stageHeight: Math.round(stageHeight),
          edgeInset,
          laneMinX: Math.round(laneMinX),
          laneRight: Math.round(laneRight),
          wrapperWidth: Math.round(wrapperWidth),
          wrapperHeight: Math.round(wrapperHeight),
          minX: Math.round(minX),
          maxX: Math.round(maxX),
          minY: Math.round(minY),
          maxY: Math.round(maxY),
          clampedX: Math.round(clampedX),
          clampedY: Math.round(clampedY),
        });
        offWrapperNode.position({ x: clampedX, y: clampedY });
        offWrapperNode.getLayer()?.batchDraw?.();
      }
    }

    // Save to history after syncing positions
    saveToHistory();
  }, [
    stageRef,
    offCanvasStageRef,
    topLayerStageRef,
    setRectangles,
    setOffCanvasRectangles,
    setCircles,
    setOffCanvasCircles,
    setImages,
    setOffCanvasImages,
    setTexts,
    setOffCanvasTexts,
    setLines,
    setOffCanvasLines,
    setArrows,
    setOffCanvasArrows,
    setDoubleSidedArrows,
    setOffCanvasDoubleSidedArrows,
    selectedObjects,
    setSelectedObjects,
    saveToHistory,
    lines, // Add lines array to check for unified elements
    offCanvasLines, // Add offCanvasLines array to check for unified elements
    arrows, // Add arrows array for legacy arrow check
    doubleSidedArrows, // Add doubleSidedArrows array for legacy arrow check
  ]);

  // Create renderer factories using extracted renderers
  // CRITICAL: Use setTexts prop (which may be setTextsForSelection wrapper from MyCanvas)
  // This wrapper prevents markUserChanges from being called during selection operations
  const renderTextElement = createTextRenderer({
    texts,
    offCanvasTexts,
    setTexts, // This is setTextsForSelection when passed from MyCanvas
    setOffCanvasTexts,
    selectedObject,
    selectedObjects, // Pass selectedObjects to textRenderer
    canvasHeight,
    canvasResizeTrigger,
    handleElementDragEnd,
    handleTransformUpdate,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    offCanvasTransformerRef,
    transformerRef,
    setSelectedText,
    setSelectedObject,
    setSelectedObjects,
    calculateTextBlockWidth,
    getTextStyle,
    calculateGroupHeight,
    setSelectedImage,
    setSelectedTextId,
    setShowTextBox,
    selectedText,
    setEditTextBoxContent,
    editTextBoxContent,
    setShowEditTask,
    setShowEditNote,
    showEditTask,
    showEditNote,
    handlePartClick,
    estimateKonvaTextHeight,
    onClick,
    setAction,
    ACTIONS,
    action,
    setEditing,
    isResizing,
    handleDragPreview,
    clearDragPreview,
    handleElementCopy: handleCopyElementBetweenCanvases,
    lockedAction,
    isCloneMode,
    isMultiSelectMode,
    isMultiSelectModeRef, // Add ref to avoid stale closures
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    handleGroupDragStart,
    handleGroupDragMove,
    isSelecting, // Pass isSelecting to allow TextElement to disable listening during selection
    stageRef,
    offCanvasStageRef,
    dragPreviewStateRef, // Use last preview position for drop when main→off→main
    crossStageGestureBegin,
    crossStageGestureEnd,
  });

  // renderTextElement is now created using createTextRenderer factory

  const renderArrowElement = createArrowRenderer({
    arrows,
    offCanvasArrows,
    setArrows,
    setOffCanvasArrows,
    selectedObject,
    strokeColor,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    setSelectedObject,
    setSelectedObjects,
    selectedObjects,
    setShowTextBox,
    setSelectedText,
    setSelectedImage,
    setSelectedArrow,
    setSelectedDoubleSidedArrow,
    setSelectedLine,
    onClick,
    onDoubleClick,
    handleElementDragEnd,
    handleArrowDragEnd,
    saveToHistory,
    handleDragPreview,
    clearDragPreview,
    handleElementCopy: handleCopyElementBetweenCanvases,
    canvasWidth: mainCanvasWidth,
    canvasHeight: fixedStageHeightPx,
    lockedAction,
    isCloneMode,
    isElementBeingEdited,
    setPendingChanges,
    isMultiSelectMode,
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    handleGroupDragStart,
    handleGroupDragMove,
    cancelActiveGroupDrag,
    rectangles,
    offCanvasRectangles,
    circles,
    offCanvasCircles,
    images,
    offCanvasImages,
    texts,
    offCanvasTexts,
    lines,
    offCanvasLines,
    doubleSidedArrows,
    offCanvasDoubleSidedArrows,
    stageRef,
    offCanvasStageRef,
  });

  const renderRectangleElement = createRectangleRenderer({
    rectangles,
    offCanvasRectangles,
    setRectangles,
    setOffCanvasRectangles,
    selectedObject,
    selectedObjects, // Add selectedObjects
    strokeColor,
    images,
    circles, // Add for cross-canvas check
    texts, // Add for cross-canvas check
    arrows, // Add for cross-canvas check
    lines, // Add for cross-canvas check
    doubleSidedArrows, // Add for cross-canvas check
    canvasHeight,
    canvasResizeTrigger,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    setSelectedObject,
    setSelectedObjects,
    setShowTextBox,
    setSelectedText,
    setSelectedImage,
    setSelectedArrow, // Add setSelectedArrow to clear arrow selections
    setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to clear double-sided arrow selections
    setSelectedLine, // Add setSelectedLine to clear line selections
    onClick,
    handleElementDragEnd,
    saveToHistory,
    handleDragPreview,
    clearDragPreview,
    handleRectangleCopy: handleCopyRectangleBetweenCanvases,
    lockedAction,
    isCloneMode,
    isElementBeingEdited,
    setPendingChanges,
    pendingChangesRef, // Pass ref to update synchronously
    isMultiSelectMode,
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    handleGroupDragStart,
    handleGroupDragMove,
    cancelActiveGroupDrag,
    stageRef,
    offCanvasStageRef,
    justFinishedTransformRef,
    lastTransformStartTimeRef,
    selectionBoxRef,
    selectionBoxStageRef,
    clearDebouncedTransformForKey,
    crossStageGestureBegin,
    crossStageGestureEnd,
  });

  const renderCircleElement = createCircleRenderer({
    circles,
    offCanvasCircles,
    setCircles,
    setOffCanvasCircles,
    selectedObject,
    selectedObjects, // Add selectedObjects
    strokeColor,
    images,
    rectangles, // Add for cross-canvas check
    texts, // Add for cross-canvas check
    arrows, // Add for cross-canvas check
    lines, // Add for cross-canvas check
    doubleSidedArrows, // Add for cross-canvas check
    canvasHeight,
    canvasResizeTrigger,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    setSelectedObject,
    setSelectedObjects,
    setShowTextBox,
    setSelectedText,
    setSelectedImage,
    setSelectedArrow, // Add setSelectedArrow to clear arrow selections
    setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to clear double-sided arrow selections
    setSelectedLine, // Add setSelectedLine to clear line selections
    onClick,
    handleElementDragEnd,
    saveToHistory,
    handleDragPreview,
    clearDragPreview,
    handleElementCopy: handleCopyElementBetweenCanvases,
    lockedAction,
    isElementBeingEdited,
    setPendingChanges,
    pendingChangesRef, // Pass ref to update synchronously
    isMultiSelectMode,
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    handleGroupDragStart,
    handleGroupDragMove,
    cancelActiveGroupDrag,
    stageRef,
    offCanvasStageRef,
    justFinishedTransformRef,
    lastTransformStartTimeRef,
    selectionBoxRef,
    selectionBoxStageRef,
    clearDebouncedTransformForKey,
    crossStageGestureBegin,
    crossStageGestureEnd,
  });

  const renderLineElement = createLineRenderer({
    lines,
    offCanvasLines,
    setLines,
    setOffCanvasLines,
    selectedObject,
    strokeColor,
    canvasResizeTrigger,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    updateLineProperties,
    setSelectedObject,
    setSelectedObjects,
    selectedObjects,
    setShowTextBox,
    setSelectedText,
    setSelectedImage,
    setSelectedLine,
    setSelectedArrow,
    setSelectedDoubleSidedArrow,
    onClick,
    handleElementDragEnd,
    handleLineDragEnd, // CRITICAL: Pass handleLineDragEnd for unified arrows/double-sided arrows
    saveToHistory,
    handleDragPreview,
    clearDragPreview,
    handleElementCopy: handleCopyElementBetweenCanvases,
    canvasWidth: mainCanvasWidth,
    canvasHeight: fixedStageHeightPx,
    lockedAction,
    isCloneMode,
    isElementBeingEdited,
    setPendingChanges,
    isMultiSelectMode,
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    handleGroupDragStart,
    handleGroupDragMove,
    cancelActiveGroupDrag,
    rectangles,
    offCanvasRectangles,
    circles,
    offCanvasCircles,
    images,
    offCanvasImages,
    texts,
    offCanvasTexts,
    arrows,
    offCanvasArrows,
    doubleSidedArrows,
    offCanvasDoubleSidedArrows,
    stageRef,
    offCanvasStageRef,
    dragPreviewStateRef,
  });

  const renderDoubleSidedArrowElement = createDoubleSidedArrowRenderer({
    doubleSidedArrows,
    offCanvasDoubleSidedArrows,
    setDoubleSidedArrows,
    setOffCanvasDoubleSidedArrows,
    selectedObject,
    strokeColor,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    setSelectedObject,
    setSelectedObjects,
    selectedObjects,
    setShowTextBox,
    setSelectedText,
    setSelectedImage,
    setSelectedDoubleSidedArrow,
    setSelectedArrow,
    setSelectedLine,
    onClick,
    onDoubleClick,
    handleElementDragEnd,
    saveToHistory,
    handleDragPreview,
    clearDragPreview,
    handleElementCopy: handleCopyElementBetweenCanvases,
    canvasWidth: mainCanvasWidth,
    canvasHeight: fixedStageHeightPx,
    lockedAction,
    isCloneMode,
    isElementBeingEdited,
    setPendingChanges,
    isMultiSelectMode,
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    handleGroupDragStart,
    handleGroupDragMove,
    cancelActiveGroupDrag,
    rectangles,
    offCanvasRectangles,
    circles,
    offCanvasCircles,
    images,
    offCanvasImages,
    texts,
    offCanvasTexts,
    arrows,
    offCanvasArrows,
    lines,
    offCanvasLines,
    stageRef,
    offCanvasStageRef,
  });

  const renderImageElement = createImageRenderer({
    images,
    offCanvasImages,
    setImages,
    setOffCanvasImages,
    selectedObject,
    effectiveZoomLevel,
    disableInteraction,
    debouncedStateUpdate,
    isTransformingRef,
    updateImageProperties,
    setSelectedObject,
    setSelectedObjects,
    selectedObjects,
    setSelectedImage, // Add setSelectedImage for direct updates during drag
    setShowTextBox,
    setSelectedText,
    setSelectedArrow,
    setSelectedDoubleSidedArrow,
    setSelectedLine,
    onClick,
    handleElementDragEnd,
    saveToHistory,
    handleDragPreview,
    clearDragPreview,
    handleElementCopy: handleCopyElementBetweenCanvases,
    lockedAction,
    isCloneMode, // Pass clone mode to prevent selection of newly created clones
    cloneTemplate,
    cloneTemplates,
    blockClicksOnOffCanvasElements,
    blockClicksOnMainStageElements,
    isElementBeingEdited,
    setPendingChanges,
    pendingChangesRef, // Pass ref to update synchronously
    latestUpdatedImagesRef,
    isMultiSelectMode,
    handleGroupDragStart,
    handleGroupDragMove,
    cancelActiveGroupDrag,
    stageRef, // CRITICAL FIX: Add missing stageRef
    offCanvasStageRef, // CRITICAL FIX: Add missing offCanvasStageRef
    topLayerStageRef, // CRITICAL FIX: Add missing topLayerStageRef for top-layer images
    isGroupDraggingRef, // Pass isGroupDraggingRef to control top-layer image animation
    recentDraggableElementMousedownRef, // Pass ref for two-click selection behavior
    justFinishedTransformRef,
    lastTransformStartTimeRef,
    justFinishedDragRef,
    lastDraggedImageRef,
    selectionBoxRef,
    selectionBoxStageRef,
    rectangles,
    offCanvasRectangles,
    circles,
    offCanvasCircles,
    texts,
    offCanvasTexts,
    arrows,
    offCanvasArrows,
    lines,
    offCanvasLines,
    doubleSidedArrows,
    offCanvasDoubleSidedArrows,
    crossStageGestureBegin,
    crossStageGestureEnd,
  });

  // Text utilities are now imported from textCanvasUtils.js

  // formatListContent is now imported from textCanvasUtils.js

  // Old render functions removed - using factory functions above

  // Removed old renderCircleElement, renderLineElement, renderDoubleSidedArrowElement, and renderImageElement - using factory functions above

  // Mouse event handlers for the stage
  const onMouseMove = (e) => {
    // Ensure stage has pointer position set for accurate tracking
    if (stageRef?.current && e?.evt) {
      stageRef.current.setPointersPositions(e.evt);
    }

    if (onPointerMove) {
      onPointerMove(e);
    }
  };

  const onMouseUp = (e) => {
    if (onPointerUp) {
      onPointerUp(e);
    }
  };

  // Effect to log and verify image dimensions as they're updated
  // CRITICAL FIX: Skip sync during/after transform to prevent visual fluctuation
  useEffect(() => {
    // Don't sync if we're currently transforming - let onTransformEnd handle it
    if (isTransformingRef.current?.isTransforming) {
      return;
    }

    if (images && images.length > 0) {
      // Use a longer delay to allow transform end to complete and state to update
      const timeoutId = setTimeout(() => {
        // Double-check we're not transforming (in case transform ended but state hasn't updated)
        if (isTransformingRef.current?.isTransforming) {
          return;
        }

        if (stageRef?.current) {
          images.forEach((img) => {
            const imageNode = stageRef.current.findOne(`#${img.id}`);
            if (imageNode) {
              // CRITICAL FIX: Only sync if dimensions differ AND it's not a transform operation
              // Check if the image has isTransform flag (set during transform end)
              // If it does, skip syncing to prevent overriding the node's current dimensions
              const isFromTransform = img.isTransform === true;

              if (
                !isFromTransform &&
                (imageNode.width() !== img.width ||
                  imageNode.height() !== img.height)
              ) {
                // Force update node to match state while preserving flips
                imageNode.width(img.width);
                imageNode.height(img.height);
                // Preserve flip via scale signs from state
                imageNode.scaleX(img.scaleX !== undefined ? img.scaleX : 1);
                imageNode.scaleY(img.scaleY !== undefined ? img.scaleY : 1);
                imageNode.getLayer().batchDraw();
              }
            }
          });
        }
      }, 100); // Reduced delay but still allows transform end to complete

      return () => clearTimeout(timeoutId);
    }
  }, [images, stageRef]);
  // Effect to trigger a redraw when text selection changes
  useEffect(() => {
    if (stageRef && stageRef.current) {
      stageRef.current.batchDraw();
    }
  }, [selectedTextId, texts, stageRef]);

  // Transformer management is now handled by useTransformerManager hook
  useTransformerManager({
    selectedObject,
    selectedObjects,
    isTransformingRef,
    isDuplicatePlacementActive,
    stageRef,
    offCanvasStageRef,
    transformerRef,
    offCanvasTransformerRef,
    topLayerStageRef,
    topLayerTransformerRef,
    rectangles,
    circles,
    images,
    texts,
    arrows,
    lines,
    doubleSidedArrows,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasImages,
    offCanvasTexts,
    offCanvasArrows,
    offCanvasLines,
    offCanvasDoubleSidedArrows,
    editTextBoxContent,
    showEditNote,
    showEditTask,
  });

  // Keep transformer visible while editing text (no clear on editTextBoxContent/showEditNote/showEditTask).
  // Previously we cleared the transformer in edit mode, which made it vanish when focusing/typing in Lexical
  // and only reappear when hovering canvas buttons; we no longer clear so it stays visible.

  // CRITICAL FIX: Force clear top-layer transformer when dragging to off-canvas
  // Use useLayoutEffect to run synchronously before paint, ensuring it runs after useTransformerManager
  React.useLayoutEffect(() => {
    if (
      selectedObject?.type === "Image" &&
      selectedObject?.isOnTopLayer &&
      topLayerTransformerRef.current
    ) {
      // Check if dragging to off-canvas
      const dragPreviewId =
        dragPreview?.data?.id?.replace("-ghost", "") ||
        dragPreviewStateRef.current?.id;
      const isDraggingToOffCanvasViaPreview =
        dragPreview &&
        dragPreview.type === "Image" &&
        dragPreviewId === selectedObject.id &&
        dragPreview.target === "offCanvas";

      const isDraggingToOffCanvasViaFlag =
        selectedObject?._isDraggingToOffCanvas === true;

      // Check if node is dragging
      let isNodeDragging = false;
      if (topLayerStageRef.current) {
        const node = topLayerStageRef.current.findOne(`#${selectedObject?.id}`);
        isNodeDragging = node && node.getAttr("_isDragging") === true;
      }

      // If dragging to off-canvas, force clear transformer nodes
      if (
        isDraggingToOffCanvasViaFlag ||
        isDraggingToOffCanvasViaPreview ||
        (isNodeDragging && isDraggingToOffCanvasViaPreview)
      ) {
        if (topLayerTransformerRef.current) {
          topLayerTransformerRef.current.nodes([]);
          const layer = topLayerTransformerRef.current.getLayer();
          if (layer) {
            layer.batchDraw();
          }
        }
      }
    }
  }, [selectedObject, dragPreview, topLayerTransformerRef, topLayerStageRef]);

  // CRITICAL FIX: During top-layer image drag, update transformer anchors every frame
  // React may not re-render frequently enough during drag, so we poll and update directly.
  useEffect(() => {
    const isTopLayerImage =
      selectedObject?.type === "Image" && selectedObject?.isOnTopLayer;
    if (
      !isTopLayerImage ||
      !topLayerStageRef?.current ||
      !topLayerTransformerRef?.current
    ) {
      return;
    }

    let rafId;
    const updateAnchors = () => {
      const node = topLayerStageRef?.current?.findOne(`#${selectedObject?.id}`);
      const tr = topLayerTransformerRef?.current;

      // CRITICAL: Don't check tr.nodes().length here - just check if node and tr exist
      // The nodes might be temporarily empty during render cycles
      if (!node || !tr) {
        rafId = requestAnimationFrame(updateAnchors);
        return;
      }

      if (isDuplicatePlacementActive) {
        try {
          tr.nodes([]);
          tr.setAttrs({
            enabledAnchors: [],
            rotateEnabled: false,
          });
          tr.getLayer()?.batchDraw();
        } catch (e) {
          // Ignore
        }
        rafId = requestAnimationFrame(updateAnchors);
        return;
      }

      const isDragging =
        (typeof node.isDragging === "function" ? node.isDragging() : false) ||
        node.getAttr?.("_isDragging");
      const anchors =
        selectedObject?.maintainAspectRatio === true
          ? ["bottom-right"]
          : ["middle-right", "bottom-center", "bottom-right"];
      const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;
      try {
        tr.setAttrs({
          enabledAnchors: effectiveAnchors,
          rotateEnabled: !isDuplicatePlacementActive,
        });
        tr.getLayer()?.batchDraw();
      } catch (e) {
        // Ignore
      }
      rafId = requestAnimationFrame(updateAnchors);
    };
    rafId = requestAnimationFrame(updateAnchors);
    return () => cancelAnimationFrame(rafId);
  }, [
    selectedObject?.id,
    selectedObject?.type,
    selectedObject?.isOnTopLayer,
    selectedObject?.maintainAspectRatio,
    isDuplicatePlacementActive,
  ]);

  // handleImageClick is now imported from canvasElementUtils.js
  const handleImageClick = (imageId, event) => {
    handleImageClickUtil({
      imageId,
      images,
      setSelectedImage,
      setSelectedObject,
      setImages,
      stageRef,
      transformerRef,
      event,
      selectedObjects,
      setSelectedObjects,
      isMultiSelectMode,
      selectedObject,
      setSelectedText,
      setShowTextBox,
      setSelectedArrow,
      setSelectedDoubleSidedArrow,
      setSelectedLine,
    });
  };

  // handleImageResize is now imported from canvasElementUtils.js
  const handleImageResize = (id, width, height) => {
    handleImageResizeUtil({
      id,
      width,
      height,
      setImages,
      saveToHistory,
    });
  };

  const stageWidth = mainCanvasWidth;
  const stageHeight = fixedStageHeightPx;
  // No offset; handle should sit at exact stage bottom
  const RESIZE_BOTTOM_SAFE = 0;

  const hasAnySelection =
    (selectedObjects && selectedObjects.length > 0) ||
    (selectedObject && selectedObject.id);
  const DEBUG_OCCLUSION = false;
  const selectedForOcclusionDemotion =
    selectedObjects && selectedObjects.length > 0
      ? selectedObjects
      : selectedObject
        ? [selectedObject]
        : [];
  const isTopLayerImageSelection = (obj) =>
    obj?.type === "Image" && obj?.isOnTopLayer === true;
  const isTextSelectionActivelyDragging = selectedForOcclusionDemotion.some(
    (obj) => {
      const isTextObj =
        obj?.id && (obj?.type === "Text" || obj?.type?.includes?.("Text"));
      if (!isTextObj) return false;

      const mainNode = stageRef?.current?.findOne?.(`#${obj.id}`);
      const offNode = offCanvasStageRef?.current?.findOne?.(`#${obj.id}`);
      const topNode = topLayerStageRef?.current?.findOne?.(`#${obj.id}`);
      const node = mainNode || offNode || topNode;
      if (!node) return false;

      return Boolean(
        (typeof node.isDragging === "function" && node.isDragging()) ||
        node.getAttr?.("_isHtmlDragging") ||
        node.getAttr?.("_isDragging"),
      );
    },
  );
  const shouldDemoteTopLayerImages =
    hasAnySelection &&
    selectedForOcclusionDemotion.some((obj) => !isTopLayerImageSelection(obj));
  const selectionHasMainCanvasElements =
    selectedForOcclusionDemotion.length > 0 &&
    selectedForOcclusionDemotion.some((obj) => isElementInMainCanvasArray(obj));
  const selectionHasOffCanvasElements =
    selectedForOcclusionDemotion.length > 0 &&
    selectedForOcclusionDemotion.some((obj) => isElementInOffCanvasArray(obj));
  const shouldDemoteMainTopLayerImages =
    shouldDemoteTopLayerImages && selectionHasMainCanvasElements;
  // Mirror main-canvas logic: only demote off-canvas top-layer images when a NON-top-layer
  // off-canvas element is selected.  When the top-layer image itself is selected we keep it
  // in offCanvasImagesTop so it continues to render in the high-z-index top-layer wrapper and
  // HTML content behind it stays hidden.  ImageElement.jsx provides a local transformer for
  // the selected top-layer image; the top-layer Stage/Layer is made interactive (listening)
  // only while a top-layer image is selected so those handles are usable.
  const shouldDemoteOffCanvasTopLayerImages =
    shouldDemoteTopLayerImages && selectionHasOffCanvasElements;

  useEffect(() => {
    if (!DEBUG_OCCLUSION) return;
    if (!hasAnySelection) return;

    const selectedDebug =
      selectedForOcclusionDemotion?.map((obj) => ({
        id: obj?.id,
        type: obj?.type,
        isOnTopLayer: obj?.isOnTopLayer === true,
        inMain: isElementInMainCanvasArray(obj),
        inOff: isElementInOffCanvasArray(obj),
      })) || [];
  }, [
    DEBUG_OCCLUSION,
    hasAnySelection,
    selectedForOcclusionDemotion,
    shouldDemoteTopLayerImages,
    isTextSelectionActivelyDragging,
    selectionHasMainCanvasElements,
    selectionHasOffCanvasElements,
    shouldDemoteMainTopLayerImages,
    shouldDemoteOffCanvasTopLayerImages,
    offCanvasImages,
    images,
  ]);

  const demoteTopLayerImageForSelection = (img, targetCanvas) => {
    // Never demote a top-layer image that is itself part of the current selection.
    // When a top-layer image is selected (alone or alongside other elements) it must
    // stay in the top-layer stage so its transformer handles remain interactive and
    // the HTML content beneath it stays correctly occluded.
    const isImgSelected = selectedForOcclusionDemotion.some(
      (obj) => obj?.id === img?.id,
    );
    if (isImgSelected) return img;

    return img?.isOnTopLayer &&
      ((targetCanvas === "main" && shouldDemoteMainTopLayerImages) ||
        (targetCanvas === "offCanvas" && shouldDemoteOffCanvasTopLayerImages))
      ? (() => {
          return {
            ...img,
            isOnTopLayer: false,
            // Keep occlusion behavior when temporarily rendered as non-top-layer.
            _forceOcclusionBlocker: true,
          };
        })()
      : img;
  };

  const renderedMainImages = (images || []).map((img) =>
    demoteTopLayerImageForSelection(img, "main"),
  );
  const renderedOffCanvasImages = (offCanvasImages || []).map((img) =>
    demoteTopLayerImageForSelection(img, "offCanvas"),
  );

  const selectionOcclusionImages = renderedMainImages.filter(
    (img) => img._forceOcclusionBlocker === true,
  );
  const offCanvasSelectionOcclusionImages = renderedOffCanvasImages.filter(
    (img) => img._forceOcclusionBlocker === true,
  );
  const mainCanvasImagesBottom = renderedMainImages.filter(
    (img) => !img.isOnTopLayer && img._forceOcclusionBlocker !== true,
  );
  // Top-layer ordering rule: keep earlier (older) top-layer images above newer ones.
  // Konva draws later nodes on top, so reverse the filtered list to render older items last.
  const mainCanvasImagesTop = renderedMainImages
    .filter((img) => img.isOnTopLayer)
    .reverse();
  const offCanvasImagesBottom = renderedOffCanvasImages.filter(
    (img) => !img.isOnTopLayer && img._forceOcclusionBlocker !== true,
  );
  const offCanvasImagesTop = renderedOffCanvasImages
    .filter((img) => img.isOnTopLayer)
    .reverse();
    
  // DEBUG: Log image classification
  renderedOffCanvasImages.forEach((img) => {
    if (img.id === 'marker-1777364582910-3-1182cf99-9539-434a-a049-2145d946ce86') {
      console.log("DEBUG: Image classification:", {
        id: img.id,
        isOnTopLayer: img.isOnTopLayer,
        _forceOcclusionBlocker: img._forceOcclusionBlocker,
        inBottomArray: offCanvasImagesBottom.some(i => i.id === img.id),
        inTopArray: offCanvasImagesTop.some(i => i.id === img.id)
      });
    }
  });
  const selectedTopPriorityImageIds = new Set([
    ...(selectedObject?.type === "Image" && selectedObject?.id
      ? [selectedObject.id]
      : []),
    ...(selectedObjects || [])
      .filter((obj) => obj?.type === "Image" && obj?.id)
      .map((obj) => obj.id),
  ]);

  // True when the currently-selected object is an off-canvas top-layer image that has NOT been
  // demoted (i.e. it is still in offCanvasImagesTop and therefore rendered in the high-z-index
  // top-layer wrapper Stage).  Used to conditionally enable listening / pointer-events on that
  // Stage so that ImageElement's local transformer handles become interactive.
  const hasSelectedOffCanvasTopLayerImage =
    selectedObject?.type === "Image" &&
    selectedObject?.isOnTopLayer === true &&
    offCanvasImagesTop.some((img) => img.id === selectedObject.id);

  // CRITICAL: Show top-layer Stage when dragging off→main with off-canvas top-layer images.
  // Top-layer group-drag previews render inside this Stage; without it they never appear.
  const hasMainTargetTopLayerPreviews = (groupDragPreviews || []).some(
    (p) =>
      p.target === "main" &&
      p.type === "Image" &&
      p.data?.isOnTopLayer === true,
  );
  const hasOffCanvasTargetTopLayerPreviews = (groupDragPreviews || []).some(
    (p) =>
      p.target === "offCanvas" &&
      p.type === "Image" &&
      p.data?.isOnTopLayer === true,
  );
  const activeOffCanvasTopLayerPreviewSourceIds = new Set(
    (groupDragPreviews || [])
      .filter(
        (p) =>
          p?.target === "offCanvas" &&
          p?.type === "Image" &&
          p?.data?.isOnTopLayer === true,
      )
      .map((p) => p?.data?.id?.replace("-ghost", ""))
      .filter(Boolean),
  );
  const activeGroupDragElementIds = new Set(
    isGroupDraggingRef.current
      ? (individualElementDragStartRef.current?.elementPositions || [])
          .map((p) => p?.id)
          .filter(Boolean)
      : [],
  );

  // CRITICAL FIX: Also check single dragPreview for top-layer images targeting main canvas
  // This ensures the top-layer Stage is shown when dragging off-canvas top-layer images to main
  const hasSingleDragPreviewForTopLayer =
    dragPreview &&
    dragPreview.target === "main" &&
    dragPreview.type === "Image" &&
    dragPreview.data?.isOnTopLayer === true;

  const shouldShowTopLayerStage =
    mainCanvasImagesTop.length > 0 ||
    selectionOcclusionImages.length > 0 ||
    hasMainTargetTopLayerPreviews ||
    (dragPreview &&
      dragPreview.target === "main" &&
      dragPreview.type === "Image" &&
      dragPreview.data?.isOnTopLayer === true);

  // CRITICAL: When multi-select is on, top layer has pointer-events: none so clicks hit Background.
  // This helper lets Background treat a click as "on a top-layer image" and add it to selection.
  const getTopLayerImageAtClick = useCallback(
    (clientX, clientY) => {
      const topLayerStage = topLayerStageRef?.current;
      if (!topLayerStage) return null;
      const topLayerContainer = topLayerStage.container();
      const topLayerRect = topLayerContainer.getBoundingClientRect();
      if (
        clientX < topLayerRect.left ||
        clientX > topLayerRect.right ||
        clientY < topLayerRect.top ||
        clientY > topLayerRect.bottom
      )
        return null;
      const topLayerX = clientX - topLayerRect.left;
      const topLayerY = clientY - topLayerRect.top;

      // Primary hit test: rely on Konva's rendered hit graph so crop/rotation/offset
      // and z-order are respected. This catches real top-layer clicks that bbox math misses.
      try {
        const hitNode = topLayerStage.getIntersection({ x: topLayerX, y: topLayerY });
        const hitId =
          hitNode?.attrs?.id ??
          (typeof hitNode?.id === "function" ? hitNode.id() : null);
        if (hitId) {
          const hitImage =
            mainCanvasImagesTop.find((img) => img.id === hitId) ||
            images.find((img) => img.id === hitId);
          if (hitImage) return hitImage;
        }
      } catch (_err) {
        // Fall through to geometric fallback below.
      }

      // Secondary fallback: query rendered top-layer Konva Image nodes directly.
      // This avoids misses when derived arrays are temporarily stale.
      try {
        const imageNodes = topLayerStage.find("Image");
        for (let i = imageNodes.length - 1; i >= 0; i -= 1) {
          const node = imageNodes[i];
          if (!node) continue;
          const rect = node.getClientRect({ relativeTo: topLayerStage });
          const isWithin =
            topLayerX >= rect.x &&
            topLayerX <= rect.x + rect.width &&
            topLayerY >= rect.y &&
            topLayerY <= rect.y + rect.height;
          if (!isWithin) continue;
          const nodeId =
            node?.attrs?.id ?? (typeof node?.id === "function" ? node.id() : null);
          if (!nodeId) continue;
          const nodeImage =
            mainCanvasImagesTop.find((img) => img.id === nodeId) ||
            images.find((img) => img.id === nodeId);
          if (nodeImage) return nodeImage;
        }
      } catch (_err) {
        // Fall through to geometric fallback below.
      }

      // Fallback geometric test for edge cases where intersection can be null transiently.
      for (const img of mainCanvasImagesTop) {
        const imgX = img.x;
        const imgY = img.y;
        const imgWidth = img.width || 0;
        const imgHeight = img.height || 0;
        const imgRotation = img.rotation || 0;
        const imgScaleX = Math.abs(img.scaleX || 1);
        const imgScaleY = Math.abs(img.scaleY || 1);
        const displayedWidth = imgWidth * imgScaleX;
        const displayedHeight = imgHeight * imgScaleY;
        const useTopLeftAnchor = img.useTopLeftAnchor === true;
        let imgLeft, imgTop, imgRight, imgBottom;
        if (useTopLeftAnchor) {
          imgLeft = imgX;
          imgTop = imgY;
          imgRight = imgX + displayedWidth;
          imgBottom = imgY + displayedHeight;
        } else {
          imgLeft = imgX - displayedWidth / 2;
          imgTop = imgY - displayedHeight / 2;
          imgRight = imgX + displayedWidth / 2;
          imgBottom = imgY + displayedHeight / 2;
        }
        let isWithinBounds = false;
        if (imgRotation === 0 || imgRotation === 360) {
          isWithinBounds =
            topLayerX >= imgLeft &&
            topLayerX <= imgRight &&
            topLayerY >= imgTop &&
            topLayerY <= imgBottom;
        } else {
          const centerX = useTopLeftAnchor ? imgX + displayedWidth / 2 : imgX;
          const centerY = useTopLeftAnchor ? imgY + displayedHeight / 2 : imgY;
          const cos = Math.abs(Math.cos((imgRotation * Math.PI) / 180));
          const sin = Math.abs(Math.sin((imgRotation * Math.PI) / 180));
          const rotatedWidth = displayedWidth * cos + displayedHeight * sin;
          const rotatedHeight = displayedWidth * sin + displayedHeight * cos;
          const rotatedLeft = centerX - rotatedWidth / 2;
          const rotatedTop = centerY - rotatedHeight / 2;
          const rotatedRight = centerX + rotatedWidth / 2;
          const rotatedBottom = centerY + rotatedHeight / 2;
          isWithinBounds =
            topLayerX >= rotatedLeft &&
            topLayerX <= rotatedRight &&
            topLayerY >= rotatedTop &&
            topLayerY <= rotatedBottom;
        }
        if (isWithinBounds) return img;
      }
      return null;
    },
    [mainCanvasImagesTop, images],
  );

  const onTopLayerImageClickedInMultiSelect = useCallback(
    (imageObj) => {
      if (!imageObj?.id) return;

      // Cross-canvas multi-select: do not add main canvas image when current selection is from off-canvas
      if (selectedObjects?.length > 0 || selectedObject) {
        const isElementInOffCanvas = (elementId, elementType) => {
          if (!elementId || !elementType) return false;
          switch (elementType) {
            case "Rect":
              return offCanvasRectangles.some((r) => r.id === elementId);
            case "Circle":
              return offCanvasCircles.some((c) => c.id === elementId);
            case "Image":
              return offCanvasImages.some((i) => i.id === elementId);
            case "Text":
              return offCanvasTexts.some((t) => t.id === elementId);
            case "Arrow":
              return offCanvasArrows.some((a) => a.id === elementId);
            case "Line":
              return offCanvasLines.some((l) => l.id === elementId);
            case "DoubleSidedArrow":
              return offCanvasDoubleSidedArrows.some((d) => d.id === elementId);
            default:
              return false;
          }
        };
        const allSelected = [...(selectedObjects || [])];
        if (
          selectedObject &&
          !allSelected.some(
            (o) => o.id === selectedObject.id && o.type === selectedObject.type,
          )
        ) {
          allSelected.push(selectedObject);
        }
        const anyFromOffCanvas = allSelected.some((obj) =>
          isElementInOffCanvas(obj.id, obj.type),
        );
        if (anyFromOffCanvas) return; // Do not add main canvas image when selection is from off-canvas
      }

      const newSelected = { ...imageObj, type: "Image" };
      setSelectedObjects((prev) => {
        const current = prev && Array.isArray(prev) ? [...prev] : [];
        if (current.some((o) => o && o.id === imageObj.id)) return current;
        return [...current, newSelected];
      });
      setSelectedObject(newSelected);
      setSelectedImage(imageObj);
      setSelectedText(null);
      setShowTextBox(false);
      setSelectedArrow(null);
      setSelectedDoubleSidedArrow(null);
      setSelectedLine(null);
    },
    [
      setSelectedObjects,
      setSelectedObject,
      setSelectedImage,
      setSelectedText,
      setShowTextBox,
      setSelectedArrow,
      setSelectedDoubleSidedArrow,
      setSelectedLine,
      selectedObjects,
      selectedObject,
      offCanvasRectangles,
      offCanvasCircles,
      offCanvasImages,
      offCanvasTexts,
      offCanvasArrows,
      offCanvasLines,
      offCanvasDoubleSidedArrows,
    ],
  );

  const onTopLayerImageClicked = useCallback(
    (imageObj) => {
      if (!imageObj?.id) return;
      const nextSelected = { ...imageObj, type: "Image" };
      setSelectedObject(nextSelected);
      setSelectedObjects([]);
      setSelectedImage(imageObj);
      setSelectedText(null);
      setShowTextBox(false);
      setSelectedArrow(null);
      setSelectedDoubleSidedArrow(null);
      setSelectedLine(null);
    },
    [
      setSelectedObject,
      setSelectedObjects,
      setSelectedImage,
      setSelectedText,
      setShowTextBox,
      setSelectedArrow,
      setSelectedDoubleSidedArrow,
      setSelectedLine,
    ],
  );

  // Update top-layer stage pointer events based on selection state
  useEffect(() => {
    const topLayerStage = topLayerStageRef?.current;
    if (!topLayerStage) return;

    const stage = topLayerStage.getStage?.();
    if (!stage) return;

    const container = stage.container();
    if (!container) return;

    // Enable pointer events when a top-layer image is selected (for transformer interaction)
    // OR when there are top-layer images present (to allow selection)
    // Disable only when no top-layer images exist (to prevent blocking text elements)
    // CRITICAL FIX: Don't change pointer events during active dragging to prevent interference
    // CRITICAL FIX: Also disable during selection to allow selection box to work properly
    // CRITICAL FIX: When multiple elements are selected, use "none" so mousedown reaches main
    // stage and user can draw a selection box (e.g. to unselect the top-layer images)
    const hasSelectedTopLayerImage =
      selectedObject?.type === "Image" && selectedObject?.isOnTopLayer === true;
    const hasTopLayerImages =
      mainCanvasImagesTop && mainCanvasImagesTop.length > 0;
    const isCurrentlySelecting = isSelecting;
    const isMultiSelected = selectedObjects && selectedObjects.length > 1;
    const pointerEventsValue =
      (hasSelectedTopLayerImage || hasTopLayerImages) &&
      !isCurrentlySelecting &&
      !isMultiSelected
        ? "auto"
        : "none";

    // Keep top-layer stage above main stage while rendering main-target drag preview,
    // so mirrored preview + transformer are not hidden by main-stage selected layers.
    const hasMainTargetSinglePreview =
      dragPreview?.target === "main" && !!dragPreview?.data;
    // CRITICAL FIX: Also update z-index during selection to allow selection box (z-index 30) to be visible
    const zIndexValue = hasMainTargetSinglePreview
      ? "30"
      : isGroupDraggingRef.current || isCurrentlySelecting
        ? "24"
        : "26";

    // Only update if not currently dragging
    const isCurrentlyDragging = isGroupDraggingRef.current;
    if (!isCurrentlyDragging) {
      // Update z-index
      container.style.setProperty("z-index", zIndexValue, "important");

      // Update pointer events
      container.style.setProperty(
        "pointer-events",
        pointerEventsValue,
        "important",
      );

      const canvas = container.querySelector("canvas");
      if (canvas) {
        canvas.style.setProperty(
          "pointer-events",
          pointerEventsValue,
          "important",
        );
      }
    }
  }, [
    selectedObject?.id,
    selectedObject?.type,
    selectedObject?.isOnTopLayer,
    isSelecting,
    selectedObjects?.length,
    dragPreview?.target,
    dragPreview?.data?.id,
  ]);

  // CRITICAL: Sync top layer stage position with main stage position
  useEffect(() => {
    if (
      !shouldShowTopLayerStage ||
      !topLayerStageRef.current ||
      !stageRef.current
    ) {
      return;
    }

    const syncStagePositions = () => {
      const mainStageContainer = stageRef.current?.container();
      const topLayerStageContainer = topLayerStageRef.current?.container();

      if (!mainStageContainer || !topLayerStageContainer) {
        return;
      }

      const mainRect = mainStageContainer.getBoundingClientRect();
      const containerRect =
        mainStageContainerRef.current?.getBoundingClientRect();

      if (!containerRect) return;

      // Find the React-created wrapper div for the top layer Stage
      const container = mainStageContainerRef.current;
      if (!container) return;

      // Find the wrapper div by data attribute (most reliable)
      let topLayerWrapper = container.querySelector(
        '[data-top-layer-wrapper="true"]',
      );

      // Fallback: find by traversing up from the Konva container
      if (!topLayerWrapper) {
        let current = topLayerStageContainer.parentElement;
        while (current && current !== container && current !== document.body) {
          if (current.parentElement === container) {
            topLayerWrapper = current;
            break;
          }
          current = current.parentElement;
        }
      }

      if (topLayerWrapper) {
        // CRITICAL: Align the Konva Stage elements themselves, not just the wrapper divs
        // The main stage container has the main Konva Stage as a direct child
        // The top layer wrapper contains the top layer Konva Stage
        // We need to ensure the Konva Stage elements are at the same position

        // Get the actual Konva Stage containers (canvas elements)
        const mainStageKonvaContainer = mainStageContainer; // This is the Konva container from stageRef.current?.container()
        const topLayerStageKonvaContainer = topLayerStageContainer; // This is the Konva container from topLayerStageRef.current?.container()

        if (mainStageKonvaContainer && topLayerStageKonvaContainer) {
          // Get the positions of the Konva containers relative to their parent containers
          const mainStageKonvaRect =
            mainStageKonvaContainer.getBoundingClientRect();
          const topLayerKonvaRect =
            topLayerStageKonvaContainer.getBoundingClientRect();

          // Get the parent container (mainStageContainerRef.current)
          const parentContainer = mainStageContainerRef?.current;
          if (parentContainer) {
            const parentRect = parentContainer.getBoundingClientRect();

            // Calculate the main stage Konva container's position relative to parent
            const mainStageOffsetLeft =
              mainStageKonvaRect.left - parentRect.left;
            const mainStageOffsetTop = mainStageKonvaRect.top - parentRect.top;

            // Position the top layer wrapper so its Konva Stage aligns with the main stage's Konva Stage
            // The top layer wrapper needs to be positioned such that the Konva Stage inside it
            // is at the same position as the main stage's Konva Stage
            topLayerWrapper.style.setProperty(
              "position",
              "absolute",
              "important",
            );
            topLayerWrapper.style.setProperty(
              "top",
              `${mainStageOffsetTop}px`,
              "important",
            );
            topLayerWrapper.style.setProperty(
              "left",
              `${mainStageOffsetLeft}px`,
              "important",
            );
            topLayerWrapper.style.setProperty("margin", "0", "important");
            topLayerWrapper.style.setProperty("padding", "0", "important");
            const wrapperZIndex =
              dragPreview?.target === "main" && dragPreview?.data ? "30" : "26";
            topLayerWrapper.style.setProperty(
              "z-index",
              wrapperZIndex,
              "important",
            );
            topLayerWrapper.style.setProperty("transform", "none", "important");
            topLayerWrapper.style.setProperty(
              "visibility",
              "visible",
              "important",
            );
            topLayerWrapper.style.setProperty("opacity", "1", "important");

            // CRITICAL: Check padding values - they MUST match for coordinate alignment
            // Note: Konva's padding() method might not exist, so we'll calculate expected padding
            // and verify stage dimensions match
            const expectedPadding = parseInt(canvasHeight) < 30 ? 10 : 20;
            const mainStagePadding =
              stageRef.current?.padding?.() ?? expectedPadding;
            const topLayerStagePadding =
              topLayerStageRef.current?.padding?.() ?? expectedPadding;
            const paddingMatch = mainStagePadding === topLayerStagePadding;

            // CRITICAL: Verify stage dimensions match - if they don't, coordinate systems won't align
            const mainStageWidth = stageRef.current?.width() || 0;
            const mainStageHeight = stageRef.current?.height() || 0;
            const topLayerStageWidth = topLayerStageRef.current?.width() || 0;
            const topLayerStageHeight = topLayerStageRef.current?.height() || 0;
            const dimensionsMatch =
              mainStageWidth === topLayerStageWidth &&
              mainStageHeight === topLayerStageHeight;
          }
        }

        // Set dimensions to match main stage
        const mainWidth = stageRef.current?.width() || mainCanvasWidth;
        const mainHeight = stageRef.current?.height() || 0;
        topLayerWrapper.style.setProperty(
          "width",
          `${mainWidth}px`,
          "important",
        );
        topLayerWrapper.style.setProperty(
          "height",
          `${mainHeight}px`,
          "important",
        );

        // Verify alignment (for debugging)
        const topLayerRect = topLayerStageContainer.getBoundingClientRect();
        const wrapperRectForDebug = topLayerWrapper.getBoundingClientRect();
      } else {
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      syncStagePositions();
      // Also sync after a short delay to catch any late renders
      setTimeout(syncStagePositions, 0);
      setTimeout(syncStagePositions, 50);
      setTimeout(syncStagePositions, 100);
    });

    // Also sync on window resize and scroll
    window.addEventListener("resize", syncStagePositions);
    window.addEventListener("scroll", syncStagePositions, true);

    // Use MutationObserver to watch for DOM changes
    const observer = new MutationObserver(syncStagePositions);
    if (mainStageContainerRef.current) {
      observer.observe(mainStageContainerRef.current, {
        attributes: true,
        attributeFilter: ["style"],
        childList: true,
        subtree: true,
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", syncStagePositions);
      window.removeEventListener("scroll", syncStagePositions, true);
      observer.disconnect();
    };
  }, [
    shouldShowTopLayerStage,
    stageRef,
    topLayerStageRef,
    mainStageContainerRef,
  ]);

  // CRITICAL: Keep top-layer Stage transform in lockstep with main Stage.
  // The top-layer images + their Transformer render in a separate Stage; if the main stage
  // pans/zooms (Stage drag or wheel zoom), we must mirror x/y/scale so the transformer stays visible.
  useEffect(() => {
    if (
      !shouldShowTopLayerStage ||
      !topLayerStageRef?.current ||
      !stageRef?.current
    ) {
      return;
    }

    const mainStage = stageRef.current;
    const topStage = topLayerStageRef.current;

    const syncKonvaTransform = () => {
      try {
        const mainPos = mainStage.position();
        const mainScale = mainStage.scale();
        const mainRotation =
          typeof mainStage.rotation === "function" ? mainStage.rotation() : 0;

        topStage.position(mainPos);
        topStage.scale(mainScale);
        if (typeof topStage.rotation === "function") {
          topStage.rotation(mainRotation || 0);
        }

        // Ensure transformer + images redraw immediately.
        topStage.batchDraw();
      } catch (e) {
        // Ignore transient Konva lifecycle issues
      }
    };

    // Initial sync (covers selection while already panned/zoomed).
    syncKonvaTransform();

    // Mirror changes from main stage.
    mainStage.on("dragmove", syncKonvaTransform);
    mainStage.on("dragend", syncKonvaTransform);
    mainStage.on("xChange yChange", syncKonvaTransform);
    mainStage.on("scaleXChange scaleYChange", syncKonvaTransform);

    return () => {
      mainStage.off("dragmove", syncKonvaTransform);
      mainStage.off("dragend", syncKonvaTransform);
      mainStage.off("xChange yChange", syncKonvaTransform);
      mainStage.off("scaleXChange scaleYChange", syncKonvaTransform);
    };
  }, [shouldShowTopLayerStage, stageRef, topLayerStageRef]);

  useEffect(() => {
    if (!mainCanvasImagesTop.length) {
      if (isPointerOverTopLayerImageRef.current) {
        isPointerOverTopLayerImageRef.current = false;
        setIsPointerOverTopLayerImage(false);
      }
      return;
    }

    const resetPointerState = () => {
      if (isPointerOverTopLayerImageRef.current) {
        isPointerOverTopLayerImageRef.current = false;
        setIsPointerOverTopLayerImage(false);
      }
    };

    const updatePointerState = (event) => {
      const currentStage = topLayerStageRef.current;
      if (!currentStage) {
        resetPointerState();
        return;
      }

      const container = currentStage.container();
      if (!container) {
        resetPointerState();
        return;
      }

      const rect = container.getBoundingClientRect();
      const withinBounds =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!withinBounds) {
        resetPointerState();
        return;
      }

      const pointerPos = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      let intersection = null;
      try {
        intersection = currentStage.getIntersection(pointerPos, "Shape");
      } catch (err) {
        intersection = null;
      }

      const overImage =
        !!intersection &&
        intersection.getClassName() !== "Transformer" &&
        intersection.getAttrs()?.id !== "resize-handle";

      if (overImage !== isPointerOverTopLayerImageRef.current) {
        isPointerOverTopLayerImageRef.current = overImage;
        setIsPointerOverTopLayerImage(overImage);
      }
    };

    window.addEventListener("pointermove", updatePointerState, {
      passive: true,
    });
    window.addEventListener("pointerover", updatePointerState, {
      passive: true,
    });
    window.addEventListener("pointerdown", updatePointerState, {
      passive: true,
    });
    window.addEventListener("pointerleave", resetPointerState);
    window.addEventListener("blur", resetPointerState);

    return () => {
      window.removeEventListener("pointermove", updatePointerState);
      window.removeEventListener("pointerover", updatePointerState);
      window.removeEventListener("pointerdown", updatePointerState);
      window.removeEventListener("pointerleave", resetPointerState);
      window.removeEventListener("blur", resetPointerState);
    };
  }, [mainCanvasImagesTop.length]);

  // CRITICAL FIX: Update pointer events on top layer stage when pointer position changes
  // This ensures TextElements and other non-image elements can be dragged when pointer is not over a top-layer image
  // and when a non-image element (like UnifiedLineArrowElement orbs) is selected.
  useEffect(() => {
    if (!topLayerStageRef.current) return;

    const updatePointerEvents = () => {
      const container = topLayerStageRef.current?.container();
      if (!container) return;

      const hasSelectedTopLayerImage =
        selectedObject?.type === "Image" &&
        selectedObject?.isOnTopLayer === true;
      const isCurrentlySelecting = isSelecting;
      const hasActiveNonImageSelection =
        (selectedObject &&
          selectedObject.type &&
          selectedObject.type !== "Image") ||
        !!selectedLine ||
        !!selectedArrow ||
        !!selectedDoubleSidedArrow;
      // Only enable pointer events when pointer is over a top-layer image OR when a top-layer image is selected,
      // and there is no active non-image selection that should receive interactions instead.
      const pointerEventsValue =
        !hasActiveNonImageSelection &&
        (hasSelectedTopLayerImage || isPointerOverTopLayerImage) &&
        !isCurrentlySelecting
          ? "auto"
          : "none";

      container.style.setProperty(
        "pointer-events",
        pointerEventsValue,
        "important",
      );

      const canvas = container.querySelector("canvas");
      if (canvas) {
        canvas.style.setProperty(
          "pointer-events",
          pointerEventsValue,
          "important",
        );
      }
    };

    updatePointerEvents();
  }, [
    isPointerOverTopLayerImage,
    selectedObject,
    selectedLine,
    selectedArrow,
    selectedDoubleSidedArrow,
    isSelecting,
  ]);

  useEffect(() => {
    // Check if the transformer and stage refs are valid
    if (transformerRef.current && stageRef.current) {
      const nodesToTransform = [];

      // If an image is selected, find its corresponding Konva node on the stage
      if (selectedImage) {
        // Find the actual Konva Node instance for the selected image by its ID.
        // This is crucial because `selectedImage` in state might just be an object with properties,
        // not the actual Konva node itself.
        const imageNode = stageRef.current.findOne(`#${selectedImage.id}`);
        if (imageNode) {
          nodesToTransform.push(imageNode);
        } else {
          // Optional: Log if the node isn't found, indicates a potential mismatch
        }
      }

      // You should extend this logic for other selectable shapes (rectangles, circles, text, etc.)
      // if they share the same transformer. For example:
      // if (selectedRectangle) {
      //     const rectNode = stageRef.current.findOne(`#${selectedRectangle.id}`);
      //     if (rectNode) {
      //         nodesToTransform.push(rectNode);
      //     }
      // }
      // if (selectedText) {
      //     const textNode = stageRef.current.findOne(`#${selectedText.id}`);
      //     if (textNode) {
      //         nodesToTransform.push(textNode);
      //     }
      // }

      // Set the nodes for the transformer
      transformerRef.current.nodes(nodesToTransform);

      // Redraw the transformer's layer to apply changes immediately
      // Only attempt to redraw if the layer exists to prevent errors during unmount/initial render
      if (transformerRef.current.getLayer()) {
        transformerRef.current.getLayer().batchDraw();
      }
    }
    // Dependencies: Rerun this effect whenever selectedImage or transformerRef changes
  }, [selectedImage, transformerRef, stageRef]); // Ensure stageRef is also a dependency if it can change dynamically

  // Handle keydown/keyup for spacebar panning and Escape for canceling paste mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "Space") {
        // Prevent default to avoid browser scrolling/zooming when space is pressed
        // Only prevent if not in an input/textarea/contenteditable
        const target = e.target;
        const isTextInput =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable;
        if (!isTextInput) {
          e.preventDefault();
        }
        setSpacePressed(true);
      } else if (
        e.key === "Escape" &&
        isWaitingForPaste &&
        setIsWaitingForPaste
      ) {
        // Cancel paste mode on Escape
        setIsWaitingForPaste(false);
        e.preventDefault();
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === "Space") {
        // Prevent default to avoid browser zoom gestures on space release
        const target = e.target;
        const isTextInput =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable;
        if (!isTextInput) {
          e.preventDefault();
        }
        setSpacePressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isWaitingForPaste]);

  // Prevent native browser drag behavior on Konva canvases.
  // Without this, Chrome can start an HTML5 drag on the <canvas> element (green "+" cursor),
  // which steals the pointer stream and makes it look like only the transformer is moving.
  useEffect(() => {
    const stageContainers = [
      stageRef?.current?.container?.(),
      offCanvasStageRef?.current?.container?.(),
      topLayerStageRef?.current?.container?.(),
    ].filter(Boolean);

    if (stageContainers.length === 0) return;

    const preventNativeDrag = (e) => {
      // Only block drags originating inside Konva containers.
      // This avoids impacting external drag/drop interactions outside the canvas.
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    stageContainers.forEach((container) => {
      try {
        // Prevent dragging the canvas element as an image.
        container.addEventListener("dragstart", preventNativeDrag, {
          capture: true,
        });

        // Mark canvases as not draggable (belt + suspenders).
        const canvases = container.querySelectorAll("canvas");
        canvases.forEach((c) => {
          try {
            c.setAttribute("draggable", "false");
          } catch (e) {
            // ignore
          }
        });
      } catch (e) {
        // ignore
      }
    });

    return () => {
      stageContainers.forEach((container) => {
        try {
          container.removeEventListener("dragstart", preventNativeDrag, {
            capture: true,
          });
        } catch (e) {
          // ignore
        }
      });
    };
  }, [stageRef, offCanvasStageRef, topLayerStageRef]);

  // Update circle transformer key when maintainAspectRatio changes
  useEffect(() => {
    if (selectedObject && selectedObject?.type === "Circle") {
      const currentMaintainAspectRatio = selectedObject?.maintainAspectRatio;
      const prevMaintainAspectRatio = prevMaintainAspectRatioRef.current;

      // Only update if maintainAspectRatio actually changed
      if (currentMaintainAspectRatio !== prevMaintainAspectRatio) {
        prevMaintainAspectRatioRef.current = currentMaintainAspectRatio;

        // CRITICAL: Detach transformer before updating key to force clean remount
        if (transformerRef.current) {
          try {
            transformerRef.current.detach();
          } catch (e) {
            console.error("Error detaching main transformer", e);
          }
        }
        if (offCanvasTransformerRef.current) {
          try {
            offCanvasTransformerRef.current.detach();
          } catch (e) {
            console.error("Error detaching off-canvas transformer", e);
          }
        }

        setCircleTransformerKey((prev) => {
          return prev + 1;
        });
      }
    } else {
      // Reset ref when no circle is selected
      prevMaintainAspectRatioRef.current = null;
    }
  }, [
    selectedObject?.maintainAspectRatio,
    selectedObject?.type,
    selectedObject?.id,
  ]);

  // CRITICAL FIX: Similar useEffect for rectangles to update transformer anchors when maintainAspectRatio changes
  useEffect(() => {
    if (
      selectedObject &&
      (selectedObject?.type === "Rect" || selectedObject?.type === "Rectangle")
    ) {
      const currentMaintainAspectRatio = selectedObject?.maintainAspectRatio;
      const prevMaintainAspectRatio = prevRectMaintainAspectRatioRef.current;

      // Only update if maintainAspectRatio actually changed
      if (currentMaintainAspectRatio !== prevMaintainAspectRatio) {
        prevRectMaintainAspectRatioRef.current = currentMaintainAspectRatio;

        // CRITICAL: Detach transformer before updating key to force clean remount
        if (transformerRef.current) {
          try {
            transformerRef.current.detach();
          } catch (e) {
            console.error("Error detaching main transformer", e);
          }
        }
        if (offCanvasTransformerRef.current) {
          try {
            offCanvasTransformerRef.current.detach();
          } catch (e) {
            console.error("Error detaching off-canvas transformer", e);
          }
        }

        setRectangleTransformerKey((prev) => {
          return prev + 1;
        });
      }
    } else {
      // Reset ref when no rectangle is selected
      prevRectMaintainAspectRatioRef.current = null;
    }
  }, [
    selectedObject?.maintainAspectRatio,
    selectedObject?.type,
    selectedObject?.id,
  ]);

  // CRITICAL FIX: Force transformer to recreate anchors after key change
  useEffect(() => {
    if (
      selectedObject &&
      (selectedObject?.type === "Rect" ||
        selectedObject?.type === "Rectangle" ||
        selectedObject?.type === "Circle")
    ) {
      // Clone/duplicate placement: do not detach/reattach here — it briefly restores default anchors
      // and fights useTransformerManager + main Transformer enabledAnchors=[].
      if (isDuplicatePlacementActive) {
        return undefined;
      }
      // Wait for transformer to mount/remount, then force anchor recreation
      const timer = setTimeout(() => {
        const anchors =
          selectedObject?.maintainAspectRatio === true
            ? ["bottom-right"]
            : ["middle-right", "bottom-center", "bottom-right"];

        if (transformerRef.current && transformerRef.current.getNode()) {
          // console.log('🔧 [TRANSFORMER-POST-MOUNT] Forcing anchor update', {
          //   id: selectedObject?.id,
          //   type: selectedObject?.type,
          //   maintainAspectRatio: selectedObject?.maintainAspectRatio,
          //   anchors
          // });
          try {
            // Detach and reattach to force anchor recreation
            const nodes = transformerRef.current.nodes();
            transformerRef.current.nodes([]);
            transformerRef.current.setAttrs({
              enabledAnchors: anchors,
              keepRatio: selectedObject?.maintainAspectRatio || false,
            });
            transformerRef.current.nodes(nodes);
            transformerRef.current.forceUpdate();
            const layer = transformerRef.current.getLayer();
            if (layer) {
              layer.batchDraw();
            }
          } catch (e) {
            console.error("Error in post-mount anchor update", e);
          }
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [
    rectangleTransformerKey,
    circleTransformerKey,
    selectedObject?.id,
    selectedObject?.type,
    selectedObject?.maintainAspectRatio,
    isDuplicatePlacementActive,
  ]);

  // Mouse event handlers for panning - using extracted handlers
  const panHandlers = createPanHandlers({
    spacePressed,
    action,
    ACTIONS,
    isPanning,
    setIsPanning,
    setLastPanPos,
    setStagePos,
    lastPanPos,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    stageContainerRef: mainStageContainerRef,
    scrollAreaRef,
    panStateRef,
    stageRef,
  });

  const handlePanStageMouseDownRef = useRef(panHandlers.handleStageMouseDown);
  handlePanStageMouseDownRef.current = panHandlers.handleStageMouseDown;

  // Middle-click on a scrollable ancestor triggers browser autoscroll; when zoomed out, padding
  // hits the scroll root so Konva never runs preventDefault. Capture on the scroll area stops
  // autoscroll and starts pan when the pointer is outside the stage (e.g. gutter/padding).
  useEffect(() => {
    const scrollEl = scrollAreaRef?.current;
    if (!scrollEl) return undefined;

    const onMiddleMouseDownCapture = (e) => {
      if (e.button !== 1) return;
      e.preventDefault();

      const stageContainer = mainStageContainerRef?.current;
      if (stageContainer && stageContainer.contains(e.target)) {
        return;
      }
      if (
        typeof e.target?.closest === "function" &&
        (e.target.closest("[data-canvas-chrome]") ||
          e.target.closest("[data-zoom-chrome]"))
      ) {
        return;
      }

      handlePanStageMouseDownRef.current?.({ evt: e });
    };

    scrollEl.addEventListener("mousedown", onMiddleMouseDownCapture, {
      capture: true,
    });
    return () => {
      scrollEl.removeEventListener("mousedown", onMiddleMouseDownCapture, {
        capture: true,
      });
    };
  }, [scrollAreaRef]);

  // Enhanced mouse handlers with selection box support
  const handleStageMouseDown = (e) => {
    const isDrawingToolMode =
      (action !== ACTIONS.SELECT && action !== ACTIONS.PAN) ||
      (lockedAction &&
        lockedAction !== ACTIONS.SELECT &&
        lockedAction !== ACTIONS.PAN);
    if (
      isDrawingToolMode &&
      (e?.evt?.button === 0 || e?.evt?.button === undefined)
    ) {
      drawingOriginCanvasRef.current = "main";
    }

    // CRITICAL FIX: Check if we're over a top-layer image first
    // Since top-layer Stage container has pointer-events: none, we need to check manually
    // When multiple elements are selected, skip this so mousedown can start selection box on main stage
    const isMultiSelected = selectedObjects && selectedObjects.length > 1;
    if (
      !isMultiSelected &&
      topLayerStageRef?.current &&
      e.evt &&
      mainCanvasImagesTop.length > 0
    ) {
      const topLayerStage = topLayerStageRef.current;
      const clientX = e.evt.clientX;
      const clientY = e.evt.clientY;

      // Get top-layer Stage container bounds
      const topLayerContainer = topLayerStage.container();
      const topLayerRect = topLayerContainer.getBoundingClientRect();

      // Check if click is within top-layer Stage bounds
      if (
        clientX >= topLayerRect.left &&
        clientX <= topLayerRect.right &&
        clientY >= topLayerRect.top &&
        clientY <= topLayerRect.bottom
      ) {
        // Convert client coordinates to top-layer Stage coordinates
        const topLayerX = clientX - topLayerRect.left;
        const topLayerY = clientY - topLayerRect.top;

        // Check if there's a top-layer image at this position
        // We need to manually check each image's bounds since getIntersection won't work with pointer-events: none
        for (const img of mainCanvasImagesTop) {
          const imgX = img.x;
          const imgY = img.y;
          const imgWidth = img.width || 0;
          const imgHeight = img.height || 0;
          const imgRotation = img.rotation || 0;
          const imgScaleX = Math.abs(img.scaleX || 1);
          const imgScaleY = Math.abs(img.scaleY || 1);

          // Calculate actual displayed dimensions
          const displayedWidth = imgWidth * imgScaleX;
          const displayedHeight = imgHeight * imgScaleY;

          // Check if click is within image bounds (accounting for center vs top-left anchor)
          const useTopLeftAnchor = img.useTopLeftAnchor === true;
          let imgLeft, imgTop, imgRight, imgBottom;

          if (useTopLeftAnchor) {
            imgLeft = imgX;
            imgTop = imgY;
            imgRight = imgX + displayedWidth;
            imgBottom = imgY + displayedHeight;
          } else {
            // Center anchor
            imgLeft = imgX - displayedWidth / 2;
            imgTop = imgY - displayedHeight / 2;
            imgRight = imgX + displayedWidth / 2;
            imgBottom = imgY + displayedHeight / 2;
          }

          // For rotated images, we need more complex hit detection
          // For now, use a simple bounding box check (can be improved later for precise rotation hit detection)
          let isWithinBounds = false;

          if (imgRotation === 0 || imgRotation === 360) {
            // No rotation - simple bounds check
            isWithinBounds =
              topLayerX >= imgLeft &&
              topLayerX <= imgRight &&
              topLayerY >= imgTop &&
              topLayerY <= imgBottom;
          } else {
            // For rotated images, use a more generous bounding box
            // This is a simplified approach - for pixel-perfect hit detection,
            // we would need to transform the click point or use Konva's built-in hit detection
            const centerX = useTopLeftAnchor ? imgX + displayedWidth / 2 : imgX;
            const centerY = useTopLeftAnchor
              ? imgY + displayedHeight / 2
              : imgY;

            // Calculate rotated bounding box (approximate)
            const cos = Math.abs(Math.cos((imgRotation * Math.PI) / 180));
            const sin = Math.abs(Math.sin((imgRotation * Math.PI) / 180));
            const rotatedWidth = displayedWidth * cos + displayedHeight * sin;
            const rotatedHeight = displayedWidth * sin + displayedHeight * cos;

            const rotatedLeft = centerX - rotatedWidth / 2;
            const rotatedTop = centerY - rotatedHeight / 2;
            const rotatedRight = centerX + rotatedWidth / 2;
            const rotatedBottom = centerY + rotatedHeight / 2;

            isWithinBounds =
              topLayerX >= rotatedLeft &&
              topLayerX <= rotatedRight &&
              topLayerY >= rotatedTop &&
              topLayerY <= rotatedBottom;
          }

          if (isWithinBounds) {
            // CRITICAL FIX: In clone mode, manually trigger clone creation for top-layer images
            // Since top-layer Stage has pointer-events: none, clicks pass through but Background
            // might not receive them reliably, so we handle clone creation here directly
            const hasCloneTemplates =
              cloneTemplates && cloneTemplates.length > 0;
            const hasCloneTemplate = cloneTemplate !== null;
            const isClickToPlaceCloneMode =
              (isCloneMode || lockedAction) &&
              lockedAction === ACTIONS.SELECT;

            if (
              isClickToPlaceCloneMode &&
              (hasCloneTemplates || hasCloneTemplate) &&
              cloneElementsAtPosition
            ) {
              // Clone mode is active - manually trigger clone creation
              const mainStage = stageRef?.current;
              if (mainStage) {
                // Convert top-layer coordinates to main stage coordinates
                // The top-layer Stage is positioned over the main Stage, so coordinates should match
                mainStage.setPointersPositions(e.evt);
                const pointerPos = mainStage.getPointerPosition();
                if (pointerPos) {
                  // Call cloneElementsAtPosition with main stage coordinates
                  cloneElementsAtPosition(
                    pointerPos.x,
                    pointerPos.y,
                    stageRef,
                    clientX,
                    clientY,
                  );
                  recentTopLayerManualCloneRef.current = {
                    ts: e?.evt?.timeStamp || Date.now(),
                    clientX,
                    clientY,
                  };

                  // Prevent further event handling
                  e.evt.preventDefault();
                  e.evt.stopPropagation();
                  return;
                }
              }
            }

            // Found a top-layer image at this position - manually trigger its click
            const imageNode = topLayerStage.findOne(`#${img.id}`);
            if (imageNode) {
              // Set pointer position on top-layer Stage
              topLayerStage.setPointersPositions(e.evt);

              // Create a Konva event and fire mousedown on the image node
              // This should trigger the onClick handler for selection, regardless of draggable state
              const konvaEvent = {
                evt: e.evt,
                target: imageNode,
                currentTarget: imageNode,
                cancelBubble: false,
                getStage: () => topLayerStage,
              };

              // Fire both mousedown and click events to ensure proper selection
              imageNode.fire("mousedown", konvaEvent);
              imageNode.fire("click", konvaEvent);

              // CRITICAL: Track that we've fired mousedown on this top-layer image
              // This will prevent Background onClick from clearing selections
              recentDraggableElementMousedownRef.current = {
                nodeId: img.id,
                timestamp: Date.now(),
              };

              // Don't let the event continue to other handlers
              e.evt.preventDefault();
              e.evt.stopPropagation();
              return;
            }
          }
        }
      }
    }

    // Check if we should pan (middle mouse, space+left mouse, or PAN action selected)
    const shouldPan =
      e.evt.button === 1 ||
      (spacePressed && e.evt.button === 0) ||
      (action === ACTIONS.PAN && e.evt.button === 0);

    if (shouldPan) {
      // Let pan handler take over
      panHandlers.handleStageMouseDown(e);
      return;
    }

    // If waiting for paste, handle paste at clicked position
    if (isWaitingForPaste && pasteElementsAtPosition && e.evt.button === 0) {
      const stage = stageRef.current;
      if (stage) {
        stage.setPointersPositions(e.evt);
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          // Paste at clicked position
          pasteElementsAtPosition(
            pointerPos.x,
            pointerPos.y,
            pendingClipboard,
            pasteModeType === "cut",
          );
          // Exit paste mode
          if (setIsWaitingForPaste) {
            setIsWaitingForPaste(false);
          }
          e.evt.preventDefault();
          e.evt.stopPropagation();
          return;
        }
      }
    }

    // If clone mode is active, handle clone at clicked position (similar to paste)
    // This should work for both whitespace clicks and element clicks
    // Check for clone mode BEFORE checking for selection box or other handlers
    // Check for both single-element (cloneTemplate) and multi-element (cloneTemplates) clone mode
    const hasCloneTemplates = cloneTemplates && cloneTemplates.length > 0;
    const hasCloneTemplate = cloneTemplate !== null;
    const isClickToPlaceCloneMode =
      (isCloneMode || lockedAction) && lockedAction === ACTIONS.SELECT;
    const recentManualClone = recentTopLayerManualCloneRef.current;
    const eventTs = e?.evt?.timeStamp || Date.now();
    const isRecentTopLayerManualClone =
      eventTs - (recentManualClone?.ts || 0) < 180 &&
      recentManualClone?.clientX !== null &&
      recentManualClone?.clientY !== null &&
      Math.abs((e?.evt?.clientX ?? 0) - recentManualClone.clientX) <= 3 &&
      Math.abs((e?.evt?.clientY ?? 0) - recentManualClone.clientY) <= 3;
    if (
      isClickToPlaceCloneMode &&
      !isRecentTopLayerManualClone &&
      cloneElementsAtPosition &&
      (hasCloneTemplates || hasCloneTemplate) &&
      e.evt.button === 0
    ) {
      const stage = stageRef.current;
      if (stage) {
        // Get the target to check if it's whitespace (Stage, Layer, or Background)
        const target = e.target;
        const stageNode = target?.getStage?.();
        const targetType = target?.getClassName?.();
        const targetId = target?.attrs?.id;

        // Check if clicking on whitespace (Stage, Layer, or Background rect)
        const isWhitespaceClick =
          target === stageNode ||
          targetType === "Stage" ||
          targetType === "Layer" ||
          (targetType === "Rect" && targetId === "bg");

        // For clone mode, we want to clone regardless of whether it's whitespace or an element
        // But we need to get the pointer position relative to the stage
        stage.setPointersPositions(e.evt);
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          // Get client coordinates for determining which canvas to add elements to
          const clientX = e.evt.clientX;
          const clientY = e.evt.clientY;
          // Clone at clicked position, passing stage ref and client coordinates
          cloneElementsAtPosition(
            pointerPos.x,
            pointerPos.y,
            stageRef,
            clientX,
            clientY,
          );
          e.evt.preventDefault();
          e.evt.stopPropagation();
          e.cancelBubble = true; // Prevent event from bubbling to onClick handler
          return;
        }
      }
    }

    // If not panning and action is SELECT, check for selection box
    // But skip selection box if clone mode is active (clone is handled above)
    if (
      action === ACTIONS.SELECT &&
      e.evt.button === 0 &&
      !spacePressed &&
      !isCloneMode
    ) {
      const stage = stageRef.current;
      if (!stage) {
        panHandlers.handleStageMouseDown(e);
        return;
      }

      // Get pointer position relative to stage
      stage.setPointersPositions(e.evt);
      const pointerPos = stage.getPointerPosition();

      if (!pointerPos) {
        panHandlers.handleStageMouseDown(e);
        return;
      }

      // Check if clicking on empty canvas (not on any shape)
      const target = e.target;
      const stageNode = target.getStage();
      const targetType = target.getClassName();
      const targetId = target.attrs?.id;

      // If clicking directly on the stage, layer, or background rect (not on any shape), start selection box
      // Shapes have types like "Rect", "Circle", "Text", "Image", "Group", etc.
      // Stage, Layer, and Background Rect (id="bg") are the ones we want to trigger selection box
      const isStageOrLayer =
        target === stageNode ||
        targetType === "Stage" ||
        targetType === "Layer" ||
        (targetType === "Rect" && targetId === "bg") || // Background component
        (targetType === "Group" && target.attrs?.id === undefined);

      if (isStageOrLayer) {
        // Clear selection only when switching from off-canvas: current selection has off-canvas elements and we're drawing on main
        const hasOffCanvasSelected = selectedObjects?.some((obj) => {
          const { isInOffCanvas } = findElementCanvas({
            selectedObject: obj,
            rectangles,
            circles,
            images,
            texts,
            arrows,
            lines,
            doubleSidedArrows,
            offCanvasRectangles,
            offCanvasCircles,
            offCanvasImages,
            offCanvasTexts,
            offCanvasArrows,
            offCanvasLines,
            offCanvasDoubleSidedArrows,
          });
          return isInOffCanvas;
        });
        if (setSelectedObjects && hasOffCanvasSelected) {
          // CRITICAL: Capture selection count before clearing it
          // This preserves the original selection count for handleGlobalMouseUp to use
          const currentSelectionCount = selectedObjectsRef.current.length;
          const currentSelectionLengthRef = selectedObjectsLengthRef.current;
          
          effectivePreMouseDownSelectionCountRef.current = currentSelectionCount;
          preMouseDownSelectionLengthRef.current = currentSelectionLengthRef;
          preMouseDownSelectionArrayRef.current = [...selectedObjectsRef.current]; // Store actual array
          
          console.log("🟡 [STAGE-MOUSE-DOWN] Capturing selection before clearing", {
            currentSelectionCount,
            currentSelectionLengthRef,
            selectedObjectsRefCurrent: selectedObjectsRef.current,
            capturedArray: preMouseDownSelectionArrayRef.current,
          });
          
          // CRITICAL: Don't clear selection if we have multi-selection that should be preserved
          // This eliminates the flicker where selection disappears briefly
          if (currentSelectionCount > 1) {
            console.log("🟡 [STAGE-MOUSE-DOWN] Preserving multi-selection, skipping clear");
            // IMPORTANT: If off-canvas group is currently selected, do NOT start a main-canvas
            // selection box. Cross-stage selection-box creation causes mixed selections.
            e.evt.preventDefault();
            e.evt.stopPropagation();
            e.cancelBubble = true;
            return;
          } else {
            console.log("🟡 [STAGE-MOUSE-DOWN] Clearing single selection");
            setSelectedObjects([]);
          }
          if (setSelectedObject) setSelectedObject(null);
        }
        setIsSelecting(true);
        setSelectionBoxStage("main");
        // CRITICAL FIX: Initialize button tracking when selection starts
        previousButtonsRef.current = 1; // Assume button is pressed when starting selection
        setSelectionBox({
          startX: pointerPos.x,
          startY: pointerPos.y,
          endX: pointerPos.x,
          endY: pointerPos.y,
        });
        // Update ref immediately
        selectionBoxRef.current = {
          startX: pointerPos.x,
          startY: pointerPos.y,
          endX: pointerPos.x,
          endY: pointerPos.y,
        };
        selectionBoxStageRef.current = "main";
        // Prevent default to avoid triggering other handlers (including Background onClick)
        e.evt.preventDefault();
        e.evt.stopPropagation();
        e.cancelBubble = true;
        return;
      }
    }

    // Let pan handler process other cases (clicked on shape, or not SELECT action)
    panHandlers.handleStageMouseDown(e);
  };

  const handleStageMouseMove = (e) => {
    // CRITICAL: Always set pointer position first for accurate tracking
    if (stageRef?.current && e?.evt) {
      stageRef.current.setPointersPositions(e.evt);
    }

    // Update crosshair position when in paste mode
    if (isWaitingForPaste) {
      const stage = stageRef.current;
      if (stage) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          setCrosshairPos({ x: pointerPos.x, y: pointerPos.y });
        }
      }
    }

    // If selecting, update selection box (do this before panning to ensure it works)
    if (
      isSelecting &&
      !isPanning &&
      selectionBox &&
      selectionBoxStage === "main"
    ) {
      const stage = stageRef.current;
      if (stage && e?.evt) {
        // CRITICAL: Update pointer positions first for accurate coordinate calculation
        stage.setPointersPositions(e.evt);

        // CRITICAL FIX: Try to use getPointerPosition() first (most accurate, accounts for stage transformations)
        // If mouse is outside stage, getPointerPosition() returns null, so fall back to manual calculation
        let stageX, stageY;
        const pointerPos = stage.getPointerPosition();

        if (pointerPos) {
          // Mouse is inside stage - use Konva's accurate coordinate calculation
          stageX = pointerPos.x;
          stageY = pointerPos.y;
        } else {
          // Mouse is outside stage - manually calculate coordinates relative to container
          const containerRect = stage.container().getBoundingClientRect();
          stageX = e.evt.clientX - containerRect.left;
          stageY = e.evt.clientY - containerRect.top;
        }

        // CRITICAL FIX: Clamp selection box to main canvas boundaries
        // When selection starts in main canvas, prevent it from extending into off-canvas area
        const stageWidth = stage.width();
        const stageHeight = stage.height();
        stageX = Math.max(0, Math.min(stageWidth, stageX));
        stageY = Math.max(0, Math.min(stageHeight, stageY));

        // CRITICAL FIX: Update ref immediately to ensure latest coordinates are available
        const updatedBox = {
          ...selectionBox,
          endX: stageX,
          endY: stageY,
        };
        selectionBoxRef.current = updatedBox;
        setSelectionBox(updatedBox);
        // Prevent panning while selecting
        e.evt.preventDefault();
        e.evt.stopPropagation();
        return;
      }
    }

    // CRITICAL: Call onPointerMove for image drawing and other drawing operations
    // This must be called before panning to ensure drawing operations work correctly
    // BUT only if we're not actively drawing in off-canvas (to avoid interference)
    // Use the same logic as pointerHandlers.js to detect which canvas is active
    const currentStage = stageRef?.current;
    const isOffCanvas =
      offCanvasStageRef?.current && currentStage === offCanvasStageRef.current;
    const isDrawingInOffCanvas =
      isOffCanvas &&
      (isDrawingImage ||
        isDrawingRect ||
        isDrawingCircle ||
        isDrawingText ||
        isDrawingLine ||
        isDrawingArrow ||
        isDrawingDoubleArrow ||
        (isSelecting && selectionBoxStage === "off-canvas"));
    const isOffCanvasOriginDrawActive =
      drawingOriginCanvasRef.current === "off-canvas" &&
      (isDrawingImage ||
        isDrawingRect ||
        isDrawingCircle ||
        isDrawingText ||
        isDrawingLine ||
        isDrawingArrow ||
        isDrawingDoubleArrow ||
        (isSelecting && selectionBoxStage === "off-canvas"));

    // CRITICAL: If the event has off-canvas context, don't handle it in main canvas
    if (
      e?.isOffCanvasEvent ||
      e?.targetCanvas === "off-canvas" ||
      isOffCanvasOriginDrawActive
    ) {
      // If draw started on off-canvas but pointer is currently over main stage,
      // still forward move events to off-canvas so live geometry keeps updating.
      if (
        isOffCanvasOriginDrawActive &&
        !e?.isOffCanvasEvent &&
        e?.targetCanvas !== "off-canvas" &&
        onPointerMove &&
        offCanvasStageRef?.current &&
        stageRef
      ) {
        const offStage = offCanvasStageRef.current;
        if (e?.evt) offStage.setPointersPositions(e.evt);
        const originalStage = stageRef.current;
        stageRef.current = offStage;
        try {
          onPointerMove({
            ...e,
            target: offStage,
            currentTarget: offStage,
            isOffCanvasEvent: true,
            targetCanvas: "off-canvas",
          });
        } finally {
          stageRef.current = originalStage;
        }
      }
      // Handle panning only after off-canvas forwarding attempt
      panHandlers.handleStageMouseMove(e, isPanning);
      return;
    }

    if (onPointerMove && !isDrawingInOffCanvas) {
      onPointerMove(e);
    }

    // Handle panning
    panHandlers.handleStageMouseMove(e, isPanning);
  };

  const handleStageMouseUp = (e) => {
    const native = e?.evt;
    if (
      native &&
      typeof native.clientX === "number" &&
      typeof native.clientY === "number" &&
      typeof native.timeStamp === "number"
    ) {
      const prevForward = lastOffCanvasForwardedUpRef.current;
      if (
        native.timeStamp - (prevForward.ts || 0) < 220 &&
        prevForward.x !== null &&
        prevForward.y !== null &&
        Math.abs(native.clientX - prevForward.x) <= 3 &&
        Math.abs(native.clientY - prevForward.y) <= 3
      ) {
        return;
      }
    }
    if (
      native &&
      typeof native.clientX === "number" &&
      typeof native.timeStamp === "number"
    ) {
      const dedupeKey = `${native.timeStamp}:${native.clientX}:${native.clientY}`;
      if (lastGlobalDrawUpKeyRef.current === dedupeKey) {
        return;
      }
      lastGlobalDrawUpKeyRef.current = dedupeKey;
    }
    // CRITICAL: If we're drawing in off-canvas, don't handle mouse up in main canvas
    // This prevents interference with off-canvas drawing operations
    // Use the same logic as pointerHandlers.js to detect which canvas is active
    const currentStage = stageRef?.current;
    const isOffCanvas =
      offCanvasStageRef?.current && currentStage === offCanvasStageRef.current;
    const isDrawingInOffCanvas =
      isOffCanvas &&
      (isDrawingImage ||
        isDrawingRect ||
        isDrawingCircle ||
        isDrawingText ||
        isDrawingLine ||
        isDrawingArrow ||
        isDrawingDoubleArrow ||
        (isSelecting && selectionBoxStage === "off-canvas"));
    const isOffCanvasOriginDrawActive =
      drawingOriginCanvasRef.current === "off-canvas" &&
      (isDrawingImage ||
        isDrawingRect ||
        isDrawingCircle ||
        isDrawingText ||
        isDrawingLine ||
        isDrawingArrow ||
        isDrawingDoubleArrow ||
        (isSelecting && selectionBoxStage === "off-canvas"));

    const shapeDrawActive =
      isDrawingRectRef?.current === true ||
      isDrawingRect ||
      isDrawingCircle ||
      isDrawingText ||
      isDrawingLine ||
      isDrawingArrow ||
      isDrawingDoubleArrow ||
      (isDrawingImage && currentImageId?.current);

    if (isDrawingInOffCanvas || isOffCanvasOriginDrawActive) {
      // If draw started on off-canvas and mouseup happens on main stage,
      // forward the finalize event to off-canvas pointerUp so draw-end clamp runs.
      if (
        isOffCanvasOriginDrawActive &&
        !e?.isOffCanvasEvent &&
        e?.targetCanvas !== "off-canvas" &&
        onPointerUp &&
        offCanvasStageRef?.current &&
        stageRef
      ) {
        const offStage = offCanvasStageRef.current;
        if (e?.evt) offStage.setPointersPositions(e.evt);
        const originalStage = stageRef.current;
        stageRef.current = offStage;
        try {
          onPointerUp({
            ...e,
            target: offStage,
            currentTarget: offStage,
            isOffCanvasEvent: true,
            targetCanvas: "off-canvas",
          });
          if (
            e?.evt &&
            typeof e.evt.clientX === "number" &&
            typeof e.evt.clientY === "number" &&
            typeof e.evt.timeStamp === "number"
          ) {
            lastOffCanvasForwardedUpRef.current = {
              ts: e.evt.timeStamp,
              x: e.evt.clientX,
              y: e.evt.clientY,
            };
          }
        } finally {
          stageRef.current = originalStage;
        }
      }
      panHandlers.handleStageMouseUp(e, isPanning);
      drawingOriginCanvasRef.current = null;
      return;
    }

    // Selection box on stage mouseup: do not process or claim – only global mouseup should process
    // so the correct box (from selectionBoxRef) is used and we avoid double-claim from mousemove/stage.
    // Never swallow mouseup while a shape draw is active — otherwise clone/draw never gets onPointerUp.
    const boxForStage = selectionBoxRef.current || selectionBox;
    if (isSelecting && !isPanning && boxForStage && !shapeDrawActive) {
      // Let global mouseup finalize selection so selected elements are computed once.
      // Swallowing/clearing here can race and skip selection application.
      drawingOriginCanvasRef.current = null;
      return;
    }

    panHandlers.handleStageMouseUp(e, isPanning);
    drawingOriginCanvasRef.current = null;
  };

  const handleStageMouseLeave = () => {
    panHandlers.handleStageMouseLeave(isPanning);

    // Don't cancel selection when mouse leaves stage - allow selection to continue at boundaries
    // This enables drawing selection boxes that extend to stage edges
    // if (isSelecting) {
    //   setIsSelecting(false);
    //   setSelectionBox(null);
    //   setSelectionBoxStage(null);
    // }

    // CRITICAL FIX: If we're selecting and mouse leaves the stage, we still need to ensure
    // that the selection will be properly closed when mouse is released anywhere
    // This is already handled by the global mouse up listeners
  };

  const forwardEventToMainStage = useCallback(
    (konvaEvt, eventType) => {
      const mainStage = stageRef?.current;
      const nativeEvent = konvaEvt?.evt;
      if (!mainStage || !nativeEvent) {
        return false;
      }

      // CRITICAL FIX: Don't forward events in multi-select mode
      // This prevents Background onClick from interfering with selections
      if (isMultiSelectMode) {
        return false;
      }

      // CRITICAL FIX: Don't forward events if the source event target is a draggable element
      // This prevents interference with drag operations on top-layer images
      const sourceTarget = konvaEvt?.target;
      if (sourceTarget && sourceTarget !== sourceTarget.getStage()) {
        // This is an element (not the stage), check if it's draggable
        // If it's draggable, don't forward - let the element handle its own events
        const isDraggable = sourceTarget.draggable && sourceTarget.draggable();
        if (isDraggable) {
          return false;
        }
      }

      // Sync pointer position with main stage
      mainStage.setPointersPositions(nativeEvent);
      const pointerPosition = mainStage.getPointerPosition();
      if (!pointerPosition) {
        return false;
      }

      const targetNode = mainStage.getIntersection(pointerPosition, "Shape");
      if (targetNode) {
        targetNode.fire(eventType, { evt: nativeEvent });
        return true;
      }

      // CRITICAL FIX: If no target node (clicking on empty space), fire the event on the main Stage itself
      // This allows selection boxes and drawing new elements to work when clicking on empty space
      // that's covered by the top-layer Stage
      if (
        eventType === "mousedown" ||
        eventType === "mousemove" ||
        eventType === "mouseup"
      ) {
        // Create a synthetic Konva event for the main Stage
        const syntheticEvent = {
          evt: nativeEvent,
          target: mainStage,
          currentTarget: mainStage,
        };
        mainStage.fire(eventType, syntheticEvent);
        return true;
      }

      return false;
    },
    [stageRef, isMultiSelectMode],
  );

  // Resize handlers - using extracted handlers
  const resizeHandlers = createResizeHandlers({
    canvasHeight,
    setCanvasHeight,
    applyCanvasHeight,
    getMinCanvasHeightPercent,
    setCanvasResizeTrigger,
    stageRef,
    saveToHistory,
  });

  const handleResizeMouseDown = (e) =>
    resizeHandlers.handleResizeMouseDown(
      e,
      setIsResizing,
      setResizeStartY,
      setInitialHeight,
    );
  const handleResizeMouseMove = (e) =>
    resizeHandlers.handleResizeMouseMove(
      e,
      isResizing,
      resizeStartY,
      initialHeight,
    );
  const handleResizeMouseUp = () =>
    resizeHandlers.handleResizeMouseUp(setIsResizing);

  // No longer needed - using native HTML element instead of Konva Stage

  // Attach global mousemove/mouseup listeners when resizing
  useEffect(() => {
    if (!isResizing) return;
    const moveListener = (e) => {
      // Create a Konva-like event for compatibility
      handleResizeMouseMove({ evt: e });
    };
    const upListener = () => {
      handleResizeMouseUp();
    };
    window.addEventListener("mousemove", moveListener);
    window.addEventListener("mouseup", upListener);
    return () => {
      window.removeEventListener("mousemove", moveListener);
      window.removeEventListener("mouseup", upListener);
    };
  }, [isResizing, resizeStartY, initialHeight]);

  // Global mouse tracking for selection box (both main and off-canvas)
  // When selecting and mouse moves outside stage boundaries, continue tracking
  useEffect(() => {
    if (!isSelecting) {
      return;
    }

    const handleGlobalMouseMove = (e) => {
      // CRITICAL FIX: Use refs to get latest values to avoid stale closures
      // Check the latest isSelecting and selectionBoxStage values
      const currentIsSelecting = isSelecting;
      const currentSelectionBoxStage = selectionBoxStageRef.current;

      // CRITICAL FIX: Early exit if not selecting to prevent unnecessary updates
      if (!currentIsSelecting) {
        return;
      }

      if (!currentSelectionBoxStage) {
        return;
      }

      if (currentSelectionBoxStage === "off-canvas") {
        const stage = offCanvasStageRef?.current;
        if (!stage) return;

        // Keep off-canvas path identical to main path so zoom/transform math stays correct.
        stage.setPointersPositions(e);

        let x, y;
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          x = pointerPos.x;
          y = pointerPos.y;
        } else {
          // Fallback for pointer outside stage: convert client to stage coordinates manually.
          const stageContainer = stage.container();
          if (!stageContainer) return;
          const containerRect = stageContainer.getBoundingClientRect();
          const scaleX =
            containerRect.width > 0 ? stage.width() / containerRect.width : 1;
          const scaleY =
            containerRect.height > 0 ? stage.height() / containerRect.height : 1;
          x = (e.clientX - containerRect.left) * scaleX;
          y = (e.clientY - containerRect.top) * scaleY;
        }

        // CRITICAL FIX: Clamp coordinates to prevent selection box from vanishing when mouse goes outside stage
        // Allow the box to extend to boundaries but not beyond, which causes invalid dimensions
        const stageWidth = stage.width();
        const stageHeight = stage.height();
        x = Math.max(0, Math.min(stageWidth, x));
        y = Math.max(0, Math.min(stageHeight, y));

        // Update selection box with new coordinates
        // Use functional update to avoid dependency on selectionBox
        // CRITICAL FIX: Also update the ref immediately to ensure latest coordinates are available
        setSelectionBox((prevBox) => {
          if (!prevBox) return null;
          const updatedBox = {
            ...prevBox,
            endX: x,
            endY: y,
          };
          // Update ref immediately so mouse up handler has latest coordinates
          selectionBoxRef.current = updatedBox;
          return updatedBox;
        });
      } else if (currentSelectionBoxStage === "main") {
        const stage = stageRef?.current;
        if (!stage) return;

        // CRITICAL FIX: Always update pointer positions first
        stage.setPointersPositions(e);

        // CRITICAL FIX: Try to use getPointerPosition() first (most accurate, accounts for stage transformations)
        // If mouse is outside stage, getPointerPosition() returns null, so fall back to manual calculation
        let x, y;
        const pointerPos = stage.getPointerPosition();

        if (pointerPos) {
          // Mouse is inside stage - use Konva's accurate coordinate calculation
          x = pointerPos.x;
          y = pointerPos.y;
        } else {
          // Mouse is outside stage - manually calculate coordinates relative to container
          const stageContainer = stage.container();
          if (!stageContainer) return;
          const containerRect = stageContainer.getBoundingClientRect();
          x = e.clientX - containerRect.left;
          y = e.clientY - containerRect.top;
        }

        // CRITICAL FIX: Clamp selection box to main canvas boundaries
        // When selection starts in main canvas, prevent it from extending into off-canvas area
        const stageWidth = stage.width();
        const stageHeight = stage.height();
        x = Math.max(0, Math.min(stageWidth, x));
        y = Math.max(0, Math.min(stageHeight, y));

        // Update selection box with new coordinates
        // Use functional update to avoid dependency on selectionBox
        // CRITICAL FIX: Also update the ref immediately to ensure latest coordinates are available
        setSelectionBox((prevBox) => {
          if (!prevBox) return null;
          const updatedBox = {
            ...prevBox,
            endX: x,
            endY: y,
          };
          // Update ref immediately so mouse up handler has latest coordinates
          selectionBoxRef.current = updatedBox;
          return updatedBox;
        });
      }
    };

    const handleGlobalMouseUp = (e) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // CRITICAL: Use the pre-mouse-down captured selection count
      // This gives us the original selection count before Stage.onMouseDown cleared it
      const originalSelectionCount = effectivePreMouseDownSelectionCountRef.current;
      const originalSelectionLengthRef = preMouseDownSelectionLengthRef.current;
      
      console.log("🔴 [GLOBAL-MOUSEUP] Using pre-mouse-down captured selection", {
        originalSelectionCount,
        originalSelectionLengthRef,
        selectedObjectsRefCurrent: selectedObjectsRef.current,
        preMouseDownSelectionCount: effectivePreMouseDownSelectionCountRef.current,
        preMouseDownSelectionLength: preMouseDownSelectionLengthRef.current,
      });

      const currentSelectionBox = selectionBoxRef.current;
      const currentSelectionBoxStage = selectionBoxStageRef.current;

      const shouldProcess =
        isSelecting || (currentSelectionBox && currentSelectionBoxStage);

      if (shouldProcess) {
        e.preventDefault();
        // Clear any prior claim so we always process here (nothing else should process selection box)
        selectionProcessedRef.current = false;
        let shouldProcessSelection = true;

        const selectionBoxToProcess = currentSelectionBox || selectionBox;

        if (shouldProcessSelection && selectionBoxToProcess) {
          // CRITICAL: If this mouseup is from a transform end (resize/rotate) or drag end, do not process selection box
          // so we don't clear selection (e.g. transform then drag → drag end would otherwise deselect)
          const transformJustFinished =
            justFinishedTransformRef?.current ||
            (lastTransformStartTimeRef?.current &&
              Date.now() - lastTransformStartTimeRef.current < 300);
          const dragJustFinished = justFinishedDragRef?.current;
          const rawShapeDrawJustFinished = justFinishedShapeDrawRef?.current;
          const shapeDrawJustFinished =
            typeof rawShapeDrawJustFinished === "number"
              ? Date.now() - rawShapeDrawJustFinished < 60
              : Boolean(rawShapeDrawJustFinished);
          if (transformJustFinished || dragJustFinished || shapeDrawJustFinished) {
            if (justFinishedTransformRef)
              justFinishedTransformRef.current = false;
            if (justFinishedDragRef) justFinishedDragRef.current = false;
            if (justFinishedShapeDrawRef) justFinishedShapeDrawRef.current = 0;
            setIsSelecting(false);
            setSelectionBox(null);
            setSelectionBoxStage(null);
            return;
          }

          // CRITICAL FIX: Always use the stage where selection STARTED, not where it ended
          // selectionBoxStage indicates where the selection started, so use that stage for element search
          // IMPORTANT: Pass the ref object, not the stage instance, to findElementsInSelectionBox
          const targetStageRef =
            currentSelectionBoxStage === "main" ? stageRef : offCanvasStageRef;
          const stage = targetStageRef?.current;

          if (stage) {
            // Use selection box coordinates directly as they are already in stage coordinate system
            const selX = Math.min(
              selectionBoxToProcess.startX,
              selectionBoxToProcess.endX,
            );
            const selY = Math.min(
              selectionBoxToProcess.startY,
              selectionBoxToProcess.endY,
            );
            const selWidth = Math.abs(
              selectionBoxToProcess.endX - selectionBoxToProcess.startX,
            );
            const selHeight = Math.abs(
              selectionBoxToProcess.endY - selectionBoxToProcess.startY,
            );

            // Allow small selection boxes (minimum 1px) to select elements
            if (selWidth >= 1 && selHeight >= 1) {
              selectionProcessedRef.current = true;
              selectionBoxResultRef.current = null; // fresh cache for this gesture (Strict Mode double-invoke)
              markSelectionJustFinished();
              e.stopPropagation();

              const normalizedSelectionBox = {
                startX: selX,
                startY: selY,
                endX: selX + selWidth,
                endY: selY + selHeight,
              };

              const selectedElements = findElementsInSelectionBoxRef.current(
                normalizedSelectionBox,
                targetStageRef,
              );

              if (selectedElements.length > 0) {
                // Sort elements by their z-index (higher z-index = on top)
                selectedElements.sort((a, b) => {
                  const aZIndex = a.zIndex || 0;
                  const bZIndex = b.zIndex || 0;
                  return bZIndex - aZIndex;
                });

                // Toggle selection ONLY within the selection box:
                // - in-box + previously selected => unselected
                // - in-box + previously unselected => selected
                // - outside box => preserve current selection
                const inBoxKeys = new Set(
                  selectedElements.map((el) => `${el.id}-${el.type}`),
                );
                setSelectedObjectsRef.current((prev) => {
                  const cached = selectionBoxResultRef.current;
                  if (cached !== null) {
                    selectionBoxResultRef.current = null;
                    return cached;
                  }
                  const prevArr = prev || [];
                  const prevIds = new Set(
                    prevArr.map((el) => `${el.id}-${el.type}`),
                  );
                  const afterRemove = prevArr.filter(
                    (el) => !inBoxKeys.has(`${el.id}-${el.type}`),
                  );
                  const toAdd = selectedElements.filter(
                    (el) => !prevIds.has(`${el.id}-${el.type}`),
                  );
                  const newSelection = [...afterRemove, ...toAdd];
                  selectionBoxResultRef.current = newSelection;
                  setSelectedObjectRef.current(newSelection[0] ?? null);
                  if (setIsMultiSelectModeRef.current) {
                    setIsMultiSelectModeRef.current(newSelection.length > 1);
                  }
                  if (setIndividuallySelectedIdsRef.current) {
                    setIndividuallySelectedIdsRef.current(
                      new Set(newSelection.map((el) => el.id)),
                    );
                  }
                  return newSelection;
                });

                justProcessedSelectionBoxRef.current = true;
                setTimeout(() => {
                  justProcessedSelectionBoxRef.current = false;
                }, 150);
              } else {
                // Clear selection for very small boxes (or empty box)
                // CRITICAL: Preserve existing multi-selection for background/tiny-box releases.
                // Use the captured original selection count because Stage.onMouseDown may have cleared selectedObjectsRef.current
                const shouldPreserveMultiSelection =
                  isMultiSelectModeRef.current ||
                  originalSelectionCount > 1 ||
                  originalSelectionLengthRef > 1;
                
                console.log("🔴 [GLOBAL-MOUSEUP] About to clear selection", {
                  shouldPreserveMultiSelection,
                  isMultiSelectMode: isMultiSelectModeRef.current,
                  originalSelectionCount,
                  originalSelectionLengthRef,
                  selectedObjectsLengthRef: selectedObjectsLengthRef.current,
                  selectedObjectsRefLength: selectedObjectsRef.current.length,
                  selWidth,
                  selHeight,
                  selectedElementsLength: selectedElements.length,
                });
                
                if (shouldPreserveMultiSelection) {
                  // Preserve existing selection; only clear selection box UI
                  console.log("🔴 [GLOBAL-MOUSEUP] Preserving multi-selection");
                } else {
                  console.log("🔴 [GLOBAL-MOUSEUP] Clearing selection in handleGlobalMouseUp");
                  setSelectedObjectsRef.current([]);
                  setSelectedObjectRef.current(null);
                  if (setIsMultiSelectModeRef.current) {
                    setIsMultiSelectModeRef.current(false);
                  }
                  if (setIndividuallySelectedIdsRef.current) {
                    setIndividuallySelectedIdsRef.current(new Set());
                  }
                }
              }
            } else {
              // Clear selection for very small boxes (selWidth/selHeight < 1)
              // CRITICAL: Preserve existing multi-selection for tiny selection boxes.
              // Use the captured original selection count because Stage.onMouseDown may have cleared selectedObjectsRef.current
              const shouldPreserveMultiSelection =
                isMultiSelectModeRef.current ||
                originalSelectionCount > 1 ||
                originalSelectionLengthRef > 1;
              
              console.log("🔴 [GLOBAL-MOUSEUP] shouldPreserveMultiSelection check", {
                shouldPreserveMultiSelection,
                isMultiSelectMode: isMultiSelectModeRef.current,
                originalSelectionCount,
                originalSelectionLengthRef,
                selectedObjectsLengthRef: selectedObjectsLengthRef.current,
                selectedObjectsRefLength: selectedObjectsRef.current.length,
                selWidth,
                selHeight,
              });
              
              console.log("🔴 [GLOBAL-MOUSEUP] About to check shouldPreserveMultiSelection", {
                shouldPreserveMultiSelection,
                preMouseDownSelectionArray: preMouseDownSelectionArrayRef.current,
              });
              
              if (shouldPreserveMultiSelection) {
                console.log("🔴 [GLOBAL-MOUSEUP] Preserving multi-selection in second location");
                // CRITICAL: Actually restore the selection that was cleared
                const originalSelection = preMouseDownSelectionArrayRef.current;
                if (originalSelection && originalSelection.length > 0) {
                  console.log("🔴 [GLOBAL-MOUSEUP] Restoring original selection (second location)", {
                    originalSelection: originalSelection,
                    originalSelectionLength: originalSelection.length,
                  });
                  setSelectedObjectsRef.current(originalSelection);
                  // Also update the selectedObjectsLengthRef to match
                  selectedObjectsLengthRef.current = originalSelection.length;
                } else {
                  console.log("🔴 [GLOBAL-MOUSEUP] No original selection to restore (second location)");
                }
              } else {
                console.log("🔴 [GLOBAL-MOUSEUP] shouldPreserveMultiSelection is false in second location, clearing selection");
                setSelectedObjectsRef.current([]);
                setSelectedObjectRef.current(null);
                if (setIsMultiSelectModeRef.current) {
                  setIsMultiSelectModeRef.current(false);
                }
                if (setIndividuallySelectedIdsRef.current) {
                  setIndividuallySelectedIdsRef.current(new Set());
                }
              }
            }
          } else {
          }
        }

        // CRITICAL FIX: Always clear selection state, regardless of whether we processed the selection
        // This ensures the selection box stops drawing even if mouse was released within canvas bounds
        setIsSelecting(false);
        setSelectionBox(null);
        setSelectionBoxStage(null);
      }
    };

    // CRITICAL: Capture selection before Stage.onMouseDown clears it
    // This prevents the race condition where Stage.onMouseDown clears selection
    // before handleGlobalMouseUp can check if multi-selection should be preserved
    const captureSelectionBeforeMouseDown = (e) => {
      // Check if the click is on a chrome element - if so, don't capture selection
      if (
        typeof e.target?.closest === "function" &&
        (e.target.closest("[data-canvas-chrome]") ||
          e.target.closest("[data-zoom-chrome]"))
      ) {
        console.log("🟡 [PRE-MOUSE-DOWN] Ignoring click on chrome element", {
          eventTarget: e.target,
          chromeElement: e.target.closest("[data-canvas-chrome]") || e.target.closest("[data-zoom-chrome]"),
        });
        return;
      }
      
      // Only capture selection when clicking on canvas content (main or off-canvas).
      // This prevents capturing selection when clicking elsewhere on the page.
      const mainStageContent = stageRef.current?.content;
      const offStageContent = offCanvasStageRef?.current?.content;
      const isMainCanvasClick =
        e.target === mainStageContent || mainStageContent?.contains(e.target);
      const isOffCanvasClick =
        e.target === offStageContent || offStageContent?.contains(e.target);
      const isCanvasClick = isMainCanvasClick || isOffCanvasClick;
      
      if (!isCanvasClick) {
        console.log("🟡 [PRE-MOUSE-DOWN] Not a canvas click", {
          eventTarget: e.target,
          isCanvasClick,
          mainStageContent,
          offStageContent,
        });
        return;
      }
      
      console.log("🟡 [PRE-MOUSE-DOWN] Mousedown capture triggered on canvas!", {
        eventTarget: e.target,
        isCanvasClick,
        isMainCanvasClick,
        isOffCanvasClick,
        mainStageContent,
        offStageContent,
      });
      
      // Store the current selection count before any clearing happens
      const currentSelectionCount = selectedObjectsRef.current.length;
      const currentSelectionLengthRef = selectedObjectsLengthRef.current;
      
      // Store in refs that handleGlobalMouseUp can access
      effectivePreMouseDownSelectionCountRef.current = currentSelectionCount;
      preMouseDownSelectionLengthRef.current = currentSelectionLengthRef;
      preMouseDownSelectionArrayRef.current = [...selectedObjectsRef.current]; // Store actual array
      
      console.log("🟡 [PRE-MOUSE-DOWN] Selection captured before Stage.onMouseDown", {
        currentSelectionCount,
        currentSelectionLengthRef,
        selectedObjectsRefCurrent: selectedObjectsRef.current,
        capturedArray: preMouseDownSelectionArrayRef.current,
      });
    };

    // CRITICAL FIX: Use capture phase and ensure handler is always called
    // Add global listeners with capture phase to catch events early
    const mouseUpHandlerCapture = (e) => {
      handleGlobalMouseUp(e);
    };

    window.addEventListener("mousedown", captureSelectionBeforeMouseDown, true);
    window.addEventListener("mousemove", handleGlobalMouseMove);
    // Single capture-phase listener only - avoids running selection logic twice (capture + bubble)
    window.addEventListener("mouseup", handleGlobalMouseUp, true);

    return () => {
      window.removeEventListener("mousedown", captureSelectionBeforeMouseDown, true);
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleGlobalMouseUp, true);
    };
  }, [
    isSelecting,
    stageRef, // Add stageRef to ensure mousedown capture has access to stage
    // Remove selectionBoxStage and selectionBox from dependencies to prevent listener recreation
    // The handlers will get the current values from refs and state when they execute
  ]);

  // DEBUG: Track selectionBoxStage changes
  useEffect(() => {}, [selectionBoxStage]);

  // CRITICAL FIX: Add timeout-based fallback to clear selection when mouse stops moving
  // This will work even if mouse up events are not firing
  useEffect(() => {
    let mouseStopTimer = null;
    let lastMouseTime = Date.now();

    const handleMouseMove = (e) => {
      lastMouseTime = Date.now();

      // CRITICAL FIX: Track button state to detect release
      const currentButtons = e?.buttons;
      const previousButtons = previousButtonsRef.current;
      const buttonJustReleased = previousButtons === 1 && currentButtons === 0;

      // Update previous buttons state
      previousButtonsRef.current = currentButtons;

      // CRITICAL FIX: Check if mouse button is released during mouse move
      // This can happen when mouse up events are not properly fired
      // Log all mouse moves when selecting to debug
      if (isSelecting) {
      }

      // CRITICAL FIX: Check for button release - buttons === 0 means no buttons pressed
      // Also detect transition from 1 to 0 (button just released)
      const buttonsReleased = currentButtons === 0 || buttonJustReleased;

      if (isSelecting && buttonsReleased) {
        // Never process or claim here: mousemove often runs before mouseup on release, and the box
        // can be stale. Only global mouseup should process; we just clear UI state and leave refs
        // so global mouseup can read selectionBoxRef/selectionBoxStageRef.
        if (selectionProcessedRef.current) {
          selectionProcessedRef.current = false;
        }
        previousButtonsRef.current = null;
        setIsSelecting(false);
        setSelectionBox(null);
        setSelectionBoxStage(null);
        setSelectAllButtonClicked(false);
        return;
      }

      // If button was just released, do not process here – let global mouseup do it (same as buttonsReleased).
      const hasSelectionBoxInRef = selectionBoxRef.current !== null;
      if (buttonJustReleased && hasSelectionBoxInRef) {
        if (selectionProcessedRef.current) {
          selectionProcessedRef.current = false;
        }
        previousButtonsRef.current = null;
        setIsSelecting(false);
        setSelectionBox(null);
        setSelectionBoxStage(null);
        setSelectAllButtonClicked(false);
        return;
      }

      // Clear any existing timer
      if (mouseStopTimer) {
        clearTimeout(mouseStopTimer);
      }

      // If we're selecting, set a timer to clear selection if mouse stops moving
      if (isSelecting) {
        mouseStopTimer = setTimeout(() => {
          // If mouse hasn't moved for 500ms and we're still selecting, force clear selection
          if (isSelecting && Date.now() - lastMouseTime >= 500) {
            if (selectionProcessedRef.current) return;
            const latestSelectionBox = selectionBoxRef.current || selectionBox;

            if (isSelecting && latestSelectionBox) {
              const targetStageRef =
                selectionBoxStage === "main" ? stageRef : offCanvasStageRef;
              const stage = targetStageRef?.current;

              if (stage) {
                const selX = Math.min(
                  latestSelectionBox.startX,
                  latestSelectionBox.endX,
                );
                const selY = Math.min(
                  latestSelectionBox.startY,
                  latestSelectionBox.endY,
                );
                const selWidth = Math.abs(
                  latestSelectionBox.endX - latestSelectionBox.startX,
                );
                const selHeight = Math.abs(
                  latestSelectionBox.endY - latestSelectionBox.startY,
                );

                if (selWidth >= 1 && selHeight >= 1) {
                  selectionProcessedRef.current = true;
                  const normalizedSelectionBox = {
                    startX: selX,
                    startY: selY,
                    endX: selX + selWidth,
                    endY: selY + selHeight,
                  };

                  const selectedElements =
                    findElementsInSelectionBoxRef.current(
                      normalizedSelectionBox,
                      targetStageRef,
                    );

                  if (selectedElements.length > 0) {
                    // Sort elements by their z-index (higher z-index = on top)
                    selectedElements.sort((a, b) => {
                      const aZIndex = a.zIndex || 0;
                      const bZIndex = b.zIndex || 0;
                      return bZIndex - aZIndex;
                    });

                    // Toggle selection: elements in box that were selected become unselected, unselected become selected
                    const inBoxKeys = new Set(
                      selectedElements.map((el) => `${el.id}-${el.type}`),
                    );
                    setSelectedObjectsRef.current((prev) => {
                      const cached = selectionBoxResultRef.current;
                      if (cached !== null) {
                        selectionBoxResultRef.current = null;
                        return cached;
                      }
                      const prevArr = prev || [];
                      const prevIds = new Set(
                        prevArr.map((el) => `${el.id}-${el.type}`),
                      );
                      const afterRemove = prevArr.filter(
                        (el) => !inBoxKeys.has(`${el.id}-${el.type}`),
                      );
                      const toAdd = selectedElements.filter(
                        (el) => !prevIds.has(`${el.id}-${el.type}`),
                      );
                      let newSelection = [...afterRemove, ...toAdd];
                      selectionBoxResultRef.current = newSelection;
                      setSelectedObjectRef.current(newSelection[0] ?? null);
                      if (setIsMultiSelectModeRef.current) {
                        setIsMultiSelectModeRef.current(
                          newSelection.length > 1,
                        );
                      }
                      if (setIndividuallySelectedIdsRef.current) {
                        setIndividuallySelectedIdsRef.current(
                          new Set(newSelection.map((el) => el.id)),
                        );
                      }
                      return newSelection;
                    });
                  } else {
                    // Clear selection for very small boxes
                    // Check if we should preserve multi-selection
                    // Use the pre-mouse-down captured selection count because Stage.onMouseDown may have cleared selectedObjectsRef.current
                    const shouldPreserveMultiSelection =
                      isMultiSelectModeRef.current ||
                      effectivePreMouseDownSelectionCountRef.current > 1 ||
                      preMouseDownSelectionLengthRef.current > 1;
                    
                    console.log("🔴 [GLOBAL-MOUSEUP] third location shouldPreserveMultiSelection check", {
                      shouldPreserveMultiSelection,
                      isMultiSelectMode: isMultiSelectModeRef.current,
                      preMouseDownSelectionCount: effectivePreMouseDownSelectionCountRef.current,
                      preMouseDownSelectionLength: preMouseDownSelectionLengthRef.current,
                      selectedObjectsRefLength: selectedObjectsRef.current.length,
                    });
                    
                    if (shouldPreserveMultiSelection) {
                      console.log("🔴 [GLOBAL-MOUSEUP] Preserving multi-selection in third location");
                      // CRITICAL: Actually restore the selection that was cleared
                      const originalSelection = preMouseDownSelectionArrayRef.current;
                      if (originalSelection && originalSelection.length > 0) {
                        console.log("🔴 [GLOBAL-MOUSEUP] Restoring original selection", {
                          originalSelection: originalSelection,
                          originalSelectionLength: originalSelection.length,
                        });
                        setSelectedObjectsRef.current(originalSelection);
                        // Also update the selectedObjectsLengthRef to match
                        selectedObjectsLengthRef.current = originalSelection.length;
                      } else {
                        console.log("🔴 [GLOBAL-MOUSEUP] No original selection to restore");
                      }
                    } else {
                      console.log("🔴 [GLOBAL-MOUSEUP] Clearing selection in handleGlobalMouseUp (third location)");
                      setSelectedObjectsRef.current([]);
                      setSelectedObjectRef.current(null);
                      if (setIsMultiSelectModeRef.current) {
                        setIsMultiSelectModeRef.current(false);
                      }
                      if (setIndividuallySelectedIdsRef.current) {
                        setIndividuallySelectedIdsRef.current(new Set());
                      }
                    }
                  }
                }
              }
            }

            // Now clear the selection state
            setIsSelecting(false);
            setSelectionBox(null);
            setSelectionBoxStage(null);
            setSelectAllButtonClicked(false);
          }
        }, 500);
      }
    };

    // Add global mouse move listener to detect when mouse stops
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (mouseStopTimer) {
        clearTimeout(mouseStopTimer);
      }
    };
  }, [isSelecting, selectionBox, selectionBoxStage, setIsMultiSelectMode]);

  // CRITICAL FIX: Add a global mouse up handler for off-canvas drawing completion
  // This ensures drawing operations are completed even if pointer up happens outside stage bounds
  useEffect(() => {
    const handleGlobalDrawingMouseUp = (e) => {
      // Check if we're currently drawing in off-canvas
      if (isDrawingImage && currentImageId?.current) {
        const isOffCanvasDrawing =
          offCanvasStageRef?.current &&
          stageRef.current === offCanvasStageRef.current;

        if (isOffCanvasDrawing) {
          // Trigger pointer up handler manually
          if (onPointerUp) {
            const originalStage = stageRef.current;
            stageRef.current = offCanvasStageRef.current;
            try {
              // Create a synthetic event
              const syntheticEvent = {
                evt: e,
                target: offCanvasStageRef.current,
                currentTarget: offCanvasStageRef.current,
              };
              onPointerUp(syntheticEvent);
            } finally {
              stageRef.current = originalStage;
            }
          }
        }
      }
    };

    // Add global listener when drawing in off-canvas
    if (isDrawingImage && stageRef.current === offCanvasStageRef.current) {
      window.addEventListener("mouseup", handleGlobalDrawingMouseUp);

      return () => {
        window.removeEventListener("mouseup", handleGlobalDrawingMouseUp);
      };
    }
  }, [
    isDrawingImage,
    currentImageId,
    onPointerUp,
    isDrawingRect,
    isDrawingCircle,
    isDrawingLine,
    isDrawingArrow,
    isDrawingDoubleArrow,
  ]);

  // Fallback: while a draw started on main canvas, keep routing global mouse move/up
  // to main-stage handlers even if cursor is over off-canvas or outside stage hit areas.
  useEffect(() => {
    const rectDrawLive = isDrawingRectRef?.current === true || isDrawingRect;
    const cloneDrawOnMain =
      isCloneMode &&
      lockedAction &&
      lockedAction !== ACTIONS.SELECT &&
      lockedAction !== ACTIONS.PAN &&
      drawingOriginCanvasRef.current === "main";

    const isMainCanvasDrawingActive =
      (cloneDrawOnMain ||
        (((rectDrawLive ||
          isDrawingCircle ||
          isDrawingText ||
          isDrawingLine ||
          isDrawingArrow ||
          isDrawingDoubleArrow) &&
          drawingOriginCanvasRef.current === "main") ||
          (isDrawingImage &&
            drawingOriginCanvasRef.current !== "off-canvas"))) &&
      selectionBoxStage !== "off-canvas";

    if (!isMainCanvasDrawingActive) return;

    const forwardMove = (evt) => {
      const mainStage = stageRef?.current;
      if (!mainStage) return;
      mainStage.setPointersPositions(evt);
      handleStageMouseMove({
        evt,
        target: mainStage,
        currentTarget: mainStage,
        cancelBubble: false,
      });
    };

    const forwardUp = (evt) => {
      const mainStage = stageRef?.current;
      if (!mainStage) return;
      mainStage.setPointersPositions(evt);
      handleStageMouseUp({
        evt,
        target: mainStage,
        currentTarget: mainStage,
        cancelBubble: false,
      });
    };

    window.addEventListener("mousemove", forwardMove, { passive: true });
    window.addEventListener("mouseup", forwardUp, { passive: true });
    window.addEventListener("pointermove", forwardMove, { passive: true });
    window.addEventListener("pointerup", forwardUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", forwardMove);
      window.removeEventListener("mouseup", forwardUp);
      window.removeEventListener("pointermove", forwardMove);
      window.removeEventListener("pointerup", forwardUp);
    };
  }, [
    isCloneMode,
    lockedAction,
    ACTIONS.SELECT,
    ACTIONS.PAN,
    isDrawingImage,
    isDrawingRect,
    isDrawingCircle,
    isDrawingText,
    isDrawingLine,
    isDrawingArrow,
    isDrawingDoubleArrow,
    selectionBoxStage,
    stageRef,
    isDrawingRectRef,
  ]);

  // Symmetric fallback: while draw starts on off-canvas, keep routing global
  // move/up to off-canvas handlers even when cursor enters main canvas/outside.
  useEffect(() => {
    const rectDrawLive = isDrawingRectRef?.current === true || isDrawingRect;
    const cloneDrawOnOffCanvas =
      isCloneMode &&
      lockedAction &&
      lockedAction !== ACTIONS.SELECT &&
      lockedAction !== ACTIONS.PAN &&
      drawingOriginCanvasRef.current === "off-canvas";

    const isOffCanvasDrawingActive =
      cloneDrawOnOffCanvas ||
      (((rectDrawLive ||
        isDrawingCircle ||
        isDrawingText ||
        isDrawingLine ||
        isDrawingArrow ||
        isDrawingDoubleArrow ||
        isDrawingImage) &&
        drawingOriginCanvasRef.current === "off-canvas") ||
        (isSelecting && selectionBoxStage === "off-canvas"));

    if (!isOffCanvasDrawingActive) return;

    const isEventInsideOffCanvasStage = (evt) => {
      const offStage = offCanvasStageRef?.current;
      const container = offStage?.container?.();
      if (!container) return false;
      const targetNode = evt?.target;
      return targetNode instanceof Node ? container.contains(targetNode) : false;
    };

    const forwardMove = (evt) => {
      const offStage = offCanvasStageRef?.current;
      if (!offStage || isEventInsideOffCanvasStage(evt)) return;
      offStage.setPointersPositions(evt);
      const originalStage = stageRef?.current;
      if (!onPointerMove || !stageRef) return;
      stageRef.current = offStage;
      try {
        onPointerMove({
          evt,
          target: offStage,
          currentTarget: offStage,
          cancelBubble: false,
          isOffCanvasEvent: true,
          targetCanvas: "off-canvas",
        });
      } finally {
        stageRef.current = originalStage;
      }
    };

    const forwardUp = (evt) => {
      const offStage = offCanvasStageRef?.current;
      if (!offStage || isEventInsideOffCanvasStage(evt)) return;
      const prevForward = lastOffCanvasForwardedUpRef.current;
      if (
        typeof evt?.clientX === "number" &&
        typeof evt?.clientY === "number" &&
        typeof evt?.timeStamp === "number" &&
        evt.timeStamp - (prevForward.ts || 0) < 220 &&
        prevForward.x !== null &&
        prevForward.y !== null &&
        Math.abs(evt.clientX - prevForward.x) <= 3 &&
        Math.abs(evt.clientY - prevForward.y) <= 3
      ) {
        return;
      }
      offStage.setPointersPositions(evt);
      const originalStage = stageRef?.current;
      if (!onPointerUp || !stageRef) return;
      stageRef.current = offStage;
      try {
        onPointerUp({
          evt,
          target: offStage,
          currentTarget: offStage,
          cancelBubble: false,
          isOffCanvasEvent: true,
          targetCanvas: "off-canvas",
        });
        if (
          typeof evt?.clientX === "number" &&
          typeof evt?.clientY === "number" &&
          typeof evt?.timeStamp === "number"
        ) {
          lastOffCanvasForwardedUpRef.current = {
            ts: evt.timeStamp,
            x: evt.clientX,
            y: evt.clientY,
          };
        }
      } finally {
        stageRef.current = originalStage;
      }
      drawingOriginCanvasRef.current = null;
    };

    window.addEventListener("mousemove", forwardMove, { passive: true });
    window.addEventListener("mouseup", forwardUp, { passive: true });
    window.addEventListener("pointermove", forwardMove, { passive: true });
    window.addEventListener("pointerup", forwardUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", forwardMove);
      window.removeEventListener("mouseup", forwardUp);
      window.removeEventListener("pointermove", forwardMove);
      window.removeEventListener("pointerup", forwardUp);
    };
  }, [
    isCloneMode,
    lockedAction,
    ACTIONS.SELECT,
    ACTIONS.PAN,
    isDrawingImage,
    isDrawingRect,
    isDrawingCircle,
    isDrawingText,
    isDrawingLine,
    isDrawingArrow,
    isDrawingDoubleArrow,
    isSelecting,
    selectionBoxStage,
    stageRef,
    offCanvasStageRef,
    onPointerMove,
    onPointerUp,
    isDrawingRectRef,
  ]);
  // Debug log for tooltip visibility
  useEffect(() => {}, [lockedAction, isWaitingForPaste]);

  /**
   * Pointer-event isolation should only apply to true multi-selection workflows.
   * For single selection, keep both stages interactive so users can click across canvases.
   * When all selected elements live on main canvas → "main"; all on off-canvas → "off"; else null.
   */
  const selectionPointerIsolationSide = useMemo(() => {
    if (!isMultiSelectMode) return null;
    const objs = [];
    if (selectedObjects && selectedObjects.length > 0) {
      selectedObjects.forEach((o) => {
        if (o && o.id && o.type) objs.push(o);
      });
    } else if (selectedObject && selectedObject.id && selectedObject.type) {
      objs.push(selectedObject);
    }
    if (objs.length <= 1) return null;

    let mainCount = 0;
    let offCount = 0;
    for (const obj of objs) {
      const { isInMainCanvas, isInOffCanvas } = findElementCanvas({
        selectedObject: obj,
        rectangles,
        circles,
        images,
        texts,
        arrows,
        lines,
        doubleSidedArrows,
        offCanvasRectangles,
        offCanvasCircles,
        offCanvasImages,
        offCanvasTexts,
        offCanvasArrows,
        offCanvasLines,
        offCanvasDoubleSidedArrows,
      });
      if (isInMainCanvas) mainCount += 1;
      if (isInOffCanvas) offCount += 1;
    }
    if (mainCount > 0 && offCount === 0) return "main";
    if (offCount > 0 && mainCount === 0) return "off";
    return null;
  }, [
    isMultiSelectMode,
    selectedObject,
    selectedObjects,
    rectangles,
    circles,
    images,
    texts,
    arrows,
    lines,
    doubleSidedArrows,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasImages,
    offCanvasTexts,
    offCanvasArrows,
    offCanvasLines,
    offCanvasDoubleSidedArrows,
  ]);

  selectionPointerIsolationSideRef.current = selectionPointerIsolationSide;

  useEffect(() => {
    const sync = topLayerStageRef.current?.__trySetZIndex;
    if (typeof sync === "function") sync();
  }, [selectionPointerIsolationSide]);

  // Keep selected off-canvas nodes visually on top (including during drag move).
  // This mirrors the main-canvas "selected-on-top" behavior without duplicating render trees.
  useEffect(() => {
    const offStage = offCanvasStageRef?.current;
    if (!offStage) return;

    const selectedPool =
      selectedObjects && selectedObjects.length > 0
        ? selectedObjects
        : selectedObject
          ? [selectedObject]
          : [];
    if (!selectedPool.length) return;

    const selectedOffCanvasIds = selectedPool
      .filter(Boolean)
      .filter((obj) => {
        const { isInOffCanvas } = findElementCanvas({
          selectedObject: obj,
          rectangles,
          circles,
          images,
          texts,
          arrows,
          lines,
          doubleSidedArrows,
          offCanvasRectangles,
          offCanvasCircles,
          offCanvasImages,
          offCanvasTexts,
          offCanvasArrows,
          offCanvasLines,
          offCanvasDoubleSidedArrows,
        });
        return isInOffCanvas;
      })
      .map((obj) => obj.id)
      .filter(Boolean);

    if (!selectedOffCanvasIds.length) return;

    const raf = window.requestAnimationFrame(() => {
      let didPromoteAny = false;
      selectedOffCanvasIds.forEach((id) => {
        const node = offStage.findOne(`#${id}`);
        if (!node || typeof node.moveToTop !== "function") return;
        
        // CRITICAL FIX: Only call moveToTop for top-layer images
        // Non-top-layer images should not be moved to top when selected
        const isImage = node.getClassName() === 'Image';
        const imageObj = isImage ? offCanvasImages.find(img => img.id === id) : null;
        const isTopLayerImage = isImage && imageObj?.isOnTopLayer === true;
        
        // Only move to top if it's a top-layer image or not an image
        if (isImage && !isTopLayerImage) {
          // Don't move non-top-layer images to top
          return;
        }
        
        try {
          node.moveToTop();
          didPromoteAny = true;
        } catch (_) {
          // ignore transient node/layer teardown during fast drag transitions
        }
      });
      if (didPromoteAny) {
        offStage.batchDraw?.();
      }
    });

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [
    selectedObject,
    selectedObjects,
    rectangles,
    circles,
    images,
    texts,
    arrows,
    lines,
    doubleSidedArrows,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasImages,
    offCanvasTexts,
    offCanvasArrows,
    offCanvasLines,
    offCanvasDoubleSidedArrows,
  ]);

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        // Browser zoom handles all scrolling automatically
      }}
    >
      {/* NEW: Off-Canvas Stage (Background/Rest of Screen) */}
      <Stage
        ref={offCanvasStageRef}
        width={offCanvasStageWidth}
        height={offCanvasStageHeightPx}
        pixelRatio={CANVAS_PIXEL_RATIO}
        style={{
          position: "fixed", // changed from absolute
          top: 0,
          left: offCanvasStageLeft,
          transform: `translate(${stagePos.x}px, ${stagePos.y}px)`,
          height: `${offCanvasStageHeightPx}px`,
          zIndex: 1,
          background: "white",
          boxShadow: "0 0 8px rgba(0,0,0,0.05)",
          touchAction: "none",
          pointerEvents:
            isSelecting && selectionBoxStage === "main"
              ? "none"
              : "auto",
          cursor:
            isPanning || spacePressed || action === ACTIONS.PAN
              ? isPanning
                ? "grabbing"
                : "grab"
              : isDuplicatePlacementActive
                ? "crosshair"
                : getCursorStyle(action),
          imageRendering: "auto",
          // Remove maxWidth or width from parent constraints
        }}
        padding={20}
        onPointerDown={(e) => {
          // For drawing clones, we need to handle pointer down events on off-canvas stage
          // Set pointer position on off-canvas stage first
          const stage = offCanvasStageRef?.current;
          if (stage && e?.evt) {
            stage.setPointersPositions(e.evt);
          }
          const isDrawingToolMode =
            (action !== ACTIONS.SELECT && action !== ACTIONS.PAN) ||
            (lockedAction &&
              lockedAction !== ACTIONS.SELECT &&
              lockedAction !== ACTIONS.PAN);
          if (
            isDrawingToolMode &&
            (e?.evt?.button === 0 || e?.evt?.button === undefined)
          ) {
            drawingOriginCanvasRef.current = "off-canvas";
          }

          // The handlers now support off-canvas drawing by detecting which stage is active
          // We just need to temporarily swap stageRef so the handlers can detect it
          if (onPointerDown && stage) {
            // Store original stage ref
            const originalStage = stageRef.current;
            // Temporarily set stageRef to off-canvas stage
            stageRef.current = stage;
            try {
              // Call the handler - it will detect off-canvas and use the correct state setters
              onPointerDown(e);
            } finally {
              // Restore original stage ref
              stageRef.current = originalStage;
            }
          }
        }}
        onPointerMove={(e) => {
          // Handle pointer move for drawing on off-canvas stage
          const stage = offCanvasStageRef?.current;
          if (stage && e?.evt) {
            stage.setPointersPositions(e.evt);
          }

          // If drawing started on main canvas, keep updating main draw even when cursor is over off-canvas.
          const isMainCanvasDrawingActive =
            (((isDrawingRect ||
              isDrawingCircle ||
              isDrawingText ||
              isDrawingLine ||
              isDrawingArrow ||
              isDrawingDoubleArrow) &&
              drawingOriginCanvasRef.current === "main") ||
              (isDrawingImage &&
                drawingOriginCanvasRef.current !== "off-canvas")) &&
            selectionBoxStage !== "off-canvas";
          if (isMainCanvasDrawingActive && stageRef?.current && e?.evt) {
            const mainStage = stageRef.current;
            mainStage.setPointersPositions(e.evt);
            const syntheticEvent = {
              evt: e.evt,
              target: mainStage,
              currentTarget: mainStage,
              cancelBubble: false,
            };
            handleStageMouseMove(syntheticEvent);
            return;
          }

          if (onPointerMove && stage) {
            // Store original stage ref
            const originalStage = stageRef.current;
            // Temporarily set stageRef to off-canvas stage
            stageRef.current = stage;

            try {
              // CRITICAL: Add off-canvas context to the event so parent handler knows which canvas to use
              const enhancedEvent = {
                ...e,
                isOffCanvasEvent: true,
                targetCanvas: "off-canvas",
              };

              // Call the handler with enhanced event
              onPointerMove(enhancedEvent);
            } finally {
              // Restore original stage ref
              stageRef.current = originalStage;
            }
          }
        }}
        onPointerUp={(e) => {
          // Handle pointer up for drawing on off-canvas stage
          const stage = offCanvasStageRef?.current;
          if (stage && e?.evt) {
            stage.setPointersPositions(e.evt);
          }

          // If drawing started on main canvas, finalize on main even when released over off-canvas.
          const isMainCanvasDrawingActive =
            (((isDrawingRect ||
              isDrawingCircle ||
              isDrawingText ||
              isDrawingLine ||
              isDrawingArrow ||
              isDrawingDoubleArrow) &&
              drawingOriginCanvasRef.current === "main") ||
              (isDrawingImage &&
                drawingOriginCanvasRef.current !== "off-canvas")) &&
            selectionBoxStage !== "off-canvas";
          if (isMainCanvasDrawingActive && stageRef?.current && e?.evt) {
            const mainStage = stageRef.current;
            mainStage.setPointersPositions(e.evt);
            const syntheticEvent = {
              evt: e.evt,
              target: mainStage,
              currentTarget: mainStage,
              cancelBubble: false,
            };
            handleStageMouseUp(syntheticEvent);
            drawingOriginCanvasRef.current = null;
            return;
          }

          if (onPointerUp && stage) {
            // Store original stage ref
            const originalStage = stageRef.current;
            // Temporarily set stageRef to off-canvas stage
            stageRef.current = stage;
            try {
              // Call the handler - it will now use offCanvasStageRef
              onPointerUp(e);
            } finally {
              // Restore original stage ref
              stageRef.current = originalStage;
            }
            drawingOriginCanvasRef.current = null;
          } else {
            drawingOriginCanvasRef.current = null;
          }
        }}
        onMouseDown={(e) => {
          // Check if we should pan (middle mouse, space+left mouse, or PAN action selected)
          const shouldPan =
            e.evt?.button === 1 ||
            (spacePressed && e.evt?.button === 0) ||
            (action === ACTIONS.PAN && e.evt?.button === 0);

          if (shouldPan) {
            // Let shared pan handler take over for off-canvas stage as well.
            panHandlers.handleStageMouseDown(e);
            return;
          }

          // CRITICAL: In clone mode with lockedAction (drag-to-draw mode), don't create clone on click
          // The clone will be created when dragging starts (onPointerMove) or on pure click (onPointerUp)
          // This prevents duplicate clones: onMouseDown creates one, then onPointerUp creates another
          const hasCloneTemplates = cloneTemplates && cloneTemplates.length > 0;
          const hasCloneTemplate = cloneTemplate !== null;
          const isDragToDrawMode =
            (isCloneMode || lockedAction) &&
            lockedAction &&
            lockedAction !== ACTIONS.SELECT; // SELECT is click-to-place duplicate mode, not drag-to-draw

          // Skip clone creation in drag-to-draw mode - let onPointerUp handle it
          if (isDragToDrawMode) {
            return;
          }

          // Handle clone mode for off-canvas stage (similar to main canvas)
          // Check for both single-element (cloneTemplate) and multi-element (cloneTemplates) clone mode
          // Also check lockedAction since clone mode sets it
          const shouldClone =
            (isCloneMode || lockedAction) &&
            cloneElementsAtPosition &&
            (hasCloneTemplates || hasCloneTemplate) &&
            e.evt?.button === 0;

          if (shouldClone) {
            // Background rect also handles clone on mousedown.
            // If target is background, let Background own clone creation to avoid double clones.
            const targetType = e?.target?.getClassName?.();
            const targetId = e?.target?.attrs?.id;
            const isBackgroundRectClick =
              targetType === "Rect" && targetId === "bg";
            if (isBackgroundRectClick) {
              return;
            }
            const stage = offCanvasStageRef?.current;
            if (stage) {
              stage.setPointersPositions(e.evt);
              const pointerPos = stage.getPointerPosition();
              if (pointerPos) {
                // Get client coordinates for determining which canvas to add elements to
                const clientX = e.evt?.clientX;
                const clientY = e.evt?.clientY;
                // Clone at clicked position, passing off-canvas stage ref and client coordinates
                cloneElementsAtPosition(
                  pointerPos.x,
                  pointerPos.y,
                  offCanvasStageRef,
                  clientX,
                  clientY,
                );
                e.evt?.preventDefault();
                e.evt?.stopPropagation();
                e.cancelBubble = true;
                return;
              }
            }
          }

          // If not panning and action is SELECT, check for selection box
          // But skip selection box if clone mode is active (clone is handled above)
          if (
            action === ACTIONS.SELECT &&
            e.evt?.button === 0 &&
            !spacePressed &&
            !isCloneMode
          ) {
            const stage = offCanvasStageRef?.current;
            if (!stage) {
              return;
            }

            // Get pointer position relative to stage
            stage.setPointersPositions(e.evt);
            const pointerPos = stage.getPointerPosition();

            if (!pointerPos) {
              return;
            }

            // Check if clicking on empty canvas (not on any shape)
            const target = e.target;
            const stageNode = target?.getStage?.();
            const targetType = target?.getClassName?.();
            const targetId = target?.attrs?.id;

            // If clicking directly on the stage, layer, or background rect (not on any shape), start selection box
            const isStageOrLayer =
              target === stageNode ||
              targetType === "Stage" ||
              targetType === "Layer" ||
              (targetType === "Rect" && targetId === "bg") || // Background component
              (targetType === "Group" && target?.attrs?.id === undefined);

            if (isStageOrLayer) {
              // When multi-select is on and selection is from main stage, keep cross-canvas
              // element selection blocked, but a whitespace click on the other stage should
              // do nothing (preserve current selection).
              if (blockClicksOnOffCanvasElements) {
                e.evt?.preventDefault?.();
                e.evt?.stopPropagation?.();
                e.cancelBubble = true;
                return;
              }
              // Clear selection only when switching from main: current selection has main canvas elements and we're drawing on off-canvas
              const hasMainCanvasSelected = selectedObjects?.some((obj) => {
                const { isInMainCanvas } = findElementCanvas({
                  selectedObject: obj,
                  rectangles,
                  circles,
                  images,
                  texts,
                  arrows,
                  lines,
                  doubleSidedArrows,
                  offCanvasRectangles,
                  offCanvasCircles,
                  offCanvasImages,
                  offCanvasTexts,
                  offCanvasArrows,
                  offCanvasLines,
                  offCanvasDoubleSidedArrows,
                });
                return isInMainCanvas;
              });
              if (setSelectedObjects && hasMainCanvasSelected) {
                // CRITICAL: Capture selection count before clearing it
                // This preserves the original selection count for handleGlobalMouseUp to use
                const currentSelectionCount = selectedObjectsRef.current.length;
                const currentSelectionLengthRef = selectedObjectsLengthRef.current;
                
                effectivePreMouseDownSelectionCountRef.current = currentSelectionCount;
                preMouseDownSelectionLengthRef.current = currentSelectionLengthRef;
                preMouseDownSelectionArrayRef.current = [...selectedObjectsRef.current]; // Store actual array
                
                console.log("🟡 [OFF-CANVAS-MOUSE-DOWN] Capturing selection before clearing", {
                  currentSelectionCount,
                  currentSelectionLengthRef,
                  selectedObjectsRefCurrent: selectedObjectsRef.current,
                  capturedArray: preMouseDownSelectionArrayRef.current,
                });
                
                // CRITICAL: Don't clear selection if we have multi-selection that should be preserved
                // This eliminates the flicker where selection disappears briefly
                if (currentSelectionCount > 1) {
                  console.log("🟡 [OFF-CANVAS-MOUSE-DOWN] Preserving multi-selection, skipping clear");
                } else {
                  console.log("🟡 [OFF-CANVAS-MOUSE-DOWN] Clearing single selection");
                  setSelectedObjects([]);
                }
                if (setSelectedObject) setSelectedObject(null);
              }
              setIsSelecting(true);
              setSelectionBoxStage("off-canvas");
              setSelectionBox({
                startX: pointerPos.x,
                startY: pointerPos.y,
                endX: pointerPos.x,
                endY: pointerPos.y,
              });
              // Prevent default to avoid triggering other handlers (including Background onClick)
              e.evt?.preventDefault();
              e.evt?.stopPropagation();
              e.cancelBubble = true;
              return;
            }
          }
        }}
        onMouseMove={(e) => {
          // CRITICAL: Always set pointer position first for accurate tracking
          const stage = offCanvasStageRef?.current;
          if (stage && e?.evt) {
            stage.setPointersPositions(e.evt);
          }

          // Keep main-canvas drawing alive when cursor crosses into off-canvas.
          const isMainCanvasDrawingActive =
            (((isDrawingRect ||
              isDrawingCircle ||
              isDrawingText ||
              isDrawingLine ||
              isDrawingArrow ||
              isDrawingDoubleArrow) &&
              drawingOriginCanvasRef.current === "main") ||
              (isDrawingImage &&
                drawingOriginCanvasRef.current !== "off-canvas")) &&
            selectionBoxStage !== "off-canvas";
          if (isMainCanvasDrawingActive && stageRef?.current && e?.evt) {
            const mainStage = stageRef.current;
            mainStage.setPointersPositions(e.evt);
            const syntheticEvent = {
              evt: e.evt,
              target: mainStage,
              currentTarget: mainStage,
              cancelBubble: false,
            };
            handleStageMouseMove(syntheticEvent);
            return;
          }

          // Keep panning behavior consistent on off-canvas stage.
          if (isPanning) {
            panHandlers.handleStageMouseMove(e, isPanning);
            return;
          }

          // If selecting, update selection box (do this before panning to ensure it works)
          if (
            isSelecting &&
            !isPanning &&
            selectionBox &&
            selectionBoxStage === "off-canvas"
          ) {
            if (stage && e?.evt) {
              // Keep off-canvas selection-box coordinates in stage space at all zoom levels.
              stage.setPointersPositions(e.evt);
              let stageX, stageY;
              const pointerPos = stage.getPointerPosition();

              if (pointerPos) {
                stageX = pointerPos.x;
                stageY = pointerPos.y;
              } else {
                const containerRect = stage.container().getBoundingClientRect();
                const scaleX =
                  containerRect.width > 0
                    ? stage.width() / containerRect.width
                    : 1;
                const scaleY =
                  containerRect.height > 0
                    ? stage.height() / containerRect.height
                    : 1;
                stageX = (e.evt.clientX - containerRect.left) * scaleX;
                stageY = (e.evt.clientY - containerRect.top) * scaleY;
              }

              // CRITICAL FIX: Clamp coordinates to prevent selection box from vanishing when mouse goes outside stage
              // Allow the box to extend to boundaries but not beyond, which causes invalid dimensions
              const stageWidth = stage.width();
              const stageHeight = stage.height();
              stageX = Math.max(0, Math.min(stageWidth, stageX));
              stageY = Math.max(0, Math.min(stageHeight, stageY));

              const updatedBox = {
                ...selectionBox,
                endX: stageX,
                endY: stageY,
              };
              setSelectionBox(updatedBox);
              selectionBoxRef.current = updatedBox;
              // Prevent panning while selecting
              e.evt?.preventDefault();
              e.evt?.stopPropagation();
              return;
            }
          }
        }}
        onMouseUp={(e) => {
          // Finalize main-canvas drawing even if mouseup happens over off-canvas.
          const isMainCanvasDrawingActive =
            (((isDrawingRect ||
              isDrawingCircle ||
              isDrawingText ||
              isDrawingLine ||
              isDrawingArrow ||
              isDrawingDoubleArrow) &&
              drawingOriginCanvasRef.current === "main") ||
              (isDrawingImage &&
                drawingOriginCanvasRef.current !== "off-canvas")) &&
            selectionBoxStage !== "off-canvas";
          if (isMainCanvasDrawingActive && stageRef?.current && e?.evt) {
            const mainStage = stageRef.current;
            mainStage.setPointersPositions(e.evt);
            const syntheticEvent = {
              evt: e.evt,
              target: mainStage,
              currentTarget: mainStage,
              cancelBubble: false,
            };
            handleStageMouseUp(syntheticEvent);
            drawingOriginCanvasRef.current = null;
            return;
          }

          if (isPanning) {
            panHandlers.handleStageMouseUp(e, isPanning);
            return;
          }

          // If we were selecting, finalize selection first (before pan handler)
          if (
            isSelecting &&
            !isPanning &&
            selectionBox &&
            selectionBoxStage === "off-canvas"
          ) {
            const stage = offCanvasStageRef?.current;
            if (!stage) {
              setIsSelecting(false);
              setSelectionBox(null);
              setSelectionBoxStage(null);
              return;
            }

            // getPointerPosition() returns coordinates in stage coordinate system
            // The coordinates already account for stage padding, so no adjustment needed
            // const stagePadding = parseInt(canvasHeight) < 30 ? 10 : 20;

            // Use selection box coordinates directly as they are already in stage coordinate system
            const selX = Math.min(selectionBox.startX, selectionBox.endX);
            const selY = Math.min(selectionBox.startY, selectionBox.endY);
            const selWidth = Math.abs(selectionBox.endX - selectionBox.startX);
            const selHeight = Math.abs(selectionBox.endY - selectionBox.startY);

            // Allow small selection boxes (minimum 1px) to select elements
            // For very small boxes, we'll use a lower overlap threshold
            if (selWidth >= 1 && selHeight >= 1) {
              const normalizedSelectionBox = {
                startX: selX,
                startY: selY,
                endX: selX + selWidth,
                endY: selY + selHeight,
              };

              // Find elements in selection box - pass offCanvasStageRef to search off-canvas elements
              const selectedElements = findElementsInSelectionBox(
                normalizedSelectionBox,
                offCanvasStageRef,
              );

              if (selectedElements.length > 0) {
                // Toggle selection: deselect already selected elements, select unselected ones
                const currentlySelectedIds = new Set(
                  selectedObjects?.map((el) => `${el.id}-${el.type}`) || [],
                );

                const finalSelection = selectedElements.filter((el) => {
                  const elementKey = `${el.id}-${el.type}`;
                  return !currentlySelectedIds.has(elementKey);
                });

                // If all elements in selection box were already selected, clear selection
                if (
                  finalSelection.length === 0 &&
                  selectedElements.length > 0
                ) {
                  // All elements were already selected, so deselect them
                  const elementsToDeselectKeys = new Set(
                    selectedElements.map((el) => `${el.id}-${el.type}`),
                  );
                  const remainingSelection =
                    selectedObjects?.filter((el) => {
                      const elementKey = `${el.id}-${el.type}`;
                      return !elementsToDeselectKeys.has(elementKey);
                    }) || [];

                  if (setSelectedObjects) {
                    setSelectedObjects(remainingSelection);
                  }

                  // Update individuallySelectedIds
                  if (setIndividuallySelectedIds) {
                    const idsSet = new Set(
                      remainingSelection.map((el) => el.id),
                    );
                    setIndividuallySelectedIds(idsSet);
                  }

                  // Set primary selected object
                  if (remainingSelection.length > 0) {
                    setSelectedObject(remainingSelection[0]);
                  } else {
                    setSelectedObject(null);
                  }
                } else {
                  // Some elements were unselected, so select them (additive selection)
                  const combinedSelection = [
                    ...(selectedObjects || []),
                    ...finalSelection,
                  ];

                  if (setSelectedObjects) {
                    setSelectedObjects(combinedSelection);
                  } else {
                    console.error(
                      "❌ [SELECTION-BOX] setSelectedObjects is not a function!",
                    );
                  }

                  // Update individuallySelectedIds to match the new selection
                  if (setIndividuallySelectedIds) {
                    const idsSet = new Set(
                      combinedSelection.map((el) => el.id),
                    );
                    setIndividuallySelectedIds(idsSet);
                  }

                  // Set the first element as the primary selected object if we have any
                  if (combinedSelection.length > 0) {
                    const firstSelected = combinedSelection[0];

                    // CRITICAL FIX: If the selected element is a Text element, find the full text object first
                    // This ensures we have all text properties (not just id/type from selection)
                    if (
                      firstSelected &&
                      (firstSelected.type === "Text" ||
                        firstSelected.type?.includes("Text"))
                    ) {
                      // Find the text in the appropriate array (main or off-canvas)
                      const textFromMain = texts?.find(
                        (t) => t.id === firstSelected.id,
                      );
                      const textFromOffCanvas = offCanvasTexts?.find(
                        (t) => t.id === firstSelected.id,
                      );
                      const textToSelect =
                        textFromMain || textFromOffCanvas || firstSelected;

                      // CRITICAL FIX: Set all state synchronously to ensure TextBoxWithButtons shows
                      // Order: selectedObject -> selectedText -> selectedTextId -> showTextBox
                      if (setSelectedText && setSelectedTextId) {
                        // Create the text object with type
                        const textObjectWithType = {
                          ...textToSelect,
                          type: "Text",
                        };

                        // Set selectedObject FIRST (required by useEffect in MyCanvas.js)
                        setSelectedObject(textObjectWithType);

                        // Then set selectedText and selectedTextId
                        setSelectedText(textToSelect);
                        setSelectedTextId(textToSelect.id);

                        // Finally set showTextBox to true
                        // This ensures TextBoxWithButtons appears when selecting via drag selection box
                        setShowTextBox(true);
                      } else {
                        // Fallback: just set selectedObject if setters aren't available
                        setSelectedObject({
                          ...textToSelect,
                          type: "Text",
                        });
                      }
                    } else {
                      // Not a text element, just set selectedObject normally
                      setSelectedObject(firstSelected);
                    }
                  } else {
                    setSelectedObject(null);
                    // Clear selectedText if no elements are selected
                    if (setSelectedText && setSelectedTextId) {
                      setSelectedText(null);
                      setSelectedTextId(null);
                      setShowTextBox(false);
                    }
                  }
                }

                // Check if all elements from the same canvas are selected
                if (selectedElements.length > 0) {
                  const referenceElement = selectedElements[0];
                  const allElementsFromSameCanvas =
                    getAllElementsFromSameCanvas(referenceElement);

                  // Check if all elements are selected by comparing IDs
                  if (allElementsFromSameCanvas.length > 0) {
                    const selectedIds = new Set(
                      selectedElements.map((el) => `${el.id}-${el.type}`),
                    );
                    const allSelected = allElementsFromSameCanvas.every((el) =>
                      selectedIds.has(`${el.id}-${el.type}`),
                    );

                    if (allSelected && selectedElements.length > 1) {
                      // All elements are selected - enable multi-select mode and show Select All button as enabled
                      if (setIsMultiSelectMode) {
                        setIsMultiSelectMode(true);
                      }
                      setSelectAllButtonClicked(true);
                    } else if (selectedElements.length > 1) {
                      // Multiple but not all elements are selected - enable multi-select mode
                      if (setIsMultiSelectMode) {
                        setIsMultiSelectMode(true);
                      }
                      setSelectAllButtonClicked(false);
                    } else {
                      // Single element selected - clear select all button flag
                      setSelectAllButtonClicked(false);
                    }
                  }
                }
              } else {
                // Clear selection if no elements found
                setSelectAllButtonClicked(false); // Clear Select All button flag
                setSelectedObjects([]);
                setSelectedObject(null);
                if (setIndividuallySelectedIds) {
                  setIndividuallySelectedIds(new Set());
                }
                // CRITICAL: Disable multi-select mode when clearing selection (drag selection with no elements)
                if (setIsMultiSelectMode) {
                  setIsMultiSelectMode(false);
                }
              }
            } else {
              // If selection box was too small, clear selection
              setSelectAllButtonClicked(false); // Clear Select All button flag
              setSelectedObjects([]);
              setSelectedObject(null);
              // CRITICAL: Disable multi-select mode when clearing selection (click/tiny box)
              if (setIsMultiSelectMode) {
                setIsMultiSelectMode(false);
              }
            }

            setIsSelecting(false);
            setSelectionBox(null);
            setSelectionBoxStage(null);
            markSelectionJustFinished();

            e.evt?.preventDefault();
            e.evt?.stopPropagation();
            return;
          }
        }}
        onMouseLeave={() => {
          panHandlers.handleStageMouseLeave(isPanning);
        }}
        onClick={(e) => {
          if (!isPrimaryMouseButton(e.evt)) {
            return;
          }
          // If clone mode is active, don't interfere (onMouseDown already handled it)
          if (isCloneMode || lockedAction) {
            e.evt?.preventDefault?.();
            e.evt?.stopPropagation?.();
            return;
          }

          // When crop modal is open (state or ref), don't clear selection. Ref fixes first-time close.
          if (isCropModalOpen || isCropModalOpenRef?.current) {
            e.evt?.preventDefault?.();
            e.evt?.stopPropagation?.();
            return;
          }

          if (e?.target === e?.target.getStage()) {
            // CRITICAL: Use pre-mouse-down captured selection count instead of current hasGroupSelection
            // Because Stage.onMouseDown already cleared the selection, so hasGroupSelection is false now
            const originalHasGroupSelection = effectivePreMouseDownSelectionCountRef.current > 1;
            const hasGroupSelection = (selectedObjects?.length || 0) > 1;
            // Don't clear selection when this click is the one that fires right after transform end or drag end
            const transformStartedRecently =
              lastTransformStartTimeRef.current &&
              Date.now() - lastTransformStartTimeRef.current < 300;
            if (
              justFinishedTransformRef.current ||
              justFinishedDragRef.current ||
              (typeof justFinishedShapeDrawRef?.current === "number"
                ? Date.now() - justFinishedShapeDrawRef.current < 60
                : Boolean(justFinishedShapeDrawRef?.current)) ||
              transformStartedRecently
            ) {
              justFinishedTransformRef.current = false;
              if (justFinishedDragRef) justFinishedDragRef.current = false;
              if (justFinishedShapeDrawRef) justFinishedShapeDrawRef.current = 0;
              return;
            }
            // Don't clear if selected object is an image that was just dragged
            const lastDraggedOff = lastDraggedImageRef?.current;
            const isImageJustDraggedOff =
              lastDraggedOff?.id != null &&
              lastDraggedOff?.time != null &&
              selectedObject?.type === "Image" &&
              selectedObject?.id === lastDraggedOff.id &&
              Date.now() - lastDraggedOff.time < 500;
            const recentMousedown = recentDraggableElementMousedownRef?.current;
            const isRecentDraggableGesture =
              !!recentMousedown?.nodeId &&
              !!recentMousedown?.timestamp &&
              Date.now() - recentMousedown.timestamp < 4000;
            const currentSelection =
              (selectedObjectsRef?.current &&
                Array.isArray(selectedObjectsRef.current) &&
                selectedObjectsRef.current.length > 0
                ? selectedObjectsRef.current
                : selectedObjects) || [];
            const isSelectionMainOnly =
              currentSelection.length > 0 &&
              currentSelection.every((sel) => {
                const id = sel?.id;
                if (!id) return false;
                const inMain =
                  rectangles.some((r) => r.id === id) ||
                  circles.some((c) => c.id === id) ||
                  images.some((i) => i.id === id) ||
                  texts.some((t) => t.id === id) ||
                  arrows.some((a) => a.id === id) ||
                  doubleSidedArrows.some((d) => d.id === id) ||
                  lines.some((l) => l.id === id);
                const inOff =
                  offCanvasRectangles.some((r) => r.id === id) ||
                  offCanvasCircles.some((c) => c.id === id) ||
                  offCanvasImages.some((i) => i.id === id) ||
                  offCanvasTexts.some((t) => t.id === id) ||
                  offCanvasArrows.some((a) => a.id === id) ||
                  offCanvasDoubleSidedArrows.some((d) => d.id === id) ||
                  offCanvasLines.some((l) => l.id === id);
                return inMain && !inOff;
              });
            const preserveCrossStageSelectionOnOffCanvasClick = isSelectionMainOnly;
            // Clear selection on off-canvas click
            // But don't clear if clone mode is active (keeps editor open)
            // Also don't clear if lockedAction is set (clone mode uses lockedAction)
            if (
              !isCloneMode &&
              !lockedAction &&
              !isImageJustDraggedOff &&
              !isRecentDraggableGesture &&
              !originalHasGroupSelection &&
              !preserveCrossStageSelectionOnOffCanvasClick
            ) {
              setSelectedObject(null);
              setSelectedObjects([]); // CRITICAL: Clear selectedObjects array when clicking on off-canvas whitespace
              // CRITICAL: Disable multi-select mode when clicking on whitespace
              if (setIsMultiSelectMode) {
                setIsMultiSelectMode(false);
              } else {
              }
            } else if (hasGroupSelection) {
            }
            if (transformerRef?.current && !isRecentDraggableGesture) {
              transformerRef.current.nodes([]);
              transformerRef.current.getLayer().batchDraw();
            }
            if (!isRecentDraggableGesture) {
              // CRITICAL: Don't call main onClick if we have a multi-selection that should be preserved
              // Because the clickHandler already handled preserving the selection
              const originalHasGroupSelection = effectivePreMouseDownSelectionCountRef.current > 1;
              if (!originalHasGroupSelection) {
                onClick(e); // Only call main onClick if we don't have a multi-selection to preserve
              }
            } else {
              e.evt?.preventDefault?.();
              e.evt?.stopPropagation?.();
            }
          }
        }}
      >
        <Layer listening={true}>
          {/* Background for off-canvas stage - handles clone mode clicks on empty space */}
          <Background
            setSelectedTextId={setSelectedTextId}
            transformerRef={transformerRef}
            setSelectedArrow={setSelectedArrow}
            setSelectedLine={setSelectedLine}
            setSelectedDoubleSidedArrow={setSelectedDoubleSidedArrow}
            setSelectedText={setSelectedText}
            setSelectedObject={setSelectedObject}
            selectedObject={selectedObject}
            selectedObjects={selectedObjects}
            selectedObjectsRef={selectedObjectsRef}
            setShowTextBox={setShowTextBox}
            setSelectedImage={setSelectedImage}
            setAction={setAction}
            ACTIONS={ACTIONS}
            action={action}
            isDrawingArrow={isDrawingArrow}
            isDrawingDoubleArrow={isDrawingDoubleArrow}
            isDrawingLine={isDrawingLine}
            setSelectedObjects={setSelectedObjects}
            justFinishedSelectionRef={activeJustFinishedSelectionRef}
            pendingSelectionRef={pendingSelection}
            pendingChangesRef={pendingChangesRef}
            setTexts={setTexts}
            lockedAction={lockedAction}
            isCloneMode={isCloneMode}
            isMultiSelectMode={isMultiSelectMode}
            isMultiSelectModeRef={isMultiSelectModeRef}
            endElementEditing={endElementEditing}
            isElementBeingEdited={isElementBeingEdited}
            cloneElementsAtPosition={cloneElementsAtPosition}
            cloneTemplates={cloneTemplates}
            cloneTemplate={cloneTemplate}
            stageRef={offCanvasStageRef}
            clickedStageRef={offCanvasStageRef}
            setIsMultiSelectMode={setIsMultiSelectMode}
            recentDraggableElementMousedownRef={
              recentDraggableElementMousedownRef
            }
            setIsSelecting={setIsSelecting}
            setSelectionBox={setSelectionBox}
            setSelectionBoxStage={setSelectionBoxStage}
            isCropModalOpen={isCropModalOpen}
            isCropModalOpenRef={isCropModalOpenRef}
            justFinishedTransformRef={justFinishedTransformRef}
            lastTransformStartTimeRef={lastTransformStartTimeRef}
            justFinishedDragRef={justFinishedDragRef}
            justFinishedShapeDrawRef={justFinishedShapeDrawRef}
            lastDraggedImageRef={lastDraggedImageRef}
          />
          {/* 🖼 Bottom-layer Images (off-canvas) - render first so they stay behind rectangles/shapes/text */}
          {(() => {
            // CRITICAL FIX: Allow selected non-top-layer images to render in regular pass
            // This prevents them from vanishing when selected
            const imagesBottomToRender = offCanvasImagesBottom;
            // CRITICAL FIX: Remove selection-based sorting to maintain proper layering
            // Non-top-layer images should stay behind other elements regardless of selection
            const sortedOffCanvasImagesBottom = [...imagesBottomToRender];

            return sortedOffCanvasImagesBottom.map((image) => {
              const dragPreviewImageId =
                dragPreview?.type === "Image"
                  ? dragPreview?.data?.id?.replace("-ghost", "") ??
                    dragPreview?.data?.id
                  : null;
              const selectedDraggingImageIds = new Set(
                [
                  selectedObject?.type === "Image" && selectedObject?._isDragging
                    ? selectedObject.id
                    : null,
                  ...((selectedObjects || [])
                    .filter(
                      (obj) =>
                        obj?.type === "Image" &&
                        obj?._isDragging === true &&
                        !!obj?.id,
                    )
                    .map((obj) => obj.id) || []),
                ].filter(Boolean),
              );
              const isActivelyDraggedSelectedOffCanvasImage =
                image.isOnTopLayer !== true &&
                (selectedDraggingImageIds.has(image.id) ||
                  dragPreviewImageId === image.id);
              if (isActivelyDraggedSelectedOffCanvasImage) {
                return null;
              }

              // CRITICAL FIX: Hide off-canvas non-top-layer images during transform operations
              // to prevent showing both original and transformed copies
              const isActivelyTransformedSelectedOffCanvasImage =
                image.isOnTopLayer !== true &&
                selectedObject?.type === "Image" &&
                selectedObject?.id === image.id &&
                isTransformingRef.current?.isTransforming === true;
              if (isActivelyTransformedSelectedOffCanvasImage) {
                return null;
              }

          
              
             
              
              // CRITICAL FIX: During group drag WITHIN off-canvas, keep images visible
              // Only hide them if they're being dragged to a DIFFERENT canvas (main)
              const isInGroupDrag =
                isGroupDraggingRef.current &&
                selectedObjects?.some(
                  (obj) => obj.id === image.id && obj.type === "Image",
                );

              // Check if being dragged to main canvas (cross-canvas drag)
              const isBeingDraggedToMainCanvas =
                isInGroupDrag &&
                groupDragPreviews.some(
                  (preview) =>
                    preview.target === "main" &&
                    preview.type === "Image" &&
                    preview.data?.id?.replace("-ghost", "") === image.id,
                );

              // ONLY hide if being dragged to main canvas
              // If dragging within off-canvas, keep visible so handleGroupDragMove can update position
              if (isBeingDraggedToMainCanvas) {
                return null;
              }

              // CRITICAL: During group drag within off-canvas, render with full opacity
              // The handleGroupDragMove will update the Konva node position directly
              const elementOpacity = getElementOpacity(image.id, "Image");
              return renderImageElement({
                imageObj: image,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                opacity: elementOpacity,
              });
            });
          })()}

          {dragPreview &&
            dragPreview.target === "offCanvas" &&
            dragPreview.type === "Image" &&
            renderImageElement({
              imageObj: dragPreview.data,
              isOffCanvas: true,
              sourceStageRef: offCanvasStageRef.current,
              isPreview: true,
            })}

          {/* Transformer for image drag preview on off-canvas */}
          {(() => {
            if (
              !dragPreview ||
              dragPreview.target !== "offCanvas" ||
              dragPreview.type !== "Image" ||
              !offCanvasStageRef?.current ||
              !dragPreview.data?.id
            ) {
              return null;
            }

            const ghostId = dragPreview.data.id;
            const previewNode = offCanvasStageRef.current.findOne(
              `#${ghostId}`,
            );

            if (!previewNode) {
              return null;
            }

            const anchors = dragPreview.data.maintainAspectRatio
              ? ["bottom-right"]
              : ["middle-right", "bottom-center", "bottom-right"];

            return (
              <Transformer
                key={`image-preview-transformer-${ghostId}`}
                nodes={[previewNode]}
                rotateEnabled={!isDuplicatePlacementActive}
                flipEnabled={false}
                enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                keepRatio={dragPreview.data.maintainAspectRatio || false}
                centeredScaling={false}
                visible={true}
                listening={false}
              />
            );
          })()}

          {/* 🟦 Rectangles */}
          {(() => {
            // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
            // When dragging within the same canvas, elements should remain visible
            const isGroupDragging =
              isGroupDraggingRef.current &&
              selectedObjects &&
              selectedObjects.length > 1;
            const hasOffCanvasPreviews = groupDragPreviews.some(
              (p) => p.target === "offCanvas",
            );
            const shouldFilterDraggedElements =
              isGroupDragging && hasOffCanvasPreviews;

            const draggedElementIds = shouldFilterDraggedElements
              ? new Set(selectedObjects.map((obj) => obj.id))
              : new Set();

            // Filter out dragged elements only when showing previews (cross-canvas drag)
            const filteredRectangles = (
              shouldFilterDraggedElements
                ? offCanvasRectangles.filter(
                    (rect) => !draggedElementIds.has(rect.id),
                  )
                : offCanvasRectangles
            ).filter(
              (rect) =>
                !(
                  offCanvasSelectionOcclusionImages.length > 0 &&
                  (selectedObject?.id === rect.id ||
                    selectedObjects?.some((o) => o.id === rect.id))
                ),
            );

            // Sort rectangles so selected ones render last (on top)
            const sortedOffCanvasRectangles = [...filteredRectangles].sort(
              (a, b) => {
                const aIsSelected =
                  selectedObject?.id === a.id &&
                  selectedObject?.type === "Rect";
                const bIsSelected =
                  selectedObject?.id === b.id &&
                  selectedObject?.type === "Rect";
                // Selected rectangles come last (render on top)
                if (aIsSelected !== bIsSelected) {
                  return aIsSelected ? 1 : -1;
                }
                return 0; // Maintain original order for non-selected
              },
            );

            return sortedOffCanvasRectangles.map((rectangle) => {
              const elementOpacity = getElementOpacity(rectangle.id, "Rect");
              return renderRectangleElement({
                rectangle,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                opacity: elementOpacity,
              });
            });
          })()}
          {dragPreview &&
            dragPreview.target === "offCanvas" &&
            dragPreview.type === "Rect" &&
            renderRectangleElement({
              rectangle: dragPreview.data,
              isOffCanvas: true,
              sourceStageRef: offCanvasStageRef.current,
              isPreview: true,
            })}

          {/* Transformer for rectangle drag preview on off-canvas */}
          {(() => {
            if (
              !dragPreview ||
              dragPreview.target !== "offCanvas" ||
              dragPreview.type !== "Rect" ||
              !offCanvasStageRef?.current ||
              !dragPreview.data?.id
            ) {
              return null;
            }

            const ghostId = dragPreview.data.id;
            const previewNode = offCanvasStageRef.current.findOne(
              `#${ghostId}`,
            );

            if (!previewNode) {
              return null;
            }

            const anchors = dragPreview.data.maintainAspectRatio
              ? ["bottom-right"]
              : ["middle-right", "bottom-center", "bottom-right"];

            return (
              <Transformer
                key={`rect-preview-transformer-${ghostId}`}
                nodes={[previewNode]}
                rotateEnabled={!isDuplicatePlacementActive}
                flipEnabled={false}
                enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                keepRatio={dragPreview.data.maintainAspectRatio || false}
                centeredScaling={false}
                visible={true}
                listening={false}
              />
            );
          })()}

          {/* Group drag previews for off-canvas - render all elements in the group */}
          {(() => {
            const offCanvasPreviews = groupDragPreviews.filter(
              (preview) =>
                preview.target === "offCanvas" &&
                !(
                  preview.type === "Text" &&
                  preview.data?.showBackground === true
                ),
            );
            if (offCanvasPreviews.length > 0) {
            }
            return offCanvasPreviews;
          })().map((preview, index) => {
            const key = `group-preview-offcanvas-${preview.data?.id || index}`;
            switch (preview.type) {
              case "Rect":
                return (
                  <React.Fragment key={key}>
                    {renderRectangleElement({
                      rectangle: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                );
              case "Circle":
                return (
                  <React.Fragment key={key}>
                    {renderCircleElement({
                      circle: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                );
              case "Image":
                return !preview.data?.isOnTopLayer ? (
                  <React.Fragment key={key}>
                    {renderImageElement({
                      imageObj: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                ) : null; // Top-layer image previews are rendered in offCanvasTopLayerStage
              case "Text":
                return (
                  <React.Fragment key={key}>
                    {renderTextElement({
                      text: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                );
              case "Arrow":
                return (
                  <React.Fragment key={key}>
                    {renderArrowElement({
                      arrow: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                );
              case "DoubleSidedArrow":
                return (
                  <React.Fragment key={key}>
                    {renderDoubleSidedArrowElement({
                      arrow: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                );
              case "Line":
                return (
                  <React.Fragment key={key}>
                    {renderLineElement({
                      line: preview.data,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                );
              default:
                return null;
            }
          })}

          {/* Preview wrapper rectangle for off-canvas group drag previews */}
          {(() => {
            const offCanvasPreviews = groupDragPreviews.filter(
              (preview) => preview.target === "offCanvas",
            );
            if (offCanvasPreviews.length > 1) {
              // Calculate bounding box from all preview elements
              const bboxes = offCanvasPreviews.map((preview) => {
                // CRITICAL FIX: Preview entries wrap the actual element in `data`.
                // Also, unified arrows can be stored as "Line" in the preview type.
                const elementForBbox = preview?.data || preview;
                const elementTypeForBbox =
                  elementForBbox?.type || preview?.type || "Line";

                // CRITICAL FIX: Some preview strokes may lose arrow flags/points depending on selection typing.
                // If points are missing/empty on the preview, fall back to the source element (strip -ghost).
                if (
                  (elementTypeForBbox === "Line" ||
                    elementTypeForBbox === "Arrow" ||
                    elementTypeForBbox === "DoubleSidedArrow") &&
                  (!elementForBbox?.points ||
                    elementForBbox.points.length < 4) &&
                  typeof elementForBbox?.id === "string"
                ) {
                  const baseId = elementForBbox.id.replace(/-ghost$/, "");
                  const sourceStroke =
                    lines.find((l) => l.id === baseId) ||
                    offCanvasLines.find((l) => l.id === baseId);
                  if (sourceStroke?.points && sourceStroke.points.length >= 4) {
                    const merged = { ...sourceStroke, ...elementForBbox };
                    const mergedType = merged.type || elementTypeForBbox;
                    return getElementBoundingBox(merged, mergedType);
                  }
                }

                const bbox = getElementBoundingBox(
                  elementForBbox,
                  elementTypeForBbox,
                );
                if (
                  elementTypeForBbox === "Line" ||
                  elementTypeForBbox === "Arrow" ||
                  elementTypeForBbox === "DoubleSidedArrow"
                ) {
                  
                }
                return bbox;
              });
              const minX = Math.min(...bboxes.map((b) => b.x));
              const minY = Math.min(...bboxes.map((b) => b.y));
              const maxX = Math.max(...bboxes.map((b) => b.x + b.width));
              const maxY = Math.max(...bboxes.map((b) => b.y + b.height));
              const wrapperBBox = {
                x: minX - 2, // Small padding
                y: minY - 2,
                width: maxX - minX + 4,
                height: maxY - minY + 4,
              };

              // Only render if valid dimensions
              if (
                wrapperBBox.width > 0 &&
                wrapperBBox.height > 0 &&
                !isNaN(wrapperBBox.x) &&
                !isNaN(wrapperBBox.y) &&
                !isNaN(wrapperBBox.width) &&
                !isNaN(wrapperBBox.height)
              ) {
                return (
                  <Rect
                    key="wrapper-preview-offcanvas"
                    x={wrapperBBox.x}
                    y={wrapperBBox.y}
                    width={wrapperBBox.width}
                    height={wrapperBBox.height}
                    fill="rgba(59, 130, 246, 0.1)"
                    stroke="#0D6EFD"
                    strokeWidth={3}
                    draggable={false}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                  />
                );
              }
            }
            return null;
          })()}

          {/* ⚪ Circles */}
          {(() => {
            // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
            const isGroupDragging =
              isGroupDraggingRef.current &&
              selectedObjects &&
              selectedObjects.length > 1;
            const hasOffCanvasPreviews = groupDragPreviews.some(
              (p) => p.target === "offCanvas",
            );
            const shouldFilterDraggedElements =
              isGroupDragging && hasOffCanvasPreviews;

            const draggedElementIds = shouldFilterDraggedElements
              ? new Set(selectedObjects.map((obj) => obj.id))
              : new Set();

            // Filter out dragged elements only when showing previews (cross-canvas drag)
            const filteredCircles = (
              shouldFilterDraggedElements
                ? offCanvasCircles.filter(
                    (circle) => !draggedElementIds.has(circle.id),
                  )
                : offCanvasCircles
            ).filter(
              (circle) =>
                // Defer selected circle to post-occlusion pass when occlusion images are active,
                // so the selected element renders above the white blocker rect (mirrors Layer 5 on main canvas).
                !(
                  offCanvasSelectionOcclusionImages.length > 0 &&
                  (selectedObject?.id === circle.id ||
                    selectedObjects?.some((o) => o.id === circle.id))
                ),
            );

            // Sort circles so selected ones render last (on top)
            const sortedOffCanvasCircles = [...filteredCircles].sort((a, b) => {
              const aIsSelected =
                selectedObject?.id === a.id &&
                selectedObject?.type === "Circle";
              const bIsSelected =
                selectedObject?.id === b.id &&
                selectedObject?.type === "Circle";
              // Selected circles come last (render on top)
              if (aIsSelected !== bIsSelected) {
                return aIsSelected ? 1 : -1;
              }
              return 0; // Maintain original order for non-selected
            });

            return sortedOffCanvasCircles.map((circle) => {
              const elementOpacity = getElementOpacity(circle.id, "Circle");
              return renderCircleElement({
                circle,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                opacity: elementOpacity,
              });
            });
          })()}
          {dragPreview &&
            dragPreview.target === "offCanvas" &&
            dragPreview.type === "Circle" &&
            renderCircleElement({
              circle: dragPreview.data,
              isOffCanvas: true,
              sourceStageRef: offCanvasStageRef.current,
              isPreview: true,
            })}

          {/* Transformer for circle drag preview on off-canvas */}
          {(() => {
            if (
              !dragPreview ||
              dragPreview.target !== "offCanvas" ||
              dragPreview.type !== "Circle" ||
              !offCanvasStageRef?.current ||
              !dragPreview.data?.id
            ) {
              return null;
            }

            // Find the preview circle node by searching for the -ghost ID
            const ghostId = dragPreview.data.id; // This already has -ghost suffix
            const previewNode = offCanvasStageRef.current.findOne(
              `#${ghostId}`,
            );

            if (!previewNode) {
              // Node not found yet, will try again on next render
              return null;
            }

            // Configure anchors based on maintainAspectRatio
            const anchors = dragPreview.data.maintainAspectRatio
              ? ["bottom-right"]
              : ["middle-right", "bottom-center", "bottom-right"];

            return (
              <Transformer
                key={`circle-preview-transformer-${ghostId}`}
                nodes={[previewNode]}
                rotateEnabled={!isDuplicatePlacementActive}
                flipEnabled={false}
                enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                keepRatio={dragPreview.data.maintainAspectRatio || false}
                centeredScaling={false}
                visible={true}
                listening={false}
              />
            );
          })()}

          {/* 📝 Texts */}

          {/* ➖ Lines */}
          {(() => {
            // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
            const isGroupDragging =
              isGroupDraggingRef.current &&
              selectedObjects &&
              selectedObjects.length > 1;
            const hasOffCanvasPreviews = groupDragPreviews.some(
              (p) => p.target === "offCanvas",
            );
            const shouldFilterDraggedElements =
              isGroupDragging && hasOffCanvasPreviews;

            const draggedElementIds = shouldFilterDraggedElements
              ? new Set(selectedObjects.map((obj) => obj.id))
              : new Set();

            // Filter out dragged elements only when showing previews (cross-canvas drag)
            const filteredLines = (
              shouldFilterDraggedElements
                ? offCanvasLines.filter(
                    (line) => !draggedElementIds.has(line.id),
                  )
                : offCanvasLines
            ).filter(
              (line) =>
                !(
                  offCanvasSelectionOcclusionImages.length > 0 &&
                  (selectedObject?.id === line.id ||
                    selectedObjects?.some((o) => o.id === line.id))
                ),
            );

            // NOTE: Do NOT filter out the line when dragPreview targets main - that would unmount the node and end the drag.
            // Instead we pass opacity 0 when rendering below so only the preview is visible.

            // Sort lines so selected ones render last (on top)
            const sortedOffCanvasLines = [...filteredLines].sort((a, b) => {
              // CRITICAL: Check if selected by ID only, not by type, since type can change
              const aIsSelected =
                selectedObject?.id === a.id &&
                (selectedObject?.type === "Line" ||
                  selectedObject?.type === "Arrow" ||
                  selectedObject?.type === "DoubleSidedArrow");
              const bIsSelected =
                selectedObject?.id === b.id &&
                (selectedObject?.type === "Line" ||
                  selectedObject?.type === "Arrow" ||
                  selectedObject?.type === "DoubleSidedArrow");
              // Selected lines come last (render on top)
              if (aIsSelected !== bIsSelected) {
                return aIsSelected ? 1 : -1;
              }
              return 0; // Maintain original order for non-selected
            });

            return sortedOffCanvasLines.map((line) => {
              const isLineTypeSelected =
                selectedObject?.type === "Line" ||
                selectedObject?.type === "Arrow" ||
                selectedObject?.type === "DoubleSidedArrow";
              const isSelectedLine =
                selectedObject?.id === line.id && isLineTypeSelected;

              // Detect cross-canvas drag: off-canvas element being dragged to main canvas
              const linePreviewId =
                dragPreview?.data?.id?.replace("-ghost", "") ??
                dragPreviewStateRef.current?.id;
              const linePreviewTarget =
                dragPreview?.target ?? dragPreviewStateRef.current?.target;
              const isLinePreviewType =
                (dragPreview?.type ?? dragPreviewStateRef.current?.type) ===
                  "Line" ||
                (dragPreview?.type ?? dragPreviewStateRef.current?.type) ===
                  "Arrow" ||
                (dragPreview?.type ?? dragPreviewStateRef.current?.type) ===
                  "DoubleSidedArrow";
              const hasPreviewOnMain =
                lineSourceHiddenForDragRef.current[line.id] === true ||
                (linePreviewTarget === "main" &&
                  isLinePreviewType &&
                  linePreviewId === line.id);

              // CRITICAL FIX: When dragging this off-canvas element to main canvas,
              // handleDragPreview sets selectedObject with main-canvas coordinates.
              // Merging those into effectiveLine causes React-Konva to write main-canvas
              // coords to the Konva Group node every animation frame, fighting Konva's
              // own drag tracking → visible jitter. Freeze x/y to the last known
              // off-canvas position instead (same pattern as the main-canvas render block).
              let effectiveLine;
              if (hasPreviewOnMain && isSelectedLine) {
                const frozen = lineLastSourcePositionRef.current[line.id];
                effectiveLine = {
                  ...line,
                  ...selectedObject,
                  x: frozen ? frozen.x : (line.x ?? 0),
                  y: frozen ? frozen.y : (line.y ?? 0),
                  _useNodePositionForGroup: true,
                };
              } else {
                effectiveLine = isSelectedLine
                  ? { ...line, ...selectedObject }
                  : line;
              }

              const elementOpacity = getElementOpacity(
                effectiveLine.id,
                "Line",
              );
              return renderLineElement({
                line: {
                  ...effectiveLine,
                  opacity: hasPreviewOnMain
                    ? 0
                    : (effectiveLine.opacity ?? elementOpacity),
                },
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                saveToHistory,
                opacity: hasPreviewOnMain ? 0 : elementOpacity,
              });
            });
          })()}
          {/* Line ghost on off-canvas (no arrow ghosts) */}
          {(() => {
            // CRITICAL: Only render drag-preview ghosts for plain lines.
            // Unified arrows (single or double sided) are already rendered via UnifiedLineArrowElement
            // and manage their own blue orbs correctly; a separate ghost layer causes the orbs to
            // appear behind the stroke during cross-canvas drag.
            if (
              dragPreview &&
              dragPreview.target === "offCanvas" &&
              dragPreview.type === "Line"
            ) {
              return renderLineElement({
                line: dragPreview.data,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                saveToHistory,
                isPreview: true,
              });
            }
            const ref = dragPreviewStateRef.current;
            const ctx = lineDragPreviewContextRef.current;
            if (
              ref?.target === "offCanvas" &&
              ctx?.element &&
              ref?.type === "Line"
            ) {
              const lineData = {
                ...ctx.element,
                id: `${ref.id}-ghost`,
                x: ref.x,
                y: ref.y,
                opacity: 1,
                points: ctx.element?.points ?? [0, 0, 0, 0],
              };
              return renderLineElement({
                line: lineData,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                saveToHistory,
                isPreview: true,
              });
            }
            return null;
          })()}

          {/* ➡ Arrows */}
          {(() => {
            // Sort arrows so selected ones render last (on top)
            const sortedOffCanvasArrows = [...offCanvasArrows].sort((a, b) => {
              const aIsSelected =
                selectedObject?.id === a.id && selectedObject?.type === "Arrow";
              const bIsSelected =
                selectedObject?.id === b.id && selectedObject?.type === "Arrow";
              // Selected arrows come last (render on top)
              if (aIsSelected !== bIsSelected) {
                return aIsSelected ? 1 : -1;
              }
              return 0; // Maintain original order for non-selected
            });

            return sortedOffCanvasArrows.map((arrow) => {
              const elementOpacity = getElementOpacity(arrow.id, "Arrow");
              return (
                <React.Fragment key={`off-${arrow.id}`}>
                  {renderArrowElement({
                    arrow,
                    isOffCanvas: true,
                    sourceStageRef: offCanvasStageRef.current,
                    opacity: elementOpacity,
                  })}
                </React.Fragment>
              );
            });
          })()}
          {dragPreview &&
            dragPreview.target === "offCanvas" &&
            dragPreview.type === "Arrow" &&
            renderArrowElement({
              arrow: dragPreview.data,
              isOffCanvas: true,
              sourceStageRef: offCanvasStageRef.current,
              isPreview: true,
            })}

          {/* ⬅➡ Double-Sided Arrows */}
          {(() => {
            // Sort double-sided arrows so selected ones render last (on top)
            const sortedOffCanvasDoubleArrows = [
              ...offCanvasDoubleSidedArrows,
            ].sort((a, b) => {
              const aIsSelected =
                selectedObject?.id === a.id &&
                selectedObject?.type === "DoubleSidedArrow";
              const bIsSelected =
                selectedObject?.id === b.id &&
                selectedObject?.type === "DoubleSidedArrow";
              // Selected arrows come last (render on top)
              if (aIsSelected !== bIsSelected) {
                return aIsSelected ? 1 : -1;
              }
              return 0; // Maintain original order for non-selected
            });

            return sortedOffCanvasDoubleArrows.map((arrow) => {
              const elementOpacity = getElementOpacity(
                arrow.id,
                "DoubleSidedArrow",
              );
              return (
                <React.Fragment key={`off-double-${arrow.id}`}>
                  {renderDoubleSidedArrowElement({
                    arrow,
                    isOffCanvas: true,
                    sourceStageRef: offCanvasStageRef.current,
                    opacity: elementOpacity,
                  })}
                </React.Fragment>
              );
            });
          })()}
          {dragPreview &&
            dragPreview.target === "offCanvas" &&
            dragPreview.type === "DoubleSidedArrow" &&
            renderDoubleSidedArrowElement({
              arrow: dragPreview.data,
              isOffCanvas: true,
              sourceStageRef: offCanvasStageRef.current,
              isPreview: true,
            })}

          {offCanvasTexts &&
            Array.isArray(offCanvasTexts) &&
            (() => {
              // CRITICAL: Filter out texts that are in selectedObjects array
              // They will be rendered in the selected layer with full opacity
              // This prevents duplicate rendering and ghost texts during drag
              const isElementInSelectedObjects = (elementId, elementType) => {
                if (!selectedObjects || selectedObjects.length === 0)
                  return false;
                return selectedObjects.some(
                  (obj) => obj.id === elementId && obj.type === elementType,
                );
              };

              // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
              // This prevents duplicate rendering (elements from state + Konva nodes)
              const isGroupDragging =
                isGroupDraggingRef.current &&
                selectedObjects &&
                selectedObjects.length > 1;
              const hasOffCanvasPreviews = groupDragPreviews.some(
                (p) => p.target === "offCanvas",
              );
              const shouldFilterDraggedElements =
                isGroupDragging && hasOffCanvasPreviews;

              const draggedElementIds = shouldFilterDraggedElements
                ? new Set(selectedObjects.map((obj) => obj.id))
                : new Set();

              // Filter out selected texts and dragged texts
              const filteredOffCanvasTexts = offCanvasTexts.filter((t) => {
                // CRITICAL: During cross-canvas group drag, hide texts that are being dragged
                // They will be shown via preview elements
                if (
                  shouldFilterDraggedElements &&
                  draggedElementIds.has(t.id)
                ) {
                  return false;
                }

                // CRITICAL: Filter out texts that are being dragged to main canvas
                // Check if this text is in selectedObject or selectedObjects with _isInOffCanvasDuringDrag === false
                if (
                  selectedObject &&
                  selectedObject.id === t.id &&
                  selectedObject._isInOffCanvasDuringDrag === false
                ) {
                  return false; // Don't render on off-canvas, it's being dragged to main canvas
                }

                if (selectedObjects && selectedObjects.length > 0) {
                  const objBeingDraggedToMain = selectedObjects.find(
                    (obj) =>
                      obj.id === t.id && obj._isInOffCanvasDuringDrag === false,
                  );
                  if (objBeingDraggedToMain) {
                    return false; // Don't render on off-canvas, it's being dragged to main canvas
                  }
                }

                // Only hide mirrored IDs while an active cross-canvas drag preview is present.
                // Legitimate clones can exist on both canvases and may share nested identity fields.
                const isInMainCanvasArray = texts.some(
                  (mainText) => mainText.id === t.id,
                );
                const hasActiveMainTargetPreviewForText =
                  (dragPreview &&
                    dragPreview.type === "Text" &&
                    dragPreview.target === "main" &&
                    dragPreview.data?.id?.replace("-ghost", "") === t.id) ||
                  groupDragPreviews.some((preview) => {
                    const previewId =
                      preview.data?.id?.replace("-ghost", "") || preview.data?.id;
                    return preview.target === "main" && previewId === t.id;
                  });
                if (isInMainCanvasArray && hasActiveMainTargetPreviewForText) {
                  return false;
                }

                const isSelected =
                  selectedObject?.id === t.id &&
                  (selectedObject?.type === "Text" ||
                    selectedObject?.type?.includes("Text"));
                const isInSelectedObjects = isElementInSelectedObjects(
                  t.id,
                  "Text",
                );

                // CRITICAL: Don't filter out selected texts from off-canvas rendering
                // because off-canvas stage doesn't have a selected layer to render them
                // Unlike main canvas which has a selected layer, off-canvas needs to render selected texts normally
                if (isSelected || isInSelectedObjects) {
                }

                // Always return true to keep all texts in off-canvas normal rendering (unless being dragged)
                return true;
              });

              // Sort texts so selected ones render last (on top)
              const sortedOffCanvasTexts = [...filteredOffCanvasTexts].sort(
                (a, b) => {
                  const aIsSelected =
                    selectedObject?.id === a.id &&
                    (selectedObject?.type === "Text" ||
                      selectedObject?.type?.includes("Text"));
                  const bIsSelected =
                    selectedObject?.id === b.id &&
                    (selectedObject?.type === "Text" ||
                      selectedObject?.type?.includes("Text"));
                  // Selected texts come last (render on top)
                  if (aIsSelected !== bIsSelected) {
                    return aIsSelected ? 1 : -1;
                  }
                  return 0; // Maintain original order for non-selected
                },
              );

              // CRITICAL FIX: During GROUP drag, do NOT render texts with _isInOffCanvasDuringDrag flag
              // They are already being rendered as preview elements via groupDragPreviews
              // Only render them for INDIVIDUAL drag (when not in group drag mode)
              const textsBeingDraggedToOffCanvas = [];

              // Only add texts being dragged if NOT in group drag mode
              const isInGroupDragMode =
                isGroupDraggingRef.current &&
                selectedObjects &&
                selectedObjects.length > 1;

              if (!isInGroupDragMode) {
                // Check selectedObject (individual drag only)
                if (
                  selectedObject &&
                  selectedObject.type === "Text" &&
                  selectedObject._isInOffCanvasDuringDrag === true
                ) {
                  // Find the full text object from texts array
                  const fullText = texts.find(
                    (t) => t.id === selectedObject.id,
                  );
                  if (fullText) {
                    textsBeingDraggedToOffCanvas.push({
                      ...fullText,
                      ...selectedObject, // Merge with selectedObject to get updated position
                      _isInOffCanvasDuringDrag: true,
                    });
                  }
                }

                // Check selectedObjects array (should not happen for individual drag, but keep for safety)
                if (selectedObjects && selectedObjects.length === 1) {
                  selectedObjects.forEach((obj) => {
                    if (
                      obj.type === "Text" &&
                      obj._isInOffCanvasDuringDrag === true
                    ) {
                      // Avoid duplicates
                      if (
                        !textsBeingDraggedToOffCanvas.some(
                          (t) => t.id === obj.id,
                        )
                      ) {
                        const fullText = texts.find((t) => t.id === obj.id);
                        if (fullText) {
                          textsBeingDraggedToOffCanvas.push({
                            ...fullText,
                            ...obj, // Merge with obj to get updated position
                            _isInOffCanvasDuringDrag: true,
                          });
                        }
                      }
                    }
                  });
                }
              }

              // When dragPreview is showing this text on off-canvas, don't also render it in the list
              // (avoids duplicate previews: one at wrong position and one correct from dragPreview)
              const dragPreviewTextId =
                dragPreview?.type === "Text" &&
                dragPreview?.target === "offCanvas" &&
                dragPreview?.data?.id
                  ? dragPreview.data.id.replace("-ghost", "")
                  : null;
              const textsBeingDraggedFiltered = dragPreviewTextId
                ? textsBeingDraggedToOffCanvas.filter(
                    (t) => t.id !== dragPreviewTextId,
                  )
                : textsBeingDraggedToOffCanvas;

              const allTextsToRender = [
                ...sortedOffCanvasTexts,
                ...textsBeingDraggedFiltered,
              ];

              return allTextsToRender.map((text) => {
                const elementOpacity = getElementOpacity(text.id, "Text");
                const bbox = getElementBoundingBox(text, "Text");
                return (
                  <React.Fragment key={`off-canvas-text-${text.id}`}>
                    {/* Konva Rect blocker - render first so it's behind the text element */}
                    <Group
                      x={bbox.x}
                      y={bbox.y}
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                    >
                      <Rect
                        x={0}
                        y={0}
                        width={bbox.width}
                        height={bbox.height}
                        fill="transparent"
                        stroke={null}
                        strokeWidth={0}
                        opacity={0}
                        listening={false}
                        perfectDrawEnabled={false}
                        shadowForStrokeEnabled={false}
                      />
                    </Group>
                    {/* Render text element after blocker so it renders on top */}
                    {renderTextElement({
                      text,
                      isOffCanvas: true,
                      sourceStageRef: offCanvasStageRef.current,
                      opacity: elementOpacity,
                      setOffCanvasTexts,
                      offCanvasTexts,
                      setTexts: setOffCanvasTexts,
                      texts: offCanvasTexts,
                      handleElementDragEnd,
                      // Let the text renderer handle onDragEnd internally (like rectangles)
                      // It will call handleElementDragEnd with proper parameters
                      onTransformEnd: (transformData) => {
                        justFinishedTransformRef.current = true;
                        // Calculate the updated state BEFORE calling setState
                        // This ensures saveToHistory receives the correct updated state
                        const updatedOffCanvasTexts = offCanvasTexts.map((t) =>
                          t.id === transformData.id
                            ? { ...t, ...transformData }
                            : t,
                        );

                        setOffCanvasTexts((prev) =>
                          prev.map((t) =>
                            t.id === transformData.id
                              ? { ...t, ...transformData }
                              : t,
                          ),
                        );

                        // Also update selectedText to reflect the transform changes
                        if (setSelectedText) {
                          setSelectedText((prev) =>
                            prev?.id === transformData.id
                              ? { ...prev, ...transformData }
                              : prev,
                          );
                        }
                        if (setSelectedObject) {
                          setSelectedObject((prev) =>
                            prev?.id === transformData.id
                              ? { ...prev, ...transformData, type: "Text" }
                              : prev,
                          );
                        }

                        // CRITICAL: Store the updated texts array in ref so element_edit_complete can use it
                        // This ensures rotation and other transform properties are saved to history
                        // Only store if element is selected/being edited (when pendingChanges will be set)
                        const isTextSelected =
                          selectedObject?.id === transformData.id &&
                          (selectedObject?.type === "Text" ||
                            selectedObject?.type?.includes("Text"));
                        if (
                          (isTextSelected || isElementBeingEdited) &&
                          latestUpdatedTextsRef
                        ) {
                          // Store the updated texts array (with rotation) in the ref
                          latestUpdatedTextsRef.current.offCanvas =
                            updatedOffCanvasTexts;
                          setPendingChanges(true);
                        } else if (isElementBeingEdited) {
                          setPendingChanges(true);
                        }

                        // CRITICAL: Don't save to history here - history will be saved when element is unselected
                        // This groups all transform changes (width, height, rotation, etc.) into a single history entry
                      },
                      saveToHistory,
                      onClick,
                      setSelectedObject,
                      setSelectedText,
                      setSelectedTextId,
                      setShowTextBox,
                      setShowEditTask,
                      setShowEditNote,
                      setEditTextBoxContent,
                      setSelectedObjects,
                      setAction,
                      ACTIONS,
                      setEditing,
                      transformerRef: offCanvasTransformerRef,
                      selectedObject,
                      selectedText,
                      setSelectedImage,
                      getTextStyle,
                      formatListContent,
                      hasUserContent,
                      estimateKonvaTextHeight,
                      stripHtmlPreserveFormatting,
                      hasTaskContent,
                      hasNoteContent,
                      calculateGroupHeight,
                      canvasWidth: offCanvasSideWidth,
                      canvasHeight: fixedStageHeightPx,
                      canvasResizeTrigger,
                    })}
                  </React.Fragment>
                );
              });
            })()}
          {dragPreview &&
            dragPreview.target === "offCanvas" &&
            dragPreview.type === "Text" &&
            renderTextElement({
              text: dragPreview.data,
              isOffCanvas: true,
              sourceStageRef: offCanvasStageRef.current,
              isPreview: true,
            })}

          {/* Transformer for text drag preview on off-canvas */}
          {(() => {
            if (
              !dragPreview ||
              dragPreview.target !== "offCanvas" ||
              dragPreview.type !== "Text" ||
              !offCanvasStageRef?.current ||
              !dragPreview.data?.id
            ) {
              return null;
            }

            const ghostId = dragPreview.data.id;
            const previewNode = offCanvasStageRef.current.findOne(
              `#${ghostId}`,
            );

            if (!previewNode) {
              return null;
            }

            // Text elements only have horizontal resize anchors
            const anchors = ["middle-left", "middle-right"];

            return (
              <Transformer
                key={`text-preview-transformer-${ghostId}`}
                nodes={[previewNode]}
                rotateEnabled={false}
                flipEnabled={false}
                enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                keepRatio={false}
                centeredScaling={false}
                visible={true}
                listening={false}
              />
            );
          })()}

          {/* 🖼 Top-layer images — rendered AFTER texts so they always sit above text
              elements (and all other non-top-layer content) in the off-canvas layer.
              When a selection causes demotion these images move to the occlusion pass
              below and offCanvasImagesTop becomes empty. */}
          {(() => {
            const sortedOffCanvasImagesTop = [...offCanvasImagesTop].sort(
              (a, b) => {
                const aIsSelected = selectedTopPriorityImageIds.has(a.id);
                const bIsSelected = selectedTopPriorityImageIds.has(b.id);
                if (aIsSelected !== bIsSelected) {
                  return aIsSelected ? 1 : -1;
                }
                return 0;
              },
            );

            return sortedOffCanvasImagesTop.map((image) => {
              // DEBUG: Log top-layer image rendering
              if (image.id === 'marker-1777364582910-3-1182cf99-9539-434a-a049-2145d946ce86') {
                console.log("DEBUG: Image being rendered in TOP-LAYER pass:", {
                  id: image.id,
                  isOnTopLayer: image.isOnTopLayer
                });
              }
              
              const isInGroupDrag =
                isGroupDraggingRef.current &&
                selectedObjects?.some(
                  (obj) => obj.id === image.id && obj.type === "Image",
                );
              const hasLiveOffCanvasTopLayerPreview =
                activeOffCanvasTopLayerPreviewSourceIds.has(image.id);
              const isBeingDraggedToMainCanvasInGroup =
                isInGroupDrag &&
                groupDragPreviews.some(
                  (preview) =>
                    preview.target === "main" &&
                    preview.type === "Image" &&
                    preview.data?.isOnTopLayer === true &&
                    preview.data?.id?.replace("-ghost", "") === image.id,
                );
              
              if (isBeingDraggedToMainCanvasInGroup) {
                return null;
              }
              if (hasLiveOffCanvasTopLayerPreview) {
                return null;
              }

              // When this image is the selected top-layer image it is exclusively
              // owned by the top-layer wrapper Stage (listening=true there).
              // Rendering it here too would leave a frozen ghost copy at the
              // original position while the top-layer-stage node moves with the drag.
              if (
                hasSelectedOffCanvasTopLayerImage &&
                image.id === selectedObject?.id
              ) {
                return null;
              }

              const elementOpacity = getElementOpacity(image.id, "Image");
              return renderImageElement({
                imageObj: image,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                opacity: elementOpacity,
              });
            });
          })()}

          {/* Selected off-canvas non-top-layer images — render AFTER top-layer image pass
              so their local Transformer anchors stay visible above all off-canvas images. */}
          {(() => {
            // During active off-canvas group drag, selected non-top images are already
            // rendered/moved in the regular bottom-image pass. Rendering them again in this
            // selected overlay pass creates a second copy at stale coordinates.
            if (
              isGroupDraggingRef.current &&
              Array.isArray(selectedObjects) &&
              selectedObjects.length > 1
            ) {
              return null;
            }
            const selectedImageIds = new Set();
            if (selectedObject?.type === "Image" && selectedObject?.id) {
              selectedImageIds.add(selectedObject.id);
            }
            (selectedObjects || []).forEach((obj) => {
              if (obj?.type === "Image" && obj?.id) {
                selectedImageIds.add(obj.id);
              }
            });

            if (selectedImageIds.size === 0) return null;

            const selectedBottomImages = offCanvasImages
              .filter(
                (img) =>
                  selectedImageIds.has(img.id) && img.isOnTopLayer !== true,
              )
              .map((img) => {
                const selectedOverlay =
                  (selectedObject?.id === img.id && selectedObject) ||
                  (selectedObjects || []).find((o) => o.id === img.id) ||
                  null;
                return selectedOverlay ? { ...img, ...selectedOverlay } : img;
              });

            return selectedBottomImages.map((imageObj) =>
              renderImageElement({
                imageObj,
                isOffCanvas: true,
                sourceStageRef: offCanvasStageRef.current,
                opacity: getElementOpacity(imageObj.id, "Image"),
              }),
            );
          })()}

          {/* Off-canvas occlusion pass — rendered LATE so the white blocker Rect sits above
              all regular elements (rects, circles, lines, arrows, images, texts), mirroring
              the Layer-5 behaviour on the main canvas.  Selected elements are filtered from
              their normal type-groups above and re-rendered here AFTER the occlusion images,
              so they remain visually on top (same as the selected-element render order in
              Layer 5 on the main canvas). */}
          {offCanvasSelectionOcclusionImages.length > 0 &&
            (() => {
              // Re-render every selected off-canvas element (except images already in the
              // occlusion pass) after the occlusion images so they sit above the blocker.
              const deferredElements = (
                selectedObjects?.length > 0
                  ? selectedObjects
                  : selectedObject
                    ? [selectedObject]
                    : []
              ).filter((obj) => {
                if (!obj?.id || !obj?.type) return false;
                // Images being demoted are already in the occlusion pass — skip them here.
                if (
                  offCanvasSelectionOcclusionImages.some(
                    (img) => img.id === obj.id,
                  )
                )
                  return false;
                // Only defer elements that actually live in the off-canvas arrays.
                switch (obj.type) {
                  case "Rect":
                    return offCanvasRectangles.some((r) => r.id === obj.id);
                  case "Circle":
                    return offCanvasCircles.some((c) => c.id === obj.id);
                  case "Line":
                  case "Arrow":
                  case "DoubleSidedArrow":
                    return offCanvasLines.some((l) => l.id === obj.id);
                  case "Image":
                    // CRITICAL FIX: Only defer top-layer images to prevent non-top-layer images
                    // from appearing on top of other elements when selected
                    const imageInOffCanvas = offCanvasImages.find((i) => i.id === obj.id);
                    const shouldDefer = imageInOffCanvas && imageInOffCanvas.isOnTopLayer === true;
                    
                    // DEBUG: Log what's happening with image filtering
                    if (imageInOffCanvas && imageInOffCanvas.isOnTopLayer !== true) {
                      console.log("DEBUG: Non-top-layer image being filtered out of deferred pass:", {
                        id: obj.id,
                        isOnTopLayer: imageInOffCanvas.isOnTopLayer,
                        isSelected: true
                      });
                    }
                    
                    return shouldDefer;
                  default:
                    return false;
                }
              });

              if (DEBUG_SELECTION_FLOW) {
                const deferredIds = deferredElements.map((el) => `${el.type}:${el.id}`);
                const selectedNonTopOffCanvasImageIds = (
                  selectedObjects?.length > 0 ? selectedObjects : selectedObject ? [selectedObject] : []
                )
                  .filter((obj) => obj?.type === "Image")
                  .map((obj) => obj.id)
                  .filter((id) =>
                    offCanvasImages.some((img) => img.id === id && img.isOnTopLayer !== true),
                  );
              }

              return (
                <>
                  {/* 1. Occlusion images with their white blocker rects — above regular elements */}
                  {offCanvasSelectionOcclusionImages.map((imageObj) => {
                    const elementOpacity = getElementOpacity(
                      imageObj.id,
                      "Image",
                    );
                    return (
                      <React.Fragment
                        key={`offcanvas-selection-occlusion-${imageObj.id}`}
                      >
                        {renderImageElement({
                          imageObj,
                          isOffCanvas: true,
                          sourceStageRef: offCanvasStageRef.current,
                          opacity: elementOpacity,
                        })}
                      </React.Fragment>
                    );
                  })}
                  {/* 2. Deferred selected elements — rendered AFTER occlusion images so they
                       appear on top, matching Layer-5 behaviour on the main canvas. */}
                  {deferredElements.map((obj) => {
                    // DEBUG: Log deferred elements
                    if (obj.type === "Image") {
                      const imageInOffCanvas = offCanvasImages.find((i) => i.id === obj.id);
                      console.log("DEBUG: Rendering deferred image:", {
                        id: obj.id,
                        isOnTopLayer: imageInOffCanvas?.isOnTopLayer,
                        type: obj.type
                      });
                    }
                    
                    switch (obj.type) {
                      case "Rect": {
                        const rect = offCanvasRectangles.find(
                          (r) => r.id === obj.id,
                        );
                        if (!rect) return null;
                        const rectPreviewId =
                          dragPreview?.data?.id?.replace("-ghost", "") ||
                          dragPreviewStateRef.current?.id;
                        const rectPreviewTarget =
                          dragPreview?.target ||
                          dragPreviewStateRef.current?.target;
                        const rectPreviewType =
                          dragPreview?.type ??
                          dragPreviewStateRef.current?.type;
                        const isRectPreviewForMain =
                          rectPreviewTarget === "main" &&
                          rectPreviewType === "Rect" &&
                          rectPreviewId === rect.id;
                        return renderRectangleElement({
                          // Keep source node mounted at off-canvas coords during off→main drag preview.
                          // Merging selected-object preview coords here can prematurely disrupt drag end.
                          rectangle: isRectPreviewForMain
                            ? { ...rect, opacity: 0 }
                            : { ...rect, ...obj },
                          isOffCanvas: true,
                          sourceStageRef: offCanvasStageRef.current,
                          opacity: isRectPreviewForMain
                            ? 0
                            : getElementOpacity(rect.id, "Rect"),
                        });
                      }
                      case "Circle": {
                        const circle = offCanvasCircles.find(
                          (c) => c.id === obj.id,
                        );
                        if (!circle) return null;
                        const circlePreviewId =
                          dragPreview?.data?.id?.replace("-ghost", "") ||
                          dragPreviewStateRef.current?.id;
                        const circlePreviewTarget =
                          dragPreview?.target ||
                          dragPreviewStateRef.current?.target;
                        const circlePreviewType =
                          dragPreview?.type ??
                          dragPreviewStateRef.current?.type;
                        const isCirclePreviewForMain =
                          circlePreviewTarget === "main" &&
                          circlePreviewType === "Circle" &&
                          circlePreviewId === circle.id;
                        return renderCircleElement({
                          // Keep source node mounted at off-canvas coords during off→main drag preview.
                          // Merging selected-object preview coords here can prematurely disrupt drag end.
                          circle: isCirclePreviewForMain
                            ? { ...circle, opacity: 0 }
                            : { ...circle, ...obj },
                          isOffCanvas: true,
                          sourceStageRef: offCanvasStageRef.current,
                          opacity: isCirclePreviewForMain
                            ? 0
                            : getElementOpacity(circle.id, "Circle"),
                        });
                      }
                      case "Line":
                      case "Arrow":
                      case "DoubleSidedArrow": {
                        const line = offCanvasLines.find(
                          (l) => l.id === obj.id,
                        );
                        if (!line) return null;
                        return renderLineElement({
                          line: { ...line, ...obj },
                          isOffCanvas: true,
                          sourceStageRef: offCanvasStageRef.current,
                          saveToHistory,
                          opacity: getElementOpacity(line.id, "Line"),
                        });
                      }
                      case "Image": {
                        const image = offCanvasImages.find(
                          (i) => i.id === obj.id,
                        );
                        if (!image) return null;
                        return renderImageElement({
                          imageObj: { ...image, ...obj },
                          isOffCanvas: true,
                          sourceStageRef: offCanvasStageRef.current,
                          opacity: getElementOpacity(image.id, "Image"),
                        });
                      }
                      default:
                        return null;
                    }
                  })}
                </>
              );
            })()}

          {/* Blue highlight rectangles for selected elements on off-canvas */}
          {!isLeavingCanvas &&
            (() => {
              const safeSelectedObjects = Array.from(
                new Map(
                  (selectedObjects || [])
                    .filter((obj) => obj?.id && obj?.type)
                    .map((obj) => [String(obj.id), obj]),
                ).values(),
              );

              if (safeSelectedObjects.length === 0) {
                return null;
              }

              // CRITICAL: Use dragUpdateTrigger to force re-render during drag
              // This ensures selection boxes update their positions in real-time
              // dragUpdateTrigger is incremented in handleGroupDragMove on each drag move
              // eslint-disable-next-line no-unused-vars
              const _dragTrigger = dragUpdateTrigger;

              // Helper function to get actual element from state arrays (checking off-canvas arrays)
              // CRITICAL: Also get current position from Konva node to ensure we use the latest position
              const getActualElement = (obj) => {
                if (!obj || !obj.id || !obj.type) return obj;

                // First, get element from state arrays
                let elementFromState = null;
                switch (obj.type) {
                  case "Rect": {
                    elementFromState = offCanvasRectangles.find(
                      (r) => r.id === obj.id,
                    );
                    break;
                  }
                  case "Circle": {
                    elementFromState = offCanvasCircles.find(
                      (c) => c.id === obj.id,
                    );
                    break;
                  }
                  case "Image": {
                    elementFromState = offCanvasImages.find(
                      (img) => img.id === obj.id,
                    );
                    break;
                  }
                  case "Text": {
                    elementFromState = offCanvasTexts.find(
                      (t) => t.id === obj.id,
                    );
                    break;
                  }
                  case "Arrow": {
                    elementFromState = offCanvasArrows.find(
                      (a) => a.id === obj.id,
                    );
                    if (!elementFromState) {
                      elementFromState = offCanvasLines.find((l) => {
                        if (l.id !== obj.id) return false;
                        const hasStartArrow =
                          l.hasStartArrow === true ||
                          l.pointerAtBeginning === true;
                        const hasEndArrow =
                          l.hasEndArrow === true || l.pointerAtEnd === true;
                        return hasStartArrow || hasEndArrow;
                      });
                    }
                    break;
                  }
                  case "DoubleSidedArrow": {
                    elementFromState = offCanvasDoubleSidedArrows.find(
                      (a) => a.id === obj.id,
                    );
                    if (!elementFromState) {
                      elementFromState = offCanvasLines.find((l) => {
                        if (l.id !== obj.id) return false;
                        const hasStartArrow =
                          l.hasStartArrow === true ||
                          l.pointerAtBeginning === true;
                        const hasEndArrow =
                          l.hasEndArrow === true || l.pointerAtEnd === true;
                        return hasStartArrow && hasEndArrow;
                      });
                    }
                    break;
                  }
                  case "Line": {
                    // CRITICAL FIX: UnifiedLineArrowElement instances (including Arrow/DoubleSidedArrow)
                    // may appear in selectedObjects as type "Line". For wrapper/selection box dimensions,
                    // pull by ID from offCanvasLines regardless of arrow-head flags so arrows are included.
                    elementFromState = offCanvasLines.find(
                      (l) => l.id === obj.id,
                    );
                    break;
                  }
                  default:
                    elementFromState = obj;
                }

                const element = elementFromState || obj;

                // CRITICAL: Get current position from Konva node to ensure we use the latest position
                // This fixes the issue where blue highlight shows at old position after element is moved
                const stage = offCanvasStageRef?.current;
                if (stage && element?.id) {
                  const node = stage.findOne(`#${element.id}`);
                  if (node) {
                    // Get current position from Konva node (this is the actual rendered position)
                    const currentX = node.x();
                    const currentY = node.y();

                    // If position is available from node, use it (it's the current rendered position)
                    if (currentX !== undefined && currentY !== undefined) {
                      return {
                        ...element,
                        x: currentX,
                        y: currentY,
                      };
                    }
                  }
                }

                return element;
              };

              // First, get all objects on off-canvas (including text) for wrapper box calculation
              const allOffCanvasObjects = safeSelectedObjects.filter((obj) => {
                // Check if element is in off-canvas arrays
                switch (obj.type) {
                  case "Rect":
                    return offCanvasRectangles.some((r) => r.id === obj.id);
                  case "Circle":
                    return offCanvasCircles.some((c) => c.id === obj.id);
                  case "Image":
                    return offCanvasImages.some((img) => img.id === obj.id);
                  case "Text":
                    return offCanvasTexts.some((t) => t.id === obj.id);
                  case "Arrow":
                    // CRITICAL FIX: Unified arrows are stored in offCanvasLines.
                    // Selection can label them as Arrow/Line and flags may be missing — use ID presence.
                    return (
                      offCanvasLines.some((l) => l.id === obj.id) ||
                      offCanvasArrows.some((a) => a.id === obj.id)
                    );
                  case "DoubleSidedArrow":
                    // CRITICAL FIX: Unified double-sided arrows are stored in offCanvasLines.
                    // Use ID presence so wrapper includes them even if flags are missing.
                    return (
                      offCanvasLines.some((l) => l.id === obj.id) ||
                      offCanvasDoubleSidedArrows.some((a) => a.id === obj.id)
                    );
                  case "Line":
                    // CRITICAL FIX: For wrapper dimensions, treat ANY stroke in offCanvasLines as present,
                    // regardless of arrow flags (unified arrows can be typed as "Line" in selection).
                    return offCanvasLines.some((l) => l.id === obj.id);
                  default:
                    return false;
                }
              });

              // Filter out text elements for individual rectangle rendering (no blue rects around texts)
              const offCanvasObjects = allOffCanvasObjects.filter(
                (obj) => obj.type !== "Text",
              );

              if (allOffCanvasObjects.length === 0) {
                return null;
              }

              // Calculate wrapper rectangle when multiple elements are selected
              // CRITICAL: Use allOffCanvasObjects (includes ALL selected objects) for wrapper box calculation
              // This ensures wrapper box shows around all selected elements, even Text and UnifiedLineArrowElement
              // Individual highlight rectangles will still be filtered (Text and UnifiedLineArrowElement won't show individual boxes)
              let wrapperBBox = null;
              if (allOffCanvasObjects.length > 1) {
                const bboxes = allOffCanvasObjects.map((obj) => {
                  const actualElement = getActualElement(obj);
                  const elementType = actualElement.type || obj.type;
                  // CRITICAL FIX: Get current position from Konva node to ensure we use the latest position
                  // This prevents selection box stretching when elements are moved between canvases
                  const elementWithCurrentPos =
                    getElementWithCurrentPositionForWrapper(
                      actualElement,
                      elementType,
                      true,
                    );
                  return getElementBoundingBox(
                    elementWithCurrentPos,
                    elementType,
                    {
                      includeStroke: true,
                    },
                  );
                });
                const minX = Math.min(...bboxes.map((b) => b.x));
                const minY = Math.min(...bboxes.map((b) => b.y));
                const maxX = Math.max(...bboxes.map((b) => b.x + b.width));
                const maxY = Math.max(...bboxes.map((b) => b.y + b.height));
                wrapperBBox = {
                  x: minX - 2, // Small padding
                  y: minY - 2,
                  width: maxX - minX + 4,
                  height: maxY - minY + 4,
                };
              }

              const offCanvasStage = offCanvasStageRef?.current;
              const offStageWidth =
                offCanvasStage?.width?.() || window.innerWidth;
              const offStageHeight =
                offCanvasStage?.height?.() || fixedStageHeightPx;
              const { mainZoneStart } = getOffCanvasMainColumnBounds(offStageWidth);
              const wrapperStrokeWidth = 3;
              const wrapperHalfStroke = wrapperStrokeWidth / 2;
              const WRAPPER_LEFT_SLACK_PX = 6;
              const WRAPPER_RIGHT_SLACK_PX = 6;
              const WRAPPER_TOP_SLACK_PX = 6;
              const WRAPPER_BOTTOM_SLACK_PX = 6;
              const wrapperEdgeInset = Math.max(
                Number(TRANSFORMER_ANCHOR_BUFFER_PX) || 0,
                0,
              );
              const clampedWrapperBBox = wrapperBBox
                ? (() => {
                    const initialSafeWidth = Math.max(
                      0,
                      Math.min(wrapperBBox.width, offStageWidth),
                    );
                    const safeHeight = Math.max(
                      0,
                      Math.min(wrapperBBox.height, offStageHeight),
                    );
                    // Keep the full wrapper stroke inside stage bounds.
                    // Clamp directly from computed wrapper bbox to avoid hidden X shifts.
                    const minVisibleX = Math.max(
                      wrapperHalfStroke,
                      wrapperEdgeInset + wrapperHalfStroke - WRAPPER_LEFT_SLACK_PX,
                    );
                    const laneRight = Math.max(
                      minVisibleX,
                      Number(mainZoneStart || offStageWidth) -
                        wrapperEdgeInset -
                        wrapperHalfStroke +
                        WRAPPER_RIGHT_SLACK_PX,
                    );
                    const laneWidth = Math.max(0, laneRight - minVisibleX);
                    const safeWidth = Math.min(initialSafeWidth, laneWidth);
                    const maxVisibleX = Math.max(
                      minVisibleX,
                      laneRight - safeWidth,
                    );
                    const minVisibleY = Math.max(
                      wrapperHalfStroke,
                      wrapperEdgeInset + wrapperHalfStroke - WRAPPER_TOP_SLACK_PX,
                    );
                    const maxVisibleY = Math.max(
                      minVisibleY,
                      offStageHeight -
                        wrapperEdgeInset -
                        safeHeight -
                        wrapperHalfStroke +
                        WRAPPER_BOTTOM_SLACK_PX,
                    );
                    const clampedX = Math.max(
                      minVisibleX,
                      Math.min(maxVisibleX, Number(wrapperBBox.x) || 0),
                    );
                    const clampedY = Math.max(
                      minVisibleY,
                      Math.min(maxVisibleY, Number(wrapperBBox.y) || 0),
                    );
                    console.log("[OFFCANVAS-WRAPPER][CLAMP-RENDER]", {
                      selectedCount: allOffCanvasObjects.length,
                      wrapperBBox: {
                        x: Math.round(Number(wrapperBBox.x) || 0),
                        y: Math.round(Number(wrapperBBox.y) || 0),
                        width: Math.round(Number(wrapperBBox.width) || 0),
                        height: Math.round(Number(wrapperBBox.height) || 0),
                      },
                      safeWidth: Math.round(safeWidth),
                      safeHeight: Math.round(safeHeight),
                      minVisibleX: Math.round(minVisibleX),
                      maxVisibleX: Math.round(maxVisibleX),
                      minVisibleY: Math.round(minVisibleY),
                      maxVisibleY: Math.round(maxVisibleY),
                      laneRight: Math.round(laneRight),
                      wrapperEdgeInset,
                      wrapperHalfStroke,
                      clampedX: Math.round(clampedX),
                      clampedY: Math.round(clampedY),
                    });
                    return {
                      x: clampedX,
                      y: clampedY,
                      width: safeWidth,
                      height: safeHeight,
                    };
                  })()
                : null;

              // Only render wrapper if it has valid dimensions and elements are on off-canvas
              // Use allOffCanvasObjects.length to ensure wrapper shows when 2+ elements are selected
              const willRenderWrapper =
                clampedWrapperBBox &&
                allOffCanvasObjects.length > 1 &&
                clampedWrapperBBox.width > 0 &&
                clampedWrapperBBox.height > 0 &&
                !isNaN(clampedWrapperBBox.x) &&
                !isNaN(clampedWrapperBBox.y) &&
                !isNaN(clampedWrapperBBox.width) &&
                !isNaN(clampedWrapperBBox.height);

              return (
                <>
                  {/* Wrapper rectangle when multiple elements are selected on off-canvas */}
                  {willRenderWrapper && (
                    <Rect
                      key="wrapper-highlight-offcanvas"
                      id="group-drag-wrapper-offcanvas"
                      x={clampedWrapperBBox.x}
                      y={clampedWrapperBBox.y}
                      width={clampedWrapperBBox.width}
                      height={clampedWrapperBBox.height}
                      fill="rgba(59, 130, 246, 0.1)"
                      stroke="#0D6EFD"
                      strokeWidth={wrapperStrokeWidth}
                      draggable={!isCloneMode && !lockedAction}
                      listening={true}
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                      dragBoundFunc={(pos) => pos}
                      onDragStart={(e) => {
                        // Track source stage so window-mousemove can call setPointersPositions
                        // keeping Konva's drag alive when pointer crosses to another stage
                        groupDragSourceStageRef.current =
                          offCanvasStageRef?.current;

                        // CRITICAL: Initialize group drag start for preview functionality
                        // Use the first element in the selection as the "dragged" element
                        const firstElementPos = allOffCanvasObjects[0];
                        if (firstElementPos) {
                          handleGroupDragStart?.(
                            firstElementPos.id,
                            firstElementPos.type,
                          );
                        }

                        // CRITICAL: Use allOffCanvasObjects to include ALL selected elements in drag
                        const wrapperNode = e.target;
                        wrapperDragStartRef.current = {
                          x: wrapperNode.x(),
                          y: wrapperNode.y(),
                          elementPositions: allOffCanvasObjects.map((obj) => {
                            const actualElement = getActualElement(obj);
                            let isOnMainCanvas = false;
                            switch (obj.type) {
                              case "Rect":
                                isOnMainCanvas = rectangles.some(
                                  (r) => r.id === obj.id,
                                );
                                break;
                              case "Circle":
                                isOnMainCanvas = circles.some(
                                  (c) => c.id === obj.id,
                                );
                                break;
                              case "Image":
                                isOnMainCanvas = images.some(
                                  (img) => img.id === obj.id,
                                );
                                break;
                              case "Text":
                                isOnMainCanvas = texts.some(
                                  (t) => t.id === obj.id,
                                );
                                break;
                              case "Arrow":
                                // Check arrows array first
                                isOnMainCanvas = arrows.some(
                                  (a) => a.id === obj.id,
                                );
                                // Also check lines array for unified arrow elements (with arrow heads)
                                if (!isOnMainCanvas) {
                                  isOnMainCanvas = lines.some((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow || hasEndArrow;
                                  });
                                }
                                break;
                              case "DoubleSidedArrow":
                                // Check doubleSidedArrows array first
                                isOnMainCanvas = doubleSidedArrows.some(
                                  (a) => a.id === obj.id,
                                );
                                // Also check lines array for unified double-sided arrow elements
                                if (!isOnMainCanvas) {
                                  isOnMainCanvas = lines.some((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow && hasEndArrow;
                                  });
                                }
                                break;
                              case "Line":
                                // Lines without arrow heads are in the lines array
                                isOnMainCanvas = lines.some((l) => {
                                  if (l.id !== obj.id) return false;
                                  // Only return lines without arrow heads
                                  return (
                                    !l.hasStartArrow &&
                                    !l.hasEndArrow &&
                                    !l.pointerAtBeginning &&
                                    !l.pointerAtEnd
                                  );
                                });
                                break;
                            }
                            return {
                              id: obj.id,
                              type: obj.type,
                              x: actualElement.x || 0,
                              y: actualElement.y || 0,
                              isOnMainCanvas,
                            };
                          }),
                        };
                        // Keep wrapper drag aligned with the shared drag-end sync/clamp pipeline.
                        individualElementDragStartRef.current = {
                          elementPositions:
                            wrapperDragStartRef.current.elementPositions,
                        };
                        wrapperJustDraggedRef.current = false;
                      }}
                      onDragMove={(e) => {
                        const wrapperNode = e.target;
                        const currentX = wrapperNode.x();
                        const currentY = wrapperNode.y();
                        const deltaX = currentX - wrapperDragStartRef.current.x;
                        const deltaY = currentY - wrapperDragStartRef.current.y;

                        // CRITICAL: Detect if we're in cross-canvas drag mode
                        // Check if mouse is over main canvas area
                        let isCrossingToMainCanvas = false;
                        try {
                          const offStage = offCanvasStageRef?.current;
                          if (offStage) {
                            const pointerPos = offStage.getPointerPosition();
                            if (pointerPos) {
                              const stageContainer = offStage.container();
                              if (stageContainer) {
                                const stageBBox =
                                  stageContainer.getBoundingClientRect();
                                const clientX = stageBBox.left + pointerPos.x;
                                const clientY = stageBBox.top + pointerPos.y;

                                const mainCanvasContainer =
                                  stageRef?.current?.container();
                                if (mainCanvasContainer) {
                                  const mainBBox =
                                    mainCanvasContainer.getBoundingClientRect();
                                  isCrossingToMainCanvas =
                                    clientX >= mainBBox.left &&
                                    clientX <= mainBBox.right &&
                                    clientY >= mainBBox.top &&
                                    clientY <= mainBBox.bottom;
                                }
                              }
                            }
                          }
                        } catch (e) {
                          // Ignore errors
                        }

                        // CRITICAL: Call handleGroupDragMove to show previews when dragging between canvases
                        // Use the first element as the "dragged" element, with its new position
                        const firstElementPos =
                          wrapperDragStartRef.current.elementPositions[0];
                        if (firstElementPos) {
                          const newX = firstElementPos.x + deltaX;
                          const newY = firstElementPos.y + deltaY;
                          handleGroupDragMove?.(
                            firstElementPos.id,
                            firstElementPos.type,
                            newX,
                            newY,
                            e,
                          );
                          // IMPORTANT: Wrapper drag should use the same shared group-drag
                          // pipeline as individual-element drag. Manual per-element node/state
                          // updates below cause double application and visible fluctuation.
                          return;
                        }

                        wrapperDragStartRef.current.elementPositions.forEach(
                          (initialPos) => {
                            let newX = initialPos.x + deltaX;
                            let newY = initialPos.y + deltaY;

                            // CRITICAL: Update Konva node position directly for immediate visual feedback
                            // This prevents fluctuation/vibration during rapid wrapper dragging
                            const mainStage = stageRef?.current;
                            const offStage = offCanvasStageRef?.current;
                            const topLayerStage = topLayerStageRef?.current;
                            const elementNode =
                              mainStage?.findOne(`#${initialPos.id}`) ||
                              offStage?.findOne(`#${initialPos.id}`) ||
                              topLayerStage?.findOne(`#${initialPos.id}`);

                            if (elementNode) {
                              // CRITICAL FIX: Constrain positions for elements during drag move
                              const elementStage = elementNode.getStage();
                              const isOnOffCanvasStage =
                                elementStage === offStage;
                              const isOnMainCanvasStage =
                                elementStage === mainStage;
                              const isOnTopLayerStage =
                                elementStage === topLayerStage;

                              // Constrain off-canvas elements
                              // CRITICAL: Skip constraints if crossing to main canvas
                              if (
                                false &&
                                isOnOffCanvasStage &&
                                !isOnTopLayerStage &&
                                offStage
                              ) {
                                const stageWidth =
                                  offStage.width() || window.innerWidth;
                                const stageHeight =
                                  offStage.height() || fixedStageHeightPx;

                                // Get element from state to calculate bounding box
                                let element = null;
                                switch (initialPos.type) {
                                  case "Rect":
                                    element = offCanvasRectangles.find(
                                      (r) => r.id === initialPos.id,
                                    );
                                    break;
                                  case "Circle":
                                    element = offCanvasCircles.find(
                                      (c) => c.id === initialPos.id,
                                    );
                                    break;
                                  case "Image":
                                    element = offCanvasImages.find(
                                      (img) => img.id === initialPos.id,
                                    );
                                    break;
                                  case "Text":
                                    element = offCanvasTexts.find(
                                      (t) => t.id === initialPos.id,
                                    );
                                    break;
                                  case "Line":
                                  case "Arrow":
                                  case "DoubleSidedArrow":
                                    element =
                                      offCanvasLines.find(
                                        (l) => l.id === initialPos.id,
                                      ) ||
                                      offCanvasArrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      offCanvasDoubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      );
                                    break;
                                }

                                if (element) {
                                  // CRITICAL FIX: For lines/arrows, check ALL points individually
                                  // This ensures no point goes off-screen, even for vertical/horizontal lines
                                  if (
                                    initialPos.type === "Line" ||
                                    initialPos.type === "Arrow" ||
                                    initialPos.type === "DoubleSidedArrow"
                                  ) {
                                    const points = element.points || [
                                      0, 0, 0, 0,
                                    ];
                                    if (points.length >= 4) {
                                      // Calculate absolute positions of all points with new group position
                                      const absolutePoints = [];
                                      for (
                                        let i = 0;
                                        i < points.length;
                                        i += 2
                                      ) {
                                        absolutePoints.push({
                                          x: points[i] + newX,
                                          y: points[i + 1] + newY,
                                        });
                                      }

                                      // Find min/max across ALL points
                                      let absMinX = Infinity;
                                      let absMaxX = -Infinity;
                                      let absMinY = Infinity;
                                      let absMaxY = -Infinity;

                                      for (const point of absolutePoints) {
                                        absMinX = Math.min(absMinX, point.x);
                                        absMaxX = Math.max(absMaxX, point.x);
                                        absMinY = Math.min(absMinY, point.y);
                                        absMaxY = Math.max(absMaxY, point.y);
                                      }

                                      // Add padding for stroke width
                                      const strokeWidth =
                                        element.strokeWidth || 2;
                                      const padding = Math.max(
                                        strokeWidth / 2,
                                        5,
                                      );

                                      // Calculate adjustment needed to keep ALL points within bounds
                                      let deltaX = 0;
                                      let deltaY = 0;

                                      // Constrain X: ensure ALL points have x >= padding and x <= stageWidth - padding
                                      if (absMinX < padding) {
                                        deltaX = padding - absMinX; // Move right
                                      }
                                      if (absMaxX > stageWidth - padding) {
                                        const rightOverflow =
                                          absMaxX - (stageWidth - padding);
                                        deltaX = Math.max(
                                          deltaX,
                                          -rightOverflow,
                                        ); // Move left if needed
                                      }

                                      // Constrain Y: ensure ALL points have y >= padding and y <= stageHeight - padding
                                      if (absMinY < padding) {
                                        deltaY = padding - absMinY; // Move down
                                      }
                                      if (absMaxY > stageHeight - padding) {
                                        const bottomOverflow =
                                          absMaxY - (stageHeight - padding);
                                        deltaY = Math.max(
                                          deltaY,
                                          -bottomOverflow,
                                        ); // Move up if needed
                                      }

                                      // Apply adjustment
                                      newX = newX + deltaX;
                                      newY = newY + deltaY;
                                    } else {
                                      // Fallback for invalid points
                                      newX = Math.max(
                                        0,
                                        Math.min(stageWidth, newX),
                                      );
                                      newY = Math.max(
                                        0,
                                        Math.min(stageHeight, newY),
                                      );
                                    }
                                  } else {
                                    // For other element types, use bounding box approach
                                    const tempElement = {
                                      ...element,
                                      x: newX,
                                      y: newY,
                                    };
                                    const bbox = getElementBoundingBox(
                                      tempElement,
                                      initialPos.type,
                                    );

                                    // Determine coordinate system based on element type
                                    const isCenterCoordinate =
                                      initialPos.type === "Image" ||
                                      initialPos.type === "Circle";

                                    if (isCenterCoordinate) {
                                      // For center coordinate elements (Image, Circle)
                                      const halfWidth = bbox.width / 2;
                                      const halfHeight = bbox.height / 2;

                                      // For circles, account for stroke width and toolbar
                                      let minX = halfWidth;
                                      if (initialPos.type === "Circle") {
                                        const strokeWidth =
                                          element.strokeWidth || 2;
                                        // Leftmost point of stroke = x - radius - strokeWidth/2
                                        // Constraint: x - radius - strokeWidth/2 >= TOOLBAR_WIDTH
                                        // Therefore: x >= TOOLBAR_WIDTH + radius + strokeWidth/2
                                        const radius =
                                          element.radius ||
                                          Math.max(
                                            element.radiusX || 0,
                                            element.radiusY || 0,
                                          );
                                        minX =
                                          TOOLBAR_WIDTH +
                                          radius +
                                          strokeWidth / 2;
                                      }

                                      newX = Math.max(
                                        minX,
                                        Math.min(stageWidth - halfWidth, newX),
                                      );
                                      newY = Math.max(
                                        halfHeight,
                                        Math.min(
                                          stageHeight - halfHeight,
                                          newY,
                                        ),
                                      );
                                    } else {
                                      // For top-left coordinate elements (Rect, Text)
                                      let minX = 0;
                                      if (initialPos.type === "Rect") {
                                        const strokeWidth =
                                          element.strokeWidth || 2;
                                        // Leftmost point of stroke = x - strokeWidth/2
                                        // Constraint: x - strokeWidth/2 >= TOOLBAR_WIDTH
                                        // Therefore: x >= TOOLBAR_WIDTH + strokeWidth/2
                                        minX = TOOLBAR_WIDTH + strokeWidth / 2;
                                      }

                                      newX = Math.max(
                                        minX,
                                        Math.min(stageWidth - bbox.width, newX),
                                      );
                                      newY = Math.max(
                                        0,
                                        Math.min(
                                          stageHeight - bbox.height,
                                          newY,
                                        ),
                                      );
                                    }
                                  }
                                } else {
                                  // Fallback: just constrain to >= 0 and <= stage dimensions
                                  newX = Math.max(
                                    0,
                                    Math.min(stageWidth, newX),
                                  );
                                  newY = Math.max(
                                    0,
                                    Math.min(stageHeight, newY),
                                  );
                                }

                                // Keep off-canvas group members inside their original strip (left/right).
                                const originalExtent = getAbsoluteHorizontalExtent(
                                  element,
                                  initialPos.type,
                                  initialPos.x,
                                );
                                const lockedStripSide =
                                  getOffCanvasHorizontalStripSide(
                                    originalExtent.minX,
                                    originalExtent.maxX,
                                    stageWidth,
                                  );
                                newX = shiftXToStayInOffCanvasStrip(
                                  element,
                                  initialPos.type,
                                  newX,
                                  stageWidth,
                                  lockedStripSide,
                                );
                              }

                              // Constrain main canvas elements
                              if (
                                false &&
                                isOnMainCanvasStage &&
                                !isOnTopLayerStage &&
                                mainStage
                              ) {
                                const stageWidth =
                                  mainStage.width() || mainCanvasWidth;
                                const stageHeight =
                                  mainStage.height() || fixedStageHeightPx;

                                // Get element from state to calculate bounding box
                                let element = null;
                                switch (initialPos.type) {
                                  case "Rect":
                                    element = rectangles.find(
                                      (r) => r.id === initialPos.id,
                                    );
                                    break;
                                  case "Circle":
                                    element = circles.find(
                                      (c) => c.id === initialPos.id,
                                    );
                                    break;
                                  case "Image":
                                    element = images.find(
                                      (img) => img.id === initialPos.id,
                                    );
                                    break;
                                  case "Text":
                                    element = texts.find(
                                      (t) => t.id === initialPos.id,
                                    );
                                    break;
                                  case "Line":
                                  case "Arrow":
                                  case "DoubleSidedArrow":
                                    element =
                                      lines.find(
                                        (l) => l.id === initialPos.id,
                                      ) ||
                                      arrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      doubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      );
                                    break;
                                }

                                if (element) {
                                  // CRITICAL FIX: For lines/arrows, check ALL points individually
                                  // This ensures no point goes off-screen, even for vertical/horizontal lines
                                  if (
                                    initialPos.type === "Line" ||
                                    initialPos.type === "Arrow" ||
                                    initialPos.type === "DoubleSidedArrow"
                                  ) {
                                    const points = element.points || [
                                      0, 0, 0, 0,
                                    ];
                                    if (points.length >= 4) {
                                      // Calculate absolute positions of all points with new group position
                                      const absolutePoints = [];
                                      for (
                                        let i = 0;
                                        i < points.length;
                                        i += 2
                                      ) {
                                        absolutePoints.push({
                                          x: points[i] + newX,
                                          y: points[i + 1] + newY,
                                        });
                                      }

                                      // Find min/max across ALL points
                                      let absMinX = Infinity;
                                      let absMaxX = -Infinity;
                                      let absMinY = Infinity;
                                      let absMaxY = -Infinity;

                                      for (const point of absolutePoints) {
                                        absMinX = Math.min(absMinX, point.x);
                                        absMaxX = Math.max(absMaxX, point.x);
                                        absMinY = Math.min(absMinY, point.y);
                                        absMaxY = Math.max(absMaxY, point.y);
                                      }

                                      // Add padding for stroke width
                                      const strokeWidth =
                                        element.strokeWidth || 2;
                                      const padding = Math.max(
                                        strokeWidth / 2,
                                        5,
                                      );

                                      // Calculate adjustment needed to keep ALL points within bounds
                                      let deltaX = 0;
                                      let deltaY = 0;

                                      // Constrain X: ensure ALL points have x >= TOOLBAR_WIDTH and x <= stageWidth - padding
                                      if (absMinX < TOOLBAR_WIDTH) {
                                        deltaX = TOOLBAR_WIDTH - absMinX; // Move right
                                      }
                                      if (absMaxX > stageWidth - padding) {
                                        const rightOverflow =
                                          absMaxX - (stageWidth - padding);
                                        deltaX = Math.max(
                                          deltaX,
                                          -rightOverflow,
                                        ); // Move left if needed
                                      }

                                      // Constrain Y: ensure ALL points have y >= padding and y <= stageHeight - padding
                                      if (absMinY < padding) {
                                        deltaY = padding - absMinY; // Move down
                                      }
                                      if (absMaxY > stageHeight - padding) {
                                        const bottomOverflow =
                                          absMaxY - (stageHeight - padding);
                                        deltaY = Math.max(
                                          deltaY,
                                          -bottomOverflow,
                                        ); // Move up if needed
                                      }

                                      // Apply adjustment
                                      newX = newX + deltaX;
                                      newY = newY + deltaY;
                                    } else {
                                      // Fallback for invalid points
                                      newX = Math.max(
                                        TOOLBAR_WIDTH,
                                        Math.min(stageWidth, newX),
                                      );
                                      newY = Math.max(
                                        0,
                                        Math.min(stageHeight, newY),
                                      );
                                    }
                                  } else {
                                    // For other element types, use bounding box approach
                                    const tempElement = {
                                      ...element,
                                      x: newX,
                                      y: newY,
                                    };
                                    const bbox = getElementBoundingBox(
                                      tempElement,
                                      initialPos.type,
                                    );

                                    // Determine coordinate system based on element type
                                    const isCenterCoordinate =
                                      initialPos.type === "Image" ||
                                      initialPos.type === "Circle";

                                    if (isCenterCoordinate) {
                                      // For center coordinate elements (Image, Circle)
                                      const halfWidth = bbox.width / 2;
                                      const halfHeight = bbox.height / 2;

                                      // For circles, account for stroke width and toolbar
                                      let minX = halfWidth;
                                      if (initialPos.type === "Circle") {
                                        const strokeWidth =
                                          element.strokeWidth || 2;
                                        // Leftmost point of stroke = x - radius - strokeWidth/2
                                        // Constraint: x - radius - strokeWidth/2 >= TOOLBAR_WIDTH
                                        // Therefore: x >= TOOLBAR_WIDTH + radius + strokeWidth/2
                                        const radius =
                                          element.radius ||
                                          Math.max(
                                            element.radiusX || 0,
                                            element.radiusY || 0,
                                          );
                                        minX =
                                          TOOLBAR_WIDTH +
                                          radius +
                                          strokeWidth / 2;
                                      }

                                      newX = Math.max(
                                        minX,
                                        Math.min(stageWidth - halfWidth, newX),
                                      );
                                      newY = Math.max(
                                        halfHeight,
                                        Math.min(
                                          stageHeight - halfHeight,
                                          newY,
                                        ),
                                      );
                                    } else {
                                      // For top-left coordinate elements (Rect, Text)
                                      let minX = 0;
                                      if (initialPos.type === "Rect") {
                                        const strokeWidth =
                                          element.strokeWidth || 2;
                                        // Leftmost point of stroke = x - strokeWidth/2
                                        // Constraint: x - strokeWidth/2 >= TOOLBAR_WIDTH
                                        // Therefore: x >= TOOLBAR_WIDTH + strokeWidth/2
                                        minX = TOOLBAR_WIDTH + strokeWidth / 2;
                                      }

                                      newX = Math.max(
                                        minX,
                                        Math.min(stageWidth - bbox.width, newX),
                                      );
                                      newY = Math.max(
                                        0,
                                        Math.min(
                                          stageHeight - bbox.height,
                                          newY,
                                        ),
                                      );
                                    }
                                  }
                                } else {
                                  // Fallback: just constrain to >= TOOLBAR_WIDTH and <= stage dimensions
                                  newX = Math.max(
                                    TOOLBAR_WIDTH,
                                    Math.min(stageWidth, newX),
                                  );
                                  newY = Math.max(
                                    0,
                                    Math.min(stageHeight, newY),
                                  );
                                }
                              }

                              // CRITICAL: Apply main canvas constraints when crossing to main canvas
                              // This prevents elements from going beyond main canvas screen when dragging from off-canvas
                              if (
                                isOnOffCanvasStage &&
                                !isOnTopLayerStage &&
                                false &&
                                isCrossingToMainCanvas &&
                                mainStage
                              ) {
                                const stageWidth =
                                  mainStage.width() || mainCanvasWidth;
                                const stageHeight =
                                  mainStage.height() || fixedStageHeightPx;

                                // Get element from state to calculate bounding box
                                let element = null;
                                switch (initialPos.type) {
                                  case "Rect":
                                    element = offCanvasRectangles.find(
                                      (r) => r.id === initialPos.id,
                                    );
                                    break;
                                  case "Circle":
                                    element = offCanvasCircles.find(
                                      (c) => c.id === initialPos.id,
                                    );
                                    break;
                                  case "Image":
                                    element = offCanvasImages.find(
                                      (img) => img.id === initialPos.id,
                                    );
                                    break;
                                  case "Text":
                                    element = offCanvasTexts.find(
                                      (t) => t.id === initialPos.id,
                                    );
                                    break;
                                  case "Line":
                                  case "Arrow":
                                  case "DoubleSidedArrow":
                                    element =
                                      offCanvasLines.find(
                                        (l) => l.id === initialPos.id,
                                      ) ||
                                      offCanvasArrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      offCanvasDoubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      );
                                    break;
                                }

                                if (element) {
                                  // Convert position from off-canvas to main canvas coordinates
                                  const offBBox = offStage
                                    .container()
                                    .getBoundingClientRect();
                                  const mainBBox = mainStage
                                    .container()
                                    .getBoundingClientRect();
                                  const absX = offBBox.left + newX;
                                  const absY = offBBox.top + newY;
                                  let mainCanvasX = absX - mainBBox.left;
                                  let mainCanvasY = absY - mainBBox.top;

                                  // Apply constraints in main canvas coordinate system
                                  if (
                                    initialPos.type === "Image" ||
                                    initialPos.type === "Circle"
                                  ) {
                                    // Center coordinate elements
                                    const width =
                                      element.width ||
                                      (element.radius ? element.radius * 2 : 0);
                                    const height =
                                      element.height ||
                                      (element.radius ? element.radius * 2 : 0);
                                    const halfWidth = width / 2;
                                    const halfHeight = height / 2;

                                    mainCanvasX = Math.max(
                                      TOOLBAR_WIDTH + halfWidth,
                                      Math.min(
                                        stageWidth - halfWidth,
                                        mainCanvasX,
                                      ),
                                    );
                                    mainCanvasY = Math.max(
                                      halfHeight,
                                      Math.min(
                                        stageHeight - halfHeight,
                                        mainCanvasY,
                                      ),
                                    );
                                  } else if (
                                    initialPos.type === "Rect" ||
                                    initialPos.type === "Text"
                                  ) {
                                    // Top-left coordinate elements
                                    const width = element.width || 0;
                                    const height = element.height || 0;

                                    mainCanvasX = Math.max(
                                      TOOLBAR_WIDTH,
                                      Math.min(stageWidth - width, mainCanvasX),
                                    );
                                    mainCanvasY = Math.max(
                                      0,
                                      Math.min(
                                        stageHeight - height,
                                        mainCanvasY,
                                      ),
                                    );
                                  } else {
                                    // For lines/arrows and other elements
                                    mainCanvasX = Math.max(
                                      TOOLBAR_WIDTH,
                                      Math.min(stageWidth, mainCanvasX),
                                    );
                                    mainCanvasY = Math.max(
                                      0,
                                      Math.min(stageHeight, mainCanvasY),
                                    );
                                  }

                                  // Convert back to off-canvas coordinates
                                  const constrainedAbsX =
                                    mainBBox.left + mainCanvasX;
                                  const constrainedAbsY =
                                    mainBBox.top + mainCanvasY;
                                  newX = constrainedAbsX - offBBox.left;
                                  newY = constrainedAbsY - offBBox.top;
                                }
                              }

                              elementNode.x(newX);
                              elementNode.y(newY);
                              // Don't call batchDraw here - we'll do it once at the end for all elements
                            }

                            if (initialPos.type === "Rect") {
                              if (initialPos.isOnMainCanvas) {
                                setRectangles((prev) =>
                                  prev.map((rect) =>
                                    rect.id === initialPos.id
                                      ? { ...rect, x: newX, y: newY }
                                      : rect,
                                  ),
                                );
                              } else {
                                setOffCanvasRectangles((prev) =>
                                  prev.map((rect) =>
                                    rect.id === initialPos.id
                                      ? { ...rect, x: newX, y: newY }
                                      : rect,
                                  ),
                                );
                              }
                            } else if (initialPos.type === "Circle") {
                              if (initialPos.isOnMainCanvas) {
                                setCircles((prev) =>
                                  prev.map((circle) =>
                                    circle.id === initialPos.id
                                      ? { ...circle, x: newX, y: newY }
                                      : circle,
                                  ),
                                );
                              } else {
                                setOffCanvasCircles((prev) =>
                                  prev.map((circle) =>
                                    circle.id === initialPos.id
                                      ? { ...circle, x: newX, y: newY }
                                      : circle,
                                  ),
                                );
                              }
                            } else if (initialPos.type === "Image") {
                              // Update images directly via state setters, similar to other elements
                              if (initialPos.isOnMainCanvas && setImages) {
                                setImages((prev) =>
                                  prev.map((img) =>
                                    img.id === initialPos.id
                                      ? { ...img, x: newX, y: newY }
                                      : img,
                                  ),
                                );
                              } else if (
                                !initialPos.isOnMainCanvas &&
                                setOffCanvasImages
                              ) {
                                setOffCanvasImages((prev) =>
                                  prev.map((img) =>
                                    img.id === initialPos.id
                                      ? { ...img, x: newX, y: newY }
                                      : img,
                                  ),
                                );
                              }
                            } else if (initialPos.type === "Text") {
                              // CRITICAL FIX: Update both main canvas and off-canvas text arrays
                              // Similar to Rect, Circle, and Image - check isOnMainCanvas
                              if (initialPos.isOnMainCanvas && setTexts) {
                                setTexts((prev) =>
                                  prev.map((text) =>
                                    text.id === initialPos.id
                                      ? { ...text, x: newX, y: newY }
                                      : text,
                                  ),
                                );
                              } else if (
                                !initialPos.isOnMainCanvas &&
                                setOffCanvasTexts
                              ) {
                                setOffCanvasTexts((prev) =>
                                  prev.map((text) =>
                                    text.id === initialPos.id
                                      ? { ...text, x: newX, y: newY }
                                      : text,
                                  ),
                                );
                              }
                            } else if (
                              initialPos.type === "Line" &&
                              updateLineProperties
                            ) {
                              updateLineProperties(initialPos.id, {
                                x: newX,
                                y: newY,
                                skipOffset: true,
                              });
                            } else if (
                              initialPos.type === "Arrow" ||
                              initialPos.type === "DoubleSidedArrow"
                            ) {
                              // CRITICAL: Arrow and DoubleSidedArrow elements can be:
                              // 1. In the arrows/doubleSidedArrows arrays (legacy)
                              // 2. In the lines array as unified arrow elements (with arrow flags)
                              // We need to update them using updateLineProperties which handles both cases

                              // CRITICAL FIX: Always use updateLineProperties for Arrow/DoubleSidedArrow types
                              // This handles both unified arrow elements and legacy arrow elements
                              // The updateLineProperties function knows how to handle both cases
                              if (updateLineProperties) {
                                updateLineProperties(initialPos.id, {
                                  x: newX,
                                  y: newY,
                                  skipOffset: true,
                                });
                              } else {
                                // Fallback: if updateLineProperties is not available, update directly via state
                                // This handles the case where updateLineProperties might not be passed
                                if (initialPos.isOnMainCanvas) {
                                  setLines((prev) =>
                                    prev.map((line) =>
                                      line.id === initialPos.id
                                        ? { ...line, x: newX, y: newY }
                                        : line,
                                    ),
                                  );
                                } else {
                                  setOffCanvasLines((prev) =>
                                    prev.map((line) =>
                                      line.id === initialPos.id
                                        ? { ...line, x: newX, y: newY }
                                        : line,
                                    ),
                                  );
                                }
                              }
                            }
                          },
                        );

                        // CRITICAL: Batch draw all stages at once for smooth animation
                        // This ensures all elements are rendered together without flickering
                        const mainStage = stageRef?.current;
                        const offStage = offCanvasStageRef?.current;
                        if (mainStage) {
                          mainStage.getLayer()?.batchDraw();
                        }
                        if (offStage) {
                          offStage.getLayer()?.batchDraw();
                        }

                        setSelectedObjects((prev) =>
                          prev.map((obj) => {
                            const initialPos =
                              wrapperDragStartRef.current.elementPositions.find(
                                (p) => p.id === obj.id,
                              );
                            if (initialPos) {
                              return {
                                ...obj,
                                x: initialPos.x + deltaX,
                                y: initialPos.y + deltaY,
                              };
                            }
                            return obj;
                          }),
                        );
                      }}
                      onDragEnd={(e) => {
                        const wrapperNode = e.target;
                        const finalX = wrapperNode.x();
                        const finalY = wrapperNode.y();

                        const mainStage = stageRef?.current;
                        const offStage = offCanvasStageRef?.current;

                        if (
                          mainStage &&
                          offStage &&
                          offCanvasObjects.length > 0
                        ) {
                          let allFromMainCanvas = true;
                          let allFromOffCanvas = true;

                          offCanvasObjects.forEach((obj) => {
                            if (!obj || !obj.id || !obj.type) return;

                            let isInMain = false;
                            let isInOff = false;

                            switch (obj.type) {
                              case "Rect":
                                isInMain = rectangles.some(
                                  (r) => r.id === obj.id,
                                );
                                isInOff = offCanvasRectangles.some(
                                  (r) => r.id === obj.id,
                                );
                                break;
                              case "Circle":
                                isInMain = circles.some((c) => c.id === obj.id);
                                isInOff = offCanvasCircles.some(
                                  (c) => c.id === obj.id,
                                );
                                break;
                              case "Image":
                                isInMain = images.some(
                                  (img) => img.id === obj.id,
                                );
                                isInOff = offCanvasImages.some(
                                  (img) => img.id === obj.id,
                                );
                                break;
                              case "Text":
                                isInMain = texts.some((t) => t.id === obj.id);
                                isInOff = offCanvasTexts.some(
                                  (t) => t.id === obj.id,
                                );
                                break;
                              case "Arrow":
                                // CRITICAL FIX: For unified arrow elements, check lines array FIRST
                                // These elements are primarily stored in lines array, not arrows array
                                isInMain = lines.some((l) => {
                                  if (l.id !== obj.id) return false;
                                  const hasStartArrow =
                                    l.hasStartArrow === true ||
                                    l.pointerAtBeginning === true;
                                  const hasEndArrow =
                                    l.hasEndArrow === true ||
                                    l.pointerAtEnd === true;
                                  return hasStartArrow || hasEndArrow;
                                });
                                isInOff = offCanvasLines.some((l) => {
                                  if (l.id !== obj.id) return false;
                                  const hasStartArrow =
                                    l.hasStartArrow === true ||
                                    l.pointerAtBeginning === true;
                                  const hasEndArrow =
                                    l.hasEndArrow === true ||
                                    l.pointerAtEnd === true;
                                  return hasStartArrow || hasEndArrow;
                                });
                                // Also check legacy arrows array as fallback
                                if (!isInMain) {
                                  isInMain = arrows.some(
                                    (a) => a.id === obj.id,
                                  );
                                }
                                if (!isInOff) {
                                  isInOff = offCanvasArrows.some(
                                    (a) => a.id === obj.id,
                                  );
                                }
                                break;
                              case "DoubleSidedArrow":
                                // Check doubleSidedArrows array first
                                isInMain = doubleSidedArrows.some(
                                  (a) => a.id === obj.id,
                                );
                                isInOff = offCanvasDoubleSidedArrows.some(
                                  (a) => a.id === obj.id,
                                );
                                // Also check lines array for unified double-sided arrow elements
                                if (!isInMain) {
                                  isInMain = lines.some((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow && hasEndArrow;
                                  });
                                }
                                if (!isInOff) {
                                  isInOff = offCanvasLines.some((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow && hasEndArrow;
                                  });
                                }
                                break;
                              case "Line":
                                // Lines without arrow heads are in the lines array
                                isInMain = lines.some((l) => {
                                  if (l.id !== obj.id) return false;
                                  // Only return lines without arrow heads
                                  return (
                                    !l.hasStartArrow &&
                                    !l.hasEndArrow &&
                                    !l.pointerAtBeginning &&
                                    !l.pointerAtEnd
                                  );
                                });
                                isInOff = offCanvasLines.some((l) => {
                                  if (l.id !== obj.id) return false;
                                  // Only return lines without arrow heads
                                  return (
                                    !l.hasStartArrow &&
                                    !l.hasEndArrow &&
                                    !l.pointerAtBeginning &&
                                    !l.pointerAtEnd
                                  );
                                });
                                break;
                            }

                            if (isInMain) allFromOffCanvas = false;
                            if (isInOff) allFromMainCanvas = false;
                          });

                          const sourceCanvas = allFromOffCanvas
                            ? "offCanvas"
                            : "main";
                          const sourceStage =
                            sourceCanvas === "offCanvas" ? offStage : mainStage;
                          const sourceBBox = sourceStage
                            .container()
                            .getBoundingClientRect();

                          // Use mouse position if available for more accurate boundary detection
                          const mouseClientX = e.evt?.clientX;
                          const mouseClientY = e.evt?.clientY;

                          // Calculate group center - prefer mouse position if available
                          let groupCenterX, groupCenterY;
                          if (mouseClientX !== null && mouseClientY !== null) {
                            // Use mouse position directly for accurate boundary detection
                            groupCenterX = mouseClientX;
                            groupCenterY = mouseClientY;
                          } else {
                            // Fallback to wrapper position calculation
                            groupCenterX =
                              sourceBBox.left + finalX + wrapperBBox.width / 2;
                            groupCenterY =
                              sourceBBox.top + finalY + wrapperBBox.height / 2;
                          }

                          const mainBBox = mainStage
                            .container()
                            .getBoundingClientRect();
                          const offBBox = offStage
                            .container()
                            .getBoundingClientRect();
                          const mainCanvasRight =
                            mainBBox.left + mainCanvasWidth;
                          const mainCanvasBottom =
                            mainBBox.top +
                            fixedStageHeightPx;

                          // Check if ANY element in the group crosses the boundary
                          // Calculate delta from wrapper drag
                          const deltaX = finalX - wrapperDragStartRef.current.x;
                          const deltaY = finalY - wrapperDragStartRef.current.y;

                          // Check each element's position after drag
                          let shouldMoveGroup = false;
                          let targetCanvas = null;

                          for (const initialPos of wrapperDragStartRef.current
                            .elementPositions) {
                            // Calculate element's current position after drag
                            const elementCurrentX = initialPos.x + deltaX;
                            const elementCurrentY = initialPos.y + deltaY;

                            // Get element's bounding box to find its center or key point
                            let element = null;
                            // Find the actual element to get its properties
                            switch (initialPos.type) {
                              case "Rect":
                                element = initialPos.isOnMainCanvas
                                  ? rectangles.find(
                                      (r) => r.id === initialPos.id,
                                    )
                                  : offCanvasRectangles.find(
                                      (r) => r.id === initialPos.id,
                                    );
                                break;
                              case "Circle":
                                element = initialPos.isOnMainCanvas
                                  ? circles.find((c) => c.id === initialPos.id)
                                  : offCanvasCircles.find(
                                      (c) => c.id === initialPos.id,
                                    );
                                break;
                              case "Image":
                                element = initialPos.isOnMainCanvas
                                  ? images.find(
                                      (img) => img.id === initialPos.id,
                                    )
                                  : offCanvasImages.find(
                                      (img) => img.id === initialPos.id,
                                    );
                                break;
                              case "Text":
                                element = initialPos.isOnMainCanvas
                                  ? texts.find((t) => t.id === initialPos.id)
                                  : offCanvasTexts.find(
                                      (t) => t.id === initialPos.id,
                                    );
                                break;
                              case "Arrow":
                                element = initialPos.isOnMainCanvas
                                  ? arrows.find(
                                      (a) => a.id === initialPos.id,
                                    ) ||
                                    lines.find((l) => {
                                      if (l.id !== initialPos.id) return false;
                                      const hasStartArrow =
                                        l.hasStartArrow === true ||
                                        l.pointerAtBeginning === true;
                                      const hasEndArrow =
                                        l.hasEndArrow === true ||
                                        l.pointerAtEnd === true;
                                      return hasStartArrow || hasEndArrow;
                                    })
                                  : offCanvasArrows.find(
                                      (a) => a.id === initialPos.id,
                                    ) ||
                                    offCanvasLines.find((l) => {
                                      if (l.id !== initialPos.id) return false;
                                      const hasStartArrow =
                                        l.hasStartArrow === true ||
                                        l.pointerAtBeginning === true;
                                      const hasEndArrow =
                                        l.hasEndArrow === true ||
                                        l.pointerAtEnd === true;
                                      return hasStartArrow || hasEndArrow;
                                    });
                                break;
                              case "DoubleSidedArrow":
                                element = initialPos.isOnMainCanvas
                                  ? doubleSidedArrows.find(
                                      (a) => a.id === initialPos.id,
                                    ) ||
                                    lines.find((l) => {
                                      if (l.id !== initialPos.id) return false;
                                      const hasStartArrow =
                                        l.hasStartArrow === true ||
                                        l.pointerAtBeginning === true;
                                      const hasEndArrow =
                                        l.hasEndArrow === true ||
                                        l.pointerAtEnd === true;
                                      return hasStartArrow && hasEndArrow;
                                    })
                                  : offCanvasDoubleSidedArrows.find(
                                      (a) => a.id === initialPos.id,
                                    ) ||
                                    offCanvasLines.find((l) => {
                                      if (l.id !== initialPos.id) return false;
                                      const hasStartArrow =
                                        l.hasStartArrow === true ||
                                        l.pointerAtBeginning === true;
                                      const hasEndArrow =
                                        l.hasEndArrow === true ||
                                        l.pointerAtEnd === true;
                                      return hasStartArrow && hasEndArrow;
                                    });
                                break;
                              case "Line":
                                element = initialPos.isOnMainCanvas
                                  ? lines.find((l) => {
                                      if (l.id !== initialPos.id) return false;
                                      return (
                                        !l.hasStartArrow &&
                                        !l.hasEndArrow &&
                                        !l.pointerAtBeginning &&
                                        !l.pointerAtEnd
                                      );
                                    })
                                  : offCanvasLines.find((l) => {
                                      if (l.id !== initialPos.id) return false;
                                      return (
                                        !l.hasStartArrow &&
                                        !l.hasEndArrow &&
                                        !l.pointerAtBeginning &&
                                        !l.pointerAtEnd
                                      );
                                    });
                                break;
                            }

                            if (!element) continue;

                            // Calculate element's center or key point in client coordinates
                            const elementBBox = getElementBoundingBox(
                              {
                                ...element,
                                x: elementCurrentX,
                                y: elementCurrentY,
                              },
                              initialPos.type,
                            );
                            const elementCenterX =
                              elementBBox.x + elementBBox.width / 2;
                            const elementCenterY =
                              elementBBox.y + elementBBox.height / 2;

                            // Convert to client coordinates
                            const elementClientX =
                              sourceBBox.left + elementCenterX;
                            const elementClientY =
                              sourceBBox.top + elementCenterY;

                            // Check if this element crosses the boundary
                            const elementTarget = determineTargetCanvas({
                              type: initialPos.type,
                              draggedElement: {
                                x: elementCurrentX,
                                y: elementCurrentY,
                              },
                              clientX: elementClientX,
                              clientY: elementClientY,
                              mainBBox,
                              offBBox,
                              mainCanvasRight,
                              mainCanvasBottom,
                              sourceStageRef: sourceStage,
                              mainStage,
                              offCanvasStage: offStage,
                              isFromMainCanvas: sourceCanvas === "main",
                            });

                            // If ANY element crosses the boundary, move the entire group
                            if (
                              elementTarget &&
                              elementTarget !== sourceCanvas
                            ) {
                              shouldMoveGroup = true;
                              targetCanvas = elementTarget;
                              break; // Found one element crossing, move entire group
                            }
                          }

                          // If any element crossed the boundary, move the entire group
                          if (shouldMoveGroup && targetCanvas) {
                            // CRITICAL: Clear group drag previews before moving elements
                            // This prevents showing both preview and actual elements
                            isGroupDraggingRef.current = false;
                            setGroupDragPreviews([]);
                            groupDragPreviewsRef.current = [];

                            // CRITICAL FIX: Calculate overridePositions for wrapper drag to match individual element drag behavior
                            // This prevents Y-axis jumping by using the same positioning logic
                            const overridePositions = {};
                            for (const initialPos of wrapperDragStartRef.current
                              .elementPositions) {
                              overridePositions[initialPos.id] = {
                                x: initialPos.x + deltaX,
                                y: initialPos.y + deltaY,
                              };
                            }

                            handleCopyGroupBetweenCanvases(
                              finalX,
                              finalY,
                              mouseClientX,
                              mouseClientY,
                              overridePositions, // Add overridePositions like individual element drag
                            );
                            wrapperDragStartRef.current = {
                              x: 0,
                              y: 0,
                              elementPositions: [],
                            };
                            return;
                          }
                        }

                        // CRITICAL FIX: Check if elements need to be transferred between canvases on drag end
                        // This handles the case where arrows are filtered out during drag and onDragMove doesn't detect the transfer
                        const deltaX = finalX - wrapperDragStartRef.current.x;
                        const deltaY = finalY - wrapperDragStartRef.current.y;

                        // Check if any element crossed the boundary
                        if (
                          wrapperDragStartRef.current.elementPositions.length >
                          0
                        ) {
                          if (mainStage && offStage) {
                            const mainBBox = mainStage
                              .container()
                              .getBoundingClientRect();
                            const offBBox = offStage
                              .container()
                              .getBoundingClientRect();
                            const mainCanvasRight =
                              mainBBox.left + mainBBox.width;
                            const mainCanvasBottom =
                              mainBBox.top + fixedStageHeightPx;

                            // Determine source canvas from first element
                            const firstElement =
                              wrapperDragStartRef.current.elementPositions[0];
                            const sourceCanvas = firstElement.isOnMainCanvas
                              ? "main"
                              : "offCanvas";
                            const sourceStage =
                              sourceCanvas === "main" ? mainStage : offStage;
                            const sourceBBox =
                              sourceCanvas === "main" ? mainBBox : offBBox;

                            let shouldMoveGroup = false;
                            let targetCanvas = null;

                            // Check each element to see if it crossed the boundary
                            for (const initialPos of wrapperDragStartRef.current
                              .elementPositions) {
                              const elementCurrentX = initialPos.x + deltaX;
                              const elementCurrentY = initialPos.y + deltaY;

                              // Get element from state arrays
                              let element = null;
                              switch (initialPos.type) {
                                case "Rect":
                                  element = initialPos.isOnMainCanvas
                                    ? rectangles.find(
                                        (r) => r.id === initialPos.id,
                                      )
                                    : offCanvasRectangles.find(
                                        (r) => r.id === initialPos.id,
                                      );
                                  break;
                                case "Circle":
                                  element = initialPos.isOnMainCanvas
                                    ? circles.find(
                                        (c) => c.id === initialPos.id,
                                      )
                                    : offCanvasCircles.find(
                                        (c) => c.id === initialPos.id,
                                      );
                                  break;
                                case "Image":
                                  element = initialPos.isOnMainCanvas
                                    ? images.find(
                                        (img) => img.id === initialPos.id,
                                      )
                                    : offCanvasImages.find(
                                        (img) => img.id === initialPos.id,
                                      );
                                  break;
                                case "Text":
                                  element = initialPos.isOnMainCanvas
                                    ? texts.find((t) => t.id === initialPos.id)
                                    : offCanvasTexts.find(
                                        (t) => t.id === initialPos.id,
                                      );
                                  break;
                                case "Arrow":
                                  element = initialPos.isOnMainCanvas
                                    ? lines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow || hasEndArrow;
                                      }) ||
                                      arrows.find((a) => a.id === initialPos.id)
                                    : offCanvasLines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow || hasEndArrow;
                                      }) ||
                                      offCanvasArrows.find(
                                        (a) => a.id === initialPos.id,
                                      );
                                  break;
                                case "DoubleSidedArrow":
                                  element = initialPos.isOnMainCanvas
                                    ? lines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow && hasEndArrow;
                                      }) ||
                                      doubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      )
                                    : offCanvasLines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow && hasEndArrow;
                                      }) ||
                                      offCanvasDoubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      );
                                  break;
                                case "Line":
                                  element = initialPos.isOnMainCanvas
                                    ? lines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        return (
                                          !l.hasStartArrow &&
                                          !l.hasEndArrow &&
                                          !l.pointerAtBeginning &&
                                          !l.pointerAtEnd
                                        );
                                      })
                                    : offCanvasLines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        return (
                                          !l.hasStartArrow &&
                                          !l.hasEndArrow &&
                                          !l.pointerAtBeginning &&
                                          !l.pointerAtEnd
                                        );
                                      });
                                  break;
                              }

                              if (!element) continue;

                              // Calculate element's center in client coordinates
                              const elementBBox = getElementBoundingBox(
                                {
                                  ...element,
                                  x: elementCurrentX,
                                  y: elementCurrentY,
                                },
                                initialPos.type,
                              );
                              const elementCenterX =
                                elementBBox.x + elementBBox.width / 2;
                              const elementCenterY =
                                elementBBox.y + elementBBox.height / 2;
                              const elementClientX =
                                sourceBBox.left + elementCenterX;
                              const elementClientY =
                                sourceBBox.top + elementCenterY;

                              // Check if this element crosses the boundary
                              const elementTarget = determineTargetCanvas({
                                type: initialPos.type,
                                draggedElement: {
                                  x: elementCurrentX,
                                  y: elementCurrentY,
                                },
                                clientX: elementClientX,
                                clientY: elementClientY,
                                mainBBox,
                                offBBox,
                                mainCanvasRight,
                                mainCanvasBottom,
                                sourceStageRef: sourceStage,
                                mainStage,
                                offCanvasStage: offStage,
                                isFromMainCanvas: sourceCanvas === "main",
                              });

                              // If ANY element crosses the boundary, move the entire group
                              if (
                                elementTarget &&
                                elementTarget !== sourceCanvas
                              ) {
                                shouldMoveGroup = true;
                                targetCanvas = elementTarget;
                                break;
                              }
                            }

                            // If any element crossed the boundary, move the entire group
                            if (shouldMoveGroup && targetCanvas) {
                              // Clear group drag state before moving
                              isGroupDraggingRef.current = false;
                              setGroupDragPreviews([]);
                              groupDragPreviewsRef.current = [];

                              // Calculate overridePositions
                              const overridePositions = {};
                              for (const initialPos of wrapperDragStartRef
                                .current.elementPositions) {
                                overridePositions[initialPos.id] = {
                                  x: initialPos.x + deltaX,
                                  y: initialPos.y + deltaY,
                                };
                              }

                              // Get mouse position
                              const mouseClientX =
                                globalMousePositionRef.current.x;
                              const mouseClientY =
                                globalMousePositionRef.current.y;

                              handleCopyGroupBetweenCanvases(
                                finalX,
                                finalY,
                                mouseClientX,
                                mouseClientY,
                                overridePositions,
                              );

                              wrapperDragStartRef.current = {
                                x: 0,
                                y: 0,
                                elementPositions: [],
                              };
                              return;
                            }
                          }
                        }

                        // Always run sync/clamp on wrapper drag-end, even when history save is disabled.
                        syncGroupDragPositionsToState();
                        if (saveToHistory) {
                          saveToHistory();
                        }
                        wrapperJustDraggedRef.current = true;
                        setTimeout(() => {
                          wrapperJustDraggedRef.current = false;
                        }, 200);
                        individualElementDragStartRef.current = {
                          elementPositions: [],
                        };
                        // CRITICAL: Clear group drag previews on drag end
                        // This prevents showing both preview and actual elements
                        isGroupDraggingRef.current = false;
                        groupDragSourceStageRef.current = null;
                        setGroupDragPreviews([]);
                        groupDragPreviewsRef.current = [];
                        wrapperDragStartRef.current = {
                          x: 0,
                          y: 0,
                          elementPositions: [],
                        };
                      }}
                      onClick={(e) => {
                        if (wrapperJustDraggedRef.current) {
                          return;
                        }

                        if (lockedAction) {
                          return;
                        }

                        const stage = offCanvasStageRef.current;
                        if (!stage) return;

                        stage.setPointersPositions(e.evt);
                        const pointerPos = stage.getPointerPosition();
                        if (!pointerPos) return;

                        const isPointInElement = (
                          element,
                          elementType,
                          point,
                        ) => {
                          const bbox = getElementBoundingBox(
                            element,
                            elementType,
                          );
                          return (
                            point.x >= bbox.x &&
                            point.x <= bbox.x + bbox.width &&
                            point.y >= bbox.y &&
                            point.y <= bbox.y + bbox.height
                          );
                        };

                        const offCanvasLineIds = new Set(
                          offCanvasLines.map((l) => l.id),
                        );
                        const allElements = [
                          ...offCanvasTexts.map((t) => ({
                            ...t,
                            type: "Text",
                          })),
                          ...offCanvasLines.map((l) => ({
                            ...l,
                            type: unifiedLineLikeToSelectionType(l),
                          })),
                          ...offCanvasArrows
                            .filter((a) => !offCanvasLineIds.has(a.id))
                            .map((a) => ({
                              ...a,
                              type: "Arrow",
                            })),
                          ...offCanvasDoubleSidedArrows
                            .filter((a) => !offCanvasLineIds.has(a.id))
                            .map((a) => ({
                              ...a,
                              type: "DoubleSidedArrow",
                            })),
                          ...offCanvasCircles.map((c) => ({
                            ...c,
                            type: "Circle",
                          })),
                          ...offCanvasRectangles.map((r) => ({
                            ...r,
                            type: "Rect",
                          })),
                          ...offCanvasImages.map((img) => ({
                            ...img,
                            type: "Image",
                          })),
                        ];

                        // Helper: is element (id, type) on main canvas (used for cross-canvas multi-select guard)
                        // Must match isElementInMainCanvasArray: Arrow/DoubleSidedArrow can be in lines array (unified line/arrow)
                        const isElementInMainCanvas = (
                          elementId,
                          elementType,
                        ) => {
                          if (!elementId || !elementType) return false;
                          switch (elementType) {
                            case "Rect":
                              return rectangles.some((r) => r.id === elementId);
                            case "Circle":
                              return circles.some((c) => c.id === elementId);
                            case "Image":
                              return images.some((i) => i.id === elementId);
                            case "Text":
                              return texts.some((t) => t.id === elementId);
                            case "Arrow":
                              return (
                                arrows.some((a) => a.id === elementId) ||
                                lines.some((l) => {
                                  const hasStartArrow =
                                    l.hasStartArrow === true ||
                                    l.pointerAtBeginning === true;
                                  const hasEndArrow =
                                    l.hasEndArrow === true ||
                                    l.pointerAtEnd === true;
                                  return (
                                    l.id === elementId &&
                                    (hasStartArrow || hasEndArrow) &&
                                    !(hasStartArrow && hasEndArrow)
                                  );
                                })
                              );
                            case "Line":
                              return lines.some((l) => l.id === elementId);
                            case "DoubleSidedArrow":
                              return (
                                doubleSidedArrows.some(
                                  (d) => d.id === elementId,
                                ) ||
                                lines.some((l) => {
                                  const hasStartArrow =
                                    l.hasStartArrow === true ||
                                    l.pointerAtBeginning === true;
                                  const hasEndArrow =
                                    l.hasEndArrow === true ||
                                    l.pointerAtEnd === true;
                                  return (
                                    l.id === elementId &&
                                    hasStartArrow &&
                                    hasEndArrow
                                  );
                                })
                              );
                            default:
                              return false;
                          }
                        };

                        const clickedElement = allElements.find((element) =>
                          isPointInElement(element, element.type, pointerPos),
                        );
                        if (!clickedElement) return;

                        const currentSelection = [...(selectedObjects || [])];
                        if (
                          selectedObject &&
                          !currentSelection.some(
                            (obj) =>
                              obj.id === selectedObject.id &&
                              obj.type === selectedObject.type,
                          )
                        ) {
                          currentSelection.push(selectedObject);
                        }

                        const isSelected = currentSelection.some(
                          (obj) =>
                            obj.id === clickedElement.id &&
                            obj.type === clickedElement.type,
                        );

                        if (isMultiSelectMode && isSelected) {
                          cancelActiveGroupDrag();
                          const remainingSelection = currentSelection.filter(
                            (obj) =>
                              !(
                                obj.id === clickedElement.id &&
                                obj.type === clickedElement.type
                              ),
                          );
                          const nextPrimary = remainingSelection[0] || null;

                          if (setSelectedObjects) {
                            setSelectedObjects(remainingSelection);
                          }
                          if (setSelectedObject) {
                            setSelectedObject(nextPrimary);
                          }
                          if (setSelectedImage) {
                            setSelectedImage(
                              nextPrimary?.type === "Image"
                                ? nextPrimary
                                : null,
                            );
                          }
                          if (setSelectedText) {
                            setSelectedText(
                              nextPrimary?.type === "Text" ? nextPrimary : null,
                            );
                          }
                          if (setSelectedTextId) {
                            setSelectedTextId(
                              nextPrimary?.type === "Text"
                                ? nextPrimary.id
                                : null,
                            );
                          }
                          if (setShowTextBox) {
                            setShowTextBox(nextPrimary?.type === "Text");
                          }
                          if (setSelectedArrow) {
                            setSelectedArrow(
                              nextPrimary?.type === "Arrow"
                                ? nextPrimary
                                : null,
                            );
                          }
                          if (setSelectedLine) {
                            setSelectedLine(
                              nextPrimary?.type === "Line" ? nextPrimary : null,
                            );
                          }
                          if (setSelectedDoubleSidedArrow) {
                            setSelectedDoubleSidedArrow(
                              nextPrimary?.type === "DoubleSidedArrow"
                                ? nextPrimary
                                : null,
                            );
                          }

                          stopKonvaDragUnderPointer(stage, pointerPos);
                          e.evt?.preventDefault?.();
                          e.evt?.stopPropagation?.();
                          return;
                        }

                        if (isSelected) return;

                        // Cross-canvas multi-select: when multi-select is on, only allow elements from the same canvas
                        if (
                          isMultiSelectMode &&
                          (selectedObjects?.length > 0 || selectedObject)
                        ) {
                          const allSelected = [...(selectedObjects || [])];
                          if (
                            selectedObject &&
                            !allSelected.some(
                              (o) =>
                                o.id === selectedObject.id &&
                                o.type === selectedObject.type,
                            )
                          ) {
                            allSelected.push(selectedObject);
                          }
                          const anyFromMainCanvas = allSelected.some((obj) =>
                            isElementInMainCanvas(obj.id, obj.type),
                          );
                          if (anyFromMainCanvas) {
                            // In normal selection mode, switching stages should reset selection
                            // (same UX as off -> main whitespace click).
                            const cloneActive =
                              isCloneMode ||
                              lockedAction === ACTIONS.SELECT ||
                              cloneTemplate !== null ||
                              (cloneTemplates && cloneTemplates.length > 0);

                            if (!cloneActive) {
                              if (setSelectedObjects) setSelectedObjects([]);
                              if (setSelectedObject) setSelectedObject(null);
                              if (setSelectedImage) setSelectedImage(null);
                              if (setSelectedText) setSelectedText(null);
                              if (setSelectedTextId) setSelectedTextId(null);
                              if (setShowTextBox) setShowTextBox(false);
                              if (setSelectedArrow) setSelectedArrow(null);
                              if (setSelectedLine) setSelectedLine(null);
                              if (setSelectedDoubleSidedArrow)
                                setSelectedDoubleSidedArrow(null);

                              // Clear transformer nodes so stale handles don't linger
                              transformerRef?.current?.nodes?.([]);
                            }

                            // Continue: below code will select the clicked off-canvas element.
                          }
                        }

                        const foundElement = {
                          ...clickedElement,
                          type: clickedElement.type,
                        };
                        if (setSelectedObject) {
                          setSelectedObject(foundElement);
                        }
                        if (isMultiSelectMode && setSelectedObjects) {
                          setSelectedObjects((prev) => {
                            const exists = prev.some(
                              (obj) =>
                                obj.id === foundElement.id &&
                                obj.type === foundElement.type,
                            );
                            if (exists) return prev;
                            return [...prev, foundElement];
                          });
                        } else if (setSelectedObjects) {
                          setSelectedObjects([foundElement]);
                        }
                      }}
                    />
                  )}

                  {/* Individual highlight rectangles removed - only wrapper box is shown */}
                </>
              );
            })()}

          {/* Transformer for off-canvas elements - REMOVED: No transformers for off-canvas elements */}
          {false &&
            !isLeavingCanvas &&
            // Only show transformer when single object is selected (not multiple)
            selectedObjects.length === 0 &&
            selectedObject?.id &&
            selectedObject?.type !== "Arrow" &&
            selectedObject?.type !== "DoubleSidedArrow" &&
            selectedObject?.type !== "Line" &&
            !(
              selectedObject?.type === "Image" &&
              selectedObject?.isOnTopLayer === true
            ) &&
            (() => {
              // Check if element is in off-canvas (not main canvas)
              const { isInOffCanvas } = findElementCanvas({
                selectedObject,
                rectangles,
                circles,
                images,
                texts,
                arrows,
                lines,
                doubleSidedArrows,
                offCanvasRectangles,
                offCanvasCircles,
                offCanvasImages,
                offCanvasTexts,
                offCanvasArrows,
                offCanvasLines,
                offCanvasDoubleSidedArrows,
              });

              // Only render transformer for off-canvas elements
              if (!isInOffCanvas || !offCanvasStageRef?.current) {
                return null;
              }
              const node = offCanvasStageRef.current.findOne(
                `#${selectedObject.id}`,
              );

              if (!node || !node.getLayer()) {
                return null;
              }

              // Additional safety check: ensure the node still exists in the layer
              try {
                const layer = node.getLayer();
                if (
                  !layer ||
                  !layer.children ||
                  !layer.children.includes(node)
                ) {
                  // Node has been deleted or removed from layer
                  return null;
                }
              } catch (e) {
                // Node access error, likely deleted
                return null;
              }

              // ⬇️ Default anchors
              let anchors = [
                "top-left",
                "top-center",
                "top-right",
                "middle-left",
                "middle-right",
                "bottom-left",
                "bottom-center",
                "bottom-right",
              ];

              // ⬇️ Configure anchors based on element type and maintainAspectRatio
              if (selectedObject?.type === "Text") {
                // CRITICAL FIX: Hide anchors when text is in edit mode
                const isTextInEditMode =
                  editTextBoxContent || showEditNote || showEditTask;
                anchors = isTextInEditMode
                  ? []
                  : ["middle-left", "middle-right"];
              } else if (
                selectedObject?.type === "Image" ||
                selectedObject?.type === "Rectangle" ||
                selectedObject?.type === "Rect" ||
                selectedObject?.type === "rectangle" ||
                selectedObject?.type === "Circle"
              ) {
                if (selectedObject?.maintainAspectRatio === true) {
                  // Checked: Show only bottom-right handle for proportional scaling
                  anchors = ["bottom-right"];
                } else {
                  // Unchecked (including undefined): Show right, bottom, and bottom-right handles
                  anchors = ["middle-right", "bottom-center", "bottom-right"];
                }
              }

              // Match main-canvas interaction: allow free transform during interaction.
              // Boundary normalization is handled on transform end.
              const boundBoxFunc =
                selectedObject?.type === "Text"
                  ? (oldBox, newBox) => {
                      if (newBox.width < 10 || newBox.height < 10) return oldBox;
                      return { ...newBox };
                    }
                  : (oldBox, newBox) => {
                      const minWidth = Math.max(10, oldBox.width * 0.1);
                      const minHeight = Math.max(10, oldBox.height * 0.1);
                      const clamped = { ...newBox };
                      if (clamped.width < minWidth) clamped.width = minWidth;
                      if (clamped.height < minHeight) clamped.height = minHeight;

                      // CRITICAL: Use active anchor to distinguish resize vs rotate.
                      // AABB width/height can change during rotation for rotated nodes.
                      const activeAnchor =
                        typeof offCanvasTransformerRef?.current?.getActiveAnchor ===
                        "function"
                          ? offCanvasTransformerRef.current.getActiveAnchor()
                          : null;
                      const isResize = !!activeAnchor && activeAnchor !== "rotater";

                      // Only apply position fix during resize, not rotation
                      // During rotation, let the transformer handle position naturally
                      if (isResize) {
                        if (selectedObject?.type === "Circle") {
                          // Circle is center-anchored, while Transformer boxes are top-left based.
                          // Lock box origin during resize so boundary clamps don't push the node down.
                          clamped.x = oldBox.x;
                          clamped.y = oldBox.y;
                        }
                      }

                      return clamped;
                    };

              // Hide anchors while the node itself is being dragged
              // CRITICAL: Also hide when elements are being dragged via custom flags:
              // - TextElement: _isHtmlDragging during HTML drags
              // - ImageElement: _isDragging during image drags
              const isDragging =
                (typeof node.isDragging === "function"
                  ? node.isDragging()
                  : false) ||
                node.getAttr?.("_isHtmlDragging") ||
                node.getAttr?.("_isDragging");
              const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;

              return (
                <Transformer
                  key={`offcanvas-transformer-${selectedObject?.id || "null"}-${selectedObject?.type === "Rect" || selectedObject?.type === "Rectangle" ? rectangleTransformerKey : circleTransformerKey}-${
                    selectedObject?.maintainAspectRatio || false
                  }-${anchors.join("-")}-${
                    selectedObject?.maintainAspectRatio ? "locked" : "free"
                  }-edit-${editTextBoxContent || false}`}
                  ref={offCanvasTransformerRef}
                  nodes={
                    node &&
                    typeof node.getLayer === "function" &&
                    node.getLayer() &&
                    !(typeof node.isDestroyed === "function" && node.isDestroyed())
                      ? [node]
                      : []
                  }
                  rotateEnabled={
                    selectedObject?.type !== "Text" &&
                    !isDuplicatePlacementActive
                  }
                  flipEnabled={false}
                  boundBoxFunc={boundBoxFunc}
                  enabledAnchors={effectiveAnchors}
                  forceUpdate={
                    selectedObject?.type === "Circle"
                      ? Date.now()
                      : selectedObject?.type === "Rect" ||
                          selectedObject?.type === "Rectangle"
                        ? Date.now()
                        : selectedObject?.type === "Text"
                          ? `text-${anchors.join(",")}`
                          : undefined
                  }
                  keepRatio={selectedObject?.maintainAspectRatio || false}
                  centeredScaling={false}
                  anchorStroke={
                    selectedObject?.type === "Image" ? "#017BFF" : undefined
                  }
                  onTransformStart={(e) => {
                    crossStageGestureBegin(
                      offCanvasStageRef.current,
                      e?.evt,
                    );
                  }}
                  onTransform={(e) => {
                    // Safety checks
                    if (
                      !e ||
                      !e.target ||
                      !selectedObject ||
                      !selectedObject.id
                    ) {
                      return;
                    }

                    const node = e.target;
                    const newWidth = node.width() * node.scaleX();
                    const newHeight = node.height() * node.scaleY();

                    // Use node coordinates directly (same as main canvas)
                    // No coordinate conversion needed - rectangles use top-left coordinates consistently
                    const x = node.x();
                    const y = node.y();

                    // Define newAttrs for all element types
                    const newAttrs = {
                      id: selectedObject.id,
                      x: x,
                      y: y,
                      width: newWidth,
                      height: newHeight,
                      scaleX: 1,
                      scaleY: 1,
                      rotation: node.rotation(),
                    };

                    if (selectedObject?.type === "Line") {
                      const lineNode = e.target;
                      const scaleX = lineNode.scaleX();
                      const scaleY = lineNode.scaleY();
                      const x = lineNode.x();
                      const y = lineNode.y();

                      // Find the line in the state
                      const lineId = selectedObject.id;
                      const currentLine = offCanvasLines.find(
                        (l) => l.id === lineId,
                      );
                      if (currentLine) {
                        const points = currentLine.points || [0, 0, 0, 0];
                        const newPoints = points.map((coord, i) => {
                          if (i % 2 === 0) {
                            // X coordinate
                            return coord * scaleX;
                          } else {
                            // Y coordinate
                            return coord * scaleY;
                          }
                        });

                        // Update the line state
                        updateLineProperties(lineId, {
                          x: x,
                          y: y,
                          points: newPoints,
                          width: Math.abs(newPoints[2] - newPoints[0]),
                          height: Math.abs(newPoints[3] - newPoints[1]),
                          skipOffset: true,
                        });
                      }
                    } else {
                      // For other elements, update their properties
                      // Update the appropriate state based on element type
                      if (selectedObject?.type === "Rect") {
                        setOffCanvasRectangles((prev) =>
                          prev.map((rect) =>
                            rect.id === selectedObject.id
                              ? { ...rect, ...newAttrs }
                              : rect,
                          ),
                        );
                      } else if (selectedObject?.type === "Circle") {
                        setOffCanvasCircles((prev) =>
                          prev.map((circle) =>
                            circle.id === selectedObject.id
                              ? { ...circle, ...newAttrs }
                              : circle,
                          ),
                        );
                      } else if (selectedObject?.type === "Image") {
                        setOffCanvasImages((prev) =>
                          prev.map((img) =>
                            img.id === selectedObject.id
                              ? { ...img, ...newAttrs }
                              : img,
                          ),
                        );
                      }
                    }

                    // Update selectedObject to reflect changes
                    setSelectedObject((prev) => ({
                      ...prev,
                      ...newAttrs,
                    }));
                  }}
                  onTransformEnd={(e) => {
                    crossStageGestureEnd();
                    justFinishedTransformRef.current = true;
                    // Safety checks
                    if (
                      !e ||
                      !e.target ||
                      !selectedObject ||
                      !selectedObject.id
                    ) {
                      return;
                    }

                    const node = e.target;

                    // CRITICAL FIX: For images, always use node dimensions to prevent resize reversion
                    // Calculate dimensions from the actual node state, not from potentially stale React state
                    const finalScaleX = node.scaleX();
                    const finalScaleY = node.scaleY();
                    let newWidth = node.width() * Math.abs(finalScaleX);
                    let newHeight = node.height() * Math.abs(finalScaleY);

                    // Use node coordinates directly (same as main canvas)
                    // No coordinate conversion needed - rectangles use top-left coordinates consistently
                    const x = node.x();
                    const y = node.y();

                    // CRITICAL FIX: For images, handle rotation extraction properly
                    let finalRotation = node.rotation();
                    if (selectedObject?.type === "Image") {
                      // Find the current image in state to get crop/transform properties
                      const currentImage = offCanvasImages.find(
                        (img) => img.id === selectedObject.id,
                      );

                      if (currentImage) {
                        // Extract post rotation from total rotation (similar to main canvas top-layer logic)
                        let totalRotation = node.rotation() % 360;
                        if (totalRotation < 0) totalRotation += 360;

                        const isApiImage =
                          currentImage.src &&
                          (currentImage.src.startsWith("http://") ||
                            currentImage.src.startsWith("https://"));
                        const straighten =
                          currentImage.originalCropRotation || 0;

                        const usesStraightenedCropPath =
                          !!currentImage.cropData &&
                          currentImage.originalCropRotation !== undefined &&
                          currentImage.originalCropRotation !== 0;

                        let postRotation;
                        // For straightened+cropped images rendered via pre-rotated source,
                        // node rotation is already post-rotation.
                        if (
                          usesStraightenedCropPath ||
                          (currentImage.hasBakedTransforms && !isApiImage)
                        ) {
                          // For baked local images too, straighten is baked in, so total = post
                          postRotation = totalRotation;
                        } else {
                          // For non-baked or API images, total = straighten + post
                          // Extract post rotation: post = total - straighten
                          postRotation = (totalRotation - straighten) % 360;
                          if (postRotation < 0) postRotation += 360;
                        }

                        finalRotation = postRotation;

                        // CRITICAL FIX: Ensure dimensions are at least minimum size
                        newWidth = Math.max(5, newWidth);
                        newHeight = Math.max(5, newHeight);

                        // Handle aspect ratio if enabled
                        if (currentImage.maintainAspectRatio) {
                          const aspectRatio =
                            currentImage.originalAspectRatio ||
                            currentImage.width / currentImage.height;
                          const dominantScale = Math.max(
                            Math.abs(finalScaleX),
                            Math.abs(finalScaleY),
                          );

                          newWidth = Math.max(5, node.width() * dominantScale);
                          newHeight = Math.max(5, newWidth / aspectRatio);

                          // Round to avoid floating-point precision issues
                          newWidth = Math.round(newWidth * 100) / 100;
                          newHeight = Math.round(newHeight * 100) / 100;
                        }
                      }
                    }

                    const newAttrs = {
                      id: selectedObject.id,
                      x: x,
                      y: y,
                      width: newWidth,
                      height: newHeight,
                      scaleX: 1,
                      scaleY: 1,
                      rotation: finalRotation,
                    };

                    // Update the appropriate state based on element type
                    if (selectedObject?.type === "Rect") {
                      setOffCanvasRectangles((prev) =>
                        prev.map((rect) =>
                          rect.id === selectedObject.id
                            ? { ...rect, ...newAttrs }
                            : rect,
                        ),
                      );
                    } else if (selectedObject?.type === "Circle") {
                      setOffCanvasCircles((prev) =>
                        prev.map((circle) =>
                          circle.id === selectedObject.id
                            ? { ...circle, ...newAttrs }
                            : circle,
                        ),
                      );
                    } else if (selectedObject?.type === "Image") {
                      setOffCanvasImages((prev) =>
                        prev.map((img) =>
                          img.id === selectedObject.id
                            ? { ...img, ...newAttrs }
                            : img,
                        ),
                      );
                    } else if (selectedObject?.type === "Text") {
                      setOffCanvasTexts((prev) =>
                        prev.map((text) =>
                          text.id === selectedObject.id
                            ? { ...text, ...newAttrs }
                            : text,
                        ),
                      );
                    }

                    // Update selectedObject to reflect changes
                    setSelectedObject((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        ...newAttrs,
                      };
                    });

                    // Save to history with appropriate operation type
                    if (typeof saveToHistory === "function") {
                      let operationType = "general";
                      if (selectedObject?.type === "Rect") {
                        operationType = "update_rectangle_properties";
                      } else if (selectedObject?.type === "Circle") {
                        operationType = "update_circle_properties";
                      } else if (selectedObject?.type === "Image") {
                        operationType = "update_image_properties";
                      } else if (selectedObject?.type === "Text") {
                        operationType = "update_text_properties";
                      }
                      // For off-canvas Image: skip save when transform was a no-op (e.g. user just selected then unselected).
                      // Avoids redundant [add_image, update_image_properties] so one undo removes the image (matches rectangles).
                      if (
                        operationType === "update_image_properties" &&
                        selectedObject?.type === "Image"
                      ) {
                        const prevImg = offCanvasImages.find(
                          (img) => img.id === selectedObject.id,
                        );
                        if (prevImg) {
                          const r = (v) =>
                            typeof v === "number" && !Number.isNaN(v)
                              ? Math.round(v * 100) / 100
                              : v;
                          if (
                            r(prevImg.x) === r(newAttrs.x) &&
                            r(prevImg.y) === r(newAttrs.y) &&
                            r(prevImg.width) === r(newAttrs.width) &&
                            r(prevImg.height) === r(newAttrs.height) &&
                            r(prevImg.rotation ?? 0) === r(newAttrs.rotation)
                          ) {
                            // No-op transform, skip so history stays [initial, add_image]
                            return;
                          }
                        }
                      }
                      saveToHistory(operationType);
                    }
                  }}
                />
              );
            })()}

          {/* Transformer for off-canvas multi-selection - REMOVED: No transformers for off-canvas elements */}
          {false &&
            !isLeavingCanvas &&
            selectedObjects &&
            selectedObjects.length > 0 &&
            // Don't show transformers when multiple objects are selected - show wrapper rectangle instead
            selectedObjects.length === 1 &&
            selectedObjects
              .filter((obj) => {
                // When multi-select mode is enabled, only show transformers for individually selected items
                if (isMultiSelectMode) {
                  return individuallySelectedIds.has(obj.id);
                }
                // When multi-select mode is disabled, only show transformer for the selectedObject
                return selectedObject?.id === obj.id;
              })
              .map((obj) => {
                if (
                  !offCanvasStageRef.current ||
                  obj.type === "Arrow" ||
                  obj.type === "DoubleSidedArrow" ||
                  obj.type === "Line" ||
                  obj.type === "Image"
                )
                  return null;

                // Check if element is in off-canvas (not main canvas)
                const { isInOffCanvas } = findElementCanvas({
                  selectedObject: obj,
                  rectangles,
                  circles,
                  images,
                  texts,
                  arrows,
                  lines,
                  doubleSidedArrows,
                  offCanvasRectangles,
                  offCanvasCircles,
                  offCanvasImages,
                  offCanvasTexts,
                  offCanvasArrows,
                  offCanvasLines,
                  offCanvasDoubleSidedArrows,
                });

                // Only render transformer for off-canvas elements
                if (!isInOffCanvas) return null;

                const node = offCanvasStageRef.current.findOne(`#${obj.id}`);
                if (!node || !node.getLayer()) return null;

                // Additional safety check: ensure the node still exists in the layer
                try {
                  const layer = node.getLayer();
                  if (
                    !layer ||
                    !layer.children ||
                    !layer.children.includes(node)
                  ) {
                    return null;
                  }
                } catch (e) {
                  return null;
                }

                const anchors =
                  obj.type === "Circle"
                    ? obj?.maintainAspectRatio === true
                      ? ["bottom-right"]
                      : ["middle-right", "bottom-center", "bottom-right"]
                    : [
                        "top-left",
                        "top-center",
                        "top-right",
                        "middle-right",
                        "bottom-right",
                        "bottom-center",
                        "bottom-left",
                        "middle-left",
                      ];

                const offStageWMulti =
                  offCanvasStageRef?.current?.width?.() ?? 0;
                const offStageHMulti =
                  offCanvasStageRef?.current?.height?.() ?? 0;

                const boundBoxFunc =
                  obj.type === "Text"
                    ? (oldBox, newBox) => {
                        if (newBox.width < 10) newBox.width = 10;
                        if (newBox.height < 10) newBox.height = 10;

                        // For text elements, prevent resizing from left edge
                        if (newBox.x !== oldBox.x) {
                          newBox.x = oldBox.x;
                        }

                        return newBox;
                      }
                    : (oldBox, newBox) => {
                        if (newBox.width < 10) newBox.width = 10;
                        if (newBox.height < 10) newBox.height = 10;
                        return newBox;
                      };

                const isDragging =
                  (typeof node.isDragging === "function"
                    ? node.isDragging()
                    : false) ||
                  node.getAttr?.("_isHtmlDragging") ||
                  node.getAttr?.("_isDragging");
                const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;

                return (
                  <Transformer
                    key={`offcanvas-transformer-multi-${obj.id}-${
                      obj?.maintainAspectRatio || false
                    }-${anchors.join("-")}-${
                      obj.maintainAspectRatio ? "locked" : "free"
                    }-edit-${editTextBoxContent || false}`}
                    ref={offCanvasTransformerRef}
                    nodes={
                      node &&
                      typeof node.getLayer === "function" &&
                      node.getLayer() &&
                      !(typeof node.isDestroyed === "function" && node.isDestroyed())
                        ? [node]
                        : []
                    }
                    rotateEnabled={
                      obj.type !== "Text" && !isDuplicatePlacementActive
                    }
                    flipEnabled={false}
                    boundBoxFunc={boundBoxFunc}
                    enabledAnchors={effectiveAnchors}
                    forceUpdate={
                      selectedObject?.type === "Circle"
                        ? Date.now()
                        : selectedObject?.type === "Text"
                          ? `text-${anchors.join(",")}`
                          : undefined
                    }
                    keepRatio={obj?.maintainAspectRatio || false}
                    centeredScaling={false}
                    onTransformStart={(e) => {
                      crossStageGestureBegin(
                        offCanvasStageRef.current,
                        e?.evt,
                      );
                    }}
                    onTransform={(e) => {
                      if (!e || !e.target || !obj || !obj.id) {
                        return;
                      }

                      const node = e.target;
                      const newWidth = node.width() * node.scaleX();
                      const newHeight = node.height() * node.scaleY();

                      // Use node coordinates directly (same as main canvas)
                      // No coordinate conversion needed - rectangles use top-left coordinates consistently
                      const x = node.x();
                      const y = node.y();

                      const newAttrs = {
                        id: obj.id,
                        x: x,
                        y: y,
                        width: newWidth,
                        height: newHeight,
                        scaleX: 1,
                        scaleY: 1,
                        rotation: node.rotation(),
                      };

                      if (obj.type === "Rect") {
                        setOffCanvasRectangles((prev) =>
                          prev.map((rect) =>
                            rect.id === obj.id
                              ? { ...rect, ...newAttrs }
                              : rect,
                          ),
                        );
                      } else if (obj.type === "Circle") {
                        setOffCanvasCircles((prev) =>
                          prev.map((circle) =>
                            circle.id === obj.id
                              ? { ...circle, ...newAttrs }
                              : circle,
                          ),
                        );
                      } else if (obj.type === "Text") {
                        setOffCanvasTexts((prev) =>
                          prev.map((text) =>
                            text.id === obj.id
                              ? { ...text, ...newAttrs }
                              : text,
                          ),
                        );
                      }

                      setSelectedObject((prev) => ({
                        ...prev,
                        ...newAttrs,
                      }));
                      setSelectedObjects((prev) =>
                        prev.map((selectedObj) =>
                          selectedObj.id === obj.id
                            ? { ...selectedObj, ...newAttrs }
                            : selectedObj,
                        ),
                      );
                    }}
                    onTransformEnd={(e) => {
                      crossStageGestureEnd();
                      justFinishedTransformRef.current = true;
                      // CRITICAL: Don't save to history here - history will be saved when element is unselected
                    }}
                  />
                );
              })}
        </Layer>

        {/* Priority overlay for group-drag text previews with background on off-canvas */}
        {parseInt(canvasHeight) >= 5 && (
          <Layer listening={false}>
            {groupDragPreviews
              .filter(
                (preview) =>
                  preview.target === "offCanvas" &&
                  preview.type === "Text" &&
                  preview.data?.showBackground === true,
              )
              .map((preview, index) => (
                <React.Fragment
                  key={`group-preview-offcanvas-priority-${
                    preview.data?.id || index
                  }`}
                >
                  {renderTextElement({
                    text: preview.data,
                    isOffCanvas: true,
                    sourceStageRef: offCanvasStageRef.current,
                    isPreview: true,
                  })}
                </React.Fragment>
              ))}
          </Layer>
        )}

        {/* Transformer Layer for off-canvas elements */}
        <Layer>
          {/* Transformer for single selection on off-canvas */}
          {!isLeavingCanvas &&
            // Mirror the main-canvas isSingleSelection logic: accept length===0 (classic
            // single-select path) OR length===1 where the element matches selectedObject
            // (the path taken after a cross-canvas drag, where dragEndUtils calls
            // setSelectedObject but does not reset selectedObjects).
            (selectedObjects.length === 0 ||
              (selectedObjects.length === 1 &&
                selectedObjects[0]?.id === selectedObject?.id &&
                selectedObjects[0]?.type === selectedObject?.type)) &&
            selectedObject?.id &&
            selectedObject?.type !== "Arrow" &&
            selectedObject?.type !== "DoubleSidedArrow" &&
            selectedObject?.type !== "Line" &&
            !(
              selectedObject?.type === "Image" &&
              selectedObject?.isOnTopLayer === true
            ) &&
            (() => {
              // Check if element is in off-canvas (not main canvas)
              const { isInOffCanvas } = findElementCanvas({
                selectedObject,
                rectangles,
                circles,
                images,
                texts,
                arrows,
                lines,
                doubleSidedArrows,
                offCanvasRectangles,
                offCanvasCircles,
                offCanvasImages,
                offCanvasTexts,
                offCanvasArrows,
                offCanvasLines,
                offCanvasDoubleSidedArrows,
              });

              // Only render transformer for off-canvas elements in this layer
              if (!isInOffCanvas || !offCanvasStageRef?.current) {
                return null;
              }

              // IMPORTANT: findOne is called during React's render phase, BEFORE new Konva
              // nodes are committed. When an element is just transferred to the off-canvas, its
              // Konva node may not exist yet. Do NOT bail on a null node — always render the
              // <Transformer> so that offCanvasTransformerRef.current is set. The imperative
              // useTransformerManager RAF fires after commit and calls .nodes([node]) once the
              // node actually exists.
              const node = offCanvasStageRef.current.findOne(
                `#${selectedObject.id}`,
              );

              // Only skip rendering if node exists but is clearly invalid (stale/destroyed).
              // A null node means the element is still committing — that is fine.
              if (node) {
                try {
                  const layer = node.getLayer();
                  if (
                    !layer ||
                    !layer.children ||
                    !layer.children.includes(node)
                  ) {
                    return null;
                  }
                } catch (e) {
                  return null;
                }
              }

              // ⬇️ Default anchors
              let anchors = [
                "top-left",
                "top-center",
                "top-right",
                "middle-left",
                "middle-right",
                "bottom-left",
                "bottom-center",
                "bottom-right",
              ];

              // ⬇️ Configure anchors based on element type and maintainAspectRatio
              if (selectedObject?.type === "Text") {
                // CRITICAL FIX: Hide anchors when text is in edit mode
                const isTextInEditMode =
                  editTextBoxContent || showEditNote || showEditTask;
                anchors = isTextInEditMode
                  ? []
                  : ["middle-left", "middle-right"];
              } else if (
                selectedObject?.type === "Image" ||
                selectedObject?.type === "Rectangle" ||
                selectedObject?.type === "Rect" ||
                selectedObject?.type === "rectangle" ||
                selectedObject?.type === "Circle"
              ) {
                if (selectedObject?.maintainAspectRatio === true) {
                  anchors = ["bottom-right"];
                } else {
                  anchors = ["middle-right", "bottom-center", "bottom-right"];
                }
              }

              // Match main-canvas interaction: allow free transform during interaction.
              // Boundary normalization is handled on transform end.
              const boundBoxFunc =
                selectedObject?.type === "Text"
                  ? (oldBox, newBox) => {
                      if (newBox.width < 10 || newBox.height < 10) return oldBox;
                      return { ...newBox };
                    }
                  : (oldBox, newBox) => {
                      const minWidth = Math.max(10, oldBox.width * 0.1);
                      const minHeight = Math.max(10, oldBox.height * 0.1);
                      const clamped = { ...newBox };
                      if (clamped.width < minWidth) clamped.width = minWidth;
                      if (clamped.height < minHeight) clamped.height = minHeight;
                      return clamped;
                    };

              // CRITICAL: Keep anchors visible during drag (don't hide them)
              // Guard against null node (element still committing to off-canvas stage).
              const isDragging = node
                ? (typeof node.isDragging === "function"
                    ? node.isDragging()
                    : false) ||
                  node.getAttr?.("_isHtmlDragging") ||
                  node.getAttr?.("_isDragging")
                : false;
              const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;
              const isOffCanvasImageTransformer = selectedObject?.type === "Image";
              const imageAnchorStroke = isOffCanvasImageTransformer
                ? "#017BFF"
                : undefined;
              const imageRotateAnchorOffset = isOffCanvasImageTransformer
                ? 30
                : undefined;

              if (DEBUG_IMAGE_TRANSFORMER_FLOW && selectedObject?.type === "Image") {
              }

              return (
                <Transformer
                  key={`offcanvas-transformer-${selectedObject?.id || "null"}-${selectedObject?.type === "Rect" || selectedObject?.type === "Rectangle" ? rectangleTransformerKey : circleTransformerKey}-${
                    selectedObject?.maintainAspectRatio || false
                  }-${anchors.join("-")}-${
                    selectedObject?.maintainAspectRatio ? "locked" : "free"
                  }-edit-${editTextBoxContent || false}`}
                  ref={offCanvasTransformerRef}
                  nodes={node ? [node] : []}
                  rotateEnabled={
                    selectedObject?.type !== "Text" &&
                    !isDuplicatePlacementActive
                  }
                  flipEnabled={false}
                  boundBoxFunc={boundBoxFunc}
                  enabledAnchors={effectiveAnchors}
                  forceUpdate={
                    selectedObject?.type === "Circle"
                      ? Date.now()
                      : selectedObject?.type === "Rect" ||
                          selectedObject?.type === "Rectangle"
                        ? Date.now()
                        : selectedObject?.type === "Text"
                          ? `text-${anchors.join(",")}`
                          : undefined
                  }
                  keepRatio={selectedObject?.maintainAspectRatio || false}
                  centeredScaling={false}
                  anchorStroke={imageAnchorStroke}
                  anchorStrokeWidth={
                    isOffCanvasImageTransformer ? 2 : undefined
                  }
                  rotateAnchorOffset={imageRotateAnchorOffset}
                  onTransformStart={(e) => {
                    crossStageGestureBegin(
                      offCanvasStageRef.current,
                      e?.evt,
                    );
                    // CRITICAL FIX: Update transform state for off-canvas non-top-layer images
                    isTransformingRef.current.isTransforming = true;
                  }}
                  onTransform={(e) => {
                    // Handle transform for off-canvas elements
                    if (
                      !e ||
                      !e.target ||
                      !selectedObject ||
                      !selectedObject.id
                    ) {
                      return;
                    }
                    if (selectedObject?.type === "Circle") {
                      // Circle transform state is handled by CircleElement/circleRenderer.
                      // Avoid conflicting writes from stage-level transformer.
                      return;
                    }

                    const node = e.target;

                    const newWidth = node.width() * node.scaleX();
                    const newHeight = node.height() * node.scaleY();

                    // Use node coordinates directly (same as main canvas)
                    // No coordinate conversion needed - rectangles use top-left coordinates consistently
                    const x = node.x();
                    const y = node.y();

                    const newAttrs = {
                      x,
                      y,
                      width: newWidth,
                      height: newHeight,
                      rotation: node.rotation(),
                      scaleX: 1,
                      scaleY: 1,
                    };

                    // Update the appropriate off-canvas state
                    if (selectedObject?.type === "Rect") {
                      setOffCanvasRectangles((prev) =>
                        prev.map((rect) =>
                          rect.id === selectedObject.id
                            ? { ...rect, ...newAttrs }
                            : rect,
                        ),
                      );
                    } else if (selectedObject?.type === "Circle") {
                      const radius = Math.min(newWidth, newHeight) / 2;
                      setOffCanvasCircles((prev) =>
                        prev.map((circle) =>
                          circle.id === selectedObject.id
                            ? { ...circle, ...newAttrs, radius }
                            : circle,
                        ),
                      );
                    } else if (selectedObject?.type === "Text") {
                      setOffCanvasTexts((prev) =>
                        prev.map((text) =>
                          text.id === selectedObject.id
                            ? { ...text, ...newAttrs }
                            : text,
                        ),
                      );
                    }

                    // Update selectedObject
                    setSelectedObject((prev) => {
                      if (!prev) return prev;
                      return { ...prev, ...newAttrs };
                    });
                  }}
                  onTransformEnd={() => {
                    crossStageGestureEnd();
                    justFinishedTransformRef.current = true;
                    // CRITICAL FIX: Update transform state for off-canvas non-top-layer images
                    isTransformingRef.current.isTransforming = false;
                    // CRITICAL: Don't save to history here - history will be saved when element is unselected
                  }}
                />
              );
            })()}

          {/* Transformer for multi-selection on off-canvas */}
          {!isLeavingCanvas &&
            selectedObjects &&
            selectedObjects.length > 0 &&
            selectedObjects.length === 1 &&
            selectedObjects
              .filter((obj) => {
                if (isMultiSelectMode) {
                  return individuallySelectedIds.has(obj.id);
                }
                return selectedObject?.id === obj.id;
              })
              .map((obj) => {
                // Check if element is in off-canvas
                const { isInOffCanvas } = findElementCanvas({
                  selectedObject: obj,
                  rectangles,
                  circles,
                  images,
                  texts,
                  arrows,
                  lines,
                  doubleSidedArrows,
                  offCanvasRectangles,
                  offCanvasCircles,
                  offCanvasImages,
                  offCanvasTexts,
                  offCanvasArrows,
                  offCanvasLines,
                  offCanvasDoubleSidedArrows,
                });

                if (!isInOffCanvas || !offCanvasStageRef?.current) return null;

                if (
                  obj.type === "Arrow" ||
                  obj.type === "DoubleSidedArrow" ||
                  obj.type === "Line" ||
                  obj.type === "Image"
                )
                  return null;

                const node = offCanvasStageRef.current.findOne(`#${obj.id}`);
                if (!node || !node.getLayer()) return null;

                try {
                  const layer = node.getLayer();
                  if (
                    !layer ||
                    !layer.children ||
                    !layer.children.includes(node)
                  ) {
                    return null;
                  }
                } catch (e) {
                  return null;
                }

                const anchors =
                  obj.type === "Text"
                    ? ["middle-left", "middle-right"]
                    : obj.type === "Circle"
                      ? obj?.maintainAspectRatio === true
                        ? ["bottom-right"]
                        : ["middle-right", "bottom-center", "bottom-right"]
                      : obj?.maintainAspectRatio
                        ? ["bottom-right"]
                        : ["middle-right", "bottom-center", "bottom-right"];

                const boundBoxFunc =
                  obj.type === "Text"
                    ? (oldBox, newBox) => {
                        if (newBox.width < 10 || newBox.height < 10) return oldBox;
                        return { ...newBox };
                      }
                    : (oldBox, newBox) => {
                        const minWidth = Math.max(10, oldBox.width * 0.1);
                        const minHeight = Math.max(10, oldBox.height * 0.1);

                        const clamped = { ...newBox };
                        if (clamped.width < minWidth) clamped.width = minWidth;
                        if (clamped.height < minHeight) clamped.height = minHeight;
                        return clamped;
                      };

                // CRITICAL: Keep anchors visible during drag (don't hide them)
                const isDragging =
                  (typeof node.isDragging === "function"
                    ? node.isDragging()
                    : false) ||
                  node.getAttr?.("_isHtmlDragging") ||
                  node.getAttr?.("_isDragging");
                const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;

                return (
                  <Transformer
                    key={`offcanvas-multi-transformer-${obj.id}-${
                      obj?.maintainAspectRatio || false
                    }`}
                    ref={offCanvasTransformerRef}
                    nodes={
                      node &&
                      typeof node.getLayer === "function" &&
                      node.getLayer() &&
                      !(typeof node.isDestroyed === "function" && node.isDestroyed())
                        ? [node]
                        : []
                    }
                    rotateEnabled={
                      obj.type !== "Text" && !isDuplicatePlacementActive
                    }
                    flipEnabled={false}
                    boundBoxFunc={boundBoxFunc}
                    enabledAnchors={effectiveAnchors}
                    forceUpdate={
                      selectedObject?.type === "Circle" ? Date.now() : undefined
                    }
                    keepRatio={obj?.maintainAspectRatio || false}
                    centeredScaling={false}
                    onTransformStart={(e) => {
                      crossStageGestureBegin(
                        offCanvasStageRef.current,
                        e?.evt,
                      );
                    }}
                    onTransform={(e) => {
                      if (!e || !e.target || !obj || !obj.id) {
                        return;
                      }
                      if (obj.type === "Circle") {
                        // Circle transform state is handled by CircleElement/circleRenderer.
                        // Avoid conflicting writes from stage-level transformer.
                        return;
                      }

                      const node = e.target;

                      const newWidth = node.width() * node.scaleX();
                      const newHeight = node.height() * node.scaleY();

                      // Use node coordinates directly (same as main canvas)
                      // No coordinate conversion needed - rectangles use top-left coordinates consistently
                      const x = node.x();
                      const y = node.y();

                      const newAttrs = {
                        id: obj.id,
                        x: x,
                        y: y,
                        width: newWidth,
                        height: newHeight,
                        scaleX: 1,
                        scaleY: 1,
                        rotation: node.rotation(),
                      };

                      if (obj.type === "Rect") {
                        setOffCanvasRectangles((prev) =>
                          prev.map((rect) =>
                            rect.id === obj.id
                              ? { ...rect, ...newAttrs }
                              : rect,
                          ),
                        );
                      } else if (obj.type === "Circle") {
                        setOffCanvasCircles((prev) =>
                          prev.map((circle) =>
                            circle.id === obj.id
                              ? { ...circle, ...newAttrs }
                              : circle,
                          ),
                        );
                      } else if (obj.type === "Text") {
                        setOffCanvasTexts((prev) =>
                          prev.map((text) =>
                            text.id === obj.id
                              ? { ...text, ...newAttrs }
                              : text,
                          ),
                        );
                      }

                      setSelectedObject((prev) => ({
                        ...prev,
                        ...newAttrs,
                      }));
                      setSelectedObjects((prev) =>
                        prev.map((selectedObj) =>
                          selectedObj.id === obj.id
                            ? { ...selectedObj, ...newAttrs }
                            : selectedObj,
                        ),
                      );
                    }}
                    onTransformEnd={() => {
                      crossStageGestureEnd();
                      justFinishedTransformRef.current = true;
                      // CRITICAL: Don't save to history here - history will be saved when element is unselected
                    }}
                  />
                );
              })}
        </Layer>
      </Stage>

      {/* Off-canvas top-layer wrapper + Stage.
          WHY A WRAPPER DIV: Konva imperatively sets `container.style.position = 'relative'`
          on every Stage's container div, which overrides any `position: fixed` set via the
          Stage's style prop.  If we put position/zIndex on the Stage directly the canvas
          ends up in normal document flow (below the regular off-canvas Stage) and never
          overlaps the <Html> overlays it is supposed to cover.
          FIX: put `position: fixed` on a plain <div> (which Konva never touches), and let
          the Stage inside use Konva's default `position: relative` within that fixed wrapper.
          The wrapper sits at zIndex: 2 (ROOT) — above the off-canvas Stage stacking context
          (ROOT zIndex: 1) and all <Html> elements inside it, while still being below the
          main-canvas container (also ROOT zIndex: 2 but later in DOM order). */}
      {(offCanvasImagesTop.length > 0 ||
        offCanvasSelectionOcclusionImages.length > 0 ||
        hasOffCanvasTargetTopLayerPreviews) && (
        <div
          data-off-canvas-top-layer-wrapper="true"
          style={{
            position: "fixed",
            top: 0,
            left: offCanvasStageLeft,
            transform: `translate(${stagePos.x}px, ${stagePos.y}px)`,
            width: "100%",
            height: "100%",
            zIndex: 2,
            // Enable pointer events only when the user has selected a top-layer image so that
            // ImageElement's local transformer handles (rendered in this Stage's Layer) become
            // interactive.  Otherwise keep "none" so clicks pass through to elements below.
            pointerEvents:
              selectionPointerIsolationSide === "main"
                ? "none"
                : hasSelectedOffCanvasTopLayerImage
                  ? "auto"
                  : "none",
          }}
        >
          <Stage
            ref={offCanvasTopLayerStageRef}
            width={offCanvasStageWidth}
            height={offCanvasStageHeightPx}
            pixelRatio={CANVAS_PIXEL_RATIO}
            listening={hasSelectedOffCanvasTopLayerImage}
            onMouseDown={(e) => {
              if (!hasSelectedOffCanvasTopLayerImage) return;
              if (isTransformingRef.current?.isTransforming) return;

              const topLayerStage = e?.target?.getStage();
              if (!topLayerStage) return;

              const pointerPos = topLayerStage.getPointerPosition();
              if (!pointerPos) return;

              const clickedNode = topLayerStage.getIntersection(pointerPos);

              // Click is on the image itself or a transformer anchor/border →
              // let Konva handle it (drag, resize, rotate) and stop propagation.
              if (
                clickedNode &&
                clickedNode !== topLayerStage &&
                clickedNode.getLayer()
              ) {
                if (e.evt) {
                  e.evt.stopPropagation();
                  e.evt.stopImmediatePropagation();
                }
                e.cancelBubble = true;
                return;
              }

              // Transparent hit on this full-screen overlay: the user may be clicking an
              // off-canvas shape below. Clearing here wipes selection and breaks multi-select.
              const multiSelectActive =
                isMultiSelectModeRef?.current ?? isMultiSelectMode;
              if (multiSelectActive) {
                const offStage = offCanvasStageRef?.current;
                if (offStage && e.evt) {
                  offStage.setPointersPositions(e.evt);
                  const offPos = offStage.getPointerPosition();
                  const container = offStage.container();
                  const cr = container?.getBoundingClientRect();
                  const insideOffCanvas =
                    cr &&
                    e.evt.clientX >= cr.left &&
                    e.evt.clientX <= cr.right &&
                    e.evt.clientY >= cr.top &&
                    e.evt.clientY <= cr.bottom;
                  if (offPos && insideOffCanvas) {
                    const hit = offStage.getIntersection(offPos);
                    const isBgRect =
                      hit?.getClassName?.() === "Rect" &&
                      hit?.attrs?.id === "bg";
                    const isUsableHit =
                      hit &&
                      hit !== offStage &&
                      hit.getLayer?.() &&
                      !isBgRect;

                    if (isUsableHit) {
                      const konvaEvent = {
                        evt: e.evt,
                        target: hit,
                        currentTarget: hit,
                        cancelBubble: false,
                        getStage: () => offStage,
                      };
                      hit.fire("mousedown", konvaEvent);
                      hit.fire("click", konvaEvent);
                      if (recentDraggableElementMousedownRef) {
                        const id =
                          hit.attrs?.id ??
                          (typeof hit.id === "function" ? hit.id() : null);
                        recentDraggableElementMousedownRef.current = {
                          nodeId: id,
                          timestamp: Date.now(),
                        };
                      }
                    } else {
                      const bgNode = offStage.findOne("#bg");
                      if (bgNode) {
                        bgNode.fire("mousedown", {
                          evt: e.evt,
                          target: bgNode,
                          currentTarget: bgNode,
                          cancelBubble: false,
                          getStage: () => offStage,
                        });
                      }
                    }
                    if (e.evt) {
                      e.evt.preventDefault?.();
                      e.evt.stopPropagation?.();
                      e.evt.stopImmediatePropagation?.();
                    }
                    e.cancelBubble = true;
                    return;
                  }
                }
                if (e.evt) {
                  e.evt.preventDefault?.();
                  e.evt.stopPropagation?.();
                  e.evt.stopImmediatePropagation?.();
                }
                e.cancelBubble = true;
                return;
              }

              // Empty area on top-layer off-canvas overlay:
              // in SELECT+left-click mode, forward to off-canvas background so marquee
              // selection can start even when a top-layer image is currently selected.
              const shouldStartMarqueeOnOffCanvas =
                action === ACTIONS.SELECT && e?.evt?.button === 0;
              if (shouldStartMarqueeOnOffCanvas) {
                const offStage = offCanvasStageRef?.current;
                if (offStage && e.evt) {
                  offStage.setPointersPositions(e.evt);
                  const pointerPos = offStage.getPointerPosition();
                  if (pointerPos) {
                    if (blockClicksOnOffCanvasElements) {
                      e.evt.preventDefault?.();
                      e.evt.stopPropagation?.();
                      e.evt.stopImmediatePropagation?.();
                      e.cancelBubble = true;
                      return;
                    }

                    // Start off-canvas marquee selection directly from top-layer overlay.
                    setIsSelecting(true);
                    setSelectionBoxStage("off-canvas");
                    const nextBox = {
                      startX: pointerPos.x,
                      startY: pointerPos.y,
                      endX: pointerPos.x,
                      endY: pointerPos.y,
                    };
                    setSelectionBox(nextBox);
                    selectionBoxRef.current = nextBox;
                  }
                  e.evt.preventDefault?.();
                  e.evt.stopPropagation?.();
                  e.evt.stopImmediatePropagation?.();
                  e.cancelBubble = true;
                  return;
                }
              }

              // Non-select interactions keep prior behavior: clear single selection.
              const hasGroupSelection = (selectedObjects?.length || 0) > 1;
              if (!hasGroupSelection) {
                if (setSelectedObject) setSelectedObject(null);
                if (setSelectedObjects) setSelectedObjects([]);
              }
            }}
          >
            <Layer listening={hasSelectedOffCanvasTopLayerImage}>
              {/* Demoted top-layer images: Only render when NO selection exists
                   When there IS selection, they render in Layer 5 of main stage instead */}
              {!selectedObject &&
                !(selectedObjects && selectedObjects.length > 0) &&
                offCanvasSelectionOcclusionImages.map((image) => {
                  const elementOpacity = getElementOpacity(image.id, "Image");
                  return renderImageElement({
                    imageObj: image,
                    isOffCanvas: true,
                    sourceStageRef: offCanvasTopLayerStageRef.current,
                    opacity: elementOpacity,
                    key: image.id, // Add a unique key to each image element
                  });
                })}
              {[...offCanvasImagesTop]
                .sort((a, b) => {
                  const aSelected = selectedTopPriorityImageIds.has(a.id);
                  const bSelected = selectedTopPriorityImageIds.has(b.id);
                  if (aSelected !== bSelected) return aSelected ? 1 : -1;
                  return 0;
                })
                .map((image) => {
                  const isSelectedTopLayerImageInActiveGroupDrag =
                    isGroupDraggingRef.current &&
                    (activeGroupDragElementIds.has(image.id) ||
                      (selectedObjects || []).some(
                        (obj) => obj?.type === "Image" && obj?.id === image.id,
                      ));
                  const hasLiveOffCanvasTopLayerPreview =
                    activeOffCanvasTopLayerPreviewSourceIds.has(image.id);
                  
                  if (isSelectedTopLayerImageInActiveGroupDrag) {
                    return null;
                  }
                  if (hasLiveOffCanvasTopLayerPreview) {
                    return null;
                  }
                  const elementOpacity = getElementOpacity(image.id, "Image");
                  return renderImageElement({
                    imageObj: image,
                    isOffCanvas: true,
                    sourceStageRef: offCanvasTopLayerStageRef.current,
                    opacity: elementOpacity,
                  });
                })}
              {/* Group drag previews for off-canvas top-layer images */}
              {groupDragPreviews
                .filter(
                  (preview) =>
                    preview.target === "offCanvas" &&
                    preview.type === "Image" &&
                    preview.data?.isOnTopLayer === true,
                )
                .map((preview, index) => {
                 
                  return (
                    <React.Fragment
                      key={`group-preview-offcanvas-toplayer-${
                        preview.data?.id || index
                      }`}
                    >
                      {renderImageElement({
                        imageObj: preview.data,
                        isOffCanvas: true,
                        sourceStageRef: offCanvasTopLayerStageRef.current,
                        isPreview: true,
                      })}
                    </React.Fragment>
                  );
                })}
              {/* Render single-item off-canvas drag preview above top-layer images.
                  Regular off-canvas preview is painted on offCanvasStage (zIndex 1), while
                  top-layer images are painted on this stage wrapper (zIndex 2). Mirroring the
                  preview here keeps the target-stage ghost visible above top-layer content. */}
              {(() => {
                if (
                  !dragPreview ||
                  dragPreview.target !== "offCanvas" ||
                  !dragPreview.data
                ) {
                  return null;
                }

                const previewProps = {
                  isOffCanvas: true,
                  sourceStageRef: offCanvasTopLayerStageRef.current,
                  isPreview: true,
                };

                switch (dragPreview.type) {
                  case "Image":
                    return renderImageElement({
                      imageObj: dragPreview.data,
                      ...previewProps,
                    });
                  case "Rect":
                    return renderRectangleElement({
                      rectangle: dragPreview.data,
                      ...previewProps,
                    });
                  case "Circle":
                    return renderCircleElement({
                      circle: dragPreview.data,
                      ...previewProps,
                    });
                  case "Text":
                    return renderTextElement({
                      text: dragPreview.data,
                      ...previewProps,
                    });
                  case "Line":
                    return renderLineElement({
                      line: dragPreview.data,
                      ...previewProps,
                    });
                  case "Arrow":
                    return renderArrowElement({
                      arrow: dragPreview.data,
                      ...previewProps,
                    });
                  case "DoubleSidedArrow":
                    return renderDoubleSidedArrowElement({
                      arrow: dragPreview.data,
                      ...previewProps,
                    });
                  default:
                    return null;
                }
              })()}
              {/* Keep drag-preview transformer above off-canvas top-layer images as well. */}
              {(() => {
                if (
                  !dragPreview ||
                  dragPreview.target !== "offCanvas" ||
                  !offCanvasTopLayerStageRef?.current ||
                  !dragPreview.data?.id
                ) {
                  return null;
                }

                const supportsTransformer =
                  dragPreview.type === "Image" ||
                  dragPreview.type === "Rect" ||
                  dragPreview.type === "Circle";
                if (!supportsTransformer) return null;

                const previewNode = offCanvasTopLayerStageRef.current.findOne(
                  `#${dragPreview.data.id}`,
                );
                if (!previewNode) return null;

                const anchors = dragPreview.data.maintainAspectRatio
                  ? ["bottom-right"]
                  : ["middle-right", "bottom-center", "bottom-right"];

                return (
                  <Transformer
                    key={`toplayer-offcanvas-preview-transformer-${dragPreview.data.id}`}
                    nodes={[previewNode]}
                    rotateEnabled={!isDuplicatePlacementActive}
                    flipEnabled={false}
                    enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                    keepRatio={dragPreview.data.maintainAspectRatio || false}
                    centeredScaling={false}
                    visible={true}
                    listening={false}
                  />
                );
              })()}
            </Layer>
          </Stage>
        </div>
      )}

      {/* Overlay for off-canvas selection box - positioned to match off-canvas stage boundaries */}
      {isSelecting && selectionBox && selectionBoxStage === "off-canvas" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: offCanvasStageLeft,
            transform: `translate(${stagePos.x}px, ${stagePos.y}px)`,
            margin: 0,
            padding: 0,
            pointerEvents: "none", // Don't interfere with mouse events
            zIndex: 1, // CRITICAL FIX: Lower z-index than main canvas stage (zIndex: 2) when selection starts in off-canvas
            visibility: "visible",
            opacity: 1,
            width: `${offCanvasStageWidth}px`, // Match off-canvas stage width
            height: `${offCanvasStageHeightPx}px`, // Match off-canvas stage height
            overflow: "hidden", // CRITICAL FIX: Clip selection box to off-canvas stage boundaries
          }}
        >
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              overflow: "hidden", // CRITICAL FIX: Clip SVG content to off-canvas stage boundaries
            }}
          >
            <rect
              x={Math.min(selectionBox.startX, selectionBox.endX)}
              y={Math.min(selectionBox.startY, selectionBox.endY)}
              width={Math.abs(selectionBox.endX - selectionBox.startX)}
              height={Math.abs(selectionBox.endY - selectionBox.startY)}
              fill="rgba(59, 130, 246, 0.3)"
              stroke="#3b82f6"
              strokeWidth="3"
            />
          </svg>
        </div>
      )}

      {/* Selection box overlay for main canvas - positioned outside stage container to avoid clipping */}
      {isSelecting && selectionBox && selectionBoxStage === "main" && (
        <div
          style={{
            position: "absolute",
            left: mainStageAbsoluteLeft,
            top: "0",
            transform: `translate(${stagePos.x}px, ${stagePos.y}px)`,
            margin: 0,
            padding: 0,
            pointerEvents: "none", // Don't interfere with mouse events
            zIndex: 30, // CRITICAL FIX: Higher than top layer stage (z-index 26) to appear above top-layer images
            visibility: "visible",
            opacity: 1,
            width: `${mainCanvasWidth}px`, // Match stage width
            height: `${fixedStageHeightPx}px`, // Match stage height
            overflow: "hidden", // CRITICAL FIX: Clip selection box to main canvas boundaries
          }}
          ref={(el) => {
            // DEBUG: Log selection box rendering
            if (el) {
              const rect = el.getBoundingClientRect();
            }
          }}
        >
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              overflow: "hidden", // CRITICAL FIX: Clip SVG content to main canvas boundaries
            }}
          >
            <rect
              x={Math.min(selectionBox.startX, selectionBox.endX)}
              y={Math.min(selectionBox.startY, selectionBox.endY)}
              width={Math.abs(selectionBox.endX - selectionBox.startX)}
              height={Math.abs(selectionBox.endY - selectionBox.startY)}
              fill="rgba(59, 130, 246, 0.3)"
              stroke="#3b82f6"
              strokeWidth="3"
              data-canvas="main"
              ref={(rect) => {
                // DEBUG: Log main canvas SVG rect coordinates
                if (rect) {
                }
              }}
            />
          </svg>
        </div>
      )}

      {/* Main Canvas Stage (Centered) */}
      <div
        ref={(node) => {
          mainStageContainerRef.current = node;
          if (mainStageContainerRefFromParent) {
            mainStageContainerRefFromParent.current = node;
          }
        }}
        id="main-canvas-stage-container"
        style={{
          position: "absolute",
          left: mainStageAbsoluteLeft,
          top: "0",
          transform: `translate(${stagePos.x}px, ${stagePos.y}px)`,
          // CRITICAL FIX: Increase z-index when objects are selected to ensure they appear above top-layer images
          // Top layer stage has z-index 26 (or 24 during drag/selection), so selected objects need z-index 27
          zIndex: (() => {
            const hasSelection =
              selectedObject || (selectedObjects && selectedObjects.length > 0);
            const hasMainGroupDragPreviews = (groupDragPreviews || []).some(
              (preview) => preview.target === "main",
            );
            const hasOffCanvasGroupDragPreviews = (
              groupDragPreviews || []
            ).some((preview) => preview.target === "offCanvas");
            // During main-target group-drag preview, keep main stage below top-layer images.
            // During off-canvas-target group-drag preview, lower main stage so off-canvas
            // top-layer images (z-index 2) stay above cross-canvas previews.
            const zIndex = hasOffCanvasGroupDragPreviews
              ? 1
              : hasMainGroupDragPreviews
                ? 25
                : hasSelection
                  ? 27
                  : 2;

            return zIndex;
          })(),
          // Disable pointer events when selecting in off-canvas to allow selection box to work;
          // when only off-canvas elements are selected, keep main from receiving hits (cross-stage drag/transform).
          pointerEvents:
            isSelecting && selectionBoxStage === "off-canvas"
              ? "none"
              : selectionPointerIsolationSide === "off"
                ? "none"
                : "auto",
        }}
        data-has-top-layer-images={mainCanvasImagesTop.length > 0}
      >
        {/* HTML Content Blockers - Render BEFORE Stage so they appear behind Konva elements */}
        {(() => {
          // CRITICAL FIX: Hide blockers during group drag to prevent white rectangles in preview
          const isGroupDragging =
            isGroupDraggingRef.current &&
            selectedObjects &&
            selectedObjects.length > 1;
          if (isGroupDragging) return null; // Don't show blockers during group drag

          // Only show blockers when there are selected elements
          const hasSelectedObject =
            selectedObject !== null &&
            selectedObject !== undefined &&
            selectedObject !== false;
          const hasSelectedObjects =
            Array.isArray(selectedObjects) && selectedObjects.length > 0;
          const shouldShowBlockers = hasSelectedObject || hasSelectedObjects;

          if (!shouldShowBlockers) return null;

          // Get all selected elements from main canvas
          const selectedElements = [];

          if (selectedObjects && selectedObjects.length > 0) {
            selectedObjects.forEach((obj) => {
              const effectiveObj =
                selectedObject && selectedObject.id === obj.id
                  ? selectedObject
                  : obj;

              // CRITICAL: Skip if element is being dragged to off-canvas - don't show blocker
              const isBeingDraggedToOffCanvas =
                dragPreview &&
                dragPreview.target === "offCanvas" &&
                dragPreview.data &&
                dragPreview.data.id &&
                effectiveObj.id === dragPreview.data.id.replace("-ghost", "");

              if (isBeingDraggedToOffCanvas) {
                return;
              }

              const { isInMainCanvas } = findElementCanvas({
                selectedObject: effectiveObj,
                rectangles,
                circles,
                images,
                texts,
                arrows,
                lines,
                doubleSidedArrows,
                offCanvasRectangles,
                offCanvasCircles,
                offCanvasImages,
                offCanvasTexts,
                offCanvasArrows,
                offCanvasLines,
                offCanvasDoubleSidedArrows,
              });

              if (isInMainCanvas) {
                selectedElements.push(effectiveObj);
              }
            });
          } else if (selectedObject) {
            // CRITICAL: Skip if element is being dragged to off-canvas - don't show blocker
            const isBeingDraggedToOffCanvas =
              dragPreview &&
              dragPreview.target === "offCanvas" &&
              dragPreview.data &&
              dragPreview.data.id &&
              selectedObject.id === dragPreview.data.id.replace("-ghost", "");

            if (isBeingDraggedToOffCanvas) {
            } else {
              const { isInMainCanvas } = findElementCanvas({
                selectedObject,
                rectangles,
                circles,
                images,
                texts,
                arrows,
                lines,
                doubleSidedArrows,
                offCanvasRectangles,
                offCanvasCircles,
                offCanvasImages,
                offCanvasTexts,
                offCanvasArrows,
                offCanvasLines,
                offCanvasDoubleSidedArrows,
              });

              if (isInMainCanvas) {
                selectedElements.push(selectedObject);
              }
            }
          }

          if (selectedElements.length === 0) return null;

          // Get stage container and stage for coordinate conversion
          const stageContainer = mainStageContainerRef.current;
          const stage = stageRef?.current;
          if (!stageContainer || !stage) return null;

          const stagePadding = parseInt(canvasHeight) < 30 ? 10 : 20;

          return selectedElements.map((element) => {
            // Skip if element is in off-canvas arrays - never show blockers for off-canvas elements
            const isInOffCanvas =
              offCanvasRectangles.some(
                (r) => r.id === element.id && element.type === "Rect",
              ) ||
              offCanvasCircles.some(
                (c) => c.id === element.id && element.type === "Circle",
              ) ||
              offCanvasImages.some(
                (i) => i.id === element.id && element.type === "Image",
              ) ||
              offCanvasTexts.some(
                (t) => t.id === element.id && element.type === "Text",
              ) ||
              offCanvasArrows.some(
                (a) => a.id === element.id && element.type === "Arrow",
              ) ||
              offCanvasLines.some(
                (l) =>
                  l.id === element.id &&
                  (element.type === "Line" ||
                    element.type === "Arrow" ||
                    element.type === "DoubleSidedArrow"),
              ) ||
              offCanvasDoubleSidedArrows.some(
                (a) =>
                  a.id === element.id && element.type === "DoubleSidedArrow",
              );

            // CRITICAL: Also skip if drag preview target is off-canvas - don't show blocker for elements being dragged to off-canvas
            const isBeingDraggedToOffCanvas =
              dragPreview &&
              dragPreview.target === "offCanvas" &&
              dragPreview.data &&
              dragPreview.data.id &&
              element.id === dragPreview.data.id.replace("-ghost", "");

            if (isInOffCanvas || isBeingDraggedToOffCanvas) {
              if (isBeingDraggedToOffCanvas) {
              }
              return null;
            }

            // Get actual element from state arrays to ensure we have latest properties
            let actualElement = element;
            switch (element.type) {
              case "Rect":
                actualElement =
                  rectangles.find((r) => r.id === element.id) || element;
                break;
              case "Circle":
                actualElement =
                  circles.find((c) => c.id === element.id) || element;
                break;
              case "Image":
                actualElement =
                  images.find((img) => img.id === element.id) || element;
                // Skip top-layer images - they're handled separately
                if (actualElement.isOnTopLayer) return null;
                break;
              case "Text":
                actualElement =
                  texts.find((t) => t.id === element.id) || element;
                break;
              case "Arrow":
              case "Line":
              case "DoubleSidedArrow":
                // For unified elements, check lines array first
                actualElement =
                  lines.find((l) => l.id === element.id) ||
                  arrows.find((a) => a.id === element.id) ||
                  doubleSidedArrows.find((a) => a.id === element.id) ||
                  element;
                break;
              default:
                break;
            }

            // Calculate bounding box in Konva coordinates - this gives us the axis-aligned bounding box
            // which accounts for rotation and gives us the exact area the element occupies
            const bbox = getElementBoundingBox(
              actualElement,
              actualElement.type || element.type,
            );

            // Convert Konva coordinates to absolute positioning relative to stage container
            // The Html component from react-konva-utils positions content absolutely
            // The Html component uses x/y props which are Konva coordinates
            // It positions the div at: stage container position + Konva x/y + padding
            // We need to match that exactly - use bounding box position + padding
            const stageX = bbox.x + stagePadding;
            const stageY = bbox.y + stagePadding;

            // Debug: Log positioning to help diagnose alignment issues

            // Always use white background for blocker to cover HTML content behind it
            const blockerElement = (
              <div
                key={`html-blocker-${element.id}-${element.type}`}
                style={{
                  position: "absolute",
                  left: `${stageX}px`,
                  top: `${stageY}px`,
                  width: `${Math.max(0, bbox.width)}px`,
                  height: `${Math.max(0, bbox.height)}px`,
                  // Use solid white background to cover HTML content behind it
                  backgroundColor: "white",
                  pointerEvents: "none",
                  zIndex: 20, // Higher than all HTML content (1, 3, 5, 10, 15) to ensure it blocks everything behind it
                  // Ensure it creates a stacking context to block HTML content behind it
                  isolation: "isolate",
                  // Use transform to create a new stacking context
                  transform: "translateZ(0)",
                  // Ensure it's visible and blocks content
                  visibility: "visible",
                  opacity: 1,
                  // Match canvas styling
                  border: "none",
                  outline: "none",
                  // Ensure it's not transparent
                  background: "white",
                  backgroundImage: "none",
                }}
                data-html-blocker="true"
                data-blocker-for={`${element.id}-${element.type}`}
                ref={(el) => {
                  if (el) {
                    setTimeout(() => {
                      const blockerStyle = window.getComputedStyle(el);

                      // Check Stage z-index
                      const stage = stageRef?.current;
                      if (stage) {
                        const stageContainer = stage.container();
                        const stageComputed =
                          window.getComputedStyle(stageContainer);
                        const canvas = stageContainer.querySelector("canvas");
                        const canvasComputed = canvas
                          ? window.getComputedStyle(canvas)
                          : null;

                        // Check for HTML content divs and their z-index
                        const stageContainerParent =
                          stageContainer.parentElement;
                        if (stageContainerParent) {
                          const htmlContentDivs =
                            stageContainerParent.querySelectorAll(
                              'div[style*="position: absolute"]:not([data-html-blocker="true"])',
                            );
                          htmlContentDivs.forEach((htmlDiv, idx) => {
                            const htmlStyle = window.getComputedStyle(htmlDiv);
                            const htmlZIndex = htmlStyle.zIndex;
                            const htmlRect = htmlDiv.getBoundingClientRect();
                            const blockerRect = el.getBoundingClientRect();

                            // Check if HTML content overlaps with blocker
                            const overlaps = !(
                              blockerRect.right < htmlRect.left ||
                              blockerRect.left > htmlRect.right ||
                              blockerRect.bottom < htmlRect.top ||
                              blockerRect.top > htmlRect.bottom
                            );

                            if (overlaps || parseInt(htmlZIndex || "0") >= 10) {
                            }
                          });
                        }
                      }
                    }, 100);
                  }
                }}
              />
            );

            return blockerElement;
          });
        })()}
        <Stage
          ref={(node) => {
            // Set stageRef first - node is the React-Konva Stage component wrapper
            if (stageRef) {
              if (typeof stageRef === "function") {
                stageRef(node);
              } else if (stageRef && "current" in stageRef) {
                stageRef.current = node;
              }
            }

            // Add data attribute and force z-index on Stage container for CSS targeting
            // Use setTimeout to ensure Stage is fully mounted
            if (node) {
              // Try to get Stage immediately, but also use setTimeout as fallback
              const trySetZIndex = () => {
                const stage = node?.getStage?.();
                if (stage) {
                  const container = stage.container();
                  if (container) {
                    container.setAttribute("data-stage-z-index", "25");
                    container.setAttribute("data-stage-container", "true");
                    // Force z-index via inline style with !important equivalent - must be above blocker (20)
                    container.style.setProperty("z-index", "25", "important");
                  }
                }
              };

              // Try immediately
              trySetZIndex();

              // Also try after a short delay in case Stage isn't ready yet
              setTimeout(trySetZIndex, 0);
            }
          }}
          width={mainCanvasWidth}
          height={fixedStageHeightPx}
          pixelRatio={CANVAS_PIXEL_RATIO}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onMouseUp={handleStageMouseUp}
          onPointerUp={handleStageMouseUp}
          onMouseLeave={handleStageMouseLeave}
          onClick={(e) => {
            // When crop modal is open (state or ref), don't run any selection logic. Ref fixes first-time close.
            if (isCropModalOpen || isCropModalOpenRef?.current) {
              e.evt?.preventDefault?.();
              e.evt?.stopPropagation?.();
              return;
            }

            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }

            const targetId = e?.target?.attrs?.id;
            const isStageClick = e?.target === e?.target.getStage();
            const isBackgroundRectClick = targetId === "bg";
            const isBackgroundOrStageClick =
              isStageClick || isBackgroundRectClick;
            const hasGroupSelection = (selectedObjects?.length || 0) > 1;
            // CRITICAL FIX: Always clear the mousedown ref on stage click to prevent two-click issue
            // The ref is only meant to prevent accidental deselection during drag initiation
            // Once we reach onClick (without dragging), it should be cleared
            const recentMousedown = recentDraggableElementMousedownRef.current;
            const clickedOnStage = e?.target === e?.target.getStage();

            // Clear the mousedown ref immediately on any stage click
            if (recentMousedown.nodeId) {
              recentDraggableElementMousedownRef.current = {
                nodeId: null,
                timestamp: 0,
              };
            }

            // If clone mode is active (lockedAction is set), don't interfere with clone creation
            // Return early - handleStageMouseDown already handled the clone creation
            // This prevents the editor from closing when placing clones
            if (isCloneMode || lockedAction) {
              e.evt?.preventDefault?.();
              e.evt?.stopPropagation?.();
              return;
            }

            // Don't clear while user is still drawing the selection box (edge case)
            if (isSelecting) {
              return;
            }
            // Always allow clear on first click after selection box (same as Ctrl+A: one click to deselect)

            // Clear when clicking on Stage (empty) OR on Background rect (id="bg") so one click deselects after selection box
            // (targetId, isStageClick, isBackgroundRectClick, isBackgroundOrStageClick already computed above)
            if (isBackgroundOrStageClick) {
              // Top-layer main-canvas images can route clicks through Stage/Background.
              // Before treating as background clear, hit-test the top layer and select image if present.
              if (e?.evt && typeof getTopLayerImageAtClick === "function") {
                const hitTopLayerImage = getTopLayerImageAtClick(
                  e.evt.clientX,
                  e.evt.clientY,
                );
                if (hitTopLayerImage && typeof onTopLayerImageClicked === "function") {
                  onTopLayerImageClicked(hitTopLayerImage);
                  e.cancelBubble = true;
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  return;
                }
              }
              // Don't clear selection when this click is the one that fires right after transform end or drag end
              // (Konva can deliver a click with target=stage after resizing/rotating or after dragging, which would wrongly deselect)
              const transformStartedRecently =
                lastTransformStartTimeRef.current &&
                Date.now() - lastTransformStartTimeRef.current < 300;
              if (
                justFinishedTransformRef.current ||
                justFinishedDragRef.current ||
                justFinishedShapeDrawRef?.current ||
                transformStartedRecently
              ) {
                justFinishedTransformRef.current = false;
                if (justFinishedDragRef) justFinishedDragRef.current = false;
                if (justFinishedShapeDrawRef) justFinishedShapeDrawRef.current = false;
                return;
              }
              transformerRef?.current?.nodes([]);
              // Don't clear selectedObject if clone mode is active (keeps editor open)
              // Also check lockedAction since clone mode sets it
              // Also check for clone templates as an additional indicator of clone mode
              // Don't clear when crop modal is open (releasing crop rectangle must not close modal)
              const hasCloneTemplates =
                cloneTemplates && cloneTemplates.length > 0;
              const hasCloneTemplate = cloneTemplate !== null;
              const isInCloneMode =
                isCloneMode ||
                lockedAction ||
                hasCloneTemplates ||
                hasCloneTemplate;
              const lastDragged = lastDraggedImageRef?.current;
              const isImageJustDragged =
                lastDragged?.id != null &&
                lastDragged?.time != null &&
                selectedObject?.type === "Image" &&
                selectedObject?.id === lastDragged.id &&
                Date.now() - lastDragged.time < 500;

              // CRITICAL: Use pre-mouse-down captured selection count instead of current hasGroupSelection
              // Because Stage.onMouseDown already cleared the selection, so hasGroupSelection is false now
              const originalHasGroupSelection = effectivePreMouseDownSelectionCountRef.current > 1;
              const shouldClear =
                !isInCloneMode &&
                !isCropModalOpen &&
                !isCropModalOpenRef?.current &&
                !isImageJustDragged &&
                !originalHasGroupSelection;

              console.log("🔴 [STAGE-ONCLICK] About to clear selection", {
                  shouldClear,
                  hasGroupSelection,
                  selectedObjectsLength: selectedObjects?.length || 0,
                  isInCloneMode,
                  isCropModalOpen,
                  isImageJustDragged,
                  targetId,
                  isStageClick,
                  isBackgroundRectClick,
                });

              if (shouldClear) {
                console.log("🔴 [STAGE-ONCLICK] Clearing selection in Stage onClick");
                // CRITICAL FIX: Reset selection box state when clearing selections
                // This ensures the next selection box can be drawn properly
                setIsSelecting(false);
                setSelectionBox(null);
                setSelectionBoxStage(null);
                setSelectAllButtonClicked(false); // Clear Select All button flag
                setSelectedObject(null);
                setSelectedObjects([]);
                setSelectedText(null);
                setSelectedImage(null);
                setSelectedArrow(null);
                setSelectedLine(null);
                setSelectedDoubleSidedArrow(null);
                setShowTextBox(false);
                setSelectedTextId(null);
                // CRITICAL: Also disable multi-select mode when clearing selections
                if (setIsMultiSelectMode) {
                  setIsMultiSelectMode(false);
                }
              } else {
                if (hasGroupSelection) {
                }
              }

              // Still call the main onClick handler for additional logic
              // BUT don't call it if clone mode is active (prevents editor from closing)
              if (onClick && !isCloneMode && !lockedAction) {
                onClick(e);
              } else {
              }
            } else {
            }
          }}
          style={{
            margin: 0, // CRITICAL: Remove any default margins to ensure alignment
            padding: 0, // CRITICAL: Remove any default padding (padding is handled by Konva stage padding prop)
            position: "relative", // CRITICAL: Ensure proper positioning context
            touchAction: "none",
            background: parseInt(canvasHeight) <= 1 ? "transparent" : "white",
            border: parseInt(canvasHeight) <= 1 ? "none" : "1px solid #DEE2E6",
            boxShadow:
              parseInt(canvasHeight) <= 1 ? "none" : "0 0 10px rgba(0,0,0,0.1)",
            cursor: isWaitingForPaste
              ? "crosshair"
              : isPanning || spacePressed || action === ACTIONS.PAN
                ? isPanning
                  ? "grabbing"
                  : "grab"
                : isDuplicatePlacementActive
                  ? "crosshair"
                  : getCursorStyle(action),
            imageRendering: "auto",
            position: "relative",
            zIndex: 12, // Above blockers (z-index 11) to ensure selected elements render on top, but below selected HTML (z-index 15)
            // CRITICAL: This z-index must be applied via inline style to override any CSS
          }}
          // REMOVED: padding prop - Konva.js doesn't support native padding, and it was causing coordinate system mismatches
          // Both stages now use the same coordinate system (0,0 at top-left of stage)
        >
          {/* Layer 1: Background and Images (lowest layer) */}
          <Layer>
            {parseInt(canvasHeight) >= 5 && (
              <Rect
                x={parseInt(canvasHeight) < 30 ? -50 : -100}
                y={parseInt(canvasHeight) < 30 ? -50 : -100}
                width={
                  mainCanvasWidth +
                  (parseInt(canvasHeight) < 30 ? 100 : 200)
                }
                height={
                  fixedStageHeightPx +
                  (parseInt(canvasHeight) < 30 ? 100 : 200)
                }
                fill="transparent"
                listening={false}
              />
            )}

            {parseInt(canvasHeight) >= 5 && (
              <Background
                setSelectedTextId={setSelectedTextId}
                transformerRef={transformerRef}
                setSelectedArrow={setSelectedArrow}
                setSelectedLine={setSelectedLine}
                setSelectedDoubleSidedArrow={setSelectedDoubleSidedArrow}
                setSelectedText={setSelectedText}
                setSelectedObject={setSelectedObject}
                selectedObjects={selectedObjects}
                selectedObjectsRef={selectedObjectsRef}
                setShowTextBox={setShowTextBox}
                setSelectedImage={setSelectedImage}
                setAction={setAction}
                ACTIONS={ACTIONS}
                action={action}
                isDrawingArrow={isDrawingArrow}
                isDrawingDoubleArrow={isDrawingDoubleArrow}
                isDrawingLine={isDrawingLine}
                setSelectedObjects={setSelectedObjects}
                justFinishedSelectionRef={activeJustFinishedSelectionRef}
                pendingSelectionRef={pendingSelection}
                pendingChangesRef={pendingChangesRef}
                setTexts={setTexts}
                lockedAction={lockedAction}
                isCloneMode={isCloneMode}
                isMultiSelectMode={isMultiSelectMode}
                isMultiSelectModeRef={isMultiSelectModeRef}
                getTopLayerImageAtClick={getTopLayerImageAtClick}
                onTopLayerImageClicked={onTopLayerImageClicked}
                onTopLayerImageClickedInMultiSelect={
                  onTopLayerImageClickedInMultiSelect
                }
                endElementEditing={endElementEditing}
                isElementBeingEdited={isElementBeingEdited}
                cloneElementsAtPosition={cloneElementsAtPosition}
                cloneTemplates={cloneTemplates}
                cloneTemplate={cloneTemplate}
                stageRef={stageRef}
                setIsMultiSelectMode={setIsMultiSelectMode}
                recentDraggableElementMousedownRef={
                  recentDraggableElementMousedownRef
                }
                setIsSelecting={setIsSelecting}
                setSelectionBox={setSelectionBox}
                setSelectionBoxStage={setSelectionBoxStage}
                isCropModalOpen={isCropModalOpen}
                isCropModalOpenRef={isCropModalOpenRef}
                justFinishedTransformRef={justFinishedTransformRef}
                lastTransformStartTimeRef={lastTransformStartTimeRef}
                justFinishedDragRef={justFinishedDragRef}
                justFinishedShapeDrawRef={justFinishedShapeDrawRef}
                lastDraggedImageRef={lastDraggedImageRef}
              />
            )}

            {/* Resize icon visual cue */}

            {/* Render images */}
            {(() => {
              // Sort images so selected ones render last (on top)
              const sortedImages = [...mainCanvasImagesBottom].sort((a, b) => {
                const aIsSelected =
                  selectedObject?.id === a.id &&
                  selectedObject?.type === "Image";
                const bIsSelected =
                  selectedObject?.id === b.id &&
                  selectedObject?.type === "Image";
                // Selected images come last (render on top)
                if (aIsSelected !== bIsSelected) {
                  return aIsSelected ? 1 : -1;
                }
                return 0; // Maintain original order for non-selected
              });

              return sortedImages.map((imageObj) => {
                // CRITICAL FIX: Don't render top-layer images on main stage
                // Top-layer images are rendered on the separate top layer stage
                // Rendering them on both stages causes click detection issues
                if (imageObj.isOnTopLayer) {
                  return null;
                }

                // CRITICAL FIX: Don't render images in Layer 1 if they're selected
                // Selected images are rendered in Layer 5 (selected layer) with full opacity
                // Rendering them in both layers creates a "copy" effect during drag
                const isSelected =
                  (selectedObject?.id === imageObj.id &&
                    selectedObject?.type === "Image") ||
                  selectedObjects?.some(
                    (obj) => obj.id === imageObj.id && obj.type === "Image",
                  );

                // Hide from Layer 1 if selected - it will be rendered in Layer 5
                if (isSelected) {
                  return null;
                }

                // CRITICAL FIX: During group drag WITHIN main canvas, keep images visible
                // Only hide them if they're being dragged to a DIFFERENT canvas (off-canvas)
                const isInGroupDrag =
                  isGroupDraggingRef.current &&
                  selectedObjects?.some(
                    (obj) => obj.id === imageObj.id && obj.type === "Image",
                  );

                // Check if being dragged to off-canvas (cross-canvas drag)
                const isBeingDraggedToOffCanvas =
                  isInGroupDrag &&
                  groupDragPreviews.some(
                    (preview) =>
                      preview.target === "offCanvas" &&
                      preview.type === "Image" &&
                      preview.data?.id?.replace("-ghost", "") === imageObj.id,
                  );

                // ONLY hide if being dragged to off-canvas
                // If dragging within main canvas, keep visible so handleGroupDragMove can update position
                if (isBeingDraggedToOffCanvas) {
                  return null;
                }

                // CRITICAL: During group drag within main canvas, render with full opacity
                // The handleGroupDragMove will update the Konva node position directly
                const elementOpacity = getElementOpacity(imageObj.id, "Image");
                return renderImageElement({
                  imageObj,
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  opacity: elementOpacity,
                });
              });
            })()}
            {dragPreview &&
              dragPreview.target === "main" &&
              dragPreview.type === "Image" &&
              !dragPreview.data?.isOnTopLayer &&
              renderImageElement({
                imageObj: dragPreview.data,
                isOffCanvas: false,
                sourceStageRef: stageRef.current,
                isPreview: true,
              })}

            {/* Transformer for image drag preview on main canvas */}
            {(() => {
              if (
                !dragPreview ||
                dragPreview.target !== "main" ||
                dragPreview.type !== "Image" ||
                dragPreview.data?.isOnTopLayer ||
                !stageRef?.current ||
                !dragPreview.data?.id
              ) {
                return null;
              }

              const ghostId = dragPreview.data.id;
              const previewNode = stageRef.current.findOne(`#${ghostId}`);

              if (!previewNode) {
                return null;
              }

              const anchors = dragPreview.data.maintainAspectRatio
                ? ["bottom-right"]
                : ["middle-right", "bottom-center", "bottom-right"];

              return (
                <Transformer
                  key={`image-preview-transformer-main-${ghostId}`}
                  nodes={[previewNode]}
                  rotateEnabled={!isDuplicatePlacementActive}
                  flipEnabled={false}
                  enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                  keepRatio={dragPreview.data.maintainAspectRatio || false}
                  centeredScaling={false}
                  visible={true}
                  listening={false}
                />
              );
            })()}
          </Layer>

          {/* Layer 2: Shapes (Rectangle, Circle, Arrow, Line) - above images */}
          {parseInt(canvasHeight) >= 5 && (
            <Layer>
              {(() => {
                // Combine all shapes with their type, render function, and original index
                // to maintain consistent z-order based on creation order
                // Filter out selected shapes - they'll be rendered in the top layer
                // When multi-select is enabled, filter out all elements in selectedObjects array
                // CRITICAL FIX: Also filter out elements being dragged during group drag
                const isElementInSelectedObjects = (elementId, elementType) => {
                  if (!selectedObjects || selectedObjects.length === 0)
                    return false;
                  return selectedObjects.some(
                    (obj) => obj.id === elementId && obj.type === elementType,
                  );
                };

                // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
                const isGroupDragging =
                  isGroupDraggingRef.current &&
                  selectedObjects &&
                  selectedObjects.length > 1;
                const hasMainCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "main",
                );
                const hasOffCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "offCanvas",
                );
                // CRITICAL FIX: Filter out dragged elements when there are ANY cross-canvas previews
                // This includes both dragging TO main canvas (hasMainCanvasPreviews) and FROM main canvas (hasOffCanvasPreviews)
                const shouldFilterDraggedElements =
                  isGroupDragging &&
                  (hasMainCanvasPreviews || hasOffCanvasPreviews);

                const draggedElementIds = shouldFilterDraggedElements
                  ? new Set(selectedObjects.map((obj) => obj.id))
                  : new Set();

                const allShapes = [
                  ...rectangles
                    .filter((r) => {
                      // Filter out if selected
                      if (
                        selectedObject?.id === r.id &&
                        selectedObject?.type === "Rect"
                      )
                        return false;
                      // Filter out if in selectedObjects
                      if (isElementInSelectedObjects(r.id, "Rect"))
                        return false;
                      // CRITICAL: Filter out if being dragged during cross-canvas group drag
                      if (
                        shouldFilterDraggedElements &&
                        draggedElementIds.has(r.id)
                      )
                        return false;
                      // CRITICAL FIX: Also check if this element has a group drag preview (ghost)
                      const hasGroupPreview = groupDragPreviews.some((p) => {
                        const previewId =
                          p.data?.id?.replace("-ghost", "") || p.data?.id;
                        return previewId === r.id;
                      });
                      if (hasGroupPreview) return false;
                      return true;
                    })
                    .map((r, idx) => ({
                      ...r,
                      shapeType: "Rect",
                      renderFn: renderRectangleElement,
                      originalIndex: idx,
                      typeOrder: 0, // Rectangles come first
                    })),
                  ...circles
                    .filter((c) => {
                      // Filter out if selected
                      if (
                        selectedObject?.id === c.id &&
                        selectedObject?.type === "Circle"
                      )
                        return false;
                      // Filter out if in selectedObjects
                      if (isElementInSelectedObjects(c.id, "Circle"))
                        return false;
                      // CRITICAL: Filter out if being dragged during cross-canvas group drag
                      if (
                        shouldFilterDraggedElements &&
                        draggedElementIds.has(c.id)
                      )
                        return false;
                      // CRITICAL FIX: Also check if this element has a group drag preview (ghost)
                      const hasGroupPreview = groupDragPreviews.some((p) => {
                        const previewId =
                          p.data?.id?.replace("-ghost", "") || p.data?.id;
                        return previewId === c.id;
                      });
                      if (hasGroupPreview) return false;
                      return true;
                    })
                    .map((c, idx) => ({
                      ...c,
                      shapeType: "Circle",
                      renderFn: renderCircleElement,
                      originalIndex: idx,
                      typeOrder: 1, // Circles come after rectangles
                    })),
                ];

                // Sort to maintain consistent rendering order:
                // 1. By type order (rectangles before circles)
                // 2. Then by original index within each type
                const sortedShapes = [...allShapes].sort((a, b) => {
                  if (a.typeOrder !== b.typeOrder) {
                    return a.typeOrder - b.typeOrder;
                  }
                  return a.originalIndex - b.originalIndex;
                });

                return sortedShapes.map((shape) => {
                  const {
                    shapeType,
                    renderFn,
                    originalIndex,
                    typeOrder,
                    ...shapeData
                  } = shape;
                  // Calculate opacity based on selection state (non-selected elements get reduced opacity)
                  const elementOpacity = getElementOpacity(
                    shapeData.id,
                    shapeData.shapeType,
                  );
                  // Render functions already set keys, so we just need to maintain order
                  return renderFn({
                    [shapeType === "Rect" ? "rectangle" : "circle"]: shapeData,
                    isOffCanvas: false,
                    sourceStageRef: stageRef.current,
                    opacity: elementOpacity,
                  });
                });
              })()}

              {/* Drag previews - render after sorted shapes */}
              {dragPreview &&
                dragPreview.target === "main" &&
                dragPreview.type === "Rect" &&
                renderRectangleElement({
                  // Force ghost visible on target canvas even if source render path hides original.
                  rectangle: { ...dragPreview.data, opacity: 1 },
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  isPreview: true,
                })}

              {/* Transformer for rectangle drag preview on main canvas */}
              {(() => {
                if (
                  !dragPreview ||
                  dragPreview.target !== "main" ||
                  dragPreview.type !== "Rect" ||
                  !stageRef?.current ||
                  !dragPreview.data?.id
                ) {
                  return null;
                }

                const ghostId = dragPreview.data.id;
                const previewNode = stageRef.current.findOne(`#${ghostId}`);

                if (!previewNode) {
                  return null;
                }

                const anchors = dragPreview.data.maintainAspectRatio
                  ? ["bottom-right"]
                  : ["middle-right", "bottom-center", "bottom-right"];

                return (
                  <Transformer
                    key={`rect-preview-transformer-main-${ghostId}`}
                    nodes={[previewNode]}
                    rotateEnabled={!isDuplicatePlacementActive}
                    flipEnabled={false}
                    enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                    keepRatio={dragPreview.data.maintainAspectRatio || false}
                    centeredScaling={false}
                    visible={true}
                    listening={false}
                  />
                );
              })()}

              {dragPreview &&
                dragPreview.target === "main" &&
                dragPreview.type === "Circle" &&
                renderCircleElement({
                  // Force ghost visible on target canvas even if source render path hides original.
                  circle: { ...dragPreview.data, opacity: 1 },
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  isPreview: true,
                })}

              {/* Transformer for circle drag preview on main canvas */}
              {(() => {
                if (
                  !dragPreview ||
                  dragPreview.target !== "main" ||
                  dragPreview.type !== "Circle" ||
                  !stageRef?.current ||
                  !dragPreview.data?.id
                ) {
                  return null;
                }

                // Find the preview circle node by searching for the -ghost ID
                const ghostId = dragPreview.data.id; // This already has -ghost suffix
                const previewNode = stageRef.current.findOne(`#${ghostId}`);

                if (!previewNode) {
                  // Node not found yet, will try again on next render
                  return null;
                }

                // Configure anchors based on maintainAspectRatio
                const anchors = dragPreview.data.maintainAspectRatio
                  ? ["bottom-right"]
                  : ["middle-right", "bottom-center", "bottom-right"];

                return (
                  <Transformer
                    key={`circle-preview-transformer-main-${ghostId}`}
                    nodes={[previewNode]}
                    rotateEnabled={!isDuplicatePlacementActive}
                    flipEnabled={false}
                    enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                    keepRatio={dragPreview.data.maintainAspectRatio || false}
                    centeredScaling={false}
                    visible={true}
                    listening={false}
                  />
                );
              })()}

              {/* Group drag previews - render all elements in the group */}
              {(() => {
                const mainPreviews = groupDragPreviews.filter(
                  (preview) =>
                    preview.target === "main" &&
                    !(
                      preview.type === "Text" &&
                      preview.data?.showBackground === true
                    ),
                );
                if (mainPreviews.length > 0) {
                }
                return mainPreviews;
              })().map((preview, index) => {
                const key = `group-preview-main-${preview.data?.id || index}`;
                switch (preview.type) {
                  case "Rect":
                    return (
                      <React.Fragment key={key}>
                        {renderRectangleElement({
                          rectangle: { ...preview.data, opacity: 1 },
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  case "Circle":
                    return (
                      <React.Fragment key={key}>
                        {renderCircleElement({
                          circle: { ...preview.data, opacity: 1 },
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  case "Image":
                    return !preview.data?.isOnTopLayer ? (
                      <React.Fragment key={key}>
                        {renderImageElement({
                          imageObj: preview.data,
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    ) : null; // Top-layer images are rendered separately
                  case "Text":
                    return (
                      <React.Fragment key={key}>
                        {renderTextElement({
                          text: preview.data,
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  case "Arrow":
                    return (
                      <React.Fragment key={key}>
                        {renderArrowElement({
                          arrow: preview.data,
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  case "DoubleSidedArrow":
                    return (
                      <React.Fragment key={key}>
                        {renderDoubleSidedArrowElement({
                          arrow: preview.data,
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  case "Line":
                    return (
                      <React.Fragment key={key}>
                        {renderLineElement({
                          line: preview.data,
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  default:
                    return null;
                }
              })}

              {/* Preview wrapper rectangle removed - selection wrapper already includes all elements including top-layer images */}

              {/* Arrows */}
              {(() => {
                // CRITICAL: Filter out selected arrows - they'll be rendered in the top layer
                // This prevents double-rendering and ensures selected elements appear on top
                const isElementInSelectedObjects = (elementId, elementType) => {
                  if (!selectedObjects || selectedObjects.length === 0)
                    return false;
                  return selectedObjects.some(
                    (obj) =>
                      obj.id === elementId &&
                      (obj.type === elementType ||
                        obj.type === "Arrow" ||
                        obj.type === "Line" ||
                        obj.type === "DoubleSidedArrow"),
                  );
                };

                // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
                const isGroupDragging =
                  isGroupDraggingRef.current &&
                  selectedObjects &&
                  selectedObjects.length > 1;
                const hasMainCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "main",
                );
                const hasOffCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "offCanvas",
                );
                // CRITICAL FIX: Filter out dragged elements when there are ANY cross-canvas previews
                // This includes both dragging TO main canvas (hasMainCanvasPreviews) and FROM main canvas (hasOffCanvasPreviews)
                const shouldFilterDraggedElements =
                  isGroupDragging &&
                  (hasMainCanvasPreviews || hasOffCanvasPreviews);

                const draggedElementIds = shouldFilterDraggedElements
                  ? new Set(selectedObjects.map((obj) => obj.id))
                  : new Set();

                const filteredArrows = arrows.filter((a) => {
                  const isSelected =
                    selectedObject?.id === a.id &&
                    (selectedObject?.type === "Arrow" ||
                      selectedObject?.type === "Line" ||
                      selectedObject?.type === "DoubleSidedArrow");
                  const isInSelectedObjects = isElementInSelectedObjects(
                    a.id,
                    "Arrow",
                  );
                  // CRITICAL: Filter out if being dragged during cross-canvas group drag
                  const isBeingDragged =
                    shouldFilterDraggedElements && draggedElementIds.has(a.id);

                  // CRITICAL FIX: Also check if this element has a group drag preview (ghost)
                  const hasGroupPreview = groupDragPreviews.some((p) => {
                    const previewId =
                      p.data?.id?.replace("-ghost", "") || p.data?.id;
                    return previewId === a.id;
                  });

                  if (
                    isSelected ||
                    isInSelectedObjects ||
                    isBeingDragged ||
                    hasGroupPreview
                  ) {
                  }

                  return (
                    !isSelected &&
                    !isInSelectedObjects &&
                    !isBeingDragged &&
                    !hasGroupPreview
                  );
                });

                // Sort arrows so selected ones render last (on top)
                const sortedArrows = [...filteredArrows].sort((a, b) => {
                  const aIsSelected =
                    selectedObject?.id === a.id &&
                    selectedObject?.type === "Arrow";
                  const bIsSelected =
                    selectedObject?.id === b.id &&
                    selectedObject?.type === "Arrow";
                  // Selected arrows come last (render on top)
                  if (aIsSelected !== bIsSelected) {
                    return aIsSelected ? 1 : -1;
                  }
                  return 0; // Maintain original order for non-selected
                });

                return sortedArrows.map((arrow) => {
                  const elementOpacity = getElementOpacity(arrow.id, "Arrow");
                  return (
                    <React.Fragment key={`main-${arrow.id}`}>
                      {renderArrowElement({
                        arrow,
                        isOffCanvas: false,
                        sourceStageRef: stageRef.current,
                        opacity: elementOpacity,
                      })}
                    </React.Fragment>
                  );
                });
              })()}
              {dragPreview &&
                dragPreview.target === "main" &&
                dragPreview.type === "Arrow" &&
                renderArrowElement({
                  arrow: dragPreview.data,
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  isPreview: true,
                })}

              {/* Double Sided Arrows */}
              {(() => {
                // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
                const isGroupDragging =
                  isGroupDraggingRef.current &&
                  selectedObjects &&
                  selectedObjects.length > 1;
                const hasMainCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "main",
                );
                const hasOffCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "offCanvas",
                );
                // CRITICAL FIX: Filter out dragged elements when there are ANY cross-canvas previews
                // This includes both dragging TO main canvas (hasMainCanvasPreviews) and FROM main canvas (hasOffCanvasPreviews)
                const shouldFilterDraggedElements =
                  isGroupDragging &&
                  (hasMainCanvasPreviews || hasOffCanvasPreviews);

                const draggedElementIds = shouldFilterDraggedElements
                  ? new Set(selectedObjects.map((obj) => obj.id))
                  : new Set();

                // Filter out dragged elements
                const filteredDoubleArrows = doubleSidedArrows.filter(
                  (arrow) => {
                    const isSelected =
                      selectedObject?.id === arrow.id &&
                      selectedObject?.type === "DoubleSidedArrow";
                    const isInSelectedObjects = selectedObjects?.some(
                      (obj) =>
                        obj.id === arrow.id && obj.type === "DoubleSidedArrow",
                    );
                    const isBeingDragged =
                      shouldFilterDraggedElements &&
                      draggedElementIds.has(arrow.id);

                    // CRITICAL FIX: Also check if this element has a group drag preview (ghost)
                    const hasGroupPreview = groupDragPreviews.some((p) => {
                      const previewId =
                        p.data?.id?.replace("-ghost", "") || p.data?.id;
                      return previewId === arrow.id;
                    });

                    return (
                      !isSelected &&
                      !isInSelectedObjects &&
                      !isBeingDragged &&
                      !hasGroupPreview
                    );
                  },
                );

                // Sort double-sided arrows so selected ones render last (on top)
                const sortedDoubleArrows = [...filteredDoubleArrows].sort(
                  (a, b) => {
                    const aIsSelected =
                      selectedObject?.id === a.id &&
                      selectedObject?.type === "DoubleSidedArrow";
                    const bIsSelected =
                      selectedObject?.id === b.id &&
                      selectedObject?.type === "DoubleSidedArrow";
                    // Selected arrows come last (render on top)
                    if (aIsSelected !== bIsSelected) {
                      return aIsSelected ? 1 : -1;
                    }
                    return 0; // Maintain original order for non-selected
                  },
                );

                return sortedDoubleArrows.map((arrow) => {
                  const elementOpacity = getElementOpacity(
                    arrow.id,
                    "DoubleSidedArrow",
                  );
                  return (
                    <React.Fragment key={`main-double-${arrow.id}`}>
                      {renderDoubleSidedArrowElement({
                        arrow,
                        isOffCanvas: false,
                        sourceStageRef: stageRef.current,
                        opacity: elementOpacity,
                      })}
                    </React.Fragment>
                  );
                });
              })()}
              {dragPreview &&
                dragPreview.target === "main" &&
                dragPreview.type === "DoubleSidedArrow" &&
                renderDoubleSidedArrowElement({
                  arrow: dragPreview.data,
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  isPreview: true,
                })}
              {/* {isDrawingLine && lineStartPoint && (
            <Circle
              x={lineStartPoint.x}
              y={lineStartPoint.y}
              radius={5}
              fill={fillColor || "black"}
              stroke={fillColor || "black"}
              strokeWidth={1}
              opacity={0.7}
              listening={false}
            />
          )} */}
              {isDrawingArrow && arrowStartPoint && (
                <Circle
                  x={arrowStartPoint.x}
                  y={arrowStartPoint.y}
                  radius={5}
                  fill={fillColor || "black"}
                  stroke={fillColor || "black"}
                  strokeWidth={1}
                  opacity={0.7}
                  listening={false}
                />
              )}
              {isDrawingDoubleArrow && doubleArrowStartPoint && (
                <Circle
                  x={doubleArrowStartPoint.x}
                  y={doubleArrowStartPoint.y}
                  radius={5}
                  fill={fillColor || "black"}
                  stroke={fillColor || "black"}
                  strokeWidth={1}
                  opacity={0.7}
                  listening={false}
                />
              )}
              {/* {isDrawingLine && lineStartPoint && hoverPoint && (
            <Line
              points={[
                lineStartPoint.x,
                lineStartPoint.y,
                hoverPoint.x,
                hoverPoint.y,
              ]}
              stroke={fillColor || "black"}
              strokeWidth={2}
              dash={[8, 4]}
              opacity={0.5}
              listening={false}
            />
          )} */}

              {/* Lines */}
              {(() => {
                // CRITICAL: Filter out selected lines - they'll be rendered in the top layer
                // This prevents double-rendering and ensures selected elements appear on top
                const isElementInSelectedObjects = (elementId, elementType) => {
                  if (!selectedObjects || selectedObjects.length === 0)
                    return false;
                  const result = selectedObjects.some(
                    (obj) =>
                      obj.id === elementId &&
                      (obj.type === elementType ||
                        obj.type === "Arrow" ||
                        obj.type === "Line" ||
                        obj.type === "DoubleSidedArrow"),
                  );
                  if (result) {
                  }
                  return result;
                };

                // Debug: Log all lines and offCanvasLines for debugging
                if (lines.length > 0 || offCanvasLines.length > 0) {
                }

                // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
                const isGroupDragging =
                  isGroupDraggingRef.current &&
                  selectedObjects &&
                  selectedObjects.length > 1;
                const hasMainCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "main",
                );
                const hasOffCanvasPreviews = groupDragPreviews.some(
                  (p) => p.target === "offCanvas",
                );
                // CRITICAL FIX: Filter out dragged elements when there are ANY cross-canvas previews
                // This includes both dragging TO main canvas (hasMainCanvasPreviews) and FROM main canvas (hasOffCanvasPreviews)
                const shouldFilterDraggedElements =
                  isGroupDragging &&
                  (hasMainCanvasPreviews || hasOffCanvasPreviews);

                const draggedElementIds = shouldFilterDraggedElements
                  ? new Set(selectedObjects.map((obj) => obj.id))
                  : new Set();

                // DEBUG: Log filtering state for lines
                if (shouldFilterDraggedElements && lines.length > 0) {
                }

                const filteredLines = lines.filter((l) => {
                  // CRITICAL: Filter out lines that are in offCanvasLines array
                  // This prevents off-canvas elements from being rendered in main canvas
                  const isInOffCanvas = offCanvasLines.some(
                    (offLine) => offLine.id === l.id,
                  );
                  if (isInOffCanvas) {
                    return false;
                  }

                  const isSelected =
                    selectedObject?.id === l.id &&
                    (selectedObject?.type === "Line" ||
                      selectedObject?.type === "Arrow" ||
                      selectedObject?.type === "DoubleSidedArrow");
                  const isInSelectedObjects = isElementInSelectedObjects(
                    l.id,
                    l.type || "Line",
                  );
                  // CRITICAL: Filter out if being dragged during cross-canvas group drag
                  const isBeingDragged =
                    shouldFilterDraggedElements && draggedElementIds.has(l.id);

                  // CRITICAL FIX: Also check if this element has a group drag preview (ghost)
                  const hasGroupPreview = groupDragPreviews.some((p) => {
                    const previewId =
                      p.data?.id?.replace("-ghost", "") || p.data?.id;
                    return previewId === l.id;
                  });

                  // CRITICAL: Filter out the selected line so it's only rendered in the selected layer
                  // (same as circle/rect). That layer uses array-backed position + _useNodePositionForGroup
                  // when dragging main→off, so the node never gets off-canvas coords and drag doesn't end.
                  if (isSelected && !isInSelectedObjects) {
                    return false;
                  }

                  // DEBUG: Log filtering decision for each line
                  if (shouldFilterDraggedElements) {
                    const shouldFilter =
                      isSelected ||
                      isInSelectedObjects ||
                      isBeingDragged ||
                      hasGroupPreview;
                  }

                  if (
                    isSelected ||
                    isInSelectedObjects ||
                    isBeingDragged ||
                    hasGroupPreview
                  ) {
                  }

                  return (
                    !isSelected &&
                    !isInSelectedObjects &&
                    !isBeingDragged &&
                    !hasGroupPreview
                  );
                });

                // Sort lines so selected ones render last (on top)
                const sortedLines = [...filteredLines].sort((a, b) => {
                  // CRITICAL: Check if selected by ID only, not by type, since type can change
                  const aIsSelected =
                    selectedObject?.id === a.id &&
                    (selectedObject?.type === "Line" ||
                      selectedObject?.type === "Arrow" ||
                      selectedObject?.type === "DoubleSidedArrow");
                  const bIsSelected =
                    selectedObject?.id === b.id &&
                    (selectedObject?.type === "Line" ||
                      selectedObject?.type === "Arrow" ||
                      selectedObject?.type === "DoubleSidedArrow");
                  // Selected lines come last (render on top)
                  if (aIsSelected !== bIsSelected) {
                    return aIsSelected ? 1 : -1;
                  }
                  return 0; // Maintain original order for non-selected
                });

                return sortedLines.map((line) => {
                  const linePreviewRef = dragPreviewStateRef.current;
                  const linePreviewId =
                    dragPreview?.data?.id?.replace("-ghost", "") ||
                    linePreviewRef?.id;
                  const linePreviewTarget =
                    dragPreview?.target || linePreviewRef?.target;
                  const linePreviewType =
                    dragPreview?.type ?? linePreviewRef?.type;
                  const isLinePreviewType =
                    linePreviewType === undefined ||
                    linePreviewType === "Line" ||
                    linePreviewType === "Arrow" ||
                    linePreviewType === "DoubleSidedArrow";
                  const previewOnOffCanvas =
                    isLinePreviewType &&
                    linePreviewTarget === "offCanvas" &&
                    linePreviewId === line.id;
                  // CRITICAL: Also treat "context says dragging from main" as cross-canvas - avoids one-frame flash
                  // when preview state hasn't updated yet (context is set on first dragMove, so it's stable).
                  const ctx = lineDragPreviewContextRef.current;
                  const isThisLineDraggedFromMain =
                    ctx?.element?.id === line.id && ctx?.isOffCanvas === false;
                  const useFrozenPosition =
                    previewOnOffCanvas || isThisLineDraggedFromMain;
                  const isSelectedLineArrow =
                    selectedObject?.id === line.id &&
                    (selectedObject?.type === "Line" ||
                      selectedObject?.type === "Arrow" ||
                      selectedObject?.type === "DoubleSidedArrow");
                  // When cross-canvas drag (or context says so), never merge selectedObject - prevents vibration.
                  let effectiveLine =
                    isSelectedLineArrow && !useFrozenPosition
                      ? { ...line, ...selectedObject }
                      : line;
                  const elementOpacity = getElementOpacity(
                    effectiveLine.id,
                    "Line",
                  );
                  if (useFrozenPosition) {
                    const frozen = lineLastSourcePositionRef.current[line.id];
                    effectiveLine = {
                      ...line,
                      ...effectiveLine,
                      _useNodePositionForGroup: true,
                      _isInOffCanvasDuringDrag: false,
                      x: frozen ? frozen.x : (line.x ?? 0),
                      y: frozen ? frozen.y : (line.y ?? 0),
                    };
                  }
                  // When dragging this line to off-canvas, hide source on main so only ghost on target stage shows.
                  const hideSourceWhenDraggingToOff =
                    lineSourceHiddenForDragRef.current[line.id] === true ||
                    previewOnOffCanvas;
                  return renderLineElement({
                    line: {
                      ...effectiveLine,
                      opacity: hideSourceWhenDraggingToOff
                        ? 0
                        : (effectiveLine.opacity ?? elementOpacity),
                    },
                    isOffCanvas: false,
                    sourceStageRef: stageRef.current,
                    saveToHistory,
                    opacity: hideSourceWhenDraggingToOff ? 0 : elementOpacity,
                  });
                });
              })()}
              {/* Line/arrow ghost - same pattern as Circle: only from dragPreview */}
              {/* Line ghost on main (no arrow ghosts) */}
              {(() => {
                // Render drag-preview ghosts on main canvas.
                // This uses the same unified renderer, but the source element copy is hidden during preview
                // to avoid Konva/React fight that manifests as fluctuation.
                if (
                  dragPreview &&
                  dragPreview.target === "main" &&
                  (dragPreview.type === "Line" ||
                    dragPreview.type === "Arrow" ||
                    dragPreview.type === "DoubleSidedArrow")
                ) {
                  return renderLineElement({
                    line: dragPreview.data,
                    isOffCanvas: false,
                    sourceStageRef: stageRef.current,
                    saveToHistory,
                    isPreview: true,
                  });
                }
                const ref = dragPreviewStateRef.current;
                const ctx = lineDragPreviewContextRef.current;
                if (
                  ref?.target !== "main" ||
                  !ctx?.element ||
                  (ref?.type !== "Line" &&
                    ref?.type !== "Arrow" &&
                    ref?.type !== "DoubleSidedArrow")
                )
                  return null;
                const lineData = {
                  ...ctx.element,
                  id: `${ref.id}-ghost`,
                  x: ref.x,
                  y: ref.y,
                  points: ctx.element?.points ?? [0, 0, 0, 0],
                };
                return renderLineElement({
                  line: lineData,
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  saveToHistory,
                  isPreview: true,
                });
              })()}

              {texts &&
                Array.isArray(texts) &&
                (() => {
                  // CRITICAL: Filter out texts that are in selectedObjects array
                  // They will be rendered in the selected layer (Layer 5) with full opacity
                  // This prevents duplicate rendering and ghost texts during drag
                  const isElementInSelectedObjects = (
                    elementId,
                    elementType,
                  ) => {
                    if (!selectedObjects || selectedObjects.length === 0)
                      return false;
                    return selectedObjects.some(
                      (obj) => obj.id === elementId && obj.type === elementType,
                    );
                  };

                  // CRITICAL FIX: Only filter out elements when showing cross-canvas previews
                  const isGroupDragging =
                    isGroupDraggingRef.current &&
                    selectedObjects &&
                    selectedObjects.length > 1;
                  const hasMainCanvasPreviews = groupDragPreviews.some(
                    (p) => p.target === "main",
                  );
                  const hasOffCanvasPreviews = groupDragPreviews.some(
                    (p) => p.target === "offCanvas",
                  );
                  // CRITICAL FIX: Filter out dragged elements when there are ANY cross-canvas previews
                  // This includes both dragging TO main canvas (hasMainCanvasPreviews) and FROM main canvas (hasOffCanvasPreviews)
                  const shouldFilterDraggedElements =
                    isGroupDragging &&
                    (hasMainCanvasPreviews || hasOffCanvasPreviews);

                  const draggedElementIds = shouldFilterDraggedElements
                    ? new Set(selectedObjects.map((obj) => obj.id))
                    : new Set();

                  // Filter out selected texts (same as Rect/Circle: when in selectedObjects, render only in selected layer to avoid duplicate/ghost at old position during group drag)
                  const filteredTexts = texts.filter((t) => {
                    // When multi-select and this text is in selectedObjects, only render it in the selected layer (which uses node position)
                    const isTextInSelectedObjects = selectedObjects?.some(
                      (obj) =>
                        obj.id === t.id &&
                        (obj.type === "Text" || obj.type?.includes("Text")),
                    );
                    if (
                      selectedObjects &&
                      selectedObjects.length > 1 &&
                      isTextInSelectedObjects
                    ) {
                      return false;
                    }

                    // CRITICAL: Filter out if being dragged during cross-canvas group drag
                    const isBeingDragged =
                      shouldFilterDraggedElements &&
                      draggedElementIds.has(t.id);

                    // CRITICAL FIX: Also check if this element has a group drag preview (ghost)
                    const hasGroupPreview = groupDragPreviews.some((p) => {
                      const previewId =
                        p.data?.id?.replace("-ghost", "") || p.data?.id;
                      return previewId === t.id;
                    });

                    // Only hide mirrored IDs while an active cross-canvas drag preview is present.
                    // Legitimate clones can exist on both canvases and may share nested identity fields.
                    const isInOffCanvasArray = offCanvasTexts.some(
                      (offText) => offText.id === t.id,
                    );
                    const hasActiveOffTargetPreviewForText =
                      (dragPreview &&
                        dragPreview.type === "Text" &&
                        dragPreview.target === "offCanvas" &&
                        dragPreview.data?.id?.replace("-ghost", "") === t.id) ||
                      groupDragPreviews.some((preview) => {
                        const previewId =
                          preview.data?.id?.replace("-ghost", "") ||
                          preview.data?.id;
                        return (
                          preview.target === "offCanvas" && previewId === t.id
                        );
                      });
                    if (isInOffCanvasArray && hasActiveOffTargetPreviewForText) {
                      return false;
                    }

                    return !isBeingDragged && !hasGroupPreview;
                  });

                  // Sort texts so selected ones render last (on top)
                  const sortedTexts = [...filteredTexts].sort((a, b) => {
                    const aIsSelected =
                      selectedObject?.id === a.id &&
                      (selectedObject?.type === "Text" ||
                        selectedObject?.type?.includes("Text"));
                    const bIsSelected =
                      selectedObject?.id === b.id &&
                      (selectedObject?.type === "Text" ||
                        selectedObject?.type?.includes("Text"));
                    // Selected texts come last (render on top)
                    if (aIsSelected !== bIsSelected) {
                      return aIsSelected ? 1 : -1;
                    }
                    return 0; // Maintain original order for non-selected
                  });

                  // When dragPreview is showing this text on main (main→off→main ghost),
                  // hide the source text entirely so only the ghost is visible.
                  const dragPreviewTextIdMain =
                    dragPreview?.type === "Text" &&
                    dragPreview?.target === "main" &&
                    dragPreview?.data?.id
                      ? dragPreview.data.id.replace("-ghost", "")
                      : null;
                  const textsToRender = dragPreviewTextIdMain
                    ? sortedTexts.filter((t) => t.id !== dragPreviewTextIdMain)
                    : sortedTexts;

                  return textsToRender.map((text) => {
                    const elementOpacity = getElementOpacity(text.id, "Text");
                    const bbox = getElementBoundingBox(text, "Text");
                    return (
                      <React.Fragment key={`text-${text.id}`}>
                        {/* Konva Rect blocker - render first so it's behind the text element */}
                        <Group
                          x={bbox.x}
                          y={bbox.y}
                          listening={false}
                          perfectDrawEnabled={false}
                          shadowForStrokeEnabled={false}
                        >
                          <Rect
                            x={0}
                            y={0}
                            width={bbox.width}
                            height={bbox.height}
                            fill="transparent"
                            stroke={null}
                            strokeWidth={0}
                            opacity={0}
                            listening={false}
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                        </Group>
                        {/* Render text element after blocker so it renders on top */}
                        {dragPreview?.type === "Text" &&
                        (dragPreview?.target === "main" ||
                          dragPreview?.target === "offCanvas") &&
                        dragPreview.data?.id?.replace("-ghost", "") === text.id
                          ? null
                          : renderTextElement({
                              text,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              opacity: elementOpacity,
                              hideBecauseGhostOnMain: false,
                              setOffCanvasTexts,
                              offCanvasTexts,
                              setTexts,
                              texts,
                              handleElementDragEnd,
                              // Let the text renderer handle onDragEnd internally (like rectangles)
                              // It will call handleElementDragEnd with proper parameters
                              onTransformEnd: (transformData) => {
                                // Calculate the updated state BEFORE calling setState
                                // This ensures saveToHistory receives the correct updated state
                                const updatedTexts = texts.map((t) =>
                                  t.id === transformData.id
                                    ? { ...t, ...transformData }
                                    : t,
                                );

                                setTexts((prev) =>
                                  prev.map((t) =>
                                    t.id === transformData.id
                                      ? { ...t, ...transformData }
                                      : t,
                                  ),
                                );

                                // Also update selectedText to reflect the transform changes
                                if (setSelectedText) {
                                  setSelectedText((prev) =>
                                    prev?.id === transformData.id
                                      ? { ...prev, ...transformData }
                                      : prev,
                                  );
                                }

                                // CRITICAL: Store the updated texts array in ref so element_edit_complete can use it
                                // This ensures rotation and other transform properties are saved to history
                                // Only store if element is selected/being edited (when pendingChanges will be set)
                                const isTextSelected =
                                  selectedObject?.id === transformData.id &&
                                  (selectedObject?.type === "Text" ||
                                    selectedObject?.type?.includes("Text"));
                                if (
                                  (isTextSelected || isElementBeingEdited) &&
                                  latestUpdatedTextsRef
                                ) {
                                  // Store the updated texts array (with rotation) in the ref
                                  latestUpdatedTextsRef.current.main =
                                    updatedTexts;
                                  setPendingChanges(true);
                                } else if (isElementBeingEdited) {
                                  setPendingChanges(true);
                                }

                                // CRITICAL: Don't save to history here - history will be saved when element is unselected
                                // This groups all transform changes (width, height, rotation, etc.) into a single history entry
                              },
                              saveToHistory,
                              onClick,
                              setSelectedObject,
                              setSelectedText,
                              setSelectedTextId,
                              setShowTextBox,
                              setShowEditTask,
                              setShowEditNote,
                              setEditTextBoxContent,
                              setSelectedObjects,
                              setAction,
                              ACTIONS,
                              setEditing,
                              transformerRef,
                              selectedObject,
                              selectedText,
                              setSelectedImage,
                              getTextStyle,
                              formatListContent,
                              hasUserContent,
                              estimateKonvaTextHeight,
                              stripHtmlPreserveFormatting,
                              hasTaskContent,
                              hasNoteContent,
                              calculateGroupHeight,
                              canvasWidth: mainCanvasWidth,
                              canvasHeight: fixedStageHeightPx,
                              canvasResizeTrigger,
                            })}
                      </React.Fragment>
                    );
                  });
                })()}
              {dragPreview &&
                dragPreview.target === "main" &&
                dragPreview.type === "Text" &&
                renderTextElement({
                  text: dragPreview.data,
                  isOffCanvas: false,
                  sourceStageRef: stageRef.current,
                  isPreview: true,
                })}

              {/* Transformer for text drag preview on main canvas */}
              {(() => {
                if (
                  !dragPreview ||
                  dragPreview.target !== "main" ||
                  dragPreview.type !== "Text" ||
                  !stageRef?.current ||
                  !dragPreview.data?.id
                ) {
                  return null;
                }

                const ghostId = dragPreview.data.id;
                const previewNode = stageRef.current.findOne(`#${ghostId}`);

                if (!previewNode) {
                  return null;
                }

                const anchors = ["middle-left", "middle-right"];

                return (
                  <Transformer
                    key={`text-preview-transformer-main-${ghostId}`}
                    nodes={[previewNode]}
                    rotateEnabled={false}
                    flipEnabled={false}
                  enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                    keepRatio={false}
                    centeredScaling={false}
                    visible={true}
                    listening={false}
                  />
                );
              })()}
            </Layer>
          )}

          {/* Priority overlay for group-drag text previews with background on main canvas */}
          {parseInt(canvasHeight) >= 5 && (
            <Layer listening={false}>
              {groupDragPreviews
                .filter(
                  (preview) =>
                    preview.target === "main" &&
                    preview.type === "Text" &&
                    preview.data?.showBackground === true,
                )
                .map((preview, index) => (
                  <React.Fragment
                    key={`group-preview-main-priority-${
                      preview.data?.id || index
                    }`}
                  >
                    {renderTextElement({
                      text: preview.data,
                      isOffCanvas: false,
                      sourceStageRef: stageRef.current,
                      isPreview: true,
                    })}
                  </React.Fragment>
                ))}
            </Layer>
          )}

          {/* Layer 3: Images pinned above shapes - REMOVED, moved to separate overlay Stage */}

          {/* Layer 4: Text (topmost layer) */}
          {parseInt(canvasHeight) >= 5 && (
            <Layer>
              {/* Selection box now handled by overlay outside Stage to avoid clipping */}

              {/* Blue highlight rectangles for selected elements */}
              {(() => {
                const safeSelectedObjects = Array.from(
                  new Map(
                    (selectedObjects || [])
                      .filter((obj) => obj?.id && obj?.type)
                      .map((obj) => [String(obj.id), obj]),
                  ).values(),
                );

                if (safeSelectedObjects.length === 0) {
                  return null;
                }

                // CRITICAL: Use dragUpdateTrigger to force re-render during drag
                // This ensures selection boxes update their positions in real-time
                // dragUpdateTrigger is incremented in handleGroupDragMove on each drag move
                // eslint-disable-next-line no-unused-vars
                const _dragTrigger = dragUpdateTrigger;

                // Helper function to get actual element from state arrays
                // CRITICAL: Also get current position from Konva node to ensure we use the latest position
                // This ensures selection box stays aligned with actual element positions during drag
                // Also checks both main and off-canvas arrays to find where element actually is
                const getActualElement = (obj) => {
                  if (!obj || !obj.id || !obj.type) return obj;

                  // First, get element from state arrays
                  let elementFromState = null;
                  switch (obj.type) {
                    case "Rect": {
                      elementFromState =
                        rectangles.find((r) => r.id === obj.id) ||
                        offCanvasRectangles.find((r) => r.id === obj.id);
                      break;
                    }
                    case "Circle": {
                      elementFromState =
                        circles.find((c) => c.id === obj.id) ||
                        offCanvasCircles.find((c) => c.id === obj.id);
                      break;
                    }
                    case "Image": {
                      elementFromState =
                        images.find((img) => img.id === obj.id) ||
                        offCanvasImages.find((img) => img.id === obj.id);
                      break;
                    }
                    case "Text": {
                      elementFromState =
                        texts.find((t) => t.id === obj.id) ||
                        offCanvasTexts.find((t) => t.id === obj.id);
                      break;
                    }
                    case "Arrow": {
                      // CRITICAL FIX: Check lines array FIRST for unified arrow elements
                      elementFromState = lines.find((l) => {
                        if (l.id !== obj.id) return false;
                        const hasStartArrow =
                          l.hasStartArrow === true ||
                          l.pointerAtBeginning === true;
                        const hasEndArrow =
                          l.hasEndArrow === true || l.pointerAtEnd === true;
                        return hasStartArrow || hasEndArrow;
                      });
                      if (!elementFromState) {
                        // Fallback to legacy arrows array
                        elementFromState = arrows.find((a) => a.id === obj.id);
                      }
                      break;
                    }
                    case "DoubleSidedArrow": {
                      // Check doubleSidedArrows array first
                      elementFromState = doubleSidedArrows.find(
                        (a) => a.id === obj.id,
                      );
                      if (!elementFromState) {
                        // Also check lines array for unified double-sided arrow elements
                        elementFromState = lines.find((l) => {
                          if (l.id !== obj.id) return false;
                          const hasStartArrow =
                            l.hasStartArrow === true ||
                            l.pointerAtBeginning === true;
                          const hasEndArrow =
                            l.hasEndArrow === true || l.pointerAtEnd === true;
                          return hasStartArrow && hasEndArrow;
                        });
                      }
                      break;
                    }
                    case "Line": {
                      elementFromState = lines.find((l) => {
                        if (l.id !== obj.id) return false;
                        // Only return true if it's actually a line (not a unified arrow)
                        const hasStartArrow =
                          l.hasStartArrow === true ||
                          l.pointerAtBeginning === true;
                        const hasEndArrow =
                          l.hasEndArrow === true || l.pointerAtEnd === true;
                        return !hasStartArrow && !hasEndArrow;
                      });
                      break;
                    }
                    default:
                      elementFromState = obj;
                  }

                  const element = elementFromState || obj;

                  // CRITICAL: Get current position from Konva node to ensure we use the latest position
                  // This fixes the issue where blue highlight shows at old position after element is moved
                  // Check main, off-canvas, AND top-layer stages to find where the element is rendered
                  const mainStage = stageRef?.current;
                  const offStage = offCanvasStageRef?.current;
                  const topLayerStage = topLayerStageRef?.current;

                  if (element?.id) {
                    // Try main canvas stage first
                    if (mainStage) {
                      const node = mainStage.findOne(`#${element.id}`);
                      if (node) {
                        // Get current position from Konva node (this is the actual rendered position)
                        const currentX = node.x();
                        const currentY = node.y();

                        // If position is available from node, use it (it's the current rendered position)
                        if (currentX !== undefined && currentY !== undefined) {
                          return {
                            ...element,
                            x: currentX,
                            y: currentY,
                          };
                        }
                      }
                    }

                    // Try off-canvas stage if not found on main canvas
                    if (offStage) {
                      const node = offStage.findOne(`#${element.id}`);
                      if (node) {
                        // Get current position from Konva node (this is the actual rendered position)
                        const currentX = node.x();
                        const currentY = node.y();

                        // If position is available from node, use it (it's the current rendered position)
                        if (currentX !== undefined && currentY !== undefined) {
                          return {
                            ...element,
                            x: currentX,
                            y: currentY,
                          };
                        }
                      }
                    }

                    // Try off-canvas stage if not found on main
                    if (offStage) {
                      const node = offStage.findOne(`#${element.id}`);
                      if (node) {
                        // Get current position from Konva node (this is the actual rendered position)
                        const currentX = node.x();
                        const currentY = node.y();

                        // If position is available from node, use it (it's the current rendered position)
                        if (currentX !== undefined && currentY !== undefined) {
                          return {
                            ...element,
                            x: currentX,
                            y: currentY,
                          };
                        }
                      }
                    }

                    // Try top-layer stage if not found on main or off-canvas
                    if (topLayerStage) {
                      const node = topLayerStage.findOne(`#${element.id}`);
                      if (node) {
                        // Get current position from Konva node (this is the actual rendered position)
                        const currentX = node.x();
                        const currentY = node.y();

                        // If position is available from node, use it (it's the current rendered position)
                        if (currentX !== undefined && currentY !== undefined) {
                          return {
                            ...element,
                            x: currentX,
                            y: currentY,
                          };
                        }
                      }
                    }
                  }

                  return element;
                };

                // Filter objects based on multi-select mode and selection state
                // When multiple objects are selected, show all of them (with wrapper rectangle)
                // When in multi-select mode, only show individually selected ones
                // Otherwise, show only the selectedObject
                let objectsToHighlight = [];

                if (safeSelectedObjects.length > 1) {
                  // Multiple objects selected - always show all of them initially
                  // If individuallySelectedIds is set and has items, filter by it
                  // Otherwise show all selected objects
                  if (individuallySelectedIds.size > 0) {
                    objectsToHighlight = safeSelectedObjects.filter((obj) =>
                      individuallySelectedIds.has(obj.id),
                    );
                    // If filtering resulted in empty or single item, show all instead
                    if (objectsToHighlight.length <= 1) {
                      objectsToHighlight = safeSelectedObjects;
                    }
                  } else {
                    // No individuallySelectedIds set yet - show all selected objects
                    objectsToHighlight = safeSelectedObjects;
                  }
                } else if (isMultiSelectMode) {
                  // Multi-select mode - show individually selected ones
                  objectsToHighlight = safeSelectedObjects.filter((obj) =>
                    individuallySelectedIds.has(obj.id),
                  );
                } else if (selectedObject && selectedObject.id) {
                  // Single selection - show only selectedObject
                  objectsToHighlight = safeSelectedObjects.filter(
                    (obj) => obj.id === selectedObject.id,
                  );
                }

                if (objectsToHighlight.length === 0) {
                  return null;
                }

                // First, get all objects on main canvas (including text) for wrapper box calculation
                const allMainCanvasObjects = objectsToHighlight.filter(
                  (obj) => {
                    // CRITICAL FIX: For top-layered images, check if they're being dragged to off-canvas
                    // During drag, check if there's a group drag preview for this image on off-canvas
                    // If there is, exclude it from main canvas wrapper box
                    if (obj.type === "Image") {
                      const isTopLayerImage = images.some(
                        (img) => img.id === obj.id && img.isOnTopLayer === true,
                      );

                      if (isTopLayerImage && isGroupDraggingRef.current) {
                        // Check both the ref and state for group drag previews
                        const previewsToCheck =
                          groupDragPreviewsRef.current?.length > 0
                            ? groupDragPreviewsRef.current
                            : groupDragPreviews;

                        // Check if there's a group drag preview for this image on off-canvas
                        const hasOffCanvasPreview = previewsToCheck?.some(
                          (preview) =>
                            preview.type === "Image" &&
                            preview.target === "offCanvas" &&
                            (preview.data?.id === `${obj.id}-ghost` ||
                              preview.data?.id === obj.id ||
                              (preview.data &&
                                preview.data.id &&
                                preview.data.id.replace("-ghost", "") ===
                                  obj.id)),
                        );

                        // Also check selectionBoxStage as a fallback indicator
                        const isDraggingToOffCanvas =
                          hasOffCanvasPreview ||
                          selectionBoxStage === "off-canvas";

                        if (isDraggingToOffCanvas) {
                          // Top-layered image is being dragged to off-canvas - exclude from main canvas wrapper box
                          return false;
                        }
                      }
                    }

                    // Check if element is in main canvas arrays
                    const isInMainCanvas = (() => {
                      switch (obj.type) {
                        case "Rect":
                          return rectangles.some((r) => r.id === obj.id);
                        case "Circle":
                          return circles.some((c) => c.id === obj.id);
                        case "Image":
                          return images.some((img) => img.id === obj.id);
                        case "Text":
                          return texts.some((t) => t.id === obj.id);
                        case "Arrow":
                          // Arrows with heads are stored in the lines array as unified elements
                          // CRITICAL FIX: Check lines array FIRST for unified arrow elements
                          return (
                            lines.some(
                              (l) =>
                                l.id === obj.id &&
                                (l.hasStartArrow ||
                                  l.hasEndArrow ||
                                  l.pointerAtBeginning ||
                                  l.pointerAtEnd),
                            ) || arrows.some((a) => a.id === obj.id)
                          );
                        case "DoubleSidedArrow":
                          // Double-sided arrows are stored in the lines array as unified elements
                          return (
                            doubleSidedArrows.some((a) => a.id === obj.id) ||
                            lines.some(
                              (l) =>
                                l.id === obj.id &&
                                l.hasStartArrow &&
                                l.hasEndArrow,
                            )
                          );
                        case "Line":
                          // Lines without arrow heads are in the lines array
                          // But also check if this line actually has arrow heads (in case type was misclassified)
                          const lineMatch = lines.find((l) => l.id === obj.id);
                          if (!lineMatch) return false;

                          // If line has arrow heads, it should have been classified as Arrow or DoubleSidedArrow
                          // But if it wasn't, still include it in the selection
                          const hasArrowHeads =
                            lineMatch.hasStartArrow ||
                            lineMatch.hasEndArrow ||
                            lineMatch.pointerAtBeginning ||
                            lineMatch.pointerAtEnd;

                          if (hasArrowHeads) {
                            // This line has arrow heads, so it should be included
                            return true;
                          }

                          // This is a pure line without arrow heads
                          return (
                            !lineMatch.hasStartArrow &&
                            !lineMatch.hasEndArrow &&
                            !lineMatch.pointerAtBeginning &&
                            !lineMatch.pointerAtEnd
                          );
                        default:
                          // CRITICAL FIX: If type is not recognized, check if it's a line with arrow heads
                          // This handles cases where the type might be misclassified
                          // Check all arrow/line arrays as fallback
                          return (
                            lines.some((l) => l.id === obj.id) ||
                            arrows.some((a) => a.id === obj.id) ||
                            doubleSidedArrows.some((d) => d.id === obj.id)
                          );
                      }
                    })();

                    if (
                      obj.type === "Arrow" ||
                      obj.type === "DoubleSidedArrow" ||
                      obj.type === "Line"
                    ) {
                    }

                    return isInMainCanvas;
                  },
                );

                // Filter out text elements for individual rectangle rendering (no blue rects around texts)
                const mainCanvasObjects = allMainCanvasObjects.filter(
                  (obj) => obj.type !== "Text",
                );

                // Calculate wrapper rectangle when multiple elements are selected
                // CRITICAL: Use allMainCanvasObjects (includes ALL selected objects) for wrapper box calculation
                // This ensures wrapper box shows around all selected elements, even Text and UnifiedLineArrowElement
                // Individual highlight rectangles will still be filtered (Text and UnifiedLineArrowElement won't show individual boxes)
                let wrapperBBox = null;

                // CRITICAL FIX: During group drag to main canvas, include preview elements in wrapper calculation
                // This ensures the wrapper box is visible when dragging from off-canvas to main canvas
                const isDraggingToMain =
                  isGroupDraggingRef.current &&
                  groupDragPreviews.some((p) => p.target === "main");
                const elementsForWrapper = isDraggingToMain
                  ? groupDragPreviews
                      .filter((p) => p.target === "main")
                      .map((p) => ({
                        ...p.data,
                        type: p.type,
                        id: p.data?.id?.replace("-ghost", "") || p.data?.id,
                      }))
                  : allMainCanvasObjects;

                if (elementsForWrapper.length > 1) {
                  const bboxes = elementsForWrapper.map((obj) => {
                    // During drag preview, use preview data directly; otherwise use actual element
                    let elementForBbox = obj;
                    if (!isDraggingToMain) {
                      const actualElement = getActualElement(obj);
                      const elementType = actualElement.type || obj.type;
                      // CRITICAL FIX: Get current position from Konva node to ensure we use the latest position
                      // This prevents selection box stretching when elements are moved between canvases
                      elementForBbox = getElementWithCurrentPositionForWrapper(
                        actualElement,
                        elementType,
                        false,
                      );
                    } else {
                      // obj is a groupDragPreview entry: { type, target, data }
                      elementForBbox = obj?.data || obj;
                    }
                    const elementType =
                      elementForBbox?.type || obj?.type || "Line";
                    const bbox = getElementBoundingBox(
                      elementForBbox,
                      elementType,
                      { includeStroke: true },
                    );

                    // Debug logging for unified arrow elements
                    if (
                      elementType === "Arrow" ||
                      elementType === "DoubleSidedArrow" ||
                      elementType === "Line"
                    ) {
                    }

                    return bbox;
                  });
                  const minX = Math.min(...bboxes.map((b) => b.x));
                  const minY = Math.min(...bboxes.map((b) => b.y));
                  const maxX = Math.max(...bboxes.map((b) => b.x + b.width));
                  const maxY = Math.max(...bboxes.map((b) => b.y + b.height));
                  wrapperBBox = {
                    x: minX - 2, // Small padding
                    y: minY - 2,
                    width: maxX - minX + 4,
                    height: maxY - minY + 4,
                  };
                }

                // Only render wrapper if it has valid dimensions
                // During drag to main, use preview elements count; otherwise use allMainCanvasObjects
                const elementCountForWrapper = isDraggingToMain
                  ? elementsForWrapper.length
                  : allMainCanvasObjects.length;
                const willRenderWrapper =
                  wrapperBBox &&
                  elementCountForWrapper > 1 &&
                  wrapperBBox.width > 0 &&
                  wrapperBBox.height > 0 &&
                  !isNaN(wrapperBBox.x) &&
                  !isNaN(wrapperBBox.y) &&
                  !isNaN(wrapperBBox.width) &&
                  !isNaN(wrapperBBox.height);

                return (
                  <>
                    {/* Wrapper rectangle when multiple elements are selected */}
                    {willRenderWrapper && (
                      <Rect
                        key="wrapper-highlight"
                        id="group-drag-wrapper-main"
                        x={wrapperBBox.x}
                        y={wrapperBBox.y}
                        width={wrapperBBox.width}
                        height={wrapperBBox.height}
                        fill="rgba(59, 130, 246, 0.1)"
                        stroke="#0D6EFD"
                        strokeWidth={3}
                        draggable={!isCloneMode && !lockedAction}
                        listening={true}
                        perfectDrawEnabled={false}
                        shadowForStrokeEnabled={false}
                        dragBoundFunc={(pos) => pos}
                        onDragStart={(e) => {
                          // Track source stage so window-mousemove can call setPointersPositions
                          // keeping Konva's drag alive when pointer crosses to another stage
                          groupDragSourceStageRef.current = stageRef?.current;

                          // CRITICAL: Initialize group drag start for preview functionality
                          // Use the first element in the selection as the "dragged" element
                          const firstElementPos = allMainCanvasObjects[0];
                          if (firstElementPos) {
                            handleGroupDragStart?.(
                              firstElementPos.id,
                              firstElementPos.type,
                            );
                          }

                          // Store initial wrapper position and all element positions
                          // CRITICAL: Use allMainCanvasObjects to include ALL selected elements in drag
                          const wrapperNode = e.target;
                          wrapperDragStartRef.current = {
                            x: wrapperNode.x(),
                            y: wrapperNode.y(),
                            elementPositions: allMainCanvasObjects.map(
                              (obj) => {
                                const actualElement = getActualElement(obj);
                                // Determine which canvas the element is on
                                let isOnMainCanvas = false;
                                switch (obj.type) {
                                  case "Rect":
                                    isOnMainCanvas = rectangles.some(
                                      (r) => r.id === obj.id,
                                    );
                                    break;
                                  case "Circle":
                                    isOnMainCanvas = circles.some(
                                      (c) => c.id === obj.id,
                                    );
                                    break;
                                  case "Image":
                                    isOnMainCanvas = images.some(
                                      (img) => img.id === obj.id,
                                    );
                                    break;
                                  case "Text":
                                    isOnMainCanvas = texts.some(
                                      (t) => t.id === obj.id,
                                    );
                                    break;
                                  case "Arrow":
                                    // Check arrows array first
                                    isOnMainCanvas = arrows.some(
                                      (a) => a.id === obj.id,
                                    );
                                    // Also check lines array for unified arrow elements (with arrow heads)
                                    if (!isOnMainCanvas) {
                                      isOnMainCanvas = lines.some((l) => {
                                        if (l.id !== obj.id) return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow || hasEndArrow;
                                      });
                                    }
                                    break;
                                  case "DoubleSidedArrow":
                                    // Check doubleSidedArrows array first
                                    isOnMainCanvas = doubleSidedArrows.some(
                                      (a) => a.id === obj.id,
                                    );
                                    // Also check lines array for unified double-sided arrow elements
                                    if (!isOnMainCanvas) {
                                      isOnMainCanvas = lines.some((l) => {
                                        if (l.id !== obj.id) return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow && hasEndArrow;
                                      });
                                    }
                                    break;
                                  case "Line":
                                    // Lines without arrow heads are in the lines array
                                    isOnMainCanvas = lines.some((l) => {
                                      if (l.id !== obj.id) return false;
                                      // Only return lines without arrow heads
                                      return (
                                        !l.hasStartArrow &&
                                        !l.hasEndArrow &&
                                        !l.pointerAtBeginning &&
                                        !l.pointerAtEnd
                                      );
                                    });
                                    break;
                                }
                                return {
                                  id: obj.id,
                                  type: obj.type,
                                  x: actualElement.x || 0,
                                  y: actualElement.y || 0,
                                  isOnMainCanvas, // Track which canvas element is on
                                };
                              },
                            ),
                          };
                          // Keep wrapper drag aligned with the shared drag-end sync/clamp pipeline.
                          individualElementDragStartRef.current = {
                            elementPositions:
                              wrapperDragStartRef.current.elementPositions,
                          };
                          // Reset drag flag when starting a new drag
                          wrapperJustDraggedRef.current = false;
                        }}
                        onDragMove={(e) => {
                          const wrapperNode = e.target;
                          const currentX = wrapperNode.x();
                          const currentY = wrapperNode.y();
                          const deltaX =
                            currentX - wrapperDragStartRef.current.x;
                          const deltaY =
                            currentY - wrapperDragStartRef.current.y;

                          // CRITICAL: Detect if we're in cross-canvas drag mode
                          // Check if mouse is over off-canvas area
                          let isCrossingToOffCanvas = false;
                          try {
                            const mainStage = stageRef?.current;
                            if (mainStage) {
                              const pointerPos = mainStage.getPointerPosition();
                              if (pointerPos) {
                                const stageContainer = mainStage.container();
                                if (stageContainer) {
                                  const stageBBox =
                                    stageContainer.getBoundingClientRect();
                                  const clientX = stageBBox.left + pointerPos.x;
                                  const clientY = stageBBox.top + pointerPos.y;

                                  const offCanvasContainer =
                                    offCanvasStageRef?.current?.container();
                                  if (offCanvasContainer) {
                                    const offBBox =
                                      offCanvasContainer.getBoundingClientRect();
                                    isCrossingToOffCanvas =
                                      clientX >= offBBox.left &&
                                      clientX <= offBBox.right &&
                                      clientY >= offBBox.top &&
                                      clientY <= offBBox.bottom;
                                  }
                                }
                              }
                            }
                          } catch (e) {
                            // Ignore errors
                          }

                          // CRITICAL: Call handleGroupDragMove to show previews when dragging between canvases
                          // Use the first element as the "dragged" element, with its new position
                          const firstElementPos =
                            wrapperDragStartRef.current.elementPositions[0];
                          if (firstElementPos) {
                            const newX = firstElementPos.x + deltaX;
                            const newY = firstElementPos.y + deltaY;
                            handleGroupDragMove?.(
                              firstElementPos.id,
                              firstElementPos.type,
                              newX,
                              newY,
                              e,
                            );
                          }

                          // Update all selected elements' Konva node positions ONLY
                          // Do NOT update state during drag - this prevents re-renders and keeps elements in sync
                          wrapperDragStartRef.current.elementPositions.forEach(
                            (initialPos) => {
                              let newX = initialPos.x + deltaX;
                              let newY = initialPos.y + deltaY;

                              // Update Konva node position directly for immediate visual feedback
                              const mainStage = stageRef?.current;
                              const offStage = offCanvasStageRef?.current;
                              const topLayerStage = topLayerStageRef?.current;
                              const elementNode =
                                mainStage?.findOne(`#${initialPos.id}`) ||
                                offStage?.findOne(`#${initialPos.id}`) ||
                                topLayerStage?.findOne(`#${initialPos.id}`);

                              if (elementNode) {
                                // CRITICAL FIX: Constrain positions for elements during drag move
                                const elementStage = elementNode.getStage();
                                const isOnOffCanvasStage =
                                  elementStage === offStage;
                                const isOnMainCanvasStage =
                                  elementStage === mainStage;
                                const isOnTopLayerStage =
                                  elementStage === topLayerStage;

                                // Constrain off-canvas elements
                                if (
                                  false &&
                                  isOnOffCanvasStage &&
                                  !isOnTopLayerStage &&
                                  offStage
                                ) {
                                  const stageWidth =
                                    offStage.width() || window.innerWidth;
                                  const stageHeight =
                                    offStage.height() || fixedStageHeightPx;

                                  // Get element from state to calculate bounding box
                                  let element = null;
                                  switch (initialPos.type) {
                                    case "Rect":
                                      element = offCanvasRectangles.find(
                                        (r) => r.id === initialPos.id,
                                      );
                                      break;
                                    case "Circle":
                                      element = offCanvasCircles.find(
                                        (c) => c.id === initialPos.id,
                                      );
                                      break;
                                    case "Image":
                                      element = offCanvasImages.find(
                                        (img) => img.id === initialPos.id,
                                      );
                                      break;
                                    case "Text":
                                      element = offCanvasTexts.find(
                                        (t) => t.id === initialPos.id,
                                      );
                                      break;
                                    case "Line":
                                    case "Arrow":
                                    case "DoubleSidedArrow":
                                      element =
                                        offCanvasLines.find(
                                          (l) => l.id === initialPos.id,
                                        ) ||
                                        offCanvasArrows.find(
                                          (a) => a.id === initialPos.id,
                                        ) ||
                                        offCanvasDoubleSidedArrows.find(
                                          (a) => a.id === initialPos.id,
                                        );
                                      break;
                                  }

                                  if (element) {
                                    // CRITICAL FIX: For lines/arrows, check ALL points individually
                                    // This ensures no point goes off-screen, even for vertical/horizontal lines
                                    if (
                                      initialPos.type === "Line" ||
                                      initialPos.type === "Arrow" ||
                                      initialPos.type === "DoubleSidedArrow"
                                    ) {
                                      const points = element.points || [
                                        0, 0, 0, 0,
                                      ];
                                      if (points.length >= 4) {
                                        // Calculate absolute positions of all points with new group position
                                        const absolutePoints = [];
                                        for (
                                          let i = 0;
                                          i < points.length;
                                          i += 2
                                        ) {
                                          absolutePoints.push({
                                            x: points[i] + newX,
                                            y: points[i + 1] + newY,
                                          });
                                        }

                                        // Find min/max across ALL points
                                        let absMinX = Infinity;
                                        let absMaxX = -Infinity;
                                        let absMinY = Infinity;
                                        let absMaxY = -Infinity;

                                        for (const point of absolutePoints) {
                                          absMinX = Math.min(absMinX, point.x);
                                          absMaxX = Math.max(absMaxX, point.x);
                                          absMinY = Math.min(absMinY, point.y);
                                          absMaxY = Math.max(absMaxY, point.y);
                                        }

                                        // Add padding for stroke width
                                        const strokeWidth =
                                          element.strokeWidth || 2;
                                        const padding = Math.max(
                                          strokeWidth / 2,
                                          5,
                                        );

                                        // Calculate adjustment needed to keep ALL points within bounds
                                        let deltaX = 0;
                                        let deltaY = 0;

                                        // Constrain X: ensure ALL points have x >= padding and x <= stageWidth - padding
                                        if (absMinX < padding) {
                                          deltaX = padding - absMinX; // Move right
                                        }
                                        if (absMaxX > stageWidth - padding) {
                                          const rightOverflow =
                                            absMaxX - (stageWidth - padding);
                                          deltaX = Math.max(
                                            deltaX,
                                            -rightOverflow,
                                          ); // Move left if needed
                                        }

                                        // Constrain Y: ensure ALL points have y >= padding and y <= stageHeight - padding
                                        if (absMinY < padding) {
                                          deltaY = padding - absMinY; // Move down
                                        }
                                        if (absMaxY > stageHeight - padding) {
                                          const bottomOverflow =
                                            absMaxY - (stageHeight - padding);
                                          deltaY = Math.max(
                                            deltaY,
                                            -bottomOverflow,
                                          ); // Move up if needed
                                        }

                                        // Apply adjustment
                                        newX = newX + deltaX;
                                        newY = newY + deltaY;
                                      } else {
                                        // Fallback for invalid points
                                        newX = Math.max(
                                          0,
                                          Math.min(stageWidth, newX),
                                        );
                                        newY = Math.max(
                                          0,
                                          Math.min(stageHeight, newY),
                                        );
                                      }
                                    } else {
                                      // For other element types, use bounding box approach
                                      const tempElement = {
                                        ...element,
                                        x: newX,
                                        y: newY,
                                      };
                                      const bbox = getElementBoundingBox(
                                        tempElement,
                                        initialPos.type,
                                      );

                                      // Determine coordinate system based on element type
                                      const isCenterCoordinate =
                                        initialPos.type === "Image" ||
                                        initialPos.type === "Circle";

                                      if (isCenterCoordinate) {
                                        // For center coordinate elements (Image, Circle)
                                        const halfWidth = bbox.width / 2;
                                        const halfHeight = bbox.height / 2;
                                        newX = Math.max(
                                          halfWidth,
                                          Math.min(
                                            stageWidth - halfWidth,
                                            newX,
                                          ),
                                        );
                                        newY = Math.max(
                                          halfHeight,
                                          Math.min(
                                            stageHeight - halfHeight,
                                            newY,
                                          ),
                                        );
                                      } else {
                                        // For top-left coordinate elements (Rect, Text)
                                        newX = Math.max(
                                          0,
                                          Math.min(
                                            stageWidth - bbox.width,
                                            newX,
                                          ),
                                        );
                                        newY = Math.max(
                                          0,
                                          Math.min(
                                            stageHeight - bbox.height,
                                            newY,
                                          ),
                                        );
                                      }
                                    }
                                  } else {
                                    // Fallback: just constrain to >= 0 and <= stage dimensions
                                    newX = Math.max(
                                      0,
                                      Math.min(stageWidth, newX),
                                    );
                                    newY = Math.max(
                                      0,
                                      Math.min(stageHeight, newY),
                                    );
                                  }

                                  // Keep off-canvas group members inside their original strip (left/right).
                                  const originalExtent = getAbsoluteHorizontalExtent(
                                    element,
                                    initialPos.type,
                                    initialPos.x,
                                  );
                                  const lockedStripSide =
                                    getOffCanvasHorizontalStripSide(
                                      originalExtent.minX,
                                      originalExtent.maxX,
                                      stageWidth,
                                    );
                                  newX = shiftXToStayInOffCanvasStrip(
                                    element,
                                    initialPos.type,
                                    newX,
                                    stageWidth,
                                    lockedStripSide,
                                  );
                                }

                                // Constrain main canvas elements
                                // CRITICAL: Skip constraints if crossing to off-canvas
                                if (
                                  false &&
                                  isOnMainCanvasStage &&
                                  !isOnTopLayerStage &&
                                  mainStage
                                ) {
                                  const stageWidth =
                                    mainStage.width() ||
                                    mainCanvasWidth;
                                  const stageHeight =
                                    mainStage.height() || fixedStageHeightPx;

                                  // Get element from state to calculate bounding box
                                  let element = null;
                                  switch (initialPos.type) {
                                    case "Rect":
                                      element = rectangles.find(
                                        (r) => r.id === initialPos.id,
                                      );
                                      break;
                                    case "Circle":
                                      element = circles.find(
                                        (c) => c.id === initialPos.id,
                                      );
                                      break;
                                    case "Image":
                                      element = images.find(
                                        (img) => img.id === initialPos.id,
                                      );
                                      break;
                                    case "Text":
                                      element = texts.find(
                                        (t) => t.id === initialPos.id,
                                      );
                                      break;
                                    case "Line":
                                    case "Arrow":
                                    case "DoubleSidedArrow":
                                      element =
                                        lines.find(
                                          (l) => l.id === initialPos.id,
                                        ) ||
                                        arrows.find(
                                          (a) => a.id === initialPos.id,
                                        ) ||
                                        doubleSidedArrows.find(
                                          (a) => a.id === initialPos.id,
                                        );
                                      break;
                                  }

                                  if (element) {
                                    // CRITICAL FIX: For lines/arrows, check ALL points individually
                                    // This ensures no point goes off-screen, even for vertical/horizontal lines
                                    if (
                                      initialPos.type === "Line" ||
                                      initialPos.type === "Arrow" ||
                                      initialPos.type === "DoubleSidedArrow"
                                    ) {
                                      const points = element.points || [
                                        0, 0, 0, 0,
                                      ];
                                      if (points.length >= 4) {
                                        // Calculate absolute positions of all points with new group position
                                        const absolutePoints = [];
                                        for (
                                          let i = 0;
                                          i < points.length;
                                          i += 2
                                        ) {
                                          absolutePoints.push({
                                            x: points[i] + newX,
                                            y: points[i + 1] + newY,
                                          });
                                        }

                                        // Find min/max across ALL points
                                        let absMinX = Infinity;
                                        let absMaxX = -Infinity;
                                        let absMinY = Infinity;
                                        let absMaxY = -Infinity;

                                        for (const point of absolutePoints) {
                                          absMinX = Math.min(absMinX, point.x);
                                          absMaxX = Math.max(absMaxX, point.x);
                                          absMinY = Math.min(absMinY, point.y);
                                          absMaxY = Math.max(absMaxY, point.y);
                                        }

                                        // Add padding for stroke width
                                        const strokeWidth =
                                          element.strokeWidth || 2;
                                        const padding = Math.max(
                                          strokeWidth / 2,
                                          5,
                                        );

                                        // Calculate adjustment needed to keep ALL points within bounds
                                        let deltaX = 0;
                                        let deltaY = 0;

                                        // Constrain X: ensure ALL points have x >= TOOLBAR_WIDTH and x <= stageWidth - padding
                                        if (absMinX < TOOLBAR_WIDTH) {
                                          deltaX = TOOLBAR_WIDTH - absMinX; // Move right
                                        }
                                        if (absMaxX > stageWidth - padding) {
                                          const rightOverflow =
                                            absMaxX - (stageWidth - padding);
                                          deltaX = Math.max(
                                            deltaX,
                                            -rightOverflow,
                                          ); // Move left if needed
                                        }

                                        // Constrain Y: ensure ALL points have y >= padding and y <= stageHeight - padding
                                        if (absMinY < padding) {
                                          deltaY = padding - absMinY; // Move down
                                        }
                                        if (absMaxY > stageHeight - padding) {
                                          const bottomOverflow =
                                            absMaxY - (stageHeight - padding);
                                          deltaY = Math.max(
                                            deltaY,
                                            -bottomOverflow,
                                          ); // Move up if needed
                                        }

                                        // Apply adjustment
                                        newX = newX + deltaX;
                                        newY = newY + deltaY;
                                      } else {
                                        // Fallback for invalid points
                                        newX = Math.max(
                                          TOOLBAR_WIDTH,
                                          Math.min(stageWidth, newX),
                                        );
                                        newY = Math.max(
                                          0,
                                          Math.min(stageHeight, newY),
                                        );
                                      }
                                    } else {
                                      // For other element types, use bounding box approach
                                      const tempElement = {
                                        ...element,
                                        x: newX,
                                        y: newY,
                                      };
                                      const bbox = getElementBoundingBox(
                                        tempElement,
                                        initialPos.type,
                                      );

                                      // Determine coordinate system based on element type
                                      const isCenterCoordinate =
                                        initialPos.type === "Image" ||
                                        initialPos.type === "Circle";

                                      if (isCenterCoordinate) {
                                        // For center coordinate elements (Image, Circle)
                                        const halfWidth = bbox.width / 2;
                                        const halfHeight = bbox.height / 2;

                                        // For circles, account for stroke width and toolbar
                                        let minX = halfWidth;
                                        if (initialPos.type === "Circle") {
                                          const strokeWidth =
                                            element.strokeWidth || 2;
                                          // Leftmost point of stroke = x - radius - strokeWidth/2
                                          // Constraint: x - radius - strokeWidth/2 >= TOOLBAR_WIDTH
                                          // Therefore: x >= TOOLBAR_WIDTH + radius + strokeWidth/2
                                          const radius =
                                            element.radius ||
                                            Math.max(
                                              element.radiusX || 0,
                                              element.radiusY || 0,
                                            );
                                          minX =
                                            TOOLBAR_WIDTH +
                                            radius +
                                            strokeWidth / 2;
                                        }

                                        newX = Math.max(
                                          minX,
                                          Math.min(
                                            stageWidth - halfWidth,
                                            newX,
                                          ),
                                        );
                                        newY = Math.max(
                                          halfHeight,
                                          Math.min(
                                            stageHeight - halfHeight,
                                            newY,
                                          ),
                                        );
                                      } else {
                                        // For top-left coordinate elements (Rect, Text)
                                        let minX = 0;
                                        if (initialPos.type === "Rect") {
                                          const strokeWidth =
                                            element.strokeWidth || 2;
                                          // Leftmost point of stroke = x - strokeWidth/2
                                          // Constraint: x - strokeWidth/2 >= TOOLBAR_WIDTH
                                          // Therefore: x >= TOOLBAR_WIDTH + strokeWidth/2
                                          minX =
                                            TOOLBAR_WIDTH + strokeWidth / 2;
                                        }

                                        newX = Math.max(
                                          minX,
                                          Math.min(
                                            stageWidth - bbox.width,
                                            newX,
                                          ),
                                        );
                                        newY = Math.max(
                                          0,
                                          Math.min(
                                            stageHeight - bbox.height,
                                            newY,
                                          ),
                                        );
                                      }
                                    }
                                  } else {
                                    // Fallback: just constrain to >= TOOLBAR_WIDTH and <= stage dimensions
                                    newX = Math.max(
                                      TOOLBAR_WIDTH,
                                      Math.min(stageWidth, newX),
                                    );
                                    newY = Math.max(
                                      0,
                                      Math.min(stageHeight, newY),
                                    );
                                  }
                                }

                                // CRITICAL: Apply off-canvas constraints when crossing to off-canvas
                                // This prevents elements from going beyond off-canvas screen when dragging from main
                                if (
                                  isOnMainCanvasStage &&
                                  !isOnTopLayerStage &&
                                  false &&
                                  isCrossingToOffCanvas &&
                                  offStage
                                ) {
                                  const stageWidth =
                                    offStage.width() || window.innerWidth;
                                  const stageHeight =
                                    offStage.height() || fixedStageHeightPx;

                                  // Get element from state to calculate bounding box
                                  let element = null;
                                  switch (initialPos.type) {
                                    case "Rect":
                                      element = rectangles.find(
                                        (r) => r.id === initialPos.id,
                                      );
                                      break;
                                    case "Circle":
                                      element = circles.find(
                                        (c) => c.id === initialPos.id,
                                      );
                                      break;
                                    case "Image":
                                      element = images.find(
                                        (img) => img.id === initialPos.id,
                                      );
                                      break;
                                    case "Text":
                                      element = texts.find(
                                        (t) => t.id === initialPos.id,
                                      );
                                      break;
                                    case "Line":
                                    case "Arrow":
                                    case "DoubleSidedArrow":
                                      element =
                                        lines.find(
                                          (l) => l.id === initialPos.id,
                                        ) ||
                                        arrows.find(
                                          (a) => a.id === initialPos.id,
                                        ) ||
                                        doubleSidedArrows.find(
                                          (a) => a.id === initialPos.id,
                                        );
                                      break;
                                  }

                                  if (element) {
                                    // Convert position from main canvas to off-canvas coordinates
                                    const mainBBox = mainStage
                                      .container()
                                      .getBoundingClientRect();
                                    const offBBox = offStage
                                      .container()
                                      .getBoundingClientRect();
                                    const absX = mainBBox.left + newX;
                                    const absY = mainBBox.top + newY;
                                    let offCanvasX = absX - offBBox.left;
                                    let offCanvasY = absY - offBBox.top;

                                    // Apply constraints in off-canvas coordinate system
                                    if (
                                      initialPos.type === "Image" ||
                                      initialPos.type === "Circle"
                                    ) {
                                      // Center coordinate elements
                                      const width =
                                        element.width ||
                                        (element.radius
                                          ? element.radius * 2
                                          : 0);
                                      const height =
                                        element.height ||
                                        (element.radius
                                          ? element.radius * 2
                                          : 0);
                                      const halfWidth = width / 2;
                                      const halfHeight = height / 2;

                                      offCanvasX = Math.max(
                                        TOOLBAR_WIDTH + halfWidth,
                                        Math.min(
                                          stageWidth - halfWidth,
                                          offCanvasX,
                                        ),
                                      );
                                      offCanvasY = Math.max(
                                        halfHeight,
                                        Math.min(
                                          stageHeight - halfHeight,
                                          offCanvasY,
                                        ),
                                      );
                                    } else if (
                                      initialPos.type === "Rect" ||
                                      initialPos.type === "Text"
                                    ) {
                                      // Top-left coordinate elements
                                      const width = element.width || 0;
                                      const height = element.height || 0;

                                      offCanvasX = Math.max(
                                        TOOLBAR_WIDTH,
                                        Math.min(
                                          stageWidth - width,
                                          offCanvasX,
                                        ),
                                      );
                                      offCanvasY = Math.max(
                                        0,
                                        Math.min(
                                          stageHeight - height,
                                          offCanvasY,
                                        ),
                                      );
                                    } else {
                                      // For lines/arrows and other elements
                                      offCanvasX = Math.max(
                                        TOOLBAR_WIDTH,
                                        Math.min(stageWidth, offCanvasX),
                                      );
                                      offCanvasY = Math.max(
                                        0,
                                        Math.min(stageHeight, offCanvasY),
                                      );
                                    }

                                    // Convert back to main canvas coordinates
                                    const constrainedAbsX =
                                      offBBox.left + offCanvasX;
                                    const constrainedAbsY =
                                      offBBox.top + offCanvasY;
                                    newX = constrainedAbsX - mainBBox.left;
                                    newY = constrainedAbsY - mainBBox.top;
                                  }
                                }

                                elementNode.x(newX);
                                elementNode.y(newY);
                              }
                            },
                          );

                          // Batch draw all stages at once for smooth animation
                          const mainStage = stageRef?.current;
                          const offStage = offCanvasStageRef?.current;
                          const topLayerStage = topLayerStageRef?.current;
                          if (mainStage) {
                            mainStage.getLayer()?.batchDraw();
                          }
                          if (offStage) {
                            offStage.getLayer()?.batchDraw();
                          }
                          if (topLayerStage) {
                            topLayerStage.getLayer()?.batchDraw();
                          }

                          // CRITICAL FIX: Do NOT update selectedObjects state during drag
                          // State updates cause React re-renders that desynchronize Konva nodes from visual state
                          // The selection box will stay in sync because getActualElement() reads from Konva nodes
                          // State arrays will be updated on onDragEnd when drag completes
                        }}
                        onDragEnd={(e) => {
                          const wrapperNode = e.target;
                          const finalX = wrapperNode.x();
                          const finalY = wrapperNode.y();

                          // Calculate group center in client coordinates to determine target canvas
                          const mainStage = stageRef?.current;
                          const offStage = offCanvasStageRef?.current;

                          if (
                            mainStage &&
                            offStage &&
                            mainCanvasObjects.length > 0
                          ) {
                            // Determine which canvas the group is currently on
                            let allFromMainCanvas = true;
                            let allFromOffCanvas = true;

                            mainCanvasObjects.forEach((obj) => {
                              if (!obj || !obj.id || !obj.type) return;

                              let isInMain = false;
                              let isInOff = false;

                              switch (obj.type) {
                                case "Rect":
                                  isInMain = rectangles.some(
                                    (r) => r.id === obj.id,
                                  );
                                  isInOff = offCanvasRectangles.some(
                                    (r) => r.id === obj.id,
                                  );
                                  break;
                                case "Circle":
                                  isInMain = circles.some(
                                    (c) => c.id === obj.id,
                                  );
                                  isInOff = offCanvasCircles.some(
                                    (c) => c.id === obj.id,
                                  );
                                  break;
                                case "Image":
                                  isInMain = images.some(
                                    (img) => img.id === obj.id,
                                  );
                                  isInOff = offCanvasImages.some(
                                    (img) => img.id === obj.id,
                                  );
                                  break;
                                case "Text":
                                  isInMain = texts.some((t) => t.id === obj.id);
                                  isInOff = offCanvasTexts.some(
                                    (t) => t.id === obj.id,
                                  );
                                  break;
                                case "Arrow":
                                  isInMain = arrows.some(
                                    (a) => a.id === obj.id,
                                  );
                                  isInOff = offCanvasArrows.some(
                                    (a) => a.id === obj.id,
                                  );
                                  break;
                                case "DoubleSidedArrow":
                                  isInMain = doubleSidedArrows.some(
                                    (a) => a.id === obj.id,
                                  );
                                  isInOff = offCanvasDoubleSidedArrows.some(
                                    (a) => a.id === obj.id,
                                  );
                                  break;
                                case "Line":
                                  isInMain = lines.some((l) => l.id === obj.id);
                                  isInOff = offCanvasLines.some(
                                    (l) => l.id === obj.id,
                                  );
                                  break;
                              }

                              if (isInMain) allFromOffCanvas = false;
                              if (isInOff) allFromMainCanvas = false;
                            });

                            const sourceCanvas = allFromOffCanvas
                              ? "offCanvas"
                              : "main";
                            const sourceStage =
                              sourceCanvas === "offCanvas"
                                ? offStage
                                : mainStage;
                            const sourceBBox = sourceStage
                              .container()
                              .getBoundingClientRect();

                            // Use mouse position if available for more accurate boundary detection
                            const mouseClientX = e.evt?.clientX;
                            const mouseClientY = e.evt?.clientY;

                            // Calculate group center - prefer mouse position if available
                            let groupCenterX, groupCenterY;
                            if (
                              mouseClientX !== null &&
                              mouseClientY !== null
                            ) {
                              // Use mouse position directly for accurate boundary detection
                              groupCenterX = mouseClientX;
                              groupCenterY = mouseClientY;
                            } else {
                              // Fallback to wrapper position calculation
                              groupCenterX =
                                sourceBBox.left +
                                finalX +
                                wrapperBBox.width / 2;
                              groupCenterY =
                                sourceBBox.top +
                                finalY +
                                wrapperBBox.height / 2;
                            }

                            // Determine target canvas - check if ANY element crosses boundary
                            const mainBBox = mainStage
                              .container()
                              .getBoundingClientRect();
                            const offBBox = offStage
                              .container()
                              .getBoundingClientRect();
                            const mainCanvasRight =
                              mainBBox.left + mainCanvasWidth;
                            const mainCanvasBottom =
                              mainBBox.top + fixedStageHeightPx;

                            // Check if ANY element in the group crosses the boundary
                            // Calculate delta from wrapper drag
                            const deltaX =
                              finalX - wrapperDragStartRef.current.x;
                            const deltaY =
                              finalY - wrapperDragStartRef.current.y;

                            // Check each element's position after drag
                            let shouldMoveGroup = false;
                            let targetCanvas = null;

                            for (const initialPos of wrapperDragStartRef.current
                              .elementPositions) {
                              // Calculate element's current position after drag
                              const elementCurrentX = initialPos.x + deltaX;
                              const elementCurrentY = initialPos.y + deltaY;

                              // Get element's bounding box to find its center or key point
                              let element = null;
                              // Find the actual element to get its properties
                              switch (initialPos.type) {
                                case "Rect":
                                  element = initialPos.isOnMainCanvas
                                    ? rectangles.find(
                                        (r) => r.id === initialPos.id,
                                      )
                                    : offCanvasRectangles.find(
                                        (r) => r.id === initialPos.id,
                                      );
                                  break;
                                case "Circle":
                                  element = initialPos.isOnMainCanvas
                                    ? circles.find(
                                        (c) => c.id === initialPos.id,
                                      )
                                    : offCanvasCircles.find(
                                        (c) => c.id === initialPos.id,
                                      );
                                  break;
                                case "Image":
                                  element = initialPos.isOnMainCanvas
                                    ? images.find(
                                        (img) => img.id === initialPos.id,
                                      )
                                    : offCanvasImages.find(
                                        (img) => img.id === initialPos.id,
                                      );
                                  break;
                                case "Text":
                                  element = initialPos.isOnMainCanvas
                                    ? texts.find((t) => t.id === initialPos.id)
                                    : offCanvasTexts.find(
                                        (t) => t.id === initialPos.id,
                                      );
                                  break;
                                case "Arrow":
                                  element = initialPos.isOnMainCanvas
                                    ? arrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      lines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow || hasEndArrow;
                                      })
                                    : offCanvasArrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      offCanvasLines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow || hasEndArrow;
                                      });
                                  break;
                                case "DoubleSidedArrow":
                                  element = initialPos.isOnMainCanvas
                                    ? doubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      lines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow && hasEndArrow;
                                      })
                                    : offCanvasDoubleSidedArrows.find(
                                        (a) => a.id === initialPos.id,
                                      ) ||
                                      offCanvasLines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        const hasStartArrow =
                                          l.hasStartArrow === true ||
                                          l.pointerAtBeginning === true;
                                        const hasEndArrow =
                                          l.hasEndArrow === true ||
                                          l.pointerAtEnd === true;
                                        return hasStartArrow && hasEndArrow;
                                      });
                                  break;
                                case "Line":
                                  element = initialPos.isOnMainCanvas
                                    ? lines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        return (
                                          !l.hasStartArrow &&
                                          !l.hasEndArrow &&
                                          !l.pointerAtBeginning &&
                                          !l.pointerAtEnd
                                        );
                                      })
                                    : offCanvasLines.find((l) => {
                                        if (l.id !== initialPos.id)
                                          return false;
                                        return (
                                          !l.hasStartArrow &&
                                          !l.hasEndArrow &&
                                          !l.pointerAtBeginning &&
                                          !l.pointerAtEnd
                                        );
                                      });
                                  break;
                              }

                              if (!element) continue;

                              // Calculate element's center or key point in client coordinates
                              const elementBBox = getElementBoundingBox(
                                {
                                  ...element,
                                  x: elementCurrentX,
                                  y: elementCurrentY,
                                },
                                initialPos.type,
                              );
                              const elementCenterX =
                                elementBBox.x + elementBBox.width / 2;
                              const elementCenterY =
                                elementBBox.y + elementBBox.height / 2;

                              // Convert to client coordinates
                              const elementClientX =
                                sourceBBox.left + elementCenterX;
                              const elementClientY =
                                sourceBBox.top + elementCenterY;

                              // Check if this element crosses the boundary
                              const elementTarget = determineTargetCanvas({
                                type: initialPos.type,
                                draggedElement: {
                                  x: elementCurrentX,
                                  y: elementCurrentY,
                                },
                                clientX: elementClientX,
                                clientY: elementClientY,
                                mainBBox,
                                offBBox,
                                mainCanvasRight,
                                mainCanvasBottom,
                                sourceStageRef: sourceStage,
                                mainStage,
                                offCanvasStage: offStage,
                                isFromMainCanvas: sourceCanvas === "main",
                              });

                              // If ANY element crosses the boundary, move the entire group
                              if (
                                elementTarget &&
                                elementTarget !== sourceCanvas
                              ) {
                                shouldMoveGroup = true;
                                targetCanvas = elementTarget;
                                break; // Found one element crossing, move entire group
                              }
                            }

                            // If any element crossed the boundary, move the entire group
                            if (shouldMoveGroup && targetCanvas) {
                              // CRITICAL: Clear group drag previews before moving elements
                              // This prevents showing both preview and actual elements
                              isGroupDraggingRef.current = false;
                              setGroupDragPreviews([]);
                              groupDragPreviewsRef.current = [];

                              // CRITICAL FIX: Build overridePositions map with current dragged positions
                              // This ensures elements are placed at their exact dragged positions in target canvas
                              const overridePositions = {};
                              const deltaX =
                                finalX - wrapperDragStartRef.current.x;
                              const deltaY =
                                finalY - wrapperDragStartRef.current.y;

                              wrapperDragStartRef.current.elementPositions.forEach(
                                (initialPos) => {
                                  // Calculate element's current position after drag in source canvas coordinates
                                  const currentX = initialPos.x + deltaX;
                                  const currentY = initialPos.y + deltaY;

                                  // Store in overridePositions for handleCopyGroupBetweenCanvases
                                  overridePositions[initialPos.id] = {
                                    x: currentX,
                                    y: currentY,
                                  };
                                },
                              );

                              // Pass wrapper's final position, mouse position, AND override positions
                              handleCopyGroupBetweenCanvases(
                                finalX,
                                finalY,
                                mouseClientX,
                                mouseClientY,
                                overridePositions,
                              );
                              // Reset drag start reference
                              wrapperDragStartRef.current = {
                                x: 0,
                                y: 0,
                                elementPositions: [],
                              };
                              return;
                            }
                          }

                          // Update state arrays with final positions from Konva nodes
                          // This ensures state is synchronized with the actual rendered positions
                          // (mainStage and offStage are already declared at the start of onDragEnd)

                          // Collect updated positions for each element type
                          const updatedRectangles = [];
                          const updatedCircles = [];
                          const updatedTexts = [];
                          const updatedImages = [];
                          const updatedArrows = [];
                          const updatedDoubleSidedArrows = [];
                          const updatedLines = [];

                          // CRITICAL FIX: Use wrapperDragStartRef.current.elementPositions instead of mainCanvasObjects
                          // mainCanvasObjects filters out Text elements, but we need to update ALL dragged elements including Text
                          // wrapperDragStartRef.current.elementPositions contains all elements that were dragged
                          const allDraggedElements =
                            wrapperDragStartRef.current?.elementPositions || [];

                          // Update each selected element's position in state
                          allDraggedElements.forEach((initialPos) => {
                            // Convert initialPos to obj format for compatibility
                            const obj = {
                              id: initialPos.id,
                              type: initialPos.type,
                            };
                            if (!obj || !obj.id || !obj.type) return;

                            // CRITICAL FIX: For top-layer images, check topLayerStage FIRST
                            const topLayerStage = topLayerStageRef?.current;
                            const isTopLayerImage =
                              obj.type === "Image" &&
                              images.some(
                                (img) =>
                                  img.id === obj.id &&
                                  img.isOnTopLayer === true,
                              );

                            // Get the Konva node to read its final position
                            let node = null;
                            if (isTopLayerImage && topLayerStage) {
                              node = topLayerStage.findOne(`#${obj.id}`);
                            }
                            if (!node) {
                              node = mainStage?.findOne(`#${obj.id}`);
                            }
                            if (!node) {
                              node = offStage?.findOne(`#${obj.id}`);
                            }

                            if (!node) return; // Element not found in any stage

                            const finalX = node.x();
                            const finalY = node.y();

                            // Find the element in state arrays and update it
                            switch (obj.type) {
                              case "Rect": {
                                const rect =
                                  rectangles.find((r) => r.id === obj.id) ||
                                  offCanvasRectangles.find(
                                    (r) => r.id === obj.id,
                                  );
                                if (rect) {
                                  updatedRectangles.push({
                                    ...rect,
                                    x: finalX,
                                    y: finalY,
                                  });
                                }
                                break;
                              }
                              case "Circle": {
                                const circle =
                                  circles.find((c) => c.id === obj.id) ||
                                  offCanvasCircles.find((c) => c.id === obj.id);
                                if (circle) {
                                  updatedCircles.push({
                                    ...circle,
                                    x: finalX,
                                    y: finalY,
                                  });
                                }
                                break;
                              }
                              case "Image": {
                                const image =
                                  images.find((img) => img.id === obj.id) ||
                                  offCanvasImages.find(
                                    (img) => img.id === obj.id,
                                  );
                                if (image) {
                                  // CRITICAL FIX: For top-layer images, only update main canvas images array
                                  // Don't update offCanvasImages array for top-layer images
                                  if (isTopLayerImage) {
                                    updatedImages.push({
                                      ...image,
                                      x: finalX,
                                      y: finalY,
                                    });
                                  } else {
                                    updatedImages.push({
                                      ...image,
                                      x: finalX,
                                      y: finalY,
                                    });
                                  }
                                }
                                break;
                              }
                              case "Text": {
                                const text =
                                  texts.find((t) => t.id === obj.id) ||
                                  offCanvasTexts.find((t) => t.id === obj.id);
                                if (text) {
                                  updatedTexts.push({
                                    ...text,
                                    x: finalX,
                                    y: finalY,
                                  });
                                }
                                break;
                              }
                              case "Arrow": {
                                const arrow =
                                  lines.find((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow || hasEndArrow;
                                  }) ||
                                  offCanvasLines.find((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow || hasEndArrow;
                                  }) ||
                                  arrows.find((a) => a.id === obj.id) ||
                                  offCanvasArrows.find((a) => a.id === obj.id);
                                if (arrow) {
                                  updatedArrows.push({
                                    ...arrow,
                                    x: finalX,
                                    y: finalY,
                                  });
                                }
                                break;
                              }
                              case "DoubleSidedArrow": {
                                const arrow =
                                  doubleSidedArrows.find(
                                    (a) => a.id === obj.id,
                                  ) ||
                                  offCanvasDoubleSidedArrows.find(
                                    (a) => a.id === obj.id,
                                  ) ||
                                  lines.find((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow && hasEndArrow;
                                  }) ||
                                  offCanvasLines.find((l) => {
                                    if (l.id !== obj.id) return false;
                                    const hasStartArrow =
                                      l.hasStartArrow === true ||
                                      l.pointerAtBeginning === true;
                                    const hasEndArrow =
                                      l.hasEndArrow === true ||
                                      l.pointerAtEnd === true;
                                    return hasStartArrow && hasEndArrow;
                                  });
                                if (arrow) {
                                  updatedDoubleSidedArrows.push({
                                    ...arrow,
                                    x: finalX,
                                    y: finalY,
                                  });
                                }
                                break;
                              }
                              case "Line": {
                                const linePure =
                                  lines.find((l) => {
                                    if (l.id !== obj.id) return false;
                                    return (
                                      !l.hasStartArrow &&
                                      !l.hasEndArrow &&
                                      !l.pointerAtBeginning &&
                                      !l.pointerAtEnd
                                    );
                                  }) ||
                                  offCanvasLines.find((l) => {
                                    if (l.id !== obj.id) return false;
                                    return (
                                      !l.hasStartArrow &&
                                      !l.hasEndArrow &&
                                      !l.pointerAtBeginning &&
                                      !l.pointerAtEnd
                                    );
                                  });
                                const lineAny =
                                  linePure ||
                                  lines.find((l) => l.id === obj.id) ||
                                  offCanvasLines.find((l) => l.id === obj.id);
                                if (lineAny) {
                                  const hasStartArrow =
                                    lineAny.hasStartArrow === true ||
                                    lineAny.pointerAtBeginning === true;
                                  const hasEndArrow =
                                    lineAny.hasEndArrow === true ||
                                    lineAny.pointerAtEnd === true;
                                  const updated = {
                                    ...lineAny,
                                    x: finalX,
                                    y: finalY,
                                  };
                                  if (hasStartArrow && hasEndArrow) {
                                    updatedDoubleSidedArrows.push(updated);
                                  } else if (hasStartArrow || hasEndArrow) {
                                    updatedArrows.push(updated);
                                  } else {
                                    updatedLines.push(updated);
                                  }
                                }
                                break;
                              }
                            }
                          });

                          // Update state arrays with the collected updated elements
                          if (updatedRectangles.length > 0) {
                            setRectangles((prev) => {
                              const updated = prev.map((r) => {
                                const updatedRect = updatedRectangles.find(
                                  (ur) => ur.id === r.id,
                                );
                                return updatedRect || r;
                              });
                              return updated;
                            });
                            setOffCanvasRectangles((prev) => {
                              const updated = prev.map((r) => {
                                const updatedRect = updatedRectangles.find(
                                  (ur) => ur.id === r.id,
                                );
                                return updatedRect || r;
                              });
                              return updated;
                            });
                          }

                          if (updatedCircles.length > 0) {
                            setCircles((prev) => {
                              const updated = prev.map((c) => {
                                const updatedCircle = updatedCircles.find(
                                  (uc) => uc.id === c.id,
                                );
                                return updatedCircle || c;
                              });
                              return updated;
                            });
                            setOffCanvasCircles((prev) => {
                              const updated = prev.map((c) => {
                                const updatedCircle = updatedCircles.find(
                                  (uc) => uc.id === c.id,
                                );
                                return updatedCircle || c;
                              });
                              return updated;
                            });
                          }

                          if (updatedTexts.length > 0) {
                            setTexts((prev) => {
                              const updated = prev.map((t) => {
                                const updatedText = updatedTexts.find(
                                  (ut) => ut.id === t.id,
                                );
                                return updatedText || t;
                              });
                              return updated;
                            });
                            setOffCanvasTexts((prev) => {
                              const updated = prev.map((t) => {
                                const updatedText = updatedTexts.find(
                                  (ut) => ut.id === t.id,
                                );
                                return updatedText || t;
                              });
                              return updated;
                            });
                          }

                          if (updatedImages.length > 0) {
                            setImages((prev) => {
                              const updated = prev.map((img) => {
                                const updatedImage = updatedImages.find(
                                  (ui) => ui.id === img.id,
                                );
                                if (updatedImage) {
                                }
                                return updatedImage || img;
                              });
                              return updated;
                            });
                            // CRITICAL FIX: Only update offCanvasImages for non-top-layer images
                            setOffCanvasImages((prev) => {
                              const updated = prev.map((img) => {
                                // Skip top-layer images - they're in main canvas images array
                                const isTopLayer = images.some(
                                  (i) =>
                                    i.id === img.id && i.isOnTopLayer === true,
                                );
                                if (isTopLayer) return img;

                                const updatedImage = updatedImages.find(
                                  (ui) => ui.id === img.id,
                                );
                                return updatedImage || img;
                              });
                              return updated;
                            });
                          }

                          if (updatedArrows.length > 0) {
                            setArrows((prev) => {
                              const updated = prev.map((a) => {
                                const updatedArrow = updatedArrows.find(
                                  (ua) => ua.id === a.id,
                                );
                                return updatedArrow || a;
                              });
                              return updated;
                            });
                            setOffCanvasArrows((prev) => {
                              const updated = prev.map((a) => {
                                const updatedArrow = updatedArrows.find(
                                  (ua) => ua.id === a.id,
                                );
                                return updatedArrow || a;
                              });
                              return updated;
                            });
                            // Also update lines array for unified arrows
                            setLines((prev) => {
                              const updated = prev.map((l) => {
                                const updatedArrow = updatedArrows.find(
                                  (ua) => ua.id === l.id,
                                );
                                return updatedArrow || l;
                              });
                              return updated;
                            });
                            setOffCanvasLines((prev) => {
                              const updated = prev.map((l) => {
                                const updatedArrow = updatedArrows.find(
                                  (ua) => ua.id === l.id,
                                );
                                return updatedArrow || l;
                              });
                              return updated;
                            });
                          }

                          if (updatedDoubleSidedArrows.length > 0) {
                            setDoubleSidedArrows((prev) => {
                              const updated = prev.map((a) => {
                                const updatedArrow =
                                  updatedDoubleSidedArrows.find(
                                    (ua) => ua.id === a.id,
                                  );
                                return updatedArrow || a;
                              });
                              return updated;
                            });
                            setOffCanvasDoubleSidedArrows((prev) => {
                              const updated = prev.map((a) => {
                                const updatedArrow =
                                  updatedDoubleSidedArrows.find(
                                    (ua) => ua.id === a.id,
                                  );
                                return updatedArrow || a;
                              });
                              return updated;
                            });
                            // Also update lines array for unified double-sided arrows
                            setLines((prev) => {
                              const updated = prev.map((l) => {
                                const updatedArrow =
                                  updatedDoubleSidedArrows.find(
                                    (ua) => ua.id === l.id,
                                  );
                                return updatedArrow || l;
                              });
                              return updated;
                            });
                            setOffCanvasLines((prev) => {
                              const updated = prev.map((l) => {
                                const updatedArrow =
                                  updatedDoubleSidedArrows.find(
                                    (ua) => ua.id === l.id,
                                  );
                                return updatedArrow || l;
                              });
                              return updated;
                            });
                          }

                          if (updatedLines.length > 0) {
                            setLines((prev) => {
                              const updated = prev.map((l) => {
                                const updatedLine = updatedLines.find(
                                  (ul) => ul.id === l.id,
                                );
                                return updatedLine || l;
                              });
                              return updated;
                            });
                            setOffCanvasLines((prev) => {
                              const updated = prev.map((l) => {
                                const updatedLine = updatedLines.find(
                                  (ul) => ul.id === l.id,
                                );
                                return updatedLine || l;
                              });
                              return updated;
                            });
                          }

                          // Update selectedObjects with final positions
                          // CRITICAL FIX: Check topLayerStage for top-layer images
                          const topLayerStage = topLayerStageRef?.current;
                          setSelectedObjects((prev) =>
                            prev.map((obj) => {
                              // CRITICAL FIX: For top-layer images, check topLayerStage FIRST
                              const isTopLayerImage =
                                obj.type === "Image" &&
                                images.some(
                                  (img) =>
                                    img.id === obj.id &&
                                    img.isOnTopLayer === true,
                                );

                              let node = null;
                              if (isTopLayerImage && topLayerStage) {
                                node = topLayerStage.findOne(`#${obj.id}`);
                              }
                              if (!node) {
                                node = mainStage?.findOne(`#${obj.id}`);
                              }
                              if (!node) {
                                node = offStage?.findOne(`#${obj.id}`);
                              }

                              if (node) {
                                const newX = node.x();
                                const newY = node.y();
                                if (isTopLayerImage) {
                                }
                                return {
                                  ...obj,
                                  x: newX,
                                  y: newY,
                                };
                              }
                              return obj;
                            }),
                          );

                          // CRITICAL FIX: Check if elements need to be transferred between canvases on drag end
                          // This handles the case where arrows are filtered out during drag and onDragMove doesn't detect the transfer
                          const deltaX = finalX - wrapperDragStartRef.current.x;
                          const deltaY = finalY - wrapperDragStartRef.current.y;

                          // Check if any element crossed the boundary
                          if (
                            wrapperDragStartRef.current.elementPositions
                              .length > 0
                          ) {
                            if (mainStage && offStage) {
                              const mainBBox = mainStage
                                .container()
                                .getBoundingClientRect();
                              const offBBox = offStage
                                .container()
                                .getBoundingClientRect();
                              const mainCanvasRight =
                                mainBBox.left + mainBBox.width;
                              const mainCanvasBottom =
                                mainBBox.top + fixedStageHeightPx;

                              // Determine source canvas from first element
                              const firstElement =
                                wrapperDragStartRef.current.elementPositions[0];
                              const sourceCanvas = firstElement.isOnMainCanvas
                                ? "main"
                                : "offCanvas";
                              const sourceStage =
                                sourceCanvas === "main" ? mainStage : offStage;
                              const sourceBBox =
                                sourceCanvas === "main" ? mainBBox : offBBox;

                              let shouldMoveGroup = false;
                              let targetCanvas = null;

                              // Check each element to see if it crossed the boundary
                              for (const initialPos of wrapperDragStartRef
                                .current.elementPositions) {
                                const elementCurrentX = initialPos.x + deltaX;
                                const elementCurrentY = initialPos.y + deltaY;

                                // Get element from state arrays
                                let element = null;
                                switch (initialPos.type) {
                                  case "Rect":
                                    element = initialPos.isOnMainCanvas
                                      ? rectangles.find(
                                          (r) => r.id === initialPos.id,
                                        )
                                      : offCanvasRectangles.find(
                                          (r) => r.id === initialPos.id,
                                        );
                                    break;
                                  case "Circle":
                                    element = initialPos.isOnMainCanvas
                                      ? circles.find(
                                          (c) => c.id === initialPos.id,
                                        )
                                      : offCanvasCircles.find(
                                          (c) => c.id === initialPos.id,
                                        );
                                    break;
                                  case "Image":
                                    element = initialPos.isOnMainCanvas
                                      ? images.find(
                                          (img) => img.id === initialPos.id,
                                        )
                                      : offCanvasImages.find(
                                          (img) => img.id === initialPos.id,
                                        );
                                    break;
                                  case "Text":
                                    element = initialPos.isOnMainCanvas
                                      ? texts.find(
                                          (t) => t.id === initialPos.id,
                                        )
                                      : offCanvasTexts.find(
                                          (t) => t.id === initialPos.id,
                                        );
                                    break;
                                  case "Arrow":
                                    element = initialPos.isOnMainCanvas
                                      ? lines.find((l) => {
                                          if (l.id !== initialPos.id)
                                            return false;
                                          const hasStartArrow =
                                            l.hasStartArrow === true ||
                                            l.pointerAtBeginning === true;
                                          const hasEndArrow =
                                            l.hasEndArrow === true ||
                                            l.pointerAtEnd === true;
                                          return hasStartArrow || hasEndArrow;
                                        }) ||
                                        arrows.find(
                                          (a) => a.id === initialPos.id,
                                        )
                                      : offCanvasLines.find((l) => {
                                          if (l.id !== initialPos.id)
                                            return false;
                                          const hasStartArrow =
                                            l.hasStartArrow === true ||
                                            l.pointerAtBeginning === true;
                                          const hasEndArrow =
                                            l.hasEndArrow === true ||
                                            l.pointerAtEnd === true;
                                          return hasStartArrow || hasEndArrow;
                                        }) ||
                                        offCanvasArrows.find(
                                          (a) => a.id === initialPos.id,
                                        );
                                    break;
                                  case "DoubleSidedArrow":
                                    element = initialPos.isOnMainCanvas
                                      ? lines.find((l) => {
                                          if (l.id !== initialPos.id)
                                            return false;
                                          const hasStartArrow =
                                            l.hasStartArrow === true ||
                                            l.pointerAtBeginning === true;
                                          const hasEndArrow =
                                            l.hasEndArrow === true ||
                                            l.pointerAtEnd === true;
                                          return hasStartArrow && hasEndArrow;
                                        }) ||
                                        doubleSidedArrows.find(
                                          (a) => a.id === initialPos.id,
                                        )
                                      : offCanvasLines.find((l) => {
                                          if (l.id !== initialPos.id)
                                            return false;
                                          const hasStartArrow =
                                            l.hasStartArrow === true ||
                                            l.pointerAtBeginning === true;
                                          const hasEndArrow =
                                            l.hasEndArrow === true ||
                                            l.pointerAtEnd === true;
                                          return hasStartArrow && hasEndArrow;
                                        }) ||
                                        offCanvasDoubleSidedArrows.find(
                                          (a) => a.id === initialPos.id,
                                        );
                                    break;
                                  case "Line":
                                    element = initialPos.isOnMainCanvas
                                      ? lines.find((l) => {
                                          if (l.id !== initialPos.id)
                                            return false;
                                          return (
                                            !l.hasStartArrow &&
                                            !l.hasEndArrow &&
                                            !l.pointerAtBeginning &&
                                            !l.pointerAtEnd
                                          );
                                        })
                                      : offCanvasLines.find((l) => {
                                          if (l.id !== initialPos.id)
                                            return false;
                                          return (
                                            !l.hasStartArrow &&
                                            !l.hasEndArrow &&
                                            !l.pointerAtBeginning &&
                                            !l.pointerAtEnd
                                          );
                                        });
                                    break;
                                }

                                if (!element) continue;

                                // Calculate element's center in client coordinates
                                const elementBBox = getElementBoundingBox(
                                  {
                                    ...element,
                                    x: elementCurrentX,
                                    y: elementCurrentY,
                                  },
                                  initialPos.type,
                                );
                                const elementCenterX =
                                  elementBBox.x + elementBBox.width / 2;
                                const elementCenterY =
                                  elementBBox.y + elementBBox.height / 2;
                                const elementClientX =
                                  sourceBBox.left + elementCenterX;
                                const elementClientY =
                                  sourceBBox.top + elementCenterY;

                                // Check if this element crosses the boundary
                                const elementTarget = determineTargetCanvas({
                                  type: initialPos.type,
                                  draggedElement: {
                                    x: elementCurrentX,
                                    y: elementCurrentY,
                                  },
                                  clientX: elementClientX,
                                  clientY: elementClientY,
                                  mainBBox,
                                  offBBox,
                                  mainCanvasRight,
                                  mainCanvasBottom,
                                  sourceStageRef: sourceStage,
                                  mainStage,
                                  offCanvasStage: offStage,
                                  isFromMainCanvas: sourceCanvas === "main",
                                });

                                // If ANY element crosses the boundary, move the entire group
                                if (
                                  elementTarget &&
                                  elementTarget !== sourceCanvas
                                ) {
                                  shouldMoveGroup = true;
                                  targetCanvas = elementTarget;
                                  break;
                                }
                              }

                              // If any element crossed the boundary, move the entire group
                              if (shouldMoveGroup && targetCanvas) {
                                // Clear group drag state before moving
                                isGroupDraggingRef.current = false;
                                setGroupDragPreviews([]);
                                groupDragPreviewsRef.current = [];

                                // Calculate overridePositions
                                const overridePositions = {};
                                for (const initialPos of wrapperDragStartRef
                                  .current.elementPositions) {
                                  overridePositions[initialPos.id] = {
                                    x: initialPos.x + deltaX,
                                    y: initialPos.y + deltaY,
                                  };
                                }

                                // Get mouse position
                                const mouseClientX =
                                  globalMousePositionRef.current.x;
                                const mouseClientY =
                                  globalMousePositionRef.current.y;

                                handleCopyGroupBetweenCanvases(
                                  finalX,
                                  finalY,
                                  mouseClientX,
                                  mouseClientY,
                                  overridePositions,
                                );

                                wrapperDragStartRef.current = {
                                  x: 0,
                                  y: 0,
                                  elementPositions: [],
                                };
                                return;
                              }
                            }
                          }

                          // Save to history after drag ends
                          // Always run sync/clamp on wrapper drag-end, even when history save is disabled.
                          syncGroupDragPositionsToState();
                          if (saveToHistory) {
                            saveToHistory();
                          }
                          // Mark that we just dragged (to prevent onClick from selecting elements)
                          wrapperJustDraggedRef.current = true;
                          // Clear the flag after a short delay
                          setTimeout(() => {
                            wrapperJustDraggedRef.current = false;
                          }, 200);
                          // Clear individual element drag ref when wrapper drag ends
                          individualElementDragStartRef.current = {
                            elementPositions: [],
                          };
                          // CRITICAL: Clear group drag previews on drag end
                          // This prevents showing both preview and actual elements
                          isGroupDraggingRef.current = false;
                          groupDragSourceStageRef.current = null;
                          setGroupDragPreviews([]);
                          groupDragPreviewsRef.current = [];
                          // Reset drag start reference
                          wrapperDragStartRef.current = {
                            x: 0,
                            y: 0,
                            elementPositions: [],
                          };
                        }}
                        onClick={(e) => {
                          // If we just dragged, ignore the click (it's a drag end, not a click)
                          if (wrapperJustDraggedRef.current) {
                            return;
                          }

                          // Disable selection if a tool is locked
                          if (lockedAction) {
                            return;
                          }

                          // Get pointer position to find elements beneath the wrapper
                          const stage = stageRef.current;
                          if (!stage) return;

                          stage.setPointersPositions(e.evt);
                          const pointerPos = stage.getPointerPosition();
                          if (!pointerPos) return;

                          // Helper function to check if a point is inside an element's bounding box
                          const isPointInElement = (
                            element,
                            elementType,
                            point,
                          ) => {
                            const bbox = getElementBoundingBox(
                              element,
                              elementType,
                            );
                            return (
                              point.x >= bbox.x &&
                              point.x <= bbox.x + bbox.width &&
                              point.y >= bbox.y &&
                              point.y <= bbox.y + bbox.height
                            );
                          };

                          // Check all elements to find which one is at the click position
                          // Check in reverse z-order (topmost first) to match rendering order:
                          // Layer 4: Text (topmost, same layer as wrapper)
                          // Layer 2: Shapes (Lines, Arrows, Circles, Rectangles)
                          // Layer 1: Images (bottom)
                          // We check in reverse so we find the topmost element first
                          const mainLineIds = new Set(lines.map((l) => l.id));
                          const allElements = [
                            // Text is on the same layer as wrapper, so check it first
                            ...texts.map((t) => ({ ...t, type: "Text" })),
                            // Then shapes (unified strokes in `lines` use Arrow/DoubleSidedArrow type in selection state)
                            ...lines.map((l) => ({
                              ...l,
                              type: unifiedLineLikeToSelectionType(l),
                            })),
                            ...arrows
                              .filter((a) => !mainLineIds.has(a.id))
                              .map((a) => ({ ...a, type: "Arrow" })),
                            ...doubleSidedArrows
                              .filter((a) => !mainLineIds.has(a.id))
                              .map((a) => ({
                                ...a,
                                type: "DoubleSidedArrow",
                              })),
                            ...circles.map((c) => ({ ...c, type: "Circle" })),
                            ...rectangles.map((r) => ({ ...r, type: "Rect" })),
                            // Images are on the bottom layer
                            ...images.map((img) => ({ ...img, type: "Image" })),
                          ];

                          // Helper: is element (id, type) on off-canvas (used for cross-canvas multi-select guard)
                          const isElementInOffCanvas = (
                            elementId,
                            elementType,
                          ) => {
                            if (!elementId || !elementType) return false;
                            switch (elementType) {
                              case "Rect":
                                return offCanvasRectangles.some(
                                  (r) => r.id === elementId,
                                );
                              case "Circle":
                                return offCanvasCircles.some(
                                  (c) => c.id === elementId,
                                );
                              case "Image":
                                return offCanvasImages.some(
                                  (i) => i.id === elementId,
                                );
                              case "Text":
                                return offCanvasTexts.some(
                                  (t) => t.id === elementId,
                                );
                              case "Arrow":
                                return offCanvasArrows.some(
                                  (a) => a.id === elementId,
                                );
                              case "Line":
                                return offCanvasLines.some(
                                  (l) => l.id === elementId,
                                );
                              case "DoubleSidedArrow":
                                return offCanvasDoubleSidedArrows.some(
                                  (d) => d.id === elementId,
                                );
                              default:
                                return false;
                            }
                          };

                          // Find topmost clicked element (selected or not)
                          const clickedElement = allElements.find((element) =>
                            isPointInElement(element, element.type, pointerPos),
                          );
                          if (!clickedElement) return;

                          const currentSelection = [...(selectedObjects || [])];
                          if (
                            selectedObject &&
                            !currentSelection.some(
                              (obj) =>
                                obj.id === selectedObject.id &&
                                obj.type === selectedObject.type,
                            )
                          ) {
                            currentSelection.push(selectedObject);
                          }

                          const isClickedSelected = currentSelection.some(
                            (obj) =>
                              obj.id === clickedElement.id &&
                              obj.type === clickedElement.type,
                          );

                          if (isMultiSelectMode && isClickedSelected) {
                            cancelActiveGroupDrag();
                            const remainingSelection = currentSelection.filter(
                              (obj) =>
                                !(
                                  obj.id === clickedElement.id &&
                                  obj.type === clickedElement.type
                                ),
                            );
                            const nextPrimary = remainingSelection[0] || null;

                            setSelectedObjects(remainingSelection);
                            setSelectedObject(nextPrimary);
                            if (setSelectedImage) {
                              setSelectedImage(
                                nextPrimary?.type === "Image"
                                  ? nextPrimary
                                  : null,
                              );
                            }
                            if (setSelectedText) {
                              setSelectedText(
                                nextPrimary?.type === "Text"
                                  ? nextPrimary
                                  : null,
                              );
                            }
                            if (setSelectedTextId) {
                              setSelectedTextId(
                                nextPrimary?.type === "Text"
                                  ? nextPrimary.id
                                  : null,
                              );
                            }
                            if (setShowTextBox) {
                              setShowTextBox(nextPrimary?.type === "Text");
                            }
                            if (setSelectedArrow) {
                              setSelectedArrow(
                                nextPrimary?.type === "Arrow"
                                  ? nextPrimary
                                  : null,
                              );
                            }
                            if (setSelectedLine) {
                              setSelectedLine(
                                nextPrimary?.type === "Line"
                                  ? nextPrimary
                                  : null,
                              );
                            }
                            if (setSelectedDoubleSidedArrow) {
                              setSelectedDoubleSidedArrow(
                                nextPrimary?.type === "DoubleSidedArrow"
                                  ? nextPrimary
                                  : null,
                              );
                            }

                            stopKonvaDragUnderPointer(stage, pointerPos);
                            e.evt?.preventDefault?.();
                            e.evt?.stopPropagation?.();
                            return;
                          }

                          if (isClickedSelected) return;

                          const foundElement = clickedElement;

                          // Cross-canvas multi-select: when multi-select is on, only allow elements from the same canvas
                          if (
                            isMultiSelectMode &&
                            (selectedObjects?.length > 0 || selectedObject)
                          ) {
                            const allSelected = [...(selectedObjects || [])];
                            if (
                              selectedObject &&
                              !allSelected.some(
                                (o) =>
                                  o.id === selectedObject.id &&
                                  o.type === selectedObject.type,
                              )
                            ) {
                              allSelected.push(selectedObject);
                            }
                            const anyFromOffCanvas = allSelected.some((obj) =>
                              isElementInOffCanvas(obj.id, obj.type),
                            );
                            if (anyFromOffCanvas) {
                            // In normal selection mode, switching stages should reset selection
                            // so main/off stage clicks behave symmetrically.
                            const cloneActive =
                              isCloneMode ||
                              lockedAction === ACTIONS.SELECT ||
                              cloneTemplate !== null ||
                              (cloneTemplates && cloneTemplates.length > 0);

                            if (!cloneActive) {
                              if (setSelectedObjects) setSelectedObjects([]);
                              if (setSelectedObject) setSelectedObject(null);
                              if (setSelectedImage) setSelectedImage(null);
                              if (setSelectedText) setSelectedText(null);
                              if (setSelectedTextId) setSelectedTextId(null);
                              if (setShowTextBox) setShowTextBox(false);
                              if (setSelectedArrow) setSelectedArrow(null);
                              if (setSelectedLine) setSelectedLine(null);
                              if (setSelectedDoubleSidedArrow)
                                setSelectedDoubleSidedArrow(null);

                              transformerRef?.current?.nodes?.([]);
                            }

                            // Continue: below code will select the clicked main element.
                            }
                          }

                          // Select the found element
                          setSelectedObject(foundElement);

                          // Set type-specific selected state
                          if (
                            foundElement.type === "Image" &&
                            setSelectedImage
                          ) {
                            setSelectedImage(foundElement);
                          } else if (foundElement.type === "Text") {
                            if (setSelectedTextId)
                              setSelectedTextId(foundElement.id);
                            if (setSelectedText) setSelectedText(foundElement);
                            if (setShowTextBox) setShowTextBox(true);
                          } else if (
                            foundElement.type === "Arrow" &&
                            setSelectedArrow
                          ) {
                            setSelectedArrow(foundElement);
                          } else if (
                            foundElement.type === "Line" &&
                            setSelectedLine
                          ) {
                            setSelectedLine(foundElement);
                          }

                          // Update selectedObjects
                          if (isMultiSelectMode && setSelectedObjects) {
                            setSelectedObjects((prev) => {
                              const exists = prev.some(
                                (obj) =>
                                  obj.id === foundElement.id &&
                                  obj.type === foundElement.type,
                              );
                              if (exists) return prev;
                              return [...prev, foundElement];
                            });
                          } else if (setSelectedObjects) {
                            setSelectedObjects([foundElement]);
                          }
                        }}
                      />
                    )}

                    {/* Individual highlight rectangles removed - only wrapper box is shown */}
                  </>
                );
              })()}
            </Layer>
          )}

          {/* Layer 5: Selected elements (renders above all other elements with full opacity) */}
          {parseInt(canvasHeight) >= 5 &&
            (selectedObject ||
              (selectedObjects && selectedObjects.length > 0)) &&
            (() => {
              // Render all elements from selectedObjects array (not just selectedObject)
              // This ensures all multi-selected elements are rendered with full opacity on top layer
              const elementsToRender = [];
              // Rendering full TextElement in Layer 5 while dragging is expensive (extra HTML + stage work)
              // and causes visible lag. Keep Layer 5 text elevation only when not actively dragging.
              const isSelectedTextActivelyDragging = (() => {
                const textId =
                  selectedText?.id ||
                  (selectedObject?.type === "Text" ? selectedObject?.id : null);
                if (!textId || !stageRef?.current) return false;
                const node = stageRef.current.findOne(`#${textId}`);
                return Boolean(
                  node &&
                  ((typeof node.isDragging === "function" &&
                    node.isDragging()) ||
                    node.getAttr?.("_isHtmlDragging") ||
                    node.getAttr?.("_isDragging")),
                );
              })();

              if (selectedObjects && selectedObjects.length > 0) {
                // Add all elements from selectedObjects
                selectedObjects.forEach((obj) => {
                  // CRITICAL: If this object matches selectedObject by ID, use selectedObject instead
                  // This ensures we have the latest values even if selectedObjects array has stale data
                  const effectiveObj =
                    selectedObject && selectedObject.id === obj.id
                      ? selectedObject
                      : obj;

                  // CRITICAL FIX: Skip top-layer images unless they're demoted for selection
                  // Demoted top-layer images have _forceOcclusionBlocker: true and should render in Layer 5
                  if (effectiveObj.type === "Image") {
                    const selectedImage = renderedMainImages.find(
                      (img) => img.id === effectiveObj.id,
                    );
                    if (
                      selectedImage &&
                      selectedImage.isOnTopLayer === true &&
                      selectedImage._forceOcclusionBlocker !== true
                    ) {
                      return; // Skip this image - don't add it to elementsToRender
                    }
                  }

                  // CRITICAL FIX: Usually skip Text in selected layer for single selection
                  // (text remains in main layer to avoid stale-copy ghost during drag).
                  // EXCEPTION: When occlusion images are active, we MUST render selected text
                  // in Layer 5 so selected text + selection rect stay above demoted top-layer images.
                  if (
                    (effectiveObj.type === "Text" ||
                      effectiveObj.type?.includes("Text")) &&
                    (!selectedObjects || selectedObjects.length <= 1) &&
                    (selectionOcclusionImages.length === 0 ||
                      isSelectedTextActivelyDragging)
                  ) {
                    return; // Skip - render only in main layer to avoid ghost at old position during drag
                  }

                  const { isInMainCanvas, isInOffCanvas } = findElementCanvas({
                    selectedObject: effectiveObj,
                    rectangles,
                    circles,
                    images,
                    texts,
                    arrows,
                    lines,
                    doubleSidedArrows,
                    offCanvasRectangles,
                    offCanvasCircles,
                    offCanvasImages,
                    offCanvasTexts,
                    offCanvasArrows,
                    offCanvasLines,
                    offCanvasDoubleSidedArrows,
                  });

                  // CRITICAL: For unified elements (Arrow, Line, DoubleSidedArrow), always render when selected
                  // so they appear on the topmost layer (Layer 5) and stay visible when dragging off→main.
                  const isUnifiedElementType =
                    effectiveObj.type === "Arrow" ||
                    effectiveObj.type === "Line" ||
                    effectiveObj.type === "DoubleSidedArrow" ||
                    effectiveObj.hasStartArrow !== undefined ||
                    effectiveObj.hasEndArrow !== undefined;

                  // Include main-canvas elements; also include Line/Arrow/DoubleSidedArrow even when from off-canvas
                  // so selected line is always on top and visible during off→main drag (match circles: top layer).
                  const willAdd = isInMainCanvas || isUnifiedElementType;

                  if (willAdd) {
                    elementsToRender.push(effectiveObj);
                  }
                });
              } else if (selectedObject) {
                // Fallback to selectedObject if selectedObjects is empty

                // CRITICAL FIX: Skip top-layer images - they should only be rendered in the top layer, not in selected layer
                if (selectedObject?.type === "Image") {
                  const selectedImage =
                    images.find((img) => img.id === selectedObject.id) ||
                    renderedMainImages.find(
                      (img) => img.id === selectedObject.id,
                    );
                  if (selectedImage && selectedImage.isOnTopLayer === true) {
                    return null;
                  }
                }

                // CRITICAL FIX: Usually don't render Text in Layer 5 when single selection
                // (avoids stale ghost). But when occlusion images are active we DO render text
                // in Layer 5 so selected text/rect stay above demoted top-layer images.
                const isTextSingleSelection =
                  (selectedObject?.type === "Text" ||
                    selectedObject?.type?.includes("Text")) &&
                  (!selectedObjects || selectedObjects.length <= 1) &&
                  (selectionOcclusionImages.length === 0 ||
                    isSelectedTextActivelyDragging);

                if (!isTextSingleSelection) {
                  const { isInMainCanvas } = findElementCanvas({
                    selectedObject,
                    rectangles,
                    circles,
                    images,
                    texts,
                    arrows,
                    lines,
                    doubleSidedArrows,
                    offCanvasRectangles,
                    offCanvasCircles,
                    offCanvasImages,
                    offCanvasTexts,
                    offCanvasArrows,
                    offCanvasLines,
                    offCanvasDoubleSidedArrows,
                  });

                  // CRITICAL: For unified elements (Arrow, Line, DoubleSidedArrow), always render when selected
                  // so they appear on the topmost layer and stay visible during off→main drag (match circles).
                  const isUnifiedElementType =
                    selectedObject?.type === "Arrow" ||
                    selectedObject?.type === "Line" ||
                    selectedObject?.type === "DoubleSidedArrow" ||
                    selectedObject?.hasStartArrow !== undefined ||
                    selectedObject?.hasEndArrow !== undefined;

                  if (isInMainCanvas || isUnifiedElementType) {
                    elementsToRender.push(selectedObject);
                  }
                }
              }

              // Only skip the layer entirely when there is nothing to render at all.
              // selectionOcclusionImages must still render even if no selected elements go in.
              if (
                elementsToRender.length === 0 &&
                selectionOcclusionImages.length === 0
              )
                return null;

              // CRITICAL: Sort elements to ensure proper z-ordering in selected layer
              // Order: 1) Images without isOnTopLayer, 2) All other elements (arrows, lines, rectangles, circles, text), 3) Images with isOnTopLayer
              // This ensures arrows/lines appear on top of regular images, but images with "display on top" still appear on top of everything
              const sortedElementsToRender = [...elementsToRender].sort(
                (a, b) => {
                  const aIsPriorityBgText =
                    (a.type === "Text" || a.type?.includes("Text")) &&
                    a.showBackground === true;
                  const bIsPriorityBgText =
                    (b.type === "Text" || b.type?.includes("Text")) &&
                    b.showBackground === true;
                  const aIsImage = a.type === "Image";
                  const bIsImage = b.type === "Image";
                  const aIsOnTop = aIsImage && a.isOnTopLayer === true;
                  const bIsOnTop = bIsImage && b.isOnTopLayer === true;

                  // Selected text with background should render above all normal selected elements.
                  if (aIsPriorityBgText && !bIsPriorityBgText) return 1;
                  if (!aIsPriorityBgText && bIsPriorityBgText) return -1;

                  // Images with isOnTopLayer always render last (on top)
                  if (aIsOnTop && !bIsOnTop) return 1;
                  if (!aIsOnTop && bIsOnTop) return -1;

                  // Images without isOnTopLayer render first (behind other elements)
                  if (aIsImage && !aIsOnTop && !bIsImage) return -1;
                  if (!aIsImage && bIsImage && !bIsOnTop) return 1;

                  // For same type, maintain original order
                  return 0;
                },
              );

              return (
                <Layer listening={true} visible={true} opacity={1}>
                  {selectionOcclusionImages.map((imageObj) => {
                    const elementOpacity = getElementOpacity(
                      imageObj.id,
                      "Image",
                    );
                    return (
                      <React.Fragment
                        key={`selection-occlusion-${imageObj.id}`}
                      >
                        {renderImageElement({
                          imageObj,
                          isOffCanvas: false,
                          sourceStageRef: stageRef.current,
                          opacity: elementOpacity,
                        })}
                      </React.Fragment>
                    );
                  })}
                  {sortedElementsToRender.map((obj) => {
                    // CRITICAL: Drag preview state can lag a frame; use ref as fallback (same pattern as Images)
                    const previewRef = dragPreviewStateRef.current;
                    const previewId =
                      dragPreview?.data?.id?.replace("-ghost", "") ||
                      previewRef?.id;
                    const previewTarget =
                      dragPreview?.target || previewRef?.target;
                    const previewType = dragPreview?.type ?? previewRef?.type;
                    const isLineArrowPreviewType =
                      previewType === "Line" ||
                      previewType === "Arrow" ||
                      previewType === "DoubleSidedArrow" ||
                      (previewType === undefined &&
                        previewRef != null &&
                        previewId != null);
                    const isLineArrowPreviewForThis =
                      isLineArrowPreviewType && previewId === obj.id;
                    if (
                      LINE_DRAG_PREVIEW_DEBUG &&
                      (obj.type === "Line" ||
                        obj.type === "Arrow" ||
                        obj.type === "DoubleSidedArrow")
                    ) {
                      const hideSource =
                        previewTarget === "offCanvas" &&
                        isLineArrowPreviewForThis;
                      if (previewRef || dragPreview) {
                      }
                    }
                    // Render selected image with full opacity
                    // CRITICAL: Skip images with isOnTopLayer - they should only be rendered in the top layer
                    // This prevents them from disappearing when unselected
                    if (obj.type === "Image") {
                      const selectedImage =
                        images.find((img) => img.id === obj.id) ||
                        renderedMainImages.find((img) => img.id === obj.id);
                      if (selectedImage) {
                        // Skip rendering images with isOnTopLayer in the selected layer
                        // They are handled separately in the top layer
                        if (selectedImage.isOnTopLayer) {
                          return null;
                        }

                        // CRITICAL FIX: Don't render top-layer images in Layer 5
                        // Top-layer images are rendered on the separate top layer stage
                        // Rendering them on main stage causes click detection issues
                        if (selectedImage.isOnTopLayer) {
                          return null;
                        }

                        // CRITICAL FIX: Skip rendering non-top-layer images in Layer 5 during drag
                        // During drag, the image in Layer 1 is being moved, and rendering it again in Layer 5
                        // at the original position creates a "copy" effect
                        // Check if this image has an active dragPreview (being dragged)
                        const dragPreviewId =
                          dragPreview?.data?.id?.replace("-ghost", "") ||
                          dragPreviewStateRef.current?.id;
                        const isBeingDragged =
                          dragPreview &&
                          dragPreview.type === "Image" &&
                          dragPreviewId === selectedImage.id;

                        // CRITICAL FIX: When dragging a selected (non-top-layer) main-canvas image to off-canvas,
                        // don't unmount it. Unmounting during an active drag causes Konva to fire onDragEnd
                        // prematurely (the exact "drag stops at main stage boundary" issue).
                        // Instead, keep the node mounted but invisible while the off-canvas ghost preview renders.
                        const isBeingDraggedToOffCanvas =
                          isBeingDragged && dragPreview?.target === "offCanvas";
                        if (isBeingDraggedToOffCanvas) {
                          return (
                            <React.Fragment
                              key={`selected-${obj.type}-${obj.id}`}
                            >
                              {renderImageElement({
                                imageObj: selectedImage,
                                isOffCanvas: false,
                                sourceStageRef: stageRef.current,
                                opacity: 0, // Invisible but still mounted so drag continues
                              })}
                            </React.Fragment>
                          );
                        }

                        // For other drag-preview scenarios (should be rare here), keep existing behavior.
                        if (isBeingDragged) {
                          return null;
                        }

                        const bbox = getElementBoundingBox(
                          selectedImage,
                          "Image",
                        );
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {/* Konva Rect blocker - render first so it's behind the image element */}
                            <Group
                              x={bbox.x}
                              y={bbox.y}
                              listening={false}
                              perfectDrawEnabled={false}
                              shadowForStrokeEnabled={false}
                            >
                              <Rect
                                x={0}
                                y={0}
                                width={bbox.width}
                                height={bbox.height}
                                fill="transparent"
                                stroke={null}
                                strokeWidth={0}
                                opacity={0}
                                listening={false}
                                perfectDrawEnabled={false}
                                shadowForStrokeEnabled={false}
                              />
                            </Group>
                            {/* Render image element after blocker so it renders on top */}
                            {renderImageElement({
                              imageObj: selectedImage,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              opacity: 1, // Full opacity for selected element
                            })}
                          </React.Fragment>
                        );
                      }
                    }

                    // Render selected rectangle with full opacity
                    if (obj.type === "Rect") {
                      const selectedRect = rectangles.find(
                        (r) => r.id === obj.id,
                      );
                      if (selectedRect) {
                        const bbox = getElementBoundingBox(
                          selectedRect,
                          "Rect",
                        );
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {/* Konva Rect blocker - render first so it's behind the rectangle element */}
                            <Group
                              x={bbox.x}
                              y={bbox.y}
                              listening={false}
                              perfectDrawEnabled={false}
                              shadowForStrokeEnabled={false}
                            >
                              <Rect
                                x={0}
                                y={0}
                                width={bbox.width}
                                height={bbox.height}
                                fill="transparent"
                                stroke={null}
                                strokeWidth={0}
                                opacity={0}
                                listening={false}
                                perfectDrawEnabled={false}
                                shadowForStrokeEnabled={false}
                              />
                            </Group>
                            {/* Render rectangle normally */}
                            {renderRectangleElement({
                              rectangle: selectedRect,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              opacity: 1, // Full opacity for selected element
                            })}
                          </React.Fragment>
                        );
                      }
                    }

                    // Render selected circle with full opacity
                    if (obj.type === "Circle") {
                      const selectedCircle = circles.find(
                        (c) => c.id === obj.id,
                      );
                      if (selectedCircle) {
                        const bbox = getElementBoundingBox(
                          selectedCircle,
                          "Circle",
                        );
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {/* Konva Rect blocker - render first so it's behind the circle element */}
                            <Group
                              x={bbox.x}
                              y={bbox.y}
                              listening={false}
                              perfectDrawEnabled={false}
                              shadowForStrokeEnabled={false}
                            >
                              <Rect
                                x={0}
                                y={0}
                                width={bbox.width}
                                height={bbox.height}
                                fill="transparent"
                                stroke={null}
                                strokeWidth={0}
                                opacity={0}
                                listening={false}
                                perfectDrawEnabled={false}
                                shadowForStrokeEnabled={false}
                              />
                            </Group>
                            {/* Render circle normally */}
                            {renderCircleElement({
                              circle: selectedCircle,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              opacity: 1, // Full opacity for selected element
                            })}
                          </React.Fragment>
                        );
                      }
                    }

                    // CRITICAL: Check if this element is in off-canvas - if so, don't render in
                    // main-canvas selected layer (except unified line/arrow handling below).
                    // Text was missing here, which caused selected off-canvas text to also render on main.
                    const isInOffCanvas =
                      offCanvasRectangles.some((r) => r.id === obj.id) ||
                      offCanvasCircles.some((c) => c.id === obj.id) ||
                      offCanvasImages.some((img) => img.id === obj.id) ||
                      offCanvasTexts.some((t) => t.id === obj.id) ||
                      offCanvasLines.some((l) => l.id === obj.id) ||
                      offCanvasArrows.some((a) => a.id === obj.id) ||
                      offCanvasDoubleSidedArrows.some((a) => a.id === obj.id);

                    // CRITICAL FIX: Also check if element is being dragged to off-canvas during group drag
                    // During drag, elements haven't been transferred yet but should not be rendered in main canvas selected layer
                    // NOTE: Do NOT use dragPreview here to skip - that would unmount the node and end the drag.
                    const isBeingDraggedToOffCanvas =
                      obj._isInOffCanvasDuringDrag === true;

                    // DEBUG: Log filtering decision for lines/arrows during group drag
                    if (
                      obj.type === "Line" ||
                      obj.type === "Arrow" ||
                      obj.type === "DoubleSidedArrow"
                    ) {
                    }

                    // Don't render off-canvas elements in main canvas selected layer, EXCEPT Line/Arrow/DoubleSidedArrow:
                    // selected line is always rendered on top (Layer 5) so it stays visible when dragging off→main (match circles).
                    const isLineArrowType =
                      obj.type === "Line" ||
                      obj.type === "Arrow" ||
                      obj.type === "DoubleSidedArrow";
                    const isLineArrowDraggingToOff =
                      isBeingDraggedToOffCanvas && isLineArrowType;
                    if (isInOffCanvas && !isLineArrowType) return null;
                    if (
                      isBeingDraggedToOffCanvas &&
                      !isLineArrowDraggingToOff
                    ) {
                      return null;
                    }

                    // CRITICAL: Check if this is a unified element (in lines array) regardless of current type
                    // This handles cases where type changes but element is still a unified element
                    // Also check obj itself for unified element indicators
                    // IMPORTANT: Only check main canvas lines array, not off-canvas (already filtered above)
                    // CRITICAL FIX: Check obj directly for arrow flags FIRST (before checking selectedObject)
                    // This ensures arrows in selectedObjects array (multi-select mode) are rendered correctly
                    // even when they're not the primary selectedObject
                    // CRITICAL FIX: Explicitly exclude Text elements from being considered unified elements
                    // Text elements should never be rendered as unified elements, even if they have arrow properties
                    const isTextElement =
                      obj.type === "Text" || obj.type?.includes("Text");
                    const isUnifiedElement =
                      !isTextElement &&
                      (lines.some(
                        (l) =>
                          l.id === obj.id &&
                          !offCanvasLines.some((ol) => ol.id === obj.id),
                      ) ||
                        offCanvasLines.some((l) => l.id === obj.id) ||
                        offCanvasArrows.some((a) => a.id === obj.id) ||
                        offCanvasDoubleSidedArrows.some(
                          (a) => a.id === obj.id,
                        ) ||
                        // Check obj directly for arrow flags - this catches arrows in multi-select mode
                        ((obj.type === "Arrow" ||
                          obj.type === "Line" ||
                          obj.type === "DoubleSidedArrow" ||
                          obj.hasStartArrow !== undefined ||
                          obj.hasEndArrow !== undefined) &&
                          !isInOffCanvas) ||
                        // Line/arrow from off-canvas: still unified so we render on Layer 5 (off→main drag)
                        (isLineArrowType && isInOffCanvas) ||
                        // Then check selectedObject as secondary condition
                        (selectedObject?.id === obj.id &&
                          (selectedObject?.type === "Arrow" ||
                            selectedObject?.type === "Line" ||
                            selectedObject?.type === "DoubleSidedArrow" ||
                            selectedObject?.hasStartArrow !== undefined ||
                            selectedObject?.hasEndArrow !== undefined) &&
                          !isInOffCanvas));

                    // Render selected arrow with full opacity
                    // Unified elements (UnifiedLineArrowElement) are stored in lines array
                    // even when they have type "Arrow" or "DoubleSidedArrow"
                    // CRITICAL: Check by ID and arrow flags, not just type, since type can change
                    // CRITICAL: Also check if element is in lines array (unified elements) regardless of type
                    // CRITICAL: Check obj directly for arrow flags, not just selectedObject
                    // This ensures arrows in selectedObjects (multi-select) are rendered even if not the primary selectedObject
                    // CRITICAL FIX: Check obj.type and obj arrow flags directly, not just selectedObject
                    // This ensures arrows in selectedObjects array (multi-select mode) are rendered correctly
                    // CRITICAL FIX: Explicitly exclude Text elements from being considered arrow types
                    const isArrowType =
                      !isTextElement &&
                      (obj.type === "Arrow" ||
                        obj.type === "DoubleSidedArrow" ||
                        obj.hasStartArrow === true ||
                        obj.hasEndArrow === true ||
                        (selectedObject?.id === obj.id &&
                          (selectedObject?.type === "Arrow" ||
                            selectedObject?.type === "DoubleSidedArrow" ||
                            ((selectedObject?.hasStartArrow ||
                              selectedObject?.hasEndArrow) &&
                              !(
                                selectedObject?.hasStartArrow &&
                                selectedObject?.hasEndArrow
                              )))));

                    if (isArrowType || isUnifiedElement) {
                      // CRITICAL: Check if this is a unified element first (has arrow flags)
                      // Unified elements should be rendered using renderLineElement, even if they're in arrows array
                      // CRITICAL FIX: Only check obj for arrow flags, not selectedObject (unless obj.id === selectedObject.id)
                      // This prevents textelements from being incorrectly identified as unified elements when selectedObject
                      // is a different element (like a unifiedlinearrowelement) that has arrow flags
                      const hasArrowFlags =
                        obj.hasStartArrow !== undefined ||
                        obj.hasEndArrow !== undefined ||
                        (obj.id === selectedObject?.id &&
                          (selectedObject?.hasStartArrow !== undefined ||
                            selectedObject?.hasEndArrow !== undefined));

                      // Check lines array first for unified elements; then off-canvas arrays (for selected line on top layer)
                      let unifiedLine =
                        lines.find((l) => l.id === obj.id) ||
                        offCanvasLines.find((l) => l.id === obj.id);

                      // If not found in lines, check if obj has arrow flags (unified element)
                      // CRITICAL FIX: Only check hasArrowFlags if obj.id === selectedObject.id, otherwise only check isUnifiedElement
                      // This ensures we don't use selectedObject's properties for a different obj
                      if (!unifiedLine && isUnifiedElement) {
                        // CRITICAL: If not found in arrays, use selectedObject as fallback only if IDs match
                        if (obj.id === selectedObject?.id) {
                          unifiedLine = selectedObject;
                        } else {
                          unifiedLine = obj;
                        }
                      } else if (unifiedLine && obj.id === selectedObject?.id) {
                        // CRITICAL: Merge with selectedObject to get latest values
                        unifiedLine = { ...unifiedLine, ...selectedObject };
                      }
                      // When selected line is from off-canvas and we're rendering on main's Layer 5, only show
                      // when drag preview targets main (off→main drag); use selectedObject/preview position.
                      if (unifiedLine && isInOffCanvas && isLineArrowType) {
                        const previewRef = dragPreviewStateRef.current;
                        const isPreviewOnMain =
                          previewRef?.target === "main" &&
                          previewRef?.id === obj.id;
                        if (!isPreviewOnMain) return null; // Don't draw off-canvas line on main when not dragging to main
                        const pos =
                          selectedObject?.id === obj.id ? selectedObject : obj;
                        const x = previewRef.x ?? pos?.x ?? unifiedLine.x;
                        const y = previewRef.y ?? pos?.y ?? unifiedLine.y;
                        unifiedLine = {
                          ...unifiedLine,
                          x,
                          y,
                          // Treat as cross-canvas drag to prevent Konva node sync jitter.
                          _useNodePositionForGroup: true,
                        };
                      }

                      // When dragging this line to off-canvas, hide source on main so only ghost on target stage shows.
                      const selectedLinePreviewOnOffCanvas =
                        lineSourceHiddenForDragRef.current[obj.id] === true ||
                        (dragPreviewStateRef.current?.id === obj.id &&
                          dragPreviewStateRef.current?.target === "offCanvas");

                      // CRITICAL: Only use frozen position + _useNodePositionForGroup when actually in cross-canvas drag
                      // (preview on off-canvas). If we do it for every "dragged from main" we pin the line and it won't
                      // move visually during main-only drag.
                      const isThisLineBeingDraggedFromMain =
                        lineDragPreviewContextRef.current?.element?.id ===
                          obj.id &&
                        lineDragPreviewContextRef.current?.isOffCanvas ===
                          false;
                      if (
                        unifiedLine &&
                        isThisLineBeingDraggedFromMain &&
                        selectedLinePreviewOnOffCanvas
                      ) {
                        const frozen =
                          lineLastSourcePositionRef.current[obj.id];
                        const arrayLine = lines.find((ll) => ll.id === obj.id);
                        // Use frozen or array position so source line stays put; ghost shows on target stage.
                        unifiedLine = {
                          ...unifiedLine,
                          x: frozen
                            ? frozen.x
                            : (arrayLine?.x ?? unifiedLine.x),
                          y: frozen
                            ? frozen.y
                            : (arrayLine?.y ?? unifiedLine.y),
                          _useNodePositionForGroup: true,
                        };
                      }

                      // Mirror the stabilization for off-canvas -> main drags.
                      // When the drag context says this unified element originated from offCanvas and the
                      // preview target is main, freeze the rendered node so React-Konva doesn't fight the
                      // preview/editor updates during the boundary transition.
                      const isThisLineBeingDraggedFromOffCanvas =
                        lineDragPreviewContextRef.current?.element?.id ===
                          obj.id &&
                        lineDragPreviewContextRef.current?.isOffCanvas === true;
                      const previewRef = dragPreviewStateRef.current;
                      const selectedLinePreviewOnMain =
                        previewRef?.target === "main" &&
                        previewRef?.id === obj.id;
                      if (
                        unifiedLine &&
                        isThisLineBeingDraggedFromOffCanvas &&
                        selectedLinePreviewOnMain
                      ) {
                        unifiedLine = {
                          ...unifiedLine,
                          x: previewRef?.x ?? unifiedLine.x,
                          y: previewRef?.y ?? unifiedLine.y,
                          _useNodePositionForGroup: true,
                        };
                      }

                      // CRITICAL: Render unified elements regardless of arrow flags
                      // Match circle: keep selected line visible (opacity 1); ghost shows on target via dragPreview.
                      // Hide (opacity 0) when preview is on off-canvas so it doesn't show on main as well.
                      if (unifiedLine) {
                        const previewRefForRender = dragPreviewStateRef.current;
                        const isPreviewForUnifiedOnMain =
                          isInOffCanvas &&
                          isLineArrowType &&
                          (unifiedLine?._previewTargetStage === "main" ||
                            selectedObject?._previewTargetStage === "main") &&
                          // Prefer pointer-driven preview stage; fall back to previewRef id/target if present.
                          (previewRefForRender
                            ? previewRefForRender.id === obj.id &&
                              previewRefForRender.target === "main"
                            : true);
                        return (
                          <React.Fragment
                            key={`selected-${unifiedLine.type || obj.type}-${
                              obj.id
                            }-${unifiedLine.hasStartArrow}-${
                              unifiedLine.hasEndArrow
                            }`}
                          >
                            {renderLineElement({
                              line: {
                                ...unifiedLine,
                                opacity:
                                  isPreviewForUnifiedOnMain ||
                                  selectedLinePreviewOnOffCanvas
                                    ? 0
                                    : (unifiedLine.opacity ?? 1),
                              },
                              isOffCanvas: false,
                              isPreview: isPreviewForUnifiedOnMain,
                              sourceStageRef: stageRef.current,
                              saveToHistory,
                              opacity:
                                isPreviewForUnifiedOnMain ||
                                selectedLinePreviewOnOffCanvas
                                  ? 0
                                  : 1,
                            })}
                          </React.Fragment>
                        );
                      }

                      // Fallback to legacy arrow rendering if not a unified element
                      let selectedArrow = arrows.find((a) => a.id === obj.id);
                      if (selectedArrow) {
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {renderArrowElement({
                              arrow: selectedArrow,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              opacity: 1,
                            })}
                          </React.Fragment>
                        );
                      }

                      // CRITICAL FIX: If we reach here, still try to render using obj as fallback
                      // Match circle: keep visible (opacity 1).
                      return (
                        <React.Fragment
                          key={`selected-fallback-${obj.type}-${obj.id}`}
                        >
                          {renderLineElement({
                            line: obj,
                            isOffCanvas: false,
                            sourceStageRef: stageRef.current,
                            saveToHistory,
                            opacity: 1,
                          })}
                        </React.Fragment>
                      );
                    }

                    // Render selected text with full opacity
                    if (obj.type === "Text" || obj.type?.includes("Text")) {
                      // Single text selection is already rendered in the normal text layer.
                      // Rendering it again in selected overlay causes duplicate rect/html instances
                      // during drag (observed as jitter/ghost copy). Keep selected-layer text rendering
                      // only for true multi-selection scenarios.
                      const isSingleSelectedTextOnly =
                        (selectedObjects?.length || 0) === 1 &&
                        selectedObject?.id === obj.id &&
                        (selectedObject?.type === "Text" ||
                          selectedObject?.type?.includes("Text"));
                      if (isSingleSelectedTextOnly) {
                        return null;
                      }

                      // CRITICAL FIX: Check both texts and offCanvasTexts arrays
                      // Text elements move between arrays when dragged across canvas boundaries
                      let selectedText = texts.find((t) => t.id === obj.id);
                      let isTextOffCanvas = false;
                      if (!selectedText) {
                        selectedText = offCanvasTexts.find(
                          (t) => t.id === obj.id,
                        );
                        isTextOffCanvas = true;
                      }
                      // CRITICAL: If not found in arrays, use obj directly as fallback
                      // This ensures we have the latest values even if arrays are empty or not updated yet
                      if (!selectedText && obj.id === selectedObject?.id) {
                        selectedText = selectedObject;
                        // Determine isOffCanvas from canvas check logic
                        const { isInOffCanvas } = findElementCanvas({
                          selectedObject: selectedText,
                          rectangles,
                          circles,
                          images,
                          texts,
                          arrows,
                          lines,
                          doubleSidedArrows,
                          offCanvasRectangles,
                          offCanvasCircles,
                          offCanvasImages,
                          offCanvasTexts,
                          offCanvasArrows,
                          offCanvasLines,
                          offCanvasDoubleSidedArrows,
                        });
                        isTextOffCanvas = isInOffCanvas;
                      }

                      if (selectedText) {
                        // CRITICAL FIX: Get actual position from Konva node if available
                        // This fixes the issue where text prop has stale negative position after moving to off-canvas
                        let textWithCurrentPosition = selectedText;
                        const targetStage = isTextOffCanvas
                          ? offCanvasStageRef.current
                          : stageRef.current;
                        const otherStage = isTextOffCanvas
                          ? stageRef.current
                          : offCanvasStageRef.current;

                        // Try to get current position from Konva node
                        if (targetStage && selectedText.id) {
                          const node = targetStage.findOne(
                            `#${selectedText.id}`,
                          );
                          if (node) {
                            const nodeX = node.x();
                            const nodeY = node.y();
                            if (nodeX !== undefined && nodeY !== undefined) {
                              textWithCurrentPosition = {
                                ...selectedText,
                                x: nodeX,
                                y: nodeY,
                              };
                            }
                          } else if (otherStage && selectedText.id) {
                            // Try the other stage as fallback
                            const node = otherStage.findOne(
                              `#${selectedText.id}`,
                            );
                            if (node) {
                              const nodeX = node.x();
                              const nodeY = node.y();
                              if (nodeX !== undefined && nodeY !== undefined) {
                                textWithCurrentPosition = {
                                  ...selectedText,
                                  x: nodeX,
                                  y: nodeY,
                                };
                              }
                            }
                          }
                        }

                        // CRITICAL: Avoid duplicate at top-left after leave/revisit - if position is (0,0) but
                        // the text from array has a valid position, use array position (node lookup may have failed).
                        const hasZeroPosition =
                          (textWithCurrentPosition.x === 0 &&
                            textWithCurrentPosition.y === 0) ||
                          (textWithCurrentPosition.x == null &&
                            textWithCurrentPosition.y == null);
                        const arrayHasPosition =
                          (selectedText.x != null && selectedText.x !== 0) ||
                          (selectedText.y != null && selectedText.y !== 0);
                        if (hasZeroPosition && arrayHasPosition) {
                          textWithCurrentPosition = {
                            ...textWithCurrentPosition,
                            x: selectedText.x ?? 0,
                            y: selectedText.y ?? 0,
                          };
                        }

                        const bbox = getElementBoundingBox(
                          textWithCurrentPosition,
                          "Text",
                        );
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {(() => {
                              // CRITICAL FIX: During cross-canvas GROUP drag, keep the Konva node mounted
                              // (so drag does not abort) but make it invisible when a preview is being shown
                              // on the other stage. For TextElement, this must hide both Konva + Html overlay.
                              const hasGroupPreviewForThisText =
                                groupDragPreviews.some((p) => {
                                  if (p?.type !== "Text") return false;
                                  const pid =
                                    p?.data?.id?.replace("-ghost", "") ||
                                    p?.data?.id;
                                  return pid === obj.id;
                                });
                              const shouldHideSourceDuringGroupPreview =
                                hasGroupPreviewForThisText &&
                                groupDragPreviews.some(
                                  (p) =>
                                    p?.type === "Text" &&
                                    p?.target === "offCanvas",
                                );

                              // Legacy individual-drag flags (kept), but we now hide by opacity instead of unmounting.
                              const shouldHideSourceDuringIndividualPreview =
                                selectedText._isInOffCanvasDuringDrag ===
                                  true ||
                                obj._isInOffCanvasDuringDrag === true ||
                                selectedText._isInOffCanvasDuringDrag ===
                                  false ||
                                obj._isInOffCanvasDuringDrag === false;

                              const shouldHideSource =
                                shouldHideSourceDuringGroupPreview ||
                                shouldHideSourceDuringIndividualPreview;

                              const effectiveTextForRender = shouldHideSource
                                ? { ...textWithCurrentPosition, opacity: 0 }
                                : textWithCurrentPosition;

                              const bbox = getElementBoundingBox(
                                effectiveTextForRender,
                                "Text",
                              );
                              return (
                                <>
                                  {/* Konva Rect blocker - render first so it's behind the text element */}
                                  <Group
                                    x={bbox.x}
                                    y={bbox.y}
                                    listening={false}
                                    perfectDrawEnabled={false}
                                    shadowForStrokeEnabled={false}
                                  >
                                    <Rect
                                      x={0}
                                      y={0}
                                      width={bbox.width}
                                      height={bbox.height}
                                      fill="transparent"
                                      stroke={null}
                                      strokeWidth={0}
                                      opacity={0}
                                      listening={false}
                                      perfectDrawEnabled={false}
                                      shadowForStrokeEnabled={false}
                                    />
                                  </Group>
                                  {/* Render text element after blocker so it renders on top */}
                                  {renderTextElement({
                                    text: effectiveTextForRender,
                                    isOffCanvas: isTextOffCanvas,
                                    sourceStageRef: isTextOffCanvas
                                      ? offCanvasStageRef.current
                                      : stageRef.current,
                                    opacity: shouldHideSource ? 0 : 1, // Keep mounted but invisible during cross-canvas previews
                                    setOffCanvasTexts,
                                    offCanvasTexts,
                                    setTexts,
                                    texts,
                                    handleElementDragEnd,
                                    onDragEnd: (
                                      id,
                                      newX,
                                      newY,
                                      mouseClientX,
                                      mouseClientY,
                                    ) => {
                                      handleElementDragEnd({
                                        id,
                                        newX,
                                        newY,
                                        type: "Text",
                                        sourceStageRef: isTextOffCanvas
                                          ? offCanvasStageRef.current
                                          : stageRef.current,
                                        mainState: texts,
                                        offCanvasState: offCanvasTexts,
                                        setMainState: setTexts,
                                        setOffCanvasState: setOffCanvasTexts,
                                        setSelectedObjects,
                                        mouseClientX,
                                        mouseClientY,
                                      });
                                    },
                                  })}
                                </>
                              );
                            })()}
                          </React.Fragment>
                        );
                      }
                    }

                    // Render selected double-sided arrow with full opacity
                    // Unified elements (UnifiedLineArrowElement) are stored in lines array
                    // even when they have type "DoubleSidedArrow"
                    if (obj.type === "DoubleSidedArrow") {
                      // First check doubleSidedArrows array (legacy arrows)
                      let selectedDoubleArrow = doubleSidedArrows.find(
                        (a) => a.id === obj.id,
                      );
                      // If not found, check lines array (unified elements)
                      if (!selectedDoubleArrow) {
                        let unifiedLine = lines.find((l) => l.id === obj.id);
                        // CRITICAL: If not found in arrays, use selectedObject as fallback
                        // This ensures we have the latest values even if arrays are empty or not updated yet
                        if (!unifiedLine && obj.id === selectedObject?.id) {
                          unifiedLine = selectedObject;
                        }
                        if (
                          unifiedLine &&
                          unifiedLine.hasStartArrow &&
                          unifiedLine.hasEndArrow
                        ) {
                          // Use renderLineElement for unified elements (it handles DoubleSidedArrow type)
                          const hideDoubleSided =
                            previewTarget === "offCanvas" &&
                            isLineArrowPreviewForThis;
                          return (
                            <React.Fragment
                              key={`selected-${obj.type}-${obj.id}`}
                            >
                              {renderLineElement({
                                line: unifiedLine,
                                isOffCanvas: false,
                                sourceStageRef: stageRef.current,
                                saveToHistory,
                                opacity: hideDoubleSided ? 0 : 1,
                              })}
                            </React.Fragment>
                          );
                        }
                      }
                      if (selectedDoubleArrow) {
                        const hideLegacyDouble =
                          previewTarget === "offCanvas" &&
                          isLineArrowPreviewForThis;
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {renderDoubleSidedArrowElement({
                              arrow: selectedDoubleArrow,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              opacity: hideLegacyDouble ? 0 : 1,
                            })}
                          </React.Fragment>
                        );
                      }
                    }

                    // Render selected line with full opacity
                    // CRITICAL: Check by ID and line flags, not just type, since type can change
                    // Also check if it's a unified element that has become a line (both flags false)
                    // CRITICAL FIX: Check obj directly for line type, not just selectedObject
                    // This ensures lines in selectedObjects array (multi-select mode) are rendered correctly
                    const isLineType =
                      obj.type === "Line" ||
                      (obj.hasStartArrow === false &&
                        obj.hasEndArrow === false &&
                        (obj.hasStartArrow !== undefined ||
                          obj.hasEndArrow !== undefined)) ||
                      (selectedObject?.id === obj.id &&
                        (selectedObject?.type === "Line" ||
                          (selectedObject?.type === "Arrow" &&
                            !selectedObject?.hasStartArrow &&
                            !selectedObject?.hasEndArrow))) ||
                      (obj.id === selectedObject?.id &&
                        selectedObject?.type !== "DoubleSidedArrow" &&
                        selectedObject?.hasStartArrow === false &&
                        selectedObject?.hasEndArrow === false);

                    if (isLineType && !isUnifiedElement) {
                      // Only render as Line if it's not already handled as a unified element
                      let selectedLine = lines.find((l) => l.id === obj.id);
                      // CRITICAL: If not found in arrays, use selectedObject as fallback
                      // This ensures we have the latest values even if arrays are empty or not updated yet
                      if (!selectedLine && obj.id === selectedObject?.id) {
                        selectedLine = selectedObject;
                      } else if (
                        selectedLine &&
                        obj.id === selectedObject?.id
                      ) {
                        // CRITICAL: Merge with selectedObject to get latest values
                        selectedLine = { ...selectedLine, ...selectedObject };
                      }
                      if (selectedLine) {
                        const hideSelectedLine =
                          previewTarget === "offCanvas" &&
                          isLineArrowPreviewForThis;
                        return (
                          <React.Fragment
                            key={`selected-${selectedLine.type || obj.type}-${
                              obj.id
                            }`}
                          >
                            {renderLineElement({
                              line: selectedLine,
                              isOffCanvas: false,
                              sourceStageRef: stageRef.current,
                              saveToHistory,
                              opacity: hideSelectedLine ? 0 : 1,
                            })}
                          </React.Fragment>
                        );
                      }
                    }

                    // Render selected text with full opacity
                    if (obj.type === "Text" || obj.type?.includes("Text")) {
                      // CRITICAL FIX: Check both texts and offCanvasTexts arrays
                      // Text elements move between arrays when dragged across canvas boundaries
                      let selectedText = texts.find((t) => t.id === obj.id);
                      let isTextOffCanvas = false;
                      if (!selectedText) {
                        selectedText = offCanvasTexts.find(
                          (t) => t.id === obj.id,
                        );
                        isTextOffCanvas = true;
                      }
                      // CRITICAL: If not found in arrays, use obj directly as fallback
                      // This ensures we have the latest values even if arrays are empty or not updated yet
                      if (!selectedText && obj.id === selectedObject?.id) {
                        selectedText = selectedObject;
                        // Determine isOffCanvas from the canvas check logic
                        const { isInOffCanvas } = findElementCanvas({
                          selectedObject: selectedText,
                          rectangles,
                          circles,
                          images,
                          texts,
                          arrows,
                          lines,
                          doubleSidedArrows,
                          offCanvasRectangles,
                          offCanvasCircles,
                          offCanvasImages,
                          offCanvasTexts,
                          offCanvasArrows,
                          offCanvasLines,
                          offCanvasDoubleSidedArrows,
                        });
                        isTextOffCanvas = isInOffCanvas;
                      }

                      if (selectedText) {
                        // CRITICAL FIX: Get actual position from Konva node if available
                        // This fixes the issue where text prop has stale negative position after moving to off-canvas
                        let textWithCurrentPosition = selectedText;
                        const targetStage = isTextOffCanvas
                          ? offCanvasStageRef.current
                          : stageRef.current;
                        const otherStage = isTextOffCanvas
                          ? stageRef.current
                          : offCanvasStageRef.current;

                        // Try to get current position from Konva node
                        if (targetStage && selectedText.id) {
                          const node = targetStage.findOne(
                            `#${selectedText.id}`,
                          );
                          if (node) {
                            const nodeX = node.x();
                            const nodeY = node.y();
                            if (nodeX !== undefined && nodeY !== undefined) {
                              textWithCurrentPosition = {
                                ...selectedText,
                                x: nodeX,
                                y: nodeY,
                              };
                            }
                          } else if (otherStage && selectedText.id) {
                            // Try the other stage as fallback
                            const node = otherStage.findOne(
                              `#${selectedText.id}`,
                            );
                            if (node) {
                              const nodeX = node.x();
                              const nodeY = node.y();
                              if (nodeX !== undefined && nodeY !== undefined) {
                                textWithCurrentPosition = {
                                  ...selectedText,
                                  x: nodeX,
                                  y: nodeY,
                                };
                              }
                            }
                          }
                        }

                        // CRITICAL: Avoid duplicate at top-left - use array position when node lookup yields 0,0.
                        const hasZeroPos =
                          (textWithCurrentPosition.x === 0 &&
                            textWithCurrentPosition.y === 0) ||
                          (textWithCurrentPosition.x == null &&
                            textWithCurrentPosition.y == null);
                        const arrayHasPos =
                          (selectedText.x != null && selectedText.x !== 0) ||
                          (selectedText.y != null && selectedText.y !== 0);
                        if (hasZeroPos && arrayHasPos) {
                          textWithCurrentPosition = {
                            ...textWithCurrentPosition,
                            x: selectedText.x ?? 0,
                            y: selectedText.y ?? 0,
                          };
                        }

                        const bbox = getElementBoundingBox(
                          textWithCurrentPosition,
                          "Text",
                        );
                        return (
                          <React.Fragment
                            key={`selected-${obj.type}-${obj.id}`}
                          >
                            {/* Konva Rect blocker - render first so it's behind the text element */}
                            <Group
                              x={bbox.x}
                              y={bbox.y}
                              listening={false}
                              perfectDrawEnabled={false}
                              shadowForStrokeEnabled={false}
                            >
                              <Rect
                                x={0}
                                y={0}
                                width={bbox.width}
                                height={bbox.height}
                                fill="transparent"
                                stroke={null}
                                strokeWidth={0}
                                opacity={0}
                                listening={false}
                                perfectDrawEnabled={false}
                                shadowForStrokeEnabled={false}
                              />
                            </Group>
                            {/* Render text element after blocker so it renders on top */}
                            {renderTextElement({
                              text: textWithCurrentPosition,
                              isOffCanvas: isTextOffCanvas,
                              sourceStageRef: isTextOffCanvas
                                ? offCanvasStageRef.current
                                : stageRef.current,
                              opacity: 1, // Full opacity for selected element
                              setOffCanvasTexts,
                              offCanvasTexts,
                              setTexts,
                              texts,
                              handleElementDragEnd,
                              onDragEnd: (
                                id,
                                newX,
                                newY,
                                mouseClientX,
                                mouseClientY,
                              ) => {
                                handleElementDragEnd({
                                  id,
                                  newX,
                                  newY,
                                  type: "Text",
                                  sourceStageRef: stageRef.current,
                                  mainState: texts,
                                  offCanvasState: offCanvasTexts,
                                  setMainState: setTexts,
                                  setOffCanvasState: setOffCanvasTexts,
                                  stageRef,
                                  offCanvasStageRef,
                                  canvasHeight,
                                  setSelectedObject,
                                  setSelectedArrow: null,
                                  setSelectedLine: null,
                                  setSelectedDoubleSidedArrow: null,
                                  transformerRef,
                                  offCanvasTransformerRef,
                                  saveToHistory,
                                  setSelectedObjects,
                                  mouseClientX,
                                  mouseClientY,
                                });
                              },
                              onTransformEnd: (transformData) => {
                                const updatedTexts = texts.map((t) =>
                                  t.id === transformData.id
                                    ? { ...t, ...transformData }
                                    : t,
                                );

                                setTexts((prev) =>
                                  prev.map((t) =>
                                    t.id === transformData.id
                                      ? { ...t, ...transformData }
                                      : t,
                                  ),
                                );

                                if (setSelectedText) {
                                  setSelectedText((prev) =>
                                    prev?.id === transformData.id
                                      ? { ...prev, ...transformData }
                                      : prev,
                                  );
                                }

                                const isTextSelected =
                                  selectedObject?.id === transformData.id &&
                                  (selectedObject?.type === "Text" ||
                                    selectedObject?.type?.includes("Text"));
                                if (
                                  (isTextSelected || isElementBeingEdited) &&
                                  latestUpdatedTextsRef
                                ) {
                                  latestUpdatedTextsRef.current.main =
                                    updatedTexts;
                                  setPendingChanges(true);
                                } else if (isElementBeingEdited) {
                                  setPendingChanges(true);
                                }
                              },
                              saveToHistory,
                              onClick,
                              setSelectedObject,
                              setSelectedText,
                              setSelectedTextId,
                              setShowTextBox,
                              setShowEditTask,
                              setShowEditNote,
                              setEditTextBoxContent,
                              setSelectedObjects,
                              setAction,
                              ACTIONS,
                              setEditing,
                              transformerRef,
                              selectedObject,
                              selectedText,
                              setSelectedImage,
                              getTextStyle,
                              formatListContent,
                              hasUserContent,
                              estimateKonvaTextHeight,
                              stripHtmlPreserveFormatting,
                              hasTaskContent,
                              hasNoteContent,
                              calculateGroupHeight,
                              canvasWidth: mainCanvasWidth,
                              canvasHeight: fixedStageHeightPx,
                              canvasResizeTrigger,
                            })}
                          </React.Fragment>
                        );
                      }
                    }

                    return null;
                  })}
                </Layer>
              );
            })()}

          {/* Layer 6: Transformer (topmost layer - renders above selected elements) */}
          {parseInt(canvasHeight) >= 5 && (
            <Layer>
              {/* Transformer for single selection */}
              {(() => {
                // CRITICAL FIX: Allow transformer to render when:
                // 1. selectedObjects is empty (classic single selection), OR
                // 2. selectedObjects has exactly 1 element that matches selectedObject (during property updates)
                const isSingleSelection =
                  selectedObjects.length === 0 ||
                  (selectedObjects.length === 1 &&
                    selectedObjects[0]?.id === selectedObject?.id &&
                    selectedObjects[0]?.type === selectedObject?.type);

                const shouldRenderTransformer =
                  !isLeavingCanvas &&
                  isSingleSelection &&
                  !isDuplicatePlacementActive &&
                  selectedObject?.id &&
                  selectedObject?.type !== "Arrow" &&
                  selectedObject?.type !== "DoubleSidedArrow" &&
                  selectedObject?.type !== "Line" &&
                  selectedObject?.type !== "Image";

                // console.log('🔍 [TRANSFORMER-CONDITION] Checking if should render transformer', {
                //   isLeavingCanvas,
                //   selectedObjectsLength: selectedObjects.length,
                //   selectedObjectId: selectedObject?.id,
                //   selectedObjectType: selectedObject?.type,
                //   isSingleSelection,
                //   shouldRenderTransformer
                // });

                return shouldRenderTransformer;
              })() &&
                (() => {
                  // Check if element is in main canvas (not off-canvas)
                  const { isInMainCanvas } = findElementCanvas({
                    selectedObject,
                    rectangles,
                    circles,
                    images,
                    texts,
                    arrows,
                    lines,
                    doubleSidedArrows,
                    offCanvasRectangles,
                    offCanvasCircles,
                    offCanvasImages,
                    offCanvasTexts,
                    offCanvasArrows,
                    offCanvasLines,
                    offCanvasDoubleSidedArrows,
                  });

                  // Only render transformer for main canvas elements in this layer
                  if (!isInMainCanvas) return null;

                  // IMPORTANT: findOne is called during React's render phase, BEFORE new Konva
                  // nodes are committed. When an element is just transferred to the main canvas,
                  // its Konva node may not exist yet. Do NOT bail on a null node — always render
                  // the <Transformer> so that transformerRef.current is set. The imperative
                  // useTransformerManager RAF fires after commit and calls .nodes([node]) once the
                  // node actually exists.
                  let node = null;

                  if (stageRef?.current) {
                    node = stageRef.current.findOne(`#${selectedObject?.id}`);
                    // For Text: attach to the Group (draggable root), not a child (e.g. Rect)
                    if (
                      node &&
                      selectedObject?.type === "Text" &&
                      typeof node.getClassName === "function" &&
                      node.getClassName() !== "Group" &&
                      node.getParent
                    ) {
                      const parent = node.getParent();
                      if (parent && parent.attrs?.id === selectedObject?.id) {
                        node = parent;
                      }
                    }
                    // When main→off→main: ghost is on main; hide transformer for source text at boundary so only ghost is visible
                    if (
                      node &&
                      selectedObject?.type === "Text" &&
                      dragPreview?.type === "Text" &&
                      dragPreview?.target === "main" &&
                      dragPreview?.data?.id?.replace("-ghost", "") ===
                        selectedObject?.id
                    ) {
                      node = null;
                    }
                  }

                  // Only skip rendering if node exists but is clearly invalid (stale/destroyed).
                  // A null node means the element is still committing — that is fine.
                  if (node) {
                    try {
                      const layer = node.getLayer();
                      if (
                        !layer ||
                        !layer.children ||
                        !layer.children.includes(node)
                      ) {
                        return null;
                      }
                    } catch (e) {
                      return null;
                    }
                  }

                  // Images are transformed by dedicated image transformers:
                  // - non-top-layer: local Transformer in ImageElement
                  // - main top-layer: topLayerTransformerRef block
                  // Rendering this generic main transformer for images causes
                  // duplicate transform handling and jittery/over-aggressive rotation.
                  if (selectedObject?.type === "Image") {
                    return null;
                  }

                  // ⬇️ Default anchors
                  let anchors = [
                    "top-left",
                    "top-center",
                    "top-right",
                    "middle-left",
                    "middle-right",
                    "bottom-left",
                    "bottom-center",
                    "bottom-right",
                  ];

                  // ⬇️ Configure anchors based on element type and maintainAspectRatio
                  if (selectedObject?.type === "Text") {
                    // CRITICAL FIX: Hide anchors when text is in edit mode
                    const isTextInEditMode =
                      editTextBoxContent || showEditNote || showEditTask;
                    anchors = isTextInEditMode
                      ? []
                      : ["middle-left", "middle-right"];
                  } else if (
                    selectedObject?.type === "Image" ||
                    selectedObject?.type === "Rectangle" ||
                    selectedObject?.type === "Rect" ||
                    selectedObject?.type === "rectangle" ||
                    selectedObject?.type === "Circle"
                  ) {
                    if (selectedObject?.maintainAspectRatio === true) {
                      // Checked: Show only bottom-right handle for proportional scaling
                      anchors = ["bottom-right"];
                      // console.log('🔧 [TRANSFORMER] Setting anchors for maintainAspectRatio=true', {
                      //   id: selectedObject?.id,
                      //   type: selectedObject?.type,
                      //   maintainAspectRatio: selectedObject?.maintainAspectRatio,
                      //   anchors,
                      //   circleTransformerKey,
                      //   rectangleTransformerKey,
                      //   activeKey: (selectedObject?.type === "Rect" || selectedObject?.type === "Rectangle") ? rectangleTransformerKey : circleTransformerKey
                      // });
                    } else {
                      // Unchecked (including undefined): Show right, bottom, and bottom-right handles
                      anchors = [
                        "middle-right",
                        "bottom-center",
                        "bottom-right",
                      ];
                      // console.log('🔧 [TRANSFORMER] Setting anchors for maintainAspectRatio=false', {
                      //   id: selectedObject?.id,
                      //   type: selectedObject?.type,
                      //   maintainAspectRatio: selectedObject?.maintainAspectRatio,
                      //   anchors,
                      //   circleTransformerKey,
                      //   rectangleTransformerKey,
                      //   activeKey: (selectedObject?.type === "Rect" || selectedObject?.type === "Rectangle") ? rectangleTransformerKey : circleTransformerKey
                      // });
                    }
                  }

                  const boundBoxFunc = (oldBox, newBox) => {
                    if (newBox.width < 10 || newBox.height < 10) return oldBox;
                    // Do not live-clamp to stage edges during transform; final clamp happens on transform end.
                    const clamped = { ...newBox };
                    const activeAnchor =
                      typeof transformerRef?.current?.getActiveAnchor === "function"
                        ? transformerRef.current.getActiveAnchor()
                        : null;
                    const isResize = !!activeAnchor && activeAnchor !== "rotater";
                    if (isResize && selectedObject?.type === "Circle") {
                      // Circle is center-anchored, while Transformer boxes are top-left based.
                      // Lock box origin during resize so boundary clamps don't push the node down.
                      clamped.x = oldBox.x;
                      clamped.y = oldBox.y;
                    }
                    return clamped;
                  };

                  // CRITICAL: Keep anchors visible during drag (don't hide them)
                  // Guard against null node (element still committing to main stage).
                  const isDragging = node
                    ? (typeof node.isDragging === "function"
                        ? node.isDragging()
                        : false) ||
                      node.getAttr?.("_isHtmlDragging") ||
                      node.getAttr?.("_isDragging")
                    : false;
                  const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;

                  return (
                    <Transformer
                      key={`main-transformer-${selectedObject?.id || "null"}-${selectedObject?.type === "Rect" || selectedObject?.type === "Rectangle" ? rectangleTransformerKey : circleTransformerKey}-${
                        selectedObject?.maintainAspectRatio || false
                      }-${anchors.join("-")}-${
                        selectedObject?.maintainAspectRatio ? "locked" : "free"
                      }-edit-${editTextBoxContent || false}`}
                      ref={transformerRef}
                      nodes={node ? [node] : []}
                      rotateEnabled={
                        selectedObject?.type !== "Text" &&
                        !isDuplicatePlacementActive
                      }
                      flipEnabled={false}
                      boundBoxFunc={boundBoxFunc}
                      enabledAnchors={effectiveAnchors}
                      forceUpdate={
                        selectedObject?.type === "Circle"
                          ? `${Date.now()}-${circleTransformerKey}-${anchors.join(",")}`
                          : selectedObject?.type === "Rect" ||
                              selectedObject?.type === "Rectangle"
                            ? `${Date.now()}-${rectangleTransformerKey}-${anchors.join(",")}`
                            : selectedObject?.type === "Text"
                              ? `text-${anchors.join(",")}-${selectedObject?.x ?? node?.x?.() ?? 0}-${selectedObject?.y ?? node?.y?.() ?? 0}`
                              : undefined
                      }
                      keepRatio={selectedObject?.maintainAspectRatio || false}
                      centeredScaling={false}
                      onTransformStart={(e) => {
                        crossStageGestureBegin(stageRef.current, e?.evt);
                      }}
                      onTransform={(e) => {
                        if (selectedObject?.type === "Line") {
                          const lineNode = e.target;
                          const scaleX = lineNode.scaleX();
                          const scaleY = lineNode.scaleY();
                          const x = lineNode.x();
                          const y = lineNode.y();

                          // Find the line in the state
                          const lineId = selectedObject.id;
                          const currentLine = lines.find(
                            (l) => l.id === lineId,
                          );

                          if (currentLine) {
                            // Calculate new scaled points
                            const relativePoints = [
                              currentLine.points[0] - currentLine.x,
                              currentLine.points[1] - currentLine.y,
                              currentLine.points[2] - currentLine.x,
                              currentLine.points[3] - currentLine.y,
                            ];

                            const scaledPoints = relativePoints.map(
                              (coord, i) =>
                                i % 2 === 0 ? coord * scaleX : coord * scaleY,
                            );

                            // Convert back to absolute points
                            const newAbsolutePoints = [
                              scaledPoints[0] + x,
                              scaledPoints[1] + y,
                              scaledPoints[2] + x,
                              scaledPoints[3] + y,
                            ];

                            // Calculate dimensions
                            const width = Math.abs(
                              newAbsolutePoints[2] - newAbsolutePoints[0],
                            );
                            const height = Math.abs(
                              newAbsolutePoints[3] - newAbsolutePoints[1],
                            );

                            // Reset scale to prevent accumulation
                            lineNode.scaleX(1);
                            lineNode.scaleY(1);

                            // Update line state
                            updateLineProperties(lineId, {
                              x: x,
                              y: y,
                              points: newAbsolutePoints,
                              width: width,
                              height: height,
                              skipOffset: true,
                            });
                          }
                        }
                      }}
                      onTransformEnd={(e) => {
                        crossStageGestureEnd();
                        justFinishedTransformRef.current = true;
                        // CRITICAL: Don't save to history here - history will be saved when element is unselected
                        // This groups all transform changes (width, height, rotation, etc.) into a single history entry
                      }}
                    />
                  );
                })()}

              {/* Transformer for multi-selection */}
              {!isLeavingCanvas &&
                selectedObjects &&
                selectedObjects.length > 0 &&
                // Don't show transformers when multiple objects are selected - show wrapper rectangle instead
                selectedObjects.length === 1 &&
                selectedObjects
                  .filter((obj) => {
                    // When multi-select mode is enabled, only show transformers for individually selected items
                    if (isMultiSelectMode) {
                      return individuallySelectedIds.has(obj.id);
                    }
                    // When multi-select mode is disabled, only show transformer for the selectedObject
                    return selectedObject?.id === obj.id;
                  })
                  .map((obj) => {
                    // Check if element is in main canvas (not off-canvas)
                    const { isInMainCanvas } = findElementCanvas({
                      selectedObject: obj,
                      rectangles,
                      circles,
                      images,
                      texts,
                      arrows,
                      lines,
                      doubleSidedArrows,
                      offCanvasRectangles,
                      offCanvasCircles,
                      offCanvasImages,
                      offCanvasTexts,
                      offCanvasArrows,
                      offCanvasLines,
                      offCanvasDoubleSidedArrows,
                    });

                    // Only render transformer for main canvas elements in this layer
                    if (!isInMainCanvas) return null;

                    if (
                      !stageRef.current ||
                      obj.type === "Arrow" ||
                      obj.type === "DoubleSidedArrow" ||
                      obj.type === "Line" ||
                      obj.type === "Image"
                    )
                      return null;

                    const node = stageRef.current.findOne(`#${obj.id}`);
                    if (!node || !node.getLayer()) return null;

                    // Additional safety check: ensure the node still exists in the layer
                    try {
                      const layer = node.getLayer();
                      if (
                        !layer ||
                        !layer.children ||
                        !layer.children.includes(node)
                      ) {
                        // Node has been deleted or removed from layer
                        return null;
                      }
                    } catch (e) {
                      // Node access error, likely deleted
                      return null;
                    }

                    const isTextInEditMode =
                      editTextBoxContent || showEditNote || showEditTask;
                    const anchors =
                      obj.type === "Text"
                        ? isTextInEditMode
                          ? []
                          : ["middle-left", "middle-right"]
                        : obj.type === "Image" ||
                            obj.type === "Rectangle" ||
                            obj.type === "Rect" ||
                            obj.type === "rectangle" ||
                            obj.type === "Circle"
                          ? obj?.maintainAspectRatio
                            ? ["bottom-right"]
                            : ["middle-right", "bottom-center", "bottom-right"]
                          : [
                              "top-left",
                              "top-center",
                              "top-right",
                              "middle-left",
                              "middle-right",
                              "bottom-left",
                              "bottom-center",
                              "bottom-right",
                            ];
                    // For text elements, use corner-based scaling with position adjustment
                    const boundBoxFunc =
                      obj.type === "Text"
                        ? (oldBox, newBox) => {
                            if (newBox.width < 10 || newBox.height < 10) return oldBox;
                            // Allow free transform outside stage; clamp on transform-end.
                            return { ...newBox };
                          }
                        : (oldBox, newBox) => {
                            if (newBox.width < 10 || newBox.height < 10) return oldBox;
                            const clamped = { ...newBox };
                            const activeAnchor =
                              typeof transformerRef?.current?.getActiveAnchor ===
                              "function"
                                ? transformerRef.current.getActiveAnchor()
                                : null;
                            const isResize =
                              !!activeAnchor && activeAnchor !== "rotater";
                            if (isResize && obj.type === "Circle") {
                              // Circle is center-anchored, while Transformer boxes are top-left based.
                              // Lock box origin during resize so boundary clamps don't push the node down.
                              clamped.x = oldBox.x;
                              clamped.y = oldBox.y;
                            }
                            // Allow free transform outside stage; clamp on transform-end.
                            return clamped;
                          };

                    // CRITICAL: Keep anchors visible during drag (don't hide them)
                    const isDragging =
                      (typeof node.isDragging === "function"
                        ? node.isDragging()
                        : false) ||
                      node.getAttr?.("_isHtmlDragging") ||
                      node.getAttr?.("_isDragging");
                    const effectiveAnchors = isDuplicatePlacementActive ? [] : anchors;

                    return (
                      <Transformer
                        key={`multi-transformer-${obj.id}-${
                          obj?.maintainAspectRatio || false
                        }-${anchors.join("-")}-edit-${editTextBoxContent || false}`}
                        ref={transformerRef}
                        nodes={
                          node &&
                          typeof node.getLayer === "function" &&
                          node.getLayer() &&
                          !(
                            typeof node.isDestroyed === "function" &&
                            node.isDestroyed()
                          )
                            ? [node]
                            : []
                        }
                        rotateEnabled={
                          obj.type !== "Text" && !isDuplicatePlacementActive
                        }
                        flipEnabled={false}
                        boundBoxFunc={boundBoxFunc}
                        enabledAnchors={effectiveAnchors}
                        forceUpdate={
                          selectedObject?.type === "Circle"
                            ? Date.now()
                            : selectedObject?.type === "Text"
                              ? `text-${anchors.join(",")}`
                              : undefined
                        }
                        keepRatio={obj?.maintainAspectRatio || false}
                        centeredScaling={false}
                        onTransformStart={(e) => {
                          crossStageGestureBegin(stageRef.current, e?.evt);
                        }}
                        onTransform={(e) => {
                          if (obj.type === "Line") {
                            const lineNode = e.target;
                            const scaleX = lineNode.scaleX();
                            const scaleY = lineNode.scaleY();
                            const x = lineNode.x();
                            const y = lineNode.y();

                            // Find the line in the state
                            const lineId = obj.id;
                            const currentLine = lines.find(
                              (l) => l.id === lineId,
                            );

                            if (currentLine) {
                              // Calculate new scaled points
                              const relativePoints = [
                                currentLine.points[0] - currentLine.x,
                                currentLine.points[1] - currentLine.y,
                                currentLine.points[2] - currentLine.x,
                                currentLine.points[3] - currentLine.y,
                              ];

                              const scaledPoints = relativePoints.map(
                                (coord, i) =>
                                  i % 2 === 0 ? coord * scaleX : coord * scaleY,
                              );

                              // Convert back to absolute points
                              const newAbsolutePoints = [
                                scaledPoints[0] + x,
                                scaledPoints[1] + y,
                                scaledPoints[2] + x,
                                scaledPoints[3] + y,
                              ];

                              // Calculate dimensions
                              const width = Math.abs(
                                newAbsolutePoints[2] - newAbsolutePoints[0],
                              );
                              const height = Math.abs(
                                newAbsolutePoints[3] - newAbsolutePoints[1],
                              );

                              // Reset scale to prevent accumulation
                              lineNode.scaleX(1);
                              lineNode.scaleY(1);

                              // Update line state
                              updateLineProperties(lineId, {
                                x: x,
                                y: y,
                                points: newAbsolutePoints,
                                width: width,
                                height: height,
                                skipOffset: true,
                              });
                            }
                          }
                        }}
                        onTransformEnd={(e) => {
                          crossStageGestureEnd();
                          justFinishedTransformRef.current = true;
                          if (obj.type === "Line") {
                            saveToHistory();
                          }
                        }}
                      />
                    );
                  })}
            </Layer>
          )}

          {/* Layer 7: Crosshair for paste mode */}
          {isWaitingForPaste && (
            <Layer>
              {/* Crosshair lines */}
              <Line
                points={[
                  crosshairPos.x - 20,
                  crosshairPos.y,
                  crosshairPos.x + 20,
                  crosshairPos.y,
                ]}
                stroke="#0D6EFD"
                strokeWidth={2}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
              />
              <Line
                points={[
                  crosshairPos.x,
                  crosshairPos.y - 20,
                  crosshairPos.x,
                  crosshairPos.y + 20,
                ]}
                stroke="#0D6EFD"
                strokeWidth={2}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
              />
              {/* Center circle */}
              <Circle
                x={crosshairPos.x}
                y={crosshairPos.y}
                radius={4}
                fill="#0D6EFD"
                stroke="#fff"
                strokeWidth={1}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
              />
            </Layer>
          )}
        </Stage>

        {/* Separate overlay Stage for top-layer images - appears above HTML content */}
        {/* CRITICAL: Also mount when group-dragging off→main with off-canvas top-layer images,
            so their previews (rendered here) are visible on the main canvas. */}
        {shouldShowTopLayerStage && (
          <div
            ref={(el) => {
              // Store ref to the wrapper div for positioning
              if (el) {
                el.dataset.topLayerWrapper = "true";
              }
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              margin: 0,
              padding: 0,
              // CRITICAL FIX: Use pointer-events: none to prevent blocking text element interactions
              // The handleStageMouseDown function manually checks for top-layer image hits using getIntersection
              // This allows text elements underneath to be dragged while preserving top-layer image interactions
              pointerEvents: "none",
              // CRITICAL FIX: Adjust z-index based on selection state
              // When objects are selected, main canvas stage has z-index 27, so top-layer needs to be below (26)
              // During group drag/selection, lower further to allow wrapper (z-index 25) to be visible
              zIndex: (() => {
                const hasMainTargetSinglePreview =
                  dragPreview?.target === "main" && !!dragPreview?.data;
                const hasSelection =
                  selectedObject ||
                  (selectedObjects && selectedObjects.length > 0);
                const isDraggingOrSelecting =
                  isGroupDraggingRef.current || isSelecting;
                const zIndex = hasMainTargetSinglePreview
                  ? 30
                  : hasSelection
                    ? 26
                    : isDraggingOrSelecting
                      ? 24
                      : 26;
               
                return zIndex;
              })(),
              visibility: "visible", // Ensure it's visible
              opacity: 1, // Ensure it's not transparent
              width: "100%", // CRITICAL: Ensure wrapper takes full width
              height: "100%", // CRITICAL: Ensure wrapper takes full height to match container
              overflow: "hidden", // CRITICAL: Prevent overflow from extending canvas height
            }}
          >
            <Stage
              ref={(node) => {
                // Set internal topLayerStageRef
                if (topLayerStageRef) {
                  if (typeof topLayerStageRef === "function") {
                    topLayerStageRef(node);
                  } else if (
                    topLayerStageRef &&
                    "current" in topLayerStageRef
                  ) {
                    topLayerStageRef.current = node;
                  }
                }
                // Set parent ref so MyCanvas can composite main + top-layer on leave (clear on unmount)
                if (
                  topLayerStageRefFromParent &&
                  "current" in topLayerStageRefFromParent
                ) {
                  topLayerStageRefFromParent.current = node ?? null;
                }

                // Set z-index on the Stage's container div to ensure it's above main Stage
                if (node) {
                  const trySetZIndex = () => {
                    const stage = node?.getStage?.();
                    if (stage) {
                      const container = stage.container();
                      if (container) {
                        // CRITICAL FIX: Adjust z-index based on selection state
                        // When objects are selected, main canvas stage has z-index 27, so top-layer needs to be below (26)
                        // During group drag/selection, lower further to allow wrapper (z-index 25) to be visible
                        const hasSelection =
                          selectedObject ||
                          (selectedObjects && selectedObjects.length > 0);
                        const isDraggingOrSelecting =
                          isGroupDraggingRef.current || isSelecting;
                        const hasMainTargetSinglePreview =
                          dragPreview?.target === "main" && !!dragPreview?.data;
                        const zIndexValue = hasMainTargetSinglePreview
                          ? "30"
                          : hasSelection
                            ? "26"
                            : isDraggingOrSelecting
                              ? "24"
                              : "26";

                        

                        container.style.setProperty(
                          "z-index",
                          zIndexValue,
                          "important",
                        );
                        container.style.setProperty(
                          "position",
                          "relative",
                          "important",
                        );
                        // CRITICAL FIX: Conditionally set pointer-events based on selection state
                        // When a top-layer image is selected, enable pointer events for transformer interaction
                        // OR when pointer is actually over a top-layer image (to allow selection)
                        // CRITICAL FIX: When multiple elements are selected, use "none" so selection box can be drawn on main stage
                        // CRITICAL FIX: Also disable during selection to allow selection box to work properly
                        const hasSelectedTopLayerImage =
                          selectedObject?.type === "Image" &&
                          selectedObject?.isOnTopLayer === true;
                        const hasActiveNonImageSelection =
                          (selectedObject &&
                            selectedObject.type &&
                            selectedObject.type !== "Image") ||
                          !!selectedLine ||
                          !!selectedArrow ||
                          !!selectedDoubleSidedArrow;
                        const isCurrentlySelecting = isSelecting;
                        const isMultiSelected =
                          selectedObjects && selectedObjects.length > 1;
                        const pointerEventsValue =
                          selectionPointerIsolationSideRef.current === "off"
                            ? "none"
                            : !hasActiveNonImageSelection &&
                                (hasSelectedTopLayerImage ||
                                  isPointerOverTopLayerImage) &&
                                !isCurrentlySelecting &&
                                !isMultiSelected
                              ? "auto"
                              : "none";
                        if (
                          DEBUG_TEXT_DRAG_ISSUE &&
                          (selectedObject?.type === "Text" ||
                            selectedObject?.type?.includes("Text"))
                        ) {
                        }

                        container.style.setProperty(
                          "pointer-events",
                          pointerEventsValue,
                          "important",
                        );

                        // CRITICAL FIX: Also set pointer-events on canvas based on selection state
                        const canvas = container.querySelector("canvas");
                        if (canvas) {
                          canvas.style.setProperty(
                            "pointer-events",
                            pointerEventsValue,
                            "important",
                          );
                        }
                      }
                    }
                  };

                  node.__trySetZIndex = trySetZIndex;

                  // Try immediately
                  trySetZIndex();

                  // Also try after a short delay in case Stage isn't ready yet
                  setTimeout(trySetZIndex, 0);
                  setTimeout(trySetZIndex, 50);
                }
              }}
              width={mainCanvasWidth}
              height={fixedStageHeightPx}
              pixelRatio={CANVAS_PIXEL_RATIO}
              // REMOVED: padding prop - Konva.js doesn't support native padding, and it was causing coordinate system mismatches
              // Both stages now use the same coordinate system (0,0 at top-left of stage)
              style={{
                margin: 0,
                padding: 0,
                display: "block",
                position: "relative", // Ensure proper stacking context
                visibility: "visible", // Ensure Stage is visible
                opacity: 1, // Ensure Stage is not transparent
              }}
              onMouseDown={(e) => {
                const topLayerStage = e?.target?.getStage();
                if (!topLayerStage) {
                  return;
                }
                const isOffCanvasOriginDrawActive =
                  drawingOriginCanvasRef.current === "off-canvas" &&
                  (isDrawingImage ||
                    isDrawingRect ||
                    isDrawingCircle ||
                    isDrawingLine ||
                    isDrawingArrow ||
                    isDrawingDoubleArrow ||
                    (isSelecting && selectionBoxStage === "off-canvas"));
                if (isOffCanvasOriginDrawActive) {
                  return;
                }

                // CRITICAL FIX: If we're currently transforming, ALWAYS let Konva handle it
                // This prevents any interference with rotation operations
                if (isTransformingRef.current?.isTransforming) {
                  return;
                }

                // CRITICAL FIX: Check if the click is actually on a top-layer image
                // If not, forward it to the main Stage so other elements can be clicked
                const pointerPos = topLayerStage.getPointerPosition();
                let isTopLayerImage = false;
                let isTransformerNode = false;

                if (pointerPos) {
                  // Check if there's a top-layer image at this position
                  const clickedNode = topLayerStage.getIntersection(pointerPos);

                  // DEBUG: Log what getIntersection detected
                  if (
                    clickedNode &&
                    clickedNode !== topLayerStage &&
                    clickedNode.getLayer()
                  ) {
                    const clickedNodeId = clickedNode.id();
                    const clickedNodeType = clickedNode.getClassName?.();

                    // CRITICAL FIX: Check if clicked node is part of the Transformer
                    // Transformer nodes include: Transformer, Circle (rotation anchor), Rect (border), etc.
                    // We need to exclude these to prevent interference with rotation
                    isTransformerNode =
                      clickedNodeType === "Transformer" ||
                      clickedNodeType === "Circle" || // Rotation anchor
                      clickedNodeType === "Rect" || // Border or resize anchors
                      clickedNode.getParent?.()?.getClassName?.() ===
                        "Transformer";

                    // Only check for top-layer image if it's not a transformer node
                    if (!isTransformerNode) {
                      isTopLayerImage = mainCanvasImagesTop.some(
                        (img) => img.id === clickedNodeId,
                      );
                    }
                  }
                }

                // CRITICAL FIX: If clicking on transformer node, let Konva handle it
                // This prevents interference with rotation operations
                if (isTransformerNode) {
                  return;
                }

                // If click is on a top-layer image, let Konva handle it (don't interfere)
                // CRITICAL: Stop propagation so the main stage/Background never receives this click
                // (otherwise Background onClick can clear selection when multi-select has rect+circle+image)
                if (isTopLayerImage === true) {
                  if (e.evt) {
                    e.evt.stopPropagation();
                    e.evt.stopImmediatePropagation();
                  }
                  e.cancelBubble = true;
                  return;
                }

                // Defensive guard: top-layer hit-testing can intermittently return null while interacting
                // with selected top-layer image controls/anchors. Historically we swallowed these events,
                // but that also blocked marquee selection start on empty space when a top-layer image was selected.
                // Allow forwarding for SELECT+left-mouse so the blue selection box can start like other element types.
                const hasSelectedTopLayerImage =
                  selectedObject?.type === "Image" &&
                  mainCanvasImagesTop.some((img) => img.id === selectedObject?.id);
                const shouldAllowMarqueeForward =
                  action === ACTIONS.SELECT &&
                  e?.evt?.button === 0 &&
                  !isMultiSelectMode;
                if (
                  hasSelectedTopLayerImage &&
                  !isTopLayerImage &&
                  !shouldAllowMarqueeForward
                ) {
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  e.cancelBubble = true;
                  return;
                }

                // CRITICAL: If we just finished dragging, do NOT forward (prevents clearing selection on next pointer event)
                if (justFinishedDragRef?.current) {
                  justFinishedDragRef.current = false;
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  e.cancelBubble = true;
                  return;
                }

                // Click is NOT on a top-layer image - forward to main Stage
                const mainStage = stageRef?.current;
                if (!mainStage || !e?.evt) {
                  return;
                }

                // CRITICAL FIX: For drawing actions (RECTANGLE, CIRCLE, etc.), call onPointerDown FIRST
                // This must happen before checking for elements, because drawing needs to start immediately.
                // Clone mode keeps action === SELECT but lockedAction is the shape — same as drawing (don't forward to hit element).
                const isLockedCloneShapeDraw =
                  isCloneMode &&
                  cloneTemplate &&
                  lockedAction &&
                  lockedAction !== ACTIONS.SELECT &&
                  lockedAction !== ACTIONS.PAN;
                const isDrawingAction =
                  (action !== ACTIONS.SELECT && action !== ACTIONS.PAN) ||
                  isLockedCloneShapeDraw;

                if (isDrawingAction && onPointerDown) {
                  // CRITICAL: Prevent event from bubbling to Background onClick
                  // This prevents Background from clearing selections and interfering with drawing
                  e.evt.preventDefault();
                  e.evt.stopPropagation();
                  e.cancelBubble = true;

                  // CRITICAL: Ensure stageRef.current points to mainStage for onPointerDown
                  // The handler uses stageRef.current to determine which stage is active
                  const originalStage = stageRef.current;
                  stageRef.current = mainStage;
                  try {
                    // CRITICAL: Set pointer position on mainStage BEFORE creating synthetic event
                    // onPointerDown expects the stage to have the pointer position set
                    mainStage.setPointersPositions(e.evt);
                    const mainPointerPos = mainStage.getPointerPosition();

                    if (!mainPointerPos) {
                      console.error(
                        "🟠 [TOP-LAYER] onMouseDown - No pointer position on main stage!",
                      );
                      return;
                    }

                    // Create a synthetic event for onPointerDown that matches Konva event format
                    const syntheticEvent = {
                      evt: e.evt,
                      target: mainStage,
                      currentTarget: mainStage,
                      cancelBubble: true, // Prevent bubbling
                      // Ensure getStage() returns the main stage
                      getStage: () => mainStage,
                    };
                    // Add getStage method to target if it doesn't exist
                    if (!syntheticEvent.target.getStage) {
                      syntheticEvent.target.getStage = () => mainStage;
                    }
                    onPointerDown(syntheticEvent);
                  } catch (error) {
                    console.error(
                      "🟠 [TOP-LAYER] onMouseDown - Error calling onPointerDown",
                      {
                        action,
                        error,
                        errorMessage: error?.message,
                        errorStack: error?.stack,
                      },
                    );
                  } finally {
                    // Restore original stage ref
                    stageRef.current = originalStage;
                  }
                  return; // CRITICAL: Return early for drawing actions, don't check for elements
                }

                // For non-drawing actions, check if there's an element at this position
                // Set pointer position on main Stage
                mainStage.setPointersPositions(e.evt);

                // CRITICAL FIX: Check if there's an element at this position on the main Stage
                // If so, fire the event on that element, not on the Stage
                const mainStagePointerPos = mainStage.getPointerPosition();

                if (mainStagePointerPos) {
                  const mainStageNode =
                    mainStage.getIntersection(mainStagePointerPos);

                  if (mainStageNode && mainStageNode !== mainStage) {
                    const nodeType = mainStageNode.getClassName?.();
                    const nodeId = mainStageNode.id?.();

                    // CRITICAL FIX: For SELECT action, clicks on Background Rect (id='bg') should be treated
                    // as Stage background clicks to start selection box, NOT as element clicks
                    // This prevents Background onClick from clearing selections and blocking selection box
                    if (
                      action === ACTIONS.SELECT &&
                      nodeType === "Rect" &&
                      nodeId === "bg"
                    ) {
                      // CRITICAL: In multi-select mode, don't forward Background clicks to prevent clearing selections
                      if (isMultiSelectMode) {
                        return;
                      }

                      // Treat as Stage background click - this will start selection box
                      const syntheticEvent = {
                        evt: e.evt,
                        target: mainStage,
                        currentTarget: mainStage,
                        cancelBubble: false,
                      };
                      handleStageMouseDown(syntheticEvent);
                      return;
                    }

                    // There's a real element at this position - fire event on it
                    // CRITICAL: Always forward element clicks to allow drag to start, even in multi-select mode

                    // CRITICAL FIX: For draggable elements (Groups like UnifiedLineArrowElement, Images), we need to:
                    // 1. Ensure pointer position is set correctly on the main stage (already done above)
                    // 2. Prevent event from bubbling to Background onClick (which clears selections)
                    // 3. Fire the event in a way that Konva's drag mechanism can properly handle

                    // CRITICAL: Check if this node is a draggable element or is inside a draggable Group
                    // UnifiedLineArrowElement renders as a Group, but getIntersection might return the Line/Arrow inside
                    const isGroup = nodeType === "Group";
                    const isImage = nodeType === "Image";
                    const isLine = nodeType === "Line";
                    const isArrow = nodeType === "Arrow";

                    // CRITICAL: Check if this node is a draggable element or is inside a draggable Group (UnifiedLineArrowElement, TextElement)
                    let targetNode = mainStageNode;
                    let targetNodeId = nodeId;
                    let isDraggableElement = isGroup || isImage;
                    let isTextElement = false;

                    // If it's a Line or Arrow, check if its parent Group is draggable (UnifiedLineArrowElement)
                    if ((isLine || isArrow) && !isDraggableElement) {
                      const parentGroup = mainStageNode.getParent?.();
                      if (
                        parentGroup &&
                        parentGroup.getClassName?.() === "Group"
                      ) {
                        const parentIsDraggable =
                          parentGroup.draggable?.() ?? false;
                        if (parentIsDraggable) {
                          // This is a UnifiedLineArrowElement - use the Group as the target
                          targetNode = parentGroup;
                          targetNodeId = parentGroup.id?.() || nodeId;
                          isDraggableElement = true;
                        }
                      }
                    }

                    // CRITICAL FIX: Check if this is a Text element Group
                    // Text elements use HTML content with handleHtmlMouseDown, which needs a native DOM event
                    // We can detect text elements by checking if there's an HTML div at the click position
                    // that's not part of the top-layer Stage
                    if (isGroup && isDraggableElement && e.evt) {
                      // Use elementsFromPoint to find all elements at the click position
                      // This includes elements in portals (like react-konva-utils Html components)
                      const elementsAtPoint = document.elementsFromPoint(
                        e.evt.clientX,
                        e.evt.clientY,
                      );

                      for (const element of elementsAtPoint) {
                        if (
                          element.tagName === "DIV" ||
                          element.tagName === "div"
                        ) {
                          const computedStyle =
                            window.getComputedStyle(element);

                          // Check if this div has pointer events enabled and is not a blocker or top-layer wrapper
                          if (
                            computedStyle.pointerEvents !== "none" &&
                            !element.closest(
                              '[data-top-layer-wrapper="true"]',
                            ) &&
                            !element.closest('[data-html-blocker="true"]')
                          ) {
                            // This is likely a text element's HTML content div
                            isTextElement = true;
                            break;
                          }
                        }
                      }
                    }

                    if (isDraggableElement) {
                      // CRITICAL FIX: For Text elements, dispatch native DOM event on HTML div
                      // Text elements use handleHtmlMouseDown which is attached to the HTML content, not the Konva Group
                      // react-konva-utils Html components render in portals, so we need to search the entire document
                      if (isTextElement && e.evt) {
                        // Stop the original event to prevent it from interfering
                        e.evt.preventDefault();
                        e.evt.stopPropagation();
                        e.evt.stopImmediatePropagation();

                        // Search for HTML divs at the click position (react-konva-utils renders in portals)
                        const elementsAtPoint = document.elementsFromPoint(
                          e.evt.clientX,
                          e.evt.clientY,
                        );
                        let foundTextDiv = false;

                        // Look for div elements at the click position
                        for (const element of elementsAtPoint) {
                          if (
                            element.tagName === "DIV" ||
                            element.tagName === "div"
                          ) {
                            const computedStyle =
                              window.getComputedStyle(element);

                            // Check if this div has pointer events enabled (text content divs do)
                            // and is not the top-layer Stage container
                            if (
                              computedStyle.pointerEvents !== "none" &&
                              !element.closest(
                                '[data-top-layer-wrapper="true"]',
                              ) &&
                              !element.closest('[data-html-blocker="true"]')
                            ) {
                              const rect = element.getBoundingClientRect();

                              // Verify the click is within the div bounds
                              if (
                                e.evt.clientX >= rect.left &&
                                e.evt.clientX <= rect.right &&
                                e.evt.clientY >= rect.top &&
                                e.evt.clientY <= rect.bottom
                              ) {
                                // Create and dispatch a native DOM mousedown event on the HTML div
                                // This will trigger handleHtmlMouseDown which starts the text drag
                                // Use setTimeout to ensure this happens after the current event handling
                                setTimeout(() => {
                                  const nativeEvent = new MouseEvent(
                                    "mousedown",
                                    {
                                      bubbles: true,
                                      cancelable: true,
                                      clientX: e.evt.clientX,
                                      clientY: e.evt.clientY,
                                      button: e.evt.button,
                                      buttons: e.evt.buttons,
                                      ctrlKey: e.evt.ctrlKey,
                                      shiftKey: e.evt.shiftKey,
                                      altKey: e.evt.altKey,
                                      metaKey: e.evt.metaKey,
                                      view: window,
                                    },
                                  );

                                  // Dispatch the event on the HTML div to trigger handleHtmlMouseDown
                                  element.dispatchEvent(nativeEvent);
                                }, 0);

                                foundTextDiv = true;

                                // Found the text div, stop searching
                                break;
                              }
                            }
                          }
                        }

                        if (foundTextDiv) {
                          // Don't fire on Konva Group - the HTML handler will take care of it
                          return;
                        }
                      }
                      // Two-click for images: if another element is selected, first click only clears; do not fire mousedown so the image never gets a click
                      if (isImage && nodeId) {
                        const clickedImageForTwoClick = images.find(
                          (img) => img.id === nodeId,
                        );
                        if (
                          clickedImageForTwoClick &&
                          !isMultiSelectMode &&
                          selectedObject &&
                          selectedObject.id !== nodeId
                        ) {
                          setSelectedObject(null);
                          setSelectedObjects([]);
                          setSelectedImage(null);
                          setSelectedText(null);
                          setSelectedTextId(null);
                          setSelectedArrow(null);
                          setSelectedLine(null);
                          setSelectedDoubleSidedArrow(null);
                          setShowTextBox(false);
                          return;
                        }
                      }
                      // CRITICAL: For draggable elements, we need to ensure Konva can properly start drag
                      // The issue is that when forwarding from top layer, Konva might not recognize it as a drag start
                      // We need to ensure the event has all necessary properties and doesn't bubble to Background

                      // Stop propagation on the original event to prevent Background onClick
                      if (e.evt) {
                        e.evt.stopPropagation();
                        e.evt.stopImmediatePropagation();
                      }

                      // CRITICAL: Ensure pointer position is set on mainStage before firing event
                      // This is essential for Konva's drag mechanism to work correctly
                      mainStage.setPointersPositions(e.evt);

                      const finalPointerPos = mainStage.getPointerPosition();

                      // CRITICAL: For UnifiedLineArrowElement (Group), we need to ensure the event reaches the Group
                      // and that Konva can properly initialize the drag operation
                      // Create a proper Konva event structure
                      const konvaEvent = {
                        evt: e.evt,
                        target: targetNode,
                        currentTarget: targetNode,
                        cancelBubble: true, // Prevent bubbling to Background
                        // Ensure getStage() returns the main stage
                        getStage: () => mainStage,
                      };

                      // Add getStage to target if it doesn't exist
                      if (!konvaEvent.target.getStage) {
                        konvaEvent.target.getStage = () => mainStage;
                      }

                      // Fire the mousedown event on the target node (Group for UnifiedLineArrowElement)
                      // Konva will handle this and start the drag operation if the element is draggable
                      targetNode.fire("mousedown", konvaEvent);

                      // CRITICAL FIX: For images, also trigger the selection logic directly
                      // This ensures images get selected even if Konva drag doesn't work properly
                      if (isImage && nodeId) {
                        // Find the image in the images array and trigger selection
                        const clickedImage = images.find(
                          (img) => img.id === nodeId,
                        );
                        if (clickedImage) {
                          // Two-click behavior: if another element is selected, first click only clears; second click selects
                          if (
                            !isMultiSelectMode &&
                            selectedObject &&
                            selectedObject.id !== nodeId
                          ) {
                            setSelectedObject(null);
                            setSelectedObjects([]);
                            setSelectedImage(null);
                            setSelectedText(null);
                            setSelectedTextId(null);
                            setSelectedArrow(null);
                            setSelectedLine(null);
                            setSelectedDoubleSidedArrow(null);
                            setShowTextBox(false);
                            return;
                          }
                          // Clear previous selections (or we're on second click with nothing selected)
                          setSelectedObject(null);
                          setSelectedObjects([]);
                          setSelectedImage(null);
                          setSelectedText(null);
                          setSelectedTextId(null);
                          setSelectedArrow(null);
                          setSelectedLine(null);
                          setSelectedDoubleSidedArrow(null);

                          // Set the clicked image as selected
                          setSelectedObject({
                            ...clickedImage,
                            type: "Image",
                          });
                          setSelectedImage(clickedImage);
                        }
                      }

                      // CRITICAL: Track that we've fired mousedown on this element
                      // This will be used to prevent Background onClick from clearing selections
                      recentDraggableElementMousedownRef.current = {
                        nodeId: targetNodeId,
                        timestamp: Date.now(),
                      };
                    } else {
                      // Two-click for images: if another element is selected, first click only clears; do not fire mousedown so the image never gets a click
                      if (isImage && nodeId) {
                        const clickedImageForTwoClick = images.find(
                          (img) => img.id === nodeId,
                        );
                        if (
                          clickedImageForTwoClick &&
                          !isMultiSelectMode &&
                          selectedObject &&
                          selectedObject.id !== nodeId
                        ) {
                          setSelectedObject(null);
                          setSelectedObjects([]);
                          setSelectedImage(null);
                          setSelectedText(null);
                          setSelectedTextId(null);
                          setSelectedArrow(null);
                          setSelectedLine(null);
                          setSelectedDoubleSidedArrow(null);
                          setShowTextBox(false);
                          return;
                        }
                      }
                      // For non-draggable elements, fire normally
                      mainStageNode.fire("mousedown", { evt: e.evt });

                      // CRITICAL FIX: For non-draggable images, also trigger selection logic directly
                      if (isImage && nodeId) {
                        const clickedImage = images.find(
                          (img) => img.id === nodeId,
                        );
                        if (clickedImage) {
                          // Two-click behavior: if another element is selected, first click only clears; second click selects
                          if (
                            !isMultiSelectMode &&
                            selectedObject &&
                            selectedObject.id !== nodeId
                          ) {
                            setSelectedObject(null);
                            setSelectedObjects([]);
                            setSelectedImage(null);
                            setSelectedText(null);
                            setSelectedTextId(null);
                            setSelectedArrow(null);
                            setSelectedLine(null);
                            setSelectedDoubleSidedArrow(null);
                            setShowTextBox(false);
                            return;
                          }
                          // Clear previous selections (or we're on second click with nothing selected)
                          setSelectedObject(null);
                          setSelectedObjects([]);
                          setSelectedImage(null);
                          setSelectedText(null);
                          setSelectedTextId(null);
                          setSelectedArrow(null);
                          setSelectedLine(null);
                          setSelectedDoubleSidedArrow(null);

                          // Set the clicked image as selected
                          setSelectedObject({
                            ...clickedImage,
                            type: "Image",
                          });
                          setSelectedImage(clickedImage);
                        }
                      }
                    }
                    return;
                  }
                }

                // No element at this position - treat as Stage background click
                // CRITICAL: In multi-select mode, don't forward empty space clicks to prevent Background onClick from clearing selections
                if (isMultiSelectMode) {
                  return;
                }

                const syntheticEvent = {
                  evt: e.evt,
                  target: mainStage,
                  currentTarget: mainStage,
                  cancelBubble: false,
                };
                handleStageMouseDown(syntheticEvent);
              }}
              onMouseMove={(e) => {
                const topLayerStage = e?.target?.getStage();
                if (!topLayerStage) return;
                const isOffCanvasOriginDrawActive =
                  drawingOriginCanvasRef.current === "off-canvas" &&
                  (isDrawingImage ||
                    isDrawingRect ||
                    isDrawingCircle ||
                    isDrawingLine ||
                    isDrawingArrow ||
                    isDrawingDoubleArrow ||
                    (isSelecting && selectionBoxStage === "off-canvas"));
                if (isOffCanvasOriginDrawActive) {
                  return;
                }

                // CRITICAL FIX: If we're currently transforming, ALWAYS let Konva handle it
                // This prevents any interference with rotation operations
                if (isTransformingRef.current?.isTransforming) {
                  return;
                }

                // CRITICAL FIX: Check if mouse is over a top-layer image
                // If not, always forward to main Stage for crosshair, cursor, and drawing
                const pointerPos = topLayerStage.getPointerPosition();
                let isOverTopLayerImage = false;
                let isTransformerNode = false;

                if (pointerPos) {
                  const clickedNode = topLayerStage.getIntersection(pointerPos);
                  if (
                    clickedNode &&
                    clickedNode !== topLayerStage &&
                    clickedNode.getLayer()
                  ) {
                    const clickedNodeType = clickedNode.getClassName?.();

                    // CRITICAL FIX: Check if clicked node is part of the Transformer
                    // Transformer nodes include: Transformer, Circle (rotation anchor), Rect (border), etc.
                    isTransformerNode =
                      clickedNodeType === "Transformer" ||
                      clickedNodeType === "Circle" || // Rotation anchor
                      clickedNodeType === "Rect" || // Border or resize anchors
                      clickedNode.getParent?.()?.getClassName?.() ===
                        "Transformer";

                    // Only check for top-layer image if it's not a transformer node
                    if (!isTransformerNode) {
                      isOverTopLayerImage = mainCanvasImagesTop.some(
                        (img) => img.id === clickedNode.id(),
                      );
                    }
                  }
                }

                // CRITICAL FIX: If over transformer node, don't forward (let transformer handle it)
                // This prevents interference with rotation operations
                if (isTransformerNode) {
                  return;
                }

                // If mouse is over a top-layer image, don't forward (let image handle it)
                if (isOverTopLayerImage && e?.target !== e?.target.getStage()) {
                  if (DEBUG_TEXT_DRAG_ISSUE) {
                  }
                  return;
                }

                // CRITICAL FIX: Get mainStage reference early so we can use it in checks
                const mainStage = stageRef?.current;

                // CRITICAL FIX: Check if we're currently dragging selected objects
                // If dragging, we MUST forward mouse move events to allow drag to work
                const isDragging =
                  isGroupDraggingRef?.current ||
                  wrapperDragStartRef?.current?.elementPositions?.length > 0 ||
                  individualElementDragStartRef?.current?.elementPositions
                    ?.length > 0;

                // CRITICAL FIX: Also check if any element on the main Stage is currently being dragged
                // This is needed for individual element drags (like UnifiedLineArrowElement) that aren't tracked by group drag refs
                let isIndividualElementDragging = false;
                if (mainStage && pointerPos) {
                  // Check if there's a draggable element at this position that might be dragging
                  const nodeAtPos = mainStage.getIntersection(pointerPos);
                  if (nodeAtPos && nodeAtPos !== mainStage) {
                    // Check if this node or its parent Group is draggable and might be dragging
                    const node =
                      nodeAtPos.getType?.() === "Group"
                        ? nodeAtPos
                        : nodeAtPos.getParent?.();
                    if (node && node.draggable?.()) {
                      // Check if Konva thinks this node is being dragged
                      // Konva sets an internal _drag property when dragging
                      isIndividualElementDragging =
                        node.isDragging?.() || false;

                      // CRITICAL: If drag has started, clear the recent mousedown ref
                      // This prevents the click prevention logic from interfering with future interactions
                      if (
                        isIndividualElementDragging &&
                        recentDraggableElementMousedownRef.current.nodeId
                      ) {
                        recentDraggableElementMousedownRef.current = {
                          nodeId: null,
                          timestamp: 0,
                        };
                      }
                    }
                  }
                }

                const isAnyDragging = isDragging || isIndividualElementDragging;

                // CRITICAL FIX: Check if a UnifiedLineArrowElement is being dragged via global flag
                // This ensures drag detection works even when Konva's isDragging() is unreliable
                const isUnifiedLineArrowDragging =
                  window.isUnifiedLineArrowDragging === true;
                const finalIsDragging =
                  isAnyDragging || isUnifiedLineArrowDragging;

                // CRITICAL FIX: Don't forward events to main stage in multi-select mode UNLESS we're dragging
                // This prevents Background onClick from clearing selections, but allows drag operations
                if (isMultiSelectMode && !finalIsDragging) {
                  return;
                }

                // CRITICAL FIX: Always forward mouse move to main Stage when not over top-layer image
                // This allows crosshair, cursor, and drawing to work properly
                if (mainStage && e?.evt) {
                  // Set pointer position on main Stage
                  mainStage.setPointersPositions(e.evt);

                  // CRITICAL: For drawing actions, ensure stageRef.current points to mainStage
                  // This allows onPointerMove (called by handleStageMouseMove) to work correctly
                  const isLockedCloneShapeDrawMove =
                    isCloneMode &&
                    cloneTemplate &&
                    lockedAction &&
                    lockedAction !== ACTIONS.SELECT &&
                    lockedAction !== ACTIONS.PAN;
                  const isDrawingAction =
                    (action !== ACTIONS.SELECT && action !== ACTIONS.PAN) ||
                    isLockedCloneShapeDrawMove;
                  const originalStage = stageRef.current;
                  if (isDrawingAction) {
                    stageRef.current = mainStage;
                  }

                  try {
                    // Create a synthetic event with the main Stage as target
                    const syntheticEvent = {
                      evt: e.evt,
                      target: mainStage,
                      currentTarget: mainStage,
                      cancelBubble: false,
                    };

                    // Call handleStageMouseMove directly (which calls onPointerMove for drawing and updates crosshair)
                    handleStageMouseMove(syntheticEvent);
                  } finally {
                    if (isDrawingAction) {
                      stageRef.current = originalStage;
                    }
                  }
                } else {
                  // Fallback to old forwarding method
                  const forwarded = forwardEventToMainStage(e, "mousemove");
                  if (!forwarded) {
                    handleStageMouseMove(e);
                  }
                }
              }}
              onMouseUp={(e) => {
                const isOffCanvasOriginDrawActive =
                  drawingOriginCanvasRef.current === "off-canvas" &&
                  (isDrawingImage ||
                    isDrawingRect ||
                    isDrawingCircle ||
                    isDrawingLine ||
                    isDrawingArrow ||
                    isDrawingDoubleArrow ||
                    (isSelecting && selectionBoxStage === "off-canvas"));
                if (isOffCanvasOriginDrawActive) {
                  return;
                }
                const isLockedCloneShapeDrawUp =
                  isCloneMode &&
                  cloneTemplate &&
                  lockedAction &&
                  lockedAction !== ACTIONS.SELECT &&
                  lockedAction !== ACTIONS.PAN;
                // CRITICAL FIX: For top-layer Stage, handle mouseup differently based on target
                // If it's on an element, ignore it (let Konva handle drag end)
                // If it's on empty space, forward to main Stage for drawing operations.
                // Locked clone draw uses action SELECT — must still finalize when target is a Layer child.
                if (
                  !isLockedCloneShapeDrawUp &&
                  e?.target !== e?.target?.getStage()
                ) {
                  // This is an element (like an image), not the stage background
                  // CRITICAL: Completely ignore mouseup on elements - let Konva handle drag end
                  // This prevents any interference with drag operations
                  return;
                }

                // CRITICAL FIX: When mouseup on empty space, forward to main Stage
                // This allows drawing operations (rectangle, circle, etc.) to complete
                const mainStage = stageRef?.current;
                if (mainStage && e?.evt) {
                  // Set pointer position on main Stage
                  mainStage.setPointersPositions(e.evt);

                  // CRITICAL: For drawing actions, ensure stageRef.current points to mainStage
                  // This allows onPointerUp (called by handleStageMouseUp) to work correctly
                  const isDrawingAction =
                    (action !== ACTIONS.SELECT && action !== ACTIONS.PAN) ||
                    isLockedCloneShapeDrawUp;
                  const originalStage = stageRef.current;
                  if (isDrawingAction) {
                    stageRef.current = mainStage;
                  }

                  try {
                    // Create a synthetic event with the main Stage as target
                    const syntheticEvent = {
                      evt: e.evt,
                      target: mainStage,
                      currentTarget: mainStage,
                      cancelBubble: false,
                    };

                    // Call handleStageMouseUp directly (which calls onPointerUp for drawing)
                    handleStageMouseUp(syntheticEvent);
                  } finally {
                    if (isDrawingAction) {
                      stageRef.current = originalStage;
                    }
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (e?.target === e?.target.getStage()) {
                  handleStageMouseLeave(e);
                }
              }}
              onClick={(e) => {
                const topLayerStage = e?.target?.getStage();
                if (!topLayerStage) return;

                if (!isPrimaryMouseButton(e.evt)) {
                  return;
                }

                // CRITICAL FIX: Always clear the mousedown ref on top-layer stage click
                // This prevents the two-click deselection issue
                const recentMousedown =
                  recentDraggableElementMousedownRef.current;
                const clickedOnStage = e?.target === topLayerStage;

                // Clear the ref immediately on any top-layer stage click
                if (recentMousedown.nodeId) {
                  recentDraggableElementMousedownRef.current = {
                    nodeId: null,
                    timestamp: 0,
                  };
                }

                // CRITICAL FIX: For drawing actions, prevent Background onClick from firing
                // This prevents Background from clearing selections and interfering with drawing
                // BUT: Allow clone mode to work - if clone mode is active, let Background handle clone creation
                const hasCloneTemplates =
                  cloneTemplates && cloneTemplates.length > 0;
                const hasCloneTemplate = cloneTemplate !== null;
                const isCloneModeActive = isCloneMode || lockedAction;
                const isDrawingAction =
                  action !== ACTIONS.SELECT && action !== ACTIONS.PAN;

                if (
                  isDrawingAction &&
                  !(
                    isCloneModeActive &&
                    (hasCloneTemplates || hasCloneTemplate)
                  )
                ) {
                  e.evt.preventDefault();
                  e.evt.stopPropagation();
                  e.cancelBubble = true;
                  return; // Don't forward click events for drawing actions (unless in clone mode)
                }

                // CRITICAL FIX: If clone mode is active, manually trigger clone creation for top-layer clicks
                // This ensures clone creation works even when clicking on top-layer images
                if (
                  isCloneModeActive &&
                  lockedAction === ACTIONS.SELECT &&
                  (hasCloneTemplates || hasCloneTemplate) &&
                  cloneElementsAtPosition
                ) {
                  const mainStage = stageRef?.current;
                  if (mainStage && e?.evt) {
                    // Convert top-layer coordinates to main stage coordinates
                    mainStage.setPointersPositions(e.evt);
                    const pointerPos = mainStage.getPointerPosition();
                    if (pointerPos) {
                      // Call cloneElementsAtPosition with main stage coordinates
                      cloneElementsAtPosition(
                        pointerPos.x,
                        pointerPos.y,
                        stageRef,
                        e.evt?.clientX,
                        e.evt?.clientY,
                      );

                      // Prevent further event handling
                      e.evt.preventDefault();
                      e.evt.stopPropagation();
                      e.cancelBubble = true;
                      return;
                    }
                  }
                }

                // CRITICAL FIX: Check if the click is actually on a top-layer image
                // If not, forward it to the main Stage so other elements can be clicked
                const pointerPos = topLayerStage.getPointerPosition();
                if (pointerPos) {
                  const clickedNode = topLayerStage.getIntersection(pointerPos);

                  // DEBUG: Log what getIntersection detected on onClick
                  const isTopLayerImage =
                    clickedNode &&
                    clickedNode !== topLayerStage &&
                    clickedNode.getLayer() &&
                    mainCanvasImagesTop.some(
                      (img) => img.id === clickedNode.id(),
                    );

                  // If click is on a real top-layer image, never forward to main stage.
                  // Forwarding causes stage/background click handlers to clear selection
                  // and makes top-layer transformer anchors appear broken.
                  if (isTopLayerImage) {
                    e.evt?.preventDefault?.();
                    e.evt?.stopPropagation?.();
                    e.evt?.stopImmediatePropagation?.();
                    e.cancelBubble = true;
                    return;
                  }

                  const hasSelectedTopLayerImage =
                    selectedObject?.type === "Image" &&
                    mainCanvasImagesTop.some((img) => img.id === selectedObject?.id);
                  // Keep protection for transient top-layer miss while interacting with transformer/image internals,
                  // but allow true empty-stage clicks to pass through so background can deselect.
                  if (hasSelectedTopLayerImage && !isTopLayerImage && !clickedOnStage) {
                    e.evt?.preventDefault?.();
                    e.evt?.stopPropagation?.();
                    e.cancelBubble = true;
                    return;
                  }

                  // CRITICAL: If we just finished dragging a top-layer image, do NOT forward to main stage
                  // (the click after drag end can have getIntersection=stage and would clear selection)
                  if (!isTopLayerImage && justFinishedDragRef?.current) {
                    justFinishedDragRef.current = false;
                    e.evt?.preventDefault?.();
                    e.evt?.stopPropagation?.();
                    e.cancelBubble = true;
                    return;
                  }
                  // NOTE: Additional protection for "just dragged" image is handled via justFinishedDragRef above.

                  if (!isTopLayerImage) {
                    // Click is not on a top-layer image - forward to main Stage
                    const mainStage = stageRef?.current;
                    if (mainStage && e?.evt) {
                      // Set pointer position on main Stage
                      mainStage.setPointersPositions(e.evt);

                      // CRITICAL FIX: Check if there's an element at this position on the main Stage
                      // If so, fire the event on that element, not on the Stage
                      const mainStagePointerPos =
                        mainStage.getPointerPosition();
                      if (mainStagePointerPos) {
                        const mainStageNode =
                          mainStage.getIntersection(mainStagePointerPos);
                        if (mainStageNode && mainStageNode !== mainStage) {
                          const nodeType = mainStageNode.getClassName?.();
                          const nodeId = mainStageNode.id?.();

                          // CRITICAL: Check if this node is a draggable element or is inside a draggable Group
                          // UnifiedLineArrowElement renders as a Group, but getIntersection might return the Line/Arrow inside
                          const isGroup = nodeType === "Group";
                          const isImage = nodeType === "Image";
                          const isLine = nodeType === "Line";
                          const isArrow = nodeType === "Arrow";

                          // Check if the node itself is draggable, or if it's inside a draggable Group (UnifiedLineArrowElement)
                          let targetNode = mainStageNode;
                          let targetNodeId = nodeId;
                          let isDraggableElement = isGroup || isImage;

                          // If it's a Line or Arrow, check if its parent Group is draggable (UnifiedLineArrowElement)
                          if ((isLine || isArrow) && !isDraggableElement) {
                            const parentGroup = mainStageNode.getParent?.();
                            if (
                              parentGroup &&
                              parentGroup.getClassName?.() === "Group"
                            ) {
                              const parentIsDraggable =
                                parentGroup.draggable?.() ?? false;
                              if (parentIsDraggable) {
                                // This is a UnifiedLineArrowElement - use the Group as the target
                                targetNode = parentGroup;
                                targetNodeId = parentGroup.id?.() || nodeId;
                                isDraggableElement = true;
                              }
                            }
                          }

                          // CRITICAL FIX: For draggable elements, prevent click from bubbling to Background
                          // This prevents Background onClick from clearing selections when clicking on elements
                          if (isDraggableElement) {
                            // Stop propagation to prevent Background onClick
                            if (e.evt) {
                              e.evt.stopPropagation();
                              e.evt.stopImmediatePropagation();
                            }

                            // Fire click event on the target node (Group for UnifiedLineArrowElement) with cancelBubble set
                            const clickEvent = {
                              evt: e.evt,
                              target: targetNode,
                              currentTarget: targetNode,
                              cancelBubble: true, // Prevent bubbling to Background
                              getStage: () => mainStage,
                            };

                            // Add getStage to target if it doesn't exist
                            if (!clickEvent.target.getStage) {
                              clickEvent.target.getStage = () => mainStage;
                            }

                            targetNode.fire("click", clickEvent);

                            return;
                          }

                          // For non-draggable elements, fire click normally
                          mainStageNode.fire("click", { evt: e.evt });
                          return;
                        }
                      }

                      // No element at this position - treat as Stage background click
                      const syntheticEvent = {
                        evt: e.evt,
                        target: mainStage,
                        currentTarget: mainStage,
                        cancelBubble: false,
                      };
                      onClick?.(syntheticEvent);
                      return;
                    }
                  }
                }

                // If click is on a top-layer image, let the element handle it
                if (e?.target !== e?.target.getStage()) {
                  // This is a top-layer image element - let it handle its own click
                  return;
                }

                // Click is on stage background, forward to main stage
                const forwarded = forwardEventToMainStage(e, "click");
                if (!forwarded) {
                  onClick?.(e);
                }
              }}
              onDblClick={(e) => {
                // CRITICAL FIX: Allow image elements to handle their own double-clicks
                // Only forward to main stage if the double-click is on the stage background (not on an element)
                if (e?.target === e?.target.getStage()) {
                  // Double-click is on stage background, forward to main stage
                  const forwarded = forwardEventToMainStage(e, "dblclick");
                  if (!forwarded) {
                    onDoubleClick?.(e);
                  }
                }
                // If e.target is not the stage (i.e., it's an image element),
                // the element's own onDblClick handler will fire automatically
              }}
            >
              <Layer>
                {/* Demoted top-layer images: Only render when NO selection exists
                     When there IS selection, they render in Layer 5 of main stage instead */}
                {!selectedObject &&
                  !(selectedObjects && selectedObjects.length > 0) &&
                  selectionOcclusionImages.map((imageObj) => (
                    <React.Fragment
                      key={`top-stage-selection-occlusion-${imageObj.id}`}
                    >
                      {renderImageElement({
                        imageObj,
                        isOffCanvas: false,
                        sourceStageRef: topLayerStageRef.current,
                        opacity: getElementOpacity(imageObj.id, "Image"),
                      })}
                    </React.Fragment>
                  ))}
                {[...mainCanvasImagesTop]
                  .sort((a, b) => {
                    const aSelected = selectedTopPriorityImageIds.has(a.id);
                    const bSelected = selectedTopPriorityImageIds.has(b.id);
                    if (aSelected !== bSelected) return aSelected ? 1 : -1;
                    return 0;
                  })
                  .map((imageObj) => {
                    // CRITICAL FIX: For top-layer images in selectedObjects during group drag,
                    // keep them visible and let handleGroupDragMove update their positions
                    // Don't hide them like non-top-layer images because they're on a separate stage
                    const isInGroupDrag =
                      isGroupDraggingRef.current &&
                      selectedObjects?.some(
                        (obj) => obj.id === imageObj.id && obj.type === "Image",
                      );

                    // Check if this image is being individually dragged (has an active dragPreview)
                    // CRITICAL: dragPreview.data.id has "-ghost" suffix, so we need to check the original id
                    // We can get it from dragPreviewStateRef or by removing "-ghost" from dragPreview.data.id
                    const dragPreviewId =
                      dragPreview?.data?.id?.replace("-ghost", "") ||
                      dragPreviewStateRef.current?.id;
                    // CRITICAL FIX: Check for both "main" and "offCanvas" targets to hide original image during drag
                    // When dragging to off-canvas, we need to hide the original image on main canvas
                    const isBeingIndividuallyDragged =
                      dragPreview &&
                      (dragPreview.target === "main" ||
                        dragPreview.target === "offCanvas") &&
                      dragPreview.type === "Image" &&
                      dragPreviewId === imageObj.id;

                    // CRITICAL FIX: Also check selectedObject for _isDraggingToOffCanvas flag
                    // This provides an additional check to ensure the image stays hidden when dragging to off-canvas
                    const isDraggingToOffCanvas =
                      selectedObject?.id === imageObj.id &&
                      selectedObject?.type === "Image" &&
                      selectedObject?._isDraggingToOffCanvas === true;

                    // CRITICAL FIX: Check if this top-layer image is part of a group drag to off-canvas
                    // If so, hide the original image since the preview will be shown on off-canvas stage
                    const isBeingDraggedToOffCanvasInGroup =
                      isInGroupDrag &&
                      groupDragPreviews.some(
                        (preview) =>
                          preview.target === "offCanvas" &&
                          preview.type === "Image" &&
                          preview.data?.id?.replace("-ghost", "") ===
                            imageObj.id,
                      );

                    // CRITICAL FIX: When dragging to off-canvas, keep the image in DOM but make it invisible
                    // Returning null removes the node from DOM, causing Konva to fire onDragEnd prematurely
                    // Instead, use opacity: 0 to hide it while keeping it in DOM so drag can continue
                    const isDraggingToOffCanvasIndividual =
                      (isBeingIndividuallyDragged &&
                        dragPreview?.target === "offCanvas") ||
                      isDraggingToOffCanvas;

                    if (
                      isDraggingToOffCanvasIndividual ||
                      isBeingDraggedToOffCanvasInGroup
                    ) {
                      // CRITICAL: Keep image in DOM but invisible (opacity: 0) to prevent premature drag end
                      // CRITICAL FIX: Get original position directly from images array, not from imageObj
                      // imageObj might be stale or updated, so we need to get fresh data from images array
                      // This ensures we always use the actual original position, not converted coordinates
                      const originalImageFromArray = images.find(
                        (img) => img.id === imageObj.id,
                      );
                      const originalImageObj = originalImageFromArray
                        ? {
                            ...originalImageFromArray,
                            // Use the original position from the images array
                            x: originalImageFromArray.x,
                            y: originalImageFromArray.y,
                          }
                        : {
                            ...imageObj,
                            // Fallback to imageObj if not found in array
                            x: imageObj.x,
                            y: imageObj.y,
                          };
                      return renderImageElement({
                        imageObj: originalImageObj,
                        isOffCanvas: false,
                        sourceStageRef: topLayerStageRef.current,
                        opacity: 0, // Invisible but still in DOM
                      });
                    }

                    // Render the image normally (including during group drag on main canvas)
                    // handleGroupDragMove will update the Konva node position directly
                    const elementOpacity = getElementOpacity(
                      imageObj.id,
                      "Image",
                    );
                    return renderImageElement({
                      imageObj,
                      isOffCanvas: false,
                      sourceStageRef: topLayerStageRef.current,
                      opacity: elementOpacity,
                    });
                  })}
                {(() => {
                  const shouldRender =
                    dragPreview &&
                    dragPreview.target === "main" &&
                    dragPreview.type === "Image" &&
                    dragPreview.data?.isOnTopLayer;

                  if (shouldRender) {
                    return renderImageElement({
                      imageObj: dragPreview.data,
                      isOffCanvas: false,
                      sourceStageRef: topLayerStageRef.current,
                      isPreview: true,
                    });
                  }
                  return null;
                })()}
                {/* Render single-item main-target drag preview above main top-layer images.
                    Main-stage previews can sit under this top-layer stage; mirroring the
                    preview here ensures off→main ghost visibility over top-layer content. */}
                {(() => {
                  if (
                    !dragPreview ||
                    dragPreview.target !== "main" ||
                    !dragPreview.data
                  ) {
                    return null;
                  }

                  // Top-layer image preview is already handled by the block above.
                  if (
                    dragPreview.type === "Image" &&
                    dragPreview.data?.isOnTopLayer === true
                  ) {
                    return null;
                  }

                  const previewProps = {
                    isOffCanvas: false,
                    sourceStageRef: topLayerStageRef.current,
                    isPreview: true,
                  };
                  const topLayerPreviewData = {
                    ...dragPreview.data,
                    // Ensure mirrored preview renders above top-layer images in this same layer.
                    zIndex: 999999,
                    opacity: 1,
                  };

                  switch (dragPreview.type) {
                    case "Image":
                      return renderImageElement({
                        imageObj: topLayerPreviewData,
                        ...previewProps,
                      });
                    case "Rect":
                      return renderRectangleElement({
                        rectangle: topLayerPreviewData,
                        ...previewProps,
                      });
                    case "Circle":
                      return renderCircleElement({
                        circle: topLayerPreviewData,
                        ...previewProps,
                      });
                    case "Text":
                      return renderTextElement({
                        text: topLayerPreviewData,
                        ...previewProps,
                      });
                    case "Line":
                      return renderLineElement({
                        line: topLayerPreviewData,
                        ...previewProps,
                      });
                    case "Arrow":
                      return renderArrowElement({
                        arrow: topLayerPreviewData,
                        ...previewProps,
                      });
                    case "DoubleSidedArrow":
                      return renderDoubleSidedArrowElement({
                        arrow: topLayerPreviewData,
                        ...previewProps,
                      });
                    default:
                      return null;
                  }
                })()}
                {/* Keep drag-preview transformer above main top-layer images as well. */}
                {(() => {
                  if (
                    !dragPreview ||
                    dragPreview.target !== "main" ||
                    !topLayerStageRef?.current ||
                    !dragPreview.data?.id
                  ) {
                    return null;
                  }

                  const supportsTransformer =
                    dragPreview.type === "Image" ||
                    dragPreview.type === "Rect" ||
                    dragPreview.type === "Circle";
                  if (!supportsTransformer) return null;

                  // Top-layer image transformer is already handled by existing top-layer logic.
                  if (
                    dragPreview.type === "Image" &&
                    dragPreview.data?.isOnTopLayer === true
                  ) {
                    return null;
                  }

                  const previewNode = topLayerStageRef.current.findOne(
                    `#${dragPreview.data.id}`,
                  );
                  if (!previewNode) return null;

                  const anchors = dragPreview.data.maintainAspectRatio
                    ? ["bottom-right"]
                    : ["middle-right", "bottom-center", "bottom-right"];

                  return (
                    <Transformer
                      key={`toplayer-main-preview-transformer-${dragPreview.data.id}`}
                      nodes={[previewNode]}
                      rotateEnabled={!isDuplicatePlacementActive}
                      flipEnabled={false}
                      enabledAnchors={isDuplicatePlacementActive ? [] : anchors}
                      keepRatio={dragPreview.data.maintainAspectRatio || false}
                      centeredScaling={false}
                      visible={true}
                      listening={false}
                    />
                  );
                })()}

                {/* Group drag previews for top-layer images */}
                {(() => {
                  const allImagePreviews = groupDragPreviews.filter(
                    (p) => p.type === "Image",
                  );
                  const topLayerPreviews = groupDragPreviews.filter(
                    (preview) => {
                      const matches =
                        preview.target === "main" &&
                        preview.type === "Image" &&
                        preview.data?.isOnTopLayer === true;
                      if (preview.type === "Image") {
                      }
                      return matches;
                    },
                  );
                  return topLayerPreviews.map((preview, index) => {
                    if (!preview.data) {
                      return null;
                    }
                    return (
                      <React.Fragment
                        key={`group-preview-toplayer-${preview.data.id || index}`}
                      >
                        {renderImageElement({
                          imageObj: preview.data,
                          isOffCanvas: false,
                          sourceStageRef: topLayerStageRef.current,
                          isPreview: true,
                        })}
                      </React.Fragment>
                    );
                  });
                })()}
                <Transformer
                  // IMPORTANT: Transformer must stay mounted during resize/rotate.
                  // Using `selectedObject.width/height` in the key remounts the component mid-transform,
                  // which can prevent `onTransformEnd` from firing (and your TOP-LAYER logs won’t appear).
                  // Keep the key stable based on identity + off-canvas drag mode only.
                  key={`top-layer-transformer-${selectedObject?.id || "none"}-${selectedObject?._isDraggingToOffCanvas ? "dragging-off" : "normal"}`}
                  ref={topLayerTransformerRef}
                  nodes={(() => {
                    // CRITICAL FIX: Don't show transformer if multiple objects are selected
                    // In multi-selection mode (2+ elements), no transformer should show
                    if (selectedObjects && selectedObjects.length > 1) {
                      return [];
                    }

                    // CRITICAL FIX: Clear transformer nodes when dragging top-layer image to off-canvas
                    // Check if the selected top-layer image is being dragged to off-canvas
                    if (
                      selectedObject?.type === "Image" &&
                      selectedObject?.isOnTopLayer &&
                      topLayerStageRef.current
                    ) {
                      // In off→main drag, the rendered node can be a preview/ghost id (often `${id}-ghost`).
                      // Prefer attaching to the actually-rendered preview node if present.
                      const candidateIds = [];

                      // 1) Single drag preview on main
                      if (
                        dragPreview &&
                        dragPreview.target === "main" &&
                        dragPreview.type === "Image" &&
                        dragPreview.data?.isOnTopLayer === true
                      ) {
                        if (dragPreview.data?.id)
                          candidateIds.push(dragPreview.data.id);
                      }

                      // 2) Group drag previews on main
                      if (
                        Array.isArray(groupDragPreviews) &&
                        groupDragPreviews.length > 0
                      ) {
                        const gp = groupDragPreviews.find(
                          (p) =>
                            p?.target === "main" &&
                            p?.type === "Image" &&
                            p?.data?.isOnTopLayer === true &&
                            (p?.data?.id?.replace?.("-ghost", "") ||
                              p?.data?.id) === selectedObject.id,
                        );
                        if (gp?.data?.id) candidateIds.push(gp.data.id);
                      }

                      // 3) Fallback: selected object id (real node after drop)
                      candidateIds.push(selectedObject.id);

                      const uniqueIds = Array.from(
                        new Set(candidateIds),
                      ).filter(Boolean);
                      let node = null;
                      for (const id of uniqueIds) {
                        node = topLayerStageRef.current.findOne(`#${id}`);
                        if (node) break;
                      }

                      if (!node) return [];

                      const isNodeDragging =
                        node.getAttr("_isDragging") === true;

                      // Check dragPreview
                      const dragPreviewId =
                        dragPreview?.data?.id?.replace("-ghost", "") ||
                        dragPreviewStateRef.current?.id;
                      const isDraggingToOffCanvasViaPreview =
                        dragPreview &&
                        dragPreview.type === "Image" &&
                        dragPreviewId === selectedObject.id &&
                        dragPreview.target === "offCanvas";

                      // Check selectedObject flag (most reliable indicator)
                      const isDraggingToOffCanvasViaFlag =
                        selectedObject?._isDraggingToOffCanvas === true;

                      // CRITICAL: If node is dragging, check if mouse is currently over off-canvas
                      // This provides immediate feedback even if flags aren't set yet
                      let isMouseOverOffCanvas = false;
                      if (isNodeDragging && offCanvasStageRef?.current) {
                        try {
                          const offCanvasContainer =
                            document.querySelector("[data-off-canvas-stage]") ||
                            document.querySelector(
                              ".off-canvas-stage-container",
                            ) ||
                            document.querySelector('[class*="off-canvas"]');
                          if (offCanvasContainer) {
                            // Use global mouse position if available, or check if dragPreview exists
                            // The dragPreview existing with target="offCanvas" is a good indicator
                            isMouseOverOffCanvas =
                              isDraggingToOffCanvasViaPreview;
                          }
                        } catch (e) {
                          // Ignore errors
                        }
                      }

                      // CRITICAL: Hide transformer if ANY of these conditions are true:
                      // 1. Flag is set (most reliable)
                      // 2. Preview shows off-canvas (reliable indicator)
                      // 3. Node is dragging AND mouse is over off-canvas
                      if (
                        isDraggingToOffCanvasViaFlag ||
                        isDraggingToOffCanvasViaPreview ||
                        (isNodeDragging && isMouseOverOffCanvas)
                      ) {
                        return [];
                      }

                      // Otherwise, attach the node
                      return [node];
                    }
                    return [];
                  })()}
                  rotateEnabled={(() => {
                    if (isDuplicatePlacementActive) {
                      return false;
                    }
                    // Top-layer images: keep rotation enabled during drag
                    if (
                      selectedObject?.type === "Image" &&
                      selectedObject?.isOnTopLayer
                    ) {
                      return true;
                    }
                    return selectedObject?.type !== "Text";
                  })()}
                  flipEnabled={false}
                  centeredScaling={
                    selectedObject?.type === "Image" ? false : true
                  }
                  resizeEnabled={true}
                  borderEnabled={true}
                  padding={0}
                  keepRatio={
                    selectedObject?.type === "Image" &&
                    selectedObject?.isOnTopLayer &&
                    selectedObject?.maintainAspectRatio === true
                  }
                  enabledAnchors={(() => {
                    if (isDuplicatePlacementActive) {
                      return [];
                    }
                    // Top-layer images: always show anchors (RAF loop will maintain during drag)
                    if (
                      selectedObject?.type === "Image" &&
                      selectedObject?.isOnTopLayer
                    ) {
                      const anchors =
                        selectedObject?.maintainAspectRatio === true
                          ? ["bottom-right"]
                          : ["middle-right", "bottom-center", "bottom-right"];
                      return anchors;
                    }
                    return [
                      "top-left",
                      "top-center",
                      "top-right",
                      "middle-left",
                      "middle-right",
                      "bottom-left",
                      "bottom-center",
                      "bottom-right",
                    ];
                  })()}
                  boundBoxFunc={(oldBox, newBox) => {
                    // Apply minimum size constraints
                    const minWidth = Math.max(10, oldBox.width * 0.1);
                    const minHeight = Math.max(10, oldBox.height * 0.1);

                    if (newBox.width < minWidth) newBox.width = minWidth;
                    if (newBox.height < minHeight) newBox.height = minHeight;

                    // Only top-left anchored images should lock top-left during resize.
                    // Center-anchored images must keep center, otherwise rotated width-resize drifts.
                    if (
                      selectedObject?.type === "Image" &&
                      selectedObject?.useTopLeftAnchor === true
                    ) {
                      newBox.x = oldBox.x;
                      newBox.y = oldBox.y;
                    }

                    return newBox;
                  }}
                  anchorSize={Math.max(
                    8,
                    8 / Math.max(0.3, effectiveZoomLevel || 1),
                  )}
                  anchorStroke="#007bff"
                  anchorFill="#ffffff"
                  anchorStrokeWidth={Math.max(
                    2,
                    2 / Math.max(0.3, effectiveZoomLevel || 1),
                  )}
                  rotateAnchorOffset={30}
                  onTransformStart={(e) => {
                    crossStageGestureBegin(
                      topLayerStageRef.current,
                      e?.evt,
                    );
                    if (
                      selectedObject?.type === "Image" &&
                      selectedObject?.isOnTopLayer
                    ) {
                      const initialImage = images.find(
                        (img) => img.id === selectedObject.id,
                      );
                      if (initialImage) {
                        const transformNode =
                          topLayerStageRef?.current?.findOne(
                            `#${selectedObject.id}`,
                          );
                        const useTopLeft =
                          initialImage.useTopLeftAnchor === true;
                        let topLeftX, topLeftY;
                        if (transformNode) {
                          const w =
                            transformNode.width() *
                            (transformNode.scaleX() || 1);
                          const h =
                            transformNode.height() *
                            (transformNode.scaleY() || 1);
                          topLeftX = useTopLeft
                            ? transformNode.x()
                            : transformNode.x() - w / 2;
                          topLeftY = useTopLeft
                            ? transformNode.y()
                            : transformNode.y() - h / 2;
                        }
                        // CRITICAL: Store/update position (topLeftX, topLeftY) on EVERY transform start.
                        // Otherwise after drag we would still use the old position and the image would jump back.
                        // Only store rotation/scale/width/height once per session (for hasActualChanges).
                        if (!isTransformingRef.current.initialTransformState) {
                          isTransformingRef.current.initialTransformState = {
                            id: initialImage.id,
                            rotation: initialImage.rotation || 0,
                            scaleX: initialImage.scaleX || 1,
                            scaleY: initialImage.scaleY || 1,
                            width: initialImage.width || 0,
                            height: initialImage.height || 0,
                            ...(transformNode && {
                              topLeftX,
                              topLeftY,
                              useTopLeftAnchor: useTopLeft,
                            }),
                          };
                        } else if (
                          transformNode &&
                          topLeftX != null &&
                          topLeftY != null
                        ) {
                          isTransformingRef.current.initialTransformState.topLeftX =
                            topLeftX;
                          isTransformingRef.current.initialTransformState.topLeftY =
                            topLeftY;
                          isTransformingRef.current.initialTransformState.useTopLeftAnchor =
                            useTopLeft;
                        }
                      }
                      isTransformingRef.current.isTransforming = true;
                    }
                  }}
                  onTransform={(e) => {
                    if (
                      !selectedObject ||
                      selectedObject?.type !== "Image" ||
                      !selectedObject.isOnTopLayer
                    ) {
                      return;
                    }

                    const node = e.target;
                    if (!node) return;

                    // Find the image in state
                    const currentImage = images.find(
                      (img) => img.id === selectedObject.id,
                    );
                    if (!currentImage) return;

                    // Pin position only for top-left anchored images.
                    const initial =
                      isTransformingRef.current.initialTransformState;
                    if (
                      initial?.topLeftX != null &&
                      initial?.topLeftY != null &&
                      initial?.useTopLeftAnchor
                    ) {
                      const w = node.width() * (node.scaleX() || 1);
                      const h = node.height() * (node.scaleY() || 1);
                      node.x(initial.topLeftX);
                      node.y(initial.topLeftY);
                    }

                    // Get current scale and rotation values
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();

                    // CRITICAL FIX: Extract post rotation from total rotation
                    // The node's rotation is the TOTAL rotation (straighten + post)
                    // But we need to save only the post rotation to state
                    // This matches how ImageElement.jsx calculates rotation prop
                    let totalRotation = node.rotation() % 360;
                    if (totalRotation < 0) totalRotation += 360;

                    // Calculate post rotation based on how ImageElement calculates total rotation
                    const isApiImage =
                      currentImage.src &&
                      (currentImage.src.startsWith("http://") ||
                        currentImage.src.startsWith("https://"));
                    const straighten = currentImage.originalCropRotation || 0;

                    let postRotation;
                    // Check if image uses pre-rotated source (would need to check imageSource, but we'll use hasBakedTransforms as proxy)
                    if (currentImage.hasBakedTransforms && !isApiImage) {
                      // For baked local images, straighten is baked in, so total = post
                      postRotation = totalRotation;
                    } else {
                      // For non-baked or API images, total = straighten + post
                      // Extract post rotation: post = total - straighten
                      postRotation = (totalRotation - straighten) % 360;
                      if (postRotation < 0) postRotation += 360;
                    }

                    // Use postRotation for state updates (this is what ImageElement expects)
                    const newRotation = postRotation;

                    // LIVE EDITOR UPDATE: Keep ShapeEditor/ImageEditor in sync with current transform
                    // without touching the images array or history. This mirrors rectangle/circle behavior.
                    const liveWidth = Math.max(
                      5,
                      Math.abs(node.width() * (scaleX || 1)),
                    );
                    const liveHeight = Math.max(
                      5,
                      Math.abs(node.height() * (scaleY || 1)),
                    );

                    const liveAttrs = {
                      ...currentImage,
                      id: currentImage.id,
                      x: node.x(),
                      y: node.y(),
                      width: liveWidth,
                      height: liveHeight,
                      rotation: newRotation,
                      type: "Image",
                    };

                    setSelectedObject(liveAttrs);
                    setSelectedObjects([liveAttrs]);
                  }}
                  onTransformEnd={(e) => {
                    crossStageGestureEnd();
                    justFinishedTransformRef.current = true;
                    if (
                      !selectedObject ||
                      selectedObject?.type !== "Image" ||
                      !selectedObject.isOnTopLayer
                    ) {
                      return;
                    }

                    const node = e.target;
                    if (!node) return;

                    // CRITICAL: Check for actual changes against INITIAL state (from onTransformStart)
                    // Not against current state, which may have been updated by previous onTransformEnd calls
                    const initialState =
                      isTransformingRef.current.initialTransformState;

                    // Get total rotation from node
                    let totalRotation = node.rotation() % 360;
                    if (totalRotation < 0) {
                      totalRotation += 360;
                    }

                    const finalScaleX = node.scaleX();
                    const finalScaleY = node.scaleY();

                    // Find the image in state
                    const currentImage = images.find(
                      (img) => img.id === selectedObject.id,
                    );
                    if (!currentImage) return;

                    // CRITICAL FIX: Extract post rotation from total rotation
                    // The node's rotation is the TOTAL rotation (straighten + post)
                    // But we need to save only the post rotation to state
                    // This matches how ImageElement.jsx calculates rotation prop
                    const isApiImage =
                      currentImage.src &&
                      (currentImage.src.startsWith("http://") ||
                        currentImage.src.startsWith("https://"));
                    const straighten = currentImage.originalCropRotation || 0;

                    const usesStraightenedCropPath =
                      !!currentImage.cropData &&
                      currentImage.originalCropRotation !== undefined &&
                      currentImage.originalCropRotation !== 0;

                    let postRotation;
                    // For straightened+cropped images rendered via pre-rotated source,
                    // node rotation is already post-rotation.
                    if (
                      usesStraightenedCropPath ||
                      (currentImage.hasBakedTransforms && !isApiImage)
                    ) {
                      // For baked local images too, straighten is baked in, so total = post
                      postRotation = totalRotation;
                    } else {
                      // For non-baked or API images, total = straighten + post
                      // Extract post rotation: post = total - straighten
                      postRotation = (totalRotation - straighten) % 360;
                      if (postRotation < 0) postRotation += 360;
                    }

                    // Check for actual changes
                    const hasActualChanges =
                      initialState &&
                      (Math.abs((initialState.rotation || 0) - postRotation) >
                        0.01 ||
                        Math.abs(
                          (initialState.scaleX || 1) - Math.abs(finalScaleX),
                        ) > 0.01 ||
                        Math.abs(
                          (initialState.scaleY || 1) - Math.abs(finalScaleY),
                        ) > 0.01 ||
                        Math.abs(
                          (initialState.width || 0) -
                            currentImage.width * Math.abs(finalScaleX),
                        ) > 0.01 ||
                        Math.abs(
                          (initialState.height || 0) -
                            currentImage.height * Math.abs(finalScaleY),
                        ) > 0.01);

                    // CRITICAL: Set pendingChanges if actual changes occurred
                    if (hasActualChanges) {
                      setPendingChanges(true);
                      if (pendingChangesRef) {
                        pendingChangesRef.current = true;
                      }
                    }

                    // CRITICAL: DON'T clear initial state here - keep it for the entire editing session
                    // This ensures all transforms in the session are compared against the ORIGINAL state
                    // The initial state will be cleared when the element is unselected (in endElementEditing)
                    // if (isTransformingRef.current.initialTransformState) {
                    //   delete isTransformingRef.current.initialTransformState;
                    // }

                    isTransformingRef.current.isTransforming = false;

                    // CRITICAL FIX: Always use the actual rotation from the transform
                    // Don't preserve old rotation - this causes position jumps after rotation
                    const preservedRotation = postRotation;

                    let newWidth = currentImage.width;
                    let newHeight = currentImage.height;

                    // CRITICAL FIX: Check if image uses top-left anchor
                    const useTopLeftAnchor =
                      currentImage.useTopLeftAnchor === true;

                    // CRITICAL FIX: Check if ACTUAL resizing occurred (not just flip state)
                    const scaleXMagnitude = Math.abs(finalScaleX);
                    const scaleYMagnitude = Math.abs(finalScaleY);

                    // Only update dimensions if scaling occurred (not just rotation or flip state)
                    if (
                      Math.abs(scaleXMagnitude - 1) > 0.01 ||
                      Math.abs(scaleYMagnitude - 1) > 0.01
                    ) {
                      // Calculate new dimensions by absorbing scale into width/height
                      newWidth = Math.max(
                        5,
                        Math.abs(currentImage.width * finalScaleX),
                      );
                      newHeight = Math.max(
                        5,
                        Math.abs(currentImage.height * finalScaleY),
                      );

                      // If maintain aspect ratio is enabled, ensure proportional scaling
                      if (currentImage.maintainAspectRatio) {
                        const aspectRatio =
                          currentImage.originalAspectRatio ||
                          currentImage.width / currentImage.height;
                        const dominantScale = Math.max(
                          Math.abs(finalScaleX),
                          Math.abs(finalScaleY),
                        );

                        newWidth = Math.max(
                          5,
                          currentImage.width * dominantScale,
                        );
                        newHeight = Math.max(5, newWidth / aspectRatio);

                        // Round to avoid floating-point precision issues
                        newWidth = Math.round(newWidth * 100) / 100;
                        newHeight = Math.round(newHeight * 100) / 100;
                      }

                      // CRITICAL FIX: DO NOT recalculate center from clientRect
                      // The center point should remain the same (node.x() and node.y())
                      // Recalculating from clientRect after scale reset causes the image to shift
                      // The boundBoxFunc already preserves the center during scaling,
                      // so node.x() and node.y() are already correct

                      // CRITICAL FIX: Reset scale based on hasBakedTransforms
                      const isApiImage =
                        currentImage.src &&
                        (currentImage.src.startsWith("http://") ||
                          currentImage.src.startsWith("https://"));
                      const shouldApplyFlipViaScale =
                        !currentImage.hasBakedTransforms || isApiImage;

                      let resetScaleX, resetScaleY;
                      if (shouldApplyFlipViaScale) {
                        // Always reset based on flip state, not current scale sign
                        resetScaleX = currentImage.flipHorizontal ? -1 : 1;
                        resetScaleY = currentImage.flipVertical ? -1 : 1;
                      } else {
                        // For baked local images: always positive
                        resetScaleX = 1;
                        resetScaleY = 1;
                      }

                      node.scaleX(resetScaleX);
                      node.scaleY(resetScaleY);
                      node.width(newWidth);
                      node.height(newHeight);

                      // CRITICAL: After resetting scale and updating dimensions, the position should still be correct
                      // because boundBoxFunc preserved it during scaling. For top-left anchor, it preserved top-left corner.
                      // For center anchor, it preserved center point. Don't recalculate it.
                    }

                    // Position: if no resize occurred (e.g. user only dragged), use current node position.
                    // On resize, only top-left anchored images should use initial top-left; center-anchored
                    // images should keep the live node position from Konva.
                    const dimensionsChanged =
                      Math.abs(scaleXMagnitude - 1) > 0.01 ||
                      Math.abs(scaleYMagnitude - 1) > 0.01;
                    let positionX, positionY;
                    if (!dimensionsChanged) {
                      // No resize - use current position (respects drag during transform session)
                      positionX = node.x();
                      positionY = node.y();
                    } else if (
                      initialState?.topLeftX != null &&
                      initialState?.topLeftY != null &&
                      initialState?.useTopLeftAnchor
                    ) {
                      positionX = initialState.topLeftX;
                      positionY = initialState.topLeftY;
                    } else {
                      positionX = node.x();
                      positionY = node.y();
                    }

                    // CRITICAL: Explicitly set position from stored top-left + new size
                    node.x(positionX);
                    node.y(positionY);

                    const stage = node.getStage();
                    const stageWidth = stage.width();
                    const stageHeight = stage.height();

                    let clampedX, clampedY;
                    if (useTopLeftAnchor) {
                      // For top-left anchor: clamp top-left corner within stage bounds
                      const minX = 0;
                      const maxX = stageWidth - newWidth;
                      const minY = 0;
                      const maxY = stageHeight - newHeight;
                      clampedX = Math.max(minX, Math.min(positionX, maxX));
                      clampedY = Math.max(minY, Math.min(positionY, maxY));
                    } else {
                      // For center anchor: clamp center point within stage bounds
                      const minX = newWidth / 2;
                      const maxX = stageWidth - newWidth / 2;
                      const minY = newHeight / 2;
                      const maxY = stageHeight - newHeight / 2;
                      clampedX = Math.max(minX, Math.min(positionX, maxX));
                      clampedY = Math.max(minY, Math.min(positionY, maxY));
                    }

                    // Only update position if clamping was necessary
                    if (clampedX !== positionX || clampedY !== positionY) {
                      node.x(clampedX);
                      node.y(clampedY);
                    }

                    // Rotation-aware final clamp: when image is already rotated and user only resizes,
                    // axis-aligned min/max clamp is not sufficient. Clamp by rendered client-rect.
                    const normalizedRotation =
                      ((Number(preservedRotation) || 0) % 360 + 360) % 360;
                    const isEffectivelyRotated =
                      Math.abs(Math.sin((normalizedRotation * Math.PI) / 180)) > 1e-3;
                    if (isEffectivelyRotated) {
                      const getEffectiveInset = (requestedInset, stageSize, renderedSize) => {
                        const safeInset = Math.max(0, Number(requestedInset) || 0);
                        const safeStage = Math.max(0, Number(stageSize) || 0);
                        const safeRendered = Math.max(0, Number(renderedSize) || 0);
                        return Math.max(0, Math.min(safeInset, (safeStage - safeRendered) / 2));
                      };
                      for (let pass = 0; pass < 8; pass += 1) {
                        const rect = node.getClientRect({ relativeTo: stage });
                        const insetX = getEffectiveInset(
                          TRANSFORMER_ANCHOR_BUFFER_PX,
                          stageWidth,
                          rect.width,
                        );
                        const insetY = getEffectiveInset(
                          TRANSFORMER_ANCHOR_BUFFER_PX,
                          stageHeight,
                          rect.height,
                        );
                        let dx = 0;
                        let dy = 0;
                        if (rect.x < insetX) dx = insetX - rect.x;
                        else if (rect.x + rect.width > stageWidth - insetX) {
                          dx = stageWidth - insetX - (rect.x + rect.width);
                        }
                        if (rect.y < insetY) dy = insetY - rect.y;
                        else if (rect.y + rect.height > stageHeight - insetY) {
                          dy = stageHeight - insetY - (rect.y + rect.height);
                        }
                        if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) break;
                        node.x((Number(node.x()) || 0) + dx);
                        node.y((Number(node.y()) || 0) + dy);
                      }
                      clampedX = node.x();
                      clampedY = node.y();
                      node.x(clampedX);
                      node.y(clampedY);
                    }

                    // CRITICAL: Force layer redraw to show the updated dimensions
                    // This ensures the visual update happens immediately
                    const layer = node.getLayer();
                    if (layer) {
                      layer.batchDraw();
                    }

                    // CRITICAL: Preserve isOnTopLayer from selectedObject (source of truth for UI)
                    // Use selectedObject.isOnTopLayer as primary source since that's what the checkbox reflects
                    const preservedIsOnTopLayer =
                      selectedObject.isOnTopLayer ??
                      currentImage.isOnTopLayer ??
                      false;

                    // Calculate the updated state BEFORE calling setState
                    const finalX = node.x();
                    const finalY = node.y();

                    const updatedImages = images.map((img) => {
                      if (img.id === selectedObject.id) {
                        return {
                          ...img,
                          id: img.id,
                          x: finalX,
                          y: finalY,
                          rotation: preservedRotation,
                          scaleX: node.scaleX(),
                          scaleY: node.scaleY(),
                          width: newWidth,
                          height: newHeight,
                          isTransform: true,
                          originalAspectRatio: img.originalAspectRatio,
                          cropData: img.cropData,
                          originalImage: img.originalImage,
                          originalImageDataUrl: img.originalImageDataUrl,
                          originalCropRotation: img.originalCropRotation,
                          originalWidth: img.originalWidth,
                          originalHeight: img.originalHeight,
                          flipHorizontal: img.flipHorizontal,
                          flipVertical: img.flipVertical,
                          hasBakedTransforms: img.hasBakedTransforms,
                          // CRITICAL: Explicitly preserve isOnTopLayer to prevent checkbox fluctuation
                          isOnTopLayer: preservedIsOnTopLayer,
                        };
                      }
                      return img;
                    });

                    // Update state
                    setImages((prev) =>
                      prev.map((img) =>
                        img.id === selectedObject.id
                          ? {
                              ...img,
                              x: finalX,
                              y: finalY,
                              rotation: preservedRotation,
                              scaleX: node.scaleX(),
                              scaleY: node.scaleY(),
                              width: newWidth,
                              height: newHeight,
                              isTransform: true,
                              originalAspectRatio: img.originalAspectRatio,
                              cropData: img.cropData,
                              originalImage: img.originalImage,
                              originalImageDataUrl: img.originalImageDataUrl,
                              originalCropRotation: img.originalCropRotation,
                              originalWidth: img.originalWidth,
                              originalHeight: img.originalHeight,
                              flipHorizontal: img.flipHorizontal,
                              flipVertical: img.flipVertical,
                              hasBakedTransforms: img.hasBakedTransforms,
                              // CRITICAL: Explicitly preserve isOnTopLayer to prevent checkbox fluctuation
                              isOnTopLayer: preservedIsOnTopLayer,
                            }
                          : img,
                      ),
                    );

                    const updatedImage = updatedImages.find(
                      (img) => img.id === selectedObject.id,
                    );

                    // CRITICAL: Explicitly preserve isOnTopLayer when updating selectedObject
                    // This ensures the checkbox doesn't fluctuate during transform operations
                    const finalUpdatedImage = {
                      ...updatedImage,
                      type: "Image",
                      // CRITICAL: Explicitly preserve isOnTopLayer to prevent checkbox fluctuation
                      isOnTopLayer: preservedIsOnTopLayer,
                    };

                    setSelectedObject(finalUpdatedImage);
                    setSelectedObjects([finalUpdatedImage]);

                    // CRITICAL: Also update selectedImage to ensure checkbox reads correct value
                    // selectedImage is what ImageEditor reads from, so it must have correct isOnTopLayer
                    if (setSelectedImage) {
                      setSelectedImage(finalUpdatedImage);
                    }

                    if (updateImageProperties) {
                      // Skip history save - we'll save it when element is unselected
                      updateImageProperties(
                        selectedObject.id,
                        updatedImage,
                        false,
                        true,
                      );
                    }

                    // CRITICAL: Store the updated images array in ref so element_edit_complete can use it
                    // This ensures all transform changes are grouped into a single history entry
                    // Only store if there are actual changes
                    if (hasActualChanges && latestUpdatedImagesRef) {
                      // Clean isTransform flag from all images before storing
                      const cleanedUpdatedImages = updatedImages.map((img) => {
                        const { isTransform, ...cleanedImg } = img;
                        return cleanedImg;
                      });
                      // Store in main canvas (top-layer images are always on main canvas)
                      latestUpdatedImagesRef.current.main =
                        cleanedUpdatedImages;
                    }

                    // CRITICAL: Don't save to history here - history will be saved when element is unselected
                    // This groups all transform changes (width, height, rotation, etc.) into a single history entry
                  }}
                />
              </Layer>
            </Stage>

            {/* Selection box overlay removed - using Konva Rect inside Stage instead */}
          </div>
        )}

        {/* Multi-select toggle and Select All buttons - shown at bottom of canvas when an element is selected */}
        {(() => {
          // Hide buttons if crop modal is open
          if (isCropModalOpen) return null;

          // More robust checks
          const hasSelectedObject =
            selectedObject !== null &&
            selectedObject !== undefined &&
            selectedObject !== false;
          const hasSelectedObjects =
            Array.isArray(selectedObjects) && selectedObjects.length > 0;
          const shouldShow = hasSelectedObject || hasSelectedObjects;

          if (!shouldShow) return null;

          // Helper function to check if all elements from the same canvas are selected
          const areAllElementsSelected = () => {
            // Get reference element - use selectedObject if available, otherwise use first item from selectedObjects
            const referenceElement =
              selectedObject ||
              (selectedObjects && selectedObjects.length > 0
                ? selectedObjects[0]
                : null);

            if (!referenceElement) return false;

            // Get all elements from the same canvas type (main or off-canvas)
            let allElementsFromSameCanvas =
              getAllElementsFromSameCanvas(referenceElement);

            // If selectedObject exists and is not in arrays, ensure it's included in the result
            if (selectedObject && selectedObject.id) {
              const isInResult = allElementsFromSameCanvas.some(
                (el) => el.id === selectedObject.id,
              );
              if (!isInResult) {
                allElementsFromSameCanvas = [
                  selectedObject,
                  ...allElementsFromSameCanvas,
                ];
              }
            }

            // If there are no elements in the canvas, return false
            if (allElementsFromSameCanvas.length === 0) return false;

            // Check if all elements are selected
            // In multi-select mode, check individuallySelectedIds
            // Otherwise, check selectedObjects
            // Only return true if ALL elements from the same canvas are selected
            if (
              isMultiSelectMode &&
              individuallySelectedIds &&
              individuallySelectedIds.size > 0
            ) {
              // In multi-select mode, check if all elements are in individuallySelectedIds
              return allElementsFromSameCanvas.every((el) =>
                individuallySelectedIds.has(el.id),
              );
            } else if (selectedObjects && selectedObjects.length > 0) {
              // Check if all elements from same canvas are in selectedObjects
              const selectedIds = new Set(
                selectedObjects.map((obj) => `${obj.id}-${obj.type}`),
              );
              return allElementsFromSameCanvas.every((el) =>
                selectedIds.has(`${el.id}-${el.type}`),
              );
            }

            return false;
          };

          const isAllSelected = areAllElementsSelected();
          // Button styling now uses selectAllButtonClickedFeedback for temporary blue background on click
          // The button is always gray by default
          const isMultiSelectButtonEnabled = isMultiSelectMode;
          const disableDuplicatePlacementMode = () => {
            if (!isDuplicatePlacementActive) return;
            if (setAction) {
              setAction(ACTIONS.SELECT);
            }
            if (setIsCloneMode) {
              setIsCloneMode(false);
            }
            if (setCloneTemplate) {
              setCloneTemplate(null);
            }
            if (setCloneTemplates) {
              setCloneTemplates([]);
            }
            if (lockedAction && setLockedAction) {
              setLockedAction(null);
            }
          };

          // Use React Portal to render buttons outside Canvas component DOM tree
          // This ensures they're always visible regardless of parent container overflow/transform
          const resizeHandleY = Math.max(
            0,
            Math.max(
              parseInt(canvasHeight, 10) <= 1
                ? 10
                : parseInt(canvasHeight, 10) <= 5
                  ? 10
                  : parseInt(canvasHeight, 10) < 30
                    ? 50
                    : 20,
              fixedStageHeightPx,
            ) -
              RESIZE_BOTTOM_SAFE +
              stagePos.y,
          );
          // Keep controls tied to the resize-orb Y anchor, but never let them go
          // outside the viewport on smaller-height screens.
          const BUTTONS_VIEWPORT_MARGIN_PX = 8;
          const BUTTONS_APPROX_HEIGHT_PX = 36;
          const controlsTop = Math.max(
            BUTTONS_VIEWPORT_MARGIN_PX,
            Math.min(
              resizeHandleY - 56,
              window.innerHeight -
                BUTTONS_APPROX_HEIGHT_PX -
                BUTTONS_VIEWPORT_MARGIN_PX,
            ),
          );
          const controlsLeft =
            mainCanvasCenterX > 0
              ? mainCanvasCenterX
              : window.innerWidth / 2 + stagePos.x;
          const buttonsContent = (
            <div
              data-canvas-chrome
              className="multi-select-controls"
              style={{
                position: "fixed",
                // Match resize-orb anchoring so controls remain fixed with the main stage.
                left: `${controlsLeft}px`,
                top: `${controlsTop}px`,
                transform: "translateX(-50%)",
                display: "flex",
                gap: "12px",
                zIndex: 998, // Keep below ShapeEditor (zIndex 999)
                pointerEvents: "auto",
                width: "auto",
                justifyContent: "center",
                padding: "0 20px",
              }}
              onClick={(e) => {
                // Prevent clicks from propagating to canvas
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                // Prevent mousedown from propagating to canvas
                e.stopPropagation();
              }}
            >
              {/* Multi-select toggle button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  disableDuplicatePlacementMode();
                  if (setIsMultiSelectMode) {
                    setIsMultiSelectMode(!isMultiSelectMode);
                    // When enabling multi-select from a single selected element, ensure
                    // selectedObjects is seeded immediately so auto-disable logic does not
                    // flip multi-select back off due to an empty selectedObjects array.
                    if (!isMultiSelectMode) {
                      const currentPrimary =
                        selectedObject ||
                        (selectedObjects && selectedObjects.length > 0
                          ? selectedObjects[0]
                          : null);
                      if (
                        currentPrimary &&
                        setSelectedObjects &&
                        (!selectedObjects || selectedObjects.length === 0)
                      ) {
                        setSelectedObjects([currentPrimary]);
                      }
                    }
                    // When disabling multi-select, keep only the last selected element
                    if (isMultiSelectMode) {
                      // Requested behavior: disabling multi-select clears selection entirely.
                      if (setSelectedObject) setSelectedObject(null);
                      if (setSelectedObjects) setSelectedObjects([]);
                      if (setSelectedImage) setSelectedImage(null);
                      if (setSelectedText) setSelectedText(null);
                      if (setSelectedArrow) setSelectedArrow(null);
                      if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                      if (setSelectedLine) setSelectedLine(null);
                      if (setShowTextBox) setShowTextBox(false);
                      // Clear individuallySelectedIds when disabling multi-select
                      if (setIndividuallySelectedIds) {
                        setIndividuallySelectedIds(new Set());
                      }
                      // Clear selectAllButtonClicked flag
                      setSelectAllButtonClicked(false);
                    }
                  }
                }}
                style={{
                  padding: "6px 12px",
                  backgroundColor: isMultiSelectButtonEnabled
                    ? "#3B82F6"
                    : "#F3F4F6",
                  color: isMultiSelectButtonEnabled ? "white" : "#374151",
                  border: isMultiSelectButtonEnabled
                    ? "1px solid #3B82F6"
                    : "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: 1,
                }}
              >
                <MultiSelectIcon
                  width={14}
                  height={14}
                  fill={isMultiSelectButtonEnabled ? "white" : "#374151"}
                />
                <span>Select Multiple</span>
              </button>

              {/* Select All button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  disableDuplicatePlacementMode();

                  // Show blue background feedback for 300ms
                  setSelectAllButtonClickedFeedback(true);
                  setTimeout(() => {
                    setSelectAllButtonClickedFeedback(false);
                  }, 300);

                  // Get reference element - use selectedObject if available, otherwise use first item from selectedObjects
                  const referenceElement =
                    selectedObject ||
                    (selectedObjects && selectedObjects.length > 0
                      ? selectedObjects[0]
                      : null);

                  if (!referenceElement) return;

                  // Get all elements from the same canvas type (main or off-canvas) as the currently selected element
                  let allElementsFromSameCanvas =
                    getAllElementsFromSameCanvas(referenceElement);

                  // CRITICAL: If selectedObject exists and is not in arrays, ensure it's included in the result
                  // This prevents the element from disappearing during select all operation
                  if (selectedObject && selectedObject.id) {
                    const isInResult = allElementsFromSameCanvas.some(
                      (el) => el.id === selectedObject.id,
                    );
                    if (!isInResult) {
                      // Element is not in arrays, add it to the result
                      allElementsFromSameCanvas = [
                        selectedObject,
                        ...allElementsFromSameCanvas,
                      ];
                    }
                  }

                  // Always select all elements - no toggle logic
                  // Automatically enable multi-select mode
                  if (setIsMultiSelectMode) {
                    setIsMultiSelectMode(true);
                  }

                  // Update selectedObjects to include all elements from same canvas
                  if (
                    setSelectedObjects &&
                    allElementsFromSameCanvas.length > 0
                  ) {
                    setSelectedObjects(allElementsFromSameCanvas);
                  }

                  // CRITICAL: Also update selectedObject to the first element to ensure it's preserved
                  // This prevents the element from disappearing during the state update
                  if (selectedObject && selectedObject.id) {
                    // Keep the current selectedObject if it exists
                    // This ensures continuity during the select all operation
                  } else if (allElementsFromSameCanvas.length > 0) {
                    setSelectedObject(allElementsFromSameCanvas[0]);
                  }

                  // Select all items in individuallySelectedIds
                  if (
                    setIndividuallySelectedIds &&
                    allElementsFromSameCanvas.length > 0
                  ) {
                    setIndividuallySelectedIds(
                      new Set(allElementsFromSameCanvas.map((obj) => obj.id)),
                    );
                  }

                  // Mark that the Select All button was clicked
                  setSelectAllButtonClicked(true);
                }}
                style={{
                  padding: "6px 12px",
                  backgroundColor: selectAllButtonClickedFeedback
                    ? "#3B82F6"
                    : "#F3F4F6",
                  color: selectAllButtonClickedFeedback ? "white" : "#374151",
                  border: selectAllButtonClickedFeedback
                    ? "1px solid #3B82F6"
                    : "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: 1,
                }}
              >
                <SelectAllIcon
                  width={14}
                  height={14}
                  fill={selectAllButtonClickedFeedback ? "white" : "#374151"}
                />
                <span>Select All</span>
              </button>
            </div>
          );

          // Render buttons via portal to document.body to escape parent container constraints
          return ReactDOM.createPortal(buttonsContent, document.body);
        })()}
      </div>

      {/* Resize Handle - using native HTML div for better zoom compatibility */}
      {parseInt(canvasHeight) !== 0 && !selectedObject && (!selectedObjects || selectedObjects.length === 0) && (
        <div
          style={{
            position: "fixed",
            left: `calc(50% - 20px + ${stagePos.x}px)`,
            top: `${Math.max(
              0,
              fixedStageHeightPx - RESIZE_BOTTOM_SAFE + stagePos.y,
            )}px`,
            width: "40px",
            height: "40px",
            marginLeft: "0",
            marginTop: "-20px",
            borderRadius: "50%",
            cursor: "ns-resize",
            zIndex: 99, // Lower than buttons (zIndex 1000) so buttons are on top
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const syntheticEvent = {
              evt: e.nativeEvent,
              target: { name: () => "resize-handle" },
            };
            handleResizeMouseDown(syntheticEvent);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const syntheticEvent = {
              evt: e.nativeEvent,
              target: { name: () => "resize-handle" },
            };
            handleResizeMouseDown(syntheticEvent);
          }}
          onMouseEnter={() => {
            if (!isResizing) {
              document.body.style.cursor = "ns-resize";
            }
          }}
          onMouseLeave={() => {
            if (!isResizing) {
              document.body.style.cursor = "default";
            }
          }}
        >
          {/* Visible resize handle circle */}
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              border: "2px solid #0D6EFD",
              backgroundColor: "transparent",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {/* Old Konva-based resize handle - commented out for now */}
      {false &&
        parseInt(canvasHeight) !== 0 &&
        (() => {
          // Calculate resize handle Y position - account for canvas height and pan offset
          const canvasBottomY =
            fixedStageHeightPx;
          const resizeHandleY = Math.max(
            0,
            Math.max(
              parseInt(canvasHeight) <= 1
                ? 10
                : parseInt(canvasHeight) <= 5
                  ? 10
                  : parseInt(canvasHeight) < 30
                    ? 50
                    : 20,
              canvasBottomY - 4,
            ) -
              RESIZE_BOTTOM_SAFE +
              stagePos.y,
          );

          // Get zoom level from parent container's inline style transform
          // Must check inline style attribute directly, not computed style (which returns matrix)
          const getZoomLevel = () => {
            try {
              // Look for elements with inline style containing "transform: scale"
              const elements = document.querySelectorAll(
                '[style*="transform"]',
              );
              for (const el of elements) {
                const inlineStyle = el.getAttribute("style") || "";
                const scaleMatch = inlineStyle.match(
                  /transform:\s*scale\(([^)]+)\)/,
                );
                if (scaleMatch && scaleMatch[1]) {
                  const zoom = parseFloat(scaleMatch[1]);
                  if (!isNaN(zoom) && zoom > 0) {
                    return zoom;
                  }
                }
              }
            } catch (e) {
              // Fallback to 1 if we can't detect zoom
            }
            return 1;
          };
          const zoomLevel = getZoomLevel();

          return (
            <Stage
              ref={resizeHandleStageRef}
              width={window.innerWidth}
              height={window.innerHeight}
              pixelRatio={CANVAS_PIXEL_RATIO}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                zIndex: 100, // Much higher than all other stages to ensure it's always on top
                pointerEvents: "auto", // Always enable - we'll handle clicks intelligently via hit detection
                // No transform needed - fixed position is relative to viewport, not parent
                // Use isolation to create new stacking context and prevent event interference
                isolation: "isolate",
              }}
            >
              <Layer listening={true}>
                {/* Transparent background rect that forwards non-handle clicks - must be first so circles render on top */}
                <Rect
                  x={0}
                  y={0}
                  width={window.innerWidth}
                  height={window.innerHeight}
                  fill="transparent"
                  listening={true}
                  name="resize-handle-stage-background"
                  onMouseDown={(e) => {
                    // Only forward if not clicking on resize handle
                    const target = e.target;
                    const targetName = target?.name?.() || "";
                    const isResizeHandle =
                      targetName === "resize-handle" ||
                      targetName === "resize-handle-area";

                    if (!isResizeHandle) {
                      // Clicked on empty area - forward to main canvas
                      if (stageRef?.current && e.evt) {
                        const mainStage = stageRef.current;
                        mainStage.setPointersPositions(e.evt);
                        const pointerPos = mainStage.getPointerPosition();
                        if (pointerPos) {
                          const shape = mainStage.getIntersection(pointerPos);
                          if (shape && shape !== mainStage) {
                            shape.fire("mousedown", { evt: e.evt });
                          } else if (handleStageMouseDown) {
                            const syntheticEvent = {
                              evt: e.evt,
                              target: mainStage,
                            };
                            handleStageMouseDown(syntheticEvent);
                          }
                        }
                      }
                    }
                  }}
                />
                {/* Larger invisible hit area for the resize handle */}
                {/* Use viewport coordinates directly - no zoom adjustment needed */}
                <Circle
                  x={mainCanvasCenterX}
                  y={resizeHandleY}
                  radius={60} // Increased hit area to 60px for better reliability at boundary
                  fill="transparent"
                  listening={true}
                  name="resize-handle-area"
                  visible={false}
                  onMouseEnter={() => {
                    if (!isResizing) {
                      document.body.style.cursor = "ns-resize";
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isResizing) {
                      document.body.style.cursor = "default";
                    }
                  }}
                  onMouseDown={(e) => {
                    // If clicking on the hit area, treat it as clicking on the handle
                    e.cancelBubble = true;
                    e.evt.stopPropagation();
                    e.evt.preventDefault();
                    handleResizeMouseDown(e);
                  }}
                />
                {/* Visible resize handle circle */}
                {/* Use viewport coordinates directly - no zoom adjustment needed */}
                <Circle
                  x={mainCanvasCenterX}
                  y={resizeHandleY}
                  radius={8}
                  fill="transparent"
                  stroke="#0D6EFD"
                  strokeWidth={2}
                  draggable={false}
                  listening={true}
                  name="resize-handle"
                  onMouseDown={(e) => {
                    e.cancelBubble = true;
                    e.evt.stopPropagation();
                    e.evt.preventDefault();
                    handleResizeMouseDown(e);
                  }}
                  onTouchStart={(e) => {
                    e.cancelBubble = true;
                    e.evt.stopPropagation();
                    e.evt.preventDefault();
                    handleResizeMouseDown(e);
                  }}
                  visible={true}
                  onMouseEnter={() => {
                    if (!isResizing) {
                      document.body.style.cursor = "ns-resize";
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isResizing) {
                      document.body.style.cursor = "default";
                    }
                  }}
                />
              </Layer>
            </Stage>
          );
        })()}

      {/* Crosshair Tooltip - shown when tool is locked or paste mode is active */}
      <CrosshairTooltip
        key={`tooltip-${
          lockedAction || isWaitingForPaste || largeImageWarning
            ? "visible"
            : "hidden"
        }`}
        stageRef={stageRef}
        isVisible={!!(lockedAction || isWaitingForPaste || largeImageWarning)}
        secondaryMessage={
          largeImageWarning
            ? "Warning: Large images (10MB+) can slow down or crash the canvas."
            : ""
        }
        onClose={() => {
          // Close paste mode if active
          if (isWaitingForPaste && setIsWaitingForPaste) {
            setIsWaitingForPaste(false);
          }
          // Exit duplicate-to-selected-location mode
          if (isCloneMode) {
            if (setAction) {
              setAction(ACTIONS.SELECT);
            }
            if (setIsCloneMode) {
              setIsCloneMode(false);
            }
            if (setCloneTemplate) {
              setCloneTemplate(null);
            }
            if (setCloneTemplates) {
              setCloneTemplates([]);
            }
          }
          // Unlock tool if locked
          if (lockedAction && setLockedAction) {
            setLockedAction(null);
          }
        }}
      />
    </div>
  );
};

export default Canvas;
